/**
 * Timbrica Core — shared utilities and UI logic.
 * Cached by browser + Service Worker, loaded on every page.
 */

/* Promise.withResolvers (ES2024) — Safari <17.4 / Chromium <119 не имеют.
 * Требуется pdfjs v4 (вендор-файлы несут свой препенд для СВОЕГО worker-глобала
 * — страничный полифилл в воркер не попадает); здесь — для main-thread
 * потребителей и любого будущего кода. Sentry TIMBRICA-FRONTEND-FB/FD. */
if (!Promise.withResolvers) {
    Promise.withResolvers = function () {
        var res, rej, p = new Promise(function (a, b) { res = a; rej = b; });
        return { promise: p, resolve: res, reject: rej };
    };
}

/* initUploadProgress safety stub.
 * The real implementation lives inline in layouts/app.blade.php (so it can't
 * lose a defer-load race against this script). But that one inline <script>
 * can still be suppressed in rare cases — Chromium content-blocking
 * extensions that strip unrecognised inline scripts, or a malformed upstream
 * value that throws before the assignment line. Sentry TIMBRICA-FRONTEND-1W:
 * 1 user on Yandex Browser 26.3 / Win10 / /ru/voice-changer hit
 * `ReferenceError: initUploadProgress is not defined` from bindEvents().
 * This stub gives every tool a no-op fallback so the page never hard-errors;
 * the real implementation, when it does run, overwrites this stub. The stub
 * is API-compatible (same keys, Promise-returning readFile) so callers don't
 * need to branch. */
if (typeof window.initUploadProgress !== 'function') {
    window.initUploadProgress = function() {
        var noop = function(){};
        return {
            show: noop,
            hide: noop,
            readFile: function(file) {
                return new Promise(function(resolve, reject) {
                    var fr = new FileReader();
                    fr.onload  = function(){ resolve(fr.result); };
                    fr.onerror = function(){ reject(fr.error || new Error('FileReader failed')); };
                    fr.readAsArrayBuffer(file);
                });
            },
            cancelled: false,
            reset: noop,
            set onCancel(fn){ /* stub ignores cancel */ }
        };
    };
    window.initUploadProgress.__isStub = true;
}

/* ── Вошёл ли читатель — по куке, а не по разметке ──────────────────────
 * `timbrica_auth` ставит каждый путь входа (LoginController::queueAuthCookie:
 * пароль, OAuth, гостевая касса, переключение аккаунтов) и снимает выход;
 * httpOnly=false именно затем, чтобы её видели nginx и страница. Это
 * единственный сигнал входа, который НЕ протухает вместе с HTML: Service
 * Worker на медленной сети (NetworkFirst, таймаут 4 с) отдаёт копию страницы
 * из кэша, и у вошедшего это гостевая копия с `data-authed="0"` от визита до
 * входа — личный ответ вытесняет её лишь когда сам доезжает. Кнопка, которая
 * поверила такой разметке, отправляла вошедшего на /login → 302 → профиль
 * (класс заявки S.V., 08–09.09.2026). Значение куки зашифровано — читаем
 * только факт её наличия. Не граница доверия: сервер проверяет сессию сам. */
window.__signedIn = function () {
    try { return /(?:^|;\s*)timbrica_auth=/.test(document.cookie || ''); } catch (_) { return false; }
};

/* __intlLocale:begin */
/* ── Язык для Intl и toLocale*: локаль страницы, а не атрибут <html lang> ──
 * Атрибут lang принадлежит не нам: переводчик браузера переписывает его на
 * язык перевода — и на своё служебное значение. 06.10 на /ru/trim (Яндекс
 * Браузер, Android) там стояло «easy»: четыре буквы — недопустимый тег
 * BCP 47, и toLocaleString с этим атрибутом бросал RangeError на каждом
 * движении ручки выделения, а кнопка «Вырезать» так и не появлялась
 * (TIMBRICA-FRONTEND-P0). Источник — window.__LOCALE: его
 * печатает сервер. Пока layout его не присвоил (инлайн-скрипт контента
 * исполняется раньше), — тот же lang, которого переводчик ещё не касался.
 * Тег проверяет сам Intl: недопустимый → undefined (язык браузера), а не
 * исключение. Арабский — с латинскими цифрами, как везде на сайте
 * (local-time.js, inflation/ui.js, helpers.php). Ответ помнится, пока не
 * сменится вход: вызов стоит в циклах перетаскивания.
 * Сторожа — IntlLocaleIsNotReadFromTheDomTest, tests/js/intl-locale.test.mjs. */
(function () {
    var seen = null, tag;
    window.__intlLocale = function () {
        var l = String(window.__LOCALE || (document.documentElement && document.documentElement.lang) || '');
        if (l === seen) return tag;
        seen = l;
        var t = (l === 'ar' || l.indexOf('ar-') === 0) ? 'ar-u-nu-latn' : l;
        try { new Intl.NumberFormat(t); tag = t || undefined; } catch (e) { tag = undefined; }
        return tag;
    };
})();
/* __intlLocale:end */

/* __localYmd:begin */
/* ── Календарная дата «как на часах человека»: 'YYYY-MM-DD' ─────────────────
 * window.__localYmd(d?) — дата момента d (Date, мс или без аргумента = сейчас)
 * в календаре УСТРОЙСТВА. window.__localYmd.addDays(ymd, n) — та же дата ± n
 * дней, без часов и поясов.
 *
 * Зачем. `d.toISOString().slice(0, 10)` — дата по ГРИНВИЧУ. Восточнее него
 * «сегодня» с местной полуночи до часа смещения — это вчера (Москва 00–03,
 * Сеул 00–09, Владивосток 00–10), а местная полночь (`new Date(y, m, d)`)
 * всегда выходит днём раньше. 16.10.2026: заготовка «Новый год» в
 * /countdown-to-date считала до 31 декабря в 6 локалях из 10; тот же приём
 * стоял в ежедневных отметках, сериях и журналах тулов. Дата, которую считают
 * по Гринвичу намеренно (Date.UTC, разбор 'YYYY-MM-DD' как UTC), — не этот
 * случай: там вход и выход в одном календаре.
 * Сторожа — LocalDateIsNotTheGreenwichDateTest, tests/js/local-ymd.test.mjs. */
window.__localYmd = function (d) {
    d = (d == null) ? new Date() : (d instanceof Date ? d : new Date(d));
    if (isNaN(d.getTime())) return '';
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
};
window.__localYmd.addDays = function (ymd, n) {
    var p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
    if (!p) return '';
    // Noon, not midnight: a clock change cannot push noon into another day.
    return window.__localYmd(new Date(+p[1], +p[2] - 1, +p[3] + (n || 0), 12));
};
/* __localYmd:end */

// ── CanvasRenderingContext2D.roundRect polyfill ──
// roundRect was added to the Canvas 2D spec in Chromium 99 (Feb 2022),
// Safari 16, Firefox 113. Older browsers — including Yandex Browser 21
// on Android (Chromium 96 base), still in active use in RU/KZ — throw
// `TypeError: ctx.roundRect is not a function` on the bare call. Many of
// our tool blades (refresh-rate, bpm-key-finder, input-lag-test,
// live-audio, audio-censor, reels-maker, cloud-gaming-readiness, ...)
// call ctx.roundRect directly, some with per-call guards, some without.
// One polyfill here removes the need for any per-tool guard going
// forward and closes TIMBRICA-FRONTEND-9G (Yandex Browser 21.11.4 /
// Android 15 / Oppo CPH2481 / Kazakhstan, /ru/refresh-rate demoLoop).
//
// Spec accepts radius as: a number | [r] | [tl, tr_and_bl] | [tl, tr_and_bl, br]
// | [tl, tr, br, bl]. We honour all four forms — covers every blade caller.
if (typeof CanvasRenderingContext2D !== 'undefined' &&
    typeof CanvasRenderingContext2D.prototype.roundRect !== 'function') {
    CanvasRenderingContext2D.prototype.roundRect = function(x, y, w, h, r) {
        var radii;
        if (typeof r === 'number') {
            radii = [r, r, r, r];
        } else if (Array.isArray(r)) {
            if (r.length === 1)      radii = [r[0], r[0], r[0], r[0]];
            else if (r.length === 2) radii = [r[0], r[1], r[0], r[1]];
            else if (r.length === 3) radii = [r[0], r[1], r[2], r[1]];
            else                     radii = [r[0], r[1], r[2], r[3]];
        } else {
            radii = [0, 0, 0, 0];
        }
        // Clamp each corner to half of the shorter side so adjacent corners
        // can't overlap (matches Chromium's native behaviour).
        var maxR = Math.min(Math.abs(w) / 2, Math.abs(h) / 2);
        var tl = Math.min(Math.max(radii[0], 0), maxR);
        var tr = Math.min(Math.max(radii[1], 0), maxR);
        var br = Math.min(Math.max(radii[2], 0), maxR);
        var bl = Math.min(Math.max(radii[3], 0), maxR);
        this.moveTo(x + tl, y);
        this.lineTo(x + w - tr, y);
        this.quadraticCurveTo(x + w, y, x + w, y + tr);
        this.lineTo(x + w, y + h - br);
        this.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
        this.lineTo(x + bl, y + h);
        this.quadraticCurveTo(x, y + h, x, y + h - bl);
        this.lineTo(x, y + tl);
        this.quadraticCurveTo(x, y, x + tl, y);
    };
}

// ── Shared WASM download progress text formatter ──
// Tools that show "loading engine X%" UI during FFmpegProcessor.init()
// pass `(p) => __wasmDownloadStatus(p, totalMB)` to render consistent
// MB / ETA text — without each tool re-implementing the math.
//
// Returns:
//   {
//     pct:    rounded 0-100 (caller can multiply for their own bar range)
//     text:   localised "Загружаем движок · 12.4 / ~32 MB · ~85 с"
//     etaSec: seconds remaining (null if not yet stable)
//   }
//
// Origin: bug-report 2026-05-01 12:53 — Edge 147 / 4G 2.25 Mbps user
// gave up after 34 s thinking the tool was broken. The bar was on a
// fake 0→18% timer that locked at 18% for 115 s while WASM downloaded
// silently. Real progress + ETA prevents that.
window.__wasmDownloadStatus = (function(){
    // Total combined size of @ffmpeg/core@0.12.10's core.js + core.wasm
    // (~32 MB raw; ~10 MB on the wire after our nginx gzip).
    var DEFAULT_TOTAL_MB = 32;
    var L_LOAD = {ru:'Загружаем движок',en:'Loading engine',de:'Engine wird geladen',ko:'엔진 로딩',pt:'Carregando motor',id:'Memuat mesin',ar:'تحميل المحرك',es:'Cargando el motor',fr:'Chargement du moteur',uk:'Завантажуємо рушій',tr:'Motor yükleniyor'};
    var L_SEC  = {ru:'с',en:'s',de:'s',ko:'초',pt:'s',id:'d',ar:'ث',es:'s',fr:'s',uk:'с',tr:'sn'};
    var startTime = null;
    // Active time (window.__activeNow): a download that stood in a frozen tab
    // did not get slower — on wall time the frozen minutes stayed in `elapsed`.
    // Bound late: this helper is defined above the clock in this file.
    var clock = function(){ return window.__activeNow ? window.__activeNow() : Date.now(); };
    return function(p, opts){
        opts = opts || {};
        var lang = window.__LOCALE || 'en';
        var totalMB = opts.totalMB || DEFAULT_TOTAL_MB;
        if (typeof p !== 'number' || !isFinite(p) || p < 0) p = 0;
        if (p > 1) p = 1;
        if (p > 0 && startTime === null) startTime = clock();
        if (p === 0) startTime = null;
        var pct = Math.round(p * 100);
        var loadedMB = (p * totalMB).toFixed(1);
        var text = (L_LOAD[lang] || L_LOAD.en) + ' · ' + loadedMB + ' / ~' + totalMB + ' MB';
        var etaSec = null;
        if (p > 0.05 && p < 0.98 && startTime !== null) {
            var elapsed = (clock() - startTime) / 1000;
            var remaining = elapsed * (1 - p) / p;
            if (remaining > 5 && remaining < 600) {
                etaSec = Math.round(remaining);
                text += ' · ~' + etaSec + ' ' + (L_SEC[lang] || 's');
            }
        }
        return { pct: pct, text: text, etaSec: etaSec };
    };
})();

// ── Shared job-progress ETA tracker ──
// A bar that moves is not enough: users abandon when they cannot tell a 20-second
// job from a 20-minute one. Origin: bug-report 2026-07-09 22:36 "не работает
// экспорт" on /video-blur — the FFmpeg export never wired onProgress, so the bar
// sat at 0% for the ~23 min a 9-minute clip needed. The job was fine; the UI said
// nothing.
//
// Each call site makes its OWN tracker — unlike __wasmDownloadStatus, whose
// module-level startTime cannot serve two concurrent jobs.
//
//   var eta = window.__makeEta();
//   eta.update(0.42)  →  { pct: 42, etaSec: 80, text: '42% · ~1 мин 20 с' }
//
// ETA is suppressed until the estimate is meaningful (>3 s elapsed, >3% done)
// and smoothed with an EMA so it counts down instead of flickering.
// ─────────────────────────────────────────────────────────────────────────────
// __audioCodecLimits — ЕДИНСТВЕННЫЙ ответчик на «что этот кодек физически может»
//
// Правило одно: «частота — ближайшая поддерживаемая НЕ ВЫШЕ запрошенной, а если
// таких нет — самая низкая из возможных». Данные — из реестра
// config/formats.php, который layout уже инлайнит в window.__FFMPEG_CONFIG__ на
// каждой странице. Здесь только правило, без своей таблицы.
//
// ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ. К 2026-08-13 это правило было написано ТРИЖДЫ:
// audio-native.encoderRateFor (lite-лейн), convert-tool.rateForFormat (план
// конвертера) и — хуже всего — ffmpeg-processor, который вместо правила
// подставлял КОНСТАНТУ 44100 всем mp3-выводам без явного `-ar`. Разъехались они
// предсказуемо: замер 2026-08-13 показал, что 5.1 WAV 48 кГц → mp3 приезжал
// 44.1 кГц на FFmpeg-лейне и 48 кГц на lite-лейне — одна и та же кнопка,
// два разных ответа. Комментарий в AudioFormats::jsSampleRatesMap предупреждал
// ровно об этом («three hand-written copies… a 48 kHz master came back at
// 44.1 kHz in some paths and not others»), но новую копию это не остановило,
// потому что общей ФУНКЦИИ не было — был только общий массив.
//
// ⚠️ Это НЕ замена договорённостям самого FFmpeg. Замерено на нашей WASM-сборке
// 2026-08-13: без `-ar` libmp3lame сам берёт 48000 из источника 192/96/48 кГц и
// сам сводит 5.1 в стерео. Поэтому лейну FFmpeg подсказывать не надо — ему
// нельзя МЕШАТЬ. Резолвер нужен тем, кто должен ПРЕДСКАЗАТЬ результат заранее
// (размер файла, честность лейна) или сконфигурировать не-FFmpeg энкодер
// (WebCodecs, lamejs), у которого никакой автоматики нет.
window.__audioCodecLimits = {
    /** Список разрешённых частот формата (по убыванию) или null — «любая». */
    ratesFor: function (fmt) {
        var cfg = window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.sampleRates;
        if (!cfg) return null;                        // реестра нет — не выдумываем
        var r = cfg[String(fmt || '').toLowerCase()];
        return (r && r.length) ? r : null;            // формата нет в карте = не ограничен
    },
    /** Частота, которую формат реально унесёт при запрошенной `want`. */
    rateFor: function (fmt, want) {
        want = parseInt(want, 10);
        if (!want || want <= 0) return 0;             // источник неизвестен — молчим
        var rates = this.ratesFor(fmt);
        if (!rates) return want;
        if (rates.indexOf(want) !== -1) return want;
        for (var i = 0; i < rates.length; i++) {      // список убывающий
            if (rates[i] < want) return rates[i];
        }
        return rates[rates.length - 1];               // ниже всех — берём минимальную
    },
    /** Потолок каналов формата или 0, когда ограничения нет/неизвестно. */
    maxChannels: function (fmt) {
        var cfg = window.__FFMPEG_CONFIG__ && window.__FFMPEG_CONFIG__.maxChannels;
        if (!cfg) return 0;
        var n = cfg[String(fmt || '').toLowerCase()];
        return (typeof n === 'number' && n > 0) ? n : 0;
    }
};

window.__makeEta = function () {
    // Active time (window.__activeNow): a job that stood in a frozen tab did not
    // get slower — on wall time the frozen minutes stayed in `elapsed`.
    var etaClock = function () { return window.__activeNow ? window.__activeNow() : Date.now(); };
    var L_MIN = { ru:'мин', en:'min', de:'Min', ko:'분', pt:'min', id:'mnt', ar:'د', es:'min', fr:'min', uk:'хв', tr:'dk' };
    var L_SEC = { ru:'с',   en:'s',   de:'s',   ko:'초', pt:'s',   id:'d',   ar:'ث', es:'s',   fr:'s', uk:'с', tr:'sn' };
    var lang = window.__LOCALE || 'en';
    var mn = L_MIN[lang] || L_MIN.en, sc = L_SEC[lang] || L_SEC.en;
    var t0 = null, smooth = null;

    function fmt(sec) {
        if (sec >= 60) {
            var m = Math.floor(sec / 60), s = Math.round(sec % 60);
            return '~' + m + ' ' + mn + (s ? ' ' + s + ' ' + sc : '');
        }
        return '~' + Math.max(1, Math.round(sec)) + ' ' + sc;
    }
    return {
        reset: function () { t0 = null; smooth = null; },
        update: function (p) {
            if (typeof p !== 'number' || !isFinite(p) || p < 0) p = 0;
            if (p > 1) p = 1;
            if (t0 === null && p > 0) t0 = etaClock();
            var pct = Math.round(p * 100), etaSec = null, text = pct + '%';
            if (t0 !== null && p > 0.03 && p < 0.99) {
                var elapsed = (etaClock() - t0) / 1000;
                if (elapsed > 3) {
                    var raw = elapsed * (1 - p) / p;
                    // EMA: a slow first chunk must not pin a wild estimate.
                    smooth = (smooth === null) ? raw : smooth * 0.7 + raw * 0.3;
                    if (smooth > 2 && smooth < 7200) { etaSec = Math.round(smooth); text = pct + '% · ' + fmt(smooth); }
                }
            }
            return { pct: pct, etaSec: etaSec, text: text };
        }
    };
};

// ── WASM health probe (lazy + Web Worker) ──
// Some browsers (e.g. Yandex Browser 26.x on Windows) can download a
// WebAssembly module fine but their `WebAssembly.instantiate()` engine hangs
// forever — every attempt times out at 60 s. Without an early probe the user
// sits through a 10-source × 60-s cascade (≈12 minutes).
//
// LAZY: `window.__wasmHealthy()` is a function that, on first call, kicks off
// the probe and caches the resulting Promise. Bounce-visits never run it.
// Warm-up calls it parallel with the first user interaction.
//
// WORKER-FIRST: probe runs in a Blob-Worker to keep the 3 s timeout race off
// the main thread entirely. Healthy: ~1–3 ms verdict via postMessage. Broken:
// 3 s wall clock with zero main-thread cost (the worker hangs, not us).
// Worker source is 8-byte minimal valid wasm module + standard probe; Blob
// URL is allowed by our CSP `worker-src 'self' blob:`. If Worker construction
// itself fails (CSP misconfig, sandboxed iframe), we fall back to main-thread.
//
// Result cached in `sessionStorage.__wasm_health` so subsequent navigations
// in the same tab reuse the verdict (one probe per session).
// Three-way verdict, NOT a boolean. A probe can be:
//   'ok'      — minimal module instantiated → WASM works.
//   'broken'  — WebAssembly.validate() returned false OR instantiate
//               REJECTED. A real engine defect (no WASM / AV blocks
//               execution synchronously). This won't change within a
//               session → safe to cache sticky and gate init.
//   'timeout' — the probe did not answer within 3 s. This is NOT evidence
//               the engine is broken; it's a scheduling artifact — the
//               worker spawn + postMessage round-trip can exceed 3 s on a
//               loaded/low-RAM device exactly during page-load contention.
//               INCONCLUSIVE → never sticky-broken; the real FFmpeg init
//               (multi-source retry + cascade interceptor) is authoritative.
//
// Why this matters (bug 2026-06-17, valeriitaglin / Edge 149 / dm=4, the
// 2nd time the same user hit it): the old probe collapsed timeout→false,
// cached sessionStorage '0', and FFmpegProcessor.init (health-gated) then
// returned false → "FFmpeg init failed" on EVERY attempt for 6 minutes,
// across 8 retries. The SAME device ran FFmpeg fine 6 min later
// (warmup_ok ms=2711) — the verdict was simply a timed-out probe. A
// 2026-06-09 fix loosened the CASCADE timeout threshold but never touched
// the probe, so the probe re-bricked him. This fixes the real layer.
window.__wasmHealthy = (function(){
    var cached = null;

    function probeMain(){
        var minimal = new Uint8Array([0,0x61,0x73,0x6d,1,0,0,0]);
        if (typeof WebAssembly === 'undefined' || typeof WebAssembly.validate !== 'function' || !WebAssembly.validate(minimal)) {
            return Promise.resolve('broken');
        }
        return Promise.race([
            WebAssembly.instantiate(minimal).then(function(){ return 'ok'; }, function(){ return 'broken'; }),
            new Promise(function(resolve){ setTimeout(function(){ resolve('timeout'); }, 3000); })
        ]).catch(function(){ return 'broken'; });
    }

    function probeWorker(){
        if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
            return null;
        }
        // Worker source: minimal wasm + instantiate + report. Single string,
        // no template literals (older browsers). Self-contained.
        var src =
            'self.onmessage=function(){' +
                'try{' +
                    'var m=new Uint8Array([0,97,115,109,1,0,0,0]);' +
                    'if(!self.WebAssembly||!self.WebAssembly.validate||!self.WebAssembly.validate(m)){' +
                        'self.postMessage({ok:false,reason:"validate"});return;' +
                    '}' +
                    'self.WebAssembly.instantiate(m).then(function(){' +
                        'self.postMessage({ok:true});' +
                    '},function(e){' +
                        'self.postMessage({ok:false,reason:"instantiate-rejected",err:(e&&e.message)||""});' +
                    '});' +
                '}catch(e){' +
                    'self.postMessage({ok:false,reason:"worker-throw",err:(e&&e.message)||""});' +
                '}' +
            '};';
        var url, w;
        try {
            url = URL.createObjectURL(new Blob([src], { type: 'application/javascript' }));
            w = new Worker(url);
        } catch (_) {
            try { if (url) URL.revokeObjectURL(url); } catch (_) {}
            return null;
        }
        return new Promise(function(resolve){
            var done = false;
            function finish(verdict){
                if (done) return;
                done = true;
                try { w.terminate(); } catch (_) {}
                try { URL.revokeObjectURL(url); } catch (_) {}
                resolve(verdict);
            }
            // No answer in 3 s = inconclusive (worker queued behind page-load
            // work), NOT broken. w.onerror = couldn't run the probe at all
            // (CSP/sandbox) — also inconclusive; let the real init decide.
            var timer = setTimeout(function(){ finish('timeout'); }, 3000);
            w.onmessage = function(ev){ clearTimeout(timer); finish(ev && ev.data && ev.data.ok ? 'ok' : 'broken'); };
            w.onerror   = function(){ clearTimeout(timer); finish('timeout'); };
            try { w.postMessage('probe'); } catch (_) { clearTimeout(timer); finish('timeout'); }
        });
    }

    var fn = function(){
        if (cached) return cached;
        try {
            var stored = sessionStorage.getItem('__wasm_health');
            if (stored === '1') return cached = Promise.resolve(true);
            if (stored === '0') return cached = Promise.resolve(false);
        } catch (_) { /* sessionStorage blocked — probe afresh */ }
        var t0 = (performance && performance.now) ? performance.now() : Date.now();
        var path = 'worker';
        var p = probeWorker();
        if (!p) { path = 'main'; p = probeMain(); }
        cached = p.then(function(verdict){
            var ms = Math.round(((performance && performance.now) ? performance.now() : Date.now()) - t0);
            if (verdict === 'ok') {
                try { sessionStorage.setItem('__wasm_health', '1'); } catch (_) {}
                try { window.__ae && window.__ae('feature_use', { slug: 'wasm', f: 'health_probe', outcome: 'ok', path: path, ms: ms }); } catch (_) {}
                return true;
            }
            if (verdict === 'broken') {
                try { sessionStorage.setItem('__wasm_health', '0'); } catch (_) {}
                try { window.__aeCritical && window.__aeCritical('wasm_health_broken', { ms: ms, path: path }); } catch (_) {}
                return false;
            }
            // 'timeout' — inconclusive. Do NOT persist; resolve OPTIMISTIC so
            // the real init runs. If the engine is genuinely broken-but-slow,
            // the real cascade will fail and the cascade interceptor marks it
            // broken one attempt later. The common case (slow device, working
            // engine) is no longer bricked.
            try { window.__ae && window.__ae('feature_use', { slug: 'wasm', f: 'health_probe', outcome: 'inconclusive', path: path, ms: ms }); } catch (_) {}
            return true;
        });
        return cached;
    };
    // Manual override / recovery: clears the module-level cache so a stale
    // verdict can't outlive a user override. The OLD "Try anyway" button only
    // wrote sessionStorage='1' — but this cached promise short-circuits before
    // reading sessionStorage, so the override was silently ineffective and the
    // user stayed bricked. optimistic=true (the override) also primes
    // sessionStorage so the next call resolves true without re-probing.
    fn.reset = function(optimistic){
        cached = null;
        try {
            if (optimistic) sessionStorage.setItem('__wasm_health', '1');
            else sessionStorage.removeItem('__wasm_health');
        } catch (_) {}
    };
    return fn;
})();
// Top-level alias so callers (the "Try anyway" banner button, tools that
// want to force a re-probe) don't reach into the closure.
window.__wasmHealthReset = function(optimistic){
    try { if (window.__wasmHealthy && window.__wasmHealthy.reset) window.__wasmHealthy.reset(optimistic); } catch (_) {}
};

// ── Global rejection handler for browser-internal noise ──
// Three classes of unhandled Promise rejections we know are NOT bugs:
//   1. `TypeError: Failed to fetch` / NetworkError / Load failed — the
//      user's connection dropped mid-request. Not an app bug; the call
//      site can choose to retry or surface UI, but the unhandled
//      rejection itself is just connectivity noise.
//   2. `AbortError` from AbortController — user navigated away or a
//      component unmounted. Expected.
//   3. `AbortError: The play() request was interrupted...` — documented
//      HTMLMediaElement behaviour when the browser auto-pauses
//      backgrounded media or pause() is called mid-play().
// Suppressing these prevents the noise from reaching Sentry at all,
// without per-callsite refactoring. Other rejections pass through
// untouched, so genuine bugs are still reported.
/* __rejectionNoise:begin */
// ── A graphics card lost UNDER a run is counted here ──
// Every ONNX engine on the JSEP build of ONNX Runtime (ort.min.js — all of them
// but MUSION and RMVPE) reads its answer back through GPUBuffer.mapAsync. When
// the browser loses the card, that readback is rejected in a promise the
// runtime does not await — and the run itself NEVER settles. Measured on a real
// card, 06–07.10.2026 (CDP Browser.crashGpuProcess): this rejection came 1.6,
// 2.3 and 8.4 s after the crash (once 66 s), together with device.lost, and
// session.run() was still pending 150 s later. The rejection is an AbortError,
// so the filter below drops it as noise — and it is the ONLY trace the event
// leaves on a page. Engines with a silence deadline end the run by it 60–120 s
// later (the stem engines: about thirty such endings a day, both domains, 22.09–06.10),
// and nobody knows how many of those are a lost card and how many a slow one.
// So the event is named, once per loss — and the page asks when a card is there
// again: that is what a fallback started at this moment would find.
// No engine acts on it yet (docs/why/primitives/ort-ep-verify.md).
window.__gpuLostUnderRun = (function () {
    var loss = { n: 0, at: 0 };
    return function (msg) {
        var now = Date.now();
        if (loss.at && now - loss.at < 60000) return;      // every readback in flight is rejected at once
        loss.at = now;
        if (++loss.n > 3) return;
        var say = function (data) {
            try {
                if (typeof window.__aeDiagnostic === 'function') window.__aeDiagnostic(data);
                else if (typeof window.__ae === 'function') window.__ae('tool_diagnostic', data);
            } catch (_) {}
        };
        say({ issue: 'gpu_lost_under_run', n: loss.n, msg: String(msg).slice(0, 80) });
        try {
            var gpu = window.navigator && window.navigator.gpu;
            if (!gpu || typeof gpu.requestAdapter !== 'function') return;
            var tries = 0;
            var back = function (ok, err) { say({ issue: 'gpu_back_after_loss', ok: ok ? 1 : 0, s: Math.round((Date.now() - now) / 1000), tries: tries, err: err ? 1 : 0 }); };
            var ask = function () {
                tries++;
                gpu.requestAdapter().then(function (a) {
                    if (a || tries >= 6) { back(!!a, false); return; }
                    window.setTimeout(ask, 10000);
                }, function () { back(false, true); });
            };
            ask();
        } catch (_) {}
    };
})();
window.addEventListener('unhandledrejection', function (event) {
    var r = event.reason;
    if (!r) return;
    var msg = (r.message || String(r)) || '';
    var name = r.name || '';
    // «Failed to execute 'mapAsync' on 'GPUBuffer': A valid external Instance
    // reference no longer exists» / «…The Device was lost before mapping was
    // resolved» — the two wordings Chrome has given a lost card's readback
    // (an AbortError both times; the wording decides, whatever the name)
    if (/mapAsync|GPUBuffer/.test(msg) && /Instance reference|[Dd]evice (?:was|is) lost|lost before mapping/.test(msg)) window.__gpuLostUnderRun(msg);
    // An Emscripten engine that DIED («RuntimeError: Aborted(OOM)», «Aborted()»)
    // is not an AbortController abort: the text filter swallowed exactly the
    // deaths nobody handled, so they never reached Sentry (2026-09-28).
    if (name === 'AbortError' || (/aborted|user aborted|abortcontroller/i.test(msg) && !/Aborted\(/.test(msg))) {
        event.preventDefault();
        return;
    }
    if (name === 'TypeError' && /Failed to fetch|NetworkError when attempting|Load failed|Network request failed/i.test(msg)) {
        event.preventDefault();
        return;
    }
    if (/play\(\) request was interrupted|play\(\) was interrupted/i.test(msg)) {
        event.preventDefault();
        return;
    }
});
/* __rejectionNoise:end */

// ── WebAssembly SIMD detection (universal) ──
//
// Synchronous probe — validates a minimal v128 SIMD module. Cached on
// first call so subsequent invocations are free. Browsers that don't
// support WASM SIMD (pre-2008 CPUs, very old mobile Chromium, some
// Yandex Browser builds) return false; modern Chrome/Firefox/Safari
// shipping in 2020+ all return true.
//
// Previously lived inside ffmpeg-processor.js (`window.__hasWasmSIMD`),
// which meant only pages that loaded FFmpeg got the probe. ORT-using
// tools without FFmpeg (e.g., /music-genre-detector, /photo-colorize)
// had to retry-on-catch reactively — wasted a failed SIMD-wasm fetch
// (~5–10 s on slow connections) before falling back to non-SIMD. Now
// any ORT user can probe upfront and call `ort.env.wasm.simd = false`
// before the first `InferenceSession.create`.
//
// Implementation duplicates ffmpeg-processor's bytecode exactly so the
// two probes can't disagree. Same caller idempotently reuses either
// definition. If ffmpeg-processor loads after timbrica-core, the
// `window.__hasWasmSIMD || ...` guard there keeps our definition.
//
// Auditied 2026-06-12 (reference_audio_worker_cors_simd_traps): pattern
// adopted from the apollo+CLAP regex fix to remove copy-paste-based
// risk surface in future tools.
window.__hasWasmSIMD = window.__hasWasmSIMD || (function(){
    var cached;
    return function(){
        if (typeof cached !== 'undefined') return cached;
        try {
            // Canonical wasm-feature-detect module: `i32.const 0; i8x16.splat;
            // drop; end` — i8x16.splat is the EARLIEST SIMD opcode, present in
            // every engine that ships WASM SIMD at all.
            // Source: github.com/GoogleChromeLabs/wasm-feature-detect
            //
            // ⚠ These bytes MUST stay byte-identical to the copy in
            // ffmpeg-processor.js — SimdProbeConsistencyTest asserts it. The
            // copy that used to live here declared a 9-byte code body while
            // v128.const needs 16 bytes of immediate, so the module was
            // malformed and `validate` returned false on EVERY engine (checked
            // on V8: canonical true, this one false). It was latent only
            // because ffmpeg-processor happens to define the probe first on
            // tool pages — one load-order change away from telling every user
            // their CPU is from before 2008. Found 2026-07-30 while chasing a
            // "works until 18:00" report.
            cached = !!(typeof WebAssembly !== 'undefined' &&
                        typeof WebAssembly.validate === 'function' &&
                        WebAssembly.validate(new Uint8Array([
                            0,97,115,109,1,0,0,0,1,4,1,96,0,0,3,2,1,0,
                            10,9,1,7,0,65,0,253,15,26,11
                        ])));
        } catch (_) { cached = false; }
        return cached;
    };
})();

// ── HTMLMediaElement.prototype.play auto-catch ──
// MDN documents that `audio.play()` and `video.play()` return a Promise
// that rejects with AbortError when the browser auto-pauses the media
// (background tab, save-power policy, pause() called before play
// resolved, end-of-playback while play() is in flight). The recommended
// fix is to always `.catch()` the returned promise. Most of our 60+
// tools instantiate `<audio>` / `<video>` and call play() without a
// catch, producing a flood of AbortError events.
//
// Monkey-patch the prototype so EVERY play() returns a promise with at
// least one rejection handler attached. The caller's own `.then` /
// `.catch` chain is unaffected — it still operates on the same
// underlying promise. This kills the entire AbortError class without
// touching individual call sites.
(function () {
    if (!window.HTMLMediaElement || !HTMLMediaElement.prototype.play) return;
    var orig = HTMLMediaElement.prototype.play;
    HTMLMediaElement.prototype.play = function () {
        var p = orig.apply(this, arguments);
        // Caller sites expect `.play().catch(...)` to work. Spec since 2016
        // says play() returns a Promise, but legacy WebKit / browser
        // userscripts that intercept .play can return undefined. Calling
        // `.catch` on undefined throws TypeError: Cannot read properties of
        // undefined (reading 'catch') — Sentry 121353205. Normalize to a
        // Promise so every .play() return value is safe to chain.
        if (p && typeof p.then === 'function') {
            // Silent handler — registers a rejection consumer so the
            // promise is never "unhandled". Caller's own chain still sees
            // the same promise and can handle (or ignore) freely.
            p.catch(function () {});
            return p;
        }
        return Promise.resolve(p);
    };
})();

// ── MediaRecorder mimeType auto-fallback ──
// 30+ tools instantiate `new MediaRecorder(stream, { mimeType: 'audio/webm…' })`.
// Safari (desktop + iOS) does NOT support audio/webm in any form — the
// constructor throws `NotSupportedError: mimeType is not supported.` Each
// tool wrote its own (often broken) fallback ladder; sound-meter for example
// went `audio/webm;codecs=opus → audio/webm`, both of which Safari rejects.
// Sentry 116925399 (Safari 17.14 / macOS, /ru/sound-meter, 2026-05-02).
//
// Wrap the constructor so a requested-but-unsupported mimeType walks a
// kind-aware fallback ladder before constructing. If everything in the
// ladder fails too, omit mimeType entirely and let the browser pick its
// own default (always works on every supported browser). Rejected mimes
// are logged via __ae so we can identify Safari-broken tools to migrate
// off hardcoded .webm filename extensions (cosmetic follow-up — bytes
// are correct, only the saved file extension may not match the format).
(function () {
    if (!window.MediaRecorder) return;
    var Orig = window.MediaRecorder;
    if (typeof Orig.isTypeSupported !== 'function') return;
    var AUDIO_FALLBACKS = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/aac',
        'audio/ogg;codecs=opus',
        'audio/ogg',
    ];
    var videoRecorders = 0;
    var VIDEO_FALLBACKS = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4;codecs=h264,aac',
        'video/mp4',
    ];
    function Patched(stream, options) {
        options = options || {};
        var requested = options.mimeType || '';
        if (requested && !Orig.isTypeSupported(requested)) {
            var kind = requested.split('/')[0];
            var ladder = kind === 'video' ? VIDEO_FALLBACKS : AUDIO_FALLBACKS;
            var picked = '';
            for (var i = 0; i < ladder.length; i++) {
                if (Orig.isTypeSupported(ladder[i])) { picked = ladder[i]; break; }
            }
            try {
                if (window.__ae) {
                    window.__ae('feature_use', {
                        f: 'media_recorder_fallback',
                        requested: requested.slice(0, 60),
                        picked: picked || 'browser-default',
                    });
                }
            } catch (_) {}
            if (picked) options.mimeType = picked;
            else delete options.mimeType; // browser default — always works
        }
        var rec = new Orig(stream, options);
        /* Field instrument for __mediaRecorderWarm below: how long a VIDEO
           recorder takes from start(timeslice) to its first real data. A cold
           Chromium encoder answers in ~2–3 s (its frames until then are
           dropped), a warm one in a few hundred ms. One event per recorder;
           only with a timeslice, since without one data comes at stop(). */
        try {
            var vt = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null;
            if (vt && !options.__tmbWarm) {
                videoRecorders++;
                var nth = videoRecorders, s0 = 0, sent = false, slice = 0;
                var origStart = rec.start;
                rec.start = function (ts) { slice = +ts || 0; s0 = performance.now(); return origStart.apply(rec, arguments); };
                rec.addEventListener('dataavailable', function (e) {
                    if (sent || !s0 || !slice || !e.data || e.data.size < 64) return;
                    sent = true;
                    var st = {};
                    try { st = vt.getSettings ? vt.getSettings() : {}; } catch (_) {}
                    var key = window.__mediaRecorderWarm && window.__mediaRecorderWarm.key
                        ? window.__mediaRecorderWarm.key(st.width, st.height, options.mimeType) : '';
                    if (window.__ae) window.__ae('feature_use', {
                        f: 'media_recorder_first_data', ms: Math.round(performance.now() - s0), slice: slice,
                        w: st.width || 0, h: st.height || 0, nth: nth,
                        warmed: key && window.__mediaRecorderWarm.isWarm(key) ? 1 : 0,
                        mime: String(options.mimeType || '').slice(0, 30),
                    });
                });
            }
        } catch (_) {}
        return rec;
    }
    Patched.prototype = Orig.prototype;
    // Forward static methods so `MediaRecorder.isTypeSupported(…)` etc keep working.
    Patched.isTypeSupported = Orig.isTypeSupported.bind(Orig);
    window.MediaRecorder = Patched;
})();

// ── __mediaRecorderWarm(): the first video recorder in a Chromium tab loses ~2 s ──
// Measured 09.10.2026 (Chrome 150, Windows): the FIRST MediaRecorder of a tab
// encodes its first frame and then drops every frame for 1.2–3.5 s while the
// encoders come up (on a loaded machine the first data took up to 7.7 s); a cold
// CAMERA recording lost 1.6–5 s, twice not one frame. Frames pushed with
// requestFrame() are lost for good: a 92-frame sequence came out with 14–84.
// Firefox: no gap (skipped below); sound-only recorders: no loss.
// Two cold things, measured with a fresh Chrome per trial: the encoder FACTORY
// of the tab, and the encoder INSTANCE of each new recorder — Chromium keeps
// ~10 frames queued and drops the rest while the instance comes up (on the
// heavy /sample-video page the frames from ~0.35 s on: 9–40 lost, gaps up to
// 1.3 s). A throwaway recorder that actually encodes warms both: on a blank
// page 0.05 s left, camera 0.06–0.08 s, requestFrame 90 of 92 (cold 14–84);
// on /sample-video 0 bursts in 8 (max gap 0.25 s). VideoEncoder.
// isConfigSupported() warms only the factory: equal on a blank page, but 6
// bursts in 8 on /sample-video and a camera at 0.95 s once in 3 — so it only
// rides along. Rejected too (8 runs each): a throwaway kept alive until the
// real recorder answers (2 bursts — the instances compete), VP8 for the real
// recording (2 spread drops of 26–30 frames), starting the recorder before the
// timeline (6 bursts plus a frozen 0.6–2.5 s intro). Waiting or
// isTypeSupported() warm nothing. The throwaway runs on a clone of the live
// track when { stream } is given (a same-size canvas left a camera 0.5 s),
// else on a synthetic canvas of the recording's size (96×54 left 1.2 s).
//   { stream, mimeType, videoBitsPerSecond } — a live track: camera, screen,
//       a remote call, a canvas that keeps drawing; size from its settings;
//   { width, height, mimeType, videoBitsPerSecond } — an export.
// One warm-up per size+codec (or track) and tab, until it answers (≤ 20 s);
// every later call gets the same promise. An export awaits it before start();
// a live tool calls it the moment its picture exists and, on Record, awaits it
// with { maxWaitMs } (≤ ~1.5 s: a click's activation lasts ~5 s —
// requestFullscreen() after a 6 s await fails). Never rejects.
// Field instrument: media_recorder_first_data (in the constructor wrapper above).
// Guard: MediaRecorderStartsWarmTest; why: docs/why/primitives/media-recorder-warm.md.
(function () {
    var warms = Object.create(null), ok = Object.create(null);
    function base(mime) { return String(mime || '').split(';')[0] || 'default'; }
    function key(w, h, mime) { return (Math.round(+w) || 0) + 'x' + (Math.round(+h) || 0) + '|' + base(mime); }
    function codecFor(mime) {
        var m = String(mime || '').toLowerCase();
        if (/avc1|h264|video\/mp4/.test(m)) return 'avc1.640033';
        if (/vp8/.test(m)) return 'vp8';
        return 'vp09.00.10.08';
    }
    function run(k, sizeKey, w, h, mime, bps, track) {
        return new Promise(function (resolve) {
            var t0 = performance.now(), done = false, rec = null, st = null, tick = 0, cap = 0;
            function finish(good, how) {
                if (done) return;
                done = true;
                clearInterval(tick); clearTimeout(cap);
                try { if (rec && rec.state !== 'inactive') rec.stop(); } catch (_) {}
                try { if (st) st.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
                if (good) { ok[k] = 1; ok[sizeKey] = 1; }
                var ms = Math.round(performance.now() - t0);
                try { if (window.__ae) window.__ae('feature_use', { f: 'media_recorder_warm', ms: ms, ok: good ? 1 : 0, how: how || '', w: w, h: h, src: track ? 'track' : 'size', hid: document.hidden ? 1 : 0 }); } catch (_) {}
                resolve({ ms: ms, ok: !!good, how: how || '' });
            }
            function throwaway() {
                try {
                    if (!window.MediaRecorder) { finish(false, 'none'); return; }
                    if (track) {
                        st = new MediaStream([track.clone()]);
                    } else {
                        if (!window.HTMLCanvasElement || !HTMLCanvasElement.prototype.captureStream) { finish(false, 'none'); return; }
                        var c = document.createElement('canvas');
                        c.width = w; c.height = h;
                        var g = c.getContext('2d');
                        st = c.captureStream(30);
                        // The capture only produces frames while the canvas changes.
                        var paint = function () { g.fillStyle = 'hsl(' + (Math.round((performance.now() - t0) / 4) % 360) + ',60%,50%)'; g.fillRect(0, 0, w, h); };
                        paint();
                        tick = setInterval(paint, 33);
                    }
                    var o = { __tmbWarm: true };   // not counted by the instrument
                    if (mime) o.mimeType = mime;
                    if (bps) o.videoBitsPerSecond = bps;
                    rec = new MediaRecorder(st, o);
                    rec.ondataavailable = function (e) { if (e.data && e.data.size >= 64) finish(true, 'rec'); };
                    rec.onerror = function () { finish(false, 'rec'); };
                    rec.start(100);
                } catch (_) { finish(false, 'rec'); }
            }
            cap = setTimeout(function () { finish(false, 'cap'); }, 20000);
            var VE = window.VideoEncoder, asked = null;
            if (VE && typeof VE.isConfigSupported === 'function') {
                // The answer does not matter — asking is what brings the factory up.
                var ask = function (cfg) { try { return VE.isConfigSupported(cfg).then(function () {}, function () {}); } catch (_) { return Promise.resolve(); } };
                asked = Promise.all([
                    ask({ codec: codecFor(mime), width: w || 1280, height: h || 720, bitrate: bps || 4000000, framerate: 30 }),
                    ask({ codec: 'avc1.42e028', width: 1920, height: 1080, bitrate: 4000000, framerate: 30, hardwareAcceleration: 'prefer-hardware' })
                ]);
            }
            // The question alone is not enough on a real page: it brings the
            // factory up, but the encoder INSTANCE of the next recorder still
            // took ~1 s and dropped everything past its ~10 queued frames (6 of
            // 8 runs on /sample-video); a throwaway that actually encoded left
            // 0 of 8. So the throwaway always runs; the question rides along.
            void asked;
            throwaway();
        });
    }
    /** opts: { stream } | { width, height }, plus mimeType, videoBitsPerSecond,
     *  and maxWaitMs to stop waiting (not warming) after that long. */
    // Firefox: no gap (0.04–0.05 s cold, 3 of 3) — nothing to warm, nothing to wait for.
    var gecko = /\bFirefox\/\d/.test(navigator.userAgent || '');
    var door = function (opts) {
        opts = opts || {};
        if (gecko) return Promise.resolve({ ms: 0, ok: true, skipped: 'gecko' });
        var p, track = null;
        try {
            var vt = opts.stream && opts.stream.getVideoTracks ? opts.stream.getVideoTracks()[0] : null;
            if (vt && vt.readyState === 'live') track = vt;
        } catch (_) {}
        if (track) {
            var set = {};
            try { set = track.getSettings ? track.getSettings() : {}; } catch (_) {}
            var tk = 'track:' + track.id;
            p = warms[tk] || (warms[tk] = run(tk, key(set.width, set.height, opts.mimeType), set.width || 0, set.height || 0, opts.mimeType || '', +opts.videoBitsPerSecond || 0, track));
        } else {
            var w = Math.max(16, Math.min(4096, Math.round(+opts.width) || 1280));
            var h = Math.max(16, Math.min(4096, Math.round(+opts.height) || 720));
            var k = key(w, h, opts.mimeType);
            p = warms[k] || (warms[k] = run(k, k, w, h, opts.mimeType || '', +opts.videoBitsPerSecond || 0, null));
        }
        var cap = +opts.maxWaitMs;
        if (!(cap > 0)) return p;
        return Promise.race([p, new Promise(function (r) { setTimeout(function () { r({ ms: cap, ok: false, waited: true }); }, cap); })]);
    };
    door.key = key;
    door.isWarm = function (k) { return !!ok[k]; };
    window.__mediaRecorderWarm = door;
})();

// ── Shared-module bootstrap registry ──
// `<script defer src="…/foo.js" onerror="this.src='@lightFallback(…)'">` breaks
// defer ordering once `onerror` swaps src — the new request runs ASYNC and may
// resolve AFTER DOMContentLoaded. Tools calling shared-module APIs inside
// DOMContentLoaded crash with `ReferenceError: X is not defined`
// (Sentry 116943205, /ru/video-compressor, 2026-05-02).
//
// `__sharedModule(name, spec)` pre-registers a stub that captures method calls
// (or returns a default value for query methods) until the real script loads.
// `__drainSharedModule(name)` re-invokes queued calls against the real
// implementation when the script finally arrives.
//
// Usage in layout (BEFORE the deferred <script>):
//   __sharedModule('ChannelModule', {
//       init: undefined,         // → queue invocation
//       detect: undefined,
//       getArgs: [],             // → return [] sync
//       robust: Promise.reject(new Error('not loaded')), // → return that Promise
//   });
//
// At end of the real module's source:
//   window.MyModule = realImpl;
//   __drainSharedModule('MyModule');
//
// This pattern fits modules whose surface is { method(): action } /
// { method(): Promise } / { getter(): constant }. It does NOT fit modules
// that export classes (used with `new` / `instanceof`) or free constants —
// those need per-call-site `typeof X === 'function'` defense or
// reliable-load guarantees (preload, inline).
window.__sharedModule = function (name, spec) {
    if (window[name]) return; // real already loaded — no-op
    var queue = [];
    window['__' + name + 'Queue'] = queue;
    var stub = { __isStub: true };
    Object.keys(spec).forEach(function (method) {
        var defaultReturn = spec[method];
        if (defaultReturn === undefined) {
            // Action method — queue the call, ignore return value.
            stub[method] = function () {
                queue.push({ kind: 'action', m: method, a: arguments });
            };
        } else if (defaultReturn === '__defer_promise__') {
            // Promise-returning method — return a *deferred* promise that
            // resolves/rejects when the real impl drains the queue. If real
            // impl never arrives (script truly fails to load), a 15s
            // timeout rejects so the caller's `.catch` fires with a
            // descriptive error instead of hanging the UI forever.
            stub[method] = function () {
                var args = arguments;
                return new Promise(function (resolve, reject) {
                    var entry = { kind: 'promise', m: method, a: args, resolve: resolve, reject: reject };
                    entry.timer = setTimeout(function () {
                        reject(new Error(name + '.' + method + ' failed: module never loaded (15s timeout)'));
                    }, 15000);
                    queue.push(entry);
                });
            };
        } else {
            // Sync query method — return the supplied default value.
            stub[method] = function () { return defaultReturn; };
        }
    });
    window[name] = stub;
};

window.__drainSharedModule = function (name) {
    var q = window['__' + name + 'Queue'];
    if (q && q.length) {
        q.forEach(function (entry) {
            try {
                if (entry.kind === 'promise') {
                    clearTimeout(entry.timer);
                    Promise.resolve(window[name][entry.m].apply(window[name], entry.a))
                        .then(entry.resolve, entry.reject);
                } else {
                    var fn = window[name][entry.m];
                    if (typeof fn === 'function') fn.apply(window[name], entry.a);
                }
            } catch (e) {
                if (entry.reject) entry.reject(e);
                /* else: action method threw — already its own concern */
            }
        });
    }
    window['__' + name + 'Queue'] = null;
    try {
        document.dispatchEvent(new CustomEvent(name + ':ready'));
    } catch (_) { /* ancient browsers without CustomEvent — silent */ }
};

/**
 * Same defense for *free-function* modules (`window.__pdfReady`,
 * `window.__videoThumbFallback`, `window.__Mp4BoxProbe`) where the export
 * is a single function, not a namespaced object. Only Promise-returning
 * functions are supported here — sync setters (`window.__pdfSetFont`)
 * have side effects that can't be deferred without breaking caller logic.
 */
window.__sharedFunction = function (name) {
    if (typeof window[name] === 'function' && !window[name].__isStub) return;
    var queue = [];
    window['__' + name + 'Queue'] = queue;
    window[name] = function () {
        var args = arguments;
        return new Promise(function (resolve, reject) {
            var entry = { args: args, resolve: resolve, reject: reject };
            entry.timer = setTimeout(function () {
                reject(new Error(name + ' failed: function never loaded (15s timeout)'));
            }, 15000);
            queue.push(entry);
        });
    };
    // Mark the stub so loaders (mp4box-probe.js, video-thumb-fallback.js,
    // pdf-fonts.js) can distinguish stub-vs-real. Without this, their
    // `if (window.__X) return;` early-returns see the stub, bail, and the
    // real impl is never installed → all queued calls reject after 15 s.
    // Cross-tool bug confirmed 2026-05-07 affecting __Mp4BoxProbe on
    // enhance-video pre-trim modal + video-fps fps probe.
    window[name].__isStub = true;
};
window.__drainSharedFunction = function (name) {
    var q = window['__' + name + 'Queue'];
    if (q && q.length) {
        q.forEach(function (entry) {
            try {
                clearTimeout(entry.timer);
                Promise.resolve(window[name].apply(null, entry.args))
                    .then(entry.resolve, entry.reject);
            } catch (e) { entry.reject(e); }
        });
    }
    window['__' + name + 'Queue'] = null;
    try {
        document.dispatchEvent(new CustomEvent(name + ':ready'));
    } catch (_) {}
};

// ── HTMLMediaElement.currentTime non-finite guard ──
// 20+ tools use the pattern `audioEl.currentTime = pct * audioEl.duration`
// in seek-bar handlers. When the user touches the bar BEFORE metadata is
// loaded, `duration` is NaN; multiplying gives NaN; the setter throws
// `TypeError: The provided double value is non-finite.` (Sentry 116909386
// & sibling, /ru/audio-enhancer on Yandex Android, 2026-05-01).
//
// Override the prototype setter to silently no-op on non-finite or
// negative values. Bad inputs become no-ops; good inputs pass through
// unchanged. Defense in one place protects every <audio>/<video> element
// across every tool, including future code that doesn't know to guard.
(function () {
    if (!window.HTMLMediaElement) return;
    var desc = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, 'currentTime');
    if (!desc || !desc.set) return;
    var origSet = desc.set;
    try {
        Object.defineProperty(HTMLMediaElement.prototype, 'currentTime', {
            get: desc.get,
            set: function (v) {
                if (typeof v !== 'number' || !isFinite(v) || v < 0) return;
                return origSet.call(this, v);
            },
            configurable: true,
            enumerable: desc.enumerable,
        });
    } catch (_) { /* some embedded WebViews freeze prototype — leave native */ }
})();

// ── AudioContext / OfflineAudioContext safety stub ──
// 50+ tools use `new (window.AudioContext || window.webkitAudioContext)(...)`
// directly. On rare browser variants without WebAudio (some Yandex Browser
// builds, embedded WebViews, privacy-mode Safari) the expression evaluates
// to `undefined`, then `new undefined(...)` throws an opaque
// `TypeError: undefined is not a constructor`. Users see a broken tool
// with no explanation; Sentry sees a noisy stack trace pointing at every
// tool that uses audio.
//
// Replace the missing constructors with a stub that surfaces a user-facing
// toast and throws a named `AudioContextUnavailable` error. Sentry filters
// the named error so it stops cluttering the dashboard, and the user gets
// actionable feedback instead of a silent-broken tool.
(function () {
    if (window.AudioContext && window.OfflineAudioContext) return;
    // Phase Y (2026-06-04) — iOS Safari ≤14 ships ONLY prefixed
    // webkitAudioContext / webkitOfflineAudioContext. Pre-fix this guard
    // installed SafeAC (throw-stub) on `window.AudioContext` even when
    // `window.webkitAudioContext` was present, then downstream
    // `new (window.AudioContext || window.webkitAudioContext)()` hit the
    // throw-stub FIRST → 22 events/7d on text-to-speech ("Этому
    // инструменту нужен Web Audio API"), broken for every iOS user.
    // Map prefixed → unprefixed FIRST. SafeAC is installed only when the
    // browser truly has neither (rare — IE11 / very old Android browsers).
    if (!window.AudioContext && typeof window.webkitAudioContext === 'function') {
        window.AudioContext = window.webkitAudioContext;
    }
    if (!window.OfflineAudioContext && typeof window.webkitOfflineAudioContext === 'function') {
        window.OfflineAudioContext = window.webkitOfflineAudioContext;
    }
    if (window.AudioContext && window.OfflineAudioContext) return;
    // Флаг для тех, кому звук не обязателен (игры): спросить его ДО `new`, а не
    // ловить тост. Сам конструктор ниже обёрнут пулом контекстов в Proxy, и по
    // нему заглушку от настоящего не отличить.
    window.__webAudioMissing = !window.AudioContext;
    var SafeAC = function () {
        var ru = (window.__LOCALE || document.documentElement.lang || '').slice(0, 2).toLowerCase() === 'ru';
        var msg = ru
            ? 'Этому инструменту нужен Web Audio API. Откройте сайт в Chrome / Firefox или обновите браузер.'
            : 'This tool needs Web Audio API. Open the site in Chrome / Firefox or update your browser.';
        try { if (typeof window.__toast === 'function') window.__toast(msg, 'error'); } catch (e) {}
        var err = new Error(msg);
        err.name = 'AudioContextUnavailable';
        err.__userNotified = true;   // тост выше уже показан — второй, от window.onerror, лишний
        throw err;
    };
    // `isStub`: a page whose sound is one lane among several (/ai-label) asks it and skips
    // the lane quietly instead of showing «this tool needs Web Audio» over a finished result.
    SafeAC.isStub = true;
    if (!window.AudioContext) window.AudioContext = SafeAC;
    if (!window.webkitAudioContext) window.webkitAudioContext = SafeAC;
    if (!window.OfflineAudioContext) window.OfflineAudioContext = SafeAC;
    if (!window.webkitOfflineAudioContext) window.webkitOfflineAudioContext = SafeAC;
})();

// ── Focus trap utility for modals ──
window.__focusTrap=(function(){
    var FOCUSABLE='a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    function trap(container,onEscape){
        var prev=document.activeElement;
        function handler(e){
            if(e.key==='Escape'){if(onEscape)onEscape();return;}
            if(e.key!=='Tab')return;
            var els=Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function(el){return el.offsetParent!==null});
            if(!els.length)return;
            var first=els[0],last=els[els.length-1];
            if(e.shiftKey){if(document.activeElement===first){e.preventDefault();last.focus()}}
            else{if(document.activeElement===last){e.preventDefault();first.focus()}}
        }
        container.addEventListener('keydown',handler);
        var first=container.querySelectorAll(FOCUSABLE);
        for(var i=0;i<first.length;i++){if(first[i].offsetParent!==null){first[i].focus();break;}}
        return function release(){container.removeEventListener('keydown',handler);if(prev&&prev.focus)prev.focus();}
    }
    return{trap:trap};
})();

// ── In-app WebView detection ──
// Google Search App, Instagram, Facebook, TikTok etc. use limited WebViews
// that don't support AudioWorklet, SharedArrayBuffer, or file downloads
(function(){
    var ua=navigator.userAgent;
    /* Two different questions, deliberately answered by two flags.
     *
     * isWebView — "this ENGINE is missing things we need" (AudioWorklet, SAB,
     *   downloads). Earns the persistent copy-the-link banner on every tool page.
     *
     * isInApp — "this browser has no chrome the user can reach": no address bar,
     *   no lock icon, no site-settings menu. The engine may be perfectly modern
     *   (YaSearchApp is Chromium 148), so it gets NO banner — but when something
     *   needs a browser-level permission, every instruction we have about
     *   padlocks and shields is useless there and must be replaced.
     *
     * ⚠️ Why this matters more than the list length suggests: on the RU domain
     * one mobile page view in six comes from the Yandex Search app's in-app
     * browser (11 126 of 66 878 in a day), and the detection above scored it as
     * an ordinary browser — as it did every VK and OK in-app view. So the
     * mic-denied help offered "click the Protect shield next to the address bar"
     * to people who have no address bar, and 0 of 32 mobile sessions that hit a
     * denial ever recovered. The old list caught 83 views in that same day.
     *
     * ⚠️ The Yandex app speaks TWO user-agent dialects, and until 2026-09-26
     * this list knew one: `… YaSearchBrowser/x BroPP/1.0 YaSearchApp/x webOmni`
     * and the newer `… YaApp_Android/x YaSearchBrowser/x BroPP/1.0` with
     * neither YaSearchApp nor webOmni — a quarter of the app's traffic (RU
     * nginx, one day: 30 738 page views against 97 178). The download relay's
     * detector knew both; this one and the telemetry's __brOf did not, so the
     * same person was "in-app" for downloads and "an ordinary Chrome" for
     * every help text and every dashboard (support chat 2026-09-25). The token
     * set is shared on purpose — YandexAppUaIsOneRuleTest holds the three
     * detectors to it.
     */
    var isWebView=/GSA\/|FBAN\/|FBAV\/|Instagram|Line\/|Snapchat|TikTok|BytedanceWebview|MicroMessenger/i.test(ua);
    var isInApp=isWebView||/YaSearchApp|YaSearchBrowser|YaApp_Android|webOmni|VKAndroidApp|OKAndroid|\bwv\b/i.test(ua);
    var isToolPage=!!document.querySelector('meta[name="tool-slug"]');
    if(isWebView&&isToolPage){
        var hint=document.createElement('div');
        hint.style.cssText='padding:14px 16px;margin:0 auto 16px;max-width:900px;background:rgba(251,191,36,.1);border:1px solid rgba(251,191,36,.25);border-radius:12px;font-size:13px;color:var(--warning,#f59e0b);text-align:center;line-height:1.6';
        var loc=window.__LOCALE||document.documentElement.lang||'en';
        var msgs={
            en:['You are using an in-app browser with limited features. Copy the link and open in <b>Safari</b> or <b>Chrome</b>.','Copy link','Copied!'],
            ru:['Вы используете встроенный браузер с ограничениями. Скопируйте ссылку и откройте в <b>Safari</b> или <b>Chrome</b>.','Скопировать ссылку','Скопировано!'],
            ko:['인앱 브라우저는 기능이 제한됩니다. 링크를 복사하여 <b>Safari</b> 또는 <b>Chrome</b>에서 열어주세요.','링크 복사','복사됨!'],
            ar:['أنت تستخدم متصفحاً مدمجاً بميزات محدودة. انسخ الرابط وافتحه في <b>Safari</b> أو <b>Chrome</b>.','نسخ الرابط','تم النسخ!'],
            id:['Anda menggunakan browser in-app dengan fitur terbatas. Salin tautan dan buka di <b>Safari</b> atau <b>Chrome</b>.','Salin tautan','Disalin!'],
            pt:['Você está usando um navegador integrado com recursos limitados. Copie o link e abra no <b>Safari</b> ou <b>Chrome</b>.','Copiar link','Copiado!'],
            de:['Sie verwenden einen In-App-Browser mit eingeschränkten Funktionen. Kopieren Sie den Link und öffnen Sie ihn in <b>Safari</b> oder <b>Chrome</b>.','Link kopieren','Kopiert!'],
            es:['Estás usando un navegador integrado con funciones limitadas. Copia el enlace y ábrelo en <b>Safari</b> o <b>Chrome</b>.','Copiar enlace','¡Copiado!'],
            fr:['Vous utilisez un navigateur intégré aux fonctions limitées. Copiez le lien et ouvrez-le dans <b>Safari</b> ou <b>Chrome</b>.','Copier le lien','Copié !'],
            uk:['Ви користуєтесь вбудованим браузером з обмеженнями. Скопіюйте посилання та відкрийте у <b>Safari</b> або <b>Chrome</b>.','Скопіювати посилання','Скопійовано!'],
            tr:['Sınırlı özelliklere sahip bir uygulama içi tarayıcı kullanıyorsunuz. Bağlantıyı kopyalayıp <b>Safari</b> veya <b>Chrome</b>’da açın.','Bağlantıyı kopyala','Kopyalandı!']
        };
        var m=msgs[loc]||msgs.en;
        hint.innerHTML='⚠️ '+m[0]+'<br><button id="wvCopyBtn" style="margin-top:8px;padding:8px 20px;background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#6366f1));color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;min-height:44px">'+m[1]+'</button>';
        var main=document.querySelector('.main-content .container');
        if(main&&main.firstElementChild)main.insertBefore(hint,main.firstElementChild.nextSibling);
        document.getElementById('wvCopyBtn').addEventListener('click',function(){
            var btn=this;
            navigator.clipboard.writeText(location.href).then(function(){
                btn.textContent=m[2];btn.style.background='var(--success,#22c55e)';
                setTimeout(function(){btn.textContent=m[1];btn.style.background=''},2000);
            }).catch(function(){
                // Fallback for older WebViews
                var t=document.createElement('textarea');t.value=location.href;t.style.cssText='position:fixed;left:-9999px';
                document.body.appendChild(t);t.select();document.execCommand('copy');document.body.removeChild(t);
                btn.textContent=m[2];btn.style.background='var(--success,#22c55e)';
                setTimeout(function(){btn.textContent=m[1];btn.style.background=''},2000);
            });
        });
    }
    window.__isWebView=isWebView;
    window.__isInAppBrowser=isInApp;
})();

// ── iOS AudioContext keep-alive ──
// iOS Safari suspends AudioContext after inactivity. Resume on any user gesture.
(function(){
    var _origACtor=window.AudioContext||window.webkitAudioContext;
    if(!_origACtor)return;
    var _allContexts=[];
    var Wrapped=function(opts){
        var ctx=opts?new _origACtor(opts):new _origACtor();
        _allContexts.push(ctx);
        watchCtx(ctx);
        /* Страница правда играет Web Audio — только тогда iOS-модуль включает
           звук при беззвучном режиме (ios-audio-unmute.js). Раньше он включался на
           первое касание ЛЮБОЙ страницы и останавливал музыку человека даже на
           калькуляторе. Модуль грузится позже (defer) — флаг он прочтёт сам. */
        try{window.__tmbWebAudioUsed=true;if(window.__iosAudioUnmute&&window.__iosAudioUnmute.arm)window.__iosAudioUnmute.arm('ctx');}catch(_){}
        return ctx;
    };
    /* ── Контекст, у которого отняли устройство вывода ────────────────────
     * Chrome (с 122) сообщает о потере устройства событием `error` на
     * AudioContext и переводит его в `suspended`. Замерено 2026-09-02 на
     * настоящем железе (стенд tests/scratch: устройство захвачено в
     * WASAPI-exclusive — так делают DAW и ASIO-драйверы у музыкантов, — и
     * отдельно убит аудио-сервис браузера; Chrome 152, Chromium 128, Yandex 26):
     *   · после `error` контекст молчит, часы стоят, `createBufferSource()
     *     .start()` НЕ бросает — тул рисует «играет» и молчит, телеметрии ноль
     *     (третья форма класса: `closed` 04.08 и NaN-время 22.08 — первые две);
     *   · `resume()` на таком контексте ЗАВИСАЕТ НАВСЕГДА, пока устройство
     *     занято, и остаётся зависшим ПОСЛЕ его освобождения — контекст мёртв
     *     до перезагрузки страницы; убитый аудио-сервис, напротив, лечится
     *     первым же `resume()`;
     *   · новый контекст после освобождения устройства играет сразу.
     * Поэтому событие помечает контекст мёртвым: `ctxUsable` его больше не
     * признаёт, оба автопробуждения (по жесту и по возврату во вкладку) его
     * не трогают — их `resume()` и есть то самое «навсегда», — а следующий
     * `__liveAudioCtx()` отдаёт свежий. Сказать человеку, что устройство
     * занято, — работа `__liveAudioCtxReady` ниже: только она знает, помог ли
     * свежий контекст. Пометка — свойство на объекте, а не список: контекст
     * может жить в замыкании тула, где списка не видно. */
    function ctxErrored(c){ try { return !!(c && c.__tmbAudioError); } catch(_) { return false; } }
    /* ⚠️⚠️ OfflineAudioContext — НЕ звуковое устройство, и всё, что ниже
     * написано про «устройство отняли», к нему неприменимо по определению.
     * Он рендерит в буфер, до `startRendering()` живёт в состоянии
     * `suspended` СОЗНАТЕЛЬНО, а `resume()` на нём до старта — ошибка по
     * спецификации («cannot resume an offline context that has not started»).
     * Сторож немоты ниже патчит `AudioBufferSourceNode.prototype.start`, общий
     * у обоих видов контекста, и офлайн-сведение выглядело для него точь-в-точь
     * как «источник поставили, а звука нет»: контекст не `running`, жест на
     * странице был (человек нажал «Экспорт»), вкладка открыта. Дальше шла
     * «починка» `__safeResume`, спецификационный отказ читался как «разбудить
     * не удалось», и человеку показывали красное «устройство вывода занято
     * другой программой (DAW, ASIO-драйвер, звонок)» — ПОСРЕДИ УДАЧНОГО
     * экспорта. Так это и пришло: Татьяна, 10.09.2026, /slideshow-maker на
     * телефоне — `clip_audio_mix mixed:2 silent:0`, файл 78 МБ скачан, музыка
     * на месте, а на экране написано, что браузер не получает доступ к звуку;
     * в поддержку она написала «слайд под музыку из фото теперь недоступно?».
     * Прод за 30 дней: тот же путь на /video-audio (5 сессий), /join (2),
     * /slideshow-maker (2) — у всех троих сведение идёт офлайн и дольше трёх
     * секунд. Признак — `startRendering`: он есть ровно у офлайн-контекста и
     * переживает вендорные префиксы. */
    function isOfflineCtx(c){
        try { return !!c && typeof c.startRendering === 'function'; } catch(_) { return false; }
    }
    /* Улика отказа устройства на этой странице. Нужна тем дверям, у которых
     * своего контекста нет вовсе (отказ `play()` у <audio>/<video>): обвинять
     * звуковую карту можно, только если она себя уже проявила. */
    var lastCtxErrorAt = 0;
    function deviceEvidence(){ return lastCtxErrorAt > 0; }
    /* ── «Устройство ОТНЯЛИ» и «устройство НИКОГДА не открывали» ──────────
     * Зависший `resume()` — улика только у ПЕРВОГО. У второго он норма:
     * контекст, созданный без живого жеста, по политике автозапуска ждёт
     * нажатия и не резолвится, а на телефоне это не редкость, а обычный ход
     * загрузки страницы. Различает их ровно одно — был ли контекст когда-либо
     * `running`, то есть открывали ли устройство вообще. Ничего другого в
     * состоянии нет: у обоих `state === 'suspended'`, оба молчат, оба висят.
     * Замер прода (RU, 01–11.09): `resume_hung` при `suspended` — 2304 сессии
     * с ТЕЛЕФОНОВ против 296 с компьютеров, и у 807 из них зависание
     * случилось РАНЬШЕ первого взаимодействия в сессии, то есть жеста не было
     * вовсе. Каждая такая пометка делала `deviceEvidence()` истинной до конца
     * жизни страницы — и следующий отказ `play()` получал «закройте DAW,
     * ASIO-драйвер или звонок» на устройстве, где ни того, ни другого быть не
     * может (жалоба #3061, /speech-to-text, iPhone: 10 таких тостов за две
     * минуты ДО первого нажатия на микрофон).
     * Это продолжение правила, записанного в `notePlayFailure`: обвинение
     * звуковой карте надо ЗАСЛУЖИТЬ. Там его заслуживал разбор отказа, здесь —
     * сам источник улики. */
    function markRan(c){
        try { if (c && c.state === 'running') c.__tmbRan = 1; } catch(_) {}
    }
    /* Две независимые улики того, что устройство КОГДА-ТО открывали, потому
     * что одной мало. Слушатель `statechange` знает только те контексты, за
     * которыми мы следили с рождения; часы знают любой — `currentTime` растёт
     * лишь пока контекст играет и СОХРАНЯЕТ достигнутое после засыпания.
     * Поэтому тул, который завёл свой контекст, час играл музыку и лишился
     * устройства, будет опознан как «отняли» даже если мы его не watch'или. */
    function everRan(c){
        try {
            if (c && c.__tmbRan) return true;
            var t = c && c.currentTime;
            return typeof t === 'number' && isFinite(t) && t > 0;
        } catch(_) { return false; }
    }
    /* Слушатель ставится один раз на контекст: `running` может наступить и
     * без нашего `resume()` (жест, смена устройства, автопробуждение). */
    function watchRan(c){
        try {
            if (!c || c.__tmbRanWatched) return;
            c.__tmbRanWatched = 1;
            markRan(c);
            c.addEventListener('statechange', function(){ markRan(c); });
        } catch(_) {}
    }
    function markCtxError(c, why){
        if (!c || c.__tmbAudioError) return false;
        var st = '?'; try { st = c.state; } catch(_) {}
        /* Устройство, которое НИКОГДА не открывали, не могли и отнять. Такой
         * контекст исправен и оживёт с первым же нажатием — метить его значит
         * выбросить рабочий контекст И соврать про звуковую карту всем
         * остальным дверям страницы. Отпускание промиса это НЕ отменяет: его
         * делает вызывающий, здесь решается только вопрос улики. */
        if (why === 'resume_hung' && !everRan(c)) {
            try {
                if (c.__tmbPolicyWaitSaid) return false;
                c.__tmbPolicyWaitSaid = 1;
            } catch(_) {}
            reportCtx('resume_policy_wait', { st: st });
            return false;
        }
        try { c.__tmbAudioError = why || 'error'; } catch(_) { return false; }
        lastCtxErrorAt = Date.now();
        reportCtx('error', { why: why || 'error', st: st });
        return true;
    }
    function watchCtx(c){
        try { c.addEventListener('error', function(){ markCtxError(c, 'device'); }); } catch(_) {}
        watchRan(c);
    }
    /* ── Зависший resume() метит контекст — для ВСЕХ, кто зовёт его напрямую ──
     * Общий примитив `__safeResume` есть, но сырых `ctx.resume()` в блейдах
     * 271: переписать их разом нельзя, а без пометки каждый такой тул остаётся
     * с мёртвым контекстом до перезагрузки вкладки. Обёртка ПАССИВНА —
     * возвращает ровно тот промис, что вернул браузер, и ничего не решает за
     * вызывающего. Она только замечает: промис не ответил за HANG_MS, значит
     * устройства нет (замерено — такой resume не отвечает никогда, даже после
     * освобождения устройства). Дальше работает уже написанное: `ctxUsable`
     * не признаёт помеченный контекст, автопробуждения его выбрасывают, и
     * СЛЕДУЮЩЕЕ нажатие получает свежий контекст вместо немого.
     * 6 с — вдвое больше самого медленного честного пробуждения (открытие
     * устройства ОС 544–1271 мс, замер 2026-07-30), поэтому ложных пометок
     * тут не бывает: настоящий отказ политики автозапуска реджектится сразу.
     * ⚠️ Оборачиваем метод ПРОТОТИПА, а не глобал `AudioContext` (его имя
     * трогать нельзя — feedback_no_native_global_overwrite) и не
     * OfflineAudioContext (у него свой resume и своя семантика). */
    var HANG_MS = 6000;
    function docHidden(){
        try { return typeof document !== 'undefined' && document.visibilityState === 'hidden'; } catch(_) { return false; }
    }
    // Слушатель «вкладку прятали» на время ожидания. Возвращает функцию снятия:
    // забытый слушатель на каждом resume() — это утечка на долгоживущей странице.
    function onHidden(fn){
        function h(){ if (docHidden()) fn(); }
        try { document.addEventListener('visibilitychange', h); } catch(_) { return function(){}; }
        return function(){ try { document.removeEventListener('visibilitychange', h); } catch(_) {} };
    }
    (function(){
        var proto = _origACtor && _origACtor.prototype;
        if (!proto || typeof proto.resume !== 'function' || proto.__tmbResumeWrapped) return;
        var orig = proto.resume;
        // Наблюдатель за ЛЮБЫМ промисом пробуждения: и за тем, что вернул
        // браузер, и за тем, что подсунул сторож (`__tmbResumeHangMs` — ручка
        // потолка, чтобы проверка не ждала шесть секунд).
        window.__tmbWatchResume = function(ctx, p){
            /* ⏱ ПОТОЛОК ДЛЯ ВСЕХ, а не только пометка. Раньше здесь стояло
             * `return p` — обёртка МЕТИЛА зависший контекст, но отдавала
             * вызывающему тот же вечный промис. Для 47 мест вида
             * `await ctx.resume()` без потолка это означало, что функция,
             * которая его ждёт, не продолжится НИКОГДА: у /audio-player так
             * замирал весь плеер (через resume идёт ensureEngine, которую
             * ждут 19 мест) — живой интерфейс, ни трека, ни звука, ни
             * сообщения.
             * Масштаб замерен 09.09.2026 по проду за 30 дней:
             * `audio_ctx_error why=resume_hung` — 3004 события, **2508
             * сессий** (~84 в сутки). Это не редкость, а самая частая форма
             * класса.
             * Поэтому промис ограничен по времени. Здоровое пробуждение и
             * честный отказ проходят НАСКВОЗЬ, байт в байт (гонку выигрывает
             * `p`); меняется ровно один случай — «не ответил никогда» — и
             * становится «не ответил за HANG_MS». Дальше тул продолжается,
             * а немоту подхватывают уже написанные двери: контекст помечен,
             * `ctxUsable` его не признаёт, а поставленный в него источник
             * поднимет `silent_play` (разбудит или назовёт отказ).
             * ⚠️ РЕЗОЛВ, а не реджект: у большинства этих мест нет
             * `try/catch` (`if (ctx.state === 'suspended') await ctx.resume();`),
             * и реджект оборвал бы их функцию посреди работы да ещё завёл
             * unhandledrejection. Отпустить — безопаснее, чем бросить.
             * ⚠️ Скрытая вкладка не отпускается: там ожидание ЗАКОННО (система
             * не даёт открыть звук фоновой странице) и промис оживёт сам при
             * возврате — отпустить его значило бы пустить тул дальше по
             * временно спящему контексту. */
            var release;
            var capped = new Promise(function(resolve){ release = resolve; });
            try {
                var settled = false, wasHidden = docHidden();
                watchRan(ctx);
                Promise.resolve(p).then(function(){ settled = true; markRan(ctx); }, function(){ settled = true; });
                var offHide = onHidden(function(){ wasHidden = true; });
                setTimeout(function(){
                    offHide();
                    if (settled) { release(); return; }
                    // Пока вкладка скрыта, зависший resume() — норма, а не отказ
                    // устройства: система не даёт открыть звук фоновой странице,
                    // и промис ждёт возвращения. Пометить его здесь значило бы
                    // выбрасывать живые контексты у каждого, кто переключил
                    // вкладку, — первые же данные с прода (02.09, iOS) показали
                    // именно такие случаи. Ждём следующей попытки на переднем плане.
                    if (wasHidden || docHidden()) return;
                    // Отпускаем ВСЕГДА; метить ли контекст — решает markCtxError.
                    markCtxError(ctx, 'resume_hung');
                    reportCtx('resume_released');
                    release();
                }, window.__tmbResumeHangMs || HANG_MS);
            } catch (_) { return p; }
            return Promise.race([p, capped]);
        };
        proto.resume = function(){
            return window.__tmbWatchResume(this, orig.apply(this, arguments));
        };
        try { proto.__tmbResumeWrapped = true; } catch (_) {}
    })();

    /* ── ПРИБОР: «источник запустили, а звука так и не было» ─────────────────
     * Корень немоты — в том, что `start()` на контексте без устройства НЕ
     * БРОСАЕТ: тул строит граф, ставит «играет» и молчит. Это невидимо для
     * всего, что у нас есть: ни исключения, ни отказа, ни события.
     * Двери и метки выше лечат тулы, идущие через примитив; остальные (191
     * тул заводит AudioContext, через `__liveAudioCtx` — 72) остаются
     * неизмеренными. Поэтому здесь ПАССИВНЫЙ прибор, а не лечение: запуск
     * источника ничего не меняет, но через SILENT_MS мы смотрим, ожил ли
     * контекст. Не ожил при живом жесте — значит человек нажал Play и не
     * услышал ничего, и мы наконец об этом знаем.
     *
     * ⚠️ Именно ПРИБОР, а не тост: «поставить источник заранее, разбудить
     * следом» — законный приём, и отличить его от немоты можно только
     * ПОСЛЕ, по факту. Один сигнал на контекст, чтобы прогон из сотни
     * источников не превратился в сотню событий.
     * ⚠️ Порог 3 с: пробуждение по жесту укладывается в 1.3 с даже на слабой
     * машине (замер 2026-07-30). Требование `hasActivation()` отсекает
     * страницу, где человек ещё ничего не нажимал — там тишина законна. */
    var SILENT_MS = 3000;
    (function(){
        var protos = [];
        try { if (window.AudioBufferSourceNode) protos.push(window.AudioBufferSourceNode.prototype); } catch(_) {}
        try { if (window.OscillatorNode) protos.push(window.OscillatorNode.prototype); } catch(_) {}
        protos.forEach(function(pr){
            if (!pr || typeof pr.start !== 'function' || pr.__tmbStartWatched) return;
            var origStart = pr.start;
            pr.start = function(){
                var r = origStart.apply(this, arguments);
                try {
                    var c = this.context;
                    // Офлайн-сведение не «молчит» — оно рендерит в буфер, и его
                    // `suspended` до старта штатен (см. isOfflineCtx выше).
                    if (isOfflineCtx(c)) return r;
                    if (c && !c.__tmbSilentSeen && typeof c.state === 'string' && c.state !== 'running') {
                        c.__tmbSilentSeen = true;
                        var hidWatch = onHidden(function(){ c.__tmbWasHidden = true; });
                        setTimeout(function(){
                            hidWatch();
                            try {
                                if (c.state === 'running') return;          // ожил — ложная тревога
                                if (c.state === 'closed') return;           // тул сам всё убрал
                                if (!hasActivation()) return;               // человек ещё не нажимал
                                // Вкладку прятали — молчание законно (страница в фоне
                                // звука и не получает). Сигнал только про передний план.
                                if (c.__tmbWasHidden || docHidden()) return;
                                reportCtx('silent_play', { st: c.state, why: c.__tmbAudioError || 'none' });
                                /* ── и теперь не только меряет. ─────────────
                                 * Здесь стоял ОДИН прибор: «лечение — работа
                                 * __liveAudioCtxReady», то есть тех тулов, что
                                 * зовут примитив. За 30 дней прод ответил,
                                 * чего это стоило: `audio_ctx_error` 2667
                                 * сессий, `audio_ctx_silent_play` 665, а слова
                                 * человеку (`audio_ctx_unavailable`) — 3.
                                 *
                                 * ⚠️⚠️ И тот же замер ОТВЁЛ очевидный ответ
                                 * «раз знаем — скажем». Разбор 665 по причине:
                                 * `why:'none'` (контекст просто спит, никакого
                                 * отказа устройства не было) — 645 сессий, а
                                 * настоящий отказ (`resume_hung` / `device`) —
                                 * 21. Тост «устройство занято другой
                                 * программой» уехал бы к 645 людям, у которых
                                 * с устройством всё в порядке, и громче всего
                                 * на /qr-scanner (233 сессии), где сканер
                                 * работает и молчит только сигнал об успехе.
                                 * Ложная тревога в тридцать раз чаще верной —
                                 * это не починка.
                                 *
                                 * Поэтому две РАЗНЫЕ ветки по причине:
                                 *   · устройство отказало (метка на контексте
                                 *     есть) → сказать словами, как и раньше;
                                 *   · контекст просто спит → не обвинять
                                 *     звуковую карту, а РАЗБУДИТЬ его. Источник
                                 *     уже поставлен, часы спящего контекста не
                                 *     шли — проснувшись, он его и сыграет.
                                 * Исход помечаем, чтобы через неделю знать,
                                 * помогло ли (`recovered` против `stuck`). */
                                if (c.__tmbAudioError) {
                                    if (window.__audioDeviceUnavailable) window.__audioDeviceUnavailable();
                                } else if (typeof window.__safeResume === 'function') {
                                    window.__safeResume(c).then(function (woke) {
                                        reportCtx('silent_play_repair', { ok: woke ? 1 : 0, st: c.state });
                                        // Разбудить не вышло, а улик против устройства нет
                                        // (эта ветка и есть «контекст не помечен») — значит
                                        // причина в правиле браузера, а не в звуковой карте.
                                        if (!woke && !docHidden() && window.__audioNeedsGesture) window.__audioNeedsGesture();
                                    });
                                }
                            } catch(_) {}
                        }, SILENT_MS);
                    }
                } catch(_) {}
                return r;
            };
            try { pr.__tmbStartWatched = true; } catch(_) {}
        });
    })();

    /* ── HTMLMediaElement.play(): дверь, у которой не было хозяина ───────────
     * У каждого браузерного API, умеющего ОТКАЗАТЬ, здесь есть владелец:
     * `__safeResume` (звук), `__safeFullscreen` (полный экран), CameraManager
     * (камера), MicManager (микрофон), `__downloadFile` (сохранение). У самого
     * частого способа издать звук — `el.play()` — владельца не было, и правило
     * по умолчанию сложилось обратное доктрине: 302 из 418 вызовов на сайте
     * гасят отказ пустым `.catch(function(){})`, ещё 102 не ловят его вовсе.
     *
     * Цена: отказ невидим ТРИЖДЫ — человеку (ни слова), нам (ни события) и
     * тесту (у десктопного Chromium липкая активация, поэтому локально и в CI
     * это всегда зелено). Тройная невидимость — причина, по которой класс
     * возвращается: жалоба 09.09.2026 «не работает звук» (/audio-restoration,
     * Firefox 111/Linux) пришла ровно отсюда, и разобрать её было нечем.
     *
     * Замер того же дня (стенд tests/scratch/silence-bench-scratch.mjs, живая
     * страница, play() отвергнут NotAllowedError): форма из блейда
     * `el.play().catch(function(){})` → иконка не переключилась, тостов 0,
     * телеметрии 0, исключений 0. То есть нажатие просто исчезает.
     *
     * Здесь — то же разделение труда, что у пробуждения контекста выше:
     *   · НАБЛЮДАТЕЛЬ на прототипе — сетка безопасности для всех старых мест.
     *     Он ПАССИВЕН: возвращает ровно тот промис, что вернул браузер, ничего
     *     не решает за вызывающего и не заводит новых unhandledrejection
     *     (`.then(null, …)` без переброса), поэтому существующие `.catch`
     *     работают как работали.
     *   · `__safePlay(el)` — передняя дверь для нового кода: тот же разбор
     *     причины, но ещё и булев ответ, по которому тул умеет ветвиться.
     *
     * ⚠️ Говорить человеку — НЕ про всякий отказ. Три класса молчат намеренно:
     *   · AbortError — play() прерван pause()/load()/сменой src. Это штатная
     *     гонка интерфейса, а не отказ: тост здесь был бы шумом на каждом
     *     переключении дорожки.
     *   · NotAllowedError БЕЗ единого жеста на странице — политика автозапуска.
     *     Ожидаемо и нормально (фоновые петли, превью): человек ничего не
     *     нажимал и ничего не ждёт.
     *   · элемент `muted` — звука в нём нет по определению, терять нечего.
     * Остаются два класса, где человек нажал и не услышит ничего: устройство
     * отказало при живом жесте и источник невозможно воспроизвести. */
    var PLAY_SAY_MS = 3000, playQuietUntil = 0;
    function unplayableError(notify, tag){
        var L = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2).toLowerCase();
        var M = {
            ru:'Браузер не может воспроизвести этот звук: формат не поддерживается или файл повреждён. Попробуйте другой файл или другой браузер.',
            en:'The browser cannot play this audio: the format is unsupported or the file is damaged. Try another file or another browser.',
            de:'Der Browser kann dieses Audio nicht abspielen: Das Format wird nicht unterstützt oder die Datei ist beschädigt. Versuchen Sie eine andere Datei oder einen anderen Browser.',
            es:'El navegador no puede reproducir este audio: el formato no es compatible o el archivo está dañado. Prueba con otro archivo u otro navegador.',
            fr:'Le navigateur ne peut pas lire cet audio : le format n’est pas pris en charge ou le fichier est endommagé. Essayez un autre fichier ou un autre navigateur.',
            uk:'Браузер не може відтворити цей звук: формат не підтримується або файл пошкоджений. Спробуйте інший файл чи інший браузер.',
            pt:'O navegador não consegue reproduzir este áudio: o formato não é compatível ou o arquivo está danificado. Tente outro arquivo ou outro navegador.',
            id:'Browser tidak dapat memutar audio ini: format tidak didukung atau berkas rusak. Coba berkas lain atau browser lain.',
            ko:'브라우저가 이 오디오를 재생할 수 없습니다. 형식이 지원되지 않거나 파일이 손상되었습니다. 다른 파일이나 다른 브라우저로 시도해 보세요.',
            ar:'لا يستطيع المتصفح تشغيل هذا الصوت: التنسيق غير مدعوم أو الملف تالف. جرّب ملفًا آخر أو متصفحًا آخر.',
            tr:'Tarayıcı bu sesi oynatamıyor: biçim desteklenmiyor ya da dosya bozuk. Başka bir dosya veya başka bir tarayıcı deneyin.'
        };
        /* A <video> that will not play is a VIDEO the browser cannot play: telling
           a person who chose a film that «this audio» is damaged sends them looking
           for the wrong fault (bug report 2026-10-01, /es/enhance-video, a DivX AVI). */
        var V = {
            ru:'Браузер не может воспроизвести это видео: формат не поддерживается или файл повреждён. Попробуйте другой файл или другой браузер.',
            en:'The browser cannot play this video: the format is unsupported or the file is damaged. Try another file or another browser.',
            de:'Der Browser kann dieses Video nicht abspielen: Das Format wird nicht unterstützt oder die Datei ist beschädigt. Versuchen Sie eine andere Datei oder einen anderen Browser.',
            es:'El navegador no puede reproducir este video: el formato no es compatible o el archivo está dañado. Prueba con otro archivo u otro navegador.',
            fr:'Le navigateur ne peut pas lire cette vidéo : le format n’est pas pris en charge ou le fichier est endommagé. Essayez un autre fichier ou un autre navigateur.',
            uk:'Браузер не може відтворити це відео: формат не підтримується або файл пошкоджений. Спробуйте інший файл чи інший браузер.',
            pt:'O navegador não consegue reproduzir este vídeo: o formato não é compatível ou o arquivo está danificado. Tente outro arquivo ou outro navegador.',
            id:'Browser tidak dapat memutar video ini: format tidak didukung atau berkas rusak. Coba berkas lain atau browser lain.',
            ko:'브라우저가 이 동영상을 재생할 수 없습니다. 형식이 지원되지 않거나 파일이 손상되었습니다. 다른 파일이나 다른 브라우저로 시도해 보세요.',
            ar:'لا يستطيع المتصفح تشغيل هذا الفيديو: التنسيق غير مدعوم أو الملف تالف. جرّب ملفًا آخر أو متصفحًا آخر.',
            tr:'Tarayıcı bu videoyu oynatamıyor: biçim desteklenmiyor ya da dosya bozuk. Başka bir dosya veya başka bir tarayıcı deneyin.'
        };
        var msg = tag === 'video' ? (V[L] || V.en) : (M[L] || M.en);
        if (notify) { try { if (typeof window.__toast === 'function') window.__toast(msg, 'error'); } catch (_) {} }
        var err = new Error(msg); err.name = 'MediaUnplayable'; err.__userNotified = true;
        return err;
    }
    /* Разбор отказа. Возвращает {reason, say} — `say` только там, где человек
     * нажал и остался без звука. Имя ошибки читаем защищённо: обёртки умеют
     * реджектить СТРОКОЙ (у html5-qrcode `err.name` — undefined; тот же урок
     * записан у CameraManager), поэтому и строка тоже разбирается. */
    function classifyPlay(el, err) {
        var name = '';
        try { name = (err && err.name) || ''; } catch (_) {}
        if (!name) { try { name = (String(err).match(/([A-Za-z]+Error)/) || [])[1] || ''; } catch (_) {} }
        var muted = false; try { muted = !!(el && (el.muted || el.volume === 0)); } catch (_) {}
        if (name === 'AbortError') return { reason: 'aborted', say: 0 };
        if (name === 'NotSupportedError') return { reason: 'unplayable', say: muted ? 0 : 1 };
        if (name === 'NotAllowedError') {
            if (!hasActivation()) return { reason: 'autoplay', say: 0 };
            /* ⚠️⚠️ «Жест на странице был» — это `hasBeenActive`, ЛИПКИЙ флаг:
             * он остаётся истинным до конца жизни страницы после первого же
             * касания. Поэтому сюда попадал КАЖДЫЙ отказ автозапуска на
             * телефоне, и все они объявлялись отказом устройства. Замер прода
             * за 30 дней: фразу «устройство вывода занято другой программой
             * (DAW, ASIO-драйвер, звонок)» получили 3585 сессий С ТЕЛЕФОНОВ
             * против 15 с компьютеров — то есть 240:1 в пользу тех, у кого ни
             * DAW, ни ASIO-драйвера быть не может, и громче всего на
             * /fancy-text, /calculator, /online-ruler, /iq-test, где звук — это
             * щелчок кнопки. Настоящих улик (контекст поднял `error`) за те же
             * 30 дней — 186 сессий на компьютерах и 44 на телефонах.
             * Различает их ЖИВАЯ активация: WebKit требует, чтобы `play()`
             * стоял в самой цепочке нажатия, и всё, что ушло за `await`
             * (дочитали файл → играем), получает NotAllowedError уже ПОСЛЕ
             * того, как transient-активация истекла. Это правило браузера, а
             * не отказ железа, и человеку тут помогает «нажмите ещё раз». */
            if (!activationLive()) return { reason: 'gesture', say: muted ? 0 : 1 };
            return { reason: 'blocked', say: muted ? 0 : 1 };
        }
        return { reason: name ? name.toLowerCase() : 'unknown', say: muted ? 0 : 1 };
    }
    // Одно нажатие обычно дёргает несколько дорожек; когда устройства нет,
    // отказывает каждая. Окно памяти схлопывает такой каскад в одно сообщение,
    // как это уже сделано для контекста (`acDeadUntil`).
    /* __adMedia:begin */
    /* Whose sound failed? An advertisement's autoplay refusal is not the page's
     * sound failing, and the message below says exactly that — on a tool whose
     * whole product IS sound it reads as "this tool is broken".
     *
     * Measured on /speaker-cleaner, 30 days: 765 sessions were shown an audio
     * failure message and only 12 of them had ever switched on the tool's own
     * audio route. The other 98% were the ad block's own <video>/<audio>
     * refusing to autoplay — which is normal, expected, and none of the
     * visitor's business. It arrives as a <video> or <audio> in OUR document
     * (the RTB block renders into #yandex_rtb_*, AdSense into ins.adsbygoogle),
     * so the prototype observer sees it like any other element.
     *
     * Ownership is decided POSITIVELY: silence only when the element sits
     * inside a container we know to be an ad. Anything we cannot attribute
     * keeps the old behaviour, because a tool whose own audio really did fail
     * must still get its sentence. */
    function isAdMedia(el) {
        try {
            for (var n = el; n && n.nodeType; n = n.parentNode) {
                if (n.nodeType !== 1) continue;
                var id = typeof n.id === 'string' ? n.id : '';
                if (/^(yandex_rtb|google_ads|aswift_|div-gpt-ad)/.test(id)) return true;
                var cl = typeof n.className === 'string' ? n.className : '';
                if (/(^|\s)(tool-ad-slot|adsbygoogle|adfox|ya-partner)/.test(cl)) return true;
            }
        } catch (_) {}
        return false;
    }
    /* __adMedia:end */

    /* Сколько раз сообщать об отказе play(). Сказанный человеку отказ —
     * каждый раз: это и есть авария, которую считаем. Молчаливый (в том числе
     * `aborted` — play() прерван паузой или новой загрузкой) — один раз на
     * страницу для каждой пары (причина, элемент): повтор той же пары ничего не
     * добавляет. Замер RU 25.09.2026, час: 8 300 событий, из них 23 сказаны
     * человеку; 4 889 — `aborted`, больше половины со словарного теста, по
     * два-три на страницу; в сутки ~147 тыс., 12 % всего feature_use, и ни один
     * запрос их не читал. */
    var playSilentSeen = {};
    function worthReporting(c, tag, ad) {
        if (c.say && !ad) return true;
        var k = c.reason + '|' + tag + '|' + (ad ? 1 : 0);
        if (playSilentSeen[k]) return false;
        playSilentSeen[k] = 1;
        return true;
    }

    /* Отказ play() РАЗОБРАН здесь — и то, что человеку сказано, и то, что
       промолчали намеренно (AbortError, автозапуск без жеста, беззвучный
       элемент). Поэтому на самой ошибке остаётся метка `__playSettled`: тул,
       который поймал тот же отказ и понёс его в __handleError, второго тоста
       не получит — раньше получал («audio-player: Failed to load because no
       supported source was found» рядом с фразой отсюда; у AbortError — тост
       о сбое на переключении дорожки). */
    function notePlayFailure(el, err, viaFrontDoor) {
        var c = classifyPlay(el, err);
        try { if (err && typeof err === 'object') err.__playSettled = c.reason; } catch (_) {}
        var tag = ''; try { tag = (el && el.tagName ? el.tagName : '?').toLowerCase(); } catch (_) {}
        var ad = isAdMedia(el);
        if (worthReporting(c, tag, ad)) reportCtx('play_refused', {
            why: c.reason, tag: tag, said: (c.say && !ad) ? 1 : 0,
            fd: viaFrontDoor ? 1 : 0, ad: ad ? 1 : 0
        });
        if (ad) return c;
        if (!c.say) return c;
        if (docHidden()) return c;                       // вкладка в фоне — молчание законно
        try { if (err && err.__userNotified) return c; } catch (_) {}
        if (Date.now() < playQuietUntil) return c;
        playQuietUntil = Date.now() + PLAY_SAY_MS;
        if (c.reason === 'unplayable') unplayableError(true, tag);
        /* Обвинение звуковой карте надо ЗАСЛУЖИТЬ. У отказа `play()` своего
         * контекста нет вовсе, поэтому единственная улика — контекст на этой
         * же странице, поднявший `error` (или зависший resume). Нет улики —
         * говорим то, что точно верно: браузер ждёт нажатия. */
        else if (c.reason === 'gesture' || !deviceEvidence()) window.__audioNeedsGesture();
        else window.__audioDeviceUnavailable();
        try { if (err) err.__userNotified = true; } catch (_) {}
        return c;
    }
    window.__notePlayFailure = notePlayFailure;   // тестовый шов
    /* Передняя дверь. `await __safePlay(el)` → true/false, и человеку уже
     * сказано, если сказать было о чём. Тул ветвится по ответу, а не гадает. */
    window.__safePlay = function (el, opts) {
        if (!el || typeof el.play !== 'function') return Promise.resolve(false);
        var p; try { p = el.play(); } catch (e) { notePlayFailure(el, e, true); return Promise.resolve(false); }
        if (!p || typeof p.then !== 'function') return Promise.resolve(true);
        try { p.__tmbPlayOwned = true; } catch (_) {}
        return p.then(function () { return true; }, function (e) {
            if (!(opts && opts.quiet)) notePlayFailure(el, e, true);
            return false;
        });
    };
    (function(){
        var proto = (typeof HTMLMediaElement !== 'undefined') && HTMLMediaElement.prototype;
        if (!proto || typeof proto.play !== 'function' || proto.__tmbPlayWrapped) return;
        var origPlay = proto.play;
        proto.play = function () {
            var p = origPlay.apply(this, arguments), el = this;
            try {
                if (p && typeof p.then === 'function') {
                    // Пассивно: наблюдаем ЗА промисом, но отдаём вызывающему
                    // ровно его. `.then(null, …)` не перебрасывает, поэтому
                    // своей unhandledrejection здесь не появляется.
                    p.then(null, function (e) {
                        try { if (p.__tmbPlayOwned) return; } catch (_) {}   // передняя дверь уже разобрала
                        notePlayFailure(el, e, false);
                    });
                }
            } catch (_) {}
            return p;
        };
        try { proto.__tmbPlayWrapped = true; } catch (_) {}
    })();

    /* ── getUserMedia: сетка безопасности для вызовов мимо двери ────────────
     * У камеры ЕСТЬ владелец — CameraManager, и с 19.09.2026 он умеет ждать
     * вслух, объяснять отказ словами этого устройства и предлагать выход.
     * Но в дверь входят не все: 24 места зовут `getUserMedia` напрямую, и ни
     * у одного из них НЕТ сторожа зависания (проверено грепом по всем шести
     * камерным файлам, `Promise.race`/`setTimeout`/`TimeoutError` — ноль).
     * Там повисший вызов ждёт ВЕЧНО: обращение 19.09 по /ar-drawing — это
     * то же самое, только с пределом в 36 секунд.
     *
     * Публика у этих мест настоящая (RU, 30 дней, сессии): /dictaphone 10 941,
     * /eye-test 7 025, /compass 6 724, /screen-recorder 4 729,
     * /3d-photo-to-model 3 002, /protractor 1 241, дальше хвост до
     * /projector-camera-analyzer 29.
     *
     * Та же форма, что у `__downloadFile` и `__speak`: передняя дверь для
     * нового кода, сетка — для старых вызовов. Сетка НЕ рисует UI и не
     * заменяет дверь: она превращает бесконечную тишину в определённый отказ,
     * который `.catch()` этих тулов уже умеет показать, и делает класс
     * ИЗМЕРИМЫМ — до неё зависание не производило ни одного события, потому
     * что событию неоткуда было взяться.
     *
     * ⚠️ Почему здесь, а не в telemetry-tier0.js, где обёртка уже стояла:
     * та обёртка живёт ЗА `if (!consentOK()) return;`, то есть не досталась бы
     * ни отказавшимся от согласия, ни тем, у кого баннер ещё висит (а это
     * первый просмотр страницы), ни DNT. Зависание про согласие не знает.
     * Сама та обёртка была `.catch(function(err){ throw err; })` — поймать и
     * перебросить, то есть ровно ничего, при живом комментарии о том, что она
     * что-то делает; она удалена этим же изменением.
     */
    (function(){
        var md = navigator.mediaDevices;
        if (!md || !md.getUserMedia) return;
        var orig = md.getUserMedia.bind(md);
        var permP = null;
        /* Состояние разрешения — чтобы окно ожидания было тем же, что у двери:
           12 с при granted/denied (промпт не блокирует, значит обязано
           ответить быстро: за 30 дней дольше 12 с пришли 4 успеха из 14 517),
           60 с при prompt (человек читает диалог браузера), 20 с при unknown. */
        function permState(){
            if (permP) return permP;
            permP = new Promise(function(resolve){
                var done = false, fin = function(v){ if (!done) { done = true; resolve(v); } };
                setTimeout(function(){ fin('unknown'); }, 2000);
                try {
                    if (!navigator.permissions || !navigator.permissions.query) return fin('unknown');
                    navigator.permissions.query({ name: 'camera' })
                        .then(function(r){ fin((r && r.state) || 'unknown'); }, function(){ fin('unknown'); });
                } catch (_) { fin('unknown'); }
            });
            return permP;
        }
        /* `net` holds the wrapper even if the assignment is refused in this
           sloppy scope — the mark tells MicManager's refusal row which
           function really stood on navigator.mediaDevices (29.10). */
        var net = md.getUserMedia = function(c){
            var wantsVideo = false;
            try { wantsVideo = !!(c && c.video); } catch (_) {}
            /* Запись звука на iPhone идёт через аудиосессию (ios-audio-unmute.js
               § 4): перед запросом тип — play-and-record, отказ «AudioSession
               category is not compatible with audio capture» спрашивается снова
               после паузы, после остановки дорожек — выбор человека. */
            var iosA = null;
            try { if (c && c.audio && window.__iosAudioUnmute) iosA = window.__iosAudioUnmute; } catch (_) { iosA = null; }
            function ask(){
                if (iosA && iosA.request) return iosA.request(orig, c);
                /* Модуль из кэша старше этого ядра — его прежний договор. */
                try { if (iosA && iosA.beforeCapture) iosA.beforeCapture(); } catch (_) {}
                var pr;
                try { pr = orig(c); } catch (e) { pr = Promise.reject(e); }
                try { if (iosA && iosA.capture) iosA.capture(pr); } catch (_) {}
                return pr;
            }
            /* Не наше: звук принадлежит MicManager, а помеченные вызовы —
               двери, у которой и сторож, и слова, и выход уже есть. Второй
               сторож поверх её сторожа дал бы два таймера и два события. */
            if (!wantsVideo || (c && c.__tmbDoor)) return ask();

            /* Запрос уходит НЕМЕДЛЕННО — ждать разрешения перед ним значило бы
               задержать саму камеру на время опроса. Окно сперва осторожное,
               потом уточняется по ответу Permissions API. */
            var p;
            try { p = ask(); } catch (e) { return Promise.reject(e); }
            if (!p || typeof p.then !== 'function') return p;

            return new Promise(function(resolve, reject){
                var settled = false, t0 = Date.now(), timer = null, win = 20000;
                function fire(){
                    if (settled) return;
                    settled = true;
                    try {
                        if (window.__aeFeature) window.__aeFeature('cam_acquire', {
                            phase: 'timeout', perm: win === 60000 ? 'prompt' : win === 12000 ? 'settled' : 'unknown',
                            ms: win, waited: Date.now() - t0, via: 'net'
                        });
                    } catch (_) {}
                    var e = new Error('getUserMedia did not settle within ' + win + 'ms');
                    e.name = 'TimeoutError';
                    reject(e);
                }
                function arm(ms){
                    win = ms;
                    clearTimeout(timer);
                    timer = setTimeout(fire, Math.max(0, ms - (Date.now() - t0)));
                }
                arm(20000);
                permState().then(function(ps){
                    if (settled) return;
                    arm(ps === 'prompt' ? 60000 : ps === 'unknown' ? 20000 : 12000);
                });
                p.then(function(stream){
                    if (settled) {
                        /* Камера освободилась уже после отказа — глушим сироту,
                           иначе останется горящий индикатор и занятое
                           устройство за сообщением, которое человек уже прочёл. */
                        try { stream.getTracks().forEach(function(t){ t.stop(); }); } catch (_) {}
                        return;
                    }
                    settled = true;
                    clearTimeout(timer);
                    try {
                        if (window.__aeFeature) window.__aeFeature('cam_acquire', {
                            phase: 'ok', ms: Date.now() - t0, via: 'net'
                        });
                    } catch (_) {}
                    resolve(stream);
                }, function(err){
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    reject(err);
                });
            });
        };
        try { net.__tmbNet = true; md.__tmbGumNet = true; } catch (_) {}
        try { window.__tmbGumMd = md; window.__tmbGumOrig = orig; } catch (_) {}
    })();

    /* ── Дверь записи: __tmbGum(c) — КАЖДЫЙ запрос камеры и микрофона ──────
     * 29.10.2026: сетка выше стоит на navigator.mediaDevices.getUserMedia
     * не везде. Трасса отказа с настоящего iPhone (Яндекс): на месте свойства
     * НЕ наша обёртка (gw=0), request() модуля сессии не вызывался ни разу
     * (tr_age=-1), тип сессии — 'playback', записанный при загрузке. Поэтому
     * ни правка 22.10, ни v3 23.10 в Яндексе не исполнялись вовсе.
     * Кто подменил свойство — оболочка браузера или запись молча не легла —
     * для двери неважно: она смотрит, что стоит на свойстве В МОМЕНТ ЗАПРОСА;
     * наша обёртка — идём через неё, чужое — сами проводим запрос через модуль
     * сессии (request()), вызывая то, что стоит, а не исходник, — оболочка
     * браузера могла стать туда не зря.
     *
     * Safari на iPhone — тот же случай (заявка на возврат №56, 29.10): за 5,6
     * суток до двери модуль сессии не оставил из Safari ни одной строки
     * ios_capture_refused (даже 'slow' — она пишется на каждом окне разрешения
     * дольше 4 с), после двери — пишет; отказов «AudioSession category» в том же
     * утреннем окне — 16 из 43 сессий 28.10 и 0 из 33 сессий 29.10. Значит, через
     * свойство на iPhone запрос до модуля не доходит в ОБОИХ браузерах, и дверь —
     * единственный путь записи, а не запасной: тул, зовущий свойство сам,
     * отказывает на iPhone после любого звука страницы. Сторож —
     * IosRecordingOwnsTheAudioSessionTest::test_every_capture_request_goes_through_the_door.
     *
     * Вне ветки выше: дверь есть и там, где mediaDevices при загрузке ядра не было
     * (свойство читается в момент запроса) — тул зовёт её без проверок. */
    window.__tmbGum = function(c){
        var m = navigator.mediaDevices || window.__tmbGumMd;
        var cur = null;
        try { cur = m && m.getUserMedia; } catch (_) {}
        if (cur && cur.__tmbNet) return cur.call(m, c);
        var gum = (typeof cur === 'function') ? function(cc){ return cur.call(m, cc); } : window.__tmbGumOrig;
        if (typeof gum !== 'function') {
            var ns = new Error('getUserMedia is not available on this page');
            ns.name = 'NotSupportedError';
            return Promise.reject(ns);
        }
        var iosA = null;
        try { if (c && c.audio && window.__iosAudioUnmute && window.__iosAudioUnmute.request) iosA = window.__iosAudioUnmute; } catch (_) {}
        try { window.__tmbGumBypass = (window.__tmbGumBypass || 0) + 1; } catch (_) {}
        if (iosA) return iosA.request(gum, c);
        try { return gum(c); } catch (e) { return Promise.reject(e); }
    };

    Wrapped.prototype=_origACtor.prototype;
    window.AudioContext=Wrapped;
    if(window.webkitAudioContext)window.webkitAudioContext=Wrapped;
    function resumeAll(){
        for(var i=_allContexts.length-1;i>=0;i--){
            var c=_allContexts[i];
            // Legacy WebKit (iOS 12) can hand back a null/broken object when the
            // 4-AudioContext limit is hit; drop it instead of reading .state on
            // null (TIMBRICA-FRONTEND-AS).
            if(!c||typeof c.state==='undefined'){_allContexts.splice(i,1);continue}
            if(c.state==='closed'||ctxErrored(c)){_allContexts.splice(i,1);continue}
            /* `interrupted` — четвёртое состояние WebKit (лист выбора файла, звонок,
             * другое приложение): так же немо, как `suspended`, и будится тем же
             * resume() из жеста. Здесь проверялся только `suspended`, и такой
             * контекст не будило ни одно касание: RU за 2 дня — 26 сессий на 15
             * тулах со звуком, запущенным в `interrupted` (чат 653, 27.09.2026). */
            if(c.state==='suspended'||c.state==='interrupted'){
                // ctx.resume() returns a Promise that REJECTS if the context
                // closed between our state check above and this call (browser
                // can close audio contexts under memory pressure, tab-blur
                // policies, etc.). The sync try/catch only catches sync throws —
                // attach .catch() to swallow the async rejection and drop the
                // dead context from our list. Without this, "Cannot resume a
                // context that has been closed" leaks to onunhandledrejection.
                try{
                    var p=c.resume();
                    if(p&&typeof p.catch==='function'){
                        (function(idx){
                            p.catch(function(){
                                if(_allContexts[idx]===c)_allContexts.splice(idx,1);
                            });
                        })(i);
                    }
                }catch(e){}
            }
        }
    }
    /* ── Живой AudioContext ────────────────────────────────────────────────
     * Браузер может ЗАКРЫТЬ аудио-контекст сам: на Android это штатная реакция
     * на нехватку памяти и на долгую жизнь вкладки в фоне. Закрытый контекст
     * не воскрешает `resume()` — из состояния `closed` возврата нет, только
     * новый контекст.
     *
     * Почему это молчаливый класс отказа: `createBufferSource()` на закрытом
     * контексте в Chromium НЕ бросает исключение. Тул спокойно строит граф,
     * ставит «играет», рисует паузу — и не издаёт ни звука. Ни ошибки в
     * консоли, ни события в телеметрии; человек видит мёртвую кнопку и
     * перезагружает вкладку. Измерено 2026-08-04 на /join: после `close()`
     * нажатие play даёт `playing:true` при `state:"closed"` и нулевом
     * количестве ошибок страницы.
     *
     * Отчёт, с которого начали (Артём Логинов, 03.08): «после удаления уже
     * сведённых треков и закидывания новых в этот же открытый джойнер —
     * вообще ничто не проигрывается… Раньше я неделями не перезагружал
     * вкладки, всё работало». Именно долгая жизнь вкладки и приводит к
     * закрытию контекста системой.
     *
     * Масштаб на момент правки: AudioContext заводит 161 тул, и 122 из них
     * не проверяют `closed` вовсе — каждый пишет свой `getAudioCtx()`.
     * Поэтому проверка живёт ЗДЕСЬ, в одной точке, а не копируется по тулам.
     *
     * Буферы переносимы: замерено — новый контекст принимает AudioBuffer,
     * декодированный в уже закрытом, так что пересоздание не требует
     * повторного декодирования.
     *
     *   audioCtx = window.__liveAudioCtx(audioCtx);              // просто
     *   audioCtx = window.__liveAudioCtx(audioCtx, function(){   // со своими
     *       return new AudioContext({ sampleRate: 48000 });      // параметрами
     *   });
     *
     * Контракт отказа: если живого контекста нет И создать новый нельзя,
     * функция БРОСАЕТ ошибку конструктора (в среде без WebAudio это
     * именованный AudioContextUnavailable от SafeAC-стаба: тост уже показан,
     * Sentry фильтрует по имени). Никогда не возвращает null — потребительский
     * однострочник `audioCtx = __liveAudioCtx(audioCtx); audioCtx.state...`
     * безопасен как есть.
     */
    // Живость контекста — это НЕ его `state`. Замерено 2026-08-22 по двум
    // событиям Sentry (B3 /speaker-test 07.07, FT /speaker-cleaner 21.08 —
    // оба iOS 18.7): WebKit отдаёт объект AudioContext, у которого `state`
    // вполне «suspended»/«running», но за ним НЕТ звукового устройства —
    // аудиосессию держит другое приложение, звонок или системное прерывание.
    // У такого контекста `currentTime` считается как currentSampleFrame /
    // sampleRate, а sampleRate равен нулю → currentTime = NaN.
    //
    // Дальше ломается ВСЁ и молча: тул пишет `setValueAtTime(0, ctx.currentTime)`,
    // WebKit бросает «The provided value is non-finite», исключение уносит
    // обработчик клика целиком — кнопка уже переключена в «идёт», звука нет,
    // сообщения нет. Ровно тот класс, что описан выше про closed-контекст,
    // только `state` здесь врёт активнее.
    //
    // ⚠️ Прошлая попытка чинить это была ФОРМОЙ ЭКЗЕМПЛЯРА: в /speaker-test
    // (c69371abff) добавили `if(!isFinite(freq))freq=440` — то есть застраховали
    // ЧАСТОТУ, тогда как нефинитным было ВРЕМЯ, и в вызове `setValueAtTime(0,
    // audioCtx.currentTime)` первый аргумент — литерал `0`. Класс пережил
    // починку и вышел в соседнем туле. Поэтому проверка стоит здесь, у
    // единственной двери, и проверяет ровно ту величину, которая портится.
    //
    // Масштаб: `.currentTime` читают 1862 места в 188 файлах — по местам это
    // не чинится в принципе.
    function ctxUsable(c){
        if (!c) return false;
        if (ctxErrored(c)) return false;
        try {
            if (c.state === 'closed') return false;
            var t = c.currentTime;
            if (typeof t !== 'number' || !isFinite(t)) return false;
            return c.sampleRate > 0;
        } catch (_) { return false; }
    }
    // Закрыть отслуживший контекст, не подорвавшись на нём же: на iOS живых
    // контекстов разрешено немного, а `close()` уже закрытого бросает
    // InvalidStateError (issue ET/D0).
    // ⚠️ `close()` ОТВЕЧАЕТ ПОЗЖЕ: пока закрытие в пути, `state` ещё не
    // 'closed', и второй `close()` (тул уже закрыл сам, или мы — дважды)
    // отказывает промисом, мимо try/catch: необработанный InvalidStateError
    // (Sentry TIMBRICA-FRONTEND-PX, Firefox 133, /finger-chooser).
    function retireCtx(c){
        if (!c || c.__tmbRetired) return;
        try {
            if (c.state !== 'closed' && typeof c.close === 'function') {
                c.__tmbRetired = true;
                var p = c.close();
                if (p && typeof p.catch === 'function') p.catch(function(){});
            }
        } catch (_) {}
    }
    function reportCtx(reason, extra){
        try {
            var p = {
                f: 'audio_ctx_' + reason,
                slug: (document.querySelector('meta[name=tool-slug]') || {}).content || ''
            };
            if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
            window.__ae && window.__ae('feature_use', p);
        } catch(_){}
    }
    // Один клик обычно дёргает несколько независимых путей звука. Когда
    // устройства нет, КАЖДЫЙ из них строил свой контекст и показывал свой
    // тост: замерено на /speaker-test — два контекста и два одинаковых тоста
    // на одно нажатие. На iOS живых контекстов разрешено немного, так что это
    // не только шум на экране, но и расход дефицита. Короткое окно памяти
    // схлопывает каскад одного нажатия и не мешает осознанному повтору.
    var acDeadUntil = 0;
    var AC_DEAD_MS = 3000;
    function acUnavailableError(notify){
        var L = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2).toLowerCase();
        var M = {
            ru:'Браузер не получает доступ к звуку: устройство вывода занято другой программой (DAW, ASIO-драйвер, звонок) или отключено. Закройте её либо проверьте устройство вывода и звук сайта — и нажмите ещё раз.',
            en:'The browser cannot reach the audio output: the device is held by another program (DAW, ASIO driver, a call) or disconnected. Close it or check the output device and the site’s sound setting, then try again.',
            de:'Der Browser erreicht die Audioausgabe nicht: Das Gerät wird von einem anderen Programm (DAW, ASIO-Treiber, Anruf) belegt oder ist getrennt. Schließen Sie es oder prüfen Sie Ausgabegerät und Website-Ton, dann erneut versuchen.',
            es:'El navegador no accede a la salida de audio: el dispositivo lo usa otro programa (DAW, controlador ASIO, una llamada) o está desconectado. Ciérralo o revisa el dispositivo de salida y el sonido del sitio, y vuelve a intentarlo.',
            fr:'Le navigateur n’accède pas à la sortie audio : le périphérique est occupé par un autre programme (DAW, pilote ASIO, appel) ou débranché. Fermez-le ou vérifiez le périphérique de sortie et le son du site, puis réessayez.', uk: 'Браузер не отримує доступ до звуку: пристрій виводу зайнятий іншою програмою (DAW, ASIO-драйвер, дзвінок) або відключений. Закрийте її або перевірте пристрій виводу та звук сайту — і натисніть ще раз.',
            pt:'O navegador não acessa a saída de áudio: o dispositivo está ocupado por outro programa (DAW, driver ASIO, uma chamada) ou desconectado. Feche-o ou verifique o dispositivo de saída e o som do site, e tente de novo.',
            id:'Browser tidak bisa mengakses keluaran audio: perangkat dipakai program lain (DAW, driver ASIO, panggilan) atau terputus. Tutup program itu atau periksa perangkat keluaran dan suara situs, lalu coba lagi.',
            ko:'브라우저가 오디오 출력에 접근하지 못합니다. 다른 프로그램(DAW, ASIO 드라이버, 통화)이 장치를 사용 중이거나 장치가 분리되었습니다. 해당 프로그램을 닫거나 출력 장치와 사이트 소리 설정을 확인한 뒤 다시 시도하세요.',
            ar:'لا يستطيع المتصفح الوصول إلى مخرج الصوت: الجهاز مشغول ببرنامج آخر (DAW أو مشغّل ASIO أو مكالمة) أو غير متصل. أغلق البرنامج أو تحقق من جهاز الإخراج وصوت الموقع ثم أعد المحاولة.',
            tr:'Tarayıcı ses çıkışına erişemiyor: cihaz başka bir program (DAW, ASIO sürücüsü, bir görüşme) tarafından kullanılıyor ya da bağlantısı kesilmiş. O programı kapatın veya çıkış cihazını ve sitenin ses ayarını kontrol edip tekrar deneyin.'
        };
        var msg = M[L] || M.en;
        if (notify) { try { if (typeof window.__toast === 'function') window.__toast(msg, 'error'); } catch (_) {} }
        var err = new Error(msg);
        err.name = 'AudioContextUnavailable';
        err.__userNotified = true;   // тост показан (или намеренно подавлен как повтор)
        return err;
    }
    /* Публичная дверь к тому же названному отказу — теми же словами и в тех же
     * локалях. Нужна тулу, который ведёт СВОЙ контекст и пересобрать его дёшево
     * не может (плеер с графом на MediaElementSource: источник элемента создаётся
     * один раз на контекст, так что «свежий контекст» там означает снос всего
     * графа и потерю позиции). Такой тул всё равно обязан СКАЗАТЬ, а не замереть
     * молча — именно молчанием он и выглядит сломанным. */
    var sayLostUntil = 0;
    function sayDeviceLost() {
        if (Date.now() < sayLostUntil) return acUnavailableError(false);
        sayLostUntil = Date.now() + 3000;
        return acUnavailableError(true);
    }
    window.__audioDeviceUnavailable = sayDeviceLost;
    /* ── Вторая причина немоты, у которой не было своих слов ─────────────────
     * Браузер разрешает звук только в момент нажатия. Отказ по этому правилу
     * и отказ устройства — РАЗНЫЕ события с разными действиями человека
     * («нажмите ещё раз» против «закройте программу, держащую звук»), а слов
     * до сих пор была одна пара на двоих, и доставались они почти целиком
     * первому случаю (замер — в classifyPlay выше). Здесь слова второго. */
    function needsGestureError(notify){
        var L = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2).toLowerCase();
        var M = {
            ru:'Браузер включает звук только в момент нажатия. Нажмите ещё раз — и звук пойдёт.',
            en:'The browser turns sound on only at the moment you tap. Tap again and it will play.',
            de:'Der Browser schaltet den Ton nur im Moment des Tippens ein. Tippen Sie erneut, dann wird abgespielt.',
            es:'El navegador activa el sonido solo en el momento en que tocas. Toca de nuevo y sonará.',
            fr:'Le navigateur active le son uniquement au moment où vous appuyez. Appuyez à nouveau et le son démarrera.',
            uk:'Браузер вмикає звук лише в момент натискання. Натисніть ще раз — і звук піде.',
            pt:'O navegador ativa o som apenas no momento do toque. Toque novamente e o som será reproduzido.',
            id:'Browser menyalakan suara hanya saat Anda menekan. Tekan sekali lagi dan suara akan diputar.',
            ko:'브라우저는 탭하는 순간에만 소리를 켭니다. 한 번 더 누르면 재생됩니다.',
            ar:'يشغّل المتصفح الصوت في لحظة الضغط فقط. اضغط مرة أخرى وسيبدأ التشغيل.',
            tr:'Tarayıcı sesi yalnızca dokunduğunuz anda açar. Tekrar dokunun, ses çalacaktır.'
        };
        var msg = M[L] || M.en;
        if (notify) { try { if (typeof window.__toast === 'function') window.__toast(msg, 'warning'); } catch (_) {} }
        var err = new Error(msg);
        err.name = 'AudioNeedsGesture';
        err.__userNotified = true;
        return err;
    }
    var sayGestureUntil = 0;
    function sayNeedsGesture() {
        if (Date.now() < sayGestureUntil) return needsGestureError(false);
        sayGestureUntil = Date.now() + 3000;
        return needsGestureError(true);
    }
    window.__audioNeedsGesture = sayNeedsGesture;
    window.__liveAudioCtx = function(current, make){
        if (ctxUsable(current)) return current;

        // Сюда попадаем только если контекста нет, он закрыт или он мёртв
        // аппаратно. Во всех трёх случаях единственный ход — новый контекст.
        if (current) {
            // Различаем два повода: контекст закрыт системой (штатное
            // перерождение долгой вкладки) и контекст жив по `state`, но без
            // устройства (аппаратный отказ). Второй мы раньше не видели вообще.
            var why = 'dead';
            try { if (current.state === 'closed') why = 'reborn'; else if (ctxErrored(current)) why = 'errored'; } catch(_) {}
            reportCtx(why);
        }

        // Уже знаем, что устройства нет: не строим ещё один контекст и не
        // повторяем тот же тост — просто отказываем тем же типом.
        if (Date.now() < acDeadUntil) { retireCtx(current); throw acUnavailableError(false); }

        var fresh = null, mkErr = null;
        try {
            fresh = make ? make() : new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) { mkErr = e; }

        // Рождённый НА ЗАМЕНУ мёртвому подозрителен: если устройство всё ещё
        // занято, его `error` придёт через мгновение, а `state` пока «running».
        // Пометка даёт `__liveAudioCtxReady` право выждать SETTLE_MS, а тулу —
        // повод пойти через неё, а не ставить источник сразу.
        if (fresh) { try { fresh.__tmbBornAt = performance.now(); fresh.__tmbReborn = !!current; } catch(_) {} }
        /* ── Свежий контекст, рождённый ВНЕ жеста, надо разбудить ──────────
         * Контекст, созданный не в обработчике нажатия, на телефоне рождается
         * СПЯЩИМ, и первый же `start()` уходит в тишину, ничего не бросая.
         * Так звучат (точнее, не звучат) короткие сигналы: подтверждение
         * сканирования, конец таймера, ход в игре — их запускает событие, а не
         * палец. Замер прибором немоты за первые часы: 20 блейдов заводят
         * осциллятор и НИ РАЗУ не зовут resume, и /qr-scanner дал 12 сессий
         * молчания за полдня.
         * Здесь — центральная половина лечения: если у страницы уже был жест,
         * будим сразу. Chromium с прилипшей активацией это разрешает, десктопу
         * это ничего не стоит (контекст и так `running`). Вторая половина —
         * готовить звук НА жесте — остаётся за тулом: WebKit требует, чтобы
         * `resume()` стоял в самой цепочке нажатия, и обойти это отсюда нельзя.
         * ⚠️ Свой `.catch` и НЕ через `__safeResume`: отказ здесь ожидаем
         * (тот же WebKit) и телеметрию засорять им незачем. */
        if (fresh) {
            try {
                if (fresh.state === 'suspended' && hasActivation()) {
                    var _rp = fresh.resume();
                    if (_rp && _rp.catch) _rp.catch(function(){});
                }
            } catch (_) {}
        }
        if (ctxUsable(fresh)) {
            retireCtx(current);
            // ⚠️ ПОДМЕНА КОНТЕКСТА — СОБЫТИЕ ДЛЯ ТУЛА, а не тихая деталь.
            // Узлы, собранные в прежнем контексте, умерли вместе с ним:
            // соединить их с источником из нового нельзя (WebKit —
            // InvalidAccessError «Source and destination nodes belong to
            // different audio contexts», Sentry TIMBRICA-FRONTEND-HH на
            // /voice-compressor; Chromium — «Failed to execute 'connect'»).
            // Тул, который держит граф между запусками, обязан пересобрать
            // его: образцы — vcRebuildAfterCtxSwap и jnAdoptCtx в /join.
            // Свип 2026-09-02: 52 файла держат долгоживущие узлы, усыновляют
            // шесть. Событие + счётчик дают чинить остальных ПО ФАКТУ
            // попадания в подмену, а не по списку догадок.
            if (current) {
                reportCtx('swapped');
                try {
                    window.dispatchEvent(new CustomEvent('timbrica:audio-ctx-swapped', {
                        detail: { previous: current, current: fresh }
                    }));
                } catch (_) {}
            }
            return fresh;
        }

        // Живого контекста получить не удалось.
        retireCtx(fresh);
        if (mkErr) {
            // Конструктор бросил — пробрасываем КАК ЕСТЬ: в среде без WebAudio
            // это именованный AudioContextUnavailable от SafeAC-стаба (тост уже
            // показан, Sentry фильтрует по имени), и 66 потребителей наследуют
            // до-обёрточную семантику «new AudioContext() бросил» без правок.
            retireCtx(current);
            throw mkErr;
        }
        // Конструктор отработал, но устройства за контекстом нет. Раньше такой
        // объект возвращался как живой и убивал вызывающего нефинитным временем.
        // Отвечаем тем же именованным отказом — это ровно «звука тут не будет».
        retireCtx(current);
        reportCtx('unavailable');
        acDeadUntil = Date.now() + AC_DEAD_MS;
        throw acUnavailableError(true);
    };

    /* ── Контекст, готовый ИГРАТЬ, — для обработчика нажатия ──────────────
     * `__liveAudioCtx` синхронна и отвечает «жив ли объект». Но живой контекст
     * бывает беззвучным: `suspended` по политике автозапуска (норма до первого
     * жеста — сюда не вмешиваемся) или `suspended` после потери устройства
     * (см. выше — там `resume()` виснет). Различить их можно только попыткой:
     * ждём `resume()` не дольше RESUME_MS; если контекст не ожил, а жест у
     * страницы БЫЛ (`navigator.userActivation`), это отказ устройства, а не
     * политика — контекст уходит в отставку, звук получает свежий. Если и
     * свежий не ожил (устройство всё ещё занято другой программой), человеку
     * говорят об этом словами — тем же именованным отказом и тостом, что и при
     * отсутствии контекста вовсе.
     *
     *   var ctx = await window.__liveAudioCtxReady(audioCtx);
     *   // здесь ctx.state === 'running' (или это тот же спящий контекст, если
     *   // жеста у страницы ещё не было); если вернулся ДРУГОЙ объект — прежний
     *   // мёртв вместе со своими узлами, тул обязан это учесть (см. join:
     *   // jnAdoptCtx / jnResetPlayersAfterCtxSwap).
     *
     * RESUME_MS: первое `resume()` открывает устройство ОС и на слабой машине
     * занимает около секунды (замер 30.07: 544–1271 мс) — ждём 2.5 с. Цена
     * ложной тревоги — лишняя пересборка контекста (буферы переносимы), цена
     * долгого ожидания — человек жмёт в пустоту. SETTLE_MS: свежему контексту
     * нужно мгновение, чтобы открыть устройство или получить `error` (в замере
     * ≤ 200 мс). */
    var RESUME_MS = 2500, SETTLE_MS = 600;
    function delay(ms){ return new Promise(function(r){ setTimeout(r, ms); }); }
    function hasActivation(){
        try { return !!(navigator.userActivation && navigator.userActivation.hasBeenActive); } catch(_) { return true; }
    }
    /* Активация ЖИВА прямо сейчас (transient), а не «когда-то была» (sticky).
     * Именно её тратит `await`, и именно её требует WebKit от `play()`.
     * Нечем различить (нет `navigator.userActivation` — старый WebKit, часть
     * встроенных браузеров) → отвечаем «жива», то есть ведём себя как раньше:
     * лучше промолчать о причине, чем выдумать её. */
    function activationLive(){
        try {
            var ua = navigator.userActivation;
            if (!ua || typeof ua.isActive !== 'boolean') return true;
            return ua.isActive;
        } catch(_) { return true; }
    }
    function ctxRunning(c){ try { return !!c && c.state === 'running' && !ctxErrored(c); } catch(_) { return false; } }
    function resumeWithin(c, ms){
        var p; try { p = c.resume(); } catch(e) { p = Promise.reject(e); }
        return Promise.race([
            Promise.resolve(p).then(function(){ return true; }, function(){ return false; }),
            delay(ms).then(function(){ return false; })
        ]);
    }
    // Свежесозданный контекст «running» ещё ничего не доказывает: отказ
    // устройства приходит событием `error` спустя мгновение (замер: ≤200 мс).
    // Поэтому только что рождённому даём SETTLE_MS, и лишь потом верим.
    function unsettled(c){
        try { return !!(c && c.__tmbReborn && performance.now() - (c.__tmbBornAt || 0) < SETTLE_MS); } catch(_) { return false; }
    }
    function ctxFail(c){
        retireCtx(c);
        reportCtx('unavailable');
        acDeadUntil = Date.now() + AC_DEAD_MS;
        throw acUnavailableError(true);
    }
    function settled(c){
        var born = 0; try { born = c.__tmbBornAt || 0; } catch(_) {}
        var wait = Math.max(0, SETTLE_MS - (performance.now() - born));
        return delay(wait).then(function(){
            try { c.__tmbReborn = false; } catch(_) {}
            if (ctxRunning(c)) return c;
            // Пришло `error` — устройство занято; resume() тут только зависнет.
            if (ctxErrored(c)) return ctxFail(c);
            return resumeWithin(c, RESUME_MS).then(function(){
                if (ctxRunning(c)) return c;
                return ctxFail(c);
            });
        });
    }
    // Для тула: контекст только что родился на замену мёртвому — источник ставить
    // рано, сперва __liveAudioCtxReady (она и выждет, и скажет человеку).
    window.__audioCtxUnsettled = unsettled;
    window.__liveAudioCtxReady = function(current, make, opts){
        var ctx;
        try { ctx = window.__liveAudioCtx(current, make); } catch(e) { return Promise.reject(e); }
        if (ctxRunning(ctx)) {
            if (!unsettled(ctx)) return Promise.resolve(ctx);
            return settled(ctx).then(function(c){ reportCtx('recovered'); return c; });
        }
        var ms = (opts && opts.resumeMs) || RESUME_MS;
        var active = hasActivation();
        return resumeWithin(ctx, ms).then(function(){
            if (ctxRunning(ctx)) return ctx;
            // Жеста ещё не было — запрет политики автозапуска, не отказ
            // устройства. Отдаём тот же контекст: его разбудит первый жест.
            if (!active) return ctx;
            markCtxError(ctx, ctxErrored(ctx) ? ctx.__tmbAudioError : 'resume_hung');
            var fresh;
            try { fresh = window.__liveAudioCtx(null, make); } catch(e) { retireCtx(ctx); throw e; }
            retireCtx(ctx);
            return settled(fresh).then(function(c){ reportCtx('recovered'); return c; });
        });
    };
    // Тестовый шов: сторожа моделируют потерю устройства без настоящего железа.
    window.__markAudioCtxError = markCtxError;

    ['touchstart','mousedown','keydown'].forEach(function(evt){
        document.addEventListener(evt,resumeAll,{capture:true,passive:true});
    });
})();

// ── Audio container header repair ──
// Some uploads arrive with a valid audio stream inside a broken/misnamed
// container, and ffmpeg's mov demuxer rejects them with "moov atom not found":
//   • garbage bytes (commonly CRLF \r\n) prepended before the MP4 `ftyp` box
//     — seen from mobile Yandex/YaSearch voice recorders (prod telemetry:
//     header `0d0a…66747970` on /audio-to-text, 19 sessions/30d)
//   • a raw ADTS-AAC stream saved with a .m4a/.mp4 name (header `fff1…`) — the
//     mov demuxer fails but it decodes fine as `-f aac`
// Given the leading bytes, return a normalised {buf, ext} the FFmpeg fallback
// feeds in so these files decode instead of erroring out. Pure + side-effect
// free (safe to call from any decode error path). Returns null when no repair
// applies (well-formed file or unknown shape).
window.__repairAudioContainer=function(buf,fileInfo){
    try{
        if(!buf||!buf.byteLength)return null;
        var n=Math.min(64,buf.byteLength);
        var b=new Uint8Array(buf,0,n);
        // ADTS AAC sync: 12 sync bits 0xFFF + layer 00 → b[0]=0xFF, (b[1]&0xF6)=0xF0.
        if(b[0]===0xFF&&(b[1]&0xF6)===0xF0){
            return {buf:buf,ext:'aac',kind:'raw_adts'};
        }
        // Locate the MP4/MOV `ftyp` box ('ftyp' = 66 74 79 70). Healthy files
        // carry it at offset 4 (after the 4-byte box size). At any other offset
        // k≥4, the real box starts at k-4 — strip the leading garbage.
        for(var i=0;i+8<=n;i++){
            if(b[i]===0x66&&b[i+1]===0x74&&b[i+2]===0x79&&b[i+3]===0x70){
                if(i<=4)return null;  // already well-formed (or can't back up to the size field)
                var brand=String.fromCharCode(b[i+4]||0,b[i+5]||0,b[i+6]||0,b[i+7]||0);
                var ext=/qt/i.test(brand)?'mov':'m4a';
                return {buf:buf.slice(i-4),ext:ext,kind:'stripped_prefix',stripped:(i-4)};
            }
        }
        return null;
    }catch(e){return null;}
};

// ── decodeAudioData: промис-форма для Safari до 14.1 ──
// Safari 12–14.0 (iPhone 5s/6 остались на iOS 12.5) знает только форму с
// колбэками: вызов decodeAudioData(buf) без них бросает СИНХРОННО TypeError
// «Not enough arguments». Обе двери ниже и весь сайт зовут промис-форму — на
// таком телефоне ни один аудиотул не открывал файл (комментарий #2044 к
// /ringtone, 28.10.2026: iOS 12.5.8, все три яруса декодера — «Not enough
// arguments», одна звезда). Эта дверь — самая внутренняя: новый браузер идёт
// прежним путём, на колбэки вызов переводит только синхронный TypeError.
// Старый WebKit зовёт колбэк ошибки с null — промису нужна настоящая ошибка.
(function(){
    var own=Object.prototype.hasOwnProperty;
    var told=false;
    [window.BaseAudioContext,window.AudioContext,window.webkitAudioContext,window.OfflineAudioContext,window.webkitOfflineAudioContext].forEach(function(C){
        try{
            var P=C&&C.prototype;
            if(!P||!own.call(P,'decodeAudioData')||P.decodeAudioData.__tmbPromiseForm)return;
            var orig=P.decodeAudioData;
            var door=function(buf,onSuccess,onError){
                if(typeof onSuccess==='function'||typeof onError==='function')return orig.apply(this,arguments);
                var ctx=this;
                try{ return orig.call(ctx,buf); }
                catch(e){
                    if(!(e instanceof TypeError))throw e;
                    if(!told){ told=true; try{ window.__ae&&window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'decode_callback_form'}); }catch(_){} }
                    return new Promise(function(resolve,reject){
                        orig.call(ctx,buf,resolve,function(err){
                            if(!err){ try{ err=new DOMException('Unable to decode audio data','EncodingError'); }catch(_){ err=new Error('Unable to decode audio data'); err.name='EncodingError'; } }
                            reject(err);
                        });
                    });
                }
            };
            door.__tmbPromiseForm=true;
            P.decodeAudioData=door;
        }catch(_){}
    });
})();

// ── decodeAudioData: фильм без звуковой дорожки — отказ, а не смерть вкладки ──
// Яндекс.Браузер (26.8 и 24.10, Windows) на mp4/mov, где в moov есть дорожки
// и ни одной звуковой, роняет процесс страницы прямо в decodeAudioData — и в
// AudioContext, и в OfflineAudioContext (Reporting API: renderer_crash
// reason:unknown). Chrome 155, Edge 155 и Opera 137 на тех же байтах отвечают
// EncodingError. Замер 20.10.2026 на 26.8.5.811: mp4 (moov в конце, faststart,
// фрагментированный) и mov — падение; webm/mkv без звука, обрезанный mp4 без
// moov, мусор, PNG — EncodingError. Поэтому такой файл до браузера не доходит:
// дверь отвечает тем же EncodingError (+ noAudioTrack:true), что и Chrome.
// Дверь стоит на прототипе, который ВЛАДЕЕТ методом (BaseAudioContext), —
// OfflineAudioContext иначе шёл бы мимо неё (обращение 20.10, /split-screen:
// три падения подряд на «Экспорт»). Разбор — docs/why/primitives/mp4-no-sound.md.
(function(){
    function u32(b,o){return ((b[o]<<24)|(b[o+1]<<16)|(b[o+2]<<8)|b[o+3])>>>0;}
    function t4(b,o){return String.fromCharCode(b[o],b[o+1],b[o+2],b[o+3]);}
    var FIRST=/^(ftyp|moov|mdat|free|skip|wide|pnot|uuid|styp|sidx|moof)$/;
    // handler_type первого hdlr прямо в mdia этой дорожки; null — не прочли.
    function trakHandler(b,s,e){
        for(var p=s;p+8<=e;){
            var sz=u32(b,p); if(sz<8||p+sz>e)return null;
            if(t4(b,p+4)==='mdia'){
                for(var q=p+8,me=p+sz;q+8<=me;){
                    var z=u32(b,q); if(z<8||q+z>me)return null;
                    if(t4(b,q+4)==='hdlr')return q+20<=me?t4(b,q+16):null;
                    q+=z;
                }
                return null;
            }
            p+=sz;
        }
        return null;
    }
    // true — ISO-BMFF, moov прочитан целиком, дорожки есть, звуковой нет;
    // false — звук есть или это не mp4/mov; null — не знаем (тогда решает браузер).
    function soundless(buf){
        var b; try{ b=new Uint8Array(buf); }catch(_){ return false; }
        var n=b.length; if(n<16||!FIRST.test(t4(b,4)))return false;
        for(var p=0,guard=0;p+8<=n&&guard<1024;guard++){
            var size=u32(b,p), hdr=8;
            if(size===1){ if(p+16>n)return null; size=u32(b,p+8)*4294967296+u32(b,p+12); hdr=16; }
            else if(size===0)size=n-p;
            if(size<hdr)return null;
            if(t4(b,p+4)==='moov'){
                if(p+size>n)return null;
                var traks=0;
                for(var q=p+hdr,e=p+size;q+8<=e;){
                    var z=u32(b,q); if(z<8||q+z>e)return null;
                    if(t4(b,q+4)==='trak'){
                        traks++;
                        var h=trakHandler(b,q+8,q+z);
                        if(h===null)return null;
                        if(h==='soun')return false;
                    }
                    q+=z;
                }
                return traks>0?true:null;
            }
            p+=size;
        }
        return null;
    }
    window.__soundlessMovie=soundless;
    var told=false;
    function guard(orig){
        return function(buf,onSuccess,onError){
            var verdict=null;
            try{ if(buf&&buf.byteLength)verdict=soundless(buf); }catch(_){}
            if(verdict!==true)return orig.apply(this,arguments);
            var err;
            try{ err=new DOMException('The file has no audio track','EncodingError'); }
            catch(_){ err=new Error('The file has no audio track'); err.name='EncodingError'; }
            try{ err.noAudioTrack=true; }catch(_){}
            if(!told){ told=true; try{ window.__ae&&window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'decode_no_audio_track',size:buf.byteLength}); }catch(_){} }
            var p=Promise.reject(err);
            if(typeof onError==='function'){ p.catch(function(){}); setTimeout(function(){ try{ onError(err); }catch(_){} },0); }
            return p;
        };
    }
    var own=Object.prototype.hasOwnProperty;
    [window.BaseAudioContext,window.AudioContext,window.webkitAudioContext,window.OfflineAudioContext,window.webkitOfflineAudioContext].forEach(function(C){
        try{
            var P=C&&C.prototype;
            if(!P||!own.call(P,'decodeAudioData')||P.decodeAudioData.__tmbSoundless)return;
            var g=guard(P.decodeAudioData); g.__tmbSoundless=true;
            P.decodeAudioData=g;
        }catch(_){}
    });
})();

// ── Global decodeAudioData fallback via FFmpeg ──
// Some formats (M4A, AMR, unusual codecs) fail native decode on certain browsers.
// When the native attempt rejects, we transparently retry by converting to WAV
// via FFmpeg WASM and decoding that. Tools with their own layered decode logic
// (e.g. dictaphone, which keeps a PCM backup in parallel) can opt out by setting
// window.__suppressDecodeFallback=true around the decodeAudioData call.
(function(){
    var ACtor=window.AudioContext||window.webkitAudioContext;
    if(!ACtor)return;
    var origDecode=ACtor.prototype.decodeAudioData;
    ACtor.prototype.decodeAudioData=function(buf,onSuccess,onError){
        var ctx=this;
        var promise=origDecode.call(ctx,buf.slice?buf.slice(0):buf);
        var enhanced=promise.then(null,function(err){
            // Caller opted out — skip the FFmpeg fallback and propagate the native error
            if(window.__suppressDecodeFallback)return Promise.reject(err);
            // Звуковой дорожки нет — FFmpeg её не создаст, а движок весит 30 МБ.
            if(err&&err.noAudioTrack)return Promise.reject(err);
            if(!window.FFmpegProcessor)return Promise.reject(err);
            var fileInfo=window.__currentFileInfo||{};
            // Bail on huge inputs: the WAV fallback materialises full PCM in
            // MEMFS (~7-30× input size) and easily blows the WASM 32-bit
            // address space. Sentry 116786270 — 191 MB MP3 → 1.43 GB
            // decoded.wav → Aborted().
            //
            // Cap derivation: WASM 32-bit Memory caps at ~2 GB practical;
            // FFmpeg + lavf state already consumes ~200 MB; the WAV buffer
            // grows ~7-30× the source (depends on bitrate, sample rate,
            // channels). Reserve ~1.4 GB max for the decode buffer, then
            // divide by a worst-case 12× expansion ratio → ~120 MB cap on
            // input. When jsHeapSizeLimit is exposed (Chromium), use it
            // directly: budget = min(jsHeapSizeLimit/2, 1.4GB). Otherwise
            // fall back to a deviceMemory tier.
            var inputBytes=(buf&&buf.byteLength)||fileInfo.size||0;
            var devMem=navigator.deviceMemory||4;
            var capMB;
            try{
                var heapLimit=(performance&&performance.memory&&performance.memory.jsHeapSizeLimit)||0;
                if(heapLimit>0){
                    var budgetMB=Math.min(heapLimit/2/(1024*1024),1400);
                    capMB=Math.max(20,Math.floor(budgetMB/12));
                }
            }catch(_){}
            if(!capMB){capMB=devMem>=8?150:(devMem>=4?80:40);}
            if(inputBytes>capMB*1024*1024){
                try{if(window.__ae)window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'decode_fallback_skip_too_big',ext:fileInfo.ext||'',size:inputBytes,capMB:capMB,devMem:devMem})}catch(e){}
                return Promise.reject(err);
            }
            return FFmpegProcessor.init().then(function(ok){
                if(!ok)throw err;
                // Repair broken/misnamed containers (CRLF-prefixed MP4, raw ADTS
                // mislabeled .m4a) before handing to ffmpeg — turns "moov atom
                // not found" hard-fails into clean decodes.
                var _rep=window.__repairAudioContainer?window.__repairAudioContainer(buf,fileInfo):null;
                var _useBuf=(_rep&&_rep.buf)?_rep.buf:buf;
                var _useExt=(_rep&&_rep.ext)?_rep.ext:(fileInfo.ext||'');
                if(_rep){try{if(window.__ae)window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'audio_container_repaired',kind:_rep.kind,toExt:_useExt,fromExt:fileInfo.ext||''})}catch(e){}}
                var blob=new Blob([_useBuf],{type:fileInfo.type||'audio/octet-stream'});
                // Unique in/out names: this shim runs in the BACKGROUND of
                // whatever tool triggered the decode — a fixed "input.mp4" /
                // "decoded.wav" collided in MEMFS with the tool's own
                // FFmpeg calls (probe/thumbnails/encode), and one flow's
                // cleanup deleted another's in-flight input (prod 07-15/16).
                // …and the two names must differ from EACH OTHER too. With
                // "shimdec_N.<ext>" in and "shimdec_N.wav" out, a WAV the
                // browser could not decode (ADPCM, µ-law, float64, a broken
                // header) asked FFmpeg to write over its own input: «Output
                // shimdec_N.wav same as Input #0 — exiting». Measured on prod
                // 22.09–06.10.2026: decode_fallback_fail ext=wav 354 events in
                // 69 sessions across 11 tools, decode_fallback_ok ext=wav — 0.
                // WAV was the one format this fallback could never rescue.
                window.__decShimSeq=(window.__decShimSeq||0)+1;
                var inName='shimdec_'+window.__decShimSeq+(_useExt?'.'+_useExt:'');
                return FFmpegProcessor.process(blob,['-f','wav'],'shimdec_'+window.__decShimSeq+'_out.wav',inName);
            }).then(function(r){
                if(!r||!r.blob||r.blob.size<100)throw err;
                return r.blob.arrayBuffer();
            }).then(function(wavBuf){
                return origDecode.call(ctx,wavBuf);
            }).then(function(decoded){
                try{if(window.__ae)window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'decode_fallback_ok',ext:fileInfo.ext||'',size:fileInfo.size||buf.byteLength||0})}catch(e){}
                return decoded;
            },function(err2){
                // Fallback also failed — logged as feature_use, not tool_error.
                // Callers with their own recovery paths (dictaphone PCM backup) succeed anyway,
                // so the spurious tool_error misrepresented actual outcomes in telemetry.
                try{if(window.__ae)window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'decode_fallback_fail',ext:fileInfo.ext||'',size:fileInfo.size||buf.byteLength||0,msg:(err.message||'').substring(0,100)})}catch(e){}
                throw err;
            });
        });
        // Support callback style (legacy)
        if(typeof onSuccess==='function'){
            enhanced.then(onSuccess,onError||function(){});
            return enhanced;
        }
        return enhanced;
    };
})();

/* ── Один звук на страницу ────────────────────────────────────────────────────
 *
 * Плеер, который глушит «всех остальных» ПО СВОЕМУ СЕЛЕКТОРУ, глушит только
 * своих. На /song-generator карточка трека делала это выражением
 * `querySelectorAll('.sg-pl')`, а образцы жанров живут в отдельном модуле со
 * своим `new Audio()` — и обе стороны честно не знали друг о друге.
 *
 * Обращение #6901 (12.09.2026): «включить образец одного из предложенных
 * жанров — дорожки будут играть параллельно, а возможность остановить
 * сгенерированную песню пропадает». Воспроизведено стендом
 * tests/playwright/song-generator-one-sound-at-a-time.mjs: два источника звучат
 * разом, до тишины одним органом дойти нельзя.
 *
 * ⚠️ Почему реестр, а не слушатель на document: `new Audio()` живёт ВНЕ
 * документа, его события никуда не всплывают, и страничный слушатель такой
 * элемент не видит вовсе — а именно такие тут и играют.
 *
 * claim(el) — «сейчас звучу я»: все прочие известные элементы ставятся на паузу.
 * Звать ПЕРЕД play(). release(el) — забыть элемент (не обязателен: ссылки
 * слабые там, где браузер это умеет).
 */
window.__soloAudio = (function () {
    var known = [];
    function forget(el) {
        var i = known.indexOf(el);
        if (i >= 0) known.splice(i, 1);
    }
    function remember(el) {
        if (!el || known.indexOf(el) >= 0) return;
        known.push(el);
        // Элемент мог начать играть и не через claim() (нативные контролы
        // <audio controls>, autoplay, восстановление после стрима) — тогда
        // солируем по факту события, а не по вежливости вызывающего.
        try { el.addEventListener('play', function () { claim(el); }); } catch (e) {}
    }
    /* Реестр не растёт бесконечно. Играющие держим ВСЕГДА (иначе потеряем
       возможность их остановить — ровно тот дефект, что чиним), а молчащие
       сверх KEEP забываем: на /song-generator каждый <audio> с загруженным
       src стоит около мегабайта (замер в шапке того же блейда: 200 элементов
       = +371 МБ), и сессия с сотней прослушанных треков не должна их копить. */
    var KEEP = 8;
    function claim(el) {
        remember(el);
        var idle = [];
        for (var i = 0; i < known.length; i++) {
            var o = known[i];
            if (o === el) continue;
            var busy = true;
            try { busy = !o.paused; if (busy) o.pause(); } catch (e) {}
            if (!busy) idle.push(o);
        }
        for (var j = 0; j < idle.length - KEEP; j++) forget(idle[j]);
        return el;
    }
    return { claim: claim, release: forget, known: function () { return known.slice(); } };
})();

// ── Screen reader announcer ──
window.__announce=function(msg){var el=document.getElementById('a11yStatus');if(el){el.textContent='';setTimeout(function(){el.textContent=msg},50)}};

/* __ageField:begin */
/* A person's age typed into a number field: ONE owner for «what was typed, and
   is it inside the range this tool serves».

   Why it exists (24.09.2026, /mental-acuity, Android, 16 years old): «В строке
   возраста указываю 16 лет, не пропускает дальше». The check was
   `if (!age || age < 18 || age > 120)` and both branches — empty and out of
   range — said «Укажите ваш возраст»: the person was told to enter the age they
   had just entered, and never learned the test had a range. The same shape was
   found in /dementia-test, /hearing-test (a generic «fill in all fields»),
   /longevity-tests and /beep-test (the result said «age not given»). None of
   them logged the refusal, so the class was invisible: the toast was a
   'warning', and warnings are not sent anywhere.

   Contract:
   · the range is the field's OWN min/max attributes — the only copy of it;
     a tool that also writes the numbers into its JS or its text drifts (it did:
     /menopause-age declared max 75 in HTML and 99 in the engine);
   · read(input)  → {state:'ok'|'missing'|'invalid'|'below'|'above', value, min, max}
                    — no UI; `value` is whole years;
   · check(input, opts) → the age, or null after saying WHY next to the field
                    (it stays until the field is edited — a toast is gone before
                    a phone keyboard closes) and focusing it.
       opts.rangeMsg   — the tool's own sentence for out of range, with :min/:max
                         (say WHY the range exists when there is a reason);
       opts.missingMsg — the tool's own sentence for an empty field;
       opts.after      — element to put the note after (default: the field; pass
                         the wrapper when the field sits inside a tight row);
       opts.focus      — false for live validation (on input/blur), so a note
                         never steals the caret;
   · show(input, text, opts) / clear(input) — the note alone, for a tool that
     decides on its own terms but must say it the same way;
   · a refused value (below / above / invalid) is reported once per value as
     tool_blocked {reason:'age_out_of_range', dir, v, min, max}, and becomes
     window.__LAST_UI_ERROR, so a bug report carries what the person saw.
   Guard: tests/Feature/PersonAgeFieldGoesThroughThePrimitiveTest.php. */
window.__ageField=(function(){
    var MSG={
        missing:{en:'Enter your age.',ru:'Укажите возраст.',uk:'Вкажіть вік.',de:'Geben Sie Ihr Alter an.',es:'Indica tu edad.',fr:'Indiquez votre âge.',pt:'Informe sua idade.',id:'Masukkan usia Anda.',ko:'나이를 입력하세요.',ar:'أدخل عمرك.'},
        range:{en:'Age must be between :min and :max.',ru:'Возраст — от :min до :max лет.',uk:'Вік — від :min до :max років.',de:'Das Alter muss zwischen :min und :max Jahren liegen.',es:'La edad debe estar entre :min y :max años.',fr:'L’âge doit être compris entre :min et :max ans.',pt:'A idade deve estar entre :min e :max anos.',id:'Usia harus antara :min dan :max tahun.',ko:'나이는 :min~:max세 사이로 입력하세요.',ar:'يجب أن يكون العمر بين :min و:max سنة.'}
    };
    var reported={};
    function lang(){
        var l=String(window.__LOCALE||(document.documentElement&&document.documentElement.getAttribute('lang'))||'en').slice(0,2).toLowerCase();
        return MSG.missing[l]?l:'en';
    }
    function num(v){var n=parseFloat(v);return isFinite(n)?n:NaN;}
    function read(input){
        var min=num(input&&input.min),max=num(input&&input.max);
        var raw=input&&input.value!=null?String(input.value).trim():'';
        var bad=!!(input&&input.validity&&input.validity.badInput);
        if(raw==='')return{state:bad?'invalid':'missing',value:null,min:min,max:max};
        var v=Number(raw.replace(',','.'));
        if(!isFinite(v))return{state:'invalid',value:null,min:min,max:max};
        v=Math.floor(v);
        if(!isNaN(min)&&v<min)return{state:'below',value:v,min:min,max:max};
        if(!isNaN(max)&&v>max)return{state:'above',value:v,min:min,max:max};
        return{state:'ok',value:v,min:min,max:max};
    }
    function message(r,opts){
        opts=opts||{};
        var noRange=isNaN(r.min)&&isNaN(r.max);
        if(r.state==='missing'||noRange)return opts.missingMsg||MSG.missing[lang()];
        var m=opts.rangeMsg||MSG.range[lang()];
        return String(m).split(':min').join(isNaN(r.min)?'0':String(r.min)).split(':max').join(isNaN(r.max)?'':String(r.max));
    }
    function noteId(input){return (input.id||'age-field')+'-age-note';}
    function clear(input){
        if(!input)return;
        var n=document.getElementById(noteId(input));
        if(n&&n.parentNode)n.parentNode.removeChild(n);
        if(input.getAttribute&&input.getAttribute('aria-invalid')==='true'){
            input.removeAttribute('aria-invalid');
            if(input.style)input.style.borderColor=input.__afBorder||'';
        }
    }
    function show(input,text,opts){
        if(!input)return;
        opts=opts||{};
        var id=noteId(input),n=document.getElementById(id);
        if(!n){
            /* A span shown as a block: valid phrasing content, so it may sit inside
               a <label> or a <p> as well as a <div> (a <p> inside a <label> is not). */
            n=document.createElement('span');
            n.id=id;
            n.className='age-field-note';
            n.setAttribute('role','alert');
            n.style.cssText='display:block;margin:6px 0 0;font-size:13px;line-height:1.4;font-weight:600;color:var(--danger,#ef4444)';
            var anchor=opts.after||input;
            if(anchor.parentNode)anchor.parentNode.insertBefore(n,anchor.nextSibling);
        }
        n.textContent=text;
        if(input.getAttribute&&input.getAttribute('aria-invalid')!=='true'){
            input.__afBorder=input.style?input.style.borderColor:'';
            input.setAttribute('aria-invalid','true');
        }
        if(input.style)input.style.borderColor='var(--danger,#ef4444)';
        var db=(input.getAttribute&&input.getAttribute('aria-describedby'))||'';
        if((' '+db+' ').indexOf(' '+id+' ')<0)input.setAttribute('aria-describedby',(db?db+' ':'')+id);
        if(!input.__afWired&&input.addEventListener){
            input.__afWired=1;
            input.addEventListener('input',function(){clear(input);});
        }
        try{window.__LAST_UI_ERROR={msg:String(text).substring(0,200),ts:Date.now(),via:'field'};}catch(_){}
    }
    function check(input,opts){
        opts=opts||{};
        var r=read(input);
        if(r.state==='ok'){clear(input);return r.value;}
        show(input,message(r,opts),opts);
        if(opts.focus!==false&&input.focus){try{input.focus();}catch(_){}}
        if(r.state!=='missing'){
            var key=(input.id||'')+':'+r.state+':'+r.value;
            if(!reported[key]){
                reported[key]=1;
                try{if(window.__aeBlocked)window.__aeBlocked({reason:'age_out_of_range',dir:r.state,v:r.value,min:isNaN(r.min)?null:r.min,max:isNaN(r.max)?null:r.max,field:input.id||''});}catch(_){}
            }
        }
        return null;
    }
    return{read:read,check:check,message:message,show:show,clear:clear};
})();
/* __ageField:end */

// ── Toast notification system ──
// Shortcut for user-input validation failures: shows an error toast AND tags the
// event as tool_blocked (with a specific reason) instead of tool_error. Lets tools
// express "this isn't a bug, the user picked a file we can't handle" in one call.
window.__reject=function(msg,reason,file){
    // Default to INVALID_INPUT when caller didn't specify. ToolErrors.REASONS may not be
    // loaded yet (script-load order race), fall back to the literal string in that case —
    // it's the same value the REASONS.INVALID_INPUT constant holds.
    var fallback=(window.ToolErrors&&window.ToolErrors.REASONS&&window.ToolErrors.REASONS.INVALID_INPUT)||'invalid_input';
    // `file` — the REJECTED file, so tool_blocked names it rather than the
    // file already open in the tool: a video refused by /audio-to-video's
    // audio zone used to be reported as the WAV that was loaded (payload from
    // window.__fileCtx), and every per-extension breakdown of rejects lied.
    window.__toast(msg,'error',{reason:reason||fallback,file:(file&&typeof file==='object')?file:null});
};

// ── Zero-size file probe ─────────────────────────────────────────────────────
// `file.size === 0` at pick time is NOT proof the file is empty. Android SAF
// content providers (Huawei/Honor pickers, MediaStore pending entries,
// messenger file providers) and some desktop drag sources hand the browser a
// File whose SIZE metadata is missing → reported as 0 — while the bytes read
// fine through the blob stream. Prod telemetry 06-27..07-02: 221 users hit the
// hard "File is empty" reject across every browser; feedback 2026-07-02 /ru/join
// (HuaweiBrowser, file "1697", size 0, no MIME) retried 5× and gave up.
//
// So: before rejecting a 0-size file, try to actually READ it. Bytes come back
// → rebuild a real in-memory File (correct .size, same name/type/lastModified)
// and let the normal pipeline continue. Nothing readable → the reject was
// honest; caller shows __emptyFileMsg().
//
// Returns Promise<File|null>. Never rejects. Emits `feature_use {f:'empty_probe'}`
// telemetry with the outcome so prod measures the real-world recovery rate.
// `kind` comes from the probe outcome and picks the sentence that is actually
// true for this file. The probe never recovers a byte (measured: 2467 runs,
// 1330 distinct sessions, ZERO recoveries — a File is a path+size+mtime snapshot, and
// Chromium refuses the read outright once the file on disk differs, so the
// «size lied, bytes read fine» premise it was built on cannot hold) — but the
// two failure shapes it CAN tell apart want different advice:
//   'empty'      read succeeded and returned 0 bytes → the file really is empty
//   'read_error' read threw → the handle is dead / the file is not on the device
// No argument → the old combined sentence, so any caller not passing one is
// unchanged.
// ── "We could not read the bytes of this file" — the sentence, in one place ──
// Not a variant of __emptyFileMsg: THAT one answers "your file is 0 bytes",
// which is a different fact and only ever true when the size metadata says so.
// The far larger population is a file whose size is honest and whose bytes are
// unreachable anyway — measured 19-25.08 on the RU shard, 1.77% of every
// Android-Chrome pick (vs 0.11% on Windows Chrome), because the Android
// chooser hands out Google Photos / Drive items that are not ON the phone
// (crbug.com/41452449). Until 26.08 that case had no sentence at all: the
// uploader swallowed the failed prime and each of ~380 tools invented its own
// wording downstream. /3d-photo-to-model said "try a different file", so
// comment #1492 ("Bad experience") picked a second cloud photo and lost again.
//
// 'read_error' — bytes unreachable. Advice must be about MAKING IT LOCAL,
//                because no read API in the renderer can rescue it (the
//                Chromium bug is open since 2019 and every read path funnels
//                through the same FileReaderLoader).
// 'not_found'  — the file moved / was renamed / was deleted after the pick.
//                Here re-picking really is the fix, so say exactly that.
window.__unreadableFileMsg=function(kind){
    var L=(window.__LOCALE||'en').substring(0,2);
    // 'browser' — FileBytes has evidence the BROWSER stopped reading (a read it
    // never finished, or a file it read before, unchanged, refused now): support
    // chat 714, Safari 26.6, seven re-picks of a local mp3 that read at once in
    // another browser on the same Mac. Advice about the file would be false here.
    if(kind==='browser'){
        var B={
            en:'The browser could not read this file. This usually means the browser itself has stalled, not the file. Quit the browser completely (on a Mac: ⌘Q) and open this page again — or open it in another browser.',
            ru:'Браузер не смог прочитать файл — обычно так бывает, когда сбоит сам браузер, а не файл. Закройте браузер полностью (на Mac — ⌘Q) и откройте страницу снова — или откройте её в другом браузере.',
            ko:'브라우저가 이 파일을 읽지 못했습니다. 보통 파일이 아니라 브라우저 자체의 문제입니다. 브라우저를 완전히 종료한 뒤(Mac: ⌘Q) 이 페이지를 다시 열거나 다른 브라우저에서 여세요.',
            ar:'تعذّر على المتصفح قراءة هذا الملف. عادةً تكون المشكلة في المتصفح نفسه لا في الملف. أغلق المتصفح تمامًا (على Mac: ⌘Q) ثم افتح هذه الصفحة من جديد — أو افتحها في متصفح آخر.',
            id:'Browser tidak dapat membaca file ini. Biasanya yang bermasalah adalah browsernya, bukan file-nya. Tutup browser sepenuhnya (di Mac: ⌘Q) lalu buka halaman ini lagi — atau buka di browser lain.',
            pt:'O navegador não conseguiu ler este arquivo. Normalmente o problema é do próprio navegador, não do arquivo. Feche o navegador por completo (no Mac: ⌘Q) e abra esta página de novo — ou abra-a em outro navegador.',
            de:'Der Browser konnte diese Datei nicht lesen. Meist liegt das am Browser selbst, nicht an der Datei. Beenden Sie den Browser vollständig (auf dem Mac: ⌘Q) und öffnen Sie diese Seite erneut — oder öffnen Sie sie in einem anderen Browser.',
            es:'El navegador no pudo leer este archivo. Normalmente la falla es del navegador, no del archivo. Cierra el navegador por completo (en Mac: ⌘Q) y vuelve a abrir esta página, o ábrela en otro navegador.',
            fr:'Le navigateur n’a pas pu lire ce fichier. En général, c’est le navigateur qui est bloqué, pas le fichier. Quittez complètement le navigateur (sur Mac : ⌘Q) puis rouvrez cette page — ou ouvrez-la dans un autre navigateur.',
            uk:'Браузер не зміг прочитати файл — зазвичай так буває, коли збоїть сам браузер, а не файл. Закрийте браузер повністю (на Mac — ⌘Q) і відкрийте сторінку знову — або відкрийте її в іншому браузері.',
            tr:'Tarayıcı bu dosyayı okuyamadı. Genellikle sorun dosyada değil, tarayıcının kendisindedir. Tarayıcıyı tamamen kapatın (Mac’te: ⌘Q) ve bu sayfayı yeniden açın — ya da başka bir tarayıcıda açın.'
        };
        return B[L]||B.en;
    }
    if(kind==='not_found'){
        var N={
            en:'The file moved, was renamed or was deleted after you picked it. Pick it again.',
            ru:'Файл переместили, переименовали или удалили после выбора. Выберите его заново.',
            ko:'선택한 뒤 파일이 이동·이름 변경·삭제되었습니다. 다시 선택하세요.',
            ar:'تم نقل الملف أو إعادة تسميته أو حذفه بعد اختياره. اختره مجددًا.',
            id:'File dipindahkan, diganti nama, atau dihapus setelah Anda memilihnya. Pilih lagi.',
            pt:'O arquivo foi movido, renomeado ou excluído depois de você selecioná-lo. Selecione novamente.',
            de:'Die Datei wurde nach der Auswahl verschoben, umbenannt oder gelöscht. Wählen Sie sie erneut.',
            es:'El archivo se movió, se renombró o se eliminó después de elegirlo. Vuelve a elegirlo.',
            fr:'Le fichier a été déplacé, renommé ou supprimé après la sélection. Re-sélectionnez-le.',
            uk:'Файл перемістили, перейменували або видалили після вибору. Виберіть його ще раз.',
            tr:'Dosya seçildikten sonra taşındı, yeniden adlandırıldı veya silindi. Dosyayı yeniden seçin.'
        };
        return N[L]||N.en;
    }
    // Default = 'read_error'. Google Photos is named FIRST on purpose: it is
    // the source the Android chooser opens by default for image/* picks, and
    // it is where a photo is cloud-only after "Free up space".
    var R={
        en:'This file could not be read. If it is stored in the cloud (Google Photos, Google Drive, iCloud, OneDrive), download it to this device first — or pick it through the Files app — then choose it again.',
        ru:'Файл не удалось прочитать. Если он хранится в облаке (Google Фото, Google Диск, Яндекс Диск, iCloud, OneDrive) — сначала скачайте его на это устройство или выберите через «Файлы», затем выберите заново.',
        ko:'이 파일을 읽을 수 없습니다. 클라우드(Google 포토, Google 드라이브, iCloud, OneDrive)에 있다면 먼저 이 기기에 다운로드하거나 파일 앱에서 선택한 뒤 다시 고르세요.',
        ar:'تعذّرت قراءة هذا الملف. إذا كان مخزّنًا في السحابة (صور Google أو Google Drive أو iCloud أو OneDrive) فنزّله إلى هذا الجهاز أولًا، أو اختره من تطبيق «الملفات»، ثم اختره مجددًا.',
        id:'File ini tidak dapat dibaca. Jika tersimpan di cloud (Google Photos, Google Drive, iCloud, OneDrive), unduh dulu ke perangkat ini — atau pilih lewat aplikasi File — lalu pilih lagi.',
        pt:'Não foi possível ler este arquivo. Se ele estiver na nuvem (Google Fotos, Google Drive, iCloud, OneDrive), baixe-o para este dispositivo — ou escolha-o pelo app Arquivos — e selecione novamente.',
        de:'Diese Datei konnte nicht gelesen werden. Liegt sie in der Cloud (Google Fotos, Google Drive, iCloud, OneDrive), laden Sie sie zuerst auf dieses Gerät herunter — oder wählen Sie sie über die Dateien-App — und wählen Sie sie dann erneut.',
        es:'No se pudo leer este archivo. Si está en la nube (Google Fotos, Google Drive, iCloud, OneDrive), descárgalo primero a este dispositivo — o elígelo desde la app Archivos — y vuelve a seleccionarlo.',
        fr:'Impossible de lire ce fichier. S’il est stocké dans le cloud (Google Photos, Google Drive, iCloud, OneDrive), téléchargez-le d’abord sur cet appareil — ou choisissez-le depuis l’app Fichiers — puis re-sélectionnez-le.',
            uk:'Не вдалося прочитати цей файл. Якщо він зберігається в хмарі (Google Фото, Google Диск, iCloud, OneDrive), спершу завантажте його на цей пристрій — або виберіть інший файл.'
    };
    return R[L]||R.en;
};
window.__emptyFileMsg=function(kind){
    if(kind==='empty'){
        var E={
            en:'This file contains 0 bytes — it was saved empty or never finished downloading. Check it and pick it again.',
            ru:'В этом файле 0 байт — он сохранён пустым или не докачался. Проверьте файл и выберите заново.',
            ko:'이 파일은 0바이트입니다 — 비어 있게 저장되었거나 다운로드가 끝나지 않았습니다. 확인 후 다시 선택하세요.',
            ar:'حجم هذا الملف 0 بايت — حُفظ فارغًا أو لم يكتمل تنزيله. تحقّق منه ثم اختره مجددًا.',
            id:'File ini berisi 0 byte — tersimpan kosong atau unduhannya belum selesai. Periksa lalu pilih lagi.',
            pt:'Este arquivo tem 0 bytes — foi salvo vazio ou o download não terminou. Verifique-o e selecione novamente.',
            de:'Diese Datei hat 0 Bytes — sie wurde leer gespeichert oder der Download wurde nicht beendet. Prüfen Sie sie und wählen Sie erneut.',
            es:'Este archivo tiene 0 bytes: se guardó vacío o la descarga no terminó. Compruébalo y vuelve a elegirlo.',
            fr:'Ce fichier fait 0 octet — il a été enregistré vide ou le téléchargement ne s’est pas terminé. Vérifiez-le puis re-sélectionnez-le.',uk:'У цьому файлі 0 байт — він збережений порожнім або не докачався. Перевірте файл і виберіть знову.'
        };
        var le=(window.__LOCALE||'en').substring(0,2);return E[le]||E.en;
    }
    if(kind==='read_error'){return window.__unreadableFileMsg('read_error');}
    var L={
        en:'File is empty or unreadable (0 bytes). If it lives in cloud storage, download it to your device first, then pick it again.',
        ru:'Файл пустой или не читается (0 байт). Если он хранится в облаке — сначала скачайте его на устройство и выберите заново.',
        ko:'파일이 비어 있거나 읽을 수 없습니다(0바이트). 클라우드에 있는 파일이라면 먼저 기기에 다운로드한 후 다시 선택하세요.',
        ar:'الملف فارغ أو تعذّرت قراءته (0 بايت). إذا كان محفوظًا في السحابة، نزّله إلى جهازك أولًا ثم اختره مجددًا.',
        id:'File kosong atau tidak dapat dibaca (0 byte). Jika file ada di cloud, unduh dulu ke perangkat Anda lalu pilih lagi.',
        pt:'O arquivo está vazio ou ilegível (0 bytes). Se estiver na nuvem, baixe-o para o dispositivo primeiro e selecione novamente.',
        de:'Die Datei ist leer oder nicht lesbar (0 Bytes). Liegt sie in der Cloud, laden Sie sie zuerst auf Ihr Gerät herunter und wählen Sie sie erneut.',
        es:'El archivo está vacío o no se puede leer (0 bytes). Si está en la nube, descárgalo primero a tu dispositivo y vuelve a elegirlo.',
        fr:'Le fichier est vide ou illisible (0 octet). S’il est stocké dans le cloud, téléchargez-le d’abord sur votre appareil puis re-sélectionnez-le.',uk:'Файл порожній або не читається (0 байт). Якщо він зберігається у хмарі — спочатку завантажте його на пристрій і виберіть знову.'
    };
    var loc=(window.__LOCALE||'en').substring(0,2);
    return L[loc]||L.en;
};
window.__probeEmptyFile=function(file,opts){
    opts=opts||{};
    var t0=Date.now();
    // Rebuild cap: the real size is unknown (metadata lied), so the stream read
    // is chunk-capped to keep a lying multi-GB file from OOMing the tab while
    // we materialize it. deviceMemory-scaled: 4 GB device → 256 MB, ≥8 GB → 512 MB.
    var cap=opts.cap||Math.min(512,Math.max(64,(navigator.deviceMemory||4)*64))*1048576;
    var src=opts.src||'';
    function emitProbe(out,via,bytes){
        // Hand the outcome back to THIS caller (not a shared global — a
        // multi-file pick probes several files at once). It decides which
        // sentence is true: 'empty' → the file really holds 0 bytes,
        // 'read_error' → the handle is dead.
        try{opts.out=out;}catch(_){}
        try{
            if(typeof window.__ae==='function'){
                var slug=(document.querySelector('meta[name="tool-slug"]')||{}).content||null;
                window.__ae('feature_use',{slug:slug,f:'empty_probe',out:out,via:via||'',b:bytes||0,
                    ext:((file&&file.name)||'').split('.').pop().toLowerCase().slice(0,8),
                    mime:((file&&file.type)||'').slice(0,40),src:src,ms:Date.now()-t0});
            }
        }catch(_){}
    }
    function rebuild(chunks,total,via){
        var bytes=new Uint8Array(total),off=0;
        for(var i=0;i<chunks.length;i++){bytes.set(chunks[i],off);off+=chunks[i].length;}
        var out=null;
        try{out=new File([bytes],file.name||'file',{type:file.type||'',lastModified:file.lastModified||Date.now()});}catch(_){}
        emitProbe(out?'recovered':'rebuild_failed',via,total);
        return out;
    }
    function viaReader(){
        return new Promise(function(resolve){
            var fr;
            try{fr=new FileReader();}catch(_){emitProbe('empty','none',0);resolve(null);return;}
            fr.onload=function(){
                var u8=null;
                try{u8=new Uint8Array(fr.result||new ArrayBuffer(0));}catch(_){}
                if(u8&&u8.length>0){
                    if(u8.length>cap){emitProbe('cap_exceeded','reader',u8.length);resolve(null);return;}
                    resolve(rebuild([u8],u8.length,'reader'));return;
                }
                emitProbe('empty','reader',0);resolve(null);
            };
            fr.onerror=function(){emitProbe('read_error','reader',0);resolve(null);};
            try{fr.readAsArrayBuffer(file);}catch(_){emitProbe('read_error','reader',0);resolve(null);}
        });
    }
    if(!file||typeof file.size!=='number'||file.size>0)return Promise.resolve(null);
    if(typeof file.stream==='function'){
        try{
            var reader=file.stream().getReader();
            var chunks=[],total=0;
            var pump=function(){
                return reader.read().then(function(r){
                    if(r.done)return null;
                    var c=r.value;chunks.push(c);total+=(c&&(c.length||c.byteLength))||0;
                    if(total>cap){try{reader.cancel();}catch(_){}var e=new Error('cap');e.__cap=1;throw e;}
                    return pump();
                });
            };
            return pump().then(function(){
                if(total>0)return rebuild(chunks,total,'stream');
                // Stream honored the lying 0-length. FileReader routes through the
                // same blob backend in modern engines, but costs one no-op read in
                // an already-failed case and rescues engines where the paths differ.
                return viaReader();
            }).catch(function(e){
                if(e&&e.__cap){emitProbe('cap_exceeded','stream',total);return null;}
                return viaReader();
            });
        }catch(_){return viaReader();}
    }
    return viaReader();
};

// ── Dropped folders ─────────────────────────────────────────────────────────
// Chromium hands a dropped DIRECTORY to `dataTransfer.files` as a File with
// size 0, type '' and the folder's name — indistinguishable, by those three
// fields alone, from an empty file. So it walked straight into the 0-byte
// reject above and every person who dragged a folder onto a tool was told
// «Файл пустой или не читается (0 байт). Если он хранится в облаке — сначала
// скачайте его на устройство» about a folder that was never in a cloud.
// Measured 30 d before this shipped: of 318 sessions whose 0-byte pick had no
// MIME, 277 carried a name that is not a known extension — `новая па`(пка),
// `dcim`, `videos`, `meshes`, `rammstei`(n), `zapret`, `e_drive`. Reproduced
// exactly (tests/playwright/folder-drop.mjs) on file-converter and join, on
// the zone AND off it.
//
// The only thing that tells a folder from a file is the entry API, and it must
// be read SYNCHRONOUSLY inside the drop handler — `dataTransfer.items` is
// neutered the moment the handler returns. So: snapshot(dt) synchronously,
// walk() afterwards at leisure.
//
// ⚠️ A zone that walks folders ITSELF must be left alone (archive-creator's
// arcDrop, disk-image-creator, equalizer): they already produce a correct
// nested tree, and "helping" them would double-add. The uploader opts them out
// via allow-empty; the page-level handler via the wired/local-drop check.
window.__folderDrop={
    // Sync. Returns null when nothing was dropped as a directory, so the
    // caller's fast path (plain files) is untouched.
    snapshot:function(dt){
        var items=(dt&&dt.items)?Array.prototype.slice.call(dt.items):[];
        var dirs=[],loose=[],handles=[];
        for(var i=0;i<items.length;i++){
            var it=items[i];
            if(it.kind!=='file')continue;
            var ent=null;
            try{ent=it.webkitGetAsEntry&&it.webkitGetAsEntry();}catch(_){}
            // A real FileSystemDirectoryHandle, for the tools whose INPUT is a
            // folder (/media-rescue picks a dying card; the entry API above
            // gives a read-only tree, this gives the same handle the directory
            // picker would). Like webkitGetAsEntry it must be CALLED
            // synchronously — the promise may be awaited at leisure, the call
            // may not wait. Chromium only; elsewhere the list stays empty and
            // every existing path behaves exactly as before.
            if(ent&&ent.isDirectory&&typeof it.getAsFileSystemHandle==='function'){
                try{handles.push(it.getAsFileSystemHandle());}catch(_){}
            }
            if(ent&&ent.isDirectory)dirs.push(ent);
            else if(ent&&ent.isFile){var lf=it.getAsFile&&it.getAsFile();if(lf)loose.push(lf);}
        }
        return dirs.length?{dirs:dirs,loose:loose,handles:handles}:null;
    },
    // Async walk. Bounded three ways — a dropped Downloads folder is a real
    // possibility and an unbounded readEntries loop would hang the tab with no
    // way back. Reports what it dropped rather than silently truncating.
    walk:function(dirs,opts){
        opts=opts||{};
        var maxFiles=opts.maxFiles||500,maxDepth=opts.maxDepth||6,budget=opts.budgetMs||8000;
        // Two different things, and conflating them cost a real bug in review:
        //   stop      — abandon the whole walk (file cap / time budget spent)
        //   truncated — something was left out, keep going and report it
        // Marking a depth cap as `stop` aborted the siblings too, so a folder
        // with one deep branch lost the files sitting right at its top level.
        var t0=Date.now(),out=[],seenFiles=0,truncated=false,stop=false;
        function readAll(reader){
            // readEntries() hands back at most ~100 per call and signals the end
            // with an empty batch — a single call silently loses the rest.
            return new Promise(function(res){
                var acc=[];
                (function pump(){
                    reader.readEntries(function(batch){
                        if(!batch||!batch.length)return res(acc);
                        acc=acc.concat(Array.prototype.slice.call(batch));
                        // Stop listing an absurdly wide directory — but say so,
                        // or the caller reports a clean "ingested" for a folder
                        // it only half-read.
                        if(acc.length>maxFiles*4){truncated=true;return res(acc);}
                        pump();
                    },function(){res(acc);});
                })();
            });
        }
        function walkEntry(ent,path,depth){
            if(stop)return Promise.resolve();
            if(Date.now()-t0>budget){stop=truncated=true;return Promise.resolve();}
            if(ent.isFile){
                seenFiles++;
                if(out.length>=maxFiles){stop=truncated=true;return Promise.resolve();}
                return new Promise(function(res){
                    ent.file(function(f){
                        // Publish the path INSIDE the folder the same way
                        // <input webkitdirectory> does, because that is the
                        // property tools already read (iso-creator's _addEntries
                        // uses `f.webkitRelativePath || f.name`). Left unset,
                        // every walked file looks top-level and a two-CD album
                        // flattens into one heap. It is a prototype accessor, so
                        // shadow it with an own value; `__relPath` stays as a
                        // fallback for engines that refuse the redefine.
                        var rel=path+ent.name;
                        try{f.__relPath=rel;}catch(_){}
                        try{Object.defineProperty(f,'webkitRelativePath',{value:rel,configurable:true});}catch(_){}
                        out.push(f);res();
                    },function(){res();});
                });
            }
            if(ent.isDirectory){
                // Hitting the depth cap is a truncation like any other — left
                // unflagged it reads to the caller as "that's all there was".
                if(depth>=maxDepth){truncated=true;return Promise.resolve();}
                var rd=ent.createReader();
                return readAll(rd).then(function(children){
                    return children.reduce(function(p,c){
                        return p.then(function(){return walkEntry(c,path+ent.name+'/',depth+1);});
                    },Promise.resolve());
                });
            }
            return Promise.resolve();
        }
        return dirs.reduce(function(p,d){
            return p.then(function(){return walkEntry(d,'',0);});
        },Promise.resolve()).then(function(){
            return {files:out,seen:seenFiles,truncated:truncated,ms:Date.now()-t0,
                    names:dirs.map(function(d){return d.name;})};
        });
    },
    // One localized sentence per outcome. Inline map, matching the convention
    // the other uploader rejects already use (REJECT_EMPTY_L et al.).
    msg:function(kind,n){
        var L={
            folder_empty:{
                en:'That is a folder, and it is empty.',
                ru:'Это папка, и она пуста.',
                ko:'폴더이며, 비어 있습니다.',
                ar:'هذا مجلد، وهو فارغ.',
                id:'Itu folder, dan isinya kosong.',
                pt:'Isso é uma pasta, e está vazia.',
                de:'Das ist ein Ordner, und er ist leer.',
                es:'Eso es una carpeta y está vacía.',
                fr:'Ceci est un dossier, et il est vide.',uk:'Це папка, і вона порожня.'
            },
            folder_no_match:{
                en:'That is a folder, not a file. Nothing inside it fits this tool — open it and drag the file you need.',
                ru:'Это папка, а не файл. Подходящих файлов внутри нет — откройте её и перетащите нужный файл.',
                ko:'파일이 아니라 폴더입니다. 이 도구에 맞는 파일이 안에 없습니다 — 폴더를 열고 필요한 파일을 끌어다 놓으세요.',
                ar:'هذا مجلد وليس ملفًا. لا يوجد بداخله ما يناسب هذه الأداة — افتحه واسحب الملف الذي تحتاجه.',
                id:'Itu folder, bukan file. Tidak ada isinya yang cocok untuk alat ini — buka foldernya lalu seret file yang Anda perlukan.',
                pt:'Isso é uma pasta, não um arquivo. Nada dentro dela serve para esta ferramenta — abra-a e arraste o arquivo que precisa.',
                de:'Das ist ein Ordner, keine Datei. Darin passt nichts zu diesem Werkzeug — öffnen Sie ihn und ziehen Sie die gewünschte Datei hierher.',
                es:'Eso es una carpeta, no un archivo. Nada de su interior sirve para esta herramienta — ábrela y arrastra el archivo que necesitas.',
                fr:'Ceci est un dossier, pas un fichier. Rien à l’intérieur ne convient à cet outil — ouvrez-le et faites glisser le fichier voulu.',uk:'Це папка, а не файл. Придатних файлів усередині немає — відкрийте її та перетягніть потрібний файл.'
            },
            // Ingested, but the walk stopped early (file cap, depth cap, or the
            // time budget). Saying nothing here would read as "all of it came
            // in" — the user would go looking for tracks that were never added.
            folder_partial:{
                en:'Only part of the folder was read — files: :n. The rest did not fit in one go.',
                ru:'Прочитана только часть папки — файлов: :n. Остальные не поместились за один раз.',
                ko:'폴더의 일부만 읽었습니다 — 파일: :n개. 나머지는 한 번에 담기지 않았습니다.',
                ar:'تمت قراءة جزء من المجلد فقط — الملفات: :n. لم يتّسع الباقي دفعة واحدة.',
                id:'Hanya sebagian folder yang terbaca — file: :n. Sisanya tidak muat sekaligus.',
                pt:'Apenas parte da pasta foi lida — arquivos: :n. O restante não coube de uma vez.',
                de:'Es wurde nur ein Teil des Ordners gelesen — Dateien: :n. Der Rest passte nicht auf einmal.',
                es:'Solo se leyó parte de la carpeta — archivos: :n. El resto no cupo de una vez.',
                fr:'Une partie du dossier seulement a été lue — fichiers : :n. Le reste n’a pas tenu en une fois.',uk:'Прочитано лише частину папки — файлів: :n. Решта не вмістилася за один раз.'
            },
            folder_pick_one:{
                en:'That is a folder. Suitable files inside: :n. This tool takes one file — drag the one you need.',
                ru:'Это папка. Подходящих файлов в ней: :n. Этот инструмент берёт один файл — перетащите нужный.',
                ko:'폴더입니다. 안에 있는 적합한 파일: :n개. 이 도구는 파일 하나만 받습니다 — 필요한 파일을 끌어다 놓으세요.',
                ar:'هذا مجلد. الملفات المناسبة بداخله: :n. تقبل هذه الأداة ملفًا واحدًا — اسحب الملف الذي تريده.',
                id:'Itu folder. File yang cocok di dalamnya: :n. Alat ini menerima satu file — seret file yang Anda perlukan.',
                pt:'Isso é uma pasta. Arquivos adequados dentro: :n. Esta ferramenta aceita um arquivo — arraste o que você precisa.',
                de:'Das ist ein Ordner. Passende Dateien darin: :n. Dieses Werkzeug nimmt eine Datei — ziehen Sie die gewünschte hierher.',
                es:'Eso es una carpeta. Archivos adecuados dentro: :n. Esta herramienta acepta un archivo — arrastra el que necesitas.',
                fr:'Ceci est un dossier. Fichiers adaptés à l’intérieur : :n. Cet outil prend un seul fichier — faites glisser celui qu’il vous faut.',uk:'Це папка. Придатних файлів у ній: :n. Цей інструмент бере один файл — перетягніть потрібний.'
            }
        };
        var m=L[kind]||L.folder_no_match;
        var loc=(window.__LOCALE||'en').substring(0,2);
        return (m[loc]||m.en).replace(':n',String(n==null?0:n));
    }
};

// ── Canvas GPU texture-size guard ───────────────────────────────────────────
// A 2-D <canvas> whose BACKING store is wider/taller than the GPU's
// MAX_TEXTURE_SIZE composites to BLANK on screen — the compositor can't upload
// it as a texture (texImage2D → GL INVALID_VALUE) — yet getImageData still reads
// the CPU buffer, so it's invisible to pixel/screenshot tests. Mobile GPUs cap
// at 4096–8192; desktop ≥16384. Zoomable waveforms / spectrograms and
// duration-sized timelines blow past this on long files or high zoom (e.g. a
// 198 s recording → ~15 800 px; a waveform at 25× zoom → 15 000 px) and vanish
// on mobile. (galina-e51 06-28 "Нот нет" on audio-to-notes; same root across
// volume / video-trim / silence-remover / audio-forensics.)
//
// __canvasTexLimit() — cached device MAX_TEXTURE_SIZE, floored 2048 / ceiled 16384.
// __fitCanvasBacking(canvas, cssW, cssH, dpr) — sizes the backing for a cssW×cssH
//   CSS box at dpr WITHOUT exceeding the limit, and RETURNS the effective scale
//   to feed ctx.setTransform/scale (so drawing stays in CSS units). Caps softly
//   downscale very wide canvases (slightly blurry) instead of blanking them.
// __screenPx() — PHYSICAL screen resolution, in device pixels.
//
// `screen.width`/`screen.height` are CSS pixels, NOT device pixels. A 1080×2400
// phone at dpr 3 reports 360×800; a 2560×1600 Retina panel reports 1280×800; a
// 1920×1080 laptop at Windows 125% scaling reports 1536×864. Multiply by dpr to
// recover what the panel actually has.
//
// Use this ANYWHERE we (a) show the user "your display resolution" or (b) score
// a panel against real-world thresholds (1280p / 1080p / 1440p / 4K). Raw screen.*
// for either is a bug: every phone lands under the 1280 threshold, so a QHD
// flagship and an $80 720p handset score identically.
//
// (Reported 2026-07-08 on /phone-benchmark, feedback #838: "I don't have an 360p
// dispaly i have a 1080p display" — the card printed the CSS 360×800.)
//
// Do NOT use this for fingerprint-surface tools (privacy-audit / ip-lookup /
// vpn-checker / trust-probe): those must show the CSS px a tracker actually sees.
//
//   { w, h }         physical device px — what the panel has
//   { cssW, cssH }   CSS px — what layout code and fingerprinters see
//   { long, short }  max/min of w,h — orientation-independent
//   { dpr, label }   device pixel ratio, and "1080×2400" ready to print
//   longAtLeast(n) / shortAtLeast(n) — threshold tests; USE THESE for scoring
//
// Precision: `screen.*` is rounded to whole CSS px, so each physical dimension
// carries up to dpr/2 px of recovered-rounding error (integral dpr is exact:
// 360×800 @3 -> exactly 1080×2400). Browsers expose no true-panel-px API, so
// this is the ceiling — do NOT "improve" it with a device-model lookup table.
// The error is why thresholds must not be compared with a bare `>=`: a real
// 1440-px QHD panel at dpr 3.5 computes to 1438.5, and `>= 1440` would drop it
// a scoring tier. longAtLeast/shortAtLeast widen by exactly that dpr/2 bound.
window.__screenPx = function () {
    var dpr  = window.devicePixelRatio || 1;
    var cssW = (window.screen && screen.width)  || 0;
    var cssH = (window.screen && screen.height) || 0;
    var exactW = cssW * dpr, exactH = cssH * dpr;
    var w = Math.round(exactW), h = Math.round(exactH);
    var slack = dpr / 2;
    var longExact = Math.max(exactW, exactH), shortExact = Math.min(exactW, exactH);
    return {
        w: w, h: h,
        cssW: cssW, cssH: cssH,
        dpr: dpr,
        long: Math.max(w, h), short: Math.min(w, h),
        label: w + '×' + h,
        longAtLeast:  function (n) { return longExact  >= n - slack; },
        shortAtLeast: function (n) { return shortExact >= n - slack; }
    };
};

/* __refreshHz:begin */
// __refreshHz({frames, maxMs}) → Promise<{hz, raw, frames, drops} | null>
//
// How often THIS page gets a frame — the display refresh as the browser runs
// it. null when the page is hidden (no frames at all) or too few arrived.
//
// Three tools carried three estimators, all wrong under load in the same way:
// a mean of trimmed intervals (/phone-benchmark, /gaming-pc-scanner) and a
// frames-per-second count (/system-diagnostics) both average DROPPED frames
// into the period. Prod /phone-benchmark: of 3009 phone runs, hundreds read
// 30, 41–58 or 61–66 Hz — no panel runs at 47 Hz. Measured 2026-10-28 with
// induced main-thread stalls (4 runs per scenario per estimator):
//   real 75 Hz panel, 50% frames dropped → trimmed mean 67–72, count 62–67,
//     median-filter 63–68; 70% dropped → 56–64 for all three;
//   headless 60 Hz, 10% of frames stalled 60 ms (a GC) → mean 52–60, count 43–57.
// The refresh is the SHORTEST interval the page keeps getting, not the
// average one: a stall only ever lengthens an interval. So the base is the
// 25th percentile (survives up to 75% dropped frames), the period is the mean
// of intervals within −20 %…+25 % of it, and the result snaps to a standard
// rate within 3 %. It read 75 and 60 in every run of every scenario.
// Hidden page: no frames come, the guard answers with what it has (or null) —
// a hidden page has no refresh rate worth reporting.
window.__refreshHz = function (opts) {
    opts = opts || {};
    var want = opts.frames > 0 ? opts.frames : 90;
    var maxMs = opts.maxMs > 0 ? opts.maxMs : 2500;
    var SKIP = 5;   /* the first frames after a call carry the caller's own work */
    var STD = [24, 30, 48, 50, 60, 72, 75, 85, 90, 100, 120, 144, 160, 165, 170, 180, 200, 240, 360];
    return new Promise(function (resolve) {
        if (document.visibilityState === 'hidden') { resolve(null); return; }
        var ts = [], raf = 0, done = false, guard = 0;
        var t0 = performance.now();
        function finish() {
            if (done) return;
            done = true;
            if (raf) cancelAnimationFrame(raf);
            clearTimeout(guard);
            var d = [];
            for (var i = SKIP + 1; i < ts.length; i++) d.push(ts[i] - ts[i - 1]);
            if (d.length < 10) { resolve(null); return; }
            var s = d.slice().sort(function (a, b) { return a - b; });
            var base = s[Math.floor(s.length * 0.25)];
            var sum = 0, n = 0, drops = 0;
            for (var k = 0; k < d.length; k++) {
                if (d[k] >= base * 0.8 && d[k] <= base * 1.25) { sum += d[k]; n++; }
                else if (d[k] > base * 1.5) drops++;
            }
            if (!n || !(sum > 0)) { resolve(null); return; }
            var raw = 1000 / (sum / n), hz = Math.round(raw);
            for (var j = 0; j < STD.length; j++) {
                if (Math.abs(raw - STD[j]) / STD[j] < 0.03) { hz = STD[j]; break; }
            }
            resolve({ hz: hz, raw: Math.round(raw * 10) / 10, frames: d.length, drops: drops });
        }
        guard = setTimeout(finish, maxMs + 500);
        function frame(t) {
            ts.push(t);
            if (ts.length >= want + SKIP + 1 || t - t0 >= maxMs) { finish(); return; }
            raf = requestAnimationFrame(frame);
        }
        raf = requestAnimationFrame(frame);
    });
};
/* __refreshHz:end */

window.__canvasTexLimit = window.__canvasTexLimit || (function(){
    var cached = 0;
    return function(){
        if (cached) return cached;
        try {
            var c = document.createElement('canvas');
            var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
            var m = gl && (gl.getParameter(gl.MAX_TEXTURE_SIZE) | 0);
            c.width = c.height = 0;
            cached = Math.max(2048, Math.min(m || 4096, 16384));
        } catch (e) { cached = 4096; }
        return cached;
    };
})();
window.__fitCanvasBacking = window.__fitCanvasBacking || function(canvas, cssW, cssH, dpr){
    dpr = dpr || (window.devicePixelRatio || 1);
    cssW = Math.max(1, cssW || 1); cssH = Math.max(1, cssH || 1);
    var cap = window.__canvasTexLimit();
    var longest = Math.max(cssW, cssH);
    var scale = (longest * dpr > cap) ? Math.max(0.0001, cap / longest) : dpr;
    canvas.width  = Math.max(1, Math.min(cap, Math.round(cssW * scale)));
    canvas.height = Math.max(1, Math.min(cap, Math.round(cssH * scale)));
    return scale;
};

// __pickPlayableRecMime(candidates, kind) — choose a MediaRecorder mimeType this
// browser can ALSO play back in an <audio>/<video> element.
//
// iOS Safari (and some Android WebViews) reports MediaRecorder.isTypeSupported(
// 'audio/webm;codecs=opus') === true, so a naive "first recordable" picker records
// webm. But its media element can't DECODE webm — <audio>.canPlayType('audio/webm;
// codecs=opus') === '' — so the clip plays back SILENTLY with no 'error' event and
// no rejected Promise. The recording is valid (decodeAudioData can still analyse it);
// only the <audio>/<video> playback path is broken. Tools that record a clip and then
// play it back in-page (dictaphone, mic-test, snoring-monitor, speech-to-text preview,
// megaphone) therefore look dead on iOS: "I recorded but hear nothing."
//
// The browser's own canPlayType is the self-correcting gate: on Chrome/Firefox webm
// both records AND plays, so it's kept (smaller/better); only where record⟹play FAILS
// (iOS) do we fall through to audio/mp4 (AAC), which iOS both records and plays. If a
// candidate records but none is playable we return the first recordable (transcode /
// decode / download paths still work); if nothing records we return '' (let the browser
// pick its default recording format, which is always one it can also play).
//
// Returns a mimeType string, or '' meaning "use MediaRecorder's default" — callers do
// `mime ? new MediaRecorder(stream,{mimeType:mime}) : new MediaRecorder(stream)`.
window.__pickPlayableRecMime = window.__pickPlayableRecMime || function(candidates, kind){
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
    candidates = candidates || ['audio/webm;codecs=opus','audio/webm','audio/ogg;codecs=opus','audio/mp4','audio/aac'];
    var probe;
    try { probe = document.createElement(kind === 'video' ? 'video' : 'audio'); } catch(e){ probe = null; }
    var firstRecordable = '';
    for (var i = 0; i < candidates.length; i++) {
        var m = candidates[i];
        try { if (!MediaRecorder.isTypeSupported(m)) continue; } catch(e){ continue; }
        if (!firstRecordable) firstRecordable = m;
        // No probe (very old engine) → trust isTypeSupported and take the first.
        if (!probe) return firstRecordable;
        try { if (probe.canPlayType(m) !== '') return m; } catch(e){}
    }
    return firstRecordable; // '' if nothing recordable → browser default (also playable)
};

/* ── window.__unsavedWork — «идёт долгая работа, уходить нельзя» ─────────────
 * ЗАЧЕМ (замер 2026-07-29 по жалобе andruchey@yandex.ru «тупо сбросилась ваша
 * страница»): у profanity-remover(-video) за 30 дней 267 сессий из 2550 с
 * загруженным файлом (10.5%) теряли страницу ПОСРЕДИ обработки и грузили файл
 * заново; 99 из них так и не получили результат. Распознавание 49-минутного
 * видео идёт ~21 минуту, всё состояние живёт в памяти вкладки — одна случайная
 * перезагрузка или закрытие стирает и извлечение, и весь прогресс.
 *
 * Класс общий для всех тяжёлых WASM-тулов, поэтому примитив ОБЩИЙ: тул не
 * должен заводить свой beforeunload (их уже 0 в проекте — начинаем правильно).
 *
 * ⚡ГЛАВНОЕ ПО РЕАЛИЗАЦИИ: слушатель вешается ТОЛЬКО на время работы и
 * снимается сразу. Постоянный beforeunload-листенер отключает back/forward
 * cache ВСЕМУ САЙТУ (Chrome/Firefox не кладут в bfcache страницы с ним) —
 * то есть «защита» одного тула замедлила бы навигацию везде.
 *
 * Текст диалога задаёт БРАУЗЕР (кастомные строки запрещены с 2017) — поэтому
 * i18n тут не нужен и не поможет; наша задача только поднять диалог.
 *
 * API:  __unsavedWork.arm('profanity:asr')   — начали долгую работу
 *       __unsavedWork.disarm()               — закончили/отменили/упали
 *       __unsavedWork.isArmed()              — для тестов и отладки
 *
 * ⚠️ Переход-скачивание (ретранслятор отдаёт файл переходом вкладки) — не уход:
 * его ведёт __downloadNavigation ниже, и вопрос на нём не задаётся вовсе.
 */
(function () {
    var armed = false, reason = '', handler = null;
    function onBeforeUnload(e) {
        if (!armed) return;
        e.preventDefault();
        e.returnValue = '';      // требуется старыми браузерами
        return '';
    }
    window.__unsavedWork = {
        arm: function (why) {
            reason = why || '';
            if (armed) return;
            armed = true;
            handler = onBeforeUnload;
            window.addEventListener('beforeunload', handler);
        },
        disarm: function () {
            if (!armed) return;
            armed = false; reason = '';
            if (handler) { window.removeEventListener('beforeunload', handler); handler = null; }
        },
        isArmed: function () { return armed; },
        reason: function () { return reason; }
    };
    // Страховка: вкладку восстановили из bfcache — работа точно не идёт.
    window.addEventListener('pageshow', function (e) { if (e.persisted) window.__unsavedWork.disarm(); });
})();

/* ── window.__downloadNavigation(url) — переход, который ЕСТЬ скачивание ──────
 * Ретранслятор (WebView, приложение Яндекса, Huawei Browser, Telegram) отдаёт
 * файл переходом вкладки на /api/download/{token}: ответ с Content-Disposition
 * браузер превращает в загрузку, и страница остаётся на месте. Но beforeunload
 * браузер зовёт ДО того, как узнает, что это загрузка, — и любой заслон «точно
 * уйти?» этот переход отменял: скобка прогона вооружает __unsavedWork после
 * 20 с работы, /video-merge — пока очередь не выдана, редакторы — свои
 * обработчики. Файл не запрошен, а дверь уже сказала «сохранён».
 * Жалоба 23.10.2026 (/audio-overlay, приложение Яндекса, экспорт 25 с): в nginx
 * только HEAD пробы, GET нет; за 8 дней «нет GET» у /audio-overlay 29 из 67
 * выдач, /video-merge 26 из 52, /document-translate 31 из 67 — у тулов без
 * заслона 0–2 %. Стенд: заслон вооружён → диалог «покинуть?», запросов 0.
 *
 * Пропуск одноразовый: его гасит первый же beforeunload (Firefox и WebKit
 * зовут его ПОСЛЕ клика, через 2–104 мс по замеру — отсюда окно 1.5 с, а не
 * обёртка вокруг клика), остальные слушатели не зовутся вовсе — ни заслоны, ни
 * «уборка» камеры и микрофона: страница не выгружается. Слушатель — ранний
 * шим в <head> раскладки (__dlNavPass): в Chromium порядок слушателей на window
 * — порядок регистрации. Разбор — docs/why/download-navigation.md.
 * Сторож — DownloadNavigationPassesTheGuardTest. */
window.__downloadNavigation = function (url) {
    try { if (window.__dlNavPass) window.__dlNavPass(1500); } catch (e) {}
    // Без атрибута `download`: работу делает Content-Disposition сервера, его и
    // перехватывает WebView. data-rawdl — мимо перехватчика выдачи и полоски перехода.
    var a = document.createElement('a');
    a.href = url; a.rel = 'noopener';
    a.setAttribute('data-rawdl', '1');
    (document.body || document.documentElement).appendChild(a);
    try { a.click(); } finally { try { a.remove(); } catch (e) {} }
};

/* ── window.__crashMarker — «вкладку убили посреди работы» становится видимым ──
 * ЗАЧЕМ. iOS (и не только) убивает вкладку по памяти БЕЗ единого события: нет
 * error, нет tool_abandon, нет final_stage — pagehide не стреляет. В телеметрии
 * это выглядит как «человек просто ушёл», а для человека — как «выкидывает»
 * (жалоба #5860 на /remove-text-from-video, 2026-08-26: три свежие вкладки
 * подряд, каждая — file_picked → wasm_ready → тишина → перезагрузка).
 * У /profanity-remover такой детектор есть с 03.08 и он ЛОКАЛЬНЫЙ — второй
 * копии в другом туле быть не должно, отсюда общий примитив.
 *
 * МЕХАНИКА. Маркер в localStorage ставится на входе в тяжёлую фазу и снимается
 * на ЛЮБОМ штатном исходе (готово/ошибка/сброс) и на pagehide (штатный уход).
 * Маркер, который нашла следующая загрузка страницы, означает, что прошлый
 * заход убит, — ЕСЛИ его хозяин мёртв (см. ниже).
 * bfcache-возврат в занятую фазу ставит маркер обратно (pageshow).
 *
 * ⚠️⚠️ МАРКЕР ЧУЖОЙ ЖИВОЙ ВКЛАДКИ — НЕ КРАХ (2026-10-07).
 * localStorage общий на все вкладки, а «следующая загрузка страницы» — это и
 * вторая вкладка того же тула, открытая, пока первая ещё считает. До этой
 * правки она читала маркер работающей соседки как свой некролог: писала
 * `hard_crash_detected`, рисовала «Прошлая обработка оборвалась. Похоже,
 * вкладке не хватило памяти…» и СТИРАЛА маркер — так что настоящая смерть
 * первой вкладки после этого была бы уже невидима.
 * Заявка на возврат №39 (04.10.2026, /vocal-remover, Linux, 8 ГБ): баннер
 * показан дважды. Первый раз — гостю, через 29 с после запуска разделения
 * бэк-вокала в соседней вкладке, за полминуты до покупки Премиума. Второй —
 * уже оплатившему, усиленным текстом («на этом устройстве памяти, скорее
 * всего, не хватает. Надёжнее открыть страницу на компьютере» — он и был за
 * компьютером), пока ДВЕ его вкладки считали; одна из них досчитала через
 * четыре минуты. Замер RU за 5 дней: из 526 срабатываний 38 (7 %) пришлись на
 * момент, когда вкладка-хозяйка заведомо жила — слала события и до, и ≥ 20 с
 * после «некролога» (нижняя оценка: занятая вкладка событий не шлёт).
 *
 * Как узнать, жив ли хозяин, не спрашивая его самого. Спрашивать нельзя: его
 * главный поток занят как раз тем, что стережёт маркер (модель на wasm держит
 * его по 16–25 с подряд — замер 12.09), и ни пульс по таймеру, ни ответ по
 * BroadcastChannel вовремя не придут. Поэтому хозяин на время фазы держит Web
 * Lock со своим именем: замок принадлежит документу, а не его потоку, и
 * браузер сам отпускает его, когда документа не стало. Замер 07.10.2026
 * (Chromium): замок вкладки с ЗАНЯТЫМ на 9 с главным потоком виден соседке за
 * 46–62 мс; после краха рендерера замок живёт ещё 10.3–12.5 с, после закрытия
 * вкладки — 0.5–6 с, после ухода по ссылке — до 26 с (но там pagehide уже
 * снял маркер). Отсюда правило:
 *   · замка нет — хозяин мёртв, говорим сразу;
 *   · замок есть — ждём: отпущен, а маркер хозяина на месте — умер, не убрав
 *     за собой (говорим); маркер исчез — доработал штатно (молчим); замок
 *     держится все 30 с — жив и считает (молчим, маркер НЕ трогаем).
 *   Ответ хозяина по BroadcastChannel — быстрый путь «жив», когда его поток
 *   свободен; молчание в канале не значит ничего.
 * Настоящая смерть при этом объявляется позже на время, которое замок её
 * переживает (до ~13 с). Без Web Locks (Safari < 15.4) — прежнее правило
 * плюс один вопрос в канал.
 * ⚠️ Один слаг — одна запись: вторая вкладка, вошедшая в фазу позже, занимает
 * её, и смерть первой после этого не видна. Чужую запись никто не стирает.
 *
 * ⚠️ Окно «маркер свежий» — параметр вызывающего (maxAgeS), не константа:
 * у тулов с часовыми файлами 45 минут были ВЫКЛЮЧЕННЫМ детектором
 * (память reference_ios_tab_oom_hard_crash_2026_08_03). Потолок — сутки.
 *
 * ⚠️ СЧЁТ ОБРЫВОВ — «ПОДРЯД», А НЕ «ЗА ВСЁ ВРЕМЯ» (2026-10-07). Со второго
 * обрыва баннер говорит «на этом устройстве памяти, скорее всего, не хватает»,
 * и счётчик, который никто не сбрасывал, делал это неправдой: замер RU за 5
 * дней — у 23 из 92 таких сообщений между двумя обрывами тот же тул на том же
 * устройстве довёл прогон до результата, а 33 из 96 устройств, которым это
 * сказали, потом закончили работу в том же туле. Довёл до результата — серия
 * кончилась: `passed(slug)`, а для тула, который объявляет результат обычным
 * `tool_complete`, это делает сам примитив (сигнал `tmb:tool-complete` после
 * тяжёлой фазы в том же документе) и заодно убирает устаревший баннер.
 *
 * Ключи и имя события одни на всех (`pc:hot:{slug}`, `pc:crashes:{slug}`,
 * `hard_crash_detected`); /profanity-remover, с которого детектор начинался,
 * с 07.10.2026 тоже ходит сюда — своей копии правила ни у кого нет.
 *
 * API:  __crashMarker.arm(slug, stage, {mb, video})  — вошли в тяжёлую фазу
 *       __crashMarker.disarm(slug)                    — штатный исход
 *       __crashMarker.passed(slug)                    — тяжёлая фаза дошла до
 *           результата: серия обрывов кончилась, баннер снят
 *       __crashMarker.check(slug, {maxAgeS, mount, texts:{title, body, body_repeat}, onCrash})
 *           → {stage, ago_s, n, ...} — крах установлен сразу (хозяина нет),
 *             null — маркера нет ИЛИ вердикт отложен: хозяин проверяется, и
 *             если он мёртв, `onCrash(info)` придёт позже. При находке шлёт
 *             событие и, если дан mount + texts, рисует честный баннер ПЕРЕД
 *             mount (без обещаний: «закройте вкладки, попробуйте снова, на
 *             компьютере надёжнее»; со второго краха подряд — «этому
 *             устройству, скорее всего, не хватает памяти»).
 * Сторожа: tests/js/crash-marker-owner.test.mjs,
 * tests/playwright/crash-marker-second-tab-is-not-a-crash.mjs,
 * CrashMarkerHasOneOwnerTest.
 */
/* __crashMarker:begin */
(function () {
    var current = {};   // slug → {stage, meta} пока фаза идёт (для pageshow-возврата)
    var armedHere = {}; // slug → 1: в этом документе шла тяжёлая фаза (для «серия кончилась»)
    var banners = {};   // slug → баннер, нарисованный этим документом
    var OWN = (function () {
        try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { /* noop */ }
        return String(Date.now()) + '-' + Math.random().toString(36).slice(2);
    })();
    var LOCK = 'tb-crash-marker:';   // + идентификатор документа-хозяина
    var PATIENCE_MS = 30000;         // дольше замок мёртвого документа не живёт (замер: до 26 с)
    var POLL_MS = 1500;
    var ASK_MS = 600;
    function hotKey(slug) { return 'pc:hot:' + slug; }
    function crashKey(slug) { return 'pc:crashes:' + slug; }
    function read(slug) {
        try { return JSON.parse(localStorage.getItem(hotKey(slug)) || 'null'); } catch (e) { return null; }
    }
    function locksOk() {
        try { return !!(navigator.locks && typeof navigator.locks.request === 'function' && typeof navigator.locks.query === 'function'); } catch (e) { return false; }
    }
    function write(slug, stage, meta) {
        try {
            localStorage.setItem(hotKey(slug), JSON.stringify({
                stage: stage || '', t: Date.now(),
                video: meta && meta.video ? 1 : 0,
                mb: meta && meta.mb ? Math.round(meta.mb) : 0,
                own: OWN, lk: locksOk() ? 1 : 0
            }));
        } catch (e) { /* приватный режим — детектор просто молчит */ }
    }
    function clear(slug) { try { localStorage.removeItem(hotKey(slug)); } catch (e) { /* noop */ } }
    /* Снять можно только СВОЮ запись (или прежнего формата, без хозяина): чужая
       принадлежит вкладке, которая ещё считает. */
    function clearOwn(slug) {
        var m = read(slug);
        if (!m || !m.own || m.own === OWN) clear(slug);
    }

    // ── замок хозяина: держится, пока хоть одна фаза этого документа идёт ──
    var lockWanted = false, lockAsked = false, lockLetGo = null;
    function holdLock() {
        lockWanted = true;
        if (lockAsked || !locksOk()) return;
        lockAsked = true;
        try {
            navigator.locks.request(LOCK + OWN, function () {
                return new Promise(function (res) {
                    var done = function () { lockLetGo = null; lockAsked = false; res(); };
                    if (!lockWanted) { done(); return; }     // фаза кончилась раньше, чем замок выдали
                    lockLetGo = done;
                });
            }).catch(function () { lockAsked = false; lockLetGo = null; });
        } catch (e) { lockAsked = false; }
    }
    function dropLock() {
        lockWanted = false;
        if (lockLetGo) lockLetGo();
    }
    function ownerHoldsLock(own) {
        return navigator.locks.query().then(function (s) {
            var held = (s && s.held) || [];
            for (var i = 0; i < held.length; i++) if (held[i] && held[i].name === LOCK + own) return true;
            return false;
        });
    }

    // ── быстрый путь «жив»: хозяин со свободным потоком отвечает сам ──
    var chan = null;
    try {
        if (typeof BroadcastChannel === 'function') {
            chan = new BroadcastChannel('tb-crash-marker');
            chan.addEventListener('message', function (e) {
                var d = e && e.data;
                if (!d || d.q !== 'alive?' || d.own !== OWN) return;
                for (var s in current) { try { chan.postMessage({ a: 'alive', own: OWN }); } catch (_) {} return; }
            });
        }
    } catch (e) { chan = null; }
    function askOwner(own, cb) {
        if (!chan) { cb(false); return; }
        var done = false;
        var h = function (e) {
            var d = e && e.data;
            if (done || !d || d.a !== 'alive' || d.own !== own) return;
            done = true; chan.removeEventListener('message', h); cb(true);
        };
        chan.addEventListener('message', h);
        try { chan.postMessage({ q: 'alive?', own: own }); } catch (_) {}
        setTimeout(function () { if (done) return; done = true; chan.removeEventListener('message', h); cb(false); }, ASK_MS);
    }

    function sameRecord(a, b) { return !!a && !!b && a.own === b.own && a.t === b.t; }

    /* Объявить крах: счётчик, событие, баннер. `m` — запись, какой её застали. */
    function announce(slug, m, opts) {
        if (!m.own || sameRecord(read(slug), m)) clear(slug);
        var agoS = Math.round((Date.now() - m.t) / 1000);
        var n = 1;
        try { n = (parseInt(localStorage.getItem(crashKey(slug)), 10) || 0) + 1; localStorage.setItem(crashKey(slug), String(n)); } catch (e) { n = 1; }
        var info = { stage: m.stage || '', video: m.video || 0, mb: m.mb || 0, ago_s: agoS, n: n };
        try { if (window.__ae) __ae('feature_use', { f: 'hard_crash_detected', stage: info.stage, video: info.video, mb: info.mb, ago_s: agoS, n: n, slug: slug }); } catch (e) { /* noop */ }
        var mount = opts.mount, tx = opts.texts;
        if (mount && mount.parentNode && tx && tx.title) {
            var REPEAT_FROM = 2;
            var body = (n >= REPEAT_FROM && tx.body_repeat) ? tx.body_repeat : (tx.body || '');
            var b = document.createElement('div');
            b.className = 'tb-crash-note';
            b.setAttribute('role', 'status');
            b.style.cssText = 'margin:0 0 12px;padding:12px 40px 12px 14px;position:relative;border:1px solid var(--warning,#eab308);border-radius:12px;background:var(--glass,rgba(255,255,255,.04));font-size:13px;line-height:1.45;color:var(--text-primary,#fff)';
            var h = document.createElement('div'); h.style.cssText = 'font-weight:600;margin-bottom:4px'; h.textContent = '⚠️ ' + tx.title;
            var p = document.createElement('div'); p.style.cssText = 'color:var(--text-secondary,#aaa)'; p.textContent = body;
            var x = document.createElement('button'); x.type = 'button'; x.setAttribute('aria-label', '×'); x.textContent = '×';
            x.style.cssText = 'position:absolute;top:8px;right:8px;background:none;border:none;color:var(--text-muted,#888);font-size:16px;cursor:pointer;padding:4px 8px';
            x.addEventListener('click', function () { b.remove(); });
            b.appendChild(h); b.appendChild(p); b.appendChild(x);
            mount.parentNode.insertBefore(b, mount);
            info.banner = b;
            banners[slug] = { el: b, video: info.video, n: n };
        }
        try { if (typeof opts.onCrash === 'function') opts.onCrash(info); } catch (e) { /* noop */ }
        return info;
    }

    /* Серия обрывов кончилась: прогон на этом устройстве дошёл до результата.
       Счёт начинается заново, а баннер про прошлый обрыв больше не к месту.
       Прошёл ПОСЛЕ показанного баннера — это мера пользы его совета («закройте
       вкладки и повторите»): `crash_retry_succeeded` с номером обрыва в серии. */
    function passed(slug) {
        try { localStorage.removeItem(crashKey(slug)); } catch (e) { /* noop */ }
        var b = banners[slug];
        if (!b) return;
        delete banners[slug];
        try { b.el.remove(); } catch (e) { /* noop */ }
        try { if (window.__ae) __ae('feature_use', { f: 'crash_retry_succeeded', slug: slug, video: b.video || 0, n: b.n || 1 }); } catch (e) { /* noop */ }
    }

    function ownerAlive(slug, m, how) {
        try { if (window.__ae) __ae('feature_use', { f: 'crash_marker_owner_alive', slug: slug, stage: m.stage || '', how: how, ago_s: Math.round((Date.now() - m.t) / 1000) }); } catch (e) { /* noop */ }
    }

    /* Хозяин держал замок: дождаться, чем кончится. Ничего не объявляет, пока
       замок не отпущен; жив — молчим и запись не трогаем. */
    function watchOwner(slug, m, opts) {
        var since = Date.now();
        var told = false;
        function step() {
            if (told) return;
            ownerHoldsLock(m.own).then(function (held) {
                if (told) return;
                if (!held) {
                    told = true;
                    // Отпущен. Запись хозяина на месте — он умер, не убрав за собой;
                    // нет её (или она уже чужая) — доработал штатно либо мы не знаем.
                    if (sameRecord(read(slug), m)) announce(slug, m, opts);
                    return;
                }
                if (Date.now() - since >= PATIENCE_MS) { told = true; ownerAlive(slug, m, 'lock'); return; }
                askOwner(m.own, function (yes) {
                    if (told) return;
                    if (yes) { told = true; ownerAlive(slug, m, 'answer'); return; }
                    setTimeout(step, POLL_MS);
                });
            }, function () {
                // Замки спросить не удалось — прежнее правило, но с одним вопросом хозяину.
                askOwner(m.own, function (yes) {
                    if (told) return;
                    told = true;
                    if (yes) { ownerAlive(slug, m, 'answer'); return; }
                    if (sameRecord(read(slug), m)) announce(slug, m, opts);
                });
            });
        }
        step();
    }

    window.__crashMarker = {
        arm: function (slug, stage, meta) {
            if (!slug) return;
            current[slug] = { stage: stage || '', meta: meta || {} };
            armedHere[slug] = 1;
            holdLock();
            write(slug, stage, meta);
        },
        disarm: function (slug) {
            if (!slug) return;
            delete current[slug];
            clearOwn(slug);
            for (var s in current) return;      // ещё идёт другая фаза — замок остаётся
            dropLock();
        },
        isArmed: function (slug) { return !!current[slug]; },
        passed: function (slug) { if (slug) passed(slug); },
        check: function (slug, opts) {
            opts = opts || {};
            var m = read(slug);
            if (!m || !m.t) return null;
            if (m.own && m.own === OWN) return null;          // наша собственная идущая фаза
            var maxAge = Math.min(86400, Math.max(60, (opts.maxAgeS | 0) || 3600));
            var agoS = Math.round((Date.now() - m.t) / 1000);
            if (agoS < 0 || agoS > maxAge) { clear(slug); return null; }   // не про этот заход
            // Запись прежнего формата (страница из кэша со старым скриптом) — хозяина
            // спросить не у кого: правило прежнее.
            if (!m.own) return announce(slug, m, opts);
            if (m.lk && locksOk()) { watchOwner(slug, m, opts); return null; }
            // Хозяин без замка (нет Web Locks): один вопрос в канал вместо немедленного некролога.
            askOwner(m.own, function (yes) {
                if (yes) { ownerAlive(slug, m, 'answer'); return; }
                if (sameRecord(read(slug), m)) announce(slug, m, opts);
            });
            return null;
        }
    };
    // штатный уход = pagehide; его отсутствие при живом маркере = килл
    window.addEventListener('pagehide', function () { for (var s in current) clearOwn(s); });
    // возврат из bfcache в занятую фазу — работа продолжается, маркер вернуть
    window.addEventListener('pageshow', function (e) {
        if (!e.persisted) return;
        for (var s in current) { holdLock(); write(s, current[s].stage, current[s].meta); }
    });
    // «Тул выдал результат» после тяжёлой фазы в этом же документе — серия кончилась.
    // Документ, который тяжёлую фазу не начинал (лёгкий режим после обрыва), серию не рвёт.
    try {
        document.addEventListener('tmb:tool-complete', function () {
            var done = armedHere; armedHere = {};
            for (var s in done) passed(s);
            for (var k in current) armedHere[k] = 1;      // фаза, которая ещё идёт, своё слово скажет позже
        });
    } catch (e) { /* noop */ }
})();
/* __crashMarker:end */

/* __cyrillicLang:begin
   Язык кириллического текста: 'uk' | 'ru' | 'other' | null (кириллицы нет).
   ЕДИНСТВЕННЫЙ определитель на сайте — своя копия правила в туле разъезжается:
   /word-counter 17.09.2026 заменил вызов этой функции строкой
   «/[іїєґ]/ ? 'uk' : 'ru'», и через месяц русский текст со словом «Київ» стал
   «украинским», а казахский — тоже. Сторож — CyrillicLanguageHasOneOwnerTest
   (никто не решает uk/ru сам), поведение — tests/js/cyrillic-lang.test.mjs.

   Приметы, по убыванию силы (замер 23.10.2026 на 140 тыс. строк наших
   переводов uk/ru и на фразах поиска как отложенной выборке):
     • служебные слова, которых в другом языке нет: що, або, чи, від, ви /
       что, или, ли, от, вы — вес 2 (та, й, з / с — вес 1: есть и в другом);
     • буквы, которых в другом языке нет: і ї є ґ / ы э ъ ё;
     • окончания: -ння, -тися, -ють, -ою, -ів / -тся, -ает, -ой, -ов, -ая;
     • слово с заглавной — вполсилы: «Київ» или «Ельцин» в чужом тексте — имя.
   Было (буквы + 20 слов) → стало: украинские фразы от 5 слов 97,45 → 99,3 %,
   русские 99,98 → 99,99 %, русская фраза с «Київ» 83 → 98 %, фразы поиска
   uk 77,7 → 89,7 %.
   'other' — кириллица другого языка, которую ни русской, ни украинской звать
   нельзя: буквы казахского, татарского, башкирского, сербского… (≥ 2 % букв),
   белорусский (і и ы вместе, ни одной «и»), болгарский (ъ перед согласной).
   Ничьей (примет нет: «Гречка») решает opts.fallback: 'uk' | 'ru' | null —
   «не знаю» для тула, которому важно не ошибиться (/slavic-accent); иначе 'ru'.
   Смотрит первые 6000 знаков: язык не меняется на середине, а /word-counter
   зовёт его на каждое нажатие клавиши. */
window.__cyrillicLang=(function(){
    var W={};
    function put(list,v){ list.split(' ').forEach(function(w){ if(w)W[w]=v; }); }
    put('що щоб або але як чи від під між після також лише тільки вже ще дуже треба потрібно можна може можуть '
       +'це цей ця ці цю цього цьому цим цих цієї який яка яке які яку якого якої якому яким яких якщо коли де чому хто скільки '
       +'він вона воно вони ви ми ти його її їх їм нього неї мені тобі собі '
       +'бути був була було були буде будуть має немає нема ні ніж нічого щось хтось навіть саме самі тобто отже адже проте бо аби хоча хоч '
       +'усе усі всі потім зазвичай одразу поки знову завжди сьогодні зараз замість тож далі нижче вище менше більше кожен кожна кожного '
       +'приблизно близько надто занадто щонайменше будь-який будь-яка будь-яке досить наче тощо щойно доки',2);
    put('та й з із зі',1);
    put('и или что чтобы если как это этот эта эти эту этого этом этой этим этих который которая которое которые которого которой '
       +'от из к со во же ли бы ни но да нет есть его её ее их он она оно они мы вы ты мне ему ей него неё нее '
       +'после под между только уже ещё еще тоже также очень нужно надо можно может могут когда где почему кто сколько '
       +'был была было были будет будут быть всё всех всего сейчас вместе сначала затем потом обычно сразу пока снова всегда сегодня '
       +'здесь именно даже ничего чем поэтому потому нельзя более лучше меньше ниже выше каждый каждая каждого какой какая какое какие '
       +'свой своя своё свои мой моя моё твой твоя твоё пожалуйста спасибо',-2);
    put('с',-1);
    var UK_END=/(?:нн(?:я|і|ю|ям|ях|ями)|[иоі]ти|[^чтц]ати|тися|теся|ють|ою|ець|ові|еві|ів)$/,
        RU_PART=/(?:[иеая][еяю]ся|[еоы]гося|[иы]хся|[иы]мся|[еоу][юй]ся)$/,
        UK_REFL=/(?:[иоау]ся|еся)$/,
        RU_END=/(?:тся|[ауяео]ет|еть|ешь|ишь|[ая]я|[оеи]е|и[яю]|ой|ов|ать)$/;
    return function(text,opts){
        var s=String(text||'');
        if(!/[Ѐ-ӿ]/.test(s))return null;
        if(s.length>6000)s=s.slice(0,6000);
        var all=0,other=0,i8=0,y=0,i6=0,i,cp;
        for(i=0;i<s.length;i++){
            cp=s.charCodeAt(i);
            if(cp<0x0400||cp>0x052F)continue;
            all++;
            if(cp===0x0438||cp===0x0418)i8++;                                       // и
            if(cp===0x044B||cp===0x042B||cp===0x044D||cp===0x042D)y++;               // ы э
            if((cp>=0x0410&&cp<=0x044F)||cp===0x0401||cp===0x0451)continue;          // общий алфавит и ё
            if(cp===0x0406||cp===0x0456){i6++;continue;}                             // і
            if(cp===0x0404||cp===0x0454||cp===0x0407||cp===0x0457||cp===0x0490||cp===0x0491)continue; // є ї ґ
            other++;
        }
        if(other>=1&&other*50>=all)return 'other';
        if(i6>=2&&y>=2&&i8===0)return 'other';
        var bg=(s.match(/ъ[бвгджзйклмнпрстфхцчшщ]/gi)||[]).length;
        if(bg>=2||(bg>=1&&!/[ыэё]/i.test(s)))return 'other';
        s=s.replace(/\u0301/g,'');                       // знак ударения режет слово надвое («бо́роду»)
        var uk=0,ru=0,re=/[0-9a-zа-яёіїєґ]+(?:['\u2019\u02BC-][0-9a-zа-яёіїєґ]+)*/gi,m,w0,w,v,wt;
        while((m=re.exec(s))){
            w0=m[0];w=w0.toLowerCase();v=W[w];
            if(v===-1&&/\d\s*$/.test(s.slice(Math.max(0,m.index-3),m.index)))continue; // «5 с» — секунды
            if(v>0){uk+=v;continue;}
            if(v<0){ru-=v;continue;}
            if(!/[а-яёіїєґ]/.test(w))continue;
            wt=w0.charAt(0)!==w.charAt(0)?0.5:1;
            uk+=wt*(w.match(/[іїєґ]/g)||[]).length;
            ru+=wt*(w.match(/[ыэъё]/g)||[]).length;
            if(/[бпвмфр]['\u2019\u02BC][яюєї]/.test(w))uk++;
            if(w.length>3){
                if(UK_END.test(w))uk++;
                else if(RU_PART.test(w))ru++;
                else if(UK_REFL.test(w))uk++;
                else if(RU_END.test(w))ru++;
            }
        }
        if(uk!==ru)return uk>ru?'uk':'ru';
        return opts&&opts.fallback===null?null:(opts&&opts.fallback==='uk'?'uk':'ru');
    };
})();
/* __cyrillicLang:end */
window.__toast=function(msg,type,opts){
    // Empty message = suppressed toast. Used by callers that ask
    // FFmpegProcessor.getErrorMessage() for a user-facing string and get back ""
    // because the error was actually a cancel signal (terminate()). Without this
    // early return, tools would flash an empty red bar after user-initiated cancels.
    if(!msg||(typeof msg==='string'&&!msg.trim()))return;
    type=type||'error';
    opts=opts||{};
    /* ── Do not contradict the duration gate ─────────────────────────────────
       The shared gate (tool-usage-limit.js checkDuration) has just told the
       person why this file is not being opened: longer than this browser can
       hold, or longer than the plan's per-file limit — and, since 2026-09-23,
       it may already be cutting the file into parts. The tool's error path
       runs right after, and in every tool it is the generic one: «wrong file
       format» + «could not decode audio» (chapter-splitter fires both, plus
       __handleError; no tool reads err.userInformed). Measured on prod: the
       device wall on /chapter-splitter was followed by exactly that pair —
       the page told the person a file it had just called «too long» was «not
       a supported format». The mark lives 2.5 s (the error path runs in the
       same microtask chain as the dialog, i.e. within milliseconds) and is
       cleared the moment a part is handed to the tool, so errors about the
       part itself are shown. Counted, so a suppression we did not intend
       shows up in telemetry instead of as silence. */
    if(type==='error'){
        var gs=window.__gateSpoke;
        if(gs&&(Date.now()-gs.at)<2500){
            try{if(!gs.told){gs.told=1;window.__ae&&window.__ae('feature_use',{slug:gs.slug,f:'toast_after_gate',why:gs.why,msg:String(msg).slice(0,80)});}}catch(_){}
            return;
        }
    }
    /* ── What the user actually SAW, for the bug report they may choose to send.
       PAGE-SCOPED ONLY: this variable is never transmitted on its own. It leaves
       the browser solely inside a feedback submission the user fills in and
       sends deliberately.

       Why it exists: feedback-widget's `last_error` read `window.__LAST_TOOL_ERROR`,
       which is written INSIDE telemetry-tier0's consent gate — the closure that
       early-returns when `!consentOK()`. So for a visitor who declined analytics
       there was no session, no event and no last error, and the widget's own
       docblock ("critical for users with DNT — this is the only context we ever
       get from them") described something that could not happen for exactly
       those users. Report #3886 arrived twice with `sid: null, last_error: null`
       while the person was staring at a red toast both times.

       Measured over 196 bug reports carrying a client-state snapshot since
       2026-08-01: 186 (95%) had `last_error: null`; 28 of those had `sid: null`
       too, i.e. no diagnostics whatsoever. Consent explains only that 28 —
       the rest is `__LAST_TOOL_ERROR` simply not covering much. A toast is the
       error surface the user is looking at, so capturing here covers both. */
    if(type==='error'){try{window.__LAST_UI_ERROR={
        /* Для noUi человек НИЧЕГО не видел — но в отчёт о баге эта строка
           по-прежнему нужна, поэтому пишем ТОЧНЫЙ текст и честный `via`.
           Иначе виджет обратной связи снова остался бы с last_error:null
           ровно там, где диагноз есть. */
        msg:String(opts.realMsg||(typeof msg==='string'?msg:String(msg))).substring(0,200),
        ts:Date.now(),
        via:opts.noUi?(opts.ui==='told'?'told':'uncaught'):'toast',
    }}catch(_){}}
    if(type==='error'){console.error('[Timbrica]',msg);try{if(window.__aeToolError){var _ms=typeof msg==='string'?msg:String(msg);if(/@context/i.test(_ms)){}else{
        // opts.silent=true — skip telemetry but still show UI. Use when the caller
        // already emitted a typed tool_error / tool_blocked event (CameraManager,
        // MicManager, video_trim_fail etc.) and wants only the visual toast.
        // Explicit per-call option — no global mutable state, no async-gap surprises.
        // opts.reason (or legacy window.__nextToastBlocked) — route as tool_blocked
        // with the given reason instead of tool_error. The tool already KNOWS why
        // it's showing the toast, so we take the reason from the caller rather than
        // pattern-matching a translated string across 7 locales.
        var _silent=!!opts.silent;
        var _blockedReason=opts.reason||window.__nextToastBlocked||'';window.__nextToastBlocked=null;
        if(!_silent){
        // msg widened 200→500: FFmpeg-exit-code tails, server-error JSON
        // bodies, and the real root cause often sit past the first 200 chars.
        // Added br/mob/dm so Pino can slice toast_error events by platform
        // without needing ip_hash joins — matches wasm_process payload shape.
        var _uaS=navigator.userAgent||'';
        var _tp={error_type:_blockedReason?'toast_blocked':'toast_error',msg:_ms.substring(0,500),
            // Четвёртая копия лестницы браузеров жила здесь; все они теперь
            // зовут один классификатор telemetry-tier0.js (он же и решает, что
            // встроенный браузер приложения — не Chrome). Запасной ответ — на
            // случай, если тост поднялся раньше, чем загрузилась телеметрия.
            br:(window.__brOf?window.__brOf(_uaS):'other'),
            mob:/Mobi|Android|iPhone/i.test(_uaS)?1:0,
            dm:navigator.deviceMemory||0};
        if(_blockedReason)_tp.reason=_blockedReason;
        // opts.ctx — first-class call-site tag (e.g. 'tts_synth', 'wasm_init').
        // Forwarded as payload.ctx so /pino/streams clusters tool_error by
        // call site instead of by translated msg-prefix. __handleError sets
        // it from its second argument; direct __toast callers can also pass.
        if(opts.ctx)_tp.ctx=opts.ctx;
        /* opts._no_auto_stage — тост показать, но НЕ переводить машину стадий
           тула в `error`. Для условий, которые видны человеку, но не мешают
           туду сделать свою работу (пользователь сам отклонил разрешение).
           Тот же флаг, что у AudioDecoderRobust на промежуточных тирах;
           читается в telemetry-tier0.js. */
        if(opts._no_auto_stage)_tp._no_auto_stage=1;
        /* Стек — то, чего этому классу не хватало, чтобы быть разбираемым.
           26 691 событие за 30 дней на 405 тулах приходило БЕЗ единой строки
           места. `realMsg` держит точный текст, когда человеку показан
           общий: телеметрия не должна терять диагноз ради вежливости. */
        if(opts.stack)_tp.err_stack=String(opts.stack).substring(0,400);
        if(opts.realMsg)_tp.msg=String(opts.realMsg).substring(0,500);
        if(opts.internal)_tp.internal=String(opts.internal).substring(0,24);
        /* opts.ui — что из этого увидел человек, когда payload.msg несёт
           строку диагноза, а не показанный текст (ставит __handleError):
           say — фраза поймавшего, door — фраза двери FFmpeg, generic — общая
           фраза страницы, text — текст ошибки как есть. */
        if(opts.ui)_tp.ui=String(opts.ui).substring(0,12);
        /* opts.uncaught — пришло из ГЛОБАЛЬНОЙ сети (window.onerror /
           unhandledrejection), то есть эту ошибку никто не ловил. Метка нужна
           в телеметрии, чтобы этот класс можно было отделить от ошибок,
           показанных осознанным вызовом; payload.msg при этом остаётся ТОЧНОЙ
           строкой движка (через realMsg) — измерение не платит за вежливость. */
        if(opts.uncaught)_tp.uncaught=1;
        if(opts.noUi)_tp.no_ui=1;
        var _fc=(opts.file&&typeof opts.file==='object')?opts.file:window.__fileCtx;
        if(_fc){if(_fc.size)_tp.file_size=_fc.size;if(_fc.type)_tp.file_type=_fc.type;if(_fc.name)_tp.file_ext=(_fc.name||'').split('.').pop()}
        if(_blockedReason){if(window.__aeBlocked)window.__aeBlocked(_tp);else if(window.__ae)window.__ae('tool_blocked',Object.assign({slug:window.__TOOL_SLUG||''},_tp))}
        else window.__aeToolError(_tp);
        }
    }}}catch(_){}}
    else if(type==='warning')console.warn('[Timbrica]',msg);
    /* opts.noUi — записать событие, но НЕ рисовать тост. Зеркало opts.silent
       («покажи, но не считай»): бывает и обратный случай — измерять надо
       всегда, а говорить человеку нечего. Стоит ПОСЛЕ блока телеметрии и ДО
       любого DOM, поэтому ни одна строка измерения не теряется. */
    if(opts.noUi)return;
    var c=document.getElementById('toastContainer');if(!c)return;
    var icons={error:'<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>',success:'<circle cx="12" cy="12" r="10"/><polyline points="9 12 11.5 14.5 16 10"/>',warning:'<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',info:'<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>'};
    var el=document.createElement('div');
    el.className='toast-msg toast-msg--'+type;
    el.setAttribute('role','alert');
    /* opts.action = {label, onclick} — renders an inline action button next
       to the close X. Used by tools that can offer the user a one-click
       recovery (volume → switch to MP3 on memory-guard refusal). The action
       runs on the same animation frame as dismiss so the click doesn't get
       eaten by the hide-transition. */
    var actionBtnHtml='';
    if(opts.action&&opts.action.label){
        actionBtnHtml='<button class="toast-action" type="button">'+String(opts.action.label).replace(/</g,'&lt;')+'</button>';
    }
    el.innerHTML='<svg class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'+(icons[type]||icons.error)+'</svg><span>'+msg.replace(/</g,'&lt;')+'</span>'+actionBtnHtml+'<button class="toast-close" aria-label="Close">&times;</button>';
    /* When it was raised. A later, better-informed verdict about the same
       user action can then retire the guess that preceded it, without having
       to close somebody else's unrelated toast - see rejectUnreadable() in
       tool-uploader.js. Stamp only; nothing here reads it. */
    try{el.dataset.tsMs=String(Date.now());}catch(_){}
    /* ── Same message twice = refresh, not a second copy ──
       A repeatable action that refuses repeatably used to build a tower: press
       the button six times behind a dead filter and six identical bars stack up,
       each with its own close X. Measured on prod (14 d, tool_error only, so a
       floor — warnings emit nothing): 2648 (session, tool, message) groups saw
       the same string ≥3 times and 661 saw it ≥6. Nothing is hidden by folding
       them: the text is already on screen, so we re-arm its timer and pulse it
       instead. Keyed on message+type; actionable toasts are left alone because
       their button may be bound to a different recovery each time. */
    /* ── Same message twice = refresh, not a second copy ──
       A repeatable action that refuses repeatably used to build a tower: press
       the button six times behind a dead filter and six identical bars stack up,
       each with its own close X. Measured on prod (14 d, tool_error only, so a
       floor — warnings emit no event at all): 2648 (session, tool, message)
       groups saw the same string ≥3 times and 661 saw it ≥6. Folding hides
       nothing, since the text is already on screen: the live bar re-arms its
       timer and nudges once. Compared on type + message as two attributes rather
       than one delimited key — the first attempt at a separator put a raw NUL
       into this file (SourceTextIsNotBrokenTest caught it). Actionable toasts are
       left alone: their button may be bound to a different recovery each time. */
    if(!opts.action){try{
        var _live=c.querySelectorAll('.toast-msg');
        for(var _i=0;_i<_live.length;_i++){
            var _o=_live[_i];
            if(_o.dataset.dupType===type&&_o.dataset.dupMsg===String(msg)
               &&!_o.classList.contains('hide')&&typeof _o._rearm==='function'){
                _o._rearm();
                if(window.__announce)window.__announce(msg);
                return {dismiss:_o._dismiss||function(){}};
            }
        }
        el.dataset.dupType=type;el.dataset.dupMsg=String(msg);
    }catch(_){}}
    c.appendChild(el);
    requestAnimationFrame(function(){requestAnimationFrame(function(){el.classList.add('show')})});
    /* Actionable toasts stay until user interacts — clicking the action
       button or close X. A 6 s auto-dismiss for an actionable error feels
       hostile (user reads, reaches for mouse, toast vanishes). Errors
       without action keep the 6 s auto-dismiss. */
    /* opts.keep — тост с инструкцией: живёт, пока человек не закроет его сам
       (шаги «как вернуть доступ» за четыре секунды не прочесть). */
    var _keep=!!(opts.action||opts.keep);
    var timer=_keep?null:setTimeout(function(){dismiss()},type==='error'?6000:4000);
    function dismiss(){if(timer)clearTimeout(timer);el.classList.remove('show');el.classList.add('hide');setTimeout(function(){if(el.parentNode)el.parentNode.removeChild(el)},300)}
    /* Re-arm for the dedupe above: restart the auto-dismiss and give the bar a
       nudge so a repeated refusal still reads as "it happened again". */
    if(!opts.action){el._rearm=function(){
        if(timer)clearTimeout(timer);
        timer=_keep?null:setTimeout(function(){dismiss()},type==='error'?6000:4000);
        try{el.dataset.tsMs=String(Date.now());el.classList.remove('again');void el.offsetWidth;el.classList.add('again');}catch(_){}
    };}
    el.querySelector('.toast-close').onclick=dismiss;
    el._dismiss=dismiss;
    if(opts.action&&typeof opts.action.onclick==='function'){
        var actBtn=el.querySelector('.toast-action');
        if(actBtn)actBtn.onclick=function(){try{opts.action.onclick();}catch(_){};dismiss();};
    }
    if(window.__announce)window.__announce(msg);
    // A handle, so a later verdict about the same action can take this bar
    // down instead of stacking a second one (the relay's take-watch below).
    return {dismiss:dismiss};
};
/* __isUserCancel:begin */
/* Did the USER cancel THIS operation, or did it fail? A cancel is the
   operation's own signal being aborted, an error NAMED AbortError, or a typed
   field — code USER_CANCELLED (FFmpegProcessor marks the user's Cancel so) or
   cancelled:true (tools' own cancel errors). NEVER a message that happens to
   say «abort», «cancel» or «terminate»: every Emscripten engine here (FFmpeg,
   onnxruntime, MediaPipe, Piper) dies with «Aborted()» / «RuntimeError:
   Aborted(OOM)», and read by the message a FAILED run ended in silence — no
   toast, no event, the progress left standing (/audio-to-video 13.09 and
   27.09.2026; 30-odd such checks across the tools, 28.09.2026).
   Pass the operation's own signal where there is one — then it DECIDES: an
   AbortError while that signal is not aborted came from somewhere else (a
   timeout controller, a shared model load another caller cancelled, an
   internal «nothing encoded», a LOST GPU: WebGPU rejects a readback with
   AbortError «The Device was lost before mapping was resolved», measured
   28.09.2026) and is a failure of THIS operation.
   Not the question __isCancelError answers below — that one also drops every
   FFmpeg terminate as noise for the generic error surface. */
window.__isUserCancel=function(err,signal){
    var typed=!!(err&&(err.code==='USER_CANCELLED'||err.cancelled===true));
    if(signal)return !!signal.aborted||typed;
    return typed||!!(err&&err.name==='AbortError');
};
/* __isUserCancel:end */
/* Recognise user-cancellation signals. When a tool calls FFmpegProcessor.reinit()
   (from a Cancel button handler, or between retry attempts), in-flight exec
   promises reject with "called FFmpeg.terminate()" — not a bug. Tools can use
   this helper to gate their error-reporting paths. */
window.__isCancelError=function(err){
    if(!err)return false;
    var msg=(err.message||String(err)||'').toLowerCase();
    return msg.indexOf('called ffmpeg.terminate')!==-1||msg.indexOf('terminate()')!==-1||err.errorType==='cancelled';
};
/* __docFramesForeign:begin */
/* ── Кадр с адресом СТРАНИЦЫ — ещё не значит «наш» ─────────────────────────
   Единственный владелец вопроса «наш ли кадр стека, у которого вместо файла —
   адрес документа». Спрашивают его три места: window.onerror ниже, ранний
   перехватчик ошибок в шапке layout и фильтр Sentry (sentry-noise-filter.js).

   Зачем. Sentry TIMBRICA-FRONTEND-MN (24–25.09.2026; архивные HB, JQ, JR —
   та же форма): «RangeError: Maximum call stack size exceeded», пара
   минифицированных имён рекурсирует — Ok/Qk, Nk/Pk, Rk/Tk — в «/ru/…:226:63» и
   «:226:408». 22 сессии на 20+ несвязанных страницах, и ВСЕ — Chrome for iOS
   или приложение Google. Это код, который вставил ХОСТ (evaluateJavaScript /
   пользовательские скрипты WebKit; в хлебных крошках — обёртки <font><font>
   Google-переводчика), а WebKit подписывает его адресом страницы. Все наши
   ворота спрашивали «тот же хост?» и называли его нашим: issue в Sentry,
   tool_error, авто-стадия `error` — а она закрывает журнал прогона
   (__runMark.reset → __runLedger.close), то есть отпускает замок экрана и
   вопрос «точно уйти?» ПОСРЕДИ прогона на iPhone, и может показать тост
   «Что-то сломалось» поверх работающего тула.

   Признак — структура, а не список сигнатур (пар имён уже три, будет
   четвёртая). Посылка: весь наш инлайн-код живёт в элементах
   `script:not([src])`, ни один из них себя не удаляет, а CSP запрещает eval —
   значит ИМЕНОВАННАЯ функция с адресом страницы, чьего имени нет ни в одном
   инлайн-скрипте, внесена браузером или хостом. Атрибуты-обработчики
   (`onclick="…"`) — тоже наш код без элемента script, их имена `on…` наши.

   Вердикт «чужое» — только доказанный: есть хотя бы одно такое имя и НИ ОДНО
   из имён страницы не объявлено. Нет именованных кадров страницы, имя, которое
   не разобрать, хоть одно наше имя рядом с чужими — «наше» (ворота ошибаются
   в сторону «оставить»). Ошибка внутри — тоже «наше».

   Вход — кадры Sentry ({function, filename|abs_path}) ИЛИ текст err.stack
   (V8 «at name (url:1:2)», JSC/SpiderMonkey «name@url:1:2»). out.names —
   чужие имена, для телеметрии. Тест: tests/js/doc-frames-foreign.test.mjs. */
(function(){
    var SKIP=/^(?:global code|eval code|module code|anonymous|<anonymous>|\?)$/;
    var IDENT=/^[A-Za-z_$][\w$]*$/;
    var memo={n:-1,v:{}};
    function bare(u){return String(u||'').split('#')[0].split('?')[0];}
    function parse(stack){
        var out=[],lines=String(stack).split('\n');
        for(var i=0;i<lines.length;i++){
            var l=lines[i].replace(/^\s+|\s+$/g,''),m;
            if(!l)continue;
            if(l.indexOf('at ')===0){
                m=/^at (?:(.+?) \((.*)\)|(.*))$/.exec(l);
                if(m)out.push({fn:m[1]||'',url:(m[1]?m[2]:m[3])||''});
            }else if((m=/^([^@]*)@(.*)$/.exec(l))){
                out.push({fn:m[1],url:m[2]});
            }
        }
        return out;
    }
    function frameUrl(f){
        var u=f.url||f.abs_path||f.absPath||f.filename||'';
        return bare(String(u).replace(/(?::\d+){1,2}$/,''));
    }
    // «new Foo», «async bar», «Object.baz», «foo [as bar]», SpiderMonkey «outer/<».
    function norm(n){
        n=String(n==null?'':n).replace(/^\s+|\s+$/g,'');
        n=n.replace(/\s*\[as [^\]]*\]$/,'');
        n=n.replace(/^(?:(?:new|async|bound|get|set)\s+)+/,'');
        if(n.indexOf('/')>=0){
            var parts=n.split('/'),k,p;
            for(n='',k=parts.length-1;k>=0;k--){p=parts[k].replace(/[<*]+$/,'');if(p){n=p;break;}}
        }
        var dot=n.lastIndexOf('.');
        if(dot>=0)n=n.slice(dot+1);
        return n;
    }
    // true — если НИ одно из имён не встречается в тексте инлайн-скриптов.
    // Граница идентификатора — с `$`. Вердикт по имени помнится, пока число
    // инлайн-скриптов то же: повторная ошибка в цикле не читает текст заново.
    function noneDeclared(names){
        var s=document.querySelectorAll('script:not([src])');
        if(s.length!==memo.n)memo={n:s.length,v:{}};
        var text=null;
        for(var i=0;i<names.length;i++){
            var n=names[i];
            if(!Object.prototype.hasOwnProperty.call(memo.v,n)){
                if(text===null){var parts=[];for(var k=0;k<s.length;k++)parts.push(s[k].text||s[k].textContent||'');text=parts.join('\n');}
                memo.v[n]=new RegExp('(^|[^A-Za-z0-9_$])'+n.replace(/\$/g,'\\$')+'(?![A-Za-z0-9_$])').test(text);
            }
            if(memo.v[n])return false;
        }
        return true;
    }
    window.__docFramesForeign=function(frames,out){
        try{
            if(typeof document==='undefined'||!document||!document.querySelectorAll)return false;
            var doc=bare(location.href);
            var list=(typeof frames==='string')?parse(frames):frames;
            if(!doc||!list||typeof list!=='object'||!list.length)return false;
            var names=[],seen={};
            for(var i=0;i<list.length;i++){
                var f=list[i];
                if(!f||frameUrl(f)!==doc)continue;
                var n=norm(f.fn!=null?f.fn:f['function']);
                if(!n||SKIP.test(n))continue;
                if(/^on[a-z]+$/.test(n))return false;
                if(!IDENT.test(n))return false;
                if(!seen[n]){seen[n]=1;names.push(n);}
            }
            if(!names.length||!noneDeclared(names))return false;
            if(out&&typeof out==='object')out.names=names;
            return true;
        }catch(_){return false;}
    };
})();
/* __docFramesForeign:end */
/* __privilegedThrow:begin */
/* ── Исключение, которое Firefox ПОДМЕНИЛ за чужой привилегированный код ────
   Единственный владелец признака. Спрашивают его четыре места: window.onerror
   и unhandledrejection ниже, ранний перехватчик в шапке layout и фильтр Sentry
   (sentry-noise-filter.js).

   Функцию расширения или менеджера юзерскриптов, отданную странице через
   exportFunction, Firefox вызывает как обычную. Если она бросает, страница
   получает не её исключение, а заглушку: InvalidStateError «An exception was
   thrown» (js/xpconnect/src/ExportHelpers.cpp, MaybeSanitizeException —
   единственное место движка с этой строкой; у Chromium и WebKit её нет, в
   нашем коде и наших библиотеках — тоже). Если функцию вызвал сам браузер
   (таймер, слушатель события), у заглушки нет ни стека, ни файла: Sentry
   TIMBRICA-FRONTEND-P9 (08.10.2026, Firefox 135 / macOS, /video-upscaler и
   /enhance-video сразу после загрузки). Sentry дорисовывал такой ошибке кадр
   «?» с адресом страницы и строкой 0, а наши ворота считали её своей:
   tool_error, авто-стадия `error` и issue. Зонд-расширение 09.10 воспроизвело
   ровно этот вид: file "", line 0, stack "".
   Признак ставится по значению: совпадать должны и имя, и текст. */
window.__privilegedThrow=function(x){
    try{ return !!x&&typeof x==='object'&&x.name==='InvalidStateError'&&x.message==='An exception was thrown'; }
    catch(_){ return false; }
};
/* __privilegedThrow:end */
/* ── Идёт ли у человека работа ПРЯМО СЕЙЧАС ───────────────────────────────
   Единственный вопрос, на который обязана ответить глобальная сеть ошибок,
   прежде чем что-то сказать: прервали ли мы то, чего человек ждёт. Ответ
   даёт машина стадий тула (`__stage` в telemetry-tier0.js) — она уже есть и
   уже пишется в телеметрию, поэтому второго источника правды не заводим.
   Терминальные и «ничего не запущено» стадии — это НЕ работа в полёте.
   Замер 30 дней (RU, 1108 показов): стадии не было вовсе у 769 (69%),
   `error` — у 276 (25%, то есть тул УЖЕ сказал своё), `complete` — у 42 (4%),
   и лишь 16 событий (1.4%) застали настоящий прогон. */
window.__workInFlight=function(){
    var s=String(window.__lastStage||'');
    if(!s)return false;
    return !/^(idle|ready|loaded|complete|done|finished|error|failed|cancelled|canceled|aborted)$/i.test(s);
};
/* __handleError:begin */
/* ── Что человек ЧИТАЕТ в тосте пойманной ошибки ─────────────────────────
   У вызова три роли, и раньше все три склеивались в одну строку на экране:
     err       — что случилось: диагноз, точный текст и стек → телеметрия;
     context   — ГДЕ: ярлык места для разработчика («remove-text-export»,
                 {stage:'batch_zip'}) → телеметрия, на экран НИКОГДА;
     opts.say  — что сказать человеку: фраза поймавшего на языке страницы.
   Замер 30 дней (RU, 07.10.2026): 3273 тоста у 2570 сессий на 107 тулах
   начинались с ярлыка — «document-signer: report_pdf_missing»,
   «{"tool":"video-frames","stage":"showDirectoryPicker"}: Failed to execute…»;
   ни один из 124 ярлыков не фраза для человека. А после ярлыка по-русски
   было 368 из 3273 (11 %): остальное — английский текст движка (2063), голый
   код (674) и хвост лога FFmpeg (168). У тостов, которые тул пишет сам
   (`__toast(t('…'))`, 25 461 за тот же месяц), на языке страницы 99,2 %.
   То есть чужим языком говорит именно эта функция — когда пересказывает
   движок вместо поймавшего.

   Правило — «кто говорит», по порядку:
     1. поймавший дал фразу (opts.say) — она и есть тост;
     2. внутренний сбой JS — общая фраза (как и было);
     3. ошибка двери FFmpeg (её префикс или errorType) — фраза ЕЁ переводчика
        `FFmpegProcessor.getErrorMessage`, та же, что у тулов, зовущих его сами;
     4. строка не для читателя этой страницы — общая фраза. Признаки:
          • один токен ASCII без пробела («decode:load-timeout», «0») — не
            предложение ни на одном из наших языков;
          • текст для разработчика на любом языке страницы: имя исключения
            («TypeError», «NotAllowedError:»), доступ к свойству («(reading
            'x')»), «is not a function», «[object Event]», дамп libav;
          • у страницы не латинская письменность (её определяет сама общая
            фраза страницы), а в строке нет ни одной её буквы;
          • у страницы латиница, а букв другой письменности в строке больше,
            чем латинских (корейский текст движка на испанской странице;
            испанская фраза с кириллическим именем файла остаётся);
          • страница не английская, у неё латиница, а строка — английская:
            только буквы a–z и хотя бы два слова из EN_WORDS (одно — если
            слов не больше трёх: «Decoding failed», «FFmpeg init failed»).
            В EN_WORDS только слова, которых нет в de/es/fr/pt/id: проверено
            на ~122 тыс. строк перевода каждого языка (08.10.2026) — под
            признак попадает не больше 0,09 % из них, и это ярлыки («After
            Effects», «Lightning Network»), а не сообщения; из английских
            текстов движков RU за 30 дней признак ловит 95 % (6886 из 7272);
     5. иначе — текст ошибки как есть, без ярлыка.
   В телеметрии и в отчёте о баге строка прежняя байт в байт — «ярлык: точный
   текст» (realMsg); что увидел человек, говорит поле `ui`.
   Разбор и реплей правил на корпусе — docs/why/primitives/error-toast.md. */
(function(){
    var ALPHABETS=[
        /[\u0400-\u052F]/,  // кириллица
        /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/,  // арабская
        /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/,  // хангыль
        /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF]/,  // кана и иероглифы
        /[\u0590-\u05FF]/,  // иврит
        /[\u0900-\u097F]/,  // деванагари
        /[\u0E00-\u0E7F]/,  // тайская
        /[\u0370-\u03FF]/  // греческая
    ];
    function pageScript(){
        var g=String(window.__GENERIC_ERR||'');
        for(var i=0;i<ALPHABETS.length;i++)if(ALPHABETS[i].test(g))return ALPHABETS[i];
        return null;
    }
    var DEV_TEXT=/\b(?:TypeError|ReferenceError|SyntaxError|RangeError|RuntimeError|DOMException|[A-Z][A-Za-z]*Error|lib(?:av|sw)[a-z]+|Input #\d|Stream #\d)\b|\(reading '|is not a function|\[object [A-Z]/;
    var EN_WORDS={};
    ('the are were be been being had could cannot unable failed fails failure to of and with for this that these those '
     +'from into than then when while which there their they your you we our would should must can\'t couldn\'t doesn\'t '
     +'don\'t isn\'t wasn\'t won\'t didn\'t please again only any invalid unsupported supported denied allowed interrupted '
     +'aborted allocate allocation properties evaluating undefined execute requested request found loaded load decode '
     +'decoding fetch network timeout timed exceeded exceed quota lost returned entries occurred typically problems before '
     +'because cause device its by already started stalled progress processing unexpected creation initialization '
     +'successful missing blocked find create redefine property closed unrecognised unrecognized hung inference chunk unknown '
     +'pending cancelled canceled specified object duration size empty expected got').split(' ').forEach(function(w){EN_WORDS[w]=1;});
    function looksEnglish(s){
        var w=s.toLowerCase().match(/[a-z\u00c0-\u024f]+(?:'[a-z\u00c0-\u024f]+)?/g)||[];
        if(!w.length)return false;
        var hits=0;
        for(var i=0;i<w.length;i++){
            if(/[^a-z']/.test(w[i]))return false;  // буква не из английского — язык читателя
            if(EN_WORDS[w[i]]===1)hits++;
        }
        return hits>=2||(hits>=1&&w.length<=3);
    }
    function otherScriptWins(s){
        var latin=(s.match(/[A-Za-z\u00c0-\u024f]/g)||[]).length, other=0;
        for(var i=0;i<ALPHABETS.length;i++)other+=(s.match(new RegExp(ALPHABETS[i].source,'g'))||[]).length;
        return other>latin;
    }
    window.__errNotForReader=function(text){
        var s=String(text==null?'':text).trim();
        if(!s||/^[\x21-\x7e]+$/.test(s))return true;
        if(DEV_TEXT.test(s))return true;
        var sc=pageScript();
        if(sc)return !sc.test(s);
        if(otherScriptWins(s))return true;
        var loc=String(window.__LOCALE||'').toLowerCase();
        return !!loc&&loc.slice(0,2)!=='en'&&looksEnglish(s);
    };
    window.__errDoorPhrase=function(err,said){
        var P=window.FFmpegProcessor;
        if(!P||P.__isStub||typeof P.getErrorMessage!=='function')return '';
        if(!((err&&err.errorType==='stalled')||/^FFmpeg exit code /.test(said)))return '';
        try{var fc=window.__fileCtx;return String(P.getErrorMessage(err,(fc&&fc.size)||0)||'');}catch(_d){return '';}
    };
    /* Устройству не хватило памяти — это не «сломалось у нас» (10.10.2026, чат
       Премиума 679: 25-минутный микс на iPhone, WebKit «Channel was not able to
       be created.», а человек прочёл «Что-то сломалось на нашей стороне» и так
       нам и написал). За 30 дней такие отказы видели ~40 тулов, и каждый
       показывал либо общую фразу, либо сырой «Array buffer allocation failed».
       Признаки — то, что пишут сами движки при неудачном выделении: V8, WebKit
       (буфер Web Audio), Chromium (рендер OfflineAudioContext), wasm и ORT.
       ⚠️ «memory access out of bounds» — ловушка wasm, НЕ нехватка памяти
       (CLAUDE.md, FFmpeg WASM): сюда не попадает. */
    var MEM_TEXT=/Array buffer allocation failed|Channel was not able to be created|Failed to create audio buffer|failed to create AudioBuffer|AudioBuffer allocation failed|\bOut of memory\b|Aborted\(OOM\)/i;
    window.__errMemoryPhrase=function(said){
        var m=String(window.__MEM_ERR||'');
        if(!m)return '';
        var s=String(said==null?'':said);
        if(/memory access out of bounds/i.test(s))return '';
        return MEM_TEXT.test(s)?m:'';
    };
})();
window.__handleError=function(err,context,opts){
    /* Cancel signals propagated from FFmpeg.terminate() — not bugs, silently drop */
    if(window.__isCancelError(err))return;
    /* «no available backend found … RangeError: Out of memory» is not the device out of
       memory: the browser could not RESERVE the shared memory of the current ORT build
       (WebKit before 18), whichever tool reports it. The owner (window.__ortBuild)
       remembers — every engine wired to it starts on ORT 1.18 from now on, and each
       attempt spared matters: these browsers do not give a shared reservation back
       until they are restarted (emscripten#19374, WebKit 255103). Engines that catch
       the failure themselves tell the owner directly; this is for the rest. */
    try{var _ob=window.__ortBuild;if(_ob&&_ob.initFailed&&_ob.isReservationFailure&&_ob.isReservationFailure(err))_ob.initFailed(err,{legacy:'',from:'funnel:'+(typeof context==='string'?context:'').slice(0,24)});}catch(_){}
    /* err.alreadyReported = true: the typed emit site (FFmpegProcessor / AudioDecoderRobust /
       media-pipeline / etc.) already fired its own tool_error with the precise error_type +
       file shape. Show the visual toast but do NOT double-count as toast_error in telemetry.
       Pino dashboards were drowning in toast_error rows that mirrored the underlying typed
       cluster (the 2026-06-02 audit found 41/30 toast_error events of which most were echoes
       of wasm_process / decode_tier_failed / audio_recover_failed). */
    var alreadyReported = !!(err && err.alreadyReported);
    /* ⚠️⚠️ `uncaught` — ошибку не поймал НИКТО (window.onerror /
       unhandledrejection). Это не оттенок, а другой контракт, и вся разница
       политики сообщений держится здесь.

       Что было. Эта функция писалась для ВЫЗЫВАЮЩЕЙ СТОРОНЫ:
       `__handleError(err,'tts_synth')` — место, которое знает, что человек
       делал, и подписывает свою фразу. Обе глобальные сети переиспользовали
       её же, подавая МАШИННУЮ подпись («Script error @файл:строка:колонка»,
       «Unhandled promise <кадр стека>») и что угодно в качестве err. Поэтому
       на экран уезжало ровно то, что напечатал движок:
         «Script error @iq-test:3:185: undefined is not an object
          (evaluating 'r["@context"].toLowerCase')».
       Замер 30 дней: RU 1108 показов у 705 сессий на 225 тулах, intl 369 у
       117 на 67. Все до единого — с `error_type: toast_error`, то есть
       человек это видел. Жалоба 03.09 (tembrica.com, Edge 152):
       «Script warnign is so bad».

       Почему не лечилось. Признак «эта строка не для человека» стоял на
       ПОЛЕЗНОЙ НАГРУЗКЕ: `err instanceof Error` и имя из четырёх. Но именно в
       тех случаях, что доходят до людей, объекта Error нет или имя другое:
       встроенные браузеры (класс VK/OK) роняют его на ошибках разбора,
       минифицированный чужой код кидает СТРОКУ (проверено на трёх движках:
       `throw 'x'` даёт isError=false везде), а DOMException и security-ошибки
       в четвёрку не входят. Отсюда девять точечных заплат за четыре месяца
       (nativeIframe, C1-символы, _0x, blob-односимвольные, хэш-имена,
       browser.runtime, CSP eval, /vendor/, пустой reject) — и класс живой:
       78 показов на RU за последнюю неделю. Список сигнатур против ОТКРЫТОГО
       множества чужого кода не закрывается никогда.

       Что теперь. Ось решения — не «на что похожа строка», а «кто говорит».
       Поймавший знает действие человека и вправе назвать его. Глобальная сеть
       не знает НИЧЕГО — она измеряет (точная строка, ctx, место и стек уходят
       в телеметрию БЕЗ изменений, payload.msg байт в байт прежний) и молчит,
       кроме двух случаев ниже, где сказать ЕСТЬ ЧТО; тогда — общей фразой на
       языке страницы, без единого слова движка.

       Ожидаемый эффект на тех же 30 днях: из 1108 показов исчезают 830 (75%),
       остальные 278 перестают быть машинными. */
    var _uncaught = !!(opts && opts.uncaught);
    /* `_said` — текст ошибки, `_ctx` — ярлык места, `msg` — строка ДИАГНОЗА
       («ярлык: текст»): по ней работают развилки ниже и она же уходит в
       телеметрию. Объектный контекст приводится к строке ЗДЕСЬ, той же
       формулой, какой его раньше приводила обёртка телеметрии — у посетителя
       без согласия обёртки нет, и на экран уезжало «[object Object]: …». */
    var _said=(err&&err.message)?err.message:String(err||'Unknown error');
    var _ctx='';
    if(context){
        if(typeof context==='string')_ctx=context;
        else{try{_ctx=String(context.slug||context.context||context.where||JSON.stringify(context)||'').slice(0,80);}catch(_cx){_ctx=String(context);}}
    }
    var msg=_ctx?_ctx+': '+_said:_said;
    /* ctx propagation (2026-06-02 etalon — closes the 116-event empty-ctx
       cluster in /pino/streams). Before this, `context` was only smashed
       into the msg string as a prefix ('tts_synth: Failed to fetch'), and
       __toast -> __aeToolError forwarded payload.msg but never a typed
       payload.ctx. /pino/streams grouped by ctx — which was always empty
       — and every tool_error from __handleError landed in the unlabeled
       bucket. Forward as a first-class field via opts.ctx: the telemetry
       row carries payload.ctx = 'tts_synth' (or whatever the call site
       passed). Zero blast radius for tools that don't pass context —
       opts.ctx stays absent and the payload shape is unchanged. */
    /* FFmpeg INIT_ERROR: show specific dialog with adblock detection instead of generic toast */
    if(/INIT_ERROR|ffmpeg.*not loaded|движок|engine.*load/i.test(msg)&&window.__showInitError){
        window.__showInitError();return;
    }
    /* Typed InvalidInputError — route to __reject so the event lands in tool_blocked
       with err.reason, not tool_error. Also covers stable English strings thrown by our
       own WASM wrappers (not user-localised) so they can be recognised without i18n concerns. */
    var REASONS=(window.ToolErrors&&window.ToolErrors.REASONS)||{};
    if(err&&err.name==='InvalidInputError'||err instanceof (window.ToolErrors&&window.ToolErrors.InvalidInputError||function(){})){
        window.__reject(err.message,err.reason||REASONS.INVALID_INPUT||'invalid_input');return;
    }
    if(/^(Input )?file is empty \(0 bytes/i.test(err&&err.message||'')){
        window.__reject(err.message,REASONS.EMPTY_FILE||'empty_file');return;
    }
    if(/FILE_LOST:/i.test(err&&err.message||'')){
        /* Пропавший файл: человеку — фраза о файле по виду отказа (текст
           FILE_LOST — английская строка для разработчика, «…Please re-select
           your file.» на русской странице), диагноз — в телеметрию (realMsg).
           И не второй раз: зона загрузки, сказавшая свой вердикт, метит ошибку
           (__userNotified). Чат 714 (24.10.2026): декод, кончившийся по
           вердикту зоны, нёс ту же ошибку сюда, и вторым тостом шёл её
           английский текст. */
        var _fk=err.fileLostKind;
        var _lostSay=(typeof window.__unreadableFileMsg==='function'&&window.__unreadableFileMsg(_fk==='not_found'||_fk==='browser'?_fk:'read_error'))||window.__GENERIC_ERR||err.message;
        var _lostOpts={reason:REASONS.FILE_LOST||'file_lost',realMsg:msg,ctx:_ctx||undefined};
        try{ if(err.__userNotified){ _lostOpts.noUi=1; _lostOpts.ui='told'; } }catch(_lt){}
        window.__toast(_lostSay,'error',_lostOpts);return;
    }
    var _toastOpts = alreadyReported ? {silent:true} : {};
    if(_ctx) _toastOpts.ctx = _ctx;
    /* ── ВНУТРЕННИЙ сбой JS — это не сообщение человеку ────────────────
       TypeError / RangeError / ReferenceError / SyntaxError движок бросает
       САМ; такие строки никто не писал для чтения. Замер 30 дней: 814 сессий
       на 153 тулах увидели в тосте «Cannot read properties of undefined
       (reading 'getContext')» и подобное — 1147 событий из 26 691 (4.3 %).
       Показываем человеческий текст, а точную строку и СТЕК уносим в
       телеметрию: раньше стека не было вовсе, поэтому такие падения были
       неразбираемы (у /remove-text-from-video ровно так 260 сессий подряд
       сообщали про getContext, и найти место было нечем).
       Признак — err.name, а НЕ шаблон сообщения: наши собственные ошибки
       для человека всегда `new Error(текст)`, и их это не трогает. */
    var _errName = (err && err.name) || '';
    var _internal = !!(err instanceof Error) && /^(TypeError|RangeError|ReferenceError|SyntaxError)$/.test(_errName);
    var _stack = '';
    try { _stack = String((err && err.stack) || '').replace(/\s+/g, ' ').substring(0, 400); } catch (_s) {}
    if(_stack) _toastOpts.stack = _stack;
    /* В телеметрию и в отчёт о баге — ВСЕГДА строка диагноза, что бы ни было
       показано: payload.msg остаётся байт в байт прежним. */
    _toastOpts.realMsg = msg;
    if(_internal)_toastOpts.internal = _errName;
    var _generic = window.__GENERIC_ERR || 'Something went wrong. Please try again.';
    var _say = (opts && typeof opts.say==='string' && opts.say.trim()) ? opts.say : '';
    var _door = '', _mem = window.__errMemoryPhrase ? window.__errMemoryPhrase(_said) : '';
    if(_say){ msg = _say; _toastOpts.ui = 'say'; }
    else if(_mem){ msg = _mem; _toastOpts.ui = 'memory'; }
    else if(_internal){ msg = _generic; _toastOpts.ui = 'generic'; }
    else if((_door = window.__errDoorPhrase(err,_said))){ msg = _door; _toastOpts.ui = 'door'; }
    else if(window.__errNotForReader(_said)){ msg = _generic; _toastOpts.ui = 'generic'; }
    else { msg = _said; _toastOpts.ui = 'text'; }
    if(_uncaught){
        /* Точная строка (вместе с местом) остаётся в телеметрии; человеку —
           общая фраза, и только когда сказать ЕСТЬ ЧТО. Два случая, и оба —
           утверждения, которые мы можем доказать, а не догадки:
             • работа в полёте — мы прервали то, чего человек ждёт;
             • упал СКРИПТ, КОТОРЫЙ ОТДАЁМ МЫ — тул может быть мёртв ещё до
               первого действия («Can't find variable: MicManager» в
               sound-meter.js), и человеку важно узнать, что дело в нас,
               а не в его файле или его браузере.
           Всё остальное — чужой код, вставленный в наш документ: про него мы
           не знаем ничего и молчим. Замер 30 дней (RU): из 1108 показов 793
           (72%) приписаны URL документа — это форма, которую даёт ТОЛЬКО
           вставленный извне скрипт (проверено на живом расширении: код из
           MAIN-мира расширения приходит с chrome-extension://, а вставленный
           в разметку — с адресом страницы), 264 (24%) — наши .js. */
        _toastOpts.uncaught = 1;
        /* Нехватку памяти доказал сам движок — это можно сказать и здесь. */
        msg = _mem || _generic; _toastOpts.ui = _mem ? 'memory' : 'generic';
        var _ours = !!(opts && opts.ourFile);
        if(!_ours && !window.__workInFlight())_toastOpts.noUi = 1;
    }
    /* «Я уже сказал человеку» — контракт бросающего (`err.__userNotified`: его
       ставят мёртвый звуковой контекст, неиграбельный источник, отказ play()),
       и «это решено у владельца» (`err.__playSettled`: наблюдатель play()
       разобрал отказ и либо сказал, либо намеренно промолчал). window.onerror
       этот контракт соблюдал, а эта функция — нет, и восемь тулов проверяли
       метку руками перед вызовом; остальные рисовали второй тост. Запись в
       телеметрию остаётся, тоста нет. */
    var _settled = false;
    try { _settled = !!(err && (err.__userNotified || err.__playSettled)); } catch(_st) {}
    if(_settled){ _toastOpts.noUi = 1; _toastOpts.ui = 'told'; }
    window.__toast(msg,'error',_toastOpts);
    if(typeof console!=='undefined'&&console.error)console.error('[Timbrica]',context||'Error',err);
};
/* __handleError:end */
window.onerror=function(msg,src,line,col,err){
    if(src&&src.indexOf(location.host)===-1)return;
    var m=String(msg||'')+(err&&err.message||'');
    // «ResizeObserver loop …» — безвредное уведомление браузера; голое слово
    // глотало и «Can't find variable: ResizeObserver» — настоящий отказ
    // старого движка (Sentry TIMBRICA-FRONTEND-NS: iOS 12, /audio-overlay
    // не догрузился, а наша телеметрия молчала).
    if(/ResizeObserver loop|Script error\.|no supported sources/i.test(m))return;
    // Контракт «я уже сказал пользователю»: бросающий сам показал человеческий
    // тост и пометил ошибку. Без этой проверки экран получал ВТОРОЙ тост, уже
    // техническим языком — «Script error @timbrica-core.js:1073:19: …» —
    // поверх понятного первого. Замерено прогоном dead-ctx-ab 2026-08-22.
    if(err&&(err.__userNotified||err.name==='AudioContextUnavailable'))return;
    // /vendor/* JS = 3rd-party libraries we don't author (Sentry, ONNX
    // runtime, lamejs, jszip, etc.). Their internal runtime errors are
    // out of our control — Sentry's own bundle.min.js was emitting 7
    // events / 7d into tool_error as `Script error @bundle.min.js:
    // 2:49288: undefined is not an obj` (recursive Sentry self-error
    // 2026-05-30 audit). Filter at the source path so the dashboard
    // stops counting vendor noise as our bugs.
    if(src&&/\/vendor\//.test(src))return;
    // Emscripten WASM libs (Tesseract worker etc) hit a legacy eval() path as
    // a fallback; WebAssembly.instantiate covers the real load, so the CSP
    // EvalError is benign and shouldn't pollute telemetry.
    if(/Content Security Policy|unsafe-eval/i.test(m))return;
    // Browser-extension messaging surfaces. The host page reports e.message
    // as "undefined is not an object (evaluating 'browser.runtime.onMessage
    // .addListener')" / "...sendMessage(...)" when an extension content-
    // script touches its API on a tab where the receiver is gone. The
    // unhandledrejection branch + Sentry beforeSend already filter the
    // rejection-flavoured cousins; this catches the synchronous throw
    // variant that lands in window.onerror with src=our page (because the
    // extension injected its IIFE into our document). Telemetry sample:
    // 5+ events of "Script error @text-to-speech:126:26: ...browser.runtime
    // .onMessage..." in /pino/streams 2026-05-06.
    if(/browser\.runtime|chrome\.runtime|runtime\.(send|on)Message/i.test(m))return;
    /* Obfuscated 3rd-party scripts (commercial JS obfuscators rename every
       identifier _0xABCDEF). A ReferenceError "_0x123456 is not defined" can
       ONLY originate from such code — our own JS never produces these names.
       The 2026-06-02 trim audit found these surfacing through a Sentry-
       hash-suffixed same-origin path (UUID@1:1515) — the prior /vendor/
       filter doesn't catch them because Sentry replays them with an
       on-page URL. Drop at the identifier pattern instead of src. */
    if(/\b_0x[0-9a-f]{4,}\b/i.test(m)&&/is not defined|is not a function|undefined is not/i.test(m))return;
    /* Browser-extension / antivirus blob-injected wrapper scripts. The
       browser surfaces them as `Script error @<UUID>:1:NNNN` after our
       on-page formatter (line 1082) splits the blob: URL — `<UUID>` is the
       blob's GUID. Symptom in /pino/streams 2026-06-19 audit: a "Uncaught
       ReferenceError: <single-letter> is not defined" at column 2247 of
       a blob: script reproduced across 15+ tools (text-to-speech 44, trim 38,
       cpu-benchmark 25 — cpu-benchmark uses neither lamejs nor FFmpeg, so
       this is definitively not our code). Same column, different blob UUIDs
       per session = same minified third-party script injected as a blob.
       We cannot author the wrapper, cannot fix it, and the host's tool is
       broken regardless — but our dashboards must not count the noise as
       our bug. FFmpeg WASM core also runs from a blob: URL (toBlobCached);
       however a real FFmpeg crash never produces a single-letter "is not
       defined" — those identifiers in FFmpeg core are multi-character
       JS names. The single-letter ReferenceError is unique to the injected
       minified wrapper. Filter on src=blob: AND single-letter ref error.
       The `\b[a-z]\b` (boundary on BOTH sides) anchors to an isolated
       one-character identifier — without the second \b, "is" itself in
       "...IdentifierName is not defined" matches `[a-z]\s+is not defined`
       through its own `i` and the filter would swallow real multi-character
       FFmpeg errors. Verified by tests/playwright/volume-3-fixes-real-files
       scratch (case C2: __ASMVMath kept, case C1: `t` dropped). */
    if(src&&/^blob:/i.test(src)&&/\b[a-z]\b\s+is not defined/i.test(m))return;
    /* ── Ещё четыре подписи ЧУЖОГО кода, замерены 30 дней (23.08) ─────────
       Пока их тут не было, человеку показывался тост с чужой поломкой, а наши
       сводки считали её нашей: 728 событий, ~436 сессий, 143 тула.
         • nativeIframe — мост встроенного браузера (VK/OK класс) объявляет
           `let nativeIframe` и инжектится ДВАЖДЫ; 553 события, 143 тула.
           Идентификатора нет нигде в нашем коде (проверено грепом).
         • illegal character U+008x/U+009x — C1-символ в скрипте, которого
           наш origin не отдаёт: 167 событий на 63 тулах, всегда один
           посетитель на тул. Ломай мы байты сами — упали бы все.
         • appendChild + Invalid regular expression flags — вставка чужого
           <script>, чей исходник не компилируется.
         • Fullscreen is not supported — обёртка встроенного браузера.
       Все четыре уже отфильтрованы для Sentry (sentry-init.js) — здесь та же
       правда просто не была применена ко ВТОРОМУ каналу. ⛔ Целиком список
       Sentry сюда переносить нельзя: там есть широкие шаблоны (/Yandex/i,
       /adfox/i), которыми легко проглотить настоящую ошибку.
       Не молчим: считаем отдельным лёгким событием, чтобы объём остался
       виден, но не лежал в tool_error. */
    /* ⚠️ СКРИПТ, ИМЯ КОТОРОГО — ГОЛЫЙ ХЭШ, НАШИМ НЕ БЫВАЕТ.
       Замер за 45 дней: верхняя строка нашей таблицы ошибок — «.at is not a
       function», 1387 событий в 375 сессиях, и ВСЕ они с `loc`
       `v4513226cdae34746b4dedf0b4dfa099e1781791509496:1` (плюс второй такой же
       хэш — вторая версия того же расширения). Туда же «Unexpected token ;»,
       129 событий. Итого 1516 чужих ошибок занимали первое и четвёртое места
       в списке, и любой, кто начинал разбор сверху, начинал с чужого кода.

       Фильтруем по ФОРМЕ ИМЕНИ, а не по тексту сообщения, и это принципиально:
       `.at is not a function` завтра может стать нашей настоящей ошибкой, а
       файл без пути и без расширения, названный сорока шестнадцатеричными
       знаками, — не может. Наши источники всегда одно из двух: слаг тула
       (`pitch:3495`) или настоящий путь с расширением (`/jsv/<ver>/js/foo.js`,
       `bundle.min.js?v=…`). Правило структурное, узкое и не проглатывает
       широкие классы — та самая осторожность, из-за которой список Sentry
       нельзя переносить сюда целиком.

       Не молчим: считается тем же лёгким `third_party_noise`, объём виден. */
    var _srcName = '';
    try { _srcName = String(src || '').split(/[?#]/)[0].split('/').pop(); } catch (_) {}
    if (_srcName && /^v?[0-9a-f]{24,}\d*$/i.test(_srcName)) {
        try{ window.__ae && window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'third_party_noise',sig:'hash_named_script'}); }catch(_){}
        return;
    }
    /* ⚠️ КОД, КОТОРЫЙ ХОСТ ВСТАВИЛ В СТРАНИЦУ, приходит с адресом страницы.
       Вердикт — у __docFramesForeign выше (владелец один на три места). Если
       ни одно имя кадров страницы не объявлено нашими инлайн-скриптами, это не
       наша ошибка — и мы о ней НЕ говорим ничем, кроме лёгкого события: ни
       тоста, ни tool_error, ни авто-стадии `error`. Последнее важнее всего:
       авто-стадия закрывает журнал прогона, то есть отпускает замок экрана и
       вопрос «точно уйти?» посреди чужой работы (Sentry MN — Chrome for iOS,
       прогоны на iPhone). Одно событие на случай: объём остаётся виден, а имена
       (`fn`) покажут, если внедряющий код сменится. */
    var _udf={};
    if(err&&err.stack&&window.__docFramesForeign&&window.__docFramesForeign(err.stack,_udf)){
        try{ window.__ae && window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'third_party_noise',sig:'undeclared_doc_frames',fn:(_udf.names||[]).slice(0,4).join(',').slice(0,60)}); }catch(_){}
        return;
    }

    var _tpSig = /'nativeIframe' has already been declared/.test(m) ? 'nativeIframe'
              : /illegal character U\+00[89][0-9A-F]/i.test(m) ? 'c1_char'
              : /appendChild[\s\S]*Invalid regular expression flags/.test(m) ? 'appendchild_regex'
              : /Fullscreen is not supported/.test(m) ? 'fullscreen_wrapper'
              /* Заглушка Firefox за исключением расширения (Sentry P9) — признак у
                 __privilegedThrow выше. */
              : (window.__privilegedThrow&&window.__privilegedThrow(err)) ? 'privileged_sanitized'
              : '';
    if(_tpSig){
        try{ window.__ae && window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'third_party_noise',sig:_tpSig}); }catch(_){}
        return;
    }
    /* Ошибку БЕЗ имени файла И без объекта Error приписать себе НЕЛЬЗЯ: нет ни
     * стека, ни источника — только строка. Замерено 2026-08-22 по нашему же
     * каналу tool_error: таких 30 из 3178 за 14 дней (1%), и самая частая из
     * них — «Cannot read properties of null (reading 'tagName')» на ДВЕНАДЦАТИ
     * несвязанных тулах, то есть заведомо не наша.
     *
     * Технический тост за неё вреден в любом случае, кем бы она ни была
     * прислана: человек видел «Script error: The object is in an invalid
     * state.» (Sentry ER, /ar-drawing) — строку, с которой ему нечего делать,
     * поверх работающей страницы.
     *
     * Данные при этом НЕ теряются: ранний перехватчик в шапке лейаута уже
     * записал событие в tool_error с ctx 'window.onerror' и пустым loc — по
     * нему класс и считается. Целевые диагностики тулов (например
     * ard_invalid_state) тоже висят отдельными слушателями и продолжают
     * срабатывать. Молчит ровно тост, а не измерение.
     *
     * ⚠️ Условие намеренно требует ОБОИХ признаков сразу. У инлайнового
     * скрипта src — это URL документа, у внешнего — URL файла; наша ошибка
     * почти всегда приносит либо файл, либо объект Error. */
    if(!src&&!err){
        try{ if(typeof console!=='undefined'&&console.warn)console.warn('[Timbrica] неатрибутируемая ошибка (ни файла, ни объекта):',String(msg||'').slice(0,160)); }catch(_){}
        return;
    }
    var ctx='Script error';
    var _ourFile=false;
    if(src){var f=src.split('/').pop().split('?')[0];if(f&&f!=='/')ctx+=' @'+f+':'+line+':'+col;
        /* Файл, который отдаём МЫ: тот же origin (проверено выше) И настоящее
           имя скрипта с расширением. Адрес ДОКУМЕНТА (`/ru/iq-test`) под это
           не подходит намеренно — именно его получает всякий скрипт, вставленный
           в нашу разметку снаружи. Признак структурный: расширения у документа
           не бывает, а у нашего файла оно есть всегда. */
        _ourFile=/\.(m?js)$/i.test(f);}
    /* Решение — на макрозадачу позже, как у раннего перехватчика в layout:
       владелец отцепленного отказа (detached-failure.js, face-swap/detect.js …)
       забирает его своим слушателем, который стоит ПОЗЖЕ этого, — и тост поверх
       забранного отказа уже был бы ложью (Sentry MV, /photo-restore). Вопрос
       «забран ли» — у window.__failureClaimed (layout, один владелец). */
    var _ev=(typeof window.event==='object')?window.event:null;
    setTimeout(function(){
        if(window.__failureClaimed&&window.__failureClaimed(_ev,err))return;
        window.__handleError(err||msg,ctx,{uncaught:true,ourFile:_ourFile});
    },0);
};
window.addEventListener('unhandledrejection',function(e){
    var r=e.reason;
    if(r&&r.name==='AbortError')return;
    var m=r&&r.message||String(r||'');
    var stack=r&&r.stack||'';
    var name=r&&r.name||'';
    // Combined haystack: name often only appears in stack for DOMExceptions but
    // can be missing for synthesised errors — pattern-matching against all three
    // ensures filter rules trigger regardless of which field carries the signal.
    var hay=name+' '+m+' '+stack;
    // Narrow filter: only ignore what's indisputably NOT our code or not actionable.
    //   - Network / SW infrastructure signals (not bugs, just connectivity noise)
    //   - Rejections whose stack originates in a browser extension (chrome-extension:// /
    //     moz-extension://) — we cannot see or fix that code, and it pollutes telemetry.
    // Everything else — including HTMLMediaElement play rejections, AudioContext errors,
    // decodeAudioData failures — reaches __handleError. __handleError now recognises
    // typed ToolErrors and stable FFmpegProcessor strings (empty file / FILE_LOST) and
    // routes THEM to tool_blocked. Tool-specific UX failures (codec mismatch, autoplay)
    // are caught at the call site via safePlay / try-catch, not via global filter.
    if(/FetchEvent\.respondWith|Load failed|NetworkError|Failed to fetch|AbortError|ResizeObserver loop/i.test(hay))return;
    // User-policy / permission denials from Promise-returning DOM APIs.
    // These are NOT bugs — they are normal outcomes when a browser refuses or
    // a user declines. If unhandled, they used to surface as red toast +
    // Sentry noise. Audited 2026-05-01 across requestFullscreen, clipboard,
    // wakeLock, navigator.share, getUserMedia, requestMIDIAccess, PiP.
    //
    //   - "not granted" / "fullscreen error" / "Permissions check failed" /
    //     "API can only be initiated by"  → requestFullscreen denied
    //   - "Fullscreen is not supported"    → requestFullscreen in an Android
    //     WebView whose host app skipped onShowCustomView (the method EXISTS,
    //     the promise rejects) — TIMBRICA-FRONTEND-CR, 2026-07-31
    //   - "NotAllowedError" / "Write permission denied" / "Read permission denied"
    //     / "Document is not focused" / "Clipboard API"
    //                                      → clipboard / wakeLock / PiP
    //   - "NotFoundError" / "OverconstrainedError"
    //                                      → getUserMedia (no device / impossible constraints)
    //   - "Permission denied by system" / "Permission dismissed"
    //                                      → geolocation / MIDI / sensor APIs
    //   - "SecurityError"                  → http context for secure-only APIs
    //
    // Call sites SHOULD still .catch() at the source (so they can show context-
    // appropriate UX). This filter is defense-in-depth for the long tail.
    if(/not granted|Permissions check failed|fullscreen error|Fullscreen is not supported|API can only be initiated by|NotAllowedError|Write permission denied|Read permission denied|Document is not focused|Clipboard API|NotFoundError|OverconstrainedError|Permission denied by system|Permission dismissed|SecurityError/i.test(hay))return;
    if(/^(chrome|moz|safari-web)-extension:\/\//i.test(stack)||/(^|\s)chrome-extension:\/\//i.test(stack))return;
    if(/(chrome|moz|safari-web)-extension:\/\//i.test(m)||/\binpage\.js\b/.test(hay))return;
    // Event-as-rejection from `el.onerror = reject` patterns (script / img /
    // audio / link load failures). Sentry surfaces these as
    // "Event captured as promise rejection" with no useful stack — they're
    // always asset-load issues which carry their own CDN fallback UX. ~10
    // tools have this pattern. Filter here so per-tool refactor isn't urgent.
    if(r instanceof Event||(r&&r.type==='error'&&typeof r.target==='object'))return;
    // MIUI WebView (Xiaomi's Android browser) bridges JS through a Java RPC.
    // When something on the Java side throws (varies by device firmware),
    // the JS receives an opaque `Error: Java exception was raised during
    // method invocation`. Stack frames are all `<anonymous>:1`. Not our bug,
    // not actionable from JS — filter to keep the error feed signal-rich.
    if(/Java exception was raised during method invocation/i.test(hay))return;
    /* Та же заглушка Firefox, что у window.onerror: async-код позвал функцию
       расширения, и её исключение стало отказом промиса. Бросал не наш код. */
    if(window.__privilegedThrow&&window.__privilegedThrow(r)){
        try{ window.__ae && window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'third_party_noise',sig:'privileged_sanitized'}); }catch(_){}
        return;
    }
    /* Отказ БЕЗ причины: `Promise.reject()` / `reject(undefined)` / `throw null`.
       Такой reason не несёт ни сообщения, ни имени, ни стека — из него нельзя
       ни составить человеческую фразу, ни найти место в коде. __handleError на
       falsy подставлял литерал 'Unknown error', приписывал 'Unhandled promise: '
       и ПОКАЗЫВАЛ это тостом: замер 9 дней (client-errors.log RU, 21–29.08) —
       1399 показов у 270 тулов, ~170 в сутки, все до единого без кадра стека.
       Русскому пользователю показывали «Unhandled promise: Unknown error».

       Соседний обработчик в layouts/app.blade.php (ранний захват для телеметрии
       и Sentry) на этот же случай делает `if(!err)return` — то есть считает его
       шумом и НЕ регистрирует. Два обработчика расходились ровно наоборот:
       молчали там, где надо считать, и говорили там, где сказать нечего.

       Ветка `_internal` ниже написана как раз против показа служебных строк
       («Cannot read properties of undefined»), но её признак — `err instanceof
       Error`, а пустой отказ Error'ом не является и мимо неё проходил.

       Что делаем: тоста нет (сообщать нечего, и в 96% случаев (1344/1399) у
       события не было даже предыдущей стадии тула — то есть человек в этот
       момент ничего не запускал), но событие НЕ теряем: уводим в отдельное
       типизированное ведро. Раньше оно приходило как toast_error с мусорным
       msg и вдобавок гнало машину стадий в `error`, портя воронку 270 тулам. */
    if(!r){
        try{ window.__ae && window.__ae('unhandled_rejection_empty',{
            slug:(document.querySelector('meta[name="tool-slug"]')||{}).content||'',
            kind:(r===null?'null':typeof r)
        }); }catch(_e){}
        return;
    }
    var ctx='Unhandled promise';
    // stack frame capture: first non-empty caller frame, widened 120→300
    // to preserve full URL + line:col on compound paths (e.g. long
    // /cdn/.../v{ts}/filename.js?v= query strings).
    if(stack){var lines=stack.split('\n');for(var i=1;i<lines.length&&i<3;i++){var l=lines[i].trim();if(l){ctx+=' '+l.substring(0,300);break;}}}
    // Тот же признак, что у window.onerror, только источник — кадры стека:
    // наш origin и настоящее имя файла со скриптовым расширением.
    var _ourFile=false;
    try{ _ourFile=new RegExp('(^|[\\s(])https?://'+location.host.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')+'/[^\\s)]*\\.m?js').test(stack||''); }catch(_o){}
    /* Как у window.onerror выше: забранный владельцем отказ — не наш тост. */
    setTimeout(function(){
        if(window.__failureClaimed&&window.__failureClaimed(e,r))return;
        window.__handleError(r,ctx,{uncaught:true,ourFile:_ourFile});
    },0);
});

// ── Microphone error helper ──
// Returns a localized, browser-specific message for mic permission errors
window.__micErrorMsg=function(err){
    var L=window.__LOCALE||'en';
    var ua=navigator.userAgent;
    var notFound=err&&(err.name==='NotFoundError'||err.name==='DevicesNotFoundError');
    var mob=/Mobi|Android|iPhone|iPad/i.test(ua);
    var saf=/Safari/i.test(ua)&&!/Chrome|CriOS|Edg/i.test(ua);
    var M={
        en:{denied:'Microphone access denied.',nf:'No microphone found. Check that a mic is connected to your device.',
            desk:'Click the 🔒 icon next to the URL, allow Microphone, then reload the page.',
            saf:'Go to Safari → Settings → Websites → Microphone → Allow for this site.',
            mob:'Open your browser Settings → Site permissions → Microphone → Allow.'},
        ru:{denied:'Доступ к микрофону запрещён.',nf:'Микрофон не найден. Проверьте, подключён ли микрофон к устройству.',
            desk:'Нажмите 🔒 рядом с адресом сайта, разрешите Микрофон, затем обновите страницу.',
            saf:'Откройте Safari → Настройки → Веб-сайты → Микрофон → Разрешить для этого сайта.',
            mob:'Откройте Настройки браузера → Разрешения сайтов → Микрофон → Разрешить.'},
        ko:{denied:'마이크 접근이 거부되었습니다.',nf:'마이크를 찾을 수 없습니다. 마이크가 연결되어 있는지 확인하세요.',
            desk:'URL 옆의 🔒 아이콘을 클릭하고 마이크를 허용한 후 페이지를 새로고침하세요.',
            saf:'Safari → 설정 → 웹사이트 → 마이크 → 이 사이트 허용으로 이동하세요.',
            mob:'브라우저 설정 → 사이트 권한 → 마이크 → 허용을 열어주세요.'},
        ar:{denied:'تم رفض الوصول إلى الميكروفون.',nf:'لم يتم العثور على ميكروفون. تأكد من توصيل ميكروفون بجهازك.',
            desk:'انقر على أيقونة 🔒 بجانب عنوان الموقع، اسمح بالميكروفون، ثم أعد تحميل الصفحة.',
            saf:'افتح Safari ← الإعدادات ← مواقع الويب ← الميكروفون ← السماح لهذا الموقع.',
            mob:'افتح إعدادات المتصفح ← أذونات الموقع ← الميكروفون ← السماح.'},
        id:{denied:'Akses mikrofon ditolak.',nf:'Mikrofon tidak ditemukan. Pastikan mikrofon terhubung ke perangkat Anda.',
            desk:'Klik ikon 🔒 di samping URL, izinkan Mikrofon, lalu muat ulang halaman.',
            saf:'Buka Safari → Pengaturan → Situs Web → Mikrofon → Izinkan untuk situs ini.',
            mob:'Buka Pengaturan browser → Izin situs → Mikrofon → Izinkan.'},
        pt:{denied:'Acesso ao microfone negado.',nf:'Microfone não encontrado. Verifique se um microfone está conectado ao seu dispositivo.',
            desk:'Clique no ícone 🔒 ao lado da URL, permita o Microfone e recarregue a página.',
            saf:'Vá em Safari → Ajustes → Sites → Microfone → Permitir para este site.',
            mob:'Abra as Configurações do navegador → Permissões do site → Microfone → Permitir.'},
        de:{denied:'Mikrofonzugriff verweigert.',nf:'Kein Mikrofon gefunden. Prüfe, ob ein Mikrofon an deinem Gerät angeschlossen ist.',
            desk:'Klicke auf das 🔒-Symbol neben der Adresse, erlaube das Mikrofon und lade die Seite neu.',
            saf:'Gehe zu Safari → Einstellungen → Websites → Mikrofon → Für diese Website erlauben.',
            mob:'Öffne die Browser-Einstellungen → Website-Berechtigungen → Mikrofon → Erlauben.'},
        es:{denied:'Acceso al micrófono denegado.',nf:'No se encontró ningún micrófono. Comprueba que haya un micrófono conectado al dispositivo.',
            desk:'Haz clic en el icono 🔒 junto a la dirección, permite el Micrófono y recarga la página.',
            saf:'Ve a Safari → Configuración → Sitios web → Micrófono → Permitir para este sitio.',
            mob:'Abre los Ajustes del navegador → Permisos del sitio → Micrófono → Permitir.'},
        fr:{denied:'Accès au microphone refusé.',nf:'Aucun microphone détecté. Vérifiez qu’un micro est bien connecté à votre appareil.',
            desk:'Cliquez sur l’icône 🔒 à côté de l’adresse, autorisez le Microphone, puis rechargez la page.',
            saf:'Ouvrez Safari → Réglages → Sites web → Microphone → Autoriser pour ce site.',
            mob:'Ouvrez les Réglages du navigateur → Autorisations du site → Microphone → Autoriser.'},
        uk:{denied:'Доступ до мікрофона заборонено.',nf:'Мікрофон не знайдено. Перевірте, чи підключений мікрофон до пристрою.',
            desk:'Натисніть 🔒 поруч з адресою сайта, дозвольте Мікрофон, потім оновіть сторінку.',
            saf:'Відкрийте Safari → Налаштування → Веб-сайти → Мікрофон → Дозволити для цього сайта.',
            mob:'Відкрийте Налаштування браузера → Дозволи сайтів → Мікрофон → Дозволити.'},
        tr:{denied:'Mikrofon erişimi reddedildi.',nf:'Mikrofon bulunamadı. Cihazınıza bir mikrofon bağlı olduğundan emin olun.',
            desk:'Adres çubuğundaki 🔒 simgesine tıklayın, Mikrofona izin verin, ardından sayfayı yeniden yükleyin.',
            saf:'Safari → Ayarlar → Web Siteleri → Mikrofon → Bu site için izin verin.',
            mob:'Tarayıcı Ayarları → Site izinleri → Mikrofon → İzin Ver bölümünü açın.'}
    };
    var m=M[L]||M.en;
    if(notFound) return m.nf;
    var hint=saf?m.saf:mob?m.mob:m.desk;
    return m.denied+' '+hint;
};

// __toolSettings is now defined in public/js/tool-settings.js (loaded
// synchronously in the layout BEFORE this file). Kept here as a comment
// so future readers searching for the global know where to find it.

// ── CSRF auto-refresh for nginx-cached pages ──
(function(){
    if(!window.fetch)return;
    var meta=document.querySelector('meta[name="csrf-token"]');
    var _f=window.fetch.bind(window);
    var rp=null,prefetched=false;
    window.__refreshCsrf=function(){
        if(!rp){rp=_f('/api/csrf-token',{credentials:'same-origin'}).then(function(r){return r.json()}).then(function(d){if(meta&&d.token)meta.content=d.token;rp=null;prefetched=true;return d.token}).catch(function(){rp=null;return new Promise(function(res){setTimeout(function(){_f('/api/csrf-token',{credentials:'same-origin'}).then(function(r){return r.json()}).then(function(d){if(meta&&d.token)meta.content=d.token;prefetched=true;res(d.token)}).catch(function(){res(null)})},1000)})})}
        return rp;
    };
    window.fetch=function(url,opts){
        return _f(url,opts).then(function(r){
            if(r.status===419&&opts&&opts.method&&/^(POST|PUT|PATCH|DELETE)$/i.test(opts.method)){
                return window.__refreshCsrf().then(function(tk){
                    if(!tk)return r;
                    if(opts.body instanceof FormData)opts.body.set('_token',tk);
                    if(opts.headers){if(opts.headers instanceof Headers)opts.headers.set('X-CSRF-TOKEN',tk);else if(typeof opts.headers==='object')opts.headers['X-CSRF-TOKEN']=tk}
                    return _f(url,opts);
                });
            }
            return r;
        });
    };
    /* Готовность токена для тулов, которые шлют запрос БЕЗ действия человека.
     *
     * Страница приходит из кэша nginx (X-Cache-Status: HIT) вместе с чужим
     * токеном в <meta> и без сессионной куки. Подогрев ниже спасает только тех,
     * кто что-то нажал: он подписан на focusin/change/click. Тул, который
     * стреляет сам при загрузке, до этого не доживает — его первый POST
     * гарантированно ловит 419 и лечится лишь повтором.
     *
     * Замер на /ip-lookup за сутки 08.08: 261 ответ 419 против 326 успешных,
     * 111 из 135 клиентов поймали 419 (108 спас повтор, трое остались вообще
     * без ответа). То есть почти каждый посетитель платил лишним кругом.
     *
     * Есть сессия — токен уже свой, ждать нечего; нет — один GET за свежим.
     * Промис никогда не отклоняется: не сумели обновить — пусть тул сделает
     * попытку со старым токеном, повтор в обёртке fetch выше остаётся вторым
     * рубежом.
     */
    window.__csrfReady=function(){
        if(prefetched||/timbrica-session/.test(document.cookie)){
            return Promise.resolve(meta&&meta.content||null);
        }
        return window.__refreshCsrf().catch(function(){return null});
    };
    if(!/timbrica-session/.test(document.cookie)){
        var once=function(){if(!prefetched)window.__refreshCsrf();document.removeEventListener('focusin',h);document.removeEventListener('change',h);document.removeEventListener('click',h)};
        var h=function(e){var t=e.target;if(t&&(t.tagName==='INPUT'||t.tagName==='SELECT'||t.tagName==='TEXTAREA'||t.tagName==='BUTTON'||t.closest&&(t.closest('.upload-zone')||t.closest('form')||t.closest('[type=file]'))))once()};
        document.addEventListener('focusin',h);document.addEventListener('change',h);document.addEventListener('click',h);
    }
})();

// ── Theme toggle (header + sidebar) — 3-state cycle: system → light → dark ──
// Decision tree MUST mirror the head bootstrap in app.blade.php so we never
// flip the theme after first paint. The head sets data-theme before body
// renders; this module just wires the cycle button and `prefers-color-scheme`
// system-pref change listener — every `apply()` re-runs the same decision.
(function(){
    var darkMql = window.matchMedia ? matchMedia('(prefers-color-scheme: dark)') : null;
    var standaloneMql = window.matchMedia ? matchMedia('(display-mode: standalone)') : null;
    var root = document.documentElement;
    var btns = ['themeToggle'].map(function(id){return document.getElementById(id)}).filter(Boolean);
    // Явный выбор из трёх — «Настройки» сайдбара (partials/sidebar-settings, 15.10.2026);
    // круговой переключатель остался в шапке страниц без сайдбара.
    var choices = document.querySelectorAll('[data-theme-choice]');

    function getPref(){
        var p = localStorage.getItem('theme');
        return (p==='light'||p==='dark'||p==='system') ? p : 'system';
    }
    // Decides the actual applied theme for a given pref. Keep in lockstep
    // with the head bootstrap — TWA / installed-PWA standalone on Android
    // force-applies dark so content matches the dark splash (#0a0f1a).
    function resolve(pref){
        if(pref === 'dark') return 'dark';
        if(pref === 'light') return 'light';
        var standalone = (standaloneMql && standaloneMql.matches) || window.navigator.standalone === true;
        var android = /Android/i.test(navigator.userAgent);
        if(standalone && android) return 'dark';
        return (darkMql && darkMql.matches) ? 'dark' : 'light';
    }
    function apply(pref){
        root.setAttribute('data-theme', resolve(pref));
        root.setAttribute('data-theme-pref', pref);
        localStorage.setItem('theme', pref);
        var labels = window.__THEME_LABELS || {};
        var label = labels[pref] || '';
        btns.forEach(function(b){ if(label){ b.title=label; b.setAttribute('aria-label',label); } });
        for(var i=0;i<choices.length;i++) choices[i].setAttribute('aria-pressed', choices[i].getAttribute('data-theme-choice')===pref ? 'true' : 'false');
    }
    function cycle(){
        var cur = getPref();
        apply(cur==='system' ? 'light' : cur==='light' ? 'dark' : 'system');
    }
    if(darkMql){
        var onChange = function(){ if(getPref()==='system') apply('system'); };
        if(darkMql.addEventListener) darkMql.addEventListener('change', onChange);
        else if(darkMql.addListener) darkMql.addListener(onChange);
    }
    btns.forEach(function(b){ b.addEventListener('click', cycle); });
    Array.prototype.forEach.call(choices, function(c){
        c.addEventListener('click', function(){
            var v = c.getAttribute('data-theme-choice');
            if(v!=='light'&&v!=='dark'&&v!=='system') return;
            apply(v);
            try{ if(window.__ae) window.__ae('feature_use', { f: 'theme_choice', v: v }); }catch(_){}
        });
    });
    apply(getPref());
})();

// ── Language dropdowns + Bookmarks ──
(function(){
    // Language switchers use <details>/<summary> — open/close works natively
    // without JS (so the locale switcher stays usable even if this file fails
    // to load, e.g. RU users hitting NL primary URLs on /en/, /de/ pages).
    // JS only enhances UX with click-outside-closes.
    var swes=document.querySelectorAll('details.lang-switch');
    if(swes.length){
        document.addEventListener('click',function(e){
            swes.forEach(function(d){
                if(d.open && !d.contains(e.target)) d.open=false;
            });
        });
    }

    window.Bookmarks={
        KEY:'timbrica_bookmarks',
        get:function(){try{return JSON.parse(localStorage.getItem(this.KEY)||'[]')}catch(e){return[]}},
        has:function(s){return this.get().indexOf(s)>=0},
        toggle:function(s){var b=this.get(),i=b.indexOf(s);if(i>=0)b.splice(i,1);else b.push(s);localStorage.setItem(this.KEY,JSON.stringify(b));var added=i<0;try{document.dispatchEvent(new CustomEvent('bookmarks-changed'))}catch(_){}if(window.__AUTH_USER){try{fetch('/api/bookmark-toggle',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','X-CSRF-TOKEN':(document.querySelector('meta[name=csrf-token]')||{}).content||''},body:JSON.stringify({slug:s})})}catch(e){}}return added}
    };

    var bmBtn=document.getElementById('bookmarkBtn');
    if(bmBtn){
        if(Bookmarks.has(bmBtn.dataset.slug))bmBtn.classList.add('active');
        bmBtn.addEventListener('click',function(){
            var added=Bookmarks.toggle(this.dataset.slug);
            this.classList.toggle('active',added);
        });
    }
})();

// ── File reader (fetch blob + FileReader fallback for iOS) ──
window.readFileBuffer=function(file,onProgress){
    var _readViaFR=function(){
        return new Promise(function(ok,fail){
            var rd=new FileReader();
            rd.onload=function(){ok(rd.result)};
            rd.onerror=function(){fail(rd.error||new Error('read failed'))};
            if(onProgress)rd.onprogress=function(ev){if(ev.lengthComputable)onProgress(ev.loaded,ev.total)};
            rd.readAsArrayBuffer(file);
        });
    };
    if(!window.fetch) return _readViaFR();
    var url=URL.createObjectURL(file);
    return fetch(url).then(function(r){return r.arrayBuffer()}).then(function(buf){
        if(onProgress)onProgress(file.size,file.size);
        URL.revokeObjectURL(url);return buf;
    }).catch(function(){
        URL.revokeObjectURL(url);
        return _readViaFR();
    });
};

// ── Печать документа, собранного на клиенте ──
// window.__printWindow(html, opts) → окно или null. opts: features, delay,
// onBlocked.
//
// ⚠️ Попап из window.open('') НАСЛЕДУЕТ CSP открывшей страницы, а скрипт,
// пришедший из document.write, — parser-inserted. `'strict-dynamic'` покрывает
// только скрипты, ВСТАВЛЕННЫЕ скриптом, поэтому на такой не распространяется, и
// нонса у него нет. Классический приём «положить в разметку
// <script>window.print()</script>» из-за этого молча не срабатывает: окно
// открывается с готовым содержимым, печать не начинается, а в консоли открывшей
// страницы пусто — отказ падает в консоль ПОПАПА, куда никто не смотрит.
//
// Так были сломаны ЧЕТЫРЕ кнопки печати (/emoji, /ocr-text-extractor,
// /russian-cursive, /portuguese-cursive) со включения строгого CSP 01.05.2026.
// Нашлось только отчётом csp:first-party-alert 26.08 — одной строкой
// «1 сессий inline ← .../js/emoji/emoji-print.js». За 30 дней кнопку нажали
// 27 раз в 21 сессии, и каждый раз не происходило ничего. Воспроизведено на
// проде: в попапе `window.onload` остаётся null, элемент <script> при этом
// в DOM есть — заблокированный скрипт оттуда никуда не девается, поэтому
// «тег на месте» ничего не доказывает.
//
// Здесь печать зовёт ОТКРЫВШАЯ страница: её код уже доверен, инлайн-скрипт не
// нужен вовсе. Это дешевле нонса и не зависит от того, что разрешает политика.
window.__printWindow = function(html, opts){
    opts = opts || {};
    var w = window.open('', '_blank', opts.features || '');
    if(!w){ if(opts.onBlocked) opts.onBlocked(); return null; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    var done = false;
    var fire = function(){
        if(done) return; done = true;
        // opts.delay — под веб-шрифты: `load` их не ждёт, и без паузы печать
        // уходит с подстановочным шрифтом (прописи так и настроены, 250 мс).
        setTimeout(function(){
            // Окно могли уже закрыть — печать чужого окна не должна ронять тул.
            try{ w.focus(); w.print(); }catch(_e){}
        }, opts.delay || 0);
    };
    // document.close() мог дособрать документ синхронно — тогда load уже был.
    if(w.document.readyState === 'complete') fire();
    else w.addEventListener('load', fire);
    return w;
};

// ── Universal file download ──
// window.__downloadFile(blob, filename) — saves a client-side Blob as a file
// in EVERY browser, including Android WebView (YaBrowser Lite, in-app browsers).
//
// The classic `<a download href="blob:">` + click is a SILENT NO-OP in Android
// WebView: WebView ignores the `download` attribute and never routes a blob:
// URL to its DownloadListener. Bug-report 2026-05-17 ("Не скачивает"): user
// clicked download 3× with nothing saved; a 7-day sweep found 89 sessions/week
// clicking 6+ times. ~174 tools share this latent bug.
//
// The fix that works everywhere: POST the blob to /api/download-stash, then
// navigate to the returned URL — the server streams it with
// `Content-Disposition: attachment`, which WebView's DownloadListener DOES
// intercept (and real browsers download normally). One mechanism, no UA
// sniffing. On any stash failure (offline, server down) it falls back to the
// classic blob download — in that scenario the user is necessarily on a real
// browser where the classic path works.
//
// Returns a Promise<boolean> — true if a download was triggered.
// opts.src — 'interceptor' when called by the legacy-anchor interceptor,
//            else the call is a tool using the primitive directly. Surfaced
//            in telemetry so the T+7d watch can read stash-success / fallback
//            rate directly instead of inferring from repeated-click proxies.
//
// ── Who actually needs the stash ──
// The stash (POST blob → navigate to Content-Disposition URL) is REQUIRED
// only where `<a download href="blob:">` is a silent no-op: Android System
// WebView and in-app browsers. For every real browser (desktop + standalone
// mobile Chrome/Safari/Firefox/Edge/Yandex) the blob anchor downloads
// natively — instant, no upload, no TTL.
// Bug-report #443 (Артём, 2026-05-17, recurring weekly across tools):
// "процесс быстро, потом тишина, иногда через минуты сохраняется, нужен
// reload". Root cause — routing EVERY browser through the stash: a
// multi-MB FormData POST blocks on a slow uplink (the "minutes" delay),
// and the 15-min stash TTL + prune-on-every-POST can delete the entry
// before a backgrounded GET lands → 404 → silence → reload. Surgically
// gating the stash to WebView removes that whole failure class for the
// ~95% of users on real browsers.
// ── Telegram Mini App detection ──
// Two signals, either suffices: the live bridge (telegram-web-app.js with
// initData — restored from sessionStorage on every in-app navigation), or
// the tmb_tg=1 marker cookie set by the /tg entry page. The cookie covers
// pages where the vendor lib failed to load AND Telegram Desktop, whose
// webview UA masquerades as a plain browser. Webview storage is isolated
// from the user's real browsers, so the cookie can't leak outside Telegram.
window.__isTelegramWebview=function(){
    try{if(window.Telegram&&window.Telegram.WebApp&&window.Telegram.WebApp.initData)return true;}catch(e){}
    try{return document.cookie.indexOf('tmb_tg=1')!==-1;}catch(e){return false;}
};
window.__downloadNeedsStash=function(){
    // Telegram webviews are exactly the "blob anchor is a silent no-op"
    // class on every platform (Android wv, iOS WKWebView host, Desktop
    // WebView2/CEF) — and Desktop's UA gives none of the tokens below away.
    if(window.__isTelegramWebview())return true;
    var ua=navigator.userAgent||'';
    // Android System WebView carries the "; wv)" token (since 2014) —
    // covers YaBrowser Lite and every in-app Android browser.
    if(/;\s*wv[\);]/i.test(ua))return true;
    // In-app browsers that host a WebView without the "wv" token
    // (iOS WKWebView hosts, some Android shells).
    if(/GSA\/|FBAN\/|FBAV\/|Instagram|Line\/|Snapchat|TikTok|BytedanceWebview|MicroMessenger/i.test(ua))return true;
    // Yandex Search / Yandex Go in-app browser (huge in the RU market): the UA
    // looks like plain Chrome — NO "; wv)" token — but carries `YaSearchApp/`,
    // `YaSearchBrowser/` + `webOmni`. It's a real WebView where `<a download
    // href="blob:">` is a silent no-op (bug-report 2026-06-14, SM-S711B,
    // 50 MB joined.mp3 "не скачивается" — file_download fired via:classic_native
    // and never downloaded). Detect it so downloads route through the stash.
    // Crucially does NOT match the STANDALONE Yandex Browser (token `YaBrowser/`,
    // a real browser where the blob anchor downloads natively) — that must keep
    // the classic fast-path to avoid the #443 slow-upload-then-silence regression.
    // Both UA dialects of the app — see the in-app detector near the top.
    if(/YaSearchApp|YaSearchBrowser|YaApp_Android|webOmni/i.test(ua))return true;
    // Chinese/OEM Android browsers whose DOWNLOAD MANAGER lives outside the
    // renderer and cannot pull a page-held blob: URL — the WebView
    // DownloadListener limitation wearing a full-browser UI. The blob anchor
    // "clicks", our telemetry says delivered, no file ever lands.
    // PROVEN for HuaweiBrowser: Лаура (Premium, LMR-LX9/Android 12 HMS,
    // HuaweiBrowser/17 = frozen Chrome/114), 2026-07-20/21 — 17 classic_native
    // "delivered" downloads, zero files on the device, rage-click on the
    // «Файл сохранён» toast. Cohort: ~527 HuaweiBrowser + 262 MiuiBrowser +
    // ~70 UC/Quark/HeyTap/Vivo/MQQ hits/day (nginx 2026-07-21); frozen-UA
    // chrome/114 mobile ≈ 567 sessions/7d. The rest of the family shares the
    // same external-download-manager architecture; for any token that is in
    // fact blob-capable the cost of this routing is one honest CONFIRMED
    // stash download (upload time) — never a silent loss, so the asymmetry
    // favours listing the family. SamsungBrowser is deliberately NOT here:
    // big cohort (~859 hits/day), blob anchor natively fine, storm-rate flat.
    if(/HuaweiBrowser\/|MiuiBrowser\/|XiaoMi\/|HeyTapBrowser\/|OppoBrowser\/|VivoBrowser\/|UCBrowser\/|UWS\/|MQQBrowser\/|Quark\//i.test(ua))return true;
    return false;
};
// ── iOS/iPadOS in-app WebView detection ──
// Every REAL iOS browser (Safari, SFSafariViewController, Chrome/CriOS,
// Firefox/FxiOS, Edge/EdgiOS, Opera, Yandex, DuckDuckGo) appends a
// "Safari/6xx" token to its UA; bare WKWebView hosts (Telegram, VK, Gmail,
// mail clients, QR scanners, …) do not. On iPadOS 13+ WKWebView defaults to
// a DESKTOP ("Macintosh") UA — also without the Safari token;
// navigator.maxTouchPoints>1 separates an iPad from a real Mac. Also catches
// iOS home-screen PWAs (bare UA too), where blob downloads historically
// black-hole — the share sheet works there as well.
// In these hosts `<a download href="blob:">` is a SILENT NO-OP (bug
// 2026-06-30, /ru/mute-video, iPhone iOS 18.7 bare-WKWebView UA: three
// file_download via:classic_native clicks, zero files delivered; telemetry
// sweep: 27 sessions / 38 dead clicks in 14 d — 100% failure in the cohort),
// and the stash's Content-Disposition navigation is only honored where the
// host app implements WKDownloadDelegate (rare). The reliable save path is
// the native share sheet — see the iOS branch in __downloadFile below.
window.__iosInAppBrowser=function(){
    var ua=navigator.userAgent||'';
    var isIosDevice=/iPhone|iPad|iPod/.test(ua)||(/Macintosh/.test(ua)&&navigator.maxTouchPoints>1);
    return isIosDevice&&!/Safari\//i.test(ua);
};
// ── iOS: where a file is saved through the SHARE SHEET, not a blob anchor ──
// `Safari/` in the UA says «a real browser», and for most of them the blob
// anchor downloads. Not for all. Measured 2026-09-27 (RU, 30 d, the same file
// downloaded again in one session = the download did not land): Yandex Browser
// for iOS 26.x — 9.5 % of files, Safari — 14 %, Chrome iOS — 12 %; YaBrowser 25
// — 22.9 %, 24 — 37.5 %, 23 — 50 %, 22 — 67 %, on every iOS version. And the
// failure is not a quiet no-op: in the report that found it (#8048, iPhone iOS
// 17.5.1, YaBrowser 25.4, Premium bought mid-way) the anchor NAVIGATED the tab
// to the blob URL — pagehide fired the same second as the click, the browser
// showed «адрес недействителен или изменён», and the page with the finished
// 36 MB video was gone. Nine exports, nine times.
// So three classes save through the share sheet («Сохранить видео» / «В
// Файлы»), which WebKit implements itself for every WKWebView host:
//   • in-app WebViews (no Safari token) — see __iosInAppBrowser above;
//   • Yandex Browser for iOS up to major 25 — the measured boundary;
//   • any iOS browser on THIS device whose blob anchor has already been seen
//     taking the page away (learned, see the anchor watch below) — the browsers
//     nobody measured yet cannot hide behind a token.
window.__DL_ANCHOR_NAV_KEY='tmb_dl_anchor_nav';
window.__iosDevice=function(){
    var ua=navigator.userAgent||'';
    return /iPhone|iPad|iPod/.test(ua)||(/Macintosh/.test(ua)&&navigator.maxTouchPoints>1);
};
window.__iosSavesViaShareSheet=function(){
    if(!window.__iosDevice())return false;
    if(window.__iosInAppBrowser())return true;
    var ua=navigator.userAgent||'';
    var ya=/YaBrowser\/(\d+)\./.exec(ua);
    if(ya&&+ya[1]<=25)return true;
    try{
        var rec=JSON.parse(localStorage.getItem(window.__DL_ANCHOR_NAV_KEY)||'null');
        // 90 days: a browser update may have fixed it, and the share sheet is
        // not a worse way to save — so forgetting early buys nothing.
        if(rec&&rec.at&&(Date.now()-rec.at)<90*864e5)return true;
    }catch(e){}
    return false;
};
// ── iOS: видео уходит туда, где человек держит видео, — в «Фото» ──────────
// Обращение #9435 (14.10.2026, /ru/add-music-to-video, iPhone iOS 16.7 Safari,
// купила Премиум посреди работы): «Одно видео скачалось моментально, другое
// стоит в ожидании вот уже 10 минут». Ролик 55 МБ ушёл, ролик 114 МБ — четыре
// нажатия «Скачать», потом ещё три экспорта того же видео и по два скачивания.
// Якорь `<a download>` в Safari кладёт файл в «Файлы» → «Загрузки», а эта
// папка по умолчанию лежит в iCloud Drive: на мобильной сети или в полном
// iCloud файл стоит там «Ожидание», а в «Фото», откуда человек брал ролик
// (IMG_5603.mov) и куда его понесёт Instagram/VK/Telegram, его нет вовсе.
// Замер, RU, «тот же файл скачан ещё раз в сессии», якорь (через лист этот
// счёт несравним: там повтор — ещё и «отправить в мессенджер»):
//   видео   — Windows 5.1 %, Android 5.3–8.3 %, Яндекс iOS 9.3 %, Safari iOS 15.5 %;
//   снимки  — Android 12.5–13.8 %, Яндекс iOS 13.4 %, Safari iOS 20.8 %;
//   звук и PDF у Safari iOS почти как у всех (12.9 / 12.4 %) — их «Фото» не берёт.
// До «Фото» из браузера ведёт только меню «Поделиться» («Сохранить видео» /
// «Сохранить изображение»), и в нём же есть «Сохранить в Файлы» — оно ничего
// не отнимает.
// Потолок — самая большая доставка листом, измеренная на iOS (319 МБ, 20 дн.):
// WebKit копирует файл в память ради листа, а выше замера нет; дальше — якорь.
window.__IOS_PHOTOS_SHARE_MAX=320*1048576;
window.__iosPhotosKind=function(name,type,size){
    if(!window.__iosDevice())return '';
    if(size>window.__IOS_PHOTOS_SHARE_MAX)return '';
    var m=/\.([A-Za-z0-9]{1,5})$/.exec(String(name||''));
    var ext=m?m[1].toLowerCase():'';
    // Расширение решает первым: «.m4a» с типом audio/mp4 — не видео. Только то,
    // что «Фото» сохраняет: webm/mkv, webp/svg/bmp оно не возьмёт.
    if(ext)return /^(mp4|mov|m4v)$/.test(ext)?'video':(/^(jpe?g|png|heic|heif|gif)$/.test(ext)?'image':'');
    var t=String(type||'');
    return /^video\/(mp4|quicktime|x-m4v)\b/i.test(t)?'video':(/^image\/(jpeg|png|heic|heif|gif)\b/i.test(t)?'image':'');
};
// ── Where THIS browser puts a download ──────────────────────────────────
// Huawei Browser does not save into Download on Android 10+: its download
// manager files a download by its TYPE through MediaStore — audio →
// Music/Browser, picture → Pictures/Browser, video → Movies/Browser, the rest
// → Download/Browser; on Android 9 and older, plain Download (HuaweiBrowser
// 17.0.6.313, read from its code: DownloadRequestUtils.getRelativePathWithMimeType,
// DownloadUtils.buildStorageType). Found 18.10.2026 behind support chat 695:
// «Куда скачиваются файлы?» — our hint had sent a Huawei owner to Download.
// The type is the one the relay serves (DownloadStashController::servedMimeFor:
// the page's type, else the extension's; SVG never as a picture). null = this
// browser keeps the common answer.
window.__downloadPlace=function(type,name,ua){
    ua=ua||navigator.userAgent||'';
    if(!/HuaweiBrowser\//i.test(ua))return null;
    var av=/Android (\d+)/i.exec(ua);
    if(av&&+av[1]<10)return {browser:'huawei',dir:'Download',path:'Download'};
    var t=String(type||'').toLowerCase().split(';')[0].trim();
    if(!t||t==='application/octet-stream'){
        var ext=(/\.([a-z0-9]{1,5})$/i.exec(String(name||''))||[])[1];
        ext=ext?ext.toLowerCase():'';
        t=/^(mp3|wav|flac|m4a|aac|ogg|oga|opus|wma|aif|aiff|amr|mid|midi)$/.test(ext)?'audio/x':
          /^(jpe?g|png|gif|webp|bmp|heic|heif|avif|tiff?)$/.test(ext)?'image/x':
          /^(mp4|m4v|mov|webm|mkv|avi|3gp|wmv|flv)$/.test(ext)?'video/x':'';
    }
    var dir=t.indexOf('svg')!==-1?'Download':/^audio\//.test(t)?'Music':/^image\//.test(t)?'Pictures':/^video\//.test(t)?'Movies':'Download';
    return {browser:'huawei',dir:dir+'/Browser',path:dir+' → Browser'};
};
// The relay's own delivery check (/api/download/{token}/taken): browsers where
// the page's navigation is NOT the download — the browser cancels it at the
// headers and its download manager requests the URL again. Returns the kind
// ('huawei' | 'webview') or '' (the navigation IS the download, or unknown).
//   huawei  — HuaweiBrowser 17 code (DownloadStartInfo without a file → enqueue);
//             nginx 14 d: 78 % of 395 tokens got the second request, 19 % never;
//   webview — an Android System WebView host («; wv)»): the platform hands the
//             host only a URL (DownloadListener.onDownloadStart), so its download
//             is always a second request — measured on an emulator host
//             (AndroidDownloadManager GET after «Загрузить», 18.10.2026); nginx
//             14 d: 78 % of 314 tokens got it, 21 % never.
// ⛔ Not the Yandex app (YaApp/YaSearchBrowser/webOmni): its own engine downloads
// the navigation itself — one request is its normal delivery (72 % of 14 595).
window.__relayTakeCheck=function(ua){
    ua=ua||navigator.userAgent||'';
    if(/HuaweiBrowser\//i.test(ua))return 'huawei';
    if(/;\s*wv[\);]/i.test(ua)&&!/YaApp|YaSearch|webOmni/i.test(ua))return 'webview';
    return '';
};
// "Where did my file go?" hint — comment #893 (Яна, 2026-07-01, iPhone):
// a blob download is instant (the bytes are already local) and the browser
// shows almost nothing (Safari iOS: a brief ↓ blink), so users conclude
// nothing was downloaded. Browsers never expose the actual save path to JS;
// the honest best is a platform-aware "here is where to look" toast after a
// successful download. Rate-limited so batch exports don't stack 20 toasts.
window.__downloadWhereHintAt=0;
window.__downloadWhereHint=function(size,type,name){
    try{
        if(!window.__toast)return;
        var now=Date.now();
        if(now-window.__downloadWhereHintAt<15000)return;
        window.__downloadWhereHintAt=now;
        var ua=navigator.userAgent||'';
        var L=(window.__LOCALE||'en').substring(0,2);
        var _place=window.__downloadPlace?window.__downloadPlace(type,name,ua):null;
        // ── «Файл сохранён» must not be said onto a full disk ──────────────
        // The anchor click is the last thing a page sees: the browser's download
        // manager then writes the file, and when the disk is full it fails
        // inside the browser's own UI, invisibly to us — while this toast has
        // already announced success. That is the exact wording alula_23 quoted
        // (04.09, /screen-recorder, 2 h 17 m on the page, 11 quota errors on
        // the same day): «пишет, что сохранена на вашем устройстве», no file.
        // Chromium reports the room it has left through storage.estimate():
        // quota − usage equals the disk's available space (minus a reserve),
        // capped at ~10 GiB for privacy — measured 2026-09-05: 10.74 GB reported
        // with 100 GB free, and the same 10.74 GB again with 2.7 GB more in
        // use, so BELOW the cap the number IS the disk. Above it we know
        // nothing and stay quiet. Firefox's quota is a per-origin share of the
        // disk, not the disk, so this is Chromium-only (`window.chrome`, which
        // Edge, Yandex, Opera and Brave share). The hint is asynchronous now;
        // if estimate() takes longer than a blink, the plain hint is shown.
        var _plainHint=function(){ try{ _whereHintToast(ua,L,_place); }catch(e){} };
        if(size>0&&window.chrome&&/Chrome\//.test(ua)&&navigator.storage&&typeof navigator.storage.estimate==='function'){
            var _settled=false;
            var _fallback=setTimeout(function(){ if(_settled)return; _settled=true; _plainHint(); },1500);
            window.__storageEstimate().then(function(e){
                if(_settled)return; _settled=true; clearTimeout(_fallback);
                var free=Math.max(0,((e&&e.quota)||0)-((e&&e.usage)||0));
                var CAP=8*1073741824;
                if(free<CAP&&size>free){
                    var mbF=Math.round(free/1048576),mbS=Math.round(size/1048576);
                    var W={ru:'На диске осталось около '+mbF+' МБ, а файл занимает '+mbS+' МБ — браузер может не сохранить его. Освободите место и нажмите «Скачать» ещё раз.',
                           en:'About '+mbF+' MB is left on the disk and this file is '+mbS+' MB — the browser may fail to save it. Free up space and click Download again.',
                           de:'Auf der Festplatte sind noch etwa '+mbF+' MB frei, die Datei ist '+mbS+' MB groß — der Browser kann sie möglicherweise nicht speichern. Schaffen Sie Platz und klicken Sie erneut auf „Herunterladen“.',
                           es:'Quedan unos '+mbF+' MB en el disco y este archivo ocupa '+mbS+' MB: puede que el navegador no consiga guardarlo. Libera espacio y vuelve a pulsar «Descargar».',
                           fr:'Il reste environ '+mbF+' Mo sur le disque et ce fichier fait '+mbS+' Mo — le navigateur risque de ne pas pouvoir l’enregistrer. Libérez de l’espace et cliquez de nouveau sur « Télécharger ».',
                           pt:'Restam cerca de '+mbF+' MB no disco e este arquivo tem '+mbS+' MB — o navegador pode não conseguir salvá-lo. Libere espaço e clique em «Baixar» novamente.',
                           id:'Ruang disk tersisa sekitar '+mbF+' MB, sedangkan file ini '+mbS+' MB — peramban mungkin gagal menyimpannya. Kosongkan ruang lalu klik «Unduh» lagi.',
                           ko:'디스크 여유 공간이 약 '+mbF+'MB인데 이 파일은 '+mbS+'MB입니다 — 브라우저가 저장하지 못할 수 있습니다. 공간을 확보한 뒤 «다운로드»를 다시 누르세요.',
                           ar:'المتبقي على القرص نحو '+mbF+' م.ب وحجم هذا الملف '+mbS+' م.ب — قد لا يتمكن المتصفح من حفظه. حرّر بعض المساحة ثم اضغط «تنزيل» مرة أخرى.',
                           uk:'На диску залишилося близько '+mbF+' МБ, а файл займає '+mbS+' МБ — браузер може не зберегти його. Звільніть місце й натисніть «Завантажити» ще раз.',
                           tr:'Diskte yaklaşık '+mbF+' MB kaldı, bu dosya ise '+mbS+' MB — tarayıcı kaydedemeyebilir. Yer açıp «İndir»e yeniden tıklayın.'};
                    window.__toast(W[L]||W.en,'warning');
                    try{if(window.__ae)window.__ae('feature_use',{f:'file_download_disk_short',
                        slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),freeMB:mbF,sizeMB:mbS});}catch(_){}
                    return;
                }
                _plainHint();
            },function(){ if(_settled)return; _settled=true; clearTimeout(_fallback); _plainHint(); });
            return;
        }
        _plainHint();
    }catch(e){}
};
function _whereHintToast(ua,L,place){
    try{
        // iPadOS 13+ masquerades as Macintosh — the touch check catches it.
        var isIOS=/iPad|iPhone|iPod/.test(ua)||(/Macintosh/.test(ua)&&navigator.maxTouchPoints>1);
        var isAndroid=/Android/i.test(ua);
        var M;
        if(place&&place.path){
            // A browser whose folder is known exactly (window.__downloadPlace).
            // Kept on screen: it is the answer to «where is it», not a status.
            var w=place.path;
            M={ru:'Файл сохранён: «Файлы» → '+w+'.',
               en:'File saved: Files → '+w+'.',
               de:'Datei gespeichert: Dateien → '+w+'.',
               es:'Archivo guardado: Archivos → '+w+'.',
               fr:'Fichier enregistré : Fichiers → '+w+'.',
               pt:'Arquivo salvo: Arquivos → '+w+'.',
               id:'File tersimpan: File → '+w+'.',
               ko:'파일 저장됨: 파일 → '+w+'.',
               ar:'تم حفظ الملف: الملفات ← '+w+'.',
               uk:'Файл збережено: «Файли» → '+w+'.',
               tr:'Dosya kaydedildi: Dosyalar → '+w+'.'};
            window.__toast(M[L]||M.en,'success',{keep:true});
            return;
        }
        if(isIOS){
            M={ru:'Файл сохранён: приложение «Файлы» → «Загрузки» (в Safari — значок ↓ вверху).',
               en:'File saved: Files app → Downloads (in Safari — the ↓ icon at the top).',
               de:'Datei gespeichert: App „Dateien“ → „Downloads“ (in Safari: ↓-Symbol oben).',
               es:'Archivo guardado: app Archivos → Descargas (en Safari, el icono ↓ arriba).',
               fr:'Fichier enregistré : app Fichiers → Téléchargements (dans Safari : icône ↓ en haut).', uk: 'Файл збережено: застосунок «Файли» → «Завантаження» (у Safari — значок ↓ вгорі).', tr: 'Dosya kaydedildi: Dosyalar uygulaması → İndirilenler (Safari’de — üstteki ↓ simgesi).',
               ko:'파일 저장됨: 파일 앱 → 다운로드 (Safari에서는 상단 ↓ 아이콘).',
               ar:'تم حفظ الملف: تطبيق «الملفات» ← «التنزيلات» (في Safari: أيقونة ↓ في الأعلى).',
               id:'File tersimpan: aplikasi Files → Unduhan (di Safari: ikon ↓ di atas).',
               pt:'Arquivo salvo: app Arquivos → Downloads (no Safari: ícone ↓ no topo).'};
        }else if(isAndroid){
            M={ru:'Файл сохранён в папку Download — смотрите уведомления или приложение «Файлы».',
               en:'File saved to the Download folder — check your notifications or the Files app.',
               de:'Datei im Download-Ordner gespeichert — siehe Benachrichtigungen oder Dateien-App.',
               es:'Archivo guardado en la carpeta Download: mira las notificaciones o la app Archivos.',
               fr:'Fichier enregistré dans le dossier Download — voir les notifications ou l’app Fichiers.', uk: 'Файл збережено в папку Download — перевірте сповіщення або застосунок «Файли».', tr: 'Dosya İndirilenler klasörüne kaydedildi — bildirimlerinizi veya Dosyalar uygulamasını kontrol edin.',
               ko:'파일이 Download 폴더에 저장됨 — 알림 또는 파일 앱을 확인하세요.',
               ar:'تم حفظ الملف في مجلد Download — راجع الإشعارات أو تطبيق الملفات.',
               id:'File tersimpan di folder Download — cek notifikasi atau aplikasi File.',
               pt:'Arquivo salvo na pasta Download — veja as notificações ou o app Arquivos.'};
        }else{
            // Desktop. Ctrl+J opens the downloads list in Chromium/Firefox on
            // Windows/Linux; Mac shortcuts differ per browser — omit there.
            var j=/Macintosh|Mac OS X/.test(ua)?'':' (Ctrl+J)';
            M={ru:'Файл сохранён в папку «Загрузки» браузера'+j+'.',
               en:'File saved to your browser’s Downloads folder'+j+'.',
               de:'Datei im Downloads-Ordner des Browsers gespeichert'+j+'.',
               es:'Archivo guardado en la carpeta Descargas del navegador'+j+'.',
               fr:'Fichier enregistré dans le dossier Téléchargements du navigateur', uk: 'Файл збережено в папку «Завантаження» браузера'+j+'.', tr: 'Dosya tarayıcınızın İndirilenler klasörüne kaydedildi'+j+'.',
               ko:'파일이 브라우저의 다운로드 폴더에 저장됨'+j+'.',
               ar:'تم حفظ الملف في مجلد التنزيلات بالمتصفح'+j+'.',
               id:'File tersimpan di folder Unduhan browser'+j+'.',
               pt:'Arquivo salvo na pasta Downloads do navegador'+j+'.'};
        }
        window.__toast(M[L]||M.en,'success');
    }catch(e){}
};
/* -- «Готово - ваш файл вот здесь» ----------------------------------------
   Зеркало tu-fold-cue (public/js/tool-uploader.js) на ВЫХОДЕ: тот же класс
   задачи и тот же разрешённый механизм, только указывает не на зону загрузки,
   а на кнопку выдачи результата.

   Зачем. Отчёт #5837 (26.08, /ru/music-mastering, TECNO/YaBrowser lite,
   360x414): «Где этот файл после мастеринга должен появиться». Человек НЕ
   нажимал скачивание ни разу - последним его действием осталось появление
   результата (всегда-включённое зеркало __LAST_TOOL_ACTION работает и без
   согласия на телеметрию, поэтому это факт, а не догадка). Замер на его же
   экране: в момент готовности кнопка скачивания лежала на 1134 px ниже края
   экрана - 2.7 экрана прокрутки, - а на видимой части не было ни слова про
   файл. За 30 дней 487 сессий /music-mastering дошли до готового мастера и
   не сделали дальше НИЧЕГО.

   Почему указатель, а не автоскачивание: формат выдачи люди выбирают руками
   (замер 30 дней: MP3 37%, WAV16 36%, WAV24 23%, FLAC 3%), то есть «скачать
   само» промахнулось бы мимо выбора у двух третей, а в Android-WebView оно
   означало бы молчаливую отправку десятков мегабайт в наш stash и уход со
   страницы. Автопрокрутка запрещена глобальным правилом; разрешённая форма -
   scrollIntoView({block:'nearest'}) ПО ТАПУ, ровно как у зоны загрузки.

   Ярлык берётся с самой кнопки: «Скачать WAV» - подпись указателя обязана
   совпадать с подписью органа, к которому он ведёт, иначе на 9 локалях они
   разъедутся молча.

   Взводится сигналом `tmb:tool-complete` (его шлёт всегда-включённое зеркало
   в telemetry-tier0.js - единственная точка, через которую проходят ВСЕ
   объявления о завершении), показывается ТОЛЬКО на грубом указателе (палец) и
   ТОЛЬКО пока цель вне экрана; уходит, как только цель показалась, по тапу,
   по клику на саму кнопку и после доставки файла. */
window.__resultCue=(function(){
    var SELECTORS=['[data-result-cue]','[data-tmb-api="download"]','[data-tmb-api^="download-"]'];
    var MAX_SHOWS=3;
    var cue=null,target=null,io=null,bannerMo=null,shown=false,shows=0,outOfView=false,dir='down';
    function coarse(){try{return !!(window.matchMedia&&window.matchMedia('(pointer:coarse)').matches);}catch(e){return false;}}
    function reduced(){try{return !!(window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches);}catch(e){return false;}}
    function visible(el){
        if(!el||el.hidden||el.disabled)return false;
        try{
            var cs=getComputedStyle(el);
            if(cs.display==='none'||cs.visibility==='hidden')return false;
            if(!el.offsetParent&&cs.position!=='fixed')return false;
            var r=el.getBoundingClientRect();
            return r.width>0&&r.height>0;
        }catch(e){return false;}
    }
    function pick(explicit){
        if(explicit&&visible(explicit))return explicit;
        for(var i=0;i<SELECTORS.length;i++){
            var list;try{list=document.querySelectorAll(SELECTORS[i]);}catch(e){continue;}
            for(var j=0;j<list.length;j++)if(visible(list[j]))return list[j];
        }
        return null;
    }
    /* Согласие по 152-ФЗ/GDPR закреплено внизу экрана и по закону главнее:
       два стакнутых нижних бара - неправильно, поэтому ждём его ухода. */
    function consentUp(){
        var cc=document.querySelector('.cc-banner.cc-visible');
        if(!cc)return false;
        try{return getComputedStyle(cc).display!=='none';}catch(e){return true;}
    }
    function labelOf(el){
        var t=(el.getAttribute('aria-label')||el.textContent||'').replace(/\s+/g,' ').trim();
        return t?t.slice(0,42):'';
    }
    function teardown(){
        try{if(io)io.disconnect();}catch(e){}
        try{if(bannerMo)bannerMo.disconnect();}catch(e){}
        io=null;bannerMo=null;shown=false;outOfView=false;
        if(target){try{target.removeEventListener('click',teardown);}catch(e){}}
        target=null;
        if(cue&&cue.parentNode){try{cue.parentNode.removeChild(cue);}catch(e){}}
        cue=null;
    }
    function build(text){
        cue=document.createElement('button');
        cue.type='button';
        cue.className='tu-fold-cue tmb-result-cue';
        cue.setAttribute('aria-label',text);
        var arrow=document.createElement('span');
        arrow.className='tu-fold-cue-arrow';
        arrow.setAttribute('aria-hidden','true');
        arrow.textContent=dir==='up'?'↑':'↓';
        var txt=document.createElement('span');
        txt.textContent=text;
        cue.appendChild(arrow);cue.appendChild(txt);
        cue.addEventListener('click',function(){
            var el=target;
            try{if(window.__ae)window.__ae('feature_use',{f:'result_cue',act:'tap'});}catch(e){}
            if(!el){teardown();return;}
            try{el.scrollIntoView({block:'nearest',behavior:reduced()?'auto':'smooth'});}catch(e){try{el.scrollIntoView();}catch(e2){}}
            if(!reduced()){
                try{
                    el.classList.remove('tmb-cue-pulse');void el.offsetWidth;el.classList.add('tmb-cue-pulse');
                    setTimeout(function(){try{el.classList.remove('tmb-cue-pulse');}catch(e){}},2400);
                }catch(e){}
            }
            /* Наблюдатель снимет указатель, когда кнопка действительно доедет. */
        });
        document.body.appendChild(cue);
    }
    function reveal(){
        if(!outOfView||!target)return;
        if(consentUp()){
            if(cue)cue.classList.remove('show');
            if(!bannerMo&&window.MutationObserver){
                var cc=document.querySelector('.cc-banner');
                if(cc){bannerMo=new MutationObserver(reveal);bannerMo.observe(cc,{attributes:true,attributeFilter:['class','style']});}
            }
            return;
        }
        if(!cue)build(labelOf(target));
        if(!shown){
            shown=true;shows++;
            requestAnimationFrame(function(){if(cue)cue.classList.add('show');});
            try{if(window.__ae)window.__ae('feature_use',{f:'result_cue',act:'shown',
                below:Math.round(target.getBoundingClientRect().top-window.innerHeight),dir:dir});}catch(e){}
        }else if(cue)cue.classList.add('show');
    }
    /* ТЕНЬ. Указатель показывается только там, где тул ОБЪЯВИЛ свою кнопку
       выдачи (data-tmb-api="download" / data-result-cue) — сейчас это 7 тулов.
       Раскатывать его вслепую на ~700 нельзя: широкий поиск «по слову» иногда
       найдёт не тот орган. Но и гадать, где ещё болит, не надо: здесь тот же
       широкий поиск гоняется ВХОЛОСТУЮ и только пишет замер — на сколько
       пикселей ниже экрана оказалась выдача в момент готовности. Через неделю
       список тулов на разметку будет ЗАМЕРОМ, а не догадкой (тот же приём, что
       у ведра `unlisted` серверного лейна). Ничего не рисует, ровно один раз на
       страницу. */
    /* Без «загрузить»: в русском это и upload, и download, а на сайте — только
       upload. Замер 7 дней до 21.10: 17 334 замера тени на 113 тулах мерили
       корень общего загрузчика («Загрузить файл», role=button), ещё ~3 400 —
       «Загрузить по ссылке / новое видео / фото…»; ни одной кнопки выдачи
       с этим словом. 16 % тени было зоной загрузки над экраном. */
    var VERBS=/скачать|сохранит|экспорт|download|save|export|herunterladen|speicher|descargar|guardar|exportar|t[ée]l[ée]charger|enregistr|baixar|salvar|unduh|simpan|ekspor|다운로드|저장|내보내기|تنزيل|حفظ|تصدير/i;
    var measured=false;
    /* Слово ищется в ВИДИМОМ тексте. `textContent` читает и то, что спрятано
       `display:none`: у /audio-enhancer карточка режима «Улучшить старую запись»
       носит скрытое описание «…сохранить характер», и замер месяц мерил её, а не
       кнопку «Скачать» (замер 25.09.2026: txt «Улучшить старую запись К» во всех
       событиях тула, below отрицательный — карточка выше экрана, выдача «видна»).
       `innerText` скрытое не отдаёт; там, где его нет, остаётся прежнее чтение. */
    function shownText(el){
        var t=el.getAttribute('aria-label');
        if(!t)t=('innerText' in el)?el.innerText:el.textContent;
        return (t||'').replace(/\s+/g,' ').trim();
    }
    function heuristicPick(){
        try{
            var root=document.querySelector('main')||document.body;
            var list=root.querySelectorAll('button,a[download],a[href^="blob:"],[role="button"]');
            for(var i=0;i<list.length;i++){
                var el=list[i];
                /* Слово — ДО видимости (04.11.2026). visible() и innerText — это
                   принудительный пересчёт стиля и раскладки, а перебор шёл по
                   КАЖДОЙ кнопке страницы: /speaker-cleaner (ни одной кнопки
                   выдачи) — 268 мс главного потока на телефоне ×6 сразу после
                   нажатия «Старт». aria-label/textContent читаются без
                   раскладки, и всё, что видно в innerText, есть и в textContent —
                   пропустить настоящую кнопку этот отсев не может. */
                var raw=el.getAttribute('aria-label')||el.textContent||'';
                if(!VERBS.test(raw))continue;
                if(el.closest&&el.closest('.cc-banner,.tmb-result-cue,#feedbackWidget,[data-ad-slot],header,footer,nav,.tool-uploader'))continue;
                if(!visible(el))continue;
                var txt=shownText(el);
                if(!txt||!VERBS.test(txt))continue;
                return el;
            }
        }catch(e){}
        return null;
    }
    /* Один замер на страницу: на сколько пикселей ниже экрана оказалась выдача
       в момент готовности. Пишется и на мышке, и на пальце, и для объявленной
       кнопки, и для найденной вслепую (`heur:1`) — именно из этого через неделю
       получится СПИСОК тулов, где выдачу не видно, вместо догадок. */
    function measureOnce(el,heur){
        if(measured)return;
        measured=true;
        try{
            var r=el.getBoundingClientRect();
            if(window.__ae)window.__ae('feature_use',{f:'result_cue',act:'measure',
                below:Math.round(r.top-window.innerHeight),vh:window.innerHeight,
                heur:heur?1:0,coarse:coarse()?1:0,
                txt:shownText(el).slice(0,24)});
        }catch(e){}
    }
    function arm(el){
        if(!('IntersectionObserver' in window))return;
        var t=pick(el),heur=false;
        if(!t){t=heuristicPick();heur=true;}
        if(!t)return;
        measureOnce(t,heur);
        /* Рисуем ТОЛЬКО по объявленной туловой кнопке и только на пальце:
           слепой поиск «по слову» имеет право ошибиться в замере, но не имеет
           права привести человека не к тому органу. */
        if(heur||!coarse()||shows>=MAX_SHOWS)return;
        if(cue&&target===t)return;          /* уже показываем на эту же кнопку */
        teardown();
        target=t;
        var text=labelOf(t);
        if(!text)return;                    /* без честной подписи - без указателя */
        try{t.addEventListener('click',teardown);}catch(e){}
        io=new IntersectionObserver(function(entries){
            var e=entries[0];if(!e)return;
            if(e.isIntersecting){teardown();return;}
            var r=e.boundingClientRect;
            dir=r.top<0?'up':'down';
            if(cue){
                var a=cue.querySelector('.tu-fold-cue-arrow');
                if(a)a.textContent=dir==='up'?'↑':'↓';
            }
            outOfView=true;
            reveal();
        },{threshold:0.01});
        try{io.observe(t);}catch(e){teardown();}
    }
    window.addEventListener('file-downloaded',function(){teardown();});
    window.addEventListener('pagehide',function(){teardown();});
    document.addEventListener('tmb:tool-complete',function(ev){
        /* Результат, объявленный САМОЙ дверью скачивания (src:'download') или
           копирования (src:'copy'), приходит уже после выдачи: человек результат
           забрал, указывать на «Скачать» поздно, а замер «где
           выдача в момент готовности» мерил бы кнопку, по которой только что
           нажали. */
        if(ev&&ev.detail&&(ev.detail.src==='download'||ev.detail.src==='copy'))return;
        /* Разметка результата раскладывается в тот же тик - даём кадру осесть,
           иначе наблюдатель померит ещё не разложенную кнопку. */
        var el=ev&&ev.detail&&ev.detail.__cueEl;
        /* …и не отнимаем у человека кадр: объявление результата чаще всего
           звучит прямо в обработчике нажатия, а на слабом телефоне кадр этого
           нажатия к 300 мс ещё не показан — голый таймер вставал в то же
           касание или в следующее. Замер и указатель ждут простоя главного
           потока (не дольше 2 с). */
        setTimeout(function(){
            var go=function(){try{arm(el);}catch(e){}};
            if(window.requestIdleCallback){try{requestIdleCallback(go,{timeout:2000});return;}catch(e){}}
            go();
        },300);
    });
    return {arm:arm,dismiss:teardown};
})();
/* ── Pro-purchase attribution: which lock/upsell led to the checkout. ──
   Lock CTAs call set('slug-reason'); the pro checkout/done pages read it into
   the funnel events (pro_checkout_view / pro_start_ok / pro_activated) and
   clear it after activation. TTL 7d: a stale source must not claim credit. */
window.__proSrc={
    set:function(src){try{localStorage.setItem('tmb_pro_src',JSON.stringify({s:String(src).slice(0,48),t:Date.now()}));}catch(e){}},
    get:function(){try{var v=JSON.parse(localStorage.getItem('tmb_pro_src')||'null');if(v&&v.s&&(Date.now()-v.t)<6048e5)return v.s;}catch(e){}return null;},
    clear:function(){try{localStorage.removeItem('tmb_pro_src');}catch(e){}},
    /* Age of the stored source (Infinity when absent) — lets the auto-capture
       below yield to a FRESH precise source instead of overwriting it. */
    ageMs:function(){try{var v=JSON.parse(localStorage.getItem('tmb_pro_src')||'null');if(v&&v.s)return Date.now()-v.t;}catch(e){}return Infinity;},
    /* Last-resort attribution so a purchase is NEVER recorded as "nothing".
       Order: whatever is already stored → same-origin referrer path → 'direct'.
       Called on the checkout pages (an explicit ?src= is captured on load below). */
    ensure:function(){
        try{
            var cur=this.get();
            if(cur)return cur;
            var v=null;
            try{
                var r=document.referrer||'';
                if(r&&r.indexOf(location.origin)===0){
                    v='ref:'+new URL(r).pathname.replace(/^\/[a-z]{2}(\/|$)/,'/');
                }
            }catch(e){}
            this.set(v||'direct');
            return this.get();
        }catch(e){return null;}
    }
};
/* ── Auto-attribution: where did this purchase actually come from? ──
   Instrumenting every CTA by hand left 80 of 89 purchases (30 d, measured
   2026-07-18) with src=null — i.e. we could not see what sells Премиум, only
   that tool-limit walls accounted for 9. Two capture points fix that without
   touching a dozen blades:
     1) an explicit ?src= on ANY page (email / newsletter / partner links land
        wherever they land, not necessarily on the checkout);
     2) a capture-phase click listener that records the ORIGIN of any click
        heading into /pro or /pricing — an explicit data-pro-src wins, else the
        page the user clicked from.
   The click capture YIELDS to a source stored in the last 30 min, so a precise
   upsell tag (e.g. 'join-limit', set by tool-usage-limit.js right before the
   wall) is never clobbered by the click that immediately follows it. */
try{
    var _tmbQsSrc=null;
    try{_tmbQsSrc=new URLSearchParams(location.search).get('src');}catch(e){}
    if(_tmbQsSrc)window.__proSrc.set('q:'+_tmbQsSrc);
    document.addEventListener('click',function(ev){
        try{
            var a=ev.target&&ev.target.closest?ev.target.closest('a[href]'):null;
            if(!a)return;
            var p='';try{p=a.pathname||'';}catch(e){}
            // Only links INTO the purchase flow: /pro, /pro/checkout, /{loc}/pricing…
            if(!/(^|\/)(pro|pricing)(\/|$)/.test(p))return;
            if(window.__proSrc.ageMs()<18e5)return; // a fresh precise source wins
            var explicit=a.getAttribute('data-pro-src');
            window.__proSrc.set(explicit||('page:'+location.pathname.replace(/^\/[a-z]{2}(\/|$)/,'/')));
        }catch(e){}
    },true);
}catch(e){}
/* ── Local download channel via Service Worker (no server round-trip) ──
   Hands the Blob to the SW (structured clone = handle, no byte copy), then
   points a hidden iframe at /__tmb-dl/<id>. The SW answers with the blob plus
   `Content-Disposition: attachment` — the one response an Android WebView /
   in-app browser always routes to its download manager.

   Why an iframe and not a top-level <a>: if the SW was killed and the entry is
   gone it answers 410, and a 410 in a hidden iframe is invisible; a top-level
   navigation would yank the user off the page.

   Resolves TRUE only when the SW reports it actually served the response
   (`tmb-download-served`) — a real delivery signal, not a guess. Any other
   outcome (no controller, no ACK, 410, timeout) resolves FALSE so the caller
   can fall through to the next channel. */
window.__swDlUnsupported=false;   // sticky: an old SW (no /__tmb-dl route) controls this page
// Fingerprint of the #1010 bug class: the SW reports a REPEAT request for the
// same /__tmb-dl/<id> (download-manager retry, Yandex Protect AV scan,
// pause/resume). Pre-fix those got a 410 → «ошибка скачивания»; post-fix they
// are served — this counter proves in prod telemetry how often the class fires.
try{
    if(navigator.serviceWorker&&!window.__swDlRepeatHooked){
        window.__swDlRepeatHooked=true;
        navigator.serviceWorker.addEventListener('message',function(ev){
            var d=ev&&ev.data;
            if(!d||d.type!=='tmb-download-repeat')return;
            try{if(window.__ae)window.__ae('feature_use',{slug:(window.__TOOL_SLUG||''),f:'sw_dl_repeat_fetch'});}catch(e){}
        });
    }
}catch(e){}
window.__swDownload=function(blob,filename){
    var sw;
    try{ sw=navigator.serviceWorker; }catch(e){ return Promise.resolve(false); }
    if(window.__swDlUnsupported||!sw||!sw.controller||!blob||!window.MessageChannel){return Promise.resolve(false);}
    var id;
    try{ id=(window.crypto&&crypto.randomUUID)?crypto.randomUUID():(Date.now()+'-'+Math.random().toString(16).slice(2)); }
    catch(e){ id=Date.now()+'-'+Math.random().toString(16).slice(2); }
    return new Promise(function(resolve){
        var settled=false,ackTimer=0,serveTimer=0;
        var finish=function(ok){
            if(settled)return; settled=true;
            clearTimeout(ackTimer); clearTimeout(serveTimer);
            try{ sw.removeEventListener('message',onServed); }catch(e){}
            resolve(ok);
        };
        var onServed=function(ev){
            var d=ev&&ev.data;
            if(!d||d.type!=='tmb-download-served'||d.id!==id)return;
            finish(!!d.served);
        };
        try{ sw.addEventListener('message',onServed); }catch(e){ return finish(false); }

        var ch;
        try{ ch=new MessageChannel(); }catch(e){ return finish(false); }
        // No ACK → an older SW (deployed before this route existed) still controls
        // the page; it will keep controlling until the next navigation. Remember
        // that for the session so every later click falls straight through to the
        // stash instead of stalling here again — the reporter clicked 13 times.
        ackTimer=setTimeout(function(){ window.__swDlUnsupported=true; finish(false); },2500);
        ch.port1.onmessage=function(ev){
            clearTimeout(ackTimer);
            if(!ev.data||!ev.data.ok)return finish(false);
            // Give the SW a window to serve the navigation request.
            serveTimer=setTimeout(function(){ finish(false); },15000);
            // TOP-LEVEL navigation — the SAME delivery mechanism the stash uses
            // (plain <a> click on a Content-Disposition URL), just with a local
            // SW-synthesised response instead of a server round-trip. This was a
            // hidden IFRAME until 2026-07-17, which silently failed in a whole
            // WebView shell class: Android's DownloadListener only fires for
            // MAIN-FRAME navigations, so sub-frame downloads never started
            // (bug #1010/#1019, Олег, YaBrowser-Lite: served-ack said delivered,
            // the shade said «ошибка скачивания», while the stash — top-level —
            // had worked for him for weeks). The iframe's original reason (keep
            // a stale-SW 410 invisible) is gone: we only navigate AFTER the ACK
            // proves the entry is stashed, and the 410 itself now answers with a
            // history.back() page instead of stranding the user.
            try{
                var a=document.createElement('a');
                a.href='/__tmb-dl/'+id;
                a.rel='noopener';
                a.setAttribute('data-rawdl','1');
                document.body.appendChild(a);a.click();a.remove();
            }catch(e){ finish(false); }
        };
        try{ sw.controller.postMessage({type:'tmb-download',id:id,filename:filename,blob:blob},[ch.port2]); }
        catch(e){ finish(false); }
    });
};
// ── Export-keep: best-effort IndexedDB persistence of the last mobile-relay
// export. Measured (dl-rescue lab, 2026-07-22): a 28 MB Blob survives reload
// byte-exact (sha256), put=1ms / get=15ms — and the LAB reproduced a class
// where the stash delivery navigation itself dies on a network flap and
// replaces the whole tool page with the browser error page (comment #1056
// «после обновления страницы файл и вовсе исчез»). This net catches BOTH:
// refresh-after-failure and the delivery-navigation death. Every step is
// silent-best-effort (private mode / quota / no IDB ⇒ no-op, never a throw).
/* ── Tool-to-tool file handoff ────────────────────────────────────────────
   Carry the file the user is already working on to a neighbouring tool, so a
   cross-link costs one click instead of "download here, upload there". Same
   IndexedDB mechanics as the export rescue below (blobs survive navigation
   byte-exact; sessionStorage cannot hold audio at all).

   Single-use by design: the receiver deletes the entry as it reads, so a stale
   carry can never load into a later visit. Silent best-effort everywhere —
   private mode / quota / no IDB just means the link still navigates and the
   user drops the file themselves.

   Added 2026-07-30 to link «BPM и тональность» ↔ «Определитель жанра»: they
   answer adjacent questions about the same file, and the homepage order is
   earned by traffic (never hand-placed), so the honest way to connect two
   tools is a contextual bridge, not a pinned position. */
(function(){
    var DB='tmb-tool-handoff',STORE='files',TTL_MS=30*60*1000;
    function openDb(){return new Promise(function(res,rej){
        try{
            var rq=indexedDB.open(DB,1);
            rq.onupgradeneeded=function(){try{rq.result.createObjectStore(STORE);}catch(e){}};
            rq.onsuccess=function(){res(rq.result);};
            rq.onerror=function(){rej(rq.error);};
        }catch(e){rej(e);}
    });}
    /** Stash a File/Blob under `key` for the tool we are navigating to.
     *
     *  Two record shapes. The first attempt stores the Blob itself (zero-copy
     *  on Chromium, and every put on iPhone Safari in prod succeeded). Some
     *  WebKit builds refuse ANY Blob in IndexedDB at the request level
     *  («Error preparing Blob/File data to be stored in object store» —
     *  Playwright WebKit for Windows, measured 02.09.2026, for 1 KB as well
     *  as 15 MB), while ArrayBuffers of 200 MB commit and read back exactly;
     *  on that failure the bytes are stored as an ArrayBuffer record {buf}.
     *  Not the default: senders put all stems in parallel, and reading six
     *  files into the heap up front is the phone's memory for no gain where
     *  the real failure lives (the take side, see below). */
    function putRecord(db,key,rec){
        return new Promise(function(res){
            try{
                var tx=db.transaction(STORE,'readwrite');
                var rq=tx.objectStore(STORE).put(rec,key);
                var failed=false;
                rq.onerror=function(){failed=true;};
                tx.oncomplete=function(){res(!failed);};
                tx.onerror=function(){res(false);};
                tx.onabort=function(){res(false);};
            }catch(e){res(false);}
        });
    }
    window.__toolHandoffPut=function(key,file){
        return new Promise(function(res){
            try{
                if(!key||!file||!file.size||file.size>209715200)return res(false);
                var meta={name:file.name||'audio',type:file.type||'',at:Date.now()};
                openDb().then(function(db){
                    return putRecord(db,key,{blob:file,name:meta.name,type:meta.type,at:meta.at}).then(function(ok){
                        if(ok){db.close();return true;}
                        return readBytes(file).then(function(buf){
                            return putRecord(db,key,{buf:buf,name:meta.name,type:meta.type,at:meta.at});
                        }).then(function(ok2){db.close();return ok2;},function(){try{db.close();}catch(e){}return false;});
                    });
                }).then(res,function(){res(false);});
            }catch(e){res(false);}
        });
    };
    /** Read AND consume the carry. Resolves null when absent or stale.
     *
     *  The BYTES are read while the record still exists, and only then is the
     *  record deleted (second transaction). The first version deleted the
     *  record in the same transaction that read it and handed the raw IDB
     *  Blob to the receiver, which read it LATER with FileReader. In WebKit an
     *  IDB Blob is a handle onto a file in the database's blob directory;
     *  once the record is gone and the connection closed, that file can be
     *  unlinked before the receiver reads it, and the read fails with a bare
     *  ProgressEvent. Seen live on iPhone Safari (30.08.2026, /stem-splitter
     *  → /audio-mixer): six stems put, six taken, FOUR unreadable —
     *  «Ошибка декодирования: Бас.flac» on a stem the server had delivered.
     *  The user re-bought the split. Reading into memory here costs the
     *  receiver nothing extra: it was going to read the same bytes anyway. */
    function readBytes(blob){
        if(blob&&typeof blob.arrayBuffer==='function'){
            return blob.arrayBuffer();
        }
        return new Promise(function(res,rej){
            try{
                var fr=new FileReader();
                fr.onload=function(){res(fr.result);};
                fr.onerror=function(){rej(fr.error||new Error('handoff read failed'));};
                fr.readAsArrayBuffer(blob);
            }catch(e){rej(e);}
        });
    }
    function deleteKey(db,key){
        return new Promise(function(res){
            try{
                var tx=db.transaction(STORE,'readwrite');
                tx.objectStore(STORE).delete(key);
                tx.oncomplete=function(){res(true);};
                tx.onerror=function(){res(false);};
                tx.onabort=function(){res(false);};
            }catch(e){res(false);}
        });
    }
    // Takes are serialised: the receiver asks for all stems at once
    // (audio-mixer takes six keys in parallel), and materialising them one at
    // a time bounds the transient to one file's bytes instead of all six.
    var takeChain=Promise.resolve();
    function takeOne(key){
        return new Promise(function(res){
            try{
                openDb().then(function(db){
                    var tx=db.transaction(STORE,'readonly'),st=tx.objectStore(STORE),rq=st.get(key);
                    rq.onsuccess=function(){
                        var v=rq.result;
                        var stale=!v||(!v.blob&&!v.buf)||(Date.now()-(v.at||0))>TTL_MS;
                        if(stale){
                            deleteKey(db,key).then(function(){db.close();res(null);});
                            return;
                        }
                        var type=v.type||(v.blob&&v.blob.type)||'';
                        var bytesP=v.buf?Promise.resolve(v.buf):readBytes(v.blob);
                        bytesP.then(function(bytes){
                            var f;
                            try{f=new File([bytes],v.name||'audio',{type:type});}
                            catch(e){try{f=new Blob([bytes],{type:type});}catch(e2){f=null;}}
                            return deleteKey(db,key).then(function(){db.close();res(f);});
                        }).catch(function(err){
                            // Unreadable already (the backing file is gone):
                            // consume the record so the next visit does not trip
                            // over the same corpse, and NAME the class — until now
                            // it surfaced only as the receiver's decode error with
                            // a bare «[object ProgressEvent]».
                            try{window.__aeDiagnostic&&window.__aeDiagnostic({issue:'handoff_take_unreadable',err:String((err&&err.name)||err).slice(0,60)});}catch(e){}
                            deleteKey(db,key).then(function(){db.close();res(null);});
                        });
                    };
                    rq.onerror=function(){try{db.close();}catch(e){}res(null);};
                }).catch(function(){res(null);});
            }catch(e){res(null);}
        });
    }
    window.__toolHandoffTake=function(key){
        var p=takeChain.then(function(){return takeOne(key);});
        takeChain=p.then(function(){},function(){});
        return p;
    };
})();

(function(){
    var DB='tmb-export-keep',STORE='exports',KEY='last',TTL_MS=24*3600*1000;
    /* WebKit's IndexedDB stores a Blob made of SLICES wrong (29.10.2026). Writing a
       Blob for IndexedDB, WebKit copies every item it is made of WHOLE — the file
       behind a `file.slice()`, the buffer behind a slice of an in-memory Blob —
       ignoring the slice's offset and length (BlobRegistryImpl::
       populateBlobsForFileWriting, WebKit main). Measured in Playwright WebKit:
       a 1 MB slice of a picked 200 MB file grows the profile by 200 MB; a 120 MB
       result made of two slices of it — by 400 MB, and after a restart it reads
       back as 419 MB of the wrong bytes. Chromium and Firefox store the slice
       (+1 MB). And a slice is what most results here are: a copy cut of a video
       (Mp4CopyTrim), a part of a split, a file with our tag written in (ai-mark,
       mp4-tags, riff-scrub). Support chat 728 (desktop Safari): nine 630 MB parts
       of a 5.5 GB take, each delivered after a run of minutes — each keep wrote
       the whole take again. So on WebKit the copy is written in whole pieces
       (measured: +120 MB for the 120 MB result, the same bytes after a restart).
       Every iOS browser is WebKit and says «Apple Computer, Inc.» here. */
    var SLICES_STORED_WHOLE=(function(){try{return navigator.vendor==='Apple Computer, Inc.';}catch(e){return false;}})();
    var KEEP_PIECE=32*1048576;
    var _tx=function(db,fn){return new Promise(function(res,rej){
        try{
            var tx=db.transaction(STORE,'readwrite');
            fn(tx.objectStore(STORE));
            tx.oncomplete=function(){res();};
            tx.onerror=function(){rej(tx.error);};
            tx.onabort=function(){rej(tx.error);};
        }catch(e){rej(e);}
    });};
    var open=function(){return new Promise(function(res,rej){
        try{
            var rq=indexedDB.open(DB,1);
            rq.onupgradeneeded=function(){try{rq.result.createObjectStore(STORE);}catch(e){}};
            rq.onsuccess=function(){res(rq.result);};
            rq.onerror=function(){rej(rq.error);};
        }catch(e){rej(e);}
    });};
    /**
     * Keep the finished export so a failed delivery costs a retry of the
     * DOWNLOAD, not of the work that produced it.
     *
     * ⚠️ There used to be a flat 200 MB cap here, mirroring the stash cap it
     * had nothing to do with. That inverted the whole point: the exports too
     * big for the relay — the only ones whose loss is expensive — were exactly
     * the ones not kept. Yuri (Pro, 2026-08-31) encoded a 406 MB video for
     * 3 h 18 min on his phone, the relay refused it for size, nothing was kept,
     * and the bytes died with the tab.
     *
     * Measured 2026-09-01 (delivery-preserve-and-fsa, one context per case,
     * verified after a RELOAD so it is survival of the tab and not of the call):
     *
     *     size     IndexedDB              OPFS
     *     100 MB   survives, write 16 ms  survives, write 151 ms
     *     400 MB   survives, write 10 ms  survives, write 558 ms
     *     800 MB   survives, write 27 ms  survives, write 1.2 s
     *
     * IndexedDB stores a blob REFERENCE — the bytes are already in Chromium's
     * blob store, so the write is ~free at any size and there was never a
     * reason to refuse 406 MB. The bound that does exist is the origin quota,
     * so that is what we check: never take more than half of what is left, so
     * a rescue copy cannot be the thing that fills someone's phone.
     */
    // The Blob this page last asked to keep. The download door clears the copy after a
    // confirmed delivery of THESE bytes whoever stored them — itself, for the browsers it
    // knows to be fragile, or a tool that keeps a costly result from the moment it exists.
    var _mine=null,_mineId='';
    /* «This copy went out» — written SYNCHRONOUSLY, because the moment that confirms a
       delivery is often the page leaving (the person pressed «Скачать» and followed a
       link a second later), and an IndexedDB delete started in pagehide does not get to
       run. Production 16–17.10.2026: 22 restore bars in the same tab within a minute of
       a delivery that had worked — the 3-s drop timer died with the page. The record
       carries an id; the bar skips (and removes) a record whose id is marked here. */
    var DELIVERED_KEY='tmb_keep_delivered';
    window.__exportKeepHas=function(blob){return !!blob&&blob===_mine;};
    /* opts.untilLeave — a result that waits for a press («Скачать»), kept from the moment
       it exists (Token.awaitDelivery in tool-usage-limit.js): a death of the tab before
       the press leaves it for the restore bar, but a person who LEAVES the page without
       taking it chose so — that pagehide marks it away (sync), and no bar offers a file
       somebody looked at and did not want. */
    var _leaveHooked=false,_mineUntilLeave=false;
    /* A flow of OURS is about to take the page away and bring the person back — the
       token wall's payment (the YooKassa widget leaves for return_url, our onPaid
       reloads). That is not the person leaving the result behind: the copy stays for
       the restore bar. Comment #2055 (30.10.2026, /video-upscaler): 2 h 27 min of
       upscaling, «Пополнить токены» pressed before «Скачать», the payment's return
       reloaded the page and the result was gone. */
    window.__exportKeepStay=function(){_mineUntilLeave=false;};
    window.__exportKeepSave=function(blob,name,opts){
        try{
            if(!blob||!blob.size)return;
            if(blob===_mine)return;                     // already kept (the door after awaitDelivery)
            _mine=blob;
            var id=_mineId=Date.now().toString(36)+Math.random().toString(36).slice(2,7);
            _mineUntilLeave=!!(opts&&opts.untilLeave);
            if(_mineUntilLeave&&!_leaveHooked){
                _leaveHooked=true;
                window.addEventListener('pagehide',function(e){
                    if(e&&e.persisted)return;
                    if(_mine&&_mineUntilLeave&&window.__exportKeepDelivered)window.__exportKeepDelivered();
                });
            }
            // Ask for persistent storage before keeping something expensive.
            // Without it the rescue copy sits in best-effort storage, which the
            // browser is free to evict under pressure — i.e. exactly when a
            // 400 MB export has just been written on a phone that is short of
            // space. This is the API's stated purpose ("data the user would be
            // upset to lose"); in Chromium and WebKit it resolves without any
            // prompt, so it costs the user nothing either way. Fire-and-forget:
            // a refusal is not a reason to skip the keep, only a reason it may
            // not survive as long.
            // ⚠️ Not in Firefox: there persist() is a permission PROMPT, and since
            // 13.10.2026 this runs in every browser (a tool keeps its result when it is
            // born), not only in the in-app WebViews the door reserved it for.
            try{if(navigator.storage&&navigator.storage.persist&&navigator.storage.persisted
                    &&!/Firefox\/|FxiOS\//.test(navigator.userAgent||'')){
                navigator.storage.persisted().then(function(already){
                    if(!already)return navigator.storage.persist();
                }).catch(function(){});
            }}catch(e){}
            var _fits=(navigator.storage&&navigator.storage.estimate)
                ? window.__storageEstimate().then(function(e){
                    var free=Math.max(0,(e.quota||0)-(e.usage||0));
                    return free>0 ? blob.size<=free/2 : true;   // no estimate → let the put decide
                }).catch(function(){return true;})
                : Promise.resolve(true);
            _fits.then(function(ok){
            if(!ok){try{if(window.__ae)window.__ae('feature_use',{f:'export_keep_skipped',reason:'quota',sizeKB:Math.round(blob.size/1024)});}catch(e){}return;}
            // Already confirmed away (or replaced) while the room was being measured: nothing to keep.
            if(_mine!==blob||_mineId!==id)return;
            open().then(function(db){
                var close=function(){try{db.close();}catch(e){}};
                if(_mine!==blob||_mineId!==id){close();return;}
                var rec={name:name||'download',at:Date.now(),id:id,
                    tool:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                    path:location.pathname};
                if(!SLICES_STORED_WHOLE){rec.blob=blob;return _tx(db,function(st){st.put(rec,KEY);}).then(close,close);}
                // WebKit: the copy as WHOLE pieces, one record each (see SLICES_STORED_WHOLE).
                // A piece read is a correct read of the result whatever it is made of; a
                // piece stored is exactly its bytes; the memory held is one piece. The
                // record that names the pieces is written LAST, so a copy cut short (the
                // page died, a newer keep or a delivery took over) is never offered.
                var n=Math.ceil(blob.size/KEEP_PIECE),k=0;
                var live=function(){return _mine===blob&&_mineId===id;};
                var next=function(){
                    if(!live())return null;
                    if(k>=n){rec.pieces=n;rec.size=blob.size;rec.type=blob.type||'';return _tx(db,function(st){st.put(rec,KEY);});}
                    var at=k*KEEP_PIECE;
                    return blob.slice(at,Math.min(blob.size,at+KEEP_PIECE)).arrayBuffer().then(function(ab){
                        if(!live())return null;
                        var key=KEY+'#'+id+'#'+(k++);
                        return _tx(db,function(st){st.put(new Blob([ab]),key);}).then(next);
                    });
                };
                return _tx(db,function(st){st.clear();}).then(next).then(close,function(){
                    // Half a copy is no copy: whatever this one wrote goes (quota, an unreadable piece).
                    try{if(window.__ae)window.__ae('feature_use',{f:'export_keep_skipped',reason:'pieces_failed',sizeKB:Math.round(blob.size/1024)});}catch(e){}
                    return (live()?_tx(db,function(st){st.clear();}):Promise.resolve()).then(close,close);
                });
            }).catch(function(){});
            }).catch(function(){});
        }catch(e){}
    };
    /* The delivery of THIS page's kept copy is confirmed: mark it (sync), then remove it. */
    window.__exportKeepDelivered=function(){
        try{if(_mineId)window.localStorage.setItem(DELIVERED_KEY,_mineId);}catch(e){}
        window.__exportKeepDrop();
    };
    window.__exportKeepDrop=function(){
        try{
            _mine=null;_mineId='';_mineUntilLeave=false;
            // clear(), not delete(KEY): the store holds this copy only — its record and,
            // on WebKit, its pieces (and the pieces of a copy that was cut short).
            open().then(function(db){
                var close=function(){try{db.close();}catch(e){}};
                return _tx(db,function(st){st.clear();}).then(close,close);
            }).catch(function(){});
        }catch(e){}
    };
    /* A copy kept in pieces comes back as one Blob of them — every piece present and
       the size what was kept, or no copy at all. */
    var _assemble=function(db,r){
        if(!r||!r.pieces)return Promise.resolve(r);
        return new Promise(function(res){
            try{
                var tx=db.transaction(STORE,'readonly'),st=tx.objectStore(STORE),got=new Array(r.pieces),bad=false;
                for(var i=0;i<r.pieces;i++)(function(i){
                    var q=st.get(KEY+'#'+r.id+'#'+i);
                    q.onsuccess=function(){if(q.result&&q.result.size)got[i]=q.result;else bad=true;};
                })(i);
                tx.oncomplete=function(){
                    var b=bad?null:new Blob(got,{type:r.type||''});
                    if(!b||b.size!==r.size){res(null);return;}
                    r.blob=b;res(r);
                };
                tx.onerror=tx.onabort=function(){res(null);};
            }catch(e){res(null);}
        });
    };
    window.__exportKeepLoad=function(){
        return new Promise(function(res){
            try{
                open().then(function(db){
                    var tx=db.transaction(STORE,'readonly');
                    var rq=tx.objectStore(STORE).get(KEY);
                    rq.onsuccess=function(){
                      _assemble(db,rq.result).then(function(r){
                        if(rq.result&&!r)r=rq.result;   // pieces missing: falls into «no blob» below and is dropped
                        db.close();
                        if(!r||!r.blob||!r.at||(Date.now()-r.at)>TTL_MS){
                            // Past its day: the record is the only thing holding the bytes on
                            // the person's disk, and nothing else ever removed it — it stayed
                            // until the next export overwrote it (a 400 MB video, for weeks).
                            if(r)window.__exportKeepDrop();
                            res(null);return;
                        }
                        var done='';
                        try{done=window.localStorage.getItem(DELIVERED_KEY)||'';}catch(e){}
                        if(r.id&&done===r.id){
                            try{if(window.__ae)window.__ae('feature_use',{f:'export_restore_skip_delivered',tool:r.tool||'',
                                sizeKB:Math.round(r.blob.size/1024),age_min:Math.round((Date.now()-r.at)/60000)});}catch(e){}
                            window.__exportKeepDrop();
                            res(null);return;
                        }
                        res(r);
                      });
                    };
                    rq.onerror=function(){try{db.close();}catch(e){}res(null);};
                }).catch(function(){res(null);});
            }catch(e){res(null);}
        });
    };
})();
// ── Upload progress pill: honest live feedback for the multi-minute mobile
// stash upload (fetch() cannot observe upload progress — this is fed by the
// XHR upload.onprogress in _stash). Fixed, bottom-center, no layout shift.
window.__upProg=(function(){
    var el=null,hideT=null;
    var ensure=function(){
        if(el&&document.body.contains(el))return el;
        el=document.createElement('div');
        el.setAttribute('role','status');
        el.style.cssText='position:fixed;left:50%;transform:translateX(-50%);bottom:64px;z-index:9990;'+
            'background:rgba(20,24,32,.92);color:#fff;border:1px solid rgba(255,255,255,.18);'+
            'border-radius:20px;padding:8px 16px;font:13px/1.4 system-ui,sans-serif;'+
            'box-shadow:0 4px 18px rgba(0,0,0,.35);max-width:92vw;white-space:nowrap;overflow:hidden;'+
            'text-overflow:ellipsis;pointer-events:none';
        document.body.appendChild(el);
        return el;
    };
    return {
        set:function(txt){try{if(hideT){clearTimeout(hideT);hideT=null;}ensure().textContent=txt;}catch(e){}},
        done:function(){try{if(el){var n=el;hideT=setTimeout(function(){try{n.remove();}catch(e){}},400);el=null;}}catch(e){}}
    };
})();
// ── Shared upload-with-progress primitive ──────────────────────────────
// Same lesson as the download-stash leg above, generalized: a tool that
// POSTs a user file with plain fetch() is BLIND (no upload progress) and
// its Cancel button is a LIE (fetch has no abort here — trim's own code
// carried the comment "Cancel can't abort the server fetch — the blob
// arrives anyway"). Prod 2026-07-20: two users watched 4.7 and 5.3-minute
// uploads with zero feedback and no way out.
//
// Contract: __uploadWithProgress(url, formData, opts) -> Promise<Response-like>
//   opts.label     — text prefix for the progress pill (default: generic)
//   opts.sizeBytes — total size, for the "N MB" part of the pill
//   opts.signal    — AbortSignal; aborting genuinely kills the request
//   opts.stallMs   — no-meaningful-advance watchdog (default 20 s)
//   opts.accept    — Accept header (default application/json)
// Resolves { ok, status, blob(), json() } so call sites read like fetch().
// Rejects Error with .kind = 'stall' | 'net' | 'abort'.
//
// Why XHR: fetch() cannot observe upload progress, so a stalled link is
// indistinguishable from a slow one. upload.onprogress + the 20 KB/20 s
// no-advance watchdog turns silent limbo into an abortable failure.
//
// Since 08.10.2026 the FILE goes ahead first, in slices (__uploadBody with
// `slices`, for our own URLs; opts.slices:false turns it off): this XHR alone
// was still ONE long POST of the whole file, the request Chromium kills on a
// buffered uplink (see SLICE_TARGET_MS). The pill shows the slices' bytes; the
// XHR below then carries only the lane's fields and the upload's name.
var _uploadXhr=function(url,formData,opts){
    opts=opts||{};
    var stallMs=opts.stallMs||20000;
    var sizeMB=opts.sizeBytes?Math.round(opts.sizeBytes/1048576):0;
    var label=opts.label||'';
    var showPill=window.__upProg&&typeof window.__upProg.set==='function';
    return new Promise(function(res,rej){
        var xhr;
        try{xhr=new XMLHttpRequest();xhr.open('POST',url,true);}
        catch(e){var e0=new Error('xhr-open');e0.kind='net';rej(e0);return;}
        try{
            var meta=document.querySelector('meta[name="csrf-token"]');
            if(meta)xhr.setRequestHeader('X-CSRF-TOKEN',meta.content);
            xhr.setRequestHeader('Accept',opts.accept||'application/json');
        }catch(e){}
        xhr.responseType='blob';
        var lastAdv=Date.now(),lastLoaded=0,watchdog=null,settled=false;
        var cleanup=function(){
            if(watchdog){clearInterval(watchdog);watchdog=null;}
            if(showPill)try{window.__upProg.done();}catch(e){}
            if(opts.signal)try{opts.signal.removeEventListener('abort',onAbort);}catch(e){}
        };
        var fin=function(fn,v){if(settled)return;settled=true;cleanup();fn(v);};
        var failWith=function(kind,msg){var er=new Error(msg);er.kind=kind;fin(rej,er);};
        function onAbort(){try{xhr.abort();}catch(e){}}
        if(opts.signal){
            if(opts.signal.aborted){failWith('abort','aborted before start');return;}
            opts.signal.addEventListener('abort',onAbort);
        }
        // Stall = no MEANINGFUL advance (>=20 KB) within stallMs. A plain
        // "any progress" check is defeated by a 100 B/s trickle that keeps
        // ticking while the upload is hopeless.
        //
        // …but only WHILE THE BODY IS BEING SENT. Once it is all handed over
        // there are no progress events by design, and the wait that follows
        // is the server's WORK (a server lane processing the file), not a
        // stall. Measured 2026-09-26: a request answered 35 s after its body
        // was sent died here at 20.0 s — and died as kind 'abort', because
        // xhr.abort() fires onabort before the stall could be reported, which
        // /trim reads as the person's own Cancel and quietly gives up. The
        // answer gets its own, long budget (opts.answerMs; the default is the
        // FPM request ceiling), and a stall reports itself as a stall.
        var answerMs=opts.answerMs||300000,sentAt=0,why=null;
        watchdog=setInterval(function(){
            var now=Date.now();
            if(sentAt){
                if(now-sentAt>answerMs){
                    why={kind:'stall',msg:'no answer '+Math.round(answerMs/1000)+'s after upload'};
                    try{xhr.abort();}catch(e){}
                    failWith(why.kind,why.msg);
                }
            }else if(now-lastAdv>stallMs){
                why={kind:'stall',msg:'upload stalled >'+Math.round(stallMs/1000)+'s'};
                try{xhr.abort();}catch(e){}
                failWith(why.kind,why.msg);
            }
        },5000);
        xhr.upload.onload=function(){if(!sentAt)sentAt=Date.now();};
        xhr.upload.onprogress=function(ev){
            // A FALL is the browser re-sending the request on a fresh
            // connection (HTTP/2 PING failure on a buffered uplink — see the
            // relay notes at SLICE_TARGET_MS): activity, not a stall.
            if(ev.loaded<lastLoaded){lastLoaded=ev.loaded;lastAdv=Date.now();sentAt=0;}
            else if(ev.loaded-lastLoaded>=20480){lastLoaded=ev.loaded;lastAdv=Date.now();}
            if(showPill&&ev.total){
                var pct=Math.min(99,Math.round(ev.loaded/ev.total*100));
                window.__upProg.set((label?label+' ':'')+(sizeMB?sizeMB+' MB · ':'')+pct+'%');
            }
        };
        xhr.onerror=function(){failWith('net','network error');};
        xhr.onabort=function(){if(why)failWith(why.kind,why.msg);else failWith('abort','aborted');};
        xhr.ontimeout=function(){failWith('net','timeout');};
        xhr.onload=function(){
            var st=xhr.status,body=xhr.response;
            fin(res,{
                ok:st>=200&&st<300,
                status:st,
                blob:function(){return Promise.resolve(body);},
                json:function(){
                    // responseType='blob' so JSON error bodies need a read.
                    return body&&body.text?body.text().then(function(t){
                        try{return JSON.parse(t);}catch(e){return null;}
                    }):Promise.resolve(null);
                }
            });
        };
        try{xhr.send(formData);}catch(e){failWith('net','send threw');}
    });
};
window.__uploadWithProgress=function(url,formData,opts){
    opts=opts||{};
    var B=window.__uploadBody,own=false;
    try{own=new URL(url,location.href).origin===location.origin;}catch(_){}
    if(typeof B!=='function'||typeof FormData==='undefined'||!(formData instanceof FormData)||!own||opts.slices===false)
        return _uploadXhr(url,formData,opts);
    var sizeMB=opts.sizeBytes?Math.round(opts.sizeBytes/1048576):0,label=opts.label||'';
    var showPill=window.__upProg&&typeof window.__upProg.set==='function';
    var send=function(fresh){
        return B(formData,{slices:true,signal:opts.signal,fresh:fresh,onProgress:function(sent,total){
            if(showPill&&total)window.__upProg.set((label?label+' ':'')+(sizeMB?sizeMB+' MB · ':'')+
                Math.min(99,Math.round(sent/total*100))+'%');
        }}).then(function(body){return _uploadXhr(url,body,opts);},function(e){
            if(showPill)try{window.__upProg.done();}catch(_){}
            // the contract above: kind 'abort' | 'net' (a file the page cannot
            // read keeps its own type — __uploadBody.kind names it)
            if((e&&e.name==='AbortError')||(opts.signal&&opts.signal.aborted)){var ea=new Error('aborted');ea.kind='abort';throw ea;}
            if(e&&e.isUploadSlice){e.kind='net';}
            throw e;
        });
    };
    return send(false).then(function(r){
        if(r.status!==410)return r;
        return r.json().then(function(j){return (j&&j.error_kind==='upload_gone')?send(true):r;},function(){return r;});
    });
};
// ── Clipboard copy: primitive + legacy safety-net ──
// navigator.clipboard is undefined or permission-blocked in in-app browsers
// (YaSearchApp/webOmni, Instagram, some TWAs) — a bare
// `navigator.clipboard.writeText(...)` there either throws synchronously or
// rejects with no UI feedback, so the tap feels dead (bug report 2026-08-02,
// /ru/ip-lookup). Same shape as the __downloadFile story: __copyText is the
// front door for new code, and a global interceptor below transparently
// covers the ~415 legacy bare call sites (39 public/js files + 222 blades,
// counted 2026-08-03) without touching each one.

// execCommand fallback path. Resolves boolean, never rejects.
function __legacyCopy(text){
    return new Promise(function(resolve){
        try{
            var t=document.createElement('textarea');
            t.value=text;
            t.setAttribute('readonly','');
            // font-size ≥16px: keep iOS from zoom-jumping on focus
            t.style.cssText='position:fixed;left:-9999px;top:0;font-size:16px';
            document.body.appendChild(t);
            t.select();
            t.setSelectionRange(0,text.length);
            var ok=document.execCommand('copy');
            document.body.removeChild(t);
            resolve(!!ok);
        }catch(e){resolve(false)}
    });
}

// window.__copyText(text) → Promise<boolean> — Clipboard API first,
// execCommand second; resolves false only when BOTH failed — callers then
// show their own "select manually" hint. Never rejects.
window.__copyText=function(text){
    text=String(text==null?'':text);
    if(navigator.clipboard&&navigator.clipboard.writeText){
        return navigator.clipboard.writeText(text).then(function(){return true},function(){return __legacyCopy(text)});
    }
    return __legacyCopy(text);
};

// Legacy-call-site interceptor (safety-net, not the front door):
// 1) API absent → install a shim so bare `navigator.clipboard.writeText(...)`
//    stops dying with a synchronous TypeError; the shim resolves through the
//    execCommand path and REJECTS when even that failed, so caller .catch()
//    branches stay honest.
// 2) API present → shadow writeText on the instance so a permission-reject
//    falls back to execCommand; the caller's .then() fires only when the text
//    actually reached the clipboard. read()/readText()/write() are untouched
//    (qr-scanner, json-formatter etc. rely on them).
(function(){
    try{
        var rejectIfFalse=function(ok){ if(!ok) return Promise.reject(new DOMException('Copy unavailable','NotAllowedError')); };
        if(!navigator.clipboard||!navigator.clipboard.writeText){
            var shim={writeText:function(t){ return __legacyCopy(String(t==null?'':t)).then(rejectIfFalse); }};
            Object.defineProperty(navigator,'clipboard',{value:shim,configurable:true});
        }else{
            var orig=navigator.clipboard.writeText.bind(navigator.clipboard);
            var wrapped=function(t){
                // VivoBrowser 12 answers writeText with undefined instead of a
                // promise (Sentry KZ, /fancy-text) — whatever the native call
                // returns is made a promise BEFORE anything chains on it.
                var p; try { p = orig(t); } catch(e0){ p = Promise.reject(e0); }
                if (!p || typeof p.then !== 'function') p = Promise.resolve(p);
                return p.catch(function(err){
                    return __legacyCopy(String(t==null?'':t)).then(function(ok){ if(!ok) throw err; });
                });
            };
            try{ navigator.clipboard.writeText=wrapped; }
            catch(e){ Object.defineProperty(navigator.clipboard,'writeText',{value:wrapped,configurable:true}); }
        }
    }catch(e){ /* frozen navigator (exotic embedder) — legacy sites keep old behaviour */ }
})();

// ─── Speech delivery: front door + legacy safety net ──────────────────────
// speechSynthesis.speak() can accept an utterance and produce nothing at all —
// no sound, no 'start', no 'error' — or deliver a fragment of the sentence and
// still report success. Cloud voices ("… Online (Natural)", "Google …") are the
// usual source: they stream from a server, and a connection that cannot carry
// the stream fails silently. Bug #1304 (2026-08-06, /dictation-practice) was
// exactly that; tts-helper.js now verifies delivery for the 21 tools that use
// it.
//
// The other ~35 speaking tools call the API bare, fire-and-forget, with no
// onend/onerror at all — so a failure there is pure silence, and several of
// them are the ones where silence costs most: /communication-board is an AAC
// board for non-speaking users, /panic-button speaks an emergency message,
// /color-reader and /reading-ruler are read-aloud aids. Same shape as the
// __downloadFile and __copyText stories above: __speak is the front door for
// new code, and the interceptor below covers the legacy call sites without
// touching each one.
window.__speechDelivery=(function(){
    // Fastest plausible delivery, characters per second. Measured 2026-08-06 on
    // Windows SAPI via Edge and Chrome (3 voices x 4 lengths x 2 rates): 7.6-16.7
    // c/s at rate 1, 4.8-9.6 at rate 0.5. The verdict only trips below 40% of
    // this, so a healthy read — even from a voice quicker than any measured —
    // cannot be mistaken for a truncated one. Keep in step with tts-helper.js.
    var FAST_CPS=22, TRUNCATED_FRACTION=0.4;
    // Cold start on a fresh browser measured p50 708 ms, max 1175 ms; a loaded
    // machine goes further. 2 s was too tight and declared healthy engines dead.
    var COLD_LOCAL_MS=4000, COLD_NET_MS=6000, RESCUE_MS=1500;

    function isNet(v){ return !!v&&v.localService===false }

    // Did the engine read the text out, or just claim it did? boundary progress
    // is independent of how fast a voice speaks, so it wins when the engine
    // emits it. Short strings are never judged — a one-word utterance is mostly
    // fixed engine overhead and the ratios stop meaning anything.
    function verdict(text,rate,elapsedMs,lastChar,bounds){
        var len=String(text||'').length;
        if(len<8) return 'ok';
        if(bounds>0&&lastChar>=0&&len>=25) return lastChar>=len*0.5?'ok':'truncated';
        return elapsedMs<(len/(FAST_CPS*(rate||1)))*1000*TRUNCATED_FRACTION?'truncated':'ok';
    }

    // How many voices the device lists, and how many speak `lang`. Recorded on
    // every stall, because the two failures need different cures and look the
    // same from outside: an engine that is dead (Huawei without Google
    // services, the Yandex app — no voice at all) and a healthy engine with no
    // voice for this language (Windows Chrome/Opera/Yandex carry en + ru only;
    // asked for Japanese they «speak» it with the English voice). Measured
    // 2026-10-30, /alphabet report.
    function voiceStats(lang){
        var st={vc:0,lv:0};
        try{
            var want=String(lang||'').toLowerCase().slice(0,2),all=speechSynthesis.getVoices()||[];
            st.vc=all.length;
            for(var i=0;i<all.length;i++) if(want&&String(all[i].lang||'').toLowerCase().slice(0,2)===want) st.lv++;
        }catch(e){}
        return st;
    }

    function tele(payload,lang){
        try{
            if(!window.__ae) return;
            var m=document.querySelector('meta[name="tool-slug"]');
            payload.slug=m?m.content:''; payload.f='tts_stall'; payload.via='safety_net';
            var st=voiceStats(lang); payload.vc=st.vc; payload.lv=st.lv; payload.lang=String(lang||'').slice(0,5);
            window.__ae('feature_use',payload);
        }catch(e){}
    }

    // The one notice for speech that could not be delivered — the safety net
    // below and tts-helper.js both end here. Rage-clicking a dead button must
    // not stack toasts, hence the 8 s quiet.
    var DEAD={
        ru:'Не удалось произнести текст: браузер не выдал звук. Проверьте громкость и голоса синтеза речи в настройках системы.',
        en:'Could not speak the text — the browser produced no audio. Check your volume and the speech voices installed in your system settings.',
        de:'Der Text konnte nicht vorgelesen werden — der Browser gab keinen Ton aus. Prüfen Sie die Lautstärke und die Sprachausgabe-Stimmen in den Systemeinstellungen.',
        es:'No se pudo leer el texto: el navegador no emitió sonido. Revisa el volumen y las voces de síntesis de voz en la configuración del sistema.',
        fr:'Impossible de lire le texte : le navigateur n’a produit aucun son. Vérifiez le volume et les voix de synthèse vocale dans les réglages du système.',
        uk:'Не вдалося відтворити текст — браузер не видав звуку. Перевірте гучність та голоси синтезу мовлення в налаштуваннях системи.',
        pt:'Não foi possível ler o texto: o navegador não produziu som. Verifique o volume e as vozes de síntese de fala nas configurações do sistema.',
        id:'Teks tidak dapat dibacakan — peramban tidak mengeluarkan suara. Periksa volume dan suara text-to-speech di pengaturan sistem.',
        ko:'텍스트를 읽어 주지 못했습니다 — 브라우저에서 소리가 나오지 않았습니다. 볼륨과 시스템 설정의 음성 합성 보이스를 확인하세요.',
        ar:'تعذّرت قراءة النص: لم يصدر المتصفح أي صوت. تحقّق من مستوى الصوت ومن أصوات تحويل النص إلى كلام في إعدادات النظام.',
        tr:'Metin seslendirilemedi — tarayıcı ses üretmedi. Ses düzeyini ve sistem ayarlarındaki konuşma sentezi seslerini kontrol edin.'
    };
    var NOLANG={
        ru:'Не удалось произнести текст: на этом устройстве нет голоса для этого языка. Голоса синтеза речи добавляются в настройках системы.',
        en:'Could not speak the text — this device has no voice for this language. Speech voices are added in your system settings.',
        de:'Der Text konnte nicht vorgelesen werden — auf diesem Gerät gibt es keine Stimme für diese Sprache. Sprachausgabe-Stimmen fügen Sie in den Systemeinstellungen hinzu.',
        es:'No se pudo leer el texto: este dispositivo no tiene voz para este idioma. Las voces de síntesis se agregan en la configuración del sistema.',
        fr:'Impossible de lire le texte : cet appareil n’a pas de voix pour cette langue. Les voix de synthèse s’ajoutent dans les réglages du système.',
        uk:'Не вдалося відтворити текст — на цьому пристрої немає голосу для цієї мови. Голоси синтезу мовлення додаються в налаштуваннях системи.',
        pt:'Não foi possível ler o texto: este aparelho não tem voz para este idioma. As vozes de síntese são adicionadas nas configurações do sistema.',
        id:'Teks tidak dapat dibacakan — perangkat ini tidak punya suara untuk bahasa ini. Suara text-to-speech ditambahkan di pengaturan sistem.',
        ko:'텍스트를 읽어 주지 못했습니다 — 이 기기에는 이 언어의 음성이 없습니다. 음성 합성 보이스는 시스템 설정에서 추가할 수 있습니다.',
        ar:'تعذّرت قراءة النص: لا يوجد على هذا الجهاز صوت لهذه اللغة. تُضاف أصوات تحويل النص إلى كلام من إعدادات النظام.',
        tr:'Metin seslendirilemedi — bu cihazda bu dil için ses yok. Konuşma sentezi sesleri sistem ayarlarından eklenir.'
    };
    var lastAnnounce=0;
    function announce(lang){
        try{
            if(typeof window.__toast!=='function') return;
            var now=Date.now();
            if(now-lastAnnounce<8000) return;
            lastAnnounce=now;
            var st=lang?voiceStats(lang):{vc:0,lv:0};
            var M=(st.vc>0&&st.lv===0)?NOLANG:DEAD, L=(window.__LOCALE||'en').substring(0,2);
            window.__toast(M[L]||M.en,'error',{ctx:'tts_undelivered'});
        }catch(e){}
    }

    // Best same-language LOCAL voice, for the rescue attempt. Deliberately not
    // "best voice": the whole point of a rescue is to stop depending on the
    // network the first attempt could not use.
    function localVoiceFor(lang){
        try{
            var want=String(lang||'').toLowerCase().slice(0,2),best=null,all=speechSynthesis.getVoices()||[];
            for(var i=0;i<all.length;i++){
                var v=all[i];
                if(v.localService===false) continue;
                if(want&&String(v.lang||'').toLowerCase().slice(0,2)!==want) continue;
                if(!best||v.default) best=v;
            }
            return best;
        }catch(e){ return null }
    }
    return {verdict:verdict,localVoiceFor:localVoiceFor,isNet:isNet,tele:tele,announce:announce,voiceStats:voiceStats,
            COLD_LOCAL_MS:COLD_LOCAL_MS,COLD_NET_MS:COLD_NET_MS,RESCUE_MS:RESCUE_MS};
})();

// window.__speak(text, opts) — front door. Delegates to TTSHelper (full voice
// ladder + learned reliability) on the pages that load it, and otherwise runs
// the same rescue the interceptor gives legacy callers.
window.__speak=function(text,opts){
    opts=opts||{};
    if(window.TTSHelper&&window.TTSHelper.isSupported&&window.TTSHelper.isSupported())
        return window.TTSHelper.speak(text,opts);
    try{
        if(!window.speechSynthesis||typeof window.SpeechSynthesisUtterance!=='function'){
            if(opts.onerror) opts.onerror(new Error('tts_unsupported'));
            return null;
        }
        var u=new SpeechSynthesisUtterance(String(text));
        if(opts.lang) u.lang=opts.lang;
        if(typeof opts.rate==='number') u.rate=opts.rate;
        if(typeof opts.pitch==='number') u.pitch=opts.pitch;
        if(typeof opts.volume==='number') u.volume=opts.volume;
        if(opts.voice) u.voice=opts.voice;
        if(opts.onstart) u.onstart=opts.onstart;
        if(opts.onend) u.onend=opts.onend;
        if(opts.onerror) u.onerror=opts.onerror;
        speechSynthesis.cancel();
        speechSynthesis.speak(u);
        return u;
    }catch(e){ if(opts.onerror) opts.onerror(e); return null }
};

// Legacy-call-site interceptor (safety-net, not the front door).
// Wraps speechSynthesis.speak so a bare `synth.speak(u)` gets ONE rescue: if
// the utterance never starts, errors, or is cut short, it is spoken again with
// a local voice. Utterances that tts-helper.js already manages are skipped —
// it runs a fuller ladder of its own and must not be double-retried.
(function(){
    try{
        var synth=window.speechSynthesis;
        if(!synth||typeof synth.speak!=='function'||typeof window.SpeechSynthesisUtterance!=='function') return;
        var D=window.__speechDelivery;
        var orig=synth.speak.bind(synth);
        var gen=0;
        // Our own cancel() bumps the generation so an interrupted utterance is
        // never mistaken for a broken one — the user pressing the next button is
        // the single most common reason speech stops early.
        var origCancel=synth.cancel.bind(synth);
        // Utterances that were spoken and have not finished, in engine order.
        // The start deadline is armed only for the HEAD: one queued behind a
        // long sentence has not started because it is waiting its turn, and
        // timing it from speak() declared it dead — with the notice below,
        // that would be a false alarm in front of the person.
        var line=[];
        function leaveLine(x){
            var i=line.indexOf(x);
            if(i<0) return;
            line.splice(i,1);
            if(i===0&&line.length&&line[0].__tmbArm) line[0].__tmbArm();
        }
        try{ synth.cancel=function(){ gen++; line.length=0; return origCancel() } }catch(e){}

        // Whether a caller listens for 'error' with addEventListener: then it
        // has its own answer to a failure, and the notice must not speak over it.
        try{
            var UP=SpeechSynthesisUtterance.prototype;
            var ael=UP.addEventListener||(window.EventTarget&&EventTarget.prototype.addEventListener);
            if(ael&&!UP.__tmbAelWrapped){
                UP.addEventListener=function(type){
                    if(type==='error') try{ this.__tmbErrListener=1 }catch(e){}
                    return ael.apply(this,arguments);
                };
                UP.__tmbAelWrapped=1;
            }
        }catch(e){}

        // A failure nobody could rescue must reach somebody. A caller with
        // onerror gets the call (as before). A caller listening with
        // addEventListener already heard the engine's own 'error' — but a start
        // that never came has NO engine event, so one is dispatched. The ~35
        // fire-and-forget callers have neither; until 2026-10-30 that was pure
        // silence (/alphabet report: 40+ presses, no sound, nothing on screen),
        // now the person is told, in words that say which of the two it is.
        function failTo(t,reason,engineSaidIt,userErr){
            if(userErr){ try{ userErr({error:reason,utterance:t}) }catch(e){} return }
            if(t.__tmbErrListener){
                if(engineSaidIt) return;
                try{
                    if(typeof t.dispatchEvent!=='function') return;
                    var ev;
                    try{ ev=new SpeechSynthesisErrorEvent('error',{utterance:t,error:'synthesis-failed'}) }
                    catch(e1){ ev=new Event('error'); try{ Object.defineProperty(ev,'error',{value:'synthesis-failed'}) }catch(e2){} }
                    t.onerror=null;
                    t.dispatchEvent(ev);
                }catch(e){}
                return;
            }
            D.announce(t.lang);
        }

        function wrapped(u){
            if(!u||u.__tmbManaged||u.__tmbRescue) return orig(u);
            u.__tmbManaged=1;
            var myGen=++gen,started=0,bounds=0,lastChar=-1,guard=0,done=0,queuedAt=0;
            var userStart=u.onstart,userEnd=u.onend,userErr=u.onerror,userBound=u.onboundary;
            var superseded=function(){ return myGen!==gen };
            var disarm=function(){ if(guard){ clearTimeout(guard); guard=0 } };

            function rescue(reason){
                if(done||superseded()) return; done=1; disarm(); leaveLine(u);
                var lv=D.localVoiceFor(u.lang);
                var already=u.voice&&lv&&u.voice.voiceURI===lv.voiceURI;
                D.tele({reason:reason,rescued:(lv&&!already)?1:0,
                        net:D.isNet(u.voice)?1:0,len:String(u.text||'').length},u.lang);
                if(!lv||already){ failTo(u,reason,/^error_/.test(reason),userErr); return; }
                var r=new SpeechSynthesisUtterance(u.text);
                r.lang=u.lang; r.rate=u.rate; r.pitch=u.pitch; r.volume=u.volume;
                r.voice=lv; r.__tmbRescue=1;
                // Hand the caller's own handlers to the rescue attempt, so a tool
                // that does track state still gets exactly one outcome.
                r.onend=userEnd; r.onboundary=userBound;
                // The rescue is watched too: a local voice can be just as dead
                // (no engine at all). Until 2026-10-30 nothing timed it, so a
                // dead rescue ended in the same silence the net exists to stop.
                var rStarted=0,rSettled=0;
                function rFail(why){
                    if(rSettled||superseded()) return; rSettled=1;
                    D.tele({reason:'rescue_'+why,rescued:0,net:0,len:String(u.text||'').length},u.lang);
                    failTo(u,'rescue_'+why,false,userErr);
                }
                r.onstart=function(e){ rStarted=1; if(userStart) userStart(e) };
                r.onerror=function(e){
                    var err=e&&e.error;
                    if(err==='canceled'||err==='interrupted'){ rSettled=1; if(userErr) userErr(e); return }
                    rFail('error_'+(err||'unknown'));
                };
                try{ origCancel() }catch(e){}
                setTimeout(function(){
                    if(superseded()) return;
                    orig(r);
                    setTimeout(function(){ if(!rStarted&&!rSettled&&!superseded()){ try{ origCancel() }catch(e){} rFail('no_start') } },D.COLD_LOCAL_MS);
                },80);
            }

            u.onstart=function(e){ started=Date.now(); disarm(); if(userStart) userStart(e) };
            u.onboundary=function(e){
                bounds++;
                if(e&&typeof e.charIndex==='number'&&e.charIndex>lastChar) lastChar=e.charIndex;
                if(userBound) userBound(e);
            };
            u.onend=function(e){
                leaveLine(u);
                if(done||superseded()){ if(userEnd&&!done) userEnd(e); return }
                disarm();
                if(!started){ rescue('no_start'); return }
                if(D.verdict(u.text,u.rate,Date.now()-started,lastChar,bounds)==='truncated'){ rescue('truncated'); return }
                done=1; if(userEnd) userEnd(e);
            };
            u.onerror=function(e){
                var err=e&&e.error;
                leaveLine(u);
                // A cancel is the caller's or the user's doing, not a fault.
                if(err==='canceled'||err==='interrupted'){ done=1; disarm(); if(userErr) userErr(e); return }
                rescue('error_'+(err||'unknown'));
            };

            queuedAt=Date.now();
            // Busy engine = this one waits its turn; its clock starts when it
            // reaches the head of the line, not now.
            var waits=line.length>0&&(synth.speaking||synth.pending);
            if(!waits) line.length=0;
            line.push(u);
            u.__tmbArm=function(){
                if(guard||started||done) return;
                guard=setTimeout(function(){ if(!started) rescue('no_start') },
                                 D.isNet(u.voice)?D.COLD_NET_MS:D.COLD_LOCAL_MS);
            };
            var out=orig(u);
            if(!waits) u.__tmbArm();
            return out;
        }
        try{ synth.speak=wrapped }
        catch(e){ Object.defineProperty(synth,'speak',{value:wrapped,configurable:true}) }
    }catch(e){ /* exotic embedder — legacy sites keep old behaviour */ }
})();

// window.__speechKeepAlive(u) — the 15-second cure, only where the disease is.
//
// Chrome on the DESKTOP goes quiet ~15 s into an utterance read by a NETWORK
// voice ("Google …"); the known cure is pause()+resume() every 10 s. Four tools
// ran it on a free-running timer for every voice on every platform. On Android
// that pair is a STOP: Chromium's TtsPlatformImplAndroid::Pause() is
// StopSpeaking(), Resume() is empty, the platform drops the utterance id, and the
// page never gets 'end' or 'error' — `speaking` stays true until cancel().
// Measured 2026-10-09, Android 16 / Chrome 133 / Google TTS: a long sentence plays
// to 'end' at 22.9 s; with pause()+resume() 3 s in, the engine's audio player is
// gone and no event arrives in 60 s. In the field (RU, 30 d) Android browsers that
// ran the timer hit no_end in 23 % of played sessions, YaBrowser (no timer) in 0 %,
// and read half as far. Report 2026-10-09: «прекращается после нескольких слов».
//
// Call __speechKeepAlive(u) right before speak(u): it arms on that utterance's
// 'start' and disarms on its end/error. __speechKeepAlive.pauseIsStop is true where
// pause() ends the utterance for good — a user Pause there is cancel() + position.
window.__speechKeepAlive=(function(){
    var ua=navigator.userAgent||'', uad=navigator.userAgentData;
    var android=/Android/i.test(ua)||!!(uad&&/android/i.test(uad.platform||''));
    var PERIOD_MS=10000, live=null;
    function needed(v){ return !android&&!!v&&v.localService===false }
    function attach(u){
        if(!u||!needed(u.voice)||typeof u.addEventListener!=='function') return false;
        var s=window.speechSynthesis, iv=0;
        function stop(){ if(iv){ clearInterval(iv); iv=0 } if(live===u) live=null }
        u.addEventListener('start',function(){
            if(iv) clearInterval(iv);
            live=u;
            iv=setInterval(function(){
                if(live!==u||!s.speaking){ stop(); return }
                if(s.paused) return;   // the person's own Pause — not ours to undo
                s.pause(); s.resume();
            },PERIOD_MS);
        });
        u.addEventListener('end',stop);
        u.addEventListener('error',stop);
        return true;
    }
    attach.needed=needed;
    attach.pauseIsStop=android;
    return attach;
})();

// window.__aiOutput — объявлен ли вывод этой страницы синтетическим.
//
// Два состояния, потому что честный ответ не всегда известен заранее:
//   content="1"           — вывод синтетический всегда (генераторы, синтез речи);
//   content="conditional" — ИИ здесь ОДИН ИЗ РЕЖИМОВ, рядом живёт обычный
//                           алгоритм. Тул зовёт __aiOutput.used(), когда байты
//                           дала именно ИИ-ветка, и __aiOutput.reset() при новом
//                           файле или переключении на не-ИИ режим.
//
// Почему без сигнала НЕ метим: запись «создано ИИ» в файле, которого нейросеть
// не касалась, — ложь в чужих метаданных, и отозвать её нельзя. Забытый же
// сигнал ловит сторож AiOutputPolicyTest: он требует, чтобы каждый conditional-
// тул реально звал used(). Список и обоснование границы — config/ai-output.php.
window.__aiOutput=(function(){
    var used=false;
    function mode(){
        var m=document.querySelector('meta[name="ai-output"]');
        return m?(m.content||'1'):'';
    }
    return {
        mode: mode,
        used: function(){ used=true; },
        reset: function(){ used=false; },
        wasUsed: function(){ return used; },
        shouldMark: function(){
            var m=mode();
            return m==='1'||(m==='conditional'&&used);
        }
    };
})();

// ── window.__uploadBody(formData) — a picked file goes to OUR server from OUR copy ──
//
// A `File` from the picker is not bytes. It is a path, a size and a time stamp,
// and the bytes are read LATER and by SOMEBODY ELSE: `fetch(url, {body: fd})`
// hands the entry to the browser's network process, which opens the file on its
// own when the request starts. On Android that second reader fails where the
// page's own reader does not — the picker hands out content:// items whose
// stamp or grant does not survive the hop — and the request dies before a
// single byte leaves: `TypeError: Failed to fetch` in 200–600 ms, nothing in the
// server's log, and a toast written for a different failure.
//
// Refund request #41 (06.10.2026, Premium bought to re-sing a finished song,
// MIUI Browser on a POCO X3): eight presses, eight instant refusals, in BOTH
// lanes of /voice-cover — three «Не получилось. Попробуйте ещё раз — попытка
// бесплатна» and five «оборвалось соединение… надёжнее всего Wi-Fi», while
// analytics beacons from the same page kept arriving every 30 seconds. In the
// lane that goes through the shared uploader the pick-time read of the same
// file had SUCCEEDED (no file_unreadable_surfaced) — the bytes were already in
// our hands and the page still sent the handle. Two more Premium accounts met
// the same wall on the same tab that week (Yandex app, Yandex Browser): 12
// instant refusals, 0 requests in nginx, then one of them got through on the
// fifth press. The cover form asks for a style and lyrics after the pick, so
// minutes and an app switch sit between «выбрал» and «отправил» by design.
//
// So a file bound for our server is read by the PAGE, and what travels is that
// copy:
//   1. the tier the uploader already made at pick time (FileBytes.settle: RAM
//      up to its cap, the OPFS spill above it; a copy still being written is
//      waited for on a phone) — no new read;
//   2. no tier (a raw <input type=file>, a zone that opted out) and the file is
//      not a video and is at most `copyMaxBytes` (64 MB — above every audio
//      lane's ceiling): read it now through FileBytes.asFile, the same retry
//      ladder the uploader uses;
//   3. otherwise the handle itself, as before: a 600 MB video is not pulled
//      into the heap in order to be uploaded.
// A read that fails is the honest answer and it arrives TYPED (FILE_LOST, with
// `fileLostKind`), before the request, so the page can say what is true —
// __uploadBody.text(err) is that sentence, the same one the uploader shows at
// pick time (window.__unreadableFileMsg). What is left for a thrown fetch after
// that is the network, and only then may a page say «проверьте интернет».
//
// Returns a NEW FormData (same entries, same order, same file names); the one
// passed in is not touched, so a retry can call it again.
//
// HOW they travel — `opts.slices` (08.10.2026): one long POST of a file is what
// Chromium kills on a phone's uplink (docs/why/primitives/upload-slices.md; RU,
// 14 days: 32 % of 20–60-second lane uploads cut, 75 % of 60–120-second ones).
// With `slices` every file entry bigger than one slice (256 KB) under a plain
// key (`audio`, `files[]`) is sent ahead through window.__sliceFile and replaced
// by `__sliced_k[]` + `__sliced_id[]`; the server puts it back as an ordinary
// upload (AttachSlicedUploads), so ONLY a route of our `web` group may get such
// a body — never a third-party URL. __serverFile turns it on for its same-origin
// lanes. `opts.signal` cancels, `opts.onProgress(sent, total)` reports bytes,
// `opts.fresh` uploads again what the server forgot (`upload_gone`).
// Guards: tests/js/upload-body.test.mjs, PickedFilesAreUploadedFromOurCopyTest,
// tests/playwright/upload-survives-a-file-that-changed.mjs.
// Разбор — docs/why/primitives/server-file.md, раздел про __uploadBody.
/* __uploadBody:begin */
window.__uploadBody=function(fd,opts){
    opts=opts||{};
    var FB=window.FileBytes;
    if(!fd||typeof fd.forEach!=='function'||typeof FormData==='undefined')return Promise.resolve(fd);
    var cap=opts.copyMaxBytes>0?opts.copyMaxBytes:64*1048576;
    var entries=[];
    fd.forEach(function(v,k){entries.push([k,v]);});
    var out=new FormData(),stat={files:0,copied:0,live:0,bytes:0,sliced:0};
    var isFile=function(v){return typeof File!=='undefined'&&v instanceof File;};
    // 262144 = SLICE_FIRST_BYTES: a file that fits one slice gains nothing.
    var sliceOn=!!opts.slices&&typeof window.__sliceFile==='function';
    var sliceable=function(k,v){return sliceOn&&(v.size||0)>262144&&/^[A-Za-z0-9_.\-]{1,64}(\[\])?$/.test(k);};
    var total=0,sent=0;
    entries.forEach(function(kv){if(isFile(kv[1])&&sliceable(kv[0],kv[1]))total+=kv[1].size;});
    var chain=Promise.resolve();
    entries.forEach(function(kv){
        chain=chain.then(function(){
            var k=kv[0],v=kv[1];
            if(!isFile(v)){out.append(k,v);return;}
            stat.files++;stat.bytes+=v.size||0;
            var tier=(FB&&typeof FB.settle==='function')?FB.settle(v):Promise.resolve(v);
            return tier.then(function(d){
                if(d&&d!==v)return d;
                var small=(v.size||0)<=cap&&!/^video\//i.test(v.type||'');
                return (FB&&small&&typeof FB.asFile==='function')?FB.asFile(v,v.name):v;
            }).then(function(d){
                if(d&&d!==v)stat.copied++;else stat.live++;
                var f=d||v;
                if(!sliceable(k,f)){out.append(k,f,v.name);return;}
                var before=sent;
                return window.__sliceFile(f,{key:v,name:v.name,signal:opts.signal,fresh:!!opts.fresh,
                    onProgress:function(b){if(opts.onProgress)try{opts.onProgress(before+b,total);}catch(_){}}
                }).then(function(id){
                    sent+=f.size||0;
                    if(!id){out.append(k,f,v.name);return;}   // the server does not take slices
                    stat.sliced++;
                    out.append('__sliced_k[]',k);
                    out.append('__sliced_id[]',id);
                });
            });
        });
    });
    return chain.then(function(){
        try{Object.defineProperty(out,'__upload',{value:stat});}catch(_){}
        return out;
    });
};
/* Which of the two things failed — said by TYPE, never by the words of the error.
   'file'    the page could not read the picked file (pressing again cannot help);
   'network' everything else a thrown request can be. */
window.__uploadBody.kind=function(err){
    var TE=window.ToolErrors;
    return (err&&TE&&typeof TE.isFileLostError==='function'&&TE.isFileLostError(err))?'file':'network';
};
/* The sentence for an unreadable file — one owner with the uploader's pick-time
   verdict. `null` when the failure is not about the file: the page then speaks
   about the network in its own words. */
window.__uploadBody.text=function(err){
    if(window.__uploadBody.kind(err)!=='file')return null;
    var fk=err&&err.fileLostKind;
    var k=(fk==='not_found'||fk==='browser')?fk:'read_error';
    return typeof window.__unreadableFileMsg==='function'?window.__unreadableFileMsg(k):null;
};
/* The server forgot a file sent ahead (410 upload_gone — answered BEFORE the
   lane ran, nothing was spent): a page with its own XMLHttpRequest sends the
   body again with {fresh:true}. __uploadFetch does this by itself. */
window.__uploadBody.gone=function(status,text){
    return status===410&&/"error_kind"\s*:\s*"upload_gone"/.test(String(text||''));
};
/* Was this body built with slices (the page's XHR upload progress is then only
   the lane's fields — keep the bar on the slices' bytes)? */
window.__uploadBody.sliced=function(body){
    try{return !!(body&&typeof body.get==='function'&&body.get('__sliced_id[]'));}catch(_){return false;}
};
/* window.__uploadFetch(url, formData, init, opts) → Promise<Response>
   fetch() for a lane request with a file, for pages that read the Response
   themselves (__serverFile is built on it). The body comes from __uploadBody —
   with `slices` when the URL is ours (opts.slices:false turns it off) — and
   when the server answers 410 `upload_gone` (it forgot the file sent ahead;
   the lane has not run) the file is sent again, once. A CSRF header that
   carried the page's token is refreshed if a slice had to renew the token.
   opts: {slices, signal (default init.signal), onProgress(sent, total),
   onSend() — the body is built and the request leaves (a page that names a
   failure by its step: before it the file, after it the network),
   fetch — what sends the final request (the interceptor below passes the
   fetch it wrapped, so the outer telemetry wrapper counts the call once)}. */
window.__uploadFetch=function(url,fd,init,opts){
    init=Object.assign({},init||{});opts=opts||{};
    var own=false;
    try{own=new URL(url,location.href).origin===location.origin;}catch(_){}
    var meta=function(){var m=document.querySelector('meta[name="csrf-token"]');return m&&m.content||'';};
    var before=meta();
    var send=function(fresh){
        return window.__uploadBody(fd,{slices:own&&opts.slices!==false,signal:opts.signal||init.signal,
            fresh:fresh,onProgress:opts.onProgress}).then(function(b){
            init.body=b;
            var now=meta(),h=init.headers;
            if(h&&now&&now!==before){
                if(typeof h.get==='function'){if(h.get('X-CSRF-TOKEN')===before)h.set('X-CSRF-TOKEN',now);}
                else if(h['X-CSRF-TOKEN']===before){init.headers=Object.assign({},h,{'X-CSRF-TOKEN':now});}
            }
            if(opts.onSend)try{opts.onSend();}catch(_){}
            return typeof opts.fetch==='function'?opts.fetch(url,init):fetch(url,init);
        });
    };
    return send(false).then(function(res){
        if(res.status!==410)return res;
        return res.clone().json().then(function(j){
            return (j&&j.error_kind==='upload_gone')?send(true):res;
        },function(){return res;});
    });
};
/* window.fetch for a FILE to our own server goes through the same door.
   About fifty pages build `new FormData()` with a picked file or a recording
   and hand it to their own fetch() — written before __uploadBody existed, one
   per tool. Each of them had both defects of the class: the picker's handle
   travels (refund request #41 — Android refuses it before a byte leaves) and
   the whole file is ONE long POST (RU nginx, 14 days: 32 % of 20–60-second lane
   uploads cut by the client, 75 % of 60–120-second ones). Rewriting fifty call
   sites one by one would leave the fifty-first; the `<a download>` interceptor
   and the clipboard one above solved the same shape the same way.
   Taken: POST, a FormData body holding a File/Blob, our own origin, a path that
   Laravel serves (nginx answers /speedtest/ itself — there the upload IS the
   measurement), no keepalive, not already built by the door. A page opts out
   with `slices:false` in the init. What the page gets back is what fetch would
   have given it: the Response; a dead link after the slice engine's patience as
   `TypeError('Failed to fetch')` (+ .isUploadSlice) — the shape every legacy
   catch already reads as "network"; a file the page itself cannot read is said
   to the person once (the uploader's sentence, err.__userNotified) and thrown
   as its typed FILE_LOST — never as "check your internet".
   Guards: tests/js/upload-body.test.mjs (what is taken, what comes back),
   tests/playwright/fetch-file-goes-through-the-door.mjs (real browser). */
(function(){
    if(typeof window.fetch!=='function'||typeof FormData==='undefined'||window.fetch.__fileDoor)return;
    var inner=window.fetch;
    // nginx answers /speedtest/ itself; the two slice endpoints ARE the slices
    var SKIP=/^\/(?:speedtest\/|api\/upload-slice$|api\/download-stash-chunk$)/;
    var hasFile=function(fd){
        var yes=false;
        try{fd.forEach(function(v){if(typeof Blob!=='undefined'&&v instanceof Blob)yes=true;});}catch(_){}
        return yes;
    };
    var taken=function(input,init){
        if(!init||typeof init!=='object'||init.keepalive||init.slices===false)return null;
        var b=init.body;
        if(typeof FormData==='undefined'||!(b instanceof FormData)||b.__upload||!hasFile(b))return null;
        if(String(init.method||'GET').toUpperCase()!=='POST')return null;
        var u=typeof input==='string'?input:((typeof URL!=='undefined'&&input instanceof URL)?input.href:null);
        if(!u)return null;   // a Request carries its body already as a stream
        var abs;
        try{abs=new URL(u,location.href);}catch(_){return null;}
        if(abs.origin!==location.origin||SKIP.test(abs.pathname))return null;
        return u;
    };
    var send=function(u,i){return inner.call(window,u,i);};
    var door=function(input,init){
        var u=null;
        try{u=taken(input,init);}catch(_){u=null;}
        if(!u||typeof window.__uploadFetch!=='function')return inner.apply(window,arguments);
        return window.__uploadFetch(u,init.body,init,{fetch:send}).catch(function(e){
            if(e&&e.isUploadSlice){
                var t=new TypeError('Failed to fetch');
                t.isUploadSlice=true;t.status=e.status||0;
                throw t;
            }
            if(e&&!e.__userNotified&&window.__uploadBody.kind(e)==='file'){
                var say=window.__uploadBody.text(e);
                if(say&&typeof window.__toast==='function'){
                    try{window.__toast(say,'error');e.__userNotified=true;}catch(_){}
                }
            }
            throw e;
        });
    };
    door.__fileDoor=true;
    window.fetch=door;
})();
/* __uploadBody:end */

// ── window.__serverFile(url, body, opts) — POST to a server lane, get a file ──
//
// Every tool with a server lane wrote its own `fetch(...).then(r => { if(!r.ok)
// throw; return r.blob(); })`. That shape has a hole none of the 15 call sites
// closed, because it is invisible from the tool's side:
//
//   A lane refusal (file over the lane ceiling, daily cap, expired CSRF) is
//   rendered as JSON *only* when the request declares itself machine-driven.
//   A bare fetch() sends `Accept: */*`, so both the central gate
//   (ToolsController::serverLaneRefusal → expectsJson()||ajax()) and Laravel's
//   own ValidationException answer a **302 back to the page**. fetch() follows
//   redirects silently, so the tool sees `res.ok === true` and calls .blob() —
//   and hands the user the site's HTML homepage named `studio-mix.mp3`.
//   Measured 2026-08-25 on /recording-studio: 655 KB of `<!doctype html>`
//   delivered as the user's audio, with «Скачано!» on screen.
//
// So the primitive owns the CONTRACT, not just the call:
//   • `X-Requested-With: XMLHttpRequest` — makes the refusal come back as JSON
//     with its real status (413 «Файл больше 50 МБ», 402, 429) and its already
//     translated message, instead of a redirect.
//   • `redirect:'error'` — a redirect is NEVER a valid file response. Any server
//     that still answers one fails the fetch loudly instead of laundering a web
//     page into the user's file.
//   • the response is checked to be bytes, not a document, before it is handed
//     back — belt for anything that slips past both of the above.
//
// Throws an Error carrying `.status`, `.serverMessage` (localized, from the
// lane) and `.kind` so a tool can branch — e.g. fall back to a local encode.
window.__serverFile=function(url,body,opts){
    opts=opts||{};
    var headers=Object.assign({'X-Requested-With':'XMLHttpRequest'},opts.headers||{});
    if(!headers['X-CSRF-TOKEN']){
        var m=document.querySelector('meta[name="csrf-token"]');
        if(m&&m.content)headers['X-CSRF-TOKEN']=m.content;
    }
    var init={method:opts.method||'POST',body:body,headers:headers,
              credentials:opts.credentials||'same-origin',redirect:'error'};
    if(opts.signal)init.signal=opts.signal;
    // The file travels from the page's own copy, not from the picker's handle
    // (see __uploadBody): a lane must not die on a file the tool has just read.
    // And it travels AHEAD, in slices (__uploadFetch), because the lane is ours:
    // a whole file in one long POST is what a phone's uplink loses. A tool may
    // pass `slices:false`; `onUploadProgress(sent, total)` sees the bytes.
    var form=typeof FormData!=='undefined'&&body instanceof FormData&&typeof window.__uploadFetch==='function';
    var sent=form?window.__uploadFetch(url,body,init,{slices:opts.slices,signal:opts.signal,onProgress:opts.onUploadProgress})
        :fetch(url,init);
    return sent.then(function(res){
        if(!res.ok){
            return res.text().catch(function(){return '';}).then(function(t){
                var msg='',kind='';
                try{var j=JSON.parse(t);msg=j.error||j.message||'';kind=j.error_kind||'';}catch(e){}
                var e=new Error(msg||('http '+res.status));
                e.status=res.status;e.serverMessage=msg;e.kind=kind;e.isServerLane=true;
                throw e;
            });
        }
        var ct=(res.headers.get('content-type')||'').toLowerCase();
        // opts.json — an ASYNC lane answers with a JSON ticket (202 + job_id), not
        // a file; the refusal parsing above is shared, this is the success shape.
        if(opts.json){
            if(ct.indexOf('application/json')!==0){
                var ej=new Error('server returned '+(ct||'no content-type')+', not JSON');
                ej.status=res.status;ej.kind='not_json';ej.isServerLane=true;throw ej;
            }
            return res.json();
        }
        if(ct.indexOf('text/html')===0||ct.indexOf('application/json')===0){
            var e2=new Error('server returned a document, not a file');
            e2.status=res.status;e2.kind='not_a_file';e2.isServerLane=true;
            throw e2;
        }
        return res.blob().then(function(b){
            if(!b||!b.size){
                var e3=new Error('empty response');e3.status=res.status;e3.kind='empty';e3.isServerLane=true;throw e3;
            }
            return b;
        });
    });
};

// ── window.__dlGesture / window.__dlHold — one download per user interaction ──
//
// MEASURED on real Chrome 152, no CDP (2026-08-29). Playwright CANNOT see this:
// it sends Browser.setDownloadBehavior and the browser's gate is bypassed, so a
// green Playwright suite proves nothing about it. The rig is a throwaway Chrome
// profile driven by real mouse input, read back from the profile's download
// folder and from Default/History:
//
//   one blob-anchor download per real click ......... 7 / 7 delivered
//                                                     (even 8 s after the click)
//   two   downloads from ONE click .................. 1 / 2 delivered
//   three downloads from ONE click .................. 1 / 3 delivered
//
// The extra file does not merely vanish — it POISONS THE TAB. Afterwards even a
// perfectly well-behaved SINGLE download keeps being dropped: measured 2/2 lost
// on a DIFFERENT PAGE of the same origin (9 s and 18 s after a same-origin
// navigation), and again after a reload; recovery is erratic (one ~60 s idle
// window healed it, a single download 10 s later was dropped again). Nothing
// throws, no History row is written, no browser UI appears.
//
// And our JS is blind to all of it: a.click() returns normally, __downloadFile
// resolves true, telemetry records via:classic_native, and the user is shown
// «Файл сохранён». That blindness is why this stayed invisible. Prod, 14 days:
// 2 021 sessions fire a same-second batch of DIFFERENT files (a program loop,
// not a double-click), and 12 866 later downloads across 118 tools happen in a
// tab one of those batches had already poisoned.
//
// The tool that finally got reported — /harmony-generator, 2026-08-27, desktop
// YaBrowser, «появляется зелёное сообщение, что файл сохранён, но ничего не
// скачивается и даже окно скачивания не появляется» — downloads exactly ONE
// file per click and was never at fault. It inherited a dead tab.
//
// A tool cannot see this: it does not know what the rest of the page already
// spent. The primitive can. So the budget lives HERE — at most one delivery per
// user interaction. Anything beyond it is HELD (never clicked, never counted as
// delivered) and offered back on a FRESH gesture through __batchDeliver, where
// a folder write is not a download at all and a ZIP is exactly one.
window.__dlGesture=(function(){
    var epoch=0,spent=-1,key='';
    function bump(ev){
        // Synthetic events do not re-arm the browser's budget either, so a page
        // dispatching its own clicks must not re-arm ours.
        if(ev&&ev.isTrusted===false)return;
        epoch++;
    }
    try{
        var o={capture:true,passive:true};
        window.addEventListener('pointerdown',bump,o);
        window.addEventListener('keydown',bump,o);
        window.addEventListener('touchstart',bump,o);
    }catch(e){
        try{window.addEventListener('mousedown',bump,true);window.addEventListener('keydown',bump,true);}catch(_){}
    }
    return {
        epoch:function(){return epoch;},
        // true = this interaction still holds its one download, and takes it.
        claim:function(k){ if(spent===epoch)return false; spent=epoch;key=k||'';return true; },
        // The delivery this interaction already spent, so a caller firing the
        // SAME file twice is recognisable as a double-fire bug rather than a
        // lost file — dropping that one silently is correct, nothing is missing.
        spentKey:function(){ return spent===epoch?key:''; },
        // Is this interaction's one delivery already spoken for? Distinct from
        // spentKey(): a caller needs to know THAT the slot is gone without
        // guessing at whose it is (an empty key is a legitimate claim).
        taken:function(){ return spent===epoch; },
        // The chosen channel refused before initiating anything (picker
        // cancelled, over the lane ceiling): the browser's slot was never spent,
        // so ours must not stay spent either.
        release:function(){ if(spent===epoch){spent=-1;key='';} }
    };
})();

// ── window.__ensureBatchDeliver() — fetch the batch primitive on demand ──
// It is 10 KB and only the tools that deliver batches load it up front. But a
// batch can turn up on ANY page — the gesture budget above holds files on tools
// that never expected to, and the shared disk/FSA engines fall back to «one
// archive» from inside a tool that never declared the dependency. «Take the
// first, sort the rest out yourself» is not a delivery, so the primitive is
// fetched the moment it is actually needed — never on the pages that never need
// it. The versioned path comes from the layout (window.__BATCH_DELIVER_SRC):
// a runtime must not build `/js/...` by hand, because static assets are served
// with a year-long max-age and a returning visitor would keep the old copy.
// Resolves when the primitive is usable; rejects if it cannot be had.
/* __viewTransition:begin */
/* Одна дверь к View Transitions API. document.startViewTransition() отдаёт объект
   с ТРЕМЯ промисами (ready / updateCallbackDone / finished), и когда переход
   пропущен — вкладка скрыта, другой переход его вытеснил, документ не «visible» —
   ready и finished ОТВЕРГАЮТСЯ InvalidStateError. Тул, который их не слушает,
   получает unhandledrejection на каждом таком действии (TIMBRICA-FRONTEND-J3,
   /puzzle-maker, 07.09.2026) — а слушал ровно один из пятнадцати вызывающих.
   Контракт: fn выполняется РОВНО один раз и в порядке вызова; анимация —
   украшение и не имеет права ломать действие. Возвращает переход или null
   (null = fn уже выполнен синхронно, анимации не будет). Сторожа:
   tests/js/view-transition-hushes-rejections.test.mjs +
   ViewTransitionGoesThroughThePrimitiveTest. */
window.__viewTransition=function(fn,opts){
    if(typeof fn!=='function')return null;
    opts=opts||{};
    var can=false;
    try{
        can=typeof document.startViewTransition==='function'
            &&document.visibilityState==='visible'
            &&!opts.skip
            &&!(window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }catch(_){can=false;}
    if(!can){fn();return null;}
    var t=null;
    try{t=document.startViewTransition(fn);}
    catch(_){fn();return null;}
    var hush=function(){};
    try{
        if(t){
            if(t.ready&&t.ready.catch)t.ready.catch(hush);
            if(t.finished&&t.finished.catch)t.finished.catch(hush);
            if(t.updateCallbackDone&&t.updateCallbackDone.catch)t.updateCallbackDone.catch(hush);
        }
    }catch(_){}
    return t;
};
/* __viewTransition:end */
/* __visibleTimeout:begin */
/* Дедлайн по ВИДИМОМУ времени. Любое ожидание «медиа должно было уже
   ответить» обязано считать только то время, когда вкладку видно.

   Chromium НЕ загружает медиа в скрытой вкладке, которая ещё ничего не
   проигрывала (components/no_state_prefetch/renderer/no_state_prefetch_utils.cc,
   DeferMediaLoad: «Don't allow autoplay/autoload of media resources in a page
   that is hidden … and never played any media before»). Скрытая — это и другая
   вкладка впереди, и свёрнутое окно, и окно, целиком закрытое другим окном.
   Замер 23.09.2026, настоящие Chrome 153 и Яндекс 26.8 по CDP: пока вкладка
   скрыта, ни <video src=blob:>, ни MediaSource не открываются вообще (20+ с,
   readyState 0, networkState 2), на видимой — 14 мс. Таймер на setTimeout
   превращал «человек отвлёкся» в «файл не открывается»: /video-player 30 д —
   28 сессий sourceopen_timeout, у заявителя 22.09 (#1726) все четыре попытки.

   __visibleTimeout(fn, ms) — fn сработает после ms миллисекунд видимого
   времени; возвращает отмену. __visibleNow() — монотонные часы видимого
   времени (разница с performance.now() за ожидание = сколько вкладка была
   скрыта). Сторож — tests/js/visible-timeout.test.mjs. */
(function(){
    var doc=document;
    function hidden(){try{return doc.visibilityState==='hidden'||doc.hidden===true;}catch(_){return false;}}
    var perf=(window.performance&&typeof performance.now==='function')?function(){return performance.now();}:function(){return Date.now();};
    var acc=0,since=hidden()?-1:perf(),live=[];
    function now(){return acc+(since>=0?perf()-since:0);}
    function drop(t){var i=live.indexOf(t);if(i>=0)live.splice(i,1);}
    function arm(t){
        clearTimeout(t.h);t.h=0;
        if(t.done||hidden())return;
        t.h=setTimeout(function(){fire(t);},Math.max(0,t.due-now()));
    }
    function fire(t){
        t.h=0;
        if(t.done||hidden())return;
        if(now()+1<t.due){arm(t);return;}
        t.done=true;drop(t);
        try{t.fn();}catch(e){setTimeout(function(){throw e;},0);}
    }
    try{doc.addEventListener('visibilitychange',function(){
        var i;
        if(hidden()){
            if(since>=0){acc+=perf()-since;since=-1;}
            for(i=0;i<live.length;i++){clearTimeout(live[i].h);live[i].h=0;}
        }else{
            if(since<0)since=perf();
            var copy=live.slice();
            for(i=0;i<copy.length;i++)arm(copy[i]);
        }
    });}catch(_){}
    window.__visibleNow=now;
    window.__visibleTimeout=function(fn,ms){
        var t={fn:fn,due:now()+Math.max(0,+ms||0),h:0,done:false};
        live.push(t);arm(t);
        return function(){if(t.done)return;t.done=true;clearTimeout(t.h);drop(t);};
    };
})();
/* __visibleTimeout:end */
/* __patientFetch:begin */
/* Запрос к НАШЕМУ серверу, который мобильная связь может оборвать, — с
   терпением, а не до первого срыва.

   fetch() отклоняется («Failed to fetch»), когда ответа не пришло совсем:
   сорвалось соединение. На телефоне это обычное дело, и длится оно минутами.
   Страница «нет связи» (offline.html) 88 % своих показов получает при
   navigator.onLine = true и conn 4g (замер 20–22.09.2026), так что 'online'
   ждать бесполезно. Дверь загрузки файлов намерила, сколько тянется срыв: у
   передач, дошедших после него, самый долгий перерыв ≤ 1 мин у 76 %, ≤ 5 мин
   у 93,5 %, ≤ 10 мин у 97,2 % (SLICE_NO_PROGRESS_MS ниже). Тул, отвечавший
   на первый же срыв «сервер недоступен», говорил неправду о сервере и бросал
   работу, которую повтор через минуту сделал бы. Обращение 25.10.2026 (чат
   719, /text-to-speech, Android, приложение Яндекса): двенадцать минут ни
   один запрос телефона не дошёл до nginx, страница сказала
   «TTS server unreachable», человек написал «2-й день вылазит».

   __patientFetch(input, init, opts) — как fetch, но срыв повторяется по
   лестнице offline.html (1,5 · 3 · 5 · 8 · 12 · 20 · 30 с); пауза кончается
   раньше, когда браузер говорит 'online' или вкладку снова видно, — пока с
   первого срыва не пройдёт opts.patienceMs АКТИВНОГО времени (__activeNow;
   по умолчанию 10 мин). Любой HTTP-ответ возвращается как есть: связь есть,
   статус — слово сервера.
     opts.signal      отмена: ожидание и запрос в полёте — сразу AbortError;
     opts.timeoutMs   сколько ждать заголовков одной попытки (0 — сколько
                      даст браузер); попытка, вышедшая по сроку, — тоже срыв;
     opts.onWait({attempt, waitedMs})  связи нет — тул говорит это человеку;
     opts.onBack({attempts, waitedMs}) первый ответ после срыва.
   Не дождались — отклоняется последней ошибкой с e.linkLost = true,
   e.waitedMs, e.attempts.
   ⚠️ Только для запросов, которые МОЖНО повторить: GET или POST, который
   сервер узнаёт (/api/tts/synthesize — по отпечатку текста). Тело — строка,
   FormData или Blob: поток второй раз не отправить.
   Сторож — tests/js/patient-fetch.test.mjs. */
(function(){
    var LADDER=[1500,3000,5000,8000,12000,20000,30000];
    var PATIENCE_MS=600000;
    function abortError(){
        try{return new DOMException('The operation was aborted.','AbortError');}
        catch(e){var a=new Error('The operation was aborted.');a.name='AbortError';return a;}
    }
    function activeNow(){return typeof window.__activeNow==='function'?window.__activeNow():Date.now();}
    function hidden(){try{return document.visibilityState==='hidden'||document.hidden===true;}catch(_){return false;}}
    window.__patientFetch=function(input,init,opts){
        opts=opts||{};
        var signal=opts.signal||null;
        var patience=(opts.patienceMs!=null&&+opts.patienceMs>=0)?+opts.patienceMs:PATIENCE_MS;
        var timeoutMs=+opts.timeoutMs>0?+opts.timeoutMs:0;
        var doFetch=opts.fetchImpl||function(i,n){return window.fetch(i,n);};
        var attempt=0,since=0,step=0;
        // One pause of the ladder. Ends early on 'online', on the tab coming
        // back into view (the person is looking — ask now), and on cancel.
        function pause(ms){
            return new Promise(function(res){
                var done=false,t=null;
                function fin(why){
                    if(done)return;done=true;clearTimeout(t);
                    try{window.removeEventListener('online',onLine);}catch(e){}
                    try{document.removeEventListener('visibilitychange',onVis);}catch(e){}
                    try{if(signal)signal.removeEventListener('abort',onAbort);}catch(e){}
                    res(why);
                }
                function onLine(){fin('online');}
                function onVis(){if(!hidden())fin('visible');}
                function onAbort(){fin('abort');}
                try{window.addEventListener('online',onLine);}catch(e){}
                try{document.addEventListener('visibilitychange',onVis);}catch(e){}
                try{if(signal)signal.addEventListener('abort',onAbort);}catch(e){}
                t=setTimeout(function(){fin('timer');},ms);
            });
        }
        // One attempt: the caller's cancel and this attempt's own deadline
        // reach the request through one controller.
        function one(){
            var i2=init,ac=null,timer=null,timedOut=false,relay=null;
            if((timeoutMs||signal)&&typeof AbortController==='function'){
                ac=new AbortController();
                i2={};
                if(init)for(var k in init)if(Object.prototype.hasOwnProperty.call(init,k))i2[k]=init[k];
                i2.signal=ac.signal;
                if(signal){relay=function(){try{ac.abort();}catch(e){}};try{signal.addEventListener('abort',relay);}catch(e){}}
                if(timeoutMs)timer=setTimeout(function(){timedOut=true;try{ac.abort();}catch(e){}},timeoutMs);
            }
            function clean(){
                if(timer)clearTimeout(timer);
                try{if(relay)signal.removeEventListener('abort',relay);}catch(e){}
            }
            var p;
            try{p=Promise.resolve(doFetch(input,i2));}catch(e){p=Promise.reject(e);}
            return p.then(function(r){clean();return r;},function(e){
                clean();
                if(timedOut&&!(signal&&signal.aborted)){
                    var t=new TypeError('Failed to fetch: no answer in '+timeoutMs+' ms');
                    t.timedOut=true;throw t;
                }
                throw e;
            });
        }
        function go(){
            if(signal&&signal.aborted)return Promise.reject(abortError());
            attempt++;
            return one().then(function(r){
                if(since){try{if(opts.onBack)opts.onBack({attempts:attempt,waitedMs:Math.round(activeNow()-since)});}catch(e){}}
                return r;
            },function(e){
                if(signal&&signal.aborted)throw abortError();
                if(e&&e.name==='AbortError')throw e;      // not ours to repeat
                var t=activeNow();
                if(!since)since=t;
                var waited=t-since;
                if(waited>=patience){
                    try{e.linkLost=true;e.waitedMs=Math.round(waited);e.attempts=attempt;}catch(_){}
                    throw e;
                }
                try{if(opts.onWait)opts.onWait({attempt:attempt,waitedMs:Math.round(waited)});}catch(_){}
                var ms=LADDER[Math.min(step,LADDER.length-1)];step++;
                return pause(ms).then(function(why){
                    if(why==='abort')throw abortError();
                    if(why!=='timer')step=0;               // a sign of life: start the ladder over
                    return go();
                });
            });
        }
        return go();
    };
    window.__patientFetch.LADDER_MS=LADDER;
    window.__patientFetch.PATIENCE_MS=PATIENCE_MS;
})();
/* __patientFetch:end */
/* __vendorScript:begin */
/* Вендорная библиотека, которую тул грузит ПО НАЖАТИЮ (PDF, PPTX, архив, разбор
   файла), — своя копия первой, на обеих половинах сайта (17.10.2026, волна 3).

   До этой даты такие загрузки шли тремя путями, и у всех чужой хост стоял ПЕРВЫМ,
   хотя файл лежит у нас (в git и на обоих origin'ах, побайтно):
     · __CDN.script/url('jsdelivr', …) — /typing-trainer (jsPDF), /slideshow-maker
       (pptxgenjs), /sound-meter (html2canvas + jsPDF): intl — jsdelivr, ru —
       прокси Бегета, который на холодном кэше идёт на тот же jsdelivr; своей
       копии в этом пути НЕТ вовсе;
     · списки [cdn('jsdelivr', …), cdn_fallback, своя] с сервера — пароли и ключи
       (zxcvbn, kdbxweb, openpgp, jsQR), словари BIP-39: своя последней;
     · App\Support\Cdn::orderFor — intl внешние первыми (/russian-transliteration).
   Замер (vendor-click-bench, 10 тулов × ru/en × Chromium/WebKit, настоящее
   нажатие): обычный случай — 34 запроса к jsdelivr за своими же файлами; CDN
   недоступен (блокировка, сбой) — /typing-trainer, /slideshow-maker и
   /sound-meter мертвы 12 из 12; у /sound-meter «запасной» менял src уже
   вставленного тега — второй загрузки браузер не делает, и при отказе прокси
   кнопка PDF висела навсегда (стенд, оба движка).

   window.__vendorScript(src, opts) → Promise<адрес, с которого пришла библиотека>
     src  — путь своей копии ('/vendor/jspdf/jspdf.umd.min.js') или готовая
            цепочка с сервера (cdn_chain()) — массив адресов по порядку;
     opts.mirror — внешнее зеркало, ПОСЛЕДНИЙ адрес (для строки-пути);
     opts.expect — имя глобала ('jspdf') или функция: ответ 200 без библиотеки
            (страница-заглушка портала, прокси) — тот же отказ адреса;
     opts.label  — имя в телеметрии (asset_fallback / asset_unreachable).
   Цепочка для пути: своя → свой запасной (window.__LIGHT_FB: ru-прод — второй
   origin, intl и стенд — тот же файл с ?fb=1, другой ключ кэша) → зеркало. Каждая
   попытка — не дольше 30 с ВИДИМОГО времени (тег без onload и onerror вешал
   кнопку навсегда — возврат #37); смена адреса — asset_fallback{asset,index};
   ни один не ответил — tool_diagnostic{issue:asset_unreachable} и
   __aeCritical('asset_unreachable') (видно и без согласия). Уже загружено
   (expect) — сразу; один и тот же src в полёте — один промис.
   Разбор — docs/why/primitives/vendor-own-copy.md, сторожа —
   tests/js/vendor-script.test.mjs, VendorOwnCopyFirstTest. */
(function(){
    var flying={};
    function after(fn,ms){
        if(typeof window.__visibleTimeout==='function')return window.__visibleTimeout(fn,ms);
        var h=setTimeout(fn,ms);return function(){clearTimeout(h);};
    }
    function ownFallback(p){
        var t=window.__LIGHT_FB;
        if(typeof t==='string'&&t.indexOf('{p}')>=0)return t.replace('{p}',String(p).replace(/^\//,''));
        return p+(String(p).indexOf('?')<0?'?':'&')+'fb=1';
    }
    function chainOf(src,mirror){
        var list=Array.isArray(src)?src.slice():[src,ownFallback(src)];
        if(mirror)list.push(mirror);
        var seen={},out=[];
        for(var i=0;i<list.length;i++){var u=list[i];if(u&&!seen[u]){seen[u]=1;out.push(String(u));}}
        return out;
    }
    function have(expect){
        if(!expect)return false;
        try{return typeof expect==='function'?!!expect():(window[expect]!=null);}catch(_){return false;}
    }
    function slug(){try{var m=document.querySelector('meta[name="tool-slug"]');return m?m.content:'';}catch(_){return '';}}
    function foreign(u){try{return new URL(u,location.href).origin!==location.origin;}catch(_){return false;}}
    function nonce(){try{var s=document.querySelector('script[nonce]');return s?(s.nonce||s.getAttribute('nonce')||''):'';}catch(_){return '';}}
    window.__vendorScript=function(src,opts){
        opts=opts||{};
        var expect=opts.expect;
        if(have(expect))return Promise.resolve(null);
        var urls=chainOf(src,opts.mirror);
        if(!urls.length)return Promise.reject(new Error('vendor_no_url'));
        var label=String(opts.label||urls[0].split('?')[0].split('/').slice(-2,-1)[0]||'vendor').slice(0,40);
        var ms=opts.attemptMs>0?opts.attemptMs:30000;
        var key=urls[0];
        if(flying[key])return flying[key];
        var p=new Promise(function(resolve,reject){
            var i=0;
            function next(err){
                if(i>=urls.length){
                    try{window.__ae&&window.__ae('tool_diagnostic',{issue:'asset_unreachable',asset:label,tried:urls.length});}catch(_){}
                    try{window.__aeCritical&&window.__aeCritical('asset_unreachable',{slug:slug(),asset:label,tried:urls.length});}catch(_){}
                    reject(err||new Error('vendor_unreachable:'+label));
                    return;
                }
                var k=i++,url=urls[k],done=false,stop=null;
                var s=document.createElement('script');
                function fail(e,stalled){
                    if(done)return;done=true;if(stop)stop();
                    if(stalled){try{window.__aeCritical&&window.__aeCritical('asset_stalled',{slug:slug(),asset:label,index:k,url:url.slice(0,160)});}catch(_){}}
                    next(e);
                }
                s.async=true;
                if(foreign(url))s.crossOrigin='anonymous';
                var n=nonce();if(n)s.setAttribute('nonce',n);
                s.onload=function(){
                    if(done)return;
                    if(expect&&!have(expect)){fail(new Error(label+'_missing:'+url));return;}
                    done=true;if(stop)stop();
                    if(k>0){try{window.__ae&&window.__ae('asset_fallback',{slug:slug(),asset:label,index:k,url:url.slice(0,160)});}catch(_){}}
                    resolve(url);
                };
                s.onerror=function(){fail(new Error('vendor_load_failed:'+url));};
                stop=after(function(){fail(new Error('vendor_stalled:'+url),true);},ms);
                s.src=url;
                (document.head||document.documentElement).appendChild(s);
            }
            next(null);
        });
        flying[key]=p;
        p.then(null,function(){delete flying[key];});
        return p;
    };
})();
/* __vendorScript:end */
/* __cameraFrames:begin */
/* A camera stream on a <video>: wait for its FRAMES, never for play().

   On a stream that never sends a frame — a privacy shutter, a wedged driver, a
   virtual device, a camera another app holds — the promise of video.play()
   never settles at all: readyState stays 0 (measured 2026-10-08). Every tool
   that wrote `await video.play()` before its "no frames" watchdog never armed
   the watchdog: /qr-scanner had 52 sessions in 30 days with the camera granted
   and a spinner for good, and the 6-second watchdog /boarding-pass keeps for
   exactly this case never ran. Seventeen camera sites carried the same line.

   And a deadline is not a verdict: a LIVE camera can be slow. On /qr-scanner
   (30 days, 23 803 starts) the first frame came within 1 s of the stream in
   95.5 %, but after 6–10 s in 21 starts and after ≥ 10 s in 100 — more than the
   cameras that never send one. So at the deadline the person is TOLD (onSlow)
   and the wait goes on; a late frame still answers true.

   __cameraFrames(video, {stream, timeoutMs, onSlow, ceilingMs, control}) puts the stream
   on the element (muted, inline) and calls play() without waiting on it. It
   resolves TRUE at the first real frame, however late; calls onSlow() once
   after timeoutMs (6 s) of VISIBLE time without one — a background tab loads
   no media, and that is not a dead camera; resolves FALSE when the stream is
   taken off the element, its track ends or is stopped, or after ceilingMs
   (90 s) of visible time. It never rejects. CameraManager#show is the same
   wait plus the camera door's own words; a tool that opens its camera through
   the door uses that. */
(function(){
    function hasFrame(v){ return !!(v && v.videoWidth > 0 && v.videoHeight > 0 && v.readyState >= 2); }
    function after(fn, ms){
        if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
        var t = setTimeout(fn, ms); return function(){ clearTimeout(t); };
    }
    window.__cameraFrames = function(video, opts){
        opts = opts || {};
        if (!video) return Promise.resolve(false);
        if (opts.stream) { try { video.srcObject = opts.stream; } catch (_) {} }
        try { video.muted = true; video.playsInline = true; video.setAttribute('playsinline', ''); } catch (_) {}
        var played; try { played = video.play(); } catch (_) {}
        if (played && typeof played.catch === 'function') played.catch(function(){});
        if (hasFrame(video)) return Promise.resolve(true);
        var src = video.srcObject, track = null;
        try { track = src && src.getVideoTracks ? (src.getVideoTracks()[0] || null) : null; } catch (_) {}
        var slowMs = typeof opts.timeoutMs === 'number' && opts.timeoutMs > 0 ? opts.timeoutMs : 6000;
        var ceilMs = typeof opts.ceilingMs === 'number' && opts.ceilingMs > 0 ? opts.ceilingMs : 90000;
        return new Promise(function(resolve){
            var done = false, tick = 0, cancelSlow = null, cancelCeil = null;
            var evs = ['loadeddata', 'canplay', 'playing', 'resize', 'timeupdate'];
            var fin = function(ok){
                if (done) return; done = true;
                try { if (cancelSlow) cancelSlow(); if (cancelCeil) cancelCeil(); } catch (_) {}
                if (tick) clearInterval(tick);
                for (var i = 0; i < evs.length; i++) { try { video.removeEventListener(evs[i], on); } catch (_) {} }
                if (track) { try { track.removeEventListener('ended', ended); } catch (_) {} }
                resolve(ok);
            };
            var on = function(){ if (hasFrame(video)) fin(true); };
            var ended = function(){ fin(hasFrame(video)); };
            /* The tool stopped the camera (track.stop() fires no 'ended') or put
               another stream on the element: this wait is over, with no frame. */
            var gone = function(){ return video.srcObject !== src || !!(track && track.readyState === 'ended'); };
            var check = function(){ if (done) return; if (hasFrame(video)) fin(true); else if (gone()) fin(false); };
            for (var i = 0; i < evs.length; i++) { try { video.addEventListener(evs[i], on); } catch (_) {} }
            if (track) { try { track.addEventListener('ended', ended); } catch (_) {} }
            /* A presented frame is the answer the events can miss (a stream that
               was already flowing when we attached): the frame callback asks the
               element itself, and a slow poll (no per-frame loop for 90 s) also
               notices a camera the tool has stopped meanwhile. */
            try {
                if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(function(){ check(); });
            } catch (_) {}
            tick = setInterval(check, 500);
            /* The owner of the camera can end the wait at once (CameraManager#stop). */
            if (opts.control) opts.control.abort = function(){ fin(false); };
            cancelSlow = after(function(){
                if (done || typeof opts.onSlow !== 'function') return;
                try { opts.onSlow(); } catch (_) {}
            }, slowMs);
            cancelCeil = after(function(){ fin(hasFrame(video)); }, ceilMs);
        });
    };
})();
/* __cameraFrames:end */

/* __trackConstraint:begin */
/* ── __trackConstraint — the one door to MediaStreamTrack.applyConstraints ──
   window.__trackConstraint(track, set, {waitMs}) → Promise<{ok, reason, error}>
   (never rejects). `set` is ONE advanced set ({zoom: 2}, {torch: true}).

   Measured 16.10.2026. /qr-scanner: zoom refused in 96 of 635 zoom sessions in
   seven days (15 %), torch in 5 of 226, all with «OperationError: The associated
   Track is in an invalid state». On Chromium's fake camera that exact message is
   what applyConstraints answers while the track is NOT ACTIVE — muted or
   disabled; 40 overlapping calls all pass, an ended track answers «UnknownError:
   setPhotoOptions failed». A phone camera goes muted for a moment on its own
   (the capture session reconfiguring after a zoom step, a notification, the
   system taking the camera), and a slider fires many steps in that moment.

   So the door: one call in flight per track; the newest value of each key wins
   (a slider's intermediate steps are dropped, zoom and torch merge); while the
   track is muted or disabled it waits for 'unmute' (up to waitMs, 4 s) and then
   applies the newest; a call refused with «invalid state» waits for 'unmute' and
   is tried once more. ok:false carries reason 'ended' | 'muted' | 'refused'. */
window.__trackConstraint = function (track, set, opts) {
    if (!track || typeof track.applyConstraints !== 'function' || !set) {
        return Promise.resolve({ ok: false, reason: 'unsupported', error: null });
    }
    var waitMs = (opts && opts.waitMs) || 4000;
    var st = track.__tmbCons;
    if (!st) {
        st = { busy: false, pending: null, waiters: [], retried: false, waiting: false };
        try { Object.defineProperty(track, '__tmbCons', { value: st, configurable: true }); } catch (_) { track.__tmbCons = st; }
    }
    var k;
    if (!st.pending) st.pending = {};
    for (k in set) { if (Object.prototype.hasOwnProperty.call(set, k)) st.pending[k] = set[k]; }
    var res = new Promise(function (resolve) { st.waiters.push(resolve); });
    var inactive = function () {
        try { return track.muted === true || track.enabled === false; } catch (_) { return false; }
    };
    var settle = function (waiters, r) { for (var i = 0; i < waiters.length; i++) { try { waiters[i](r); } catch (_) {} } };
    var run = function () {
        if (st.busy || st.waiting || !st.pending) return;
        if (track.readyState === 'ended') {
            var w0 = st.waiters; st.waiters = []; st.pending = null;
            settle(w0, { ok: false, reason: 'ended', error: null });
            return;
        }
        if (inactive()) {
            st.waiting = true;
            var done = false;
            var finish = function (resumed) {
                if (done) return;
                done = true;
                try { track.removeEventListener('unmute', onUnmute); } catch (_) {}
                clearTimeout(timer);
                clearInterval(poll);
                st.waiting = false;
                if (resumed && !inactive()) { run(); return; }
                var w1 = st.waiters; st.waiters = []; st.pending = null; st.retried = false;
                settle(w1, { ok: false, reason: 'muted', error: null });
            };
            var onUnmute = function () { finish(true); };
            try { track.addEventListener('unmute', onUnmute); } catch (_) {}
            var timer = setTimeout(function () { finish(false); }, waitMs);
            // 'unmute' does not cover a track the page re-enables, and is not
            // promised everywhere: look again four times a second too
            var poll = setInterval(function () { if (!inactive()) finish(true); }, 250);
            return;
        }
        var now = st.pending, waiters = st.waiters;
        st.pending = null; st.waiters = []; st.busy = true;
        var p;
        try { p = Promise.resolve(track.applyConstraints({ advanced: [now] })); } catch (e) { p = Promise.reject(e); }
        p.then(function () {
            st.busy = false; st.retried = false;
            settle(waiters, { ok: true, reason: '', error: null });
            run();
        }, function (err) {
            st.busy = false;
            var invalid = !!err && (err.name === 'OperationError' || err.name === 'InvalidStateError') && /invalid state/i.test(String(err.message || ''));
            if (invalid && !st.retried && track.readyState !== 'ended') {
                // went inactive between the check and the call: once more, after it is back
                st.retried = true;
                var merged = {}, kk;
                for (kk in now) { if (Object.prototype.hasOwnProperty.call(now, kk)) merged[kk] = now[kk]; }
                if (st.pending) { for (kk in st.pending) { if (Object.prototype.hasOwnProperty.call(st.pending, kk)) merged[kk] = st.pending[kk]; } }
                st.pending = merged;
                st.waiters = waiters.concat(st.waiters);
                if (!inactive()) {
                    // not visibly muted: give the camera a beat before the second try
                    st.waiting = true;
                    setTimeout(function () { st.waiting = false; run(); }, 250);
                    return;
                }
                run();
                return;
            }
            st.retried = false;
            settle(waiters, { ok: false, reason: track.readyState === 'ended' ? 'ended' : 'refused', error: err || null });
            run();
        });
    };
    run();
    return res;
};
/* __trackConstraint:end */
/* __drawCameraFrame:begin */
/* A live camera frame onto a canvas: true, or false = «no frame right now».
   WebKit throws InvalidStateError from drawImage(video) when the system
   interrupts capture (app switcher, Control Center, a call): the track goes
   `muted`, not `ended`, and readyState >= 2 does not rule that out, so a check
   BEFORE the draw is always late. Uncaught, the exception killed whatever it
   was in — /qr-scanner stopped scanning (its busy flag stayed set), the shutter
   of /online-mirror died (Sentry J4, K0: all iOS, after three rounds of
   readyState checks; none since 01.10, when both moved onto this). A muted or
   ended track is not even drawn: on iOS 18.7 a draw from an ended track
   poisons the canvas and the NEXT getImageData throws (Sentry 9E,
   /video-surveillance). Any other error is a real defect and goes on.
   CameraManager.drawFrame is this same function; a press (shutter, snapshot,
   export) that got no frame says so with __cameraNoFrame(). */
window.__drawCameraFrame = function (ctx, video) {
    if (!ctx || !video || !(video.videoWidth > 0) || video.readyState < 2) return false;
    var so = video.srcObject;
    var tracks = (so && typeof so.getVideoTracks === 'function') ? so.getVideoTracks() : [];
    for (var i = 0; i < tracks.length; i++) {
        if (tracks[i].readyState === 'ended' || tracks[i].muted) return false;
    }
    try {
        ctx.drawImage.apply(ctx, Array.prototype.slice.call(arguments, 1));
        return true;
    } catch (e) {
        if (e && (e.name === 'InvalidStateError' || e.code === 11)) return false;
        throw e;
    }
};
/* The camera is there and the frame was not: the next press a second later
   usually works, and the door's «close Zoom and reload» would be wrong advice. */
window.__cameraNoFrame = function () {
    var L = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2).toLowerCase();
    var M = {
        ru: 'Камера на миг не отдала кадр. Подождите секунду и нажмите ещё раз.',
        en: 'The camera skipped a frame just then. Wait a second and press again.',
        de: 'Die Kamera hat gerade kein Bild geliefert. Warten Sie eine Sekunde und drücken Sie erneut.',
        es: 'La cámara no entregó una imagen en ese momento. Espera un segundo y presiona de nuevo.',
        fr: 'La caméra n’a pas fourni d’image à cet instant. Attendez une seconde et appuyez à nouveau.',
        uk: 'Камера на мить не віддала кадр. Зачекайте секунду й натисніть ще раз.',
        pt: 'A câmera não entregou uma imagem naquele instante. Aguarde um segundo e toque de novo.',
        id: 'Kamera tidak memberi gambar barusan. Tunggu sebentar lalu tekan lagi.',
        ko: '방금 카메라가 화면을 보내지 않았습니다. 잠시 후 다시 눌러 주세요.',
        ar: 'لم تُرسل الكاميرا صورة في تلك اللحظة. انتظر ثانية واضغط مرة أخرى.',
        tr: 'Kamera o an bir kare vermedi. Bir saniye bekleyip tekrar basın.'
    };
    return M[L] || M.en;
};
/* __drawCameraFrame:end */
/* __canvasRefused:begin */
/* The browser gave this page no 2D canvas: getContext('2d') answered null.
   WebKit does that when the canvas memory of its process is spent — Sentry
   S4/R6/R7/RH/RJ/RK (29.10.2026): five games, three people, every one on iOS 16,
   and the very FIRST canvas of a freshly opened page refused, so nothing on that
   page could have prevented it. Until then each game read `ctx.setTransform` of
   null and the person was left with a blank box. Now: one notice in place of the
   game (why, and what helps: close other tabs and reload), one report with what
   the page itself holds (canvases, pixels) so the next look can tell whose
   memory it was, and an Error marked `expected` for the caller to THROW — its
   start stops cleanly, and Sentry drops expected errors by the site's contract.
     if (!ctx) throw window.__canvasRefused(canvas[, {anchor: el}]);
   `anchor` — where the notice goes when the refused canvas is off-screen
   (a sprite sheet): the game's visible canvas. */
window.__canvasRefused = (function () {
    var shown = false;
    var M = {
        ru: ['Браузер не дал странице холст для рисования — так бывает, когда устройству не хватает памяти. Закройте другие вкладки и обновите страницу.', 'Обновить страницу'],
        en: ['The browser gave this page no drawing canvas — this happens when the device is short on memory. Close other tabs and reload the page.', 'Reload the page'],
        de: ['Der Browser hat dieser Seite keine Zeichenfläche gegeben – das passiert, wenn dem Gerät der Speicher ausgeht. Schließen Sie andere Tabs und laden Sie die Seite neu.', 'Seite neu laden'],
        es: ['El navegador no le dio a esta página un lienzo para dibujar; pasa cuando al dispositivo le falta memoria. Cierra otras pestañas y vuelve a cargar la página.', 'Volver a cargar'],
        fr: ['Le navigateur n’a pas donné de zone de dessin à cette page — cela arrive quand l’appareil manque de mémoire. Fermez d’autres onglets et rechargez la page.', 'Recharger la page'],
        pt: ['O navegador não deu a esta página uma tela de desenho — isso acontece quando falta memória ao aparelho. Feche outras abas e recarregue a página.', 'Recarregar a página'],
        id: ['Peramban tidak memberi halaman ini kanvas untuk menggambar — ini terjadi saat memori perangkat kurang. Tutup tab lain lalu muat ulang halaman.', 'Muat ulang halaman'],
        uk: ['Браузер не дав сторінці полотно для малювання — так буває, коли пристрою бракує пам’яті. Закрийте інші вкладки й оновіть сторінку.', 'Оновити сторінку'],
        ko: ['브라우저가 이 페이지에 그림을 그릴 캔버스를 주지 않았습니다. 기기 메모리가 부족할 때 생깁니다. 다른 탭을 닫고 페이지를 새로 고치세요.', '페이지 새로 고침'],
        ar: ['لم يمنح المتصفح هذه الصفحة لوحة للرسم — يحدث هذا عندما تنقص ذاكرة الجهاز. أغلق علامات التبويب الأخرى وأعد تحميل الصفحة.', 'إعادة تحميل الصفحة']
    };
    return function (canvas, opts) {
        var err = new Error('canvas_2d_refused');
        err.code = 'CANVAS_2D_REFUSED';
        err.expected = true;          // Sentry drops it (the site's contract)
        err.__userNotified = true;    // the notice below IS the message: no second, generic toast
        if (shown) return err;
        shown = true;
        try {
            var all = document.getElementsByTagName('canvas'), px = 0;
            for (var i = 0; i < all.length; i++) px += (all[i].width | 0) * (all[i].height | 0);
            var data = {
                slug: window.__TOOL_SLUG || '',
                w: canvas ? canvas.width | 0 : 0, h: canvas ? canvas.height | 0 : 0,
                dpr: window.devicePixelRatio || 1, n: all.length, mpx: Math.round(px / 1e5) / 10,
                nav: (performance.getEntriesByType && (performance.getEntriesByType('navigation')[0] || {}).type) || ''
            };
            /* a game starting while the page is parsed runs before telemetry-tier0.js
               (it sits after the tools' scripts): the report waits for it */
            var send = function () { try { if (window.__aeCritical) window.__aeCritical('canvas_refused', data); } catch (_) {} };
            if (window.__aeCritical) send(); else window.addEventListener('load', send, { once: true });
        } catch (_) {}
        try {
            var anchor = (opts && opts.anchor) || canvas;
            if (!anchor || !anchor.parentNode || !anchor.isConnected) return err;
            var L = (window.__LOCALE || 'en').substring(0, 2).toLowerCase(), t = M[L] || M.en;
            var box = document.createElement('div');
            box.className = 'tu-pick-help tu-canvas-refused';
            box.setAttribute('role', 'alert');
            box.style.position = 'relative';
            box.style.zIndex = '2';
            var p = document.createElement('p');
            p.className = 'tu-pick-help-text';
            p.textContent = t[0];
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'tu-pick-help-btn';
            b.textContent = t[1];
            b.addEventListener('click', function () { location.reload(); });
            box.appendChild(p);
            box.appendChild(b);
            /* the canvas may sit in a screen not shown yet (a game's stage behind its menu):
               the notice goes where the person is looking — the nearest shown ancestor */
            var host = anchor;
            while (host.parentElement && host !== document.body && !host.offsetParent) host = host.parentElement;
            if (host === anchor) anchor.parentNode.insertBefore(box, anchor);
            else host.insertBefore(box, host.firstChild);
        } catch (_) {}
        return err;
    };
})();
/* __canvasRefused:end */
/* __canvasBlob:begin */
/* Ожидание, которое ВСЕГДА отвечает, и холст → Blob, который всегда отвечает.

   Возврат #37 (30.09.2026, /stamp-maker): экспорт ждал кодировщик холста,
   воркер, шрифты и три скрипта — ни у одного ожидания не было срока, и одна
   тишина держала «Скачать» мёртвой во всех форматах: 173 с в «exporting» на
   каждом из шести заходов, ни слова на экране. Те же уходы из «exporting» —
   у /business-card-maker (61–690 с) и /photo-frame (83 мин), и ВСЕ они — у
   отказавшихся от cookies: у принявших 0 из 254 экспортов визиток. Здесь
   общий ответ, чтобы следующий тул не писал свою копию.

   __bounded(promise, ms, phase) — тот же промис, но не дольше ms ВИДИМОГО
     времени (__visibleTimeout: скрытая вкладка — не зависание); тишина →
     отказ с code 'WAIT_TIMEOUT', .phase, .ms. __isWaitTimeout(e) узнаёт его,
     __waitTimeoutError(phase, ms) — делает такой же для своих сторожей.

   __canvasBlob(canvas, type, quality) → Promise<Blob>, который решится всегда:
     • toBlob — быстрый путь; срок растёт с площадью (15 с + 1,5 с на Мп:
       честное кодирование большого PNG на слабом телефоне укладывается,
       молчание — нет);
     • замолчал — OffscreenCanvas.convertToBlob, а опоздавший ответ toBlob
       всё ещё принимается (кто первый — того и файл);
     • null/пустой (так браузер говорит «не хватило памяти») — следующая
       ступень, но только у холста до 16 Мп (больше — сразу отказ: вторая
       копия уронила бы вкладку); последняя — toDataURL, тоже до 16 Мп;
     • ни одна ступень не дала файла → отказ code 'CANVAS_ENCODE_FAILED',
       .phase 'encode', .why; испорченный чужой картинкой холст —
       SecurityError сразу: ступени ему не помогут.
   Замолчавшая ступень на этой странице больше не пробуется — пачка из сотни
   файлов не выжидает срок сотню раз. Холст не перерисовывать, пока промис не
   решён: запасная ступень снимает его в момент своего запуска.

   Телеметрия — tier-0 (/api/log-error, видна без согласия — у тех, у кого и
   случались зависания): wait_timeout{phase,ms}, canvas_blob{via,why,mp,ms} —
   файл спасла запасная ступень, canvas_blob_failed{why,mp,mime}.
   Сторожа — tests/js/canvas-blob.test.mjs, CanvasBlobHasOneOwnerTest.
   Разбор — docs/why/primitives/canvas-blob.md. */
(function(){
    var told={};
    function slug(){try{var m=document.querySelector('meta[name="tool-slug"]');return m?m.content:'';}catch(_){return '';}}
    function beacon(type,data){
        var k=type+'|'+(data.phase||'')+'|'+(data.via||'')+'|'+(data.why||'');
        if(told[k])return;told[k]=1;
        data.slug=slug();
        try{if(window.__aeCritical)window.__aeCritical(type,data);}catch(_){}
    }
    function after(fn,ms){
        if(typeof window.__visibleTimeout==='function')return window.__visibleTimeout(fn,ms);
        var h=setTimeout(fn,ms);return function(){clearTimeout(h);};
    }
    function waitErr(phase,ms){
        var e=new Error('wait "'+phase+'" did not answer within '+ms+' ms');
        e.name='WaitTimeoutError';e.code='WAIT_TIMEOUT';e.phase=phase;e.ms=ms;
        return e;
    }
    window.__waitTimeoutError=waitErr;
    window.__isWaitTimeout=function(e){return !!(e&&e.code==='WAIT_TIMEOUT');};
    window.__bounded=function(p,ms,phase){
        phase=phase||'wait';
        var cancel=null;
        var timer=new Promise(function(_,reject){
            cancel=after(function(){beacon('wait_timeout',{phase:phase,ms:ms});reject(waitErr(phase,ms));},ms);
        });
        return Promise.race([Promise.resolve(p),timer]).then(
            function(v){if(cancel)cancel();return v;},
            function(e){if(cancel)cancel();throw e;}
        );
    };

    var silent={toBlob:false,offscreen:false};
    /* Больше этого холста запасные ступени после `null` не пробуются, а data URL
       не строится вовсе: `null` на большом холсте — нехватка памяти, и вторая
       полная копия (OffscreenCanvas, ×4 байта на пиксель) или строка base64
       (UTF-16 в памяти JS — сотни МБ) скорее уронят вкладку с работой, чем
       отдадут файл. Честный отказ — лучше. */
    var BIG_PX=16e6;
    function has(b){return !!(b&&b.size>0);}
    function viaToBlob(c,type,q){
        /* Синхронный бросок (SecurityError у испорченного холста) исполнитель
           промиса превращает в отказ — он и уйдёт наружу. */
        return new Promise(function(resolve){c.toBlob(function(b){resolve(b);},type,q);});
    }
    function viaOffscreen(c,type,q){
        if(typeof OffscreenCanvas==='undefined')return Promise.resolve(null);
        return Promise.resolve().then(function(){
            var oc=new OffscreenCanvas(c.width,c.height),g=oc.getContext('2d');
            if(!g)return null;
            g.drawImage(c,0,0);
            return oc.convertToBlob({type:type,quality:q});
        }).catch(function(e){if(e&&e.name==='SecurityError')throw e;return null;});
    }
    function viaDataUrl(c,type,q){
        if(c.width*c.height>BIG_PX)return null;
        var url=c.toDataURL(type,q),comma=url.indexOf(',');
        if(comma<0||url.length-comma<16)return null;
        /* Неподдержанный тип toDataURL молча отдаёт PNG — тип берём из ответа. */
        var mime=url.slice(5,url.indexOf(';'))||type;
        var bin=atob(url.slice(comma+1)),n=bin.length,bytes=new Uint8Array(n);
        for(var i=0;i<n;i++)bytes[i]=bin.charCodeAt(i);
        return new Blob([bytes],{type:mime});
    }
    window.__canvasBlob=function(c,type,quality){
        type=type||'image/png';
        var px=(c.width||0)*(c.height||0),mp=Math.round(px/1e5)/10;
        var ms=Math.round(15000+1500*px/1e6),t0=Date.now(),why='',late=null;
        function won(b,via){
            if(via!=='toBlob')beacon('canvas_blob',{via:via,why:why,mp:mp,ms:Date.now()-t0});
            return b;
        }
        var first;
        if(silent.toBlob){why='skipped';first=Promise.resolve(null);}
        else{
            var raw=viaToBlob(c,type,quality);
            first=window.__bounded(raw,ms,'encode').then(function(b){
                if(has(b))return {b:b,via:'toBlob'};
                why='null';return null;
            },function(e){
                if(!window.__isWaitTimeout(e))throw e;
                silent.toBlob=true;why='silent';late=raw;
                return null;
            });
        }
        return first.then(function(r){
            if(r)return r;
            if(why==='null'&&px>BIG_PX)return null;
            var second=silent.offscreen?Promise.resolve(null):window.__bounded(viaOffscreen(c,type,quality),ms,'encode').then(
                function(b){return has(b)?{b:b,via:'offscreen'}:null;},
                function(e){if(!window.__isWaitTimeout(e))throw e;silent.offscreen=true;return null;}
            );
            /* Опоздавший toBlob соревнуется со второй ступенью, но только пока
               та не ответила: ждать его дольше значило бы вернуть вечное
               ожидание через заднюю дверь. */
            return new Promise(function(resolve,reject){
                var over=false;
                function take(v){if(!over&&v){over=true;resolve(v);}}
                if(late)late.then(function(b){if(has(b)){silent.toBlob=false;take({b:b,via:'toBlob-late'});}},function(){});
                second.then(function(v){take(v);if(!over){over=true;resolve(null);}},function(e){if(!over){over=true;reject(e);}});
            });
        }).then(function(r){
            if(r)return won(r.b,r.via);
            var b=(why==='null'&&px>BIG_PX)?null:viaDataUrl(c,type,quality);
            if(has(b))return won(b,'dataurl');
            var e=new Error('canvas could not be encoded ('+why+')');
            e.name='CanvasEncodeError';e.code='CANVAS_ENCODE_FAILED';e.phase='encode';e.why=why;
            beacon('canvas_blob_failed',{why:why,mp:mp,mime:type});
            throw e;
        });
    };
})();
/* __canvasBlob:end */
/* __yieldTick:begin */
/* Уступить поток в долгом цикле так, чтобы СКРЫТАЯ вкладка его не усыпляла.
   Видимая вкладка — прежний таймер (кадр успевает отрисоваться, клик «Отмена»
   успевает сработать), скрытая — задача через MessageChannel: таймеры скрытой
   вкладки браузер будит не чаще раза в секунду (Firefox — dom.min_background_
   timeout_value 1000 мс; Chrome после 5 минут скрытия — раз в минуту), а
   сообщения канала не троттлит. Тот же приём, что _yieldTick в
   webcodecs-pipeline.js и canvas-video-export.js (там замер 25.09.2026); этот —
   для цикла, который живёт в блейде. Первый потребитель — покадровый лейн
   /audio-to-video: он уступал поток таймером после каждой партии (2 с видео),
   то есть в фоне спал ≥1 с на партию, а 13-минутный трек — 390 партий.
   ms в скрытой вкладке не соблюдается: уступка, а не задержка.
   Уступка БЕЗ задержки (ms 0) — канал и в видимой вкладке (30.09.2026). Таймер
   там стоил 4.8 мс на уступку (вложенные таймеры браузер зажимает до ≥4 мс),
   под ЦП ×4 — 11 мс; канал — 0.14 и 2.4 мс (tests/playwright/yield-cost-bench.mjs).
   Быстрый лейн /vocal-remover с уступкой каждые 50 мс счёта стал от таймера на
   31 % дольше. Кадр и клик канал пропускают — это замерено на разделителе
   Ультры (mdx-stem-separator.js, 22.08.2026: кадров вдвое больше, поток «не
   отвечает» 1–6 % вместо 59 %). ⚠️ Дверь — для ВЫЧИСЛИТЕЛЬНОГО цикла: цикл,
   ЖДУЩИЙ события (очередь декодера), на канале вращался бы вхолостую — ему
   событие или ms > 0 (таймер в видимой вкладке остаётся для явной задержки). */
(function(){
    var chan=null,q=[];
    window.__yieldTick=function(ms){
        var hid=false;try{hid=document.visibilityState==='hidden'||document.hidden===true;}catch(_){}
        if(typeof MessageChannel==='undefined'||(!hid&&ms>0))return new Promise(function(r){setTimeout(r,ms||0);});
        if(!chan){chan=new MessageChannel();chan.port1.onmessage=function(){var f=q.shift();if(f)f();};}
        return new Promise(function(r){q.push(r);chan.port2.postMessage(0);});
    };
})();
/* __yieldTick:end */
/* __activeNow:begin */
/* Часы АКТИВНОГО времени: performance.now() минус то время, когда страница не
   могла исполнять код, — вкладка заморожена браузером (фоновая вкладка Opera,
   свёрнутый браузер на телефоне) или система спала. Скорость долгой работы
   меряется по ним, а не по стенным часам: по стенным два отсчёта прогресса по
   обе стороны замороженного часа читаются как «полпроцента в час».

   Прод 26.09.2026 (/video-fps, сессия 06e6d5e3, Opera): замеренный ИИ-прогон
   4K «около 2 ч 20 мин» стоял в фоновой вкладке; на 89 % уведомление по живой
   скорости сказало «около 30 ч», и через 11 минут человек бросил почти
   готовую работу. 60 дней: 71 повторное уведомление со скачком прогноза ≥10×
   (до 1946×), до файла дошли 41 % таких прогонов против 56–60 %.

   Заморозку браузер объявляет событиями freeze/resume — она учитывается
   точно. Сон системы событий не имеет: его выдаёт разрыв между отсчётами этих
   часов (тикер раз в 15 с запускается первым вызовом; скрытой вкладке таймеры
   режут до раза в минуту, поэтому разрывом считается только то, что длиннее
   ожидаемого интервала на минуту). __activeGaps() — {ms, n}: сколько времени
   исключено и сколько раз. Сторож — tests/js/active-now.test.mjs. */
(function(){
    var doc=document;
    var perf=(window.performance&&typeof performance.now==='function')?function(){return performance.now();}:function(){return Date.now();};
    var TICK=15000,SLACK=60000;
    var frozen=0,gaps=0,last=-1,frozeAt=-1,frozenAtFreeze=0,ticker=0;
    function hidden(){try{return doc.visibilityState==='hidden'||doc.hidden===true;}catch(_){return false;}}
    function note(){
        var n=perf();
        if(frozeAt>=0){frozen=frozenAtFreeze+Math.max(0,n-frozeAt);}
        else if(last>=0){
            var d=n-last,e=hidden()?60000:TICK;
            if(d>e+SLACK){frozen+=d-e;gaps++;}
        }
        last=n;
        return n;
    }
    function now(){
        if(!ticker){try{ticker=setInterval(note,TICK);}catch(_){}}
        return note()-frozen;
    }
    try{
        doc.addEventListener('freeze',function(){note();frozeAt=perf();frozenAtFreeze=frozen;});
        doc.addEventListener('resume',function(){
            if(frozeAt<0)return;
            var n=perf();
            frozen=frozenAtFreeze+Math.max(0,n-frozeAt);gaps++;
            frozeAt=-1;last=n;
        });
    }catch(_){}
    window.__activeNow=now;
    window.__activeGaps=function(){note();return{ms:Math.round(frozen),n:gaps};};
})();
/* __activeNow:end */
/* __mp4Pcm:begin */
/* НЕСЖАТЫЙ ЗВУК В MP4/MOV — одна дверь для всех, кто читает файл через mp4box.
   Камеры (Nikon, Canon, Sony, Panasonic), рекордеры и IP-камеры пишут звук без
   сжатия: QuickTime `sowt`/`twos`/`in24`/`in32`/`fl32`/`fl64`/`lpcm`/`raw `,
   ISO `ipcm`/`fpcm`, G.711 `alaw`/`ulaw`. mp4box 0.5.4 таких записей не знает
   (тип 'metadata') и строит по ОБЪЕКТУ НА КАЖДЫЙ ОТСЧЁТ, а `stsz` с одним
   размером на всех разворачивает в массив той же длины. Замер 22.10.2026 на
   копии файла из обращения (/video-stabilizer, Nikon DSC_1328.MOV, 4,6 мин,
   стерео 48 кГц): 13,26 млн объектов, 12,4 с главного потока и 2 655 МБ кучи —
   и так при каждом разборе (проба дорожек, проход разбора, сборка). Страница
   замирала на 17 с, пул воркеров не успевал подняться, разбор шёл 14 минут, а
   сам звук конвейер не видел и отказывал в сборке все 12 раз.

   Здесь: (1) такая дорожка отдаётся ЧАНКАМИ — как её читает FFmpeg
   (mov_build_index): кусок = отсчёты, лежащие в файле подряд, ~25 на секунду;
   `stsz` одного размера разворачивается, только если его кто-то прочтёт;
   (2) запись разобрана в `trak.pcm` (разрядность, порядок байт, каналы,
   частота); (3) `decode(bytes, layout)` превращает кусок в отсчёты.
   Ставится сам, когда скрипт mp4box объявляет MP4Box, — любым из загрузчиков,
   с нашей копии или с CDN. Сторож — tests/js/mp4-pcm.test.mjs. */
(function(root){
    var FOURCC=/^(sowt|twos|in24|in32|fl32|fl64|lpcm|raw |ipcm|fpcm|ulaw|alaw)$/;
    function u8(d){return d instanceof Uint8Array?d:(d?new Uint8Array(d):null);}
    /* Дочерние боксы записи: `enda` (порядок байт, бывает внутри `wave`) и
       ISO `pcmC` (флаги формата и разрядность). */
    function kids(d,from,to,out){
        var dv=new DataView(d.buffer,d.byteOffset,d.byteLength);
        for(var p=from;p+8<=to;){
            var sz=dv.getUint32(p),ty=String.fromCharCode(d[p+4],d[p+5],d[p+6],d[p+7]);
            if(sz<8||p+sz>to)break;
            if(ty==='wave')kids(d,p+8,p+sz,out);
            else if(ty==='enda'&&sz>=10)out.little=dv.getUint16(p+8)!==0;
            else if(ty==='pcmC'&&sz>=14)out.pcmC={flags:d[p+12],bits:d[p+13]};
            p+=sz;
        }
        return out;
    }
    /* Формат несжатой записи по её байтам (entry.data у mp4box — всё после
       data_reference_index). null — не несжатый звук или раскладка, которую
       здесь не прочесть честно (неперемежённые каналы, разрядность вне ряда). */
    function layout(entry,timescale){
        var t=entry&&entry.type;
        if(!t||!FOURCC.test(t))return null;
        var d=u8(entry.data);
        if(!d||d.length<20)return null;
        var dv=new DataView(d.buffer,d.byteOffset,d.byteLength);
        var iso=(t==='ipcm'||t==='fpcm'),ver=iso?0:dv.getUint16(0);
        var ch=dv.getUint16(8),ss=dv.getUint16(10),rate=dv.getUint32(16)>>>16;   // 16.16, у звука целая
        var from=20,bpf=0,bits=0,flt=false,be=true,uns=false,law='',flags=-1;
        if(ver===1){if(d.length<36)return null;bpf=dv.getUint32(28);from=36;}
        else if(ver===2){
            if(d.length<56)return null;
            rate=dv.getFloat64(24);ch=dv.getUint32(32);bits=dv.getUint32(40);flags=dv.getUint32(44);
            var bpp=dv.getUint32(48),fpp=dv.getUint32(52);
            bpf=fpp>0?bpp/fpp:0;from=56;
        }else if(ver!==0)return null;
        var x=kids(d,from,d.length,{little:null,pcmC:null});
        switch(t){
            case 'sowt':bits=bits||ss;be=false;break;
            case 'twos':bits=bits||ss;break;
            case 'raw ':bits=8;uns=true;break;
            case 'in24':bits=24;be=x.little!==true;break;
            case 'in32':bits=32;be=x.little!==true;break;
            case 'fl32':bits=32;flt=true;be=x.little!==true;break;
            case 'fl64':bits=64;flt=true;be=x.little!==true;break;
            case 'ulaw':case 'alaw':bits=8;law=t.charAt(0);break;
            case 'ipcm':case 'fpcm':
                if(!x.pcmC)return null;
                bits=x.pcmC.bits;be=!(x.pcmC.flags&1);flt=(t==='fpcm');break;
            case 'lpcm':
                if(ver!==2||flags<0)return null;
                if(flags&32)return null;                       // неперемежённые каналы
                flt=!!(flags&1);be=!!(flags&2);uns=!flt&&!(flags&4)&&bits===8;break;
        }
        if(flt?(bits!==32&&bits!==64):[8,16,24,32].indexOf(bits)<0)return null;
        if(!(ch>=1&&ch<=32))return null;
        /* v0/v1 пишут частоту 16.16 — 88,2/96 кГц туда не помещаются; тогда
           честнее шкала времени дорожки (у несжатого звука это и есть частота). */
        if(!(rate>=1000&&rate<=768000)||(ver<2&&rate>=65535))rate=timescale>=1000?timescale:0;
        if(!rate)return null;
        var need=ch*bits/8;
        if(bpf&&bpf!==need)return null;
        return{codec:t,channels:ch,sampleRate:Math.round(rate),bits:bits,float:flt,bigEndian:bits>8&&be,
               unsigned:uns,law:law,bytesPerFrame:need};
    }
    /* Таблица отсчётов дорожки — по чанкам. null: не наш случай, mp4box строит
       как строил (переменный размер, вторая запись, ctts, группы отсчётов). */
    function chunked(trak){
        var md=trak&&trak.mdia,st=md&&md.minf&&md.minf.stbl;
        if(!st||!md.hdlr||md.hdlr.handler!=='soun')return null;
        var sd=st.stsd;
        if(!sd||!sd.entries||sd.entries.length!==1)return null;
        var e=sd.entries[0],L=layout(e,md.mdhd&&md.mdhd.timescale);
        if(!L)return null;
        var co=st.stco||st.co64,sc=st.stsc,sz=st.stsz,tt=st.stts;
        if(!co||!sc||!sz||!tt||st.ctts||st.stz2)return null;
        if((st.sbgps&&st.sbgps.length)||(st.sgpds&&st.sgpds.length))return null;
        var one=sz.sample_size;
        /* Отсчёт stsz — один кадр (размер кадра; у старых QuickTime — 1) или их
           целое число. Иначе раскладку нельзя проверить — не трогаем. */
        var per=(one===L.bytesPerFrame||one===1)?1:(one>0&&one%L.bytesPerFrame===0?one/L.bytesPerFrame:0);
        if(!per)return null;
        var n=sz.sample_count,offs=co.chunk_offsets,fc=sc.first_chunk,spc=sc.samples_per_chunk,sdi=sc.sample_description_index;
        var cnt=tt.sample_counts,dl=tt.sample_deltas,ti=0,tl=cnt.length?cnt[0]:0;
        var out=[],run=0,done=0,dts=0,bytes=0,ts=md.mdhd.timescale,id=trak.tkhd.track_id;
        for(var c=1;c<=offs.length&&done<n;c++){
            while(run+1<fc.length&&fc[run+1]<=c)run++;
            var k=Math.min(spc[run],n-done),left=k,dur=0;
            if(!(k>0))continue;
            while(left>0){
                if(tl===0){if(++ti>=cnt.length)return null;tl=cnt[ti];continue;}
                var take=Math.min(left,tl);dur+=take*dl[ti];left-=take;tl-=take;
            }
            var size=k*per*L.bytesPerFrame;
            out.push({number:out.length,track_id:id,timescale:ts,alreadyRead:0,size:size,chunk_index:c,chunk_run_index:run,
                description_index:sdi[run]-1,description:e,offset:offs[c-1],dts:dts,cts:dts,duration:dur,is_sync:true,
                is_leading:0,depends_on:0,is_depended_on:0,has_redundancy:0,degradation_priority:0,frames:k*per});
            dts+=dur;bytes+=size;done+=k;
        }
        if(!out.length||done<n)return null;
        trak.samples=out;trak.samples_duration=dts;trak.samples_size=bytes;trak.pcm=L;
        return L;
    }
    function fit(g){
        var I=g&&g.ISOFile,B=g&&g.BoxParser;
        if(!I||!I.prototype||typeof I.prototype.buildTrakSampleLists!=='function'||I.prototype.buildTrakSampleLists.__pcm)return false;
        var build=I.prototype.buildTrakSampleLists;
        var mine=function(trak){
            var L=null;
            try{L=chunked(trak);}catch(_){L=null;}
            if(!L)return build.apply(this,arguments);
        };
        mine.__pcm=true;
        I.prototype.buildTrakSampleLists=mine;
        /* stsz с одним размером на всех: массив того же размера появляется,
           только когда его читают (сборка таблицы НЕ несжатой дорожки, запись
           файла). Тело — то же, что у mp4box; сверка — в сторожe. */
        if(B&&B.stszBox&&B.stszBox.prototype&&!B.stszBox.prototype.parse.__pcm){
            var p=function(t){
                this.parseFullHeader(t);
                this.sample_sizes=[];
                if(this.version!==0)return;
                this.sample_size=t.readUint32();this.sample_count=t.readUint32();
                var i;
                if(this.sample_size===0){for(i=0;i<this.sample_count;i++)this.sample_sizes.push(t.readUint32());return;}
                var box=this,full=null;
                Object.defineProperty(this,'sample_sizes',{configurable:true,enumerable:true,
                    get:function(){if(!full){full=[];for(var j=0;j<box.sample_count;j++)full.push(box.sample_size);}return full;},
                    set:function(v){Object.defineProperty(box,'sample_sizes',{value:v,writable:true,configurable:true,enumerable:true});}});
            };
            p.__pcm=true;
            B.stszBox.prototype.parse=p;
        }
        return true;
    }
    /* G.711 → 16 бит (классические alaw2linear / ulaw2linear). */
    function alaw(a){a^=0x55;var t=(a&0x0F)<<4,s=(a&0x70)>>4;t+=s?0x108:8;if(s>1)t<<=s-1;return(a&0x80)?t:-t;}
    function ulaw(u){u=~u&0xFF;var t=((u&0x0F)<<3)+0x84;t<<=(u&0x70)>>4;return(u&0x80)?(0x84-t):(t-0x84);}
    /* Кусок → отсчёты, перемежённые: до 16 бит — {format:'s16', data:Int16Array},
       шире и плавающая точка — {format:'f32', data:Float32Array}. Формы AudioData. */
    function decode(bytes,L){
        var b=u8(bytes),n=Math.floor(b.length/L.bytesPerFrame),m=n*L.channels,i;
        var dv=new DataView(b.buffer,b.byteOffset,n*L.bytesPerFrame),le=!L.bigEndian;
        if(!L.float&&L.bits<=16){
            var s=new Int16Array(m);
            if(L.law==='a')for(i=0;i<m;i++)s[i]=alaw(b[i]);
            else if(L.law==='u')for(i=0;i<m;i++)s[i]=ulaw(b[i]);
            else if(L.bits===8)for(i=0;i<m;i++)s[i]=L.unsigned?(b[i]-128)<<8:((b[i]<<24)>>24)<<8;
            else for(i=0;i<m;i++)s[i]=dv.getInt16(i*2,le);
            return{format:'s16',data:s,frames:n};
        }
        var f=new Float32Array(m);
        if(L.float&&L.bits===32)for(i=0;i<m;i++)f[i]=dv.getFloat32(i*4,le);
        else if(L.float)for(i=0;i<m;i++)f[i]=dv.getFloat64(i*8,le);
        else if(L.bits===32)for(i=0;i<m;i++)f[i]=dv.getInt32(i*4,le)/2147483648;
        else for(i=0;i<m;i++){
            var o=i*3,v=le?(b[o]|(b[o+1]<<8)|(b[o+2]<<16)):((b[o]<<16)|(b[o+1]<<8)|b[o+2]);
            f[i]=((v<<8)>>8)/8388608;
        }
        return{format:'f32',data:f,frames:n};
    }
    root.__mp4Pcm={layout:layout,decode:decode,fit:function(){return fit(root);},FOURCC:FOURCC};
    /* mp4box объявляет MP4Box последней строкой — к этому моменту ISOFile и
       BoxParser уже есть. Уже загружен — ставим сразу; объявление ловит сеттер
       (`var MP4Box = …` в обычном скрипте присваивает через него). Сеттер
       остаётся: два загрузчика наперегонки грузят скрипт дважды, и второй
       объявляет СВОЙ ISOFile — его тоже надо чинить. */
    try{
        var held=root.MP4Box;
        if(held)fit(root);
        Object.defineProperty(root,'MP4Box',{configurable:true,enumerable:true,
            get:function(){return held;},
            set:function(v){held=v;try{fit(root);}catch(_){}}});
    }catch(_){}
})(window);
/* __mp4Pcm:end */
/* __mpSoftFit(opts) — задача MediaPipe там, где браузер не даёт WebGL2.
   MediaPipe (tasks-vision 0.10.34) подаёт кадр в граф только через GL-текстуру,
   даже на CPU-делегате, и без WebGL2 падает: WebGL1 → `gl_texture_buffer.cc:239
   Check failed: prod_token` → Aborted(), без GL вовсе — TypeError (RU 30 д:
   /passport-photo 26 сессий; у остальных тулов причина в телеметрию не
   доходила). Эта дверь — одна строка перед `createFromOptions(fileset, opts)`:
   при живом WebGL2 не делает НИЧЕГО и ничего не грузит; без него подгружает
   mediapipe-soft-gl.js и ставит в opts программный «WebGL2» (`canvas`) и
   CPU-делегат. Вывод побитово тот же, что на настоящем GL (FaceLandmarker,
   FaceDetector, ObjectDetector, Pose/HandLandmarker — замер 27.09.2026).
   ⛔ ImageSegmenter — нет: маски он строит шейдерами, их у прокладки нет.
   Проверяется холст того же типа, на котором GL поднимет MediaPipe
   (OffscreenCanvas, иначе <canvas>). Промис всегда разрешается: провал
   загрузки = «как раньше», а не новая ошибка. */
/* __mpSoftFit:begin */
window.__mpSoftFit=(function(){
    var noGl2=null,swGl=false,pending=null;
    function inspect(){
        if(noGl2!==null)return;
        try{
            var c=(typeof OffscreenCanvas!=='undefined')?new OffscreenCanvas(1,1):document.createElement('canvas');
            var g=c.getContext('webgl2');
            noGl2=!g;
            if(g){
                var e=g.getExtension('WEBGL_debug_renderer_info');
                swGl=/swiftshader|llvmpipe|softpipe|software|basic render/i.test(e?String(g.getParameter(e.UNMASKED_RENDERER_WEBGL)||''):'');
                var l=g.getExtension('WEBGL_lose_context');if(l)l.loseContext();
            }
        }catch(_){noGl2=true;}
    }
    function lacksWebGL2(){inspect();return noGl2;}
    function once(){
        return new Promise(function(res,rej){
            try{
                var sc=document.createElement('script');
                sc.src=(typeof window.__jsSrc==='function')?window.__jsSrc('/js/mediapipe-soft-gl.js'):'/js/mediapipe-soft-gl.js';
                sc.async=true;
                var n=document.querySelector('script[nonce]');
                if(n&&n.nonce)sc.setAttribute('nonce',n.nonce);
                sc.onload=function(){ window.MediaPipeSoftGL?res(window.MediaPipeSoftGL):rej(new Error('softgl-missing')); };
                sc.onerror=function(){ if(sc.parentNode)sc.parentNode.removeChild(sc); rej(new Error('softgl-load-failed')); };
                document.head.appendChild(sc);
            }catch(e){ rej(e); }
        });
    }
    /* Три попытки: без WebGL2 оборванный запрос на 14 КБ иначе стоил бы
       распознавания целиком — источник MediaPipe шёл бы на WebGL1 и падал
       (стенд 27.09: один сетевой сбой = «Ошибка распознавания лиц»). */
    function load(){
        if(window.MediaPipeSoftGL)return Promise.resolve(window.MediaPipeSoftGL);
        if(pending)return pending;
        var tries=0;
        pending=(function attempt(){
            return once().catch(function(e){
                if(++tries>=3)throw e;
                return new Promise(function(r){setTimeout(r,tries===1?600:1800);}).then(attempt);
            });
        })().catch(function(e){ pending=null; throw e; });
        return pending;
    }
    var fit=function(opts){
        if(!opts||!lacksWebGL2())return Promise.resolve(false);
        return load().then(function(SG){
            opts.canvas=SG.canvas();
            opts.baseOptions=Object.assign({},opts.baseOptions||{},{delegate:'CPU'});
            try{if(window.__ae)window.__ae('feature_use',{f:'mp_soft_gl'});}catch(_){}
            return true;
        }).catch(function(e){
            try{if(window.__ae)window.__ae('tool_diagnostic',{issue:'mp_soft_gl_failed',msg:String((e&&e.message)||e).slice(0,80)});}catch(_){}
            return false;
        });
    };
    /* Воркеру дверь не дотянуть (нет window): страница спрашивает здесь и
       передаёт ему адрес прокладки — образец object-eraser/detect.js. */
    fit.lacksWebGL2=lacksWebGL2;
    fit.src=function(){return (typeof window.__jsSrc==='function')?window.__jsSrc('/js/mediapipe-soft-gl.js'):'/js/mediapipe-soft-gl.js';};
    /* Порядок делегатов — одно правило на все тулы. GPU первым только на
       АППАРАТНОМ WebGL2: на программном (SwiftShader, «Basic Render Driver»)
       GPU-граф BlazeFace втрое медленнее CPU (замер 27.09: 32 против 10 мс),
       без WebGL2 дверь сама ставит CPU. На аппаратном выигрыш есть там, где
       слаб процессор (CPU ×4 медленнее: 1127 против 380 мс на «Найти лица»). */
    fit.gpuFirst=function(){inspect();return !noGl2&&!swGl;};
    /* «Создался» ≠ «работает»: GPU-граф MediaPipe поднимается на ПЕРВОМ выводе,
       и калечный WebGL2 (нет float-текстур) падает там, а не в createFromOptions
       (/video-blur, RU 60 д: 11 сессий с готовой моделью и «Ошибкой
       распознавания лиц»). Один настоящий вывод; провал закрывает задачу. */
    fit.probe=function(task,mode){
        try{
            var c=document.createElement('canvas');c.width=c.height=64;
            var x=c.getContext('2d');x.fillStyle='#8a8a8a';x.fillRect(0,0,64,64);
            /* метка 1 мс: любая настоящая метка тула (performance.now, rAF) больше — MediaPipe требует строгого роста */
            if(mode==='VIDEO')task.detectForVideo(c,1);else task.detect(c);
            return true;
        }catch(e){
            try{task.close();}catch(_){}
            try{if(window.__ae)window.__ae('feature_use',{f:'mp_probe_failed',msg:String((e&&e.message)||e).slice(0,80)});}catch(_){}
            return false;
        }
    };
    return fit;
})();
/* __mpSoftFit:end */
window.__ensureBatchDeliver=(function(){
    var pending=null;
    return function(){
        if(window.__batchDeliver)return Promise.resolve(window.__batchDeliver);
        if(pending)return pending;
        if(!window.__BATCH_DELIVER_SRC)return Promise.reject(new Error('no-batch-src'));
        pending=new Promise(function(res,rej){
            try{
                var sc=document.createElement('script');
                sc.src=window.__BATCH_DELIVER_SRC;
                sc.async=true;
                var n=document.querySelector('script[nonce]');
                if(n&&n.nonce)sc.setAttribute('nonce',n.nonce);
                sc.onload=function(){ window.__batchDeliver?res(window.__batchDeliver):rej(new Error('batch-missing')); };
                sc.onerror=function(){ pending=null; rej(new Error('batch-load-failed')); };
                document.head.appendChild(sc);
            }catch(e){ pending=null; rej(e); }
        });
        return pending;
    };
})();

// Files this page produced that the browser would have thrown away. They are
// real, readable bytes — only the delivery slot was gone — so they are kept and
// handed back on the user's next gesture instead of being lost behind a lie.
/* __dlHold:begin */
window.__dlHold=(function(){
    var items=[],bar=null,timer=null,mo=null;
    function L(){return (window.__LOCALE||'en').substring(0,2);}
    var T={
        ru:{n:function(n){return 'Готово ещё файлов: '+n+'. Браузер сохраняет по одному за раз.';},save:'Сохранить'},
        en:{n:function(n){return n+' more file(s) ready. Browsers save one at a time.';},save:'Save'},
        de:{n:function(n){return 'Noch '+n+' Datei(en) fertig. Browser speichern nur eine pro Klick.';},save:'Speichern'},
        es:{n:function(n){return 'Hay '+n+' archivo(s) más listos. El navegador guarda uno a la vez.';},save:'Guardar'},
        fr:{n:function(n){return n+' fichier(s) de plus sont prêts. Le navigateur en enregistre un à la fois.';},save:'Enregistrer'},
        pt:{n:function(n){return 'Mais '+n+' arquivo(s) prontos. O navegador salva um de cada vez.';},save:'Salvar'},
        id:{n:function(n){return n+' file lagi siap. Peramban menyimpan satu per satu.';},save:'Simpan'},
        ko:{n:function(n){return '파일 '+n+'개가 더 준비되었습니다. 브라우저는 한 번에 하나씩 저장합니다.';},save:'저장'},
        ar:{n:function(n){return 'جاهز '+n+' ملف إضافي. يحفظ المتصفح ملفًا واحدًا في كل مرة.';},save:'حفظ'},
        uk:{n:function(n){return 'Готово ще файлів: '+n+'. Браузер зберігає по одному за раз.';},save:'Зберегти'}
    };
    function txt(){return T[L()]||T.en;}
    function remove(){ try{if(bar)bar.remove();}catch(e){} bar=null; if(mo){try{mo.disconnect()}catch(e){} mo=null;} }
    // The 152-ФЗ consent banner is fixed to bottom:0 at z-index 9998. Sitting on
    // top of it would cover a legally-required control (the same reason the
    // Yandex floor ad waits for consent); sitting under it would hide the user's
    // own files. So sit ABOVE it, and follow it when it goes away.
    function ccHeight(){
        try{
            var b=document.querySelector('.cc-banner');
            if(!b||!document.body.contains(b))return 0;
            var st=window.getComputedStyle(b);
            if(st.display==='none'||st.visibility==='hidden')return 0;
            return Math.round(b.getBoundingClientRect().height)||0;
        }catch(e){return 0;}
    }
    function place(){ if(bar)bar.style.bottom=(12+ccHeight())+'px'; }
    function slug(){ try{return window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||'';}catch(e){return '';} }
    function render(){
        if(!items.length){remove();return;}
        var t=txt();
        if(!bar){
            bar=document.createElement('div');
            bar.id='tmb-dl-hold';
            bar.setAttribute('role','region');
            bar.style.cssText='position:fixed;left:50%;transform:translateX(-50%);bottom:12px;z-index:9992;'+
                'display:flex;align-items:center;gap:10px;max-width:94vw;box-sizing:border-box;'+
                'background:rgba(20,24,32,.94);color:#fff;border:1px solid rgba(255,255,255,.18);'+
                'border-radius:14px;padding:10px 14px;font:13px/1.35 system-ui,sans-serif;'+
                'box-shadow:0 6px 22px rgba(0,0,0,.4)';
            var span=document.createElement('span');
            span.setAttribute('data-hold-text','1');
            span.style.cssText='overflow:hidden;text-overflow:ellipsis;min-width:0';
            var btn=document.createElement('button');
            btn.type='button';
            btn.setAttribute('data-hold-save','1');
            btn.style.cssText='flex:0 0 auto;background:#3b82f6;color:#fff;border:0;border-radius:10px;'+
                'padding:7px 14px;font:600 13px system-ui,sans-serif;cursor:pointer';
            btn.textContent=t.save;
            btn.addEventListener('click',flush);
            var x=document.createElement('button');
            x.type='button';x.setAttribute('aria-label',((window.__commonStrings||{}).ui_close || "Close"));
            x.style.cssText='flex:0 0 auto;background:transparent;color:rgba(255,255,255,.7);border:0;'+
                'font:16px/1 system-ui,sans-serif;cursor:pointer;padding:4px 6px';
            x.textContent='✕';
            x.addEventListener('click',function(){
                try{if(window.__ae)window.__ae('feature_use',{f:'dl_hold_dismissed',n:items.length,slug:slug()});}catch(e){}
                items=[];remove();
            });
            bar.appendChild(span);bar.appendChild(btn);bar.appendChild(x);
            document.body.appendChild(bar);
            place();
            try{
                mo=new MutationObserver(place);
                mo.observe(document.body,{childList:true,subtree:false,attributes:true,attributeFilter:['class','style']});
                window.addEventListener('resize',place);
            }catch(e){}
            try{if(window.__ae)window.__ae('feature_use',{f:'dl_hold_offered',n:items.length,slug:slug()});}catch(e){}
        }
        bar.querySelector('[data-hold-text]').textContent=t.n(items.length);
    }
    // The button's own click is a FRESH interaction, so it carries a full
    // budget: a folder write spends none of it, and a ZIP spends exactly one.
    // The ladder itself (folder -> archive -> honest refusal) is NOT written
    // here — it is __batchDeliver.deliver(), the same call the tools use, so
    // the rescue and the intended path cannot drift apart.
    function flush(){
        // A PART of a batch that could not travel as one archive (__batchDeliver,
        // `zip_parts`) is already the unit of delivery. Packing the parts
        // together again would rebuild the archive that was just refused, so a
        // press hands over exactly one of them and the rest stay on the bar.
        var si=-1;
        for(var k=0;k<items.length;k++){ if(items[k].single){si=k;break;} }
        if(si>=0){
            var one=items.splice(si,1)[0];
            render();
            try{if(window.__ae)window.__ae('feature_use',{f:'dl_hold_flush',via:one.url?'server_link':'single_part',n:1,left:items.length,slug:slug()});}catch(e){}
            // A held LINK (a file on our server, see _dlServerLink) is handed
            // to the browser the way it was meant to go: as a link, inside this
            // press, through the interceptor — which takes this press's slot
            // and reports the delivery. Its bytes never pass through the page.
            if(one.url){
                if(window.__downloadLink(one.url,one.name))return Promise.resolve(true);
                items.unshift(one);render();
                return Promise.resolve(false);
            }
            return Promise.resolve(window.__downloadFile(one.blob,one.name,{src:'dl_hold'})).then(function(ok){
                if(ok===false){
                    // Not delivered (dialog closed, channel refused): the part
                    // goes back on the bar, once — the door may have held it itself.
                    var back=false;
                    for(var j=0;j<items.length;j++){ if(items[j].blob===one.blob){items[j].single=true;back=true;} }
                    if(!back)items.unshift(one);
                    render();
                }
                return ok;
            });
        }
        var batch=items.slice();items=[];remove();
        if(!batch.length)return Promise.resolve(false);
        var done=function(via,extra){
            try{if(window.__ae)window.__ae('feature_use',Object.assign(
                {f:'dl_hold_flush',via:via,n:batch.length,slug:slug()},extra||{}));}catch(e){}
        };
        var bd=window.__batchDeliver;
        if(bd&&bd.deliver){
            return bd.deliver(batch,{zipName:(slug()||'timbrica')+'-files.zip',slug:slug()})
                .then(function(r){
                    done(r.via,{written:r.written});
                    // Nothing landed and nothing was refused on purpose — keep
                    // the files rather than dropping them on the floor.
                    if(r.via==='refused'||r.via==='folder_fail'){items=batch.concat(items);render();}
                    return r.via==='folder'||r.via==='zip';
                });
        }
        // Batch primitive not there (blocked, offline, still loading). Then this
        // click buys exactly one file — which is the honest amount — and the
        // rest stay on the bar with the count visible. Clicking them into the
        // void is the failure being fixed.
        var first=batch.shift();
        items=batch.concat(items);render();
        done('single_no_batch');
        return window.__downloadFile(first.blob,first.name,{src:'dl_hold'});
    }

    /* Прогрев примитива пачки. ⚠️ try/catch вокруг ПРОМИСА ничего не ловит: отказ
       загрузки (`batch-load-failed` — сеть на Chrome iOS, Sentry HD) приходил
       позже и улетал необработанным. Прогрев — не обещание доставки: настоящая
       выдача зовёт __ensureBatchDeliver сама и сама разбирает его отказ. */
    function ensureBatch(){
        try{ var p=window.__ensureBatchDeliver(); if(p&&p.catch) p.catch(function(){}); }catch(e){}
    }
    return {
        // o.single — the item is delivered on its own, one press each (see flush).
        // o.url — the item is a same-origin LINK, not bytes (blob is null).
        add:function(blob,name,o){
            var url=(o&&o.url)||'';
            items.push({blob:blob,name:url?(name||''):(name||'download'),single:!!(o&&o.single)||!!url,url:url});
            ensureBatch();
            if(timer)clearTimeout(timer);
            timer=setTimeout(render,120);   // one bar for the whole burst
        },
        singles:true,
        count:function(){return items.length;},
        flush:flush,
        clear:function(){items=[];remove();},
        peek:function(){return items.slice();}
    };
})();
/* __dlHold:end */

/* ── Delivery ceilings, in one place ─────────────────────────────────────────
   Every number here was a literal buried in a branch until 2026-09-01, and two
   of them silently disagreed with the server. Named, measured, and covered by
   DownloadStashLimitsAgreeTest (JS ↔ PHP) so they cannot drift again.        */

/** Chromium's kMaxSharedFileBytes. See the measurement in _failNotice. */
var SHARE_MAX_BYTES=52428800;

/** MUST equal DownloadStashController::MAX_BYTES — the relay refuses above it,
 *  and refusing after a ten-minute mobile upload is worse than not starting.
 *  1 → 4 GiB on 2026-10-08: in-app browsers (Yandex app, YaBrowser Lite) have
 *  no other lane, and every size refusal in 30 days was a finished result of
 *  1.1–3.98 GB (comment #1816: two VR files of 1.23/1.25 GB, ten tries each).
 *  The server side of the old 1 GB wall is gone — see the controller. */
var STASH_MAX_BYTES=4294967296;

/** Above this, the stash is a bad deal even though it works: the user pays
 *  twice the file in mobile data and minutes of waiting, for bytes already on
 *  their device. Measured on prod, 30 d, stash retries per delivery by size —
 *  0.15 under 10 MB, 0.48 at 10–50 MB, 0.84 at 50–100 MB, 1.02 at 100–200 MB:
 *  the pain is not a cliff, it is a ramp that starts around 50 MB. So that is
 *  where the disk lane is tried FIRST; below it the invisible relay stays the
 *  default, because a save dialog for a 3 MB export is friction with no payoff
 *  (the round-8 desktop lesson: a dialog that replaces a WORKING channel is a
 *  net negative — 45 % of them were cancelled). */
var DISK_LANE_MIN_BYTES=52428800;
/** A dismissed save dialog in an in-app browser is remembered on the device
 *  for this long, and the next deliveries go straight to the relay — see the
 *  measured reason at _tryDisk in the WebView chain. */
var FSA_DECLINE_KEY='tmb_fsa_declined';
var FSA_DECLINE_DAYS=30;
function _fsaDeclinedRecently(){
    try{
        var t=+(localStorage.getItem(FSA_DECLINE_KEY)||0);
        return t>0&&(Date.now()-t)<FSA_DECLINE_DAYS*86400000;
    }catch(e){return false;}
}

/* ── The relay's upload leg: slices sized by the LINK, not by the file ──────
   Support chat 2026-09-25 (МаРиНиКа, Premium, Yandex-app browser, /trim, 8.7 MB
   MP3): «не могу скачать обрезанное аудио: всё время находится какой-то
   непреодолимый предел в процентах и загрузка сбрасывается куда-то в начало».
   Two hours, dozens of attempts; nginx shows every first 4 MB chunk landing in
   ~52 s (≈75 KB/s — a working link) and every second one dying 15–86 s in.

   The mechanism, read in Chromium's source and reproduced in a lab (real
   Chromium → HTTP/2 nginx → a shaped uplink with a buffer, as on cellular):
   an HTTP/2 upload is SILENT downstream — nginx buffers the body and answers
   only at the end. Chromium sends a PING with every DATA frame once nothing has
   been read for 10 s (kSpdyDefaultConnectionAtRiskOfLossSeconds), and if
   nothing arrives within the next 10 s (kHungIntervalSeconds) it tears the
   WHOLE connection down with ERR_HTTP2_PING_FAILED — then silently re-sends
   the request from byte zero, twice (kMaxRetryAttempts), before giving up.
   On a phone the PING waits in the socket/network buffer BEHIND the upload's
   own bytes, so with more than ~10 s of data queued it cannot come back in
   time. The XHR's progress falls back to 0 on each re-send — exactly «the
   percentage hits a limit and restarts from the beginning» — and our old
   stall watchdog read that fall as "no progress" and killed the request too.
   Prod, RU nginx, ~2 days: 13 % of whole-file stash POSTs from the Yandex app
   were cut by the client (239 of 1 848), 3.6 % of 4 MB chunks.

   The rule that follows, and why it is structural rather than tuned: a PING
   can only fail when the buffer ahead of it holds >10 s of data AND DATA frames
   are still being written >10 s after the last byte READ. Every slice's answer
   is a read. So a slice that takes no more than ~20 s of link time cannot
   produce a failed PING whatever the buffer; we aim at SLICE_TARGET_MS — a 5×
   margin for a misjudged rate — using the goodput the previous slice actually
   achieved end to end (the server's answer is the only honest clock: XHR
   progress counts bytes handed to the device's own socket, not bytes that
   arrived). The first slice is small because nothing is known yet. */
var SLICE_FIRST_BYTES=262144;
var SLICE_MIN_BYTES=131072;
/** MUST fit DownloadStashController::CHUNK_MAX_KB — DownloadStashLimitsAgreeTest. */
var SLICE_MAX_BYTES=8388608;
var SLICE_TARGET_MS=4000;
/** Uploads of the same file that are already on their way — a second press
 *  rides the first one instead of splitting the uplink with it. The reporter's
 *  log has two and three copies of her file racing each other.
 *  ⚠️ Keyed by IDENTITY (the Blob object, or the blob: URL a legacy anchor
 *  points at), never by name|size: she re-ran the trim while the first upload
 *  was on its way, and a new cut of the same length has the same name and the
 *  same size — joining the old upload would have delivered the OLD cut. */
var _stashInflight=(typeof Map==='function')?new Map():null;
/** Where an earlier, failed attempt of this file stopped, so the next press
 *  continues from the server's byte count instead of from zero. A Blob key
 *  lives in a WeakMap (a discarded export is not kept alive by this); a blob:
 *  URL key in a small table of the last few. */
function _resumeTable(){
    var weak=(typeof WeakMap==='function')?new WeakMap():null,byUrl={},order=[];
    return {
        get:function(k){return typeof k==='string'?byUrl[k]:(weak?weak.get(k):undefined);},
        set:function(k,v){
            if(typeof k==='string'){
                if(!byUrl[k]){order.push(k);if(order.length>16)delete byUrl[order.shift()];}
                byUrl[k]=v;return;
            }
            if(weak)weak.set(k,v);
        },
        'delete':function(k){if(typeof k==='string')delete byUrl[k];else if(weak)weak['delete'](k);}
    };
}
var _stashResume=_resumeTable();
/** The same, for files sent TO a server lane (__uploadBody): its upload ids
 *  belong to /api/upload-slice, never to the relay's store. */
var _upResume=_resumeTable();

/* __opfsOwner:begin */
/** Who owns an OPFS temp file — and the janitor that removes the files whose
 *  owner is gone.
 *
 *  Support chat 649 (26.09.2026, iPad, Yandex Browser, Premium): 55.5 GB of our
 *  data on his iPad before he paid, 38.5 GB again the next night, and the Photos
 *  picker could no longer hand over a 4K video («кружок заполнился и больше
 *  ничего»). Pages leave files in OPFS: every picked file above the RAM tier is
 *  copied to fb-spill (insurance against a revoked picker handle), every big
 *  export writes its result to _muxtmp_ / _cve_ and hands the person that very
 *  file, the player remuxes a movie it cannot play to vp_remux_. The page cleans
 *  up when the work ends — if it gets to: a closed tab, a navigation, a tab iOS
 *  killed for memory never do, and the sweeps that exist run late (_muxtmp_ at
 *  the NEXT big export and 6 h old, fb-spill 24 h) or never (_cve_, vp_remux_,
 *  the grant probes).
 *
 *  Age cannot tell a leftover from a file a live tab still reads — a result
 *  Blob is a view of its OPFS file, and deleting it breaks the download. The
 *  owner can: a page that writes a temp file holds a Web Lock named after
 *  itself for as long as it lives and puts the same id into the file name. The
 *  browser releases the lock with the page however it died, so the next page
 *  load anywhere on the site sees which owners are gone and removes exactly
 *  their files. A page that never writes one never takes the lock (a held lock
 *  keeps a page out of Chrome's back/forward cache).
 *
 *  Files a later page reads on purpose are pinned — FileBytes.spillInfo()
 *  (music-video-maker's restore manifests) and the slideshow's finished WebM
 *  (its salvage offer): pin(name, ms), kept in localStorage; the janitor leaves
 *  a pinned file alone until the pin runs out, and keeps every pinnable file of
 *  a dead owner when localStorage cannot be read. Untagged files — written
 *  before this, or by an engine without Web Locks — keep an age rule per kind.
 *  Only the kinds in KINDS are ever touched: recordings, drafts, checkpoints and
 *  results kept on purpose live under other names.
 *
 *  window.__opfsOwner: { id(), tag(sep), ownerOf(name), pin(name, ms),
 *  pinned(name), sweep() → Promise<result>, lastSweep: Promise|null }.
 *  Guards: tests/js/opfs-owner.test.mjs,
 *  tests/playwright/opfs-janitor-removes-dead-owners.mjs */
(function(){
    if(window.__opfsOwner)return;
    var LOCK='tb-opfs-owner:',PINS='tb_opfs_pins',H=3600000;
    /* ttl — the age rule for an UNTAGGED file of the kind: the same 6 h the
       pipeline's own sweep used for _muxtmp_, 24 h the FileBytes sweep used for
       spills (a music-video-maker saved result is offered for 24 h), 12 h for a
       remuxed movie a paused player may still be reading, 1 h for a probe that
       lives for seconds. pin — whether a later page reads the kind on purpose. */
    var KINDS=[
        {k:'muxtmp',re:/^_muxtmp_(\d{12,14})/,ttl:6*H},
        {k:'cve',re:/^_cve_(\d{12,14})/,ttl:6*H,pin:1},
        {k:'mux',re:/^_mux_(\d{12,14})/,ttl:6*H},
        {k:'probe',re:/^_(?:mem|room)_probe_(\d{12,14})/,ttl:H},
        {k:'remux',re:/^vp_remux_(\d{12,14})/,ttl:12*H},
        {k:'dlr',re:/^_dlr_(\d{12,14})/,ttl:H,pin:1},
        /* The shared audio writer's results (audio-output-sink.js). Until
           14.10.2026 it swept them itself, by the age in the NAME — and took the
           results of living pages with the leftovers (a result two hours old,
           still held, died at the next export in any tab). `sink` is the name
           it writes now (stamp and owner first); `sinkold` the one before
           (`_audio_sink_<label>_<stamp>.bin`): no owner there, and its label may
           LOOK like a tag (`…-overdubmix12_…`), so it is never read for one. */
        {k:'sink',re:/^_audio_sink_(\d{12,14})/,ttl:6*H},
        {k:'sinkold',re:/^_audio_sink_.*_(\d{12,14})\.bin$/,ttl:H,untagged:1}
    ];
    var SPILL={k:'spill',dir:'fb-spill',re:/^s(\d{12,14})-/,ttl:24*H,pin:1};
    /* Kinds that live in their own folder of the private file system. `replay` —
       the screen recorder's instant-replay loop and its clips (dashcam's
       LoopWriter in a storage worker, «sr-replay/», 26.10.2026): seconds to
       minutes of encoded video per page, worthless once the page is gone. */
    var DIRS=[SPILL,{k:'replay',dir:'sr-replay',re:/^r(\d{12,14})-/,ttl:6*H}];
    var myId=null;
    function locksApi(){
        try{return !!(navigator.locks&&typeof navigator.locks.request==='function'&&typeof navigator.locks.query==='function');}
        catch(_){return false;}
    }
    /* The id is taken — and the lock requested — the first time a file needs it,
       BEFORE the file exists: a janitor that lists the file finds the request at
       least pending. '' without Web Locks: the file is then untagged, i.e. aged. */
    function id(){
        if(myId!==null)return myId;
        myId='';
        if(!locksApi())return myId;
        var v=Date.now().toString(36)+Math.random().toString(36).slice(2,6);
        try{
            var p=navigator.locks.request(LOCK+v,function(){return new Promise(function(){});});
            if(p&&p.catch)p.catch(function(){});
            myId=v;
        }catch(_){}
        return myId;
    }
    function tag(sep){var v=id();return v?(sep||'_')+'o'+v:'';}
    function ownerOf(name){
        var m=/[_-]o([0-9a-z]{8,16})(?=[._-]|$)/.exec(String(name||'').replace(/\.crswap$/,''));
        return m?m[1]:'';
    }
    /* → {name: expiresAtMs} with the expired ones dropped, or null when
       localStorage cannot be read (a pinned file must then be assumed pinned). */
    function readPins(){
        var raw;
        try{raw=window.localStorage.getItem(PINS);}catch(_){return null;}
        var o={},now=Date.now(),k;
        try{o=raw?JSON.parse(raw)||{}:{};}catch(_){o={};}
        for(k in o)if(!(o[k]>now))delete o[k];
        return o;
    }
    function pin(name,ms){
        if(!name)return false;
        var o=readPins();if(!o)return false;
        o[String(name)]=Date.now()+Math.max(0,+ms||0);
        try{window.localStorage.setItem(PINS,JSON.stringify(o));return true;}catch(_){return false;}
    }
    function pinned(name){var o=readPins();return !!(o&&o[String(name)]);}
    /* Names of the FILES in dir (a directory is never ours to remove). */
    async function listNames(dir){
        var out=[],it=dir.entries(),st;
        /* A manual iterator, not for-await: Safari once rejected the for-await form
           at PARSE time and took the whole file with it (file-buffer-cache.js). */
        while(!(st=await it.next()).done){if(st.value[1]&&st.value[1].kind==='file')out.push(st.value[0]);}
        return out;
    }
    async function sweep(){
        var t0=Date.now(),now=t0;
        /* A dead owner's file goes once nothing has written it for this long — not at
           once: a download of an export's result still reads its OPFS file after
           the person has moved on to another page of the site (a 4.2 GB blob took
           58 s in Chrome 152), and __downloadFile pins only a File it can name. */
        var grace=typeof window.__opfsJanitorGraceMs==='number'?window.__opfsJanitorGraceMs:10*60000;
        var r={scanned:0,removed:0,bytes:0,kinds:{},legacy:0,live:0,kept_live:0,kept_pin:0,ms:0};
        if(!(navigator.storage&&typeof navigator.storage.getDirectory==='function'))return r;
        /* live === null — cannot tell who is alive: tagged files stay. */
        var live=null;
        if(locksApi()){
            try{
                var q=await navigator.locks.query();live={};
                [].concat(q.held||[],q.pending||[]).forEach(function(l){
                    var n=String((l&&l.name)||'');
                    if(n.indexOf(LOCK)===0){live[n.slice(LOCK.length)]=1;}
                });
                r.live=Object.keys(live).length;
            }catch(_){live=null;}
        }
        var pins=readPins();
        var root=await navigator.storage.getDirectory();
        async function consider(dir,name,rec){
            var base=name.replace(/\.crswap$/,''),m=rec.re.exec(base);
            if(!m)return;
            r.scanned++;
            var ts=+m[1],owner=rec.untagged?'':ownerOf(name),legacy=!owner;
            /* Unreadable pins keep a dead owner's pinnable file; an untagged one keeps
               its age rule, which is what the kind had before (FileBytes: 24 h). */
            if(rec.pin&&((pins&&(pins[name]||pins[base]))||(pins===null&&owner))){r.kept_pin++;return;}
            if(owner&&(live===null||live[owner]||owner===myId)){r.kept_live++;return;}
            /* Age from the LAST write: an export that ran for hours is as old as its end. */
            var size=0,mod=0;
            try{var f=await (await dir.getFileHandle(name)).getFile();size=f.size||0;mod=+f.lastModified||0;}catch(_){}
            var age=now-Math.max(ts,mod);
            if(owner?age<grace:!(age>rec.ttl))return;
            try{await dir.removeEntry(name);}catch(_){return;}
            r.removed++;r.bytes+=size;if(legacy)r.legacy++;
            var kk=r.kinds[rec.k]||(r.kinds[rec.k]={n:0,b:0});kk.n++;kk.b+=size;
        }
        var names=[];
        try{names=await listNames(root);}catch(_){}
        for(var i=0;i<names.length;i++){
            for(var j=0;j<KINDS.length;j++){
                if(KINDS[j].re.test(names[i].replace(/\.crswap$/,''))){await consider(root,names[i],KINDS[j]);break;}
            }
        }
        for(var di=0;di<DIRS.length;di++){
            try{
                var sd=await root.getDirectoryHandle(DIRS[di].dir);
                var sn=await listNames(sd);
                for(var s=0;s<sn.length;s++)await consider(sd,sn[s],DIRS[di]);
            }catch(_){}
        }
        /* Since 27.09.2026 FileBytes writes its copies into their own storage
           bucket `copies` where the browser has buckets (file-buffer-cache.js,
           _opfsDir) — the same names, the same owner tags, one more place to
           look. keys() first: open() would create the bucket in a browser that
           never wrote a copy. */
        try{
            var sbk=navigator.storageBuckets;
            if(sbk&&typeof sbk.keys==='function'&&(await sbk.keys()).indexOf('copies')!==-1){
                var cb=await sbk.open('copies');
                var cd=await (await cb.getDirectory()).getDirectoryHandle(SPILL.dir);
                var cn=await listNames(cd);
                for(var c=0;c<cn.length;c++)await consider(cd,cn[c],SPILL);
            }
        }catch(_){}
        r.ms=Date.now()-t0;
        if(r.removed>0){
            try{
                var ks=Object.keys(r.kinds).map(function(k){return k+':'+r.kinds[k].n+'/'+Math.round(r.kinds[k].b/1048576);}).join(';');
                window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_janitor',n:r.removed,
                    mb:Math.round(r.bytes/1048576),kinds:ks,legacy:r.legacy,live:r.live,kept_pin:r.kept_pin,ms:r.ms});
            }catch(_){}
        }
        return r;
    }
    /* One janitor at a time across the tabs — two would race for the same files. */
    function sweepOnce(){
        if(locksApi()){
            return navigator.locks.request('tb-opfs-janitor',{ifAvailable:true},function(l){return l?sweep():null;});
        }
        return sweep();
    }
    var api={id:id,tag:tag,ownerOf:ownerOf,pin:pin,pinned:pinned,
        sweep:function(){var p=sweepOnce();api.lastSweep=p;return p;},lastSweep:null};
    window.__opfsOwner=api;
    /* Once per page, after the page has settled — the listing is cheap, but it is
       storage IO on a phone that is loading a tool. */
    function schedule(){
        if(!(navigator.storage&&typeof navigator.storage.getDirectory==='function'))return;
        var go=function(){
            try{api.sweep().catch(function(){});}catch(_){}
            /* Renders kept in windows (ResumableRender, rr-checkpoints/ — up to a
               result's size on a phone): a person who never comes back to the
               tool must not keep them forever. Their horizon lives in that
               module, so it is loaded only when such a directory exists — one
               cheap lookup on every other page. */
            try{
                navigator.storage.getDirectory().then(function(r){return r.getDirectoryHandle('rr-checkpoints');}).then(function(){
                    var run=function(){try{window.ResumableRender&&window.ResumableRender.purge();}catch(_){}};
                    if(window.ResumableRender)return run();
                    var s=document.createElement('script');
                    s.src=(typeof window.__jsSrc==='function')?window.__jsSrc('/js/resumable-render.js'):'/js/resumable-render.js';
                    s.onload=run;document.head.appendChild(s);
                }).catch(function(){});
            }catch(_){}
        };
        var later=function(){
            if(typeof requestIdleCallback==='function')requestIdleCallback(go,{timeout:10000});
            else setTimeout(go,1000);
        };
        setTimeout(later,typeof window.__opfsJanitorDelayMs==='number'?window.__opfsJanitorDelayMs:6000);
    }
    try{
        if(document.prerendering)document.addEventListener('prerenderingchange',schedule,{once:true});
        else if(document.readyState==='complete')schedule();
        else window.addEventListener('load',schedule,{once:true});
    }catch(_){}
})();
/* __opfsOwner:end */

/* __opfsMuxTarget:begin */
/** OPFS stream target for mp4-muxer / webm-muxer that hands the browser
 *  EXACT-SIZE buffers only.
 *
 *  WebKit's FileSystemWritableFileStream writes the WHOLE ArrayBuffer behind a
 *  typed-array view, not the view (Source/WebCore/Modules/filesystem/
 *  FileSystemWritableFileStreamSink.cpp, fetchDataBytesForWrite:
 *  `bufferView->possiblySharedBuffer()` → `buffer->span()`; the sync access
 *  handle next to it uses the view's own span and is fine). The muxers' own
 *  FileSystemWritableFileStreamTarget hands it `subarray` views of their
 *  16 MiB chunk buffers, so on Safari 26 every export ends on a 16 MiB
 *  boundary, padded with the chunk's zeros: RU, 30 d to 15.09.2026, 394 of
 *  1 767 mobile OPFS slideshow exports were exact multiples of 16 MiB, +2.3 MB
 *  on average, and the 64 MiB «Slideshow» of report #7211 was one. A patch
 *  written at a non-zero offset inside a chunk — moov-at-end mode seeking back
 *  for the mdat size, the reserved-moov mode writing the head at finalize —
 *  would put up to 16 MiB of the patch chunk's contents over the file.
 *  Chromium and Firefox honour the view, so nobody saw it on a desktop.
 *
 *  A view that does not cover its buffer is copied into one that does: one
 *  memcpy per flushed section, and both engines write the same bytes. Guards:
 *  tests/js/opfs-mux-target.test.mjs, OpfsMuxTargetsGoThroughThePrimitiveTest
 *  (no site constructs the muxers' FileSystemWritableFileStreamTarget itself). */
window.__exactBytes=function(data){
    if(!data||!ArrayBuffer.isView(data))return data;
    if(data.byteOffset===0&&data.byteLength===data.buffer.byteLength)return data;
    return new Uint8Array(data.buffer.slice(data.byteOffset,data.byteOffset+data.byteLength));
};
window.__opfsMuxTarget=function(MuxerNs,writable,opts){
    opts=opts||{};
    if(!MuxerNs||typeof MuxerNs.StreamTarget!=='function')throw new Error('opfs-mux-target: muxer namespace without StreamTarget');
    if(!writable||typeof writable.write!=='function')throw new Error('opfs-mux-target: writable stream required');
    /* end — the file's length is the furthest byte written (the muxers write at
       explicit positions, and seek back to patch); __opfsMuxLane trims its
       reservation down to it. failed — the first rejected write: the muxers
       never await onData, so without a handler here a full disk surfaced only
       as an unhandled rejection (support chat 647) and as close() failing on
       an errored stream, minutes later. */
    var st={writes:0,copies:0,bytes:0,end:0,failed:null};
    var o={onData:function(data,position){
        st.writes++;st.bytes+=data.byteLength;
        if(typeof position==='number'&&position+data.byteLength>st.end)st.end=position+data.byteLength;
        var d=window.__exactBytes(data);
        if(d!==data)st.copies++;
        var p=writable.write({type:'write',data:d,position:position});
        if(p&&typeof p.catch==='function')p.catch(function(e){
            if(st.failed)return;
            st.failed=e||new Error('write-failed');
            /* The lane says what it was when the write was refused (onFail): until
               14.10.2026 a refusal carried no facts, and 74 sessions a month could
               not be told apart — unreserved lane, outgrown reservation, file gone. */
            if(typeof opts.onFail==='function'){try{opts.onFail(st.failed,st,position);}catch(_){}}
        });
        return p;
    },chunked:true};
    if(opts.chunkSize!==undefined)o.chunkSize=opts.chunkSize;
    var t=new MuxerNs.StreamTarget(o);
    t.__opfsStats=st;
    return t;
};
/* __opfsMuxTarget:end */

/* __opfsMuxLane:begin */
/** The disk lane for a muxer — opened only when the browser will REALLY take
 *  the bytes, and holding them for the whole export.
 *
 *  Why a lane and not «open a file and hope». Support chat 647 (Em Olga,
 *  Premium, 24.09.2026, /slideshow-maker, Chrome 153, 8 GB): six exports ended
 *  in «устройству не хватило оперативной памяти», after 3–11 minutes of
 *  encoding each. It was never RAM. Her system disk was nearly full, and
 *  Chromium's grant to a site is usage + (free disk − ~1 GB): a few MB, or
 *  none. Opening the OPFS file costs nothing, so the export started; the
 *  muxer's writes — all of them at finalize — were refused with
 *  QuotaExceededError. Four other attempts that day, whose open happened to be
 *  refused up front, landed in memory (151 and 322 MB): the disk lane was an
 *  optimisation, and it was the only thing that failed.
 *
 *  navigator.storage.estimate() cannot see this: on most Chromium builds it
 *  reports usage + 10 GiB whatever the real grant (anti-fingerprinting static
 *  quota; device_cap, 2 d to 25.09.2026: sq − su = 10240 MB exactly in 81 % of
 *  Chrome, 94 % of Edge, 90 % of Opera sessions — hers too). truncate(N) on
 *  the fresh writable IS checked against the real grant: 1 ms at any N,
 *  refused exactly above the headroom (Chrome 154 under
 *  Storage.overrideQuotaForOrigin: 20 MB headroom takes 18, refuses 22). And
 *  the room it takes stays taken — a download filling the disk mid-export
 *  cannot take it back.
 *
 *  opts: { reserveBytes, prefix, ext, lane }
 *  → null when the grant refuses reserveBytes (the caller encodes into memory,
 *    as it always did when OPFS was unavailable; one `opfs_room_refused`
 *    event), else { target, writable, handle, root, name, reserved,
 *    unreservedWhy, diskShort, persisted, stats, finish() → Promise<File>,
 *    abort() }.
 *  Anything else going wrong on the way in throws, exactly as the inline
 *  open it replaces did — the callers already fall back on a throw.
 *
 *  A truncate refused for another reason (an engine without it) keeps the
 *  lane unreserved rather than losing the disk — and says so: `unreservedWhy`
 *  ('memory' — an off-the-record profile; 'truncate:<Name>') plus one
 *  `opfs_reserve_failed` event. A refused write reports the lane's facts once
 *  (`opfs_write_refused`: reserved, asked, where, how long after the open).
 *
 *  THE BROWSER MAY WIPE THIS STORAGE UNDER THE OPEN FILE (14.10.2026). 21 field
 *  sessions in 30 days ended in «NotFoundError: A requested file or directory
 *  could not be found» 5–64 minutes into an export. Reproduced in Chrome 155
 *  (tests/playwright/storage-eviction-probe.mjs): no page of the origin can
 *  remove a file under an open writable (NoModificationAllowedError — our own
 *  janitor is innocent, and the field agrees: 1 of 21), but Chromium's quota
 *  EVICTION does — 5 minutes after the browser starts and every 30 after, and
 *  at once on any full-disk error, it deletes the least recently accessed
 *  sites' storage while the disk has under 2 GiB free (min(2 GiB, 10 %)) and
 *  the profile holds at least half of the shortage. An open writable is no
 *  protection (6 arms of 6 lost the file being written, with that very
 *  error), and neither is re-opening one's own storage every 3 s. What is:
 *  storage the browser has made PERSISTENT (the durable-storage setting: the
 *  round took the other site, the file came out whole), memory, and a file
 *  the person picked. So the lane (1) asks persist() — Chromium decides
 *  silently, by the site's standing with the person; Firefox would show a
 *  prompt, it is not asked — and (2) when not persistent, checks after its
 *  reservation whether EVICT_FLOOR more would still fit (__storageFitsMore):
 *  if not, the disk is in the eviction zone and the lane says `diskShort`
 *  (one `opfs_disk_short` event). The callers take memory then when the file
 *  fits there.
 *  ⚠️ A DevTools permission override (Browser.grantPermissions durableStorage)
 *  makes persisted() answer true and does NOT stop the eviction — a stand
 *  that wants persistence writes the content setting into the profile.
 *  Guards: tests/js/opfs-mux-lane.test.mjs, tests/js/opfs-lane-says-what-it-was.test.mjs,
 *  tests/playwright/export-survives-a-full-disk.mjs. */
window.__opfsMuxLane=async function(MuxerNs,opts){
    opts=opts||{};
    if(!(navigator.storage&&typeof navigator.storage.getDirectory==='function'))return null;
    if(!MuxerNs||typeof MuxerNs.StreamTarget!=='function')return null;
    var reserve=Math.max(0,Math.round(+opts.reserveBytes||0));
    function isQuota(e){return !!(e&&(e.name==='QuotaExceededError'||e.code===22));}
    function refused(step){
        var r={mb:Math.round(reserve/1048576),lane:opts.lane||'',step:step,at:Date.now()};
        window.__opfsMuxLane.lastRefusal=r;
        try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_room_refused',mb:r.mb,lane:r.lane,step:step});}catch(_){}
        return null;
    }
    /* Below this much room left AFTER the reservation the disk is where Chromium
       evicts: it keeps 2 GiB free and refuses writes under ~1 GiB (1 % on a disk
       below 100 GB), so the grant left is «free − 1 GiB» there and the zone
       starts at 1 GiB left on a PC, at 1.4 GiB on a 64 GB phone. */
    var EVICT_FLOOR=1536*1048576;
    /* → Promise<boolean>: the browser will not evict this origin. Once per page. */
    function persistAsk(){
        var self=window.__opfsMuxLane;
        if(self.__persist)return self.__persist;
        var st=navigator.storage;
        if(!(st&&typeof st.persisted==='function'))return (self.__persist=Promise.resolve(false));
        function say(was,granted){try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_persist',was:was?1:0,granted:granted?1:0,lane:opts.lane||''});}catch(_){}}
        self.__persist=Promise.resolve().then(function(){return st.persisted();}).then(function(was){
            if(was){say(1,1);return true;}
            var ua='';try{ua=String(navigator.userAgent||'');}catch(_){}
            /* Only an engine that decides WITHOUT asking the person: Chromium
               (measured in Chrome 155 and Yandex Browser: 1–21 ms, no prompt). */
            if(typeof st.persist!=='function'||!/Chrome\/\d/.test(ua)||/Firefox\//.test(ua))return false;
            return Promise.resolve(st.persist()).then(function(ok){say(0,ok);return !!ok;});
        }).catch(function(){return false;});
        return self.__persist;
    }
    var root=await navigator.storage.getDirectory();
    /* The owner tag lets the next page load remove the file once this page is
       gone (__opfsOwner above) — a result file outlives its export otherwise. */
    var name=(opts.prefix||'_mux_')+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||'')+'.'+(opts.ext||'mp4');
    var handle,writable;
    /* With (almost) no grant at all even the empty file or its swap is refused —
       the same class, counted the same way, one step earlier. */
    try{handle=await root.getFileHandle(name,{create:true});}
    catch(e){if(isQuota(e))return refused('open');throw e;}
    try{writable=await handle.createWritable();}
    catch(e){try{await root.removeEntry(name);}catch(_){}if(isQuota(e))return refused('open');throw e;}
    var reserved=0;
    /* WHY the lane holds no room although it was asked to — '' when it does (or
       was not asked). A lane without its room is the one whose writes are refused
       minutes into the work: measured 14.10.2026 on Chrome 155 and Yandex 26.8,
       a write INSIDE a held reservation is never refused, even with the grant
       dropped 150 MB below what is used — so every `opfs-write-failed` in the
       field (74 sessions in 30 days) came from a lane like this, or from a file
       that outgrew its room. The caller reads `unreservedWhy` and takes memory
       instead when the file fits there (CanvasVideoExport, webcodecs-pipeline). */
    var unreservedWhy='';
    /* An off-the-record profile keeps OPFS in MEMORY (see __opfsInMemory): a
       reservation there is a RAM block of the export's size at once, and past
       ~2 GiB the end of the browser — the lane stays unreserved, as before 25.09. */
    if(reserve>0&&typeof window.__opfsInMemory==='function'){try{if(await window.__opfsInMemory()){reserve=0;unreservedWhy='memory';}}catch(_){}}
    if(reserve>0&&typeof writable.truncate==='function'){
        try{await writable.truncate(reserve);reserved=reserve;}
        catch(e){
            if(isQuota(e)){
                try{await writable.abort();}catch(_){}
                try{await root.removeEntry(name);}catch(_){}
                return refused('reserve');
            }
            /* Not a refusal — and the stream is errored now; start it again, unreserved.
               Counted: until 14.10.2026 this branch left no trace at all. */
            unreservedWhy='truncate:'+String((e&&e.name)||'Error');
            try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_reserve_failed',mb:Math.round(reserve/1048576),lane:opts.lane||'',name:String((e&&e.name)||'Error')});}catch(_){}
            try{await writable.abort();}catch(_){}
            writable=await handle.createWritable();
        }
    }
    /* The room is held — will the browser leave the file alone? (see the header) */
    var persisted=false,diskShort=false;
    /* Neither question may cost the lane: an answer that does not come is «no» / «cannot tell». */
    function soon(p,ms,dflt){
        return new Promise(function(res){
            var t=setTimeout(function(){res(dflt);},ms);
            Promise.resolve(p).then(function(v){clearTimeout(t);res(v);},function(){clearTimeout(t);res(dflt);});
        });
    }
    if(reserved>0){
        try{persisted=!!(await soon(persistAsk(),2000,false));}catch(_){}
        if(!persisted){
            var more=null;
            try{if(typeof window.__storageFitsMore==='function')more=await soon(window.__storageFitsMore(EVICT_FLOOR),3000,null);}catch(_){}
            if(more===false){
                diskShort=true;
                try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_disk_short',mb:Math.round(reserved/1048576),lane:opts.lane||''});}catch(_){}
            }
        }
    }
    var openedAt=Date.now();
    var targetOpts=Object.assign({},opts.targetOpts,{onFail:function(e,s,position){
        try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_write_refused',lane:opts.lane||'',picked:0,short:diskShort?1:0,persisted:persisted?1:0,
            name:String((e&&e.name)||'Error'),reserved_mb:Math.round(reserved/1048576),asked_mb:Math.round((+opts.reserveBytes||0)/1048576),
            at_mb:Math.round((+position||0)/1048576),end_mb:Math.round(s.end/1048576),writes:s.writes,unreserved:unreservedWhy,
            age_s:Math.round((Date.now()-openedAt)/1000)});}catch(_){}
    }});
    var target=window.__opfsMuxTarget(MuxerNs,writable,targetOpts);
    var st=target.__opfsStats;
    var closed=false,gone=false;
    function drop(){
        if(closed||gone)return;gone=true;
        try{writable.abort().catch(function(){});}catch(_){}
        try{root.removeEntry(name).catch(function(){});}catch(_){}
    }
    return{
        target:target,writable:writable,handle:handle,root:root,name:name,reserved:reserved,unreservedWhy:unreservedWhy,
        diskShort:diskShort,persisted:persisted,stats:st,
        finish:async function(){
            if(closed||gone)throw new Error('opfs-lane-finished');
            try{
                /* The reservation is longer than the file: give the rest back. A write past
                   it (the estimate was low) simply grew the file — nothing to trim then. */
                if(reserved>0&&st.end<reserved&&!st.failed)await writable.truncate(st.end);
                await writable.close();
            }catch(e){drop();throw e;}   // a half-written temp file must not sit in the grant
            closed=true;
            return handle.getFile();
        },
        abort:drop
    };
};
/* __opfsMuxLane:end */

/* __pickedMuxLane:begin */
/** A muxer's disk lane over a file the PERSON picked — the way out when the
 *  browser's own storage has no room for the result.
 *
 *  Report 12.10.2026 (/video-editor, Yandex Browser on Windows, 16 GB of RAM):
 *  a 16-minute 1080p montage, ≈ 985 MB. Five builds in 25 minutes ended in «на
 *  диске не хватает места»: the origin's file system lives in the browser
 *  profile on the SYSTEM disk, hers was nearly full, and the page had nowhere
 *  else to put the file — «не предлагает никакой траектории для загрузки».
 *  A file picked with showSaveFilePicker is an ordinary file on whatever disk
 *  the person points at: its writes are not charged to the origin's grant, and
 *  Chromium keeps the swap (.crswap) NEXT TO the target, so the system disk is
 *  not asked for anything. MEASURED 14.10.2026 with the real native dialog
 *  (Chrome 155 on a hidden desktop): under a ZERO grant the origin's own
 *  storage refuses 4 MB while the picked file takes a 1.5 GB reservation and
 *  320 MB of writes; the swap sits in the chosen folder, the reservation takes
 *  real room there (NTFS: 4 GB in 45 ms, no zero-fill), the browser profile
 *  does not grow; close() of 1 GB takes ~17 s (scan + move).
 *  ⚠️ Chrome answers a pick inside a «sensitive» folder (AppData, the profile,
 *  system folders) with its own «can't save here» and the promise stays
 *  pending until the person chooses again or cancels.
 *  ⚠️ A file the person agrees to replace is emptied BY THE DIALOG: the handle
 *  arrives with size 0 — there are no old bytes left to protect.
 *
 *  __pickSaveHandle({suggestedName, mime}) → Promise<FileSystemFileHandle|null>
 *    null — no such API here, or the person closed the dialog; why is in
 *    __pickSaveHandle.last ('no_api' | 'cancel' | 'no_dialog' | 'blocked').
 *    Call it first thing in the click: the dialog needs the gesture.
 *    'no_dialog' is an AbortError faster than a person can read a dialog — a
 *    host that never painted one (see FSA_NO_DIALOG_MS in __downloadFile).
 *  __pickedMuxLane(MuxerNs, handle, {reserveBytes, lane, reserveTimeoutMs, onFail})
 *    → the shape of __opfsMuxLane: { target, writable, handle, name, reserved,
 *    stats, picked:true, finish() → Promise<File>, abort() }; null when the
 *    chosen disk refuses the reservation (one `picked_room_refused` event) —
 *    the caller says «no room there either» and never falls back to memory
 *    behind the person's choice.
 *    A reservation that takes longer than reserveTimeoutMs (FAT32 zero-fills a
 *    file as it grows — a USB stick) is given up, not waited for: the lane
 *    opens unreserved and a full disk shows as stats.failed on the first
 *    refused write. onFail(err) hears that write at once — a live recording
 *    stops feeding a stream that is already dead.
 *    abort() discards the swap and removes the file when it is EMPTY — which
 *    is every file the save dialog hands over; a handle that still has bytes
 *    (not from the dialog) is never removed.
 *  Guards: tests/js/picked-mux-lane.test.mjs. */
window.__pickSaveHandle=function(opts){
    opts=opts||{};
    var self=window.__pickSaveHandle;
    function out(why,ms){self.last={why:why,ms:ms||0,at:Date.now()};return null;}
    if(typeof window.showSaveFilePicker!=='function')return Promise.resolve(out('no_api'));
    var name=String(opts.suggestedName||'file');
    var po={suggestedName:name};
    var ext=(/\.[a-z0-9]{1,8}$/i.exec(name)||[])[0];
    if(opts.mime&&ext){var acc={};acc[opts.mime]=[ext.toLowerCase()];po.types=[{accept:acc}];}
    var t0=Date.now(),p;
    try{p=window.showSaveFilePicker(po);}catch(e){return Promise.resolve(out('blocked'));}
    return p.catch(function(e){
        // TypeError — the typed filter was rejected: once more, untyped.
        if(e&&e.name==='TypeError'&&po.types)return window.showSaveFilePicker({suggestedName:name});
        throw e;
    }).then(function(h){self.last={why:'picked',ms:Date.now()-t0,at:Date.now()};return h||null;},function(e){
        var ms=Date.now()-t0;
        if(e&&e.name==='AbortError')return out(ms<400?'no_dialog':'cancel',ms);
        return out('blocked',ms);
    });
};
window.__pickSaveHandle.available=function(){return typeof window.showSaveFilePicker==='function';};
/** A result the person HAS on their disk without a download: written into a
 *  folder they picked (showDirectoryPicker) or into the file they named in the
 *  save dialog, close() confirmed. Reported as the download door reports one —
 *  `file_download` with the verdict `ok:1`, the extension and the size — because
 *  every reader of «did the person get the result» (BuyerActivity::isDelivery,
 *  RenderedPaidService, the delivery census) reads exactly that row. Until
 *  2026-10-22 these lanes said nothing: refund request №51 — 26 HEIC → JPG
 *  written into the buyer's folder — showed «забрал 0» and a п. 1.5.1 count of
 *  0. One call per file. No `file-downloaded`: its listeners are the «saved to
 *  Downloads» toast and the download history, and a place the person chose is
 *  neither. (A file written through __pickedMuxLane is reported by its tool as
 *  `picked_stream` — not here, or it would count twice.)
 *  __deliveredToDisk(name, bytesOrBlobOrSize, via)  via: 'folder' | 'save_handle'
 *  Guard: DiskWritesReportTheDeliveryTest. */
window.__deliveredToDisk=function(name,bytes,via){
    try{
        if(!window.__ae)return;
        var n=typeof bytes==='number'?bytes:(bytes?(bytes.size!=null?bytes.size:(bytes.byteLength||0)):0);
        var m=/\.([A-Za-z0-9]{1,8})$/.exec(String(name||''));
        window.__ae('feature_use',{slug:window.__TOOL_SLUG||((document.querySelector('meta[name="tool-slug"]')||{}).content)||'',
            f:'file_download',via:via==='save_handle'?'save_handle':'folder',src:'disk',ok:1,
            ext:m?m[1].toLowerCase():'',sizeKB:Math.round(n/1024)});
    }catch(e){}
};
window.__pickedMuxLane=async function(MuxerNs,handle,opts){
    opts=opts||{};
    if(!handle||typeof handle.createWritable!=='function')throw new Error('picked-mux-lane: a file handle is required');
    if(!MuxerNs||typeof MuxerNs.StreamTarget!=='function')throw new Error('picked-mux-lane: muxer namespace without StreamTarget');
    var reserve=Math.max(0,Math.round(+opts.reserveBytes||0));
    var patience=+opts.reserveTimeoutMs>0?+opts.reserveTimeoutMs:15000;
    function isQuota(e){return !!(e&&(e.name==='QuotaExceededError'||e.code===22));}
    function ev(f,more){try{window.__ae&&window.__ae('feature_use',Object.assign({slug:window.__TOOL_SLUG||'',f:f,mb:Math.round(reserve/1048576),lane:opts.lane||''},more||{}));}catch(_){}}
    /* What the dialog handed over: a file it has just created is empty — ours
       to remove if the build does not finish; one with bytes is the person's. */
    var wasEmpty=false;
    try{var f0=await handle.getFile();wasEmpty=!f0||!f0.size;}catch(_){}
    var writable=await handle.createWritable();
    function forget(){
        if(!wasEmpty||typeof handle.remove!=='function')return;
        try{handle.remove().catch(function(){});}catch(_){}
    }
    var reserved=0;
    if(reserve>0&&typeof writable.truncate==='function'){
        var slow=false,timer=null;
        var late=new Promise(function(res){timer=setTimeout(function(){slow=true;res('slow');},patience);});
        var took=writable.truncate(reserve).then(function(){return 'ok';},function(e){return e||new Error('truncate-failed');});
        var r=await Promise.race([took,late]);
        clearTimeout(timer);
        if(r==='ok'&&!slow){reserved=reserve;}
        else{
            /* Refused, failed, or still filling: this stream is of no further use. */
            try{writable.abort().catch(function(){});}catch(_){}
            if(r!=='slow'&&isQuota(r)){forget();ev('picked_room_refused');return null;}
            if(r==='slow')ev('picked_reserve_slow',{ms:patience});
            writable=await handle.createWritable();
        }
    }
    var openedAt=Date.now();
    var target=window.__opfsMuxTarget(MuxerNs,writable,Object.assign({},opts.targetOpts,{onFail:function(e,s,position){
        ev('opfs_write_refused',{picked:1,name:String((e&&e.name)||'Error'),reserved_mb:Math.round(reserved/1048576),asked_mb:Math.round(reserve/1048576),
            at_mb:Math.round((+position||0)/1048576),end_mb:Math.round(s.end/1048576),writes:s.writes,unreserved:'',age_s:Math.round((Date.now()-openedAt)/1000)});
        /* The caller hears it at once — a live recording must stop feeding a dead stream. */
        if(typeof opts.onFail==='function'){try{opts.onFail(e);}catch(_){}}
    }}));
    var st=target.__opfsStats;
    var closed=false,gone=false;
    function drop(){
        if(closed||gone)return;gone=true;
        var a;
        try{a=writable.abort();}catch(_){}
        Promise.resolve(a).catch(function(){}).then(forget);
    }
    return{
        target:target,writable:writable,handle:handle,name:handle.name||'',reserved:reserved,stats:st,picked:true,
        finish:async function(){
            if(closed||gone)throw new Error('picked-lane-finished');
            try{
                if(reserved>0&&st.end<reserved&&!st.failed)await writable.truncate(st.end);
                await writable.close();
            }catch(e){drop();throw e;}
            closed=true;
            return handle.getFile();
        },
        abort:drop
    };
};
/* __pickedMuxLane:end */

/* __memoryMuxTarget:begin */
/** A muxer's target in MEMORY without the one growing buffer — the lane for a
 *  big file when the disk is not an option (the grant refused the room, the
 *  lane could not hold it, the person chose «build in memory»).
 *
 *  The muxers' own ArrayBufferTarget is ONE ArrayBuffer that doubles: past
 *  1 GiB it asks for 2 GiB, which Chromium refuses, and with mp4 fastStart
 *  'in-memory' the chunks are held beside it until finalize. Measured
 *  14.10.2026 (Chrome 155, the site's mp4-muxer, fake encoded chunks, private
 *  bytes of the whole browser tree read from the OS):
 *      file      one buffer                      blocks
 *      1000 MB   whole, peak 2.9–4.0 GB          whole, peak 1.5 GB
 *      1100 MB   RangeError at FINALIZE          whole
 *      1900 MB   RangeError                      whole
 *      finalize  1.2–2.6 s                       20–60 ms
 *  — «at finalize» meaning after every frame had been encoded. The blocks are
 *  16 MiB arrays addressed by position (the muxers' chunked stream target
 *  writes by position and seeks back to patch) and go into one Blob as they
 *  are: no copy into a second buffer, no allocation larger than a block.
 *
 *  __memoryMuxTarget(MuxerNs, {blockSize}) → { target, stats:{writes, bytes,
 *    end, blocks}, finish(type) → Blob (the blocks are let go), release() }.
 *    (finish — not the canvas method's name: a guard counts every call spelled that way.)
 *    The muxer must write a STREAM layout: mp4 `fastStart:false` (moov at the
 *    end) or a reserved head (`fastStart:{expectedVideoChunks…}`) — never
 *    'in-memory', which keeps the whole file a second time.
 *  __memoryMuxCeiling() → the largest file, in bytes, this lane is offered on
 *    this device (the file lives in RAM, ~1.3x at the peak): 1536 MiB with
 *    8 GB or more, 640 MiB with 4 GB, 256 MiB below; a browser that does not
 *    say (Safari, Firefox) counts as 8 GB on a desktop and 4 GB on a phone.
 *  Guards: tests/js/memory-mux-target.test.mjs. */
window.__memoryMuxTarget=function(MuxerNs,opts){
    opts=opts||{};
    if(!MuxerNs||typeof MuxerNs.StreamTarget!=='function')throw new Error('memory-mux-target: muxer namespace without StreamTarget');
    var BS=Math.max(65536,Math.round(+opts.blockSize||16*1048576));
    var blocks=[],st={writes:0,bytes:0,end:0,blocks:0};
    var t=new MuxerNs.StreamTarget({chunked:true,onData:function(data,position){
        var len=data.byteLength,off=0;
        st.writes++;st.bytes+=len;
        if(position+len>st.end)st.end=position+len;
        while(off<len){
            var bi=Math.floor((position+off)/BS),bo=(position+off)%BS,n=Math.min(BS-bo,len-off);
            var b=blocks[bi];
            if(!b){b=blocks[bi]=new Uint8Array(BS);st.blocks++;}
            /* A copy on purpose: the muxer reuses the buffer behind `data`. */
            b.set(data.subarray(off,off+n),bo);
            off+=n;
        }
    }});
    function release(){blocks=[];}
    return{target:t,stats:st,release:release,
        finish:function(type){
            var parts=[],nb=Math.ceil(st.end/BS);
            for(var i=0;i<nb;i++){
                var last=i===nb-1,want=last?(st.end-i*BS):BS;
                /* A block the muxer never touched reads as zeros, like a hole in a file. */
                var b=blocks[i]||new Uint8Array(want);
                parts.push(b.byteLength===want?b:b.subarray(0,want));
            }
            var blob=new Blob(parts,{type:type||'application/octet-stream'});
            release();
            return blob;
        }};
};
window.__memoryMuxCeiling=function(){
    var MiB=1048576,dm=+navigator.deviceMemory||0;
    if(!dm){
        var mob=false;
        try{mob=!!(navigator.userAgentData&&navigator.userAgentData.mobile)||/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent||'');}catch(_){}
        dm=mob?4:8;
    }
    return (dm>=8?1536:dm>=4?640:256)*MiB;
};
/* __memoryMuxTarget:end */

/* __muxVideoTrack:begin */
/** The one door from a VideoEncoder's output into mp4-muxer's video track:
 *    var addVideo=window.__muxVideoTrack(muxer);            // once per muxer
 *    addVideo(chunk,meta,timestamp,compositionTimeOffset);  // muxer.addVideoChunk's own arguments
 *
 *  mp4-muxer keeps ONE decoder configuration per track: every later
 *  meta.decoderConfig is Object.assign'ed over the first, so the file's avcC is
 *  whatever the encoder announced LAST. Chromium before 130 encodes software
 *  H.264 with OpenH264's default INCREASING_ID (Chromium 59604ef941, 09.2024,
 *  moved it to SPS_LISTING): every IDR — ours every 2 s, plus the encoder's own
 *  at a scene cut — gets NEW SPS/PPS ids, which Chromium strips from the sample
 *  and announces as a new decoderConfig. Each GOP of such a file refers to
 *  parameter sets the file does not hold: players show one picture and play the
 *  sound under it (report #8247, 30.09.2026, Yandex 22.9 = Chromium 104 on
 *  Linux: «первый слайд — и всё, дальше на его фоне песня»; FFmpeg: «non-existing
 *  PPS 33 referenced», 1604 of 1680 packets lost). RU, 30 d to 30.09.2026: ~70
 *  slideshow MP4s took that lane (Chrome 109 on Windows 7/8, old Opera, Yandex
 *  on Linux). The same overwrite hits a lane that REBUILDS its encoder
 *  mid-export (hardware died → software) in any browser: the frames before the
 *  rebuild are read with the new encoder's SPS.
 *
 *  So the first configuration stays the track's, and from the first CHANGE of
 *  parameter sets on, every keyframe carries the sets of its own configuration
 *  in-band — length-prefixed NAL units at the head of the sample, the way a
 *  TS→MP4 remux keeps them — so each sync sample decodes on its own and seeking
 *  works; later decoderConfigs never reach the muxer. A stream whose sets never
 *  change (hardware encoders, Chromium ≥ 130) goes through argument for
 *  argument, as before; so does every non-AVC track.
 *    window.__muxVideoTrack.paramSets(avcC) → {len, nals} | null
 *    window.__muxVideoTrack.withParamSets(sample, sets, len) → the sample with the sets in-band
 *  (the dashcam's ring stores keyframes that way: its stretches are muxed later).
 *  Guards: tests/js/mux-video-track.test.mjs, MuxVideoTrackGoesThroughTheDoorTest. */
(function(){
    function bytesOf(d){
        if(!d)return null;
        if(d instanceof Uint8Array)return d;
        if(ArrayBuffer.isView(d))return new Uint8Array(d.buffer,d.byteOffset,d.byteLength);
        if(d instanceof ArrayBuffer)return new Uint8Array(d);
        return null;
    }
    /* avcC → {len: the NAL length size, nals: [SPS…, PPS…]}; null when it is not one. */
    function paramSets(desc){
        var b=bytesOf(desc);
        if(!b||b.length<7||b[0]!==1)return null;
        var nals=[],p=5,k,i,n,count;
        for(k=0;k<2;k++){
            if(p>=b.length)return null;
            count=k?b[p]:(b[p]&31);p++;
            for(i=0;i<count;i++){
                if(p+2>b.length)return null;
                n=(b[p]<<8)|b[p+1];p+=2;
                if(!n||p+n>b.length)return null;
                nals.push(b.subarray(p,p+n));p+=n;
            }
        }
        if(!nals.length)return null;
        /* Firefox #458 doubles the header byte of every set (sanitizeAvcC in
           canvas-video-export.js repairs the track's copy; the in-band copy gets the same). */
        var s0=nals[0];
        if(s0.length>=3&&(s0[0]&31)===7&&s0[1]!==b[1]&&s0[2]===b[1])
            nals=nals.map(function(x){return(x.length>=2&&x[1]===x[0])?x.subarray(1):x;});
        return{len:(b[4]&3)+1,nals:nals};
    }
    function sig(ps){
        var s=ps.len+':';
        for(var i=0;i<ps.nals.length;i++)s+=Array.prototype.join.call(ps.nals[i],',')+';';
        return s;
    }
    /* A length-prefixed sample (NAL lengths of `from` bytes) written with `to`-byte
       lengths, `add` — parameter sets — at its head, after an access unit delimiter,
       which must stay first. A sample that already holds an SPS keeps its own; one
       whose lengths do not add up is returned untouched. */
    function reframe(sample,from,to,add){
        var b=bytesOf(sample);
        if(!b)return sample;
        var list=[],p=0,n,k,i;
        while(p<b.length){
            if(p+from>b.length)return sample;
            for(n=0,k=0;k<from;k++)n=n*256+b[p+k];
            p+=from;
            if(p+n>b.length)return sample;
            list.push(b.subarray(p,p+n));p+=n;
        }
        if(add)for(i=0;i<list.length;i++)if(list[i].length&&(list[i][0]&31)===7){add=null;break;}
        if(!add&&from===to)return sample;
        if(add){
            var at=(list.length&&list[0].length&&(list[0][0]&31)===9)?1:0;
            list=list.slice(0,at).concat(add,list.slice(at));
        }
        var max=to>=4?4294967295:Math.pow(256,to)-1,size=0;
        for(i=0;i<list.length;i++){if(list[i].length>max)return sample;size+=to+list[i].length;}
        var out=new Uint8Array(size);
        for(p=0,i=0;i<list.length;i++){
            for(n=list[i].length,k=to-1;k>=0;k--){out[p+k]=n&255;n=Math.floor(n/256);}
            p+=to;out.set(list[i],p);p+=list[i].length;
        }
        return out;
    }
    function withParamSets(sample,sets,len){return reframe(sample,sets.len,len||sets.len,sets.nals);}
    window.__muxVideoTrack=function(muxer){
        var st={kind:'',len:4,cur:null,curSig:'',changes:0,inband:0};
        function add(chunk,meta,timestamp,cto){
            var dc=meta&&meta.decoderConfig,ps=null;
            if(!st.kind){
                /* Until a configuration WITH parameter sets arrives, the muxer gets exactly
                   what it always got — a caller's fallback config without them included. */
                if(dc&&dc.description){
                    ps=/^avc[13]\./i.test(String(dc.codec||''))?paramSets(dc.description):null;
                    st.kind=ps?'avc':'other';
                    if(ps){st.cur=ps;st.curSig=sig(ps);st.len=ps.len;}
                }
                return muxer.addVideoChunk(chunk,meta,timestamp,cto);
            }
            if(st.kind!=='avc')return muxer.addVideoChunk(chunk,meta,timestamp,cto);
            var fresh=false;
            if(dc&&dc.description&&(ps=paramSets(dc.description))){
                var s=sig(ps);
                if(s!==st.curSig){
                    fresh=true;st.cur=ps;st.curSig=s;
                    if(!st.changes++){
                        try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'avc_sets_changed',key:chunk.type==='key'?1:0,codec:String(dc.codec||'').slice(0,24)});}catch(_){}
                    }
                }
            }
            var relen=st.cur.len!==st.len;
            if(!relen&&(!st.changes||(chunk.type!=='key'&&!fresh)))return muxer.addVideoChunk(chunk,undefined,timestamp,cto);
            var raw=new Uint8Array(chunk.byteLength);chunk.copyTo(raw);
            var data=(st.changes&&(chunk.type==='key'||fresh))?withParamSets(raw,st.cur,st.len):reframe(raw,st.cur.len,st.len,null);
            if(data!==raw&&st.changes)st.inband++;
            return muxer.addVideoChunkRaw(data,chunk.type,timestamp!=null?timestamp:chunk.timestamp,chunk.duration,undefined,cto);
        }
        add.stats=st;
        return add;
    };
    window.__muxVideoTrack.paramSets=paramSets;
    window.__muxVideoTrack.withParamSets=withParamSets;
})();
/* __muxVideoTrack:end */

/* __encoderClock:begin */
/** Which frame a VideoEncoder's output chunk is — the time to mux it at:
 *    var clock=window.__encoderClock({onEnd:fn});   // one per encode run
 *    clock.fed(timestamp,duration);                 // every frame handed to encode(), in order
 *    clock.ts(chunk)                                // → the start of the frame the chunk encodes (µs)
 *    clock.dts(pts)                                 // → the chunk's DECODE time (µs); once per chunk, in output order
 *    clock.restart();                               // a new encoder instance: its first frame asks again
 *
 * dts (04.11.2026). A WebCodecs chunk carries no decode time; the muxer needs one and
 * writes CTS − DTS as the composition offset. The exporters used the running sum of the
 * chunks' durations — but those are ROUNDED (41 667 µs at 24 fps) and the frames' own
 * times are not, so the sum ran ahead by ⅓ µs a frame: no B-frames, and still every
 * offset −1…−10 ticks from the first second on (a 1-hour export ends 29 ms ahead),
 * written into a version 0 ctts. Our byte-range cut read those unsigned — hours — and
 * «keep 0–9 s» of a /reverse-video file kept 14 frames (Nikolai, 04.11.2026). The
 * decode time of the k-th chunk is the k-th smallest frame time fed: the chunk's own
 * time when nothing is reordered (offset 0, no ctts noise), the B-frame delay when
 * something is (the negative offsets of a signed ctts — FFmpeg's negative_cts_offsets
 * form). Strictly increasing; restart() drops what a dead encoder swallowed (the replay
 * feeds it again); a caller that never fed gets its own time, never going back.
 *
 * Firefox's H.264 encoder with B-frames (Main / High; 148, measured 16.10.2026 —
 * tests/scratch/encoder-output-timestamps-scratch.mjs) stamps every chunk with the END of
 * its frame, start + duration: frames 0+48000, 48000+97000, 145000+48000 come out as
 * 48000, 193000, 145000. Baseline, VP9 and Chrome stamp the start. At one frame rate it
 * is one constant shift (a video-only file starts a frame late; a remux that rebases the
 * start hides it); at a source's own frame times (a phone's variable rate, #9567) — every
 * frame at its neighbour's time, and a retention map keyed by the start releases the
 * wrong frame. RU, 20 days: the first chunk
 * one frame late (webcodecs_video_chunk_offset_detected) — Firefox 182 events in 143
 * sessions, Opera 7, Yandex 2, Safari 1.
 * The first chunk of an encoder is always its first frame (forced key), so its stamp says
 * which clock the encoder keeps. An end-stamped chunk carries its frame's own duration, and
 * stamp − duration is that frame's start: 581 of 581 chunks of a variable-rate clip through
 * WebCodecsPipeline, whose durations are the source samples' (decode order) — there 11
 * frames END at the same instant as another, so «the frame that ends here» is the fallback
 * for a chunk without a duration, not the rule.
 */
(function(){
    window.__encoderClock=function(o){
        o=o||{};
        var first=null,clock='',pend=new Map(),ends=new Map();
        /* dts: frame times fed and not yet decoded, ascending (q[qh..]); the last decode time given */
        var q=[],qh=0,lastDts=null;
        function queue(ts){
            if(qh>=q.length||ts>=q[q.length-1]){q.push(ts);return;}
            var lo=qh,hi=q.length;
            while(lo<hi){var mid=(lo+hi)>>1;if(q[mid]<=ts)lo=mid+1;else hi=mid;}
            q.splice(lo,0,ts);
        }
        return{
            fed:function(ts,dur){
                dur=dur>0?dur:0;
                if(!first)first={ts:ts,dur:dur};
                queue(ts);
                if(clock==='start')return;
                pend.set(ts,dur);
                if(dur)ends.set(ts+dur,ts);
            },
            ts:function(chunk){
                var t=chunk.timestamp;
                if(!clock&&first){
                    clock=(t!==first.ts&&first.dur>0&&t===first.ts+first.dur)?'end':'start';
                    if(clock==='end'){try{if(o.onEnd)o.onEnd();}catch(_){}}
                    else{pend.clear();ends.clear();}
                }
                if(clock!=='end')return t;
                var d=chunk.duration>0?chunk.duration:0,s;
                if(d&&pend.has(t-d))s=t-d;
                else if(ends.has(t)&&pend.has(ends.get(t)))s=ends.get(t);
                else s=t-d;
                var sd=pend.get(s);
                pend.delete(s);
                if(sd&&ends.get(s+sd)===s)ends.delete(s+sd);
                return s;
            },
            dts:function(pts){
                var d=qh<q.length?q[qh++]:pts;
                if(qh>1024&&qh*2>q.length){q=q.slice(qh);qh=0;}
                if(lastDts!==null&&!(d>lastDts))d=lastDts+1;
                lastDts=d;
                return d;
            },
            restart:function(){first=null;clock='';pend.clear();ends.clear();q=[];qh=0;},
            clock:function(){return clock;}
        };
    };
})();
/* __encoderClock:end */

/* __leadingPictures:begin */
/** The samples a decoder that STARTS at sync sample k must not be fed:
 *    window.__leadingPictures(n, k, tsAt, isKeyAt) → ascending indices
 *    n — samples in DECODE order; tsAt(i) — presentation time; isKeyAt(i) — sync sample
 *  They are the leading pictures of k: after it in decode order, shown before it.
 *
 * An iPhone films HEVC as an open GOP (VideoToolbox allows open GOP for HEVC by
 * default): the first sync sample is an IDR, every later one a CRA followed by RASL
 * pictures that reference the previous group (iPhone 12 Pro clip, iOS 17.3: 21 of 22
 * sync samples are CRA with 3 RASL each; FFmpeg found the same on iPhone XR and 13 —
 * «avformat/mov: fix seeking with HEVC open GOP files», 2022). A decoder that starts
 * at a CRA cannot build them, and the spec discards them (NoRaslOutputFlag). Chromium
 * drops them quietly; WebKit's VideoDecoder turns ANY input that comes back without a
 * picture into «Decoder failure» and closes itself (RemoteVideoDecoderCallbacks::
 * notifyDecodingResult → closeDecoder(EncodingError)). So on every iPhone a decode that
 * started anywhere but at the first frame of such a clip died on its first chunks:
 * /remove-text-from-video and /remove-object-from-video (RU 03.11: `from` 165 of 174
 * and 111 of 139, 0 frames out, both rescues the same) and the rescue of /video-merge.
 * They are never needed there: a cold start is chosen at a key shown at or before the
 * first wanted moment, and a leading picture is shown before its key. Trailing pictures
 * never reference them (HEVC forbids it), so the rest of the group decodes as it would
 * have. Closed-GOP streams (H.264 from a phone: an IDR every group) have none. */
(function(){
    window.__leadingPictures=function(n,k,tsAt,isKeyAt){
        var out=[];
        if(!(k>=0&&k<n))return out;
        var t0=tsAt(k);
        for(var i=k+1;i<n;i++){
            if(isKeyAt(i))break;
            if(tsAt(i)<t0)out.push(i);
        }
        return out;
    };
    /** The other half of a cold start: can a FRESH decoder start on this sync sample?
     *    window.__leadingPictures.startsADecoder(codec, bytes) → true | false
     *  bytes — the sample as stored in mp4 (4-byte length-prefixed NAL units).
     *  H.264: only an IDR slice (NAL 5). An open GOP marks its recovery-point I-frames
     *  as sync, and Chromium refuses them as a first chunk («marked as type `key` but
     *  wasn't a keyframe» — measured on an x264 open-GOP clip). HEVC: any IRAP picture
     *  (16–23); a CRA's RASL are what __leadingPictures leaves out. Other codecs: their
     *  sync samples are keyframes. One owner for the pipeline's rebuilds (since
     *  26.09.2026) and /reverse-video's windows. */
    window.__leadingPictures.startsADecoder=function(codec,b){
        var c=String(codec||'').slice(0,4);
        if(c!=='avc1'&&c!=='avc3'&&c!=='hvc1'&&c!=='hev1')return true;
        if(!b)return false;
        var hevc=c==='hvc1'||c==='hev1',pos=0;
        while(pos+5<=b.length){
            var len=((b[pos]<<24)|(b[pos+1]<<16)|(b[pos+2]<<8)|b[pos+3])>>>0;
            if(!len||pos+4+len>b.length)break;
            var t=hevc?((b[pos+4]>>1)&0x3f):(b[pos+4]&0x1f);
            if(hevc?(t>=16&&t<=23):t===5)return true;
            if(!hevc&&t===1)return false;          // a non-IDR slice came first
            pos+=4+len;
        }
        return false;
    };
})();
/* __leadingPictures:end */

/* __pdfSvgPath:begin */
/** An SVG path into a pdf-lib page, of ANY length:
 *    window.__pdfSvgPath(page, d, opts[, PDFLib])   // opts = page.drawSvgPath's options
 *  The same operators in the same order as page.drawSvgPath(d, opts) — the saved
 *  PDF is byte-identical — handed to the content stream in slices.
 *  Why: pdf-lib 1.17.1 passes EVERY operator of the path as an argument of one call
 *  (contentStream.push.apply, and again inside push), and an engine caps the number
 *  of arguments. Measured 2026-10-11: Chromium ~62 000 path commands (fewer in a
 *  deep stack — Yandex Browser failed below that), WebKit ~319 000, Firefox ~500 000;
 *  above it the export dies with «Maximum call stack size exceeded». /text-to-handwriting
 *  draws a page's ink as a few bucket paths and lost the PDF in 36 sessions in five
 *  weeks (chat 681); /stencil-maker traces a photo into one path. pdf-lib is frozen and
 *  most tools take it from jsdelivr, so the library cannot be fixed — this door is ours.
 *  `fillRule: 'evenodd'` is honoured (f*, B*); pdf-lib ignores it silently. */
(function(){
    var SLICE=4096;
    function or(v,d){return v===null||v===undefined?d:v;}
    window.__pdfSvgPath=function(page,d,opts,PL){
        PL=PL||window.PDFLib;
        var o={},k;
        /* A copy: pdf-lib writes its default border colour INTO the caller's object. */
        for(k in (opts||{}))o[k]=opts[k];
        /* Operators must come from the copy of pdf-lib that made the page (pushOperators
           checks instanceof) — a page of another copy goes the library's own way. */
        if(!PL||typeof PL.drawSvgPath!=='function'||!(page instanceof PL.PDFPage)||typeof page.maybeEmbedGraphicsState!=='function')return page.drawSvgPath(d,o);
        var gs=page.maybeEmbedGraphicsState({opacity:o.opacity,borderOpacity:o.borderOpacity,blendMode:o.blendMode});
        if(!('color' in o)&&!('borderColor' in o))o.borderColor=PL.rgb(0,0,0);
        var ops=PL.drawSvgPath(d,{
            x:or(o.x,page.getX()),y:or(o.y,page.getY()),scale:o.scale,
            rotate:or(o.rotate,PL.degrees(0)),color:or(o.color,undefined),
            borderColor:or(o.borderColor,undefined),borderWidth:or(o.borderWidth,0),
            borderDashArray:or(o.borderDashArray,undefined),borderDashPhase:or(o.borderDashPhase,undefined),
            borderLineCap:or(o.borderLineCap,undefined),graphicsState:gs
        });
        if(o.fillRule==='evenodd'){
            /* The paint operator is the one before the closing Q. */
            var N=PL.PDFOperatorNames,p=ops.length-2,nm=ops[p]&&ops[p].name;
            if(nm===N.FillNonZero)ops[p]=PL.PDFOperator.of(N.FillEvenOdd);
            else if(nm===N.FillNonZeroAndStroke)ops[p]=PL.PDFOperator.of(N.FillEvenOddAndStroke);
        }
        for(var i=0;i<ops.length;i+=SLICE)page.pushOperators.apply(page,ops.slice(i,i+SLICE));
    };
})();
/* __pdfSvgPath:end */

/* __savePdf:begin */
/** A jsPDF document saved through the site's download door:
 *    window.__savePdf(pdf, filename)   // → Promise<boolean>, the door's verdict
 *  `pdf` — a jsPDF document, or an html2pdf worker (the chain a tool would end with .save()).
 *  jsPDF's own pdf.save() clicks an <a download href="blob:"> that is NOT in the document.
 *  The Yandex app and Android WebViews ignore such an anchor silently (no file, no error),
 *  and the click never reaches this file's anchor interceptor — so no `file_download`
 *  either, and the refund panel and RefundService counted a buyer's PDFs as nothing:
 *  refund request #43 (12.10.2026) read «забрал файлов: 1» for one MusicXML and two PDFs.
 *  /audio-to-notes alone, 30 days: 2 114 PDF exports, 188 of them in the Yandex app and
 *  Android WebViews. pdf.output('blob') goes to window.__downloadFile — the WebView relay,
 *  the iOS share sheet, the AI mark and the delivery verdict come with it. */
window.__savePdf=function(pdf,filename){
    var name=String(filename||'document.pdf');
    if(!/\.pdf$/i.test(name))name+='.pdf';
    // an html2pdf worker (it has outputPdf, a jsPDF document has not): its .save() is the same
    // detached anchor, and its blob comes asynchronously
    if(pdf&&typeof pdf.outputPdf==='function'&&typeof window.__downloadFile==='function'){
        return Promise.resolve(pdf.outputPdf('blob')).then(function(blob){
            if(!blob||!(blob.size>0))throw new Error('empty pdf');
            return Promise.resolve(window.__downloadFile(blob,name)).then(function(ok){return ok!==false;});
        });
    }
    try{
        if(typeof window.__downloadFile==='function'&&pdf&&typeof pdf.output==='function'){
            var blob=pdf.output('blob');
            if(blob&&blob.size>0){
                if(blob.type!=='application/pdf'){try{blob=new Blob([blob],{type:'application/pdf'});}catch(_){}}
                return Promise.resolve(window.__downloadFile(blob,name)).then(function(ok){return ok!==false;},function(){return false;});
            }
        }
    }catch(e){}
    // no door on this page (or the document cannot hand out a blob): jsPDF's own way, as before
    try{pdf.save(name);return Promise.resolve(true);}catch(e){return Promise.resolve(false);}
};
/* __savePdf:end */

/* __swEncoderBudget:begin */
/** The bits a SOFTWARE H.264 encoder (WebCodecs `prefer-software` — OpenH264 in
 *  Chromium; the FIRST rung on every Android device) gets for the picture a
 *  hardware encoder makes from `bps`:
 *    var b = window.__swEncoderBudget(bps, width, height, fps);   // ≥ bps
 *  One rule for every engine: webcodecs-pipeline.js (run, runInterpolation),
 *  hybrid-encode-lane.js, canvas-video-export.js. Never for a bitrate that is a
 *  promise about BYTES (/video-compressor's target size, /car-usb's head unit).
 *
 *  Measured 2026-10-08 (Chrome 141, Windows; reference = the lossless source
 *  frames; VMAF over six clips — three 720p walks, the 1080p jellyfish, scrolling
 *  text, a photo slideshow; bits per pixel = bps ÷ (w·h·fps)):
 *   · at the same bits the software encoder is far behind — 720p at 0.18 bpp
 *     (the CRF-18 table): 90.6 / 94.1 / 80.6 against 97.6 / 98.3 / 96.5;
 *     1080p at 0.09: 86.4 against 96.6; scrolling text at 0.04: 76.8 / 98.9;
 *   · to reach the hardware figure at 0.04–0.14 bpp it needs ×1.9–2.3 the bits
 *     (natural footage; ×2.2–2.6 on the 1080p clip);
 *   · it SATURATES: 0.35 → 0.5 bpp adds 0–2 VMAF, ceiling ≈ 96 (hardware 98–100)
 *     — past ~0.4 bpp more bits are bytes, not picture.
 *  Rule: ×2.2, up to 0.4 bpp, never below the given bitrate; 60 Mbit/s cap.
 *  `contentHint` does not lift the ceiling: 'motion' = the default, 'detail' /
 *  'text' put OpenH264 in screen mode, which refuses the bits (asked 13.8, wrote
 *  5–6.8 Mbit/s) and loses on footage.
 *  The bug report behind it — /enhance-video, Android: «Стало намного хуже, как
 *  будто видео жестко ужали» (480×852 at 2.4 Mbit/s, `prefer-software`). */
(function(){
    var GAIN=2.2, KNEE_BPP=0.4, CAP_BPS=60000000;
    window.__swEncoderBudget=function(bps,w,h,fps){
        bps=+bps||0;
        var px=(+w||0)*(+h||0)*((+fps>0)?+fps:30);
        if(!(bps>0)||!(px>0))return Math.round(bps);
        if(bps/px>=KNEE_BPP)return Math.round(bps);
        var want=Math.min(bps*GAIN,KNEE_BPP*px,CAP_BPS);
        return Math.round(Math.max(bps,want));
    };
    window.__swEncoderBudget.GAIN=GAIN;
    window.__swEncoderBudget.KNEE_BPP=KNEE_BPP;
    /* MaxBR of the H.264 level an `avc1.PPCCLL` string names (Baseline/Main —
       the conservative figure), 0 when it names none. An engine whose codec
       string is fixed (CanvasVideoExport) keeps the budget under it; the
       pipeline raises the level instead (h264LevelHex with a bitrate). */
    var MAXBR={0x1e:10e6,0x1f:14e6,0x20:20e6,0x28:20e6,0x29:50e6,0x2a:50e6,0x32:135e6,0x33:240e6,0x34:240e6};
    window.__swEncoderBudget.levelCap=function(codec){
        var m=/^avc1\.[0-9a-f]{4}([0-9a-f]{2})$/i.exec(String(codec||''));
        return m?(MAXBR[parseInt(m[1],16)]||0):0;
    };
})();
/* __swEncoderBudget:end */

/* __encoderRate:begin */
/** The bitrate (and the framerate) to CONFIGURE an H.264 encoder with, so that
 *  it WRITES `bps` for frames that really arrive at `fps`:
 *    var r = window.__encoderRate(bps, fps, told, hint);   // → { bitrate, framerate, wrapGuard? }
 *  `told` — the framerate the config would carry (default `fps`); `hint` — its
 *  hardwareAcceleration. One rule for every engine that configures H.264:
 *  webcodecs-pipeline.js (pickEncoderConfig), hybrid-encode-lane.js,
 *  canvas-video-export.js.
 *
 *  Measured 2026-10-22 (Chrome 155, Windows; 1920×1072 frames of a 3D game
 *  stamped at 112.77 fps; the bitrate the encoder WROTE):
 *   · hardware (Media Foundation) budgets bitrate ÷ told per frame: told 113 —
 *     20 → 20.3 Mbit/s; told 60 — 18 → 34.6, but 9.6 (= 18 × 60 ÷ 112.77) → 18.3;
 *   · and bitrate × told must stay under 2³²: at 113, 38 Mbit/s → 38.4 and
 *     39 → 2.1; 41.5 → 3.3, which is (41.5e6 × 113 − 2³²) ÷ 113 to the bit; at
 *     120, 36 → 2.1; at 60, 70 is fine and 80 → 16.5. The same at 720p and for
 *     VP9: the product wraps in the Windows wrapper, whatever the encoder. On
 *     /video-fps 11 of 84 desktop ×4 «high» exports came out smaller than their
 *     source (RU, 30 d) — 1080p×4 asks 41.5 Mbit/s;
 *   · OpenH264 (`prefer-software`) budgets bitrate ÷ min(told, 60): told 30 →
 *     ×3.7 the bits, told 60 / 90 / 113 → ×1.87 for 112.77 fps frames.
 *  Rule: configure bps × eff ÷ fps (eff = told; OpenH264 — min(told, 60)). On
 *  Windows a hardware config whose bitrate × told reaches 2³² tells a LOWER rate
 *  (60, 30, …) with the bitrate scaled to it — the bits per frame, and so the
 *  picture, stay — and is capped only when even that wraps. Hardware elsewhere
 *  and Android's software encoder were not measured: their bits are left as
 *  given. */
(function(){
    var WRAP=4294967296*0.97, SW_RATE_CEIL=60;
    function ua(){ try{ return String((navigator&&navigator.userAgent)||''); }catch(e){ return ''; } }
    window.__encoderRate=function(bps,fps,told,hint){
        bps=+bps||0; fps=(+fps>0)?+fps:0; told=(+told>0)?+told:fps;
        if(!(bps>0)||!(fps>0)||!(told>0))return {bitrate:Math.round(bps),framerate:told||fps||30};
        var sw=hint==='prefer-software', android=/Android/i.test(ua()), windows=/Windows/i.test(ua());
        if(sw&&android)return {bitrate:Math.round(bps),framerate:told};
        var eff=sw?Math.min(told,SW_RATE_CEIL):(windows?told:fps);
        var b=bps*eff/fps;
        if(!sw&&windows&&b*Math.ceil(told)>=WRAP){
            var rates=[60,30,24,15,10];
            for(var i=0;i<rates.length;i++){
                var t=rates[i]; if(t>=told)continue;
                var bt=bps*t/fps;
                if(bt*t<WRAP)return {bitrate:Math.round(bt),framerate:t,wrapGuard:'rate'};
            }
            return {bitrate:Math.floor(WRAP/Math.ceil(told)),framerate:told,wrapGuard:'cap'};
        }
        return {bitrate:Math.round(b),framerate:told};
    };
    window.__encoderRate.WRAP=WRAP;
    window.__encoderRate.SW_RATE_CEIL=SW_RATE_CEIL;
})();
/* __encoderRate:end */

/* __storageRoom:begin */
/** navigator.storage.estimate() with a quota that is TRUE.
 *
 *  Most Chromium builds answer estimate() with usage + 10 GiB whatever the real
 *  grant (see __opfsMuxLane for the field numbers and the incident). Every
 *  guard that reads «free = quota − usage» from it — the recorders' low-space
 *  warning, their auto-eviction, their stop-before-the-disk-is-full, a
 *  merge's pre-flight — was therefore blind on the most common browser, and
 *  the first sign of a full disk was a write refused mid-work.
 *
 *  When the reported headroom is that static 10 GiB, the grant is MEASURED:
 *  truncate on a scratch writable, bisected (a refused truncate errors the
 *  stream, so it is reopened), up to `upTo` bytes (default 2 GiB). Measured on
 *  Chrome 154: 4–33 ms, within 4 MB (0.5 MB → 0, 37 → 36, 700 → 696,
 *  5 000 → «at least 2 048»). A measurement that reaches `upTo` leaves the
 *  reported numbers alone — there is at least that much, and a capped quota
 *  would invent a usage percentage. Any other answer is passed through as the
 *  browser gave it (Safari, Samsung, MIUI report real numbers).
 *
 *  → Promise<{usage, quota, reported, measured, grantBytes}> (null only when the
 *    browser has no estimate() at all; rejects when estimate() rejects); same
 *    usage/quota fields as estimate(), so a caller swaps the call and nothing
 *    else. Concurrent calls share one measurement; a result lives 3 s.
 *  Guards: tests/js/opfs-mux-lane.test.mjs, StorageRoomIsMeasuredNotEstimatedTest,
 *  tests/playwright/storage-probe-survives-incognito.mjs. */
(function(){
    var STATIC=10737418240;   // 10 GiB — Chromium's static headroom
    var MiB=1048576;
    var cache=null,inflight=null,memVerdict=null,MEM={memory:true};
    function nowMs(){return (window.performance&&typeof performance.now==='function')?performance.now():Date.now();}
    /* Where does this origin's OPFS live — on disk, or in the browser's MEMORY?
       An off-the-record profile — an incognito window, and Playwright's
       newContext() is the same kind — keeps it in memory, and truncate(n) there
       allocates n bytes inside the browser process: the 2 GiB first step of the
       grant measurement killed the WHOLE browser, every window of it (Chrome 153,
       exit 0xE0000008 = Chromium's OOM code, 20 s after the call; 25.09.2026,
       found by the /mashup-maker studio, whose «What next» rail asks for room
       after every build). A disk lane reserved there is the same kind of block.
       No quota tells the two apart: estimate(), webkitTemporaryStorage and
       webkitPersistentStorage all answer the static usage + 10 GiB in both.
       The COST of a growth does — measured on this machine, 4 rounds each:
         in memory  64 MiB 17–74 ms, 128 MiB 52–157 ms (grows with every MiB);
         on disk    1.5–25 ms at ANY size (a loaded machine's noise, not size).
       So a scratch file is grown by doubling from 64 MiB, every step timed, and
       TWO growths in a row slower than a quiet disk ever is RAISE THE SUSPICION
       of memory: stop right there, at 128–512 MiB, never at the size that kills —
       and confirm it on fresh files (confirmMemory below; since 14.10.2026 —
       a busy disk raised the same suspicion in 4 page loads of 10). ⚠️ Not «the same step
       measured again»: shrinking keeps the memory's capacity, so growing back to
       the same size is nearly free (64 MiB: 6 ms again against 17–39 the first
       time) — the first version of this guard retried that way, called memory a
       disk and let the browser die anyway. Every error left falls on the safe
       side — «memory» only skips a measurement, the pre-25.09 behaviour.
       Guards: tests/js/opfs-mux-lane.test.mjs («memory-backed»),
       tests/playwright/storage-probe-survives-incognito.mjs. */
    /* a growth that paid for its bytes: above a loaded disk's noise (8 % of disk
       samples reach 20 ms on a busy machine, two in a row ~0.7 %) and above
       0.1 ms/MiB (memory here 0.4–1.2, the fastest machines ~0.12) */
    function slow(dt,from,to){return dt>Math.max(20,(to-from)/MiB*0.1);}
    /* resize(n) → Promise<boolean> (false: the grant refused n). Resolves MEM, or
       {top, refusedAt} — the largest size taken and the first one refused (null).
       trust — the storage is known to be a disk: no growth is ever read as memory. */
    var memAt=0;
    function growTimed(resize,upTo,trust){
        var lo=0,slowRun=0;
        function next(n){
            var t=nowMs();
            return resize(n).then(function(ok){
                var dt=nowMs()-t;
                if(!ok)return {top:lo,refusedAt:n};
                slowRun=(!trust&&slow(dt,lo,n))?slowRun+1:0;
                if(slowRun>=2){memAt=n;return MEM;}       // two growths in a row paid for their bytes
                lo=n;
                return n>=upTo?{top:n,refusedAt:null}:next(Math.min(upTo,n*2));
            });
        }
        return next(Math.min(upTo,64*MiB));
    }
    /* «Two slow growths» is a SUSPICION, not a verdict. Measured 14.10.2026 on a
       machine busy with other work (regular disk profiles, production page): the
       ladder called a disk «memory» in 2 of 5 page loads in Chrome 155 and in 2
       of 5 in Yandex 26.8 — single truncates took 20–76 ms on a disk that
       normally answers in 2–6 — and a page so judged skips every reservation,
       the one protection against a write refused minutes into the work (24 of
       the 74 refused-write sessions of a month carried this verdict; in one the
       next page load judged the same profile a disk).
       What memory cannot fake away and a busy disk cannot fake up: the cost of a
       FRESH file grows with its bytes. So a suspicion is confirmed on new files
       (never the same one grown again — its capacity is kept, see above): five
       of the suspect size n and five of n/8, in turn. Memory: the big one is
       slow at its best AND costs at least three times the small one, at its
       best and in the middle (here 7–15x: Chrome 0.4–1.8 ms/MiB, Yandex ~1) —
       a ratio, so it holds on a fast machine as on a slow one. Three rounds
       against n/4 with a 2x rule still called a busy disk memory once in six.
       A probe that cannot be made (refused, failed) keeps the suspicion — the
       side on which no browser dies. */
    function freshCost(root,n){
        var name='_mem_probe_'+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||''),w=null;
        return root.getFileHandle(name,{create:true}).then(function(h){return h.createWritable();}).then(function(x){
            w=x;var t=nowMs();
            return w.truncate(n).then(function(){return nowMs()-t;},function(){return null;});
        }).catch(function(){return null;}).then(function(v){
            return Promise.resolve(w&&w.abort()).catch(function(){})
                .then(function(){return root.removeEntry(name).catch(function(){});})
                .then(function(){return v;});
        });
    }
    function confirmMemory(root,n){
        var big=[],small=[],sn=Math.max(16*MiB,Math.floor(n/8)),ROUNDS=5;
        function round(k){
            if(k>=ROUNDS)return null;
            return freshCost(root,sn).then(function(s){small.push(s);return freshCost(root,n);})
                .then(function(b){big.push(b);return round(k+1);});
        }
        function med(a){var c=a.slice().sort(function(x,y){return x-y;});return c[Math.floor(c.length/2)];}
        return round(0).then(function(){
            if(big.indexOf(null)!==-1||small.indexOf(null)!==-1)return true;      // could not ask: keep the suspicion
            var B=Math.min.apply(null,big),S=Math.min.apply(null,small),Bm=med(big),Sm=med(small);
            /* slow at its best (a busy disk's best of five is not), and three times the
               cost of a file an eighth its size — at its best and in the middle */
            var yes=B>Math.max(25,n/MiB*0.08)&&B>=3*S&&Bm>=3*Sm;
            if(!yes){try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_memory_unconfirmed',at_mb:Math.round(n/MiB),big_ms:Math.round(B),small_ms:Math.round(S),big_med:Math.round(Bm),small_med:Math.round(Sm)});}catch(_){}}
            return yes;
        },function(){return true;});
    }
    function sawMemory(){
        memVerdict=Promise.resolve(true);
        try{window.__ae&&window.__ae('feature_use',{slug:window.__TOOL_SLUG||'',f:'opfs_in_memory'});}catch(_){}
    }
    /* → Promise<boolean>, once per page. Undecidable (no OPFS, no truncate, an
       error, a grant too small to probe) → false: the caller keeps its path. */
    window.__opfsInMemory=function(){
        if(memVerdict)return memVerdict;
        memVerdict=(function(){
            if(!(navigator.storage&&typeof navigator.storage.getDirectory==='function'))return Promise.resolve(false);
            return navigator.storage.getDirectory().then(function(root){
                var name='_mem_probe_'+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||''),w=null;
                function done(v){
                    return Promise.resolve(w&&w.abort()).catch(function(){})
                        .then(function(){return root.removeEntry(name).catch(function(){});})
                        .then(function(){return v;});
                }
                return root.getFileHandle(name,{create:true}).then(function(h){return h.createWritable();}).then(function(x){
                    w=x;
                    if(typeof w.truncate!=='function')return false;
                    return growTimed(function(n){return w.truncate(n).then(function(){return true;},function(){return false;});},512*MiB)
                        .then(function(g){return g===MEM;});
                }).then(function(suspect){
                    /* the ladder's file goes first — in memory it holds its bytes — then the suspicion is confirmed */
                    return done(suspect).then(function(){return suspect?confirmMemory(root,memAt||128*MiB):false;});
                },function(){return done(false);}).then(function(mem){
                    if(mem)sawMemory();
                    return mem;
                });
            }).catch(function(){return false;});
        })();
        return memVerdict;
    };
    function measureGrant(upTo){
        return navigator.storage.getDirectory().then(function(root){
            var name='_room_probe_'+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||'');
            return root.getFileHandle(name,{create:true}).then(function(h){
                var w=null;
                function open(){return h.createWritable().then(function(x){w=x;});}
                function fits(n){
                    return w.truncate(n).then(function(){return true;},function(e){
                        if(!e||(e.name!=='QuotaExceededError'&&e.code!==22))throw e;
                        return Promise.resolve(w.abort()).catch(function(){}).then(open).then(function(){return false;});
                    });
                }
                var lo=0,hi=upTo;
                function step(){
                    if(hi-lo<=Math.max(4*MiB,lo*0.02))return lo;
                    var mid=Math.floor((lo+hi)/2);
                    return fits(mid).then(function(ok){if(ok)lo=mid;else hi=mid;return step();});
                }
                function cleanup(v){
                    return Promise.resolve(w&&w.abort()).catch(function(){})
                        .then(function(){return root.removeEntry(name).catch(function(){});})
                        .then(function(){if(v instanceof Error)throw v;return v;});
                }
                /* up by doubling, each step timed — never one jump to upTo: in memory that
                   jump was the 2 GiB that killed the browser */
                function settle(g){
                    if(g.refusedAt==null)return upTo;
                    lo=g.top;hi=g.refusedAt;return step();
                }
                return open().then(function(){return growTimed(fits,upTo);})
                    .then(function(g){
                        if(g!==MEM)return settle(g);
                        /* A suspicion: let the scratch file go (in memory it holds its
                           bytes), confirm on fresh files, and on a disk measure again
                           without reading any growth as memory. */
                        var at=memAt||128*MiB;
                        return Promise.resolve(w&&w.abort()).catch(function(){})
                            .then(function(){return confirmMemory(root,at);})
                            .then(function(mem){
                                if(mem){sawMemory();return MEM;}
                                return open().then(function(){return growTimed(fits,upTo,true);}).then(settle);
                            });
                    })
                    .then(cleanup,function(e){return cleanup(e instanceof Error?e:new Error(String(e)));});
            });
        });
    }
    /* Would `more` bytes still fit — a yes/no for a caller that has ALREADY taken its
       room (the disk lane, after its reservation, asks whether the disk is left in
       Chromium's eviction zone). The same timed ladder as the grant measurement, so
       a storage that turns out to be memory is never grown to the size that kills.
       → Promise<true | false | null>: fits / the grant refuses it (or has no room
       even for an empty file) / cannot tell. Never cached: it is about this moment. */
    window.__storageFitsMore=function(more){
        more=Math.max(64*MiB,Math.round(+more||0));
        if(!(navigator.storage&&typeof navigator.storage.getDirectory==='function'))return Promise.resolve(null);
        function quota(e){return !!(e&&(e.name==='QuotaExceededError'||e.code===22));}
        return navigator.storage.getDirectory().then(function(root){
            var name='_room_probe_'+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||''),w=null;
            function done(v){
                return Promise.resolve(w&&w.abort()).catch(function(){})
                    .then(function(){return root.removeEntry(name).catch(function(){});})
                    .then(function(){return v;});
            }
            return root.getFileHandle(name,{create:true}).then(function(h){return h.createWritable();}).then(function(x){
                w=x;
                if(typeof w.truncate!=='function')return null;
                return growTimed(function(n){
                    return w.truncate(n).then(function(){return true;},function(e){if(quota(e))return false;throw e;});
                },more).then(function(g){return g===MEM?null:g.refusedAt==null;});
            }).then(done,function(e){return done(quota(e)?false:null);});
        }).catch(function(){return null;});
    };
    window.__storageEstimate=function(opts){
        opts=opts||{};
        if(!(navigator.storage&&typeof navigator.storage.estimate==='function'))return Promise.resolve(null);
        var upTo=Math.max(64*MiB,+opts.upTo||2048*MiB);
        var now=Date.now();
        if(cache&&cache.upTo>=upTo&&now-cache.at<3000)return Promise.resolve(cache.v);
        if(inflight&&inflight.upTo>=upTo)return inflight.p;
        var p=navigator.storage.estimate().then(function(e){
            var usage=(e&&e.usage)||0,quota=(e&&e.quota)||0;
            var base={usage:usage,quota:quota,reported:quota,measured:false,grantBytes:null};
            if(quota-usage!==STATIC||typeof navigator.storage.getDirectory!=='function')return base;
            /* in memory there is no disk to be full — and a bisection from 2 GiB kills the browser */
            return window.__opfsInMemory().then(function(mem){
                if(mem)return Object.assign(base,{memory:true});
                return measureGrant(upTo).then(function(g){
                    if(g===MEM)return Object.assign(base,{memory:true});
                    /* At least `upTo` is there. Keep the reported number (a capped
                       one would invent a usage percentage) — unless the question
                       was bigger than it: a 12 GB archive asks upTo 12 GiB, the
                       static answer says 10, and 10 would be a false refusal. */
                    if(g>=upTo)return Object.assign(base,{quota:Math.max(quota,usage+g),measured:true,grantBytes:g});
                    return Object.assign(base,{quota:usage+g,measured:true,grantBytes:g});
                },function(){return base;});
            });
        }).then(function(v){cache={at:Date.now(),upTo:upTo,v:v};inflight=null;return v;},
               /* estimate() itself rejected: reject too, exactly as the call it
                  replaces — every caller already has its catch for that. */
               function(e){inflight=null;throw e;});
        inflight={upTo:upTo,p:p};
        return p;
    };
})();
/* __storageRoom:end */
/* __sliceSend:begin */
/* ── _sliceSend(o) — a file to our server in slices sized by the link ───────
 * ONE engine for both directions of the same class (see the notes above
 * SLICE_TARGET_MS): the download relay (__downloadFile → /api/download-stash-chunk)
 * and inbound uploads to the server lanes (__uploadBody → /api/upload-slice).
 * Until 08.10.2026 the loop lived inside __downloadFile only, and every tool
 * that sent a file to a server lane sent it as ONE long POST: RU nginx, 14 days —
 * of uploads that took 20–60 s, 32 % were cut by the client; 60–120 s, 75 %.
 *
 *   o.url        the offset-protocol endpoint (DownloadStashController /
 *                UploadSliceController — one server owner, OffsetUpload)
 *   o.blob, o.filename
 *   o.key, o.resume   where a failed attempt stopped (per key), so the next
 *                press continues from the server's byte count
 *   o.onProgress(bytes), o.onEvent(name, data), o.waitOnline(ms)
 *   o.healCsrf() a fresh CSRF token after a 419
 *   o.mono()     what to do when the server does not speak offsets on the
 *                very first slice (a page newer than its server)
 *   o.fields     extra form fields sent with every slice (the file's type)
 *   o.signal     the operation's AbortSignal: the slice in the air is
 *                aborted and the promise rejects with an AbortError
 * Resolves {json, mode:'slice'|'mono-fallback', chunks, resends, restarts,
 * secs, kbps, maxKB, resumedKB}. */
/* Pauses between tries of a slice that failed, then every 30 s.
   ⚠️ What ends the upload is NOT the number of tries: it is SLICE_NO_PROGRESS_MS
   of (active) time in which the server's byte count did not move. Until
   08.10.2026 the ladder was "3 tries" — but a failure deep in the chain also
   went back through the error handler of every slice that had already landed,
   so a file that broke on its first slice gave up after 28 s while a file 50
   slices in kept retrying for up to two hours. Measured on the relay (RU, 20
   days, 21 489 uploads): of the 825 that were delivered after trouble, the
   longest stretch without progress was ≤ 1 min for 76 %, ≤ 5 min for 93.5 %,
   ≤ 10 min for 97.2 %, ≤ 30 min for 99.2 % — and the failed ones gave up after
   a median of 60 s. Ten minutes is the knee: patience for every file size,
   and a definite answer instead of an hour of spinner. Active time
   (__activeNow) — a phone asleep in a pocket does not spend the budget. */
var SLICE_RETRY_MS=[1500,6000,20000,30000];
var SLICE_NO_PROGRESS_MS=600000;
function _sliceAbortError(){
    try{return new DOMException('The upload was aborted.','AbortError');}
    catch(e){var a=new Error('The upload was aborted.');a.name='AbortError';return a;}
}
function _sliceSend(o){
    var blob=o.blob,filename=o.filename,_flyKey=o.key,_stashResume=o.resume,signal=o.signal||null;
    var _showProg=o.onProgress||function(){},_ev=o.onEvent||function(){};
    var RETRY_MS=SLICE_RETRY_MS,csrfHealed=false;
    var activeNow=function(){return typeof window.__activeNow==='function'?window.__activeNow():Date.now();};
    var tries=0,gaps=0,stuckSince=0;   // failures since the server's count last moved
    // A pause between attempts ends early when the link comes back, and at
    // once when the operation is cancelled.
    var _waitOnline=o.waitOnline||function(ms){
        return new Promise(function(res){
            var done=false,t=null;
            var fin=function(){if(done)return;done=true;if(t)clearTimeout(t);
                try{window.removeEventListener('online',fin);}catch(e){}
                try{if(signal)signal.removeEventListener('abort',fin);}catch(e){}
                res();};
            try{window.addEventListener('online',fin);}catch(e){}
            try{if(signal)signal.addEventListener('abort',fin);}catch(e){}
            t=setTimeout(fin,ms);
        });
    };
    var size=blob.size;
    var keep=_stashResume.get(_flyKey)||null;
    if(keep&&keep.size!==size)keep=null;
    var uploadId=keep&&keep.id||null,received=keep&&keep.received||0;
    var resumedFrom=received;
    var slice=keep&&keep.slice||SLICE_FIRST_BYTES,rate=keep&&keep.rate||0;
    var reqs=0,resends=0,restarts=0,maxSlice=0,restarted=0,t0=Date.now();
    // Throttle pauses have their own budget: they are not errors, and
    // spending the network-retry ladder on them is what killed a
    // working 500 MB upload (see the 429 branch).
    var throttleWaits=0,THROTTLE_MAX_WAITS=6;
    var remember=function(){try{_stashResume.set(_flyKey,{id:uploadId,received:received,slice:slice,rate:rate,size:size});}catch(e){}};
    var sendOne=function(off,len){
        return new Promise(function(res,rej){
            var xhr;
            try{xhr=new XMLHttpRequest();xhr.open('POST',o.url,true);}
            catch(e){rej({kind:'net',msg:'xhr-open'});return;}
            var meta=document.querySelector('meta[name="csrf-token"]');
            try{xhr.setRequestHeader('X-CSRF-TOKEN',meta?meta.content:'');}catch(e){}
            xhr.responseType='json';
            var lastAdv=Date.now(),lastLoaded=0,sentAt=0,watchdog=null,settled=false,why=null;
            // Two phases, two questions. While the body is still being
            // handed over, "no meaningful advance in 20 s" means a dead
            // link (a 100 B/s trickle must not keep it alive). Once it
            // is all handed over there are no more progress events by
            // design, and the only question is whether the answer is
            // later than this slice can plausibly take.
            var expectMs=rate?len/rate*1000:0;
            var tailMs=Math.max(20000,Math.min(60000,3*expectMs));
            var onAbort=function(){why={kind:'abort'};try{xhr.abort();}catch(e){}};
            var fin=function(fn,v){if(settled)return;settled=true;
                if(watchdog)clearInterval(watchdog);
                try{if(signal)signal.removeEventListener('abort',onAbort);}catch(e){}
                fn(v);};
            if(signal){if(signal.aborted){fin(rej,{kind:'abort'});return;}
                try{signal.addEventListener('abort',onAbort);}catch(e){}}
            var kill=function(phase,msg){why={kind:'stall',phase:phase,msg:msg};try{xhr.abort();}catch(e){}fin(rej,why);};
            watchdog=setInterval(function(){
                var now=Date.now();
                if(sentAt){if(now-sentAt>tailMs)kill('tail','slice answer >'+Math.round(tailMs/1000)+'s');}
                else if(now-lastAdv>20000)kill('body','slice stalled >20s');
            },2000);
            xhr.upload.onprogress=function(ev){
                // A FALL is the browser re-sending this request on a
                // fresh connection — activity, not a stall; count it.
                if(ev.loaded<lastLoaded){restarts++;lastLoaded=ev.loaded;lastAdv=Date.now();sentAt=0;}
                else if(ev.loaded-lastLoaded>=Math.min(20480,len)){lastLoaded=ev.loaded;lastAdv=Date.now();}
                _showProg(off+Math.min(ev.loaded,len));
            };
            xhr.upload.onload=function(){if(!sentAt)sentAt=Date.now();};
            xhr.onerror=function(){fin(rej,{kind:'net',msg:'slice net'});};
            xhr.onabort=function(){fin(rej,why||{kind:'net',msg:'slice aborted'});};
            xhr.onload=function(){
                var st=xhr.status,j=xhr.response;
                if(st>=200&&st<300&&j)fin(res,j);
                else{
                    // Carry Retry-After: on a 429 the server knows how
                    // long its window has left, and guessing shorter is
                    // how a legitimate upload gives up (see the 429
                    // branch below).
                    var ra=0;
                    try{ra=parseInt(xhr.getResponseHeader('Retry-After'),10)||0;}catch(_e){}
                    fin(rej,{kind:'http',msg:'slice '+st,status:st,body:j,retryAfter:ra});
                }
            };
            var fd=new FormData();
            fd.append('chunk',blob.slice(off,off+len),filename+'.part');
            fd.append('offset',off);
            fd.append('size',size);
            fd.append('filename',filename);
            if(uploadId)fd.append('upload_id',uploadId);
            if(o.fields)for(var fk in o.fields)if(Object.prototype.hasOwnProperty.call(o.fields,fk))fd.append(fk,o.fields[fk]);
            try{xhr.send(fd);}catch(e){fin(rej,{kind:'net',msg:'slice send threw'});}
        });
    };
    // One request at a time. The answer's handler and the failure's handler are
    // the two arguments of ONE .then(): a failure further down the chain must
    // not come back through the failure handler of a slice that already landed
    // (that is how "3 tries" became 4 × the number of slices, see SLICE_RETRY_MS).
    var giveUp=function(e,len){
        throw {kind:'chunkgive',msg:String(e&&e.msg||'slice net'),resends:resends,tries:tries,
               stuckS:stuckSince?Math.round((activeNow()-stuckSince)/1000):0,sliceKB:Math.round(len/1024)};
    };
    var step=function(){
        if(signal&&signal.aborted)return Promise.reject(_sliceAbortError());
        var len=Math.min(slice,size-received);
        var tReq=Date.now();
        if(len>maxSlice)maxSlice=len;
        return sendOne(received,len).then(function(j){
            reqs++;
            if(j.upload_id)uploadId=j.upload_id;
            // How fast the LINK carried this slice: the request's time
            // minus the server's own work (`srv_ms`, counted from the
            // moment PHP got the already-buffered body). Without the
            // subtraction a slow server reads as a slow link, slices
            // shrink, requests multiply, each pays the server again —
            // measured spiral to the minimum slice. The running
            // estimate leans on the newest slice without trusting it
            // alone.
            var srv=(typeof j.srv_ms==='number'&&j.srv_ms>0)?j.srv_ms:0;
            var r=len/Math.max(50,Date.now()-tReq-srv)*1000;
            rate=rate?(rate+r)/2:r;
            if(j.done){
                try{_stashResume['delete'](_flyKey);}catch(e){}
                return {json:j,mode:'slice',chunks:reqs,resends:resends,restarts:restarts,
                        secs:Math.round((Date.now()-t0)/1000),kbps:Math.round(rate/1024),
                        maxKB:Math.round(maxSlice/1024),resumedKB:Math.round(resumedFrom/1024)};
            }
            var before=received;
            received=(typeof j.received==='number')?j.received:received+len;
            if(received>before){tries=0;gaps=0;stuckSince=0;}   // progress: a fresh ladder
            slice=Math.max(SLICE_MIN_BYTES,Math.min(SLICE_MAX_BYTES,slice*8,Math.round(rate*SLICE_TARGET_MS/1000)));
            remember();
            return step();
        },function(e){
            // Cancelled: no retry, no fallback — the operation is over.
            if((e&&e.kind==='abort')||(signal&&signal.aborted))throw _sliceAbortError();
            // A gateway answer (502/504) is the server's front door, not
            // its verdict: PHP restarting under a deploy (dozens a day),
            // a proxy timeout. Measured in the lab: one 502 during a
            // cache rebuild ended the whole delivery at 6 s when it was
            // read as final. The slice goes round the same ladder as a
            // dropped link — the offset protocol makes a resend safe.
            if(e&&e.kind==='http'&&(e.status===502||e.status===504))e={kind:'net',msg:'slice '+e.status};
            if(e&&e.kind==='http'){
                // The server does not speak offsets (a page newer than its
                // server — deploy skew): the whole file in one POST, as
                // before slices existed.
                if((e.status===404||e.status===405||e.status===422)&&!uploadId&&received===0)
                    return o.mono().then(function(j){return {json:j,mode:'mono-fallback'};});
                if(e.status===419&&!csrfHealed){
                    csrfHealed=true;
                    _ev('stash_retry',{err:'csrf heal',chunk:reqs});
                    return o.healCsrf().then(function(){return step();})
                        .catch(function(){throw e;});
                }
                // A gap: the server holds fewer bytes than we thought —
                // resend from its count at once (a few times in a row at
                // most: a server that keeps answering "gap" is not a link).
                if(e.status===409&&e.body&&typeof e.body.received==='number'&&gaps<3){
                    gaps++;
                    received=e.body.received;
                    if(e.body.upload_id)uploadId=e.body.upload_id;
                    remember();
                    return step();
                }
                if((e.status===429||e.status===503)&&throttleWaits<THROTTLE_MAX_WAITS){
                    // A 429 is "come back later", not a failure — so it
                    // gets its OWN budget and it WAITS AS LONG AS THE
                    // SERVER SAYS. The old branch spent the network
                    // ladder (1.5/6/20 s) on it and gave up inside a
                    // 60-second window: measured 2026-09-01, two big
                    // exports back to back on one IP exhausted the
                    // per-minute allowance and the second died at 54 s
                    // with everything working — the same 500 MB file
                    // landed byte-exact when it ran alone. A transfer
                    // that has already uploaded 400 MB must not throw
                    // that away because it guessed a shorter pause than
                    // the server asked for. 503 is the server's own
                    // "busy / disk" with a Retry-After — same stance.
                    throttleWaits++;resends++;
                    var _wait=Math.min(65000,Math.max(RETRY_MS[0],(e.retryAfter||0)*1000+500));
                    _ev('stash_retry',{err:'slice '+e.status,n:throttleWaits,chunk:reqs,waitMs:_wait});
                    return _waitOnline(_wait).then(function(){return step();});
                }
                if(e.status===410&&restarted<1){
                    // the server forgot this upload (30 min idle) — one
                    // clean restart from byte zero
                    restarted++;uploadId=null;received=0;resumedFrom=0;resends++;
                    try{_stashResume['delete'](_flyKey);}catch(_e){}
                    _ev('stash_retry',{err:'chunk state gone',n:tries+1});
                    return step();
                }
                throw e; // real HTTP failure — terminal
            }
            // A slice that stalled or dropped is re-sent SMALLER, after a
            // pause, for as long as SLICE_NO_PROGRESS_MS of active time
            // has not passed since the server's count last moved.
            slice=Math.max(SLICE_MIN_BYTES,Math.floor(Math.min(slice,len)/2));
            remember();
            if(!stuckSince)stuckSince=activeNow();
            if(activeNow()-stuckSince>=SLICE_NO_PROGRESS_MS)giveUp(e,len);
            tries++;resends++;
            _ev('stash_retry',{err:String(e&&e.msg||'net').slice(0,40),n:tries,
                stalled:e&&e.kind==='stall'?1:0,phase:e&&e.phase||'',chunk:reqs,
                offKB:Math.round(received/1024),sliceKB:Math.round(len/1024),restarts:restarts,
                stuckS:Math.round((activeNow()-stuckSince)/1000)});
            return _waitOnline(RETRY_MS[Math.min(tries-1,RETRY_MS.length-1)]).then(function(){return step();});
        });
    };
    return step();
}
/* __sliceSend:end */
/* A fresh CSRF token into the page's meta (an hours-old tab's token has
   expired, and a slice or a delivery answers 419). */
function _healCsrfToken(){
    return fetch('/api/csrf-token').then(function(r){return r.json();}).then(function(j){
        if(!j||!j.token)throw new Error('no token');
        var m=document.querySelector('meta[name="csrf-token"]');
        if(m)m.content=j.token;
        return true;
    });
}
/* __sliceFile:begin */
/* ── window.__sliceFile(file, o) — a file sent AHEAD to a server lane ─────────
 * The inbound half of the same class as the relay: one long POST of a file is
 * what Chromium kills on a buffered uplink (see the notes above
 * SLICE_TARGET_MS). RU nginx, 14 days to 07.10.2026 — of lane uploads that ran
 * 20–60 s, 32 % were cut by the client (499/408); of 60–120 s, 75 %: thirteen
 * presses in 25 minutes on /song-generator, four in five on /audio-to-text.
 *
 * The file goes to /api/upload-slice through _sliceSend and resolves to its
 * upload id; the lane request then carries `__sliced_k[]` + `__sliced_id[]`
 * instead of the file, and AttachSlicedUploads (server) puts the file back as
 * an ordinary upload. Callers do not use this directly: __uploadBody(fd,
 * {slices:true}) does, and __serverFile turns it on for our own lanes.
 *
 *   o.key        the picked file, for its name and time stamp
 *   o.name       the name the server should see
 *   o.signal     cancel; o.onProgress(bytesSent)
 *   o.fresh      do not reuse an earlier upload of this file (the server said
 *                `upload_gone`)
 * Resolves the upload id, or null when the server does not take slices (the
 * caller then sends the file inside its own request, as before). A failure
 * rejects with an Error {kind:'upload', status, isUploadSlice} — a network-class
 * failure for __uploadBody.kind; a cancel rejects with the AbortError.
 * The same file again within UPSLICE_REUSE_MS (another run, other settings) is
 * not uploaded again: the server keeps it TTL_MINUTES from the last use.
 * ⚠️ "The same file" is decided by CONTENT, not by object identity: a FormData
 * re-wraps a named file into a new File on every append, so identity would
 * never match — and name|size alone is how the relay once nearly delivered an
 * OLD cut of the same length (see _stashInflight). The key is name, size and
 * time stamp plus a 64-bit hash of three 16 KB windows of the bytes. */
var UPSLICE_REUSE_MS=45*60*1000;  /* < UploadSliceController::TTL_MINUTES (60) */
var _upDone=_resumeTable();
function _upEv(via,file,extra){
    try{if(window.__ae)window.__ae('feature_use',Object.assign(
        {slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
         f:'upload_slice',via:via,sizeKB:file?Math.round(file.size/1024):0},extra||{}));}catch(e){}
}
function _upFingerprint(file){
    var n=file.size,W=16384,parts=[[0,Math.min(W,n)]];
    if(n>W)parts.push([Math.max(W,Math.floor(n/2)-W/2),Math.min(n,Math.floor(n/2)+W/2)]);
    if(n>2*W)parts.push([Math.max(0,n-W),n]);
    return Promise.all(parts.map(function(q){return file.slice(q[0],q[1]).arrayBuffer();})).then(function(bufs){
        var a=2166136261,b=5381;
        bufs.forEach(function(buf){
            var u=new Uint8Array(buf);
            for(var i=0;i<u.length;i++){a=Math.imul(a^u[i],16777619);b=(Math.imul(b,33)+u[i])|0;}
        });
        return (a>>>0).toString(16)+(b>>>0).toString(16);
    });
}
window.__sliceFile=function(file,o){
    o=o||{};
    var id0=o.key||file,size=file.size;
    return _upFingerprint(file).then(null,function(){return '';}).then(function(fp){
        // No fingerprint (the bytes did not read): no reuse, no resume — the
        // upload itself will say what is wrong with the file.
        var key=fp?[id0.name||'',size,id0.lastModified||0,fp].join('|'):file;
        var had=(o.fresh||!fp)?null:_upDone.get(key);
        if(had&&had.size===size&&Date.now()-had.at<UPSLICE_REUSE_MS){
            _upEv('reused',file);
            return had.id;
        }
        return _upSend(file,o,key);
    });
};
function _upSend(file,o,key){
    var size=file.size;
    return _sliceSend({url:'/api/upload-slice',blob:file,filename:o.name||file.name||'upload',key:key,
        resume:_upResume,fields:{mime:file.type||''},signal:o.signal,healCsrf:_healCsrfToken,
        mono:function(){return Promise.resolve(null);},
        onProgress:o.onProgress,
        onEvent:function(name,d){_upEv('retry',file,d);}
    }).then(function(r){
        if(r.mode!=='slice'||!r.json||!r.json.upload_id){_upEv('mono',file);return null;}
        _upDone.set(key,{id:r.json.upload_id,size:size,at:Date.now()});
        _upEv('done',file,{chunks:r.chunks,resends:r.resends,restarts:r.restarts,secs:r.secs,
            kbps:r.kbps,maxKB:r.maxKB,resumedKB:r.resumedKB});
        return r.json.upload_id;
    },function(e){
        if(e&&e.name==='AbortError')throw e;
        var st=(e&&typeof e.status==='number')?e.status:0;
        // Bigger than the server takes as an upload (UploadSliceController::
        // attachMaxBytes — PHP's own ceiling): the file goes inside the lane's
        // request, as before slices, and the server's door answers it as it
        // always did. Slices never widen what a route may receive.
        if(st===413){_upEv('too_large',file);return null;}
        _upEv('failed',file,{err:String(e&&e.msg||st||'').slice(0,40),status:st});
        var er=new Error('upload '+(st||(e&&e.msg)||'failed'));
        er.status=st;er.kind='upload';er.isUploadSlice=true;
        throw er;
    });
}
/* __sliceFile:end */
/* __isProgramFile:begin */
/* __isProgramFile — a program never leaves this site as a saved file.
   A browser stamps every saved file with the page that produced it
   (Mark-of-the-Web), and antivirus telemetry then names THIS domain as the
   source of whatever it detects in the file. Audit 2026-10-01: cracks were
   pulled out of archives here, and tembrica.com sits on Avast's blacklist since
   2026-07-07. The archive extractor stopped handing programs out then; the
   mail/PST/DBX viewers and disk recovery still saved attachments as they came
   (audit 2026-10-09) — so the rule moved to the doors every tool goes through:
   __downloadFile and __batchDeliver. By extension (Windows and Android run these
   on a tap) and, where the bytes are at hand, by the first bytes (MZ…PE, ELF,
   Mach-O, DEX), so a renamed binary is caught too. .js/.sh/.iso stay: web
   projects and disk images are ordinary contents. The server relay keeps the
   same list (DownloadStashController::PROGRAM_EXT). A tool whose OWN output has
   such an extension (the registry viewer's .reg export of the person's own
   hive) says so per call: {program:true}. */
var _PROGRAM_EXT=/\.(exe|dll|sys|ocx|drv|cpl|scr|com|pif|msi|msp|msix|msixbundle|appx|appxbundle|apk|xapk|apks|aab|jar|dmg|pkg|mpkg|deb|rpm|lnk|hta|vbs|vbe|jse|wsf|wsh|msc|reg|chm|bat|cmd|ps1|psm1)$/i;
window.__isProgramFile=function(name,bytes){
    if(_PROGRAM_EXT.test(String(name||'')))return true;
    if(!bytes||bytes.length<4)return false;
    var a=bytes[0],b=bytes[1],c=bytes[2],d=bytes[3];
    if(a===0x4D&&b===0x5A&&bytes.length>=0x40){                                    // MZ … "PE\0\0" — Windows PE
        var pe=bytes[0x3C]|(bytes[0x3D]<<8)|(bytes[0x3E]<<16)|(bytes[0x3F]<<24);
        if(pe>0&&pe+4<=bytes.length&&bytes[pe]===0x50&&bytes[pe+1]===0x45&&bytes[pe+2]===0&&bytes[pe+3]===0)return true;
    }
    if(a===0x7F&&b===0x45&&c===0x4C&&d===0x46)return true;                         // ELF
    if(a===0xFE&&b===0xED&&c===0xFA&&(d===0xCE||d===0xCF))return true;             // Mach-O
    if((a===0xCE||a===0xCF)&&b===0xFA&&c===0xED&&d===0xFE)return true;
    if(a===0x64&&b===0x65&&c===0x78&&d===0x0A)return true;                         // "dex\n"
    return false;
};
/* The refusal, said once per delivery and counted: how many people meet it
   (tool_event program_withheld, via door|batch) is the measure of the rule. */
window.__isProgramFile.refuse=function(via,name,n){
    try{if(window.__ae)window.__ae('tool_event',{action:'program_withheld',via:via,
        ext:(String(name||'').match(/\.([a-z0-9]+)$/i)||[,''])[1].toLowerCase(),files:n||1});}catch(e){}
    try{
        var _L=(window.__LOCALE||'en').substring(0,2);
        var _M={ru:'Программы (.exe, .apk и подобные) с этого сайта не сохраняются.',
                en:'Programs (.exe, .apk and the like) are not saved from this site.',
                de:'Programme (.exe, .apk usw.) werden von dieser Website nicht gespeichert.',
                es:'Los programas (.exe, .apk y similares) no se guardan desde este sitio.',
                fr:'Les programmes (.exe, .apk, etc.) ne sont pas enregistrés depuis ce site.',
                pt:'Programas (.exe, .apk e similares) não são salvos a partir deste site.',
                id:'Program (.exe, .apk, dan sejenisnya) tidak disimpan dari situs ini.',
                ko:'프로그램(.exe, .apk 등)은 이 사이트에서 저장되지 않습니다.',
                ar:'لا تُحفَظ البرامج (\u200E.exe و\u200E.apk وما شابهها) من هذا الموقع.',
                uk:'Програми (.exe, .apk і подібні) з цього сайту не зберігаються.',
                tr:'Programlar (.exe, .apk vb.) bu siteden kaydedilmez.'};
        window.__toast&&window.__toast(_M[_L]||_M.en,'info');
    }catch(e){}
};
/* __isProgramFile:end */
/* The first statement below: an export's result handed over as its OPFS file.
   A download still reading it when the person moves on must not lose it to the
   next page's janitor (__opfsOwner above) — pinned for half an hour, and before
   the AI-mark re-entry, which hands the door a new blob without the file's name. */
/* The MIME type a file NAME stands for — only the formats the tools hand out, and no markup.
   Used by the download door for a Blob that carries no type of its own (see there). */
window.__mimeOfName=function(name){
    var x=(/.([a-z0-9]{1,5})$/i.exec(String(name||''))||[])[1];
    if(!x)return '';
    return ({
        mp4:'video/mp4',m4v:'video/x-m4v',mov:'video/quicktime',webm:'video/webm',mkv:'video/x-matroska',
        avi:'video/x-msvideo','3gp':'video/3gpp',wmv:'video/x-ms-wmv',flv:'video/x-flv',mpg:'video/mpeg',
        mpeg:'video/mpeg',ts:'video/mp2t',ogv:'video/ogg',
        mp3:'audio/mpeg',wav:'audio/wav',m4a:'audio/mp4',m4r:'audio/mp4',aac:'audio/aac',ogg:'audio/ogg',
        oga:'audio/ogg',opus:'audio/ogg',flac:'audio/flac',wma:'audio/x-ms-wma',aiff:'audio/aiff',
        aif:'audio/aiff',amr:'audio/amr',ac3:'audio/ac3',mka:'audio/x-matroska',mid:'audio/midi',midi:'audio/midi',
        png:'image/png',jpg:'image/jpeg',jpeg:'image/jpeg',webp:'image/webp',gif:'image/gif',bmp:'image/bmp',
        avif:'image/avif',heic:'image/heic',tif:'image/tiff',tiff:'image/tiff',ico:'image/x-icon',
        pdf:'application/pdf',zip:'application/zip',epub:'application/epub+zip',json:'application/json',
        docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pptx:'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        txt:'text/plain',csv:'text/csv',vtt:'text/vtt',srt:'application/x-subrip',md:'text/markdown',
        ics:'text/calendar',stl:'model/stl',obj:'model/obj',
        ttf:'font/ttf',otf:'font/otf',woff:'font/woff',woff2:'font/woff2'
    })[x.toLowerCase()]||'';
};
window.__downloadFile=function(blob,filename,opts){
    try{if(blob&&blob.name&&window.__opfsOwner&&window.__opfsOwner.ownerOf(blob.name))window.__opfsOwner.pin(blob.name,30*60000);}catch(_){}
    // ── EU AI Act ст. 50(2): отметка ставится ЗДЕСЬ, а не в каждом туле ──
    // Страница объявляет <meta name="ai-output"> (список — config/ai-output.php),
    // и любой путь выдачи файла получает машиночитаемую отметку одинаково:
    // кнопка, автосохранение, «Поделиться», пакетный режим и тот путь, который
    // допишут завтра. Пер-тульные вызовы __aiMark ловили только те места, о
    // которых кто-то помнил, — /photo-restore, /photo-colorize, /video-upscaler
    // и ещё девять генеративных тулов не имели ни одного.
    // Перезаход через сам примитив (а не вынос тела в функцию) держит ВСЮ
    // доставку — WebView-stash, iOS-share, FSA — в одном месте.
    // Маркировка идемпотентна и при любой ошибке возвращает исходные байты:
    // отметка не имеет права стать причиной несохранённого файла.
    // opts.aiMark===false — явный отказ для ОДНОЙ выдачи: тул из списка может
    // отдавать пользователю его же исходник (например, «улучшать нечего, вот
    // ваш файл обратно»), и пометить такой файл «создано ИИ» — ложь в чужих
    // метаданных, ровно та, из-за которой список вообще разделён надвое.
    if(blob&&!(opts&&opts.__aiMarked)&&!(opts&&opts.aiMark===false)&&window.__aiMark&&window.__aiOutput&&window.__aiOutput.shouldMark()){
        var _mSlug=(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||'generator');
        var _mOpts=Object.assign({},opts||{},{__aiMarked:1});
        try{
            return Promise.resolve(window.__aiMark.auto(blob,filename,_mSlug))
                .catch(function(){return blob;})
                .then(function(b){return window.__downloadFile(b||blob,filename,(b&&b!==blob&&_mOpts.bytes)?Object.assign({},_mOpts,{bytes:null}):_mOpts);});
        }catch(e){/* маркер недоступен — отдаём файл как есть */}
    }
    filename=filename||'download';
    // ── «;» и «"» — не в имя файла ──
    // WebKit (Safari и любой браузер iOS) читает имя скачивания как заголовок
    // Content-Disposition: «Post 2; shift.png» → «Post .png», «a;b.png» → пустое
    // имя, «ab"cd» → «ab\"cd» с обратной косой. Замер 23.10.2026 (Playwright
    // WebKit, iPhone 13, 18 знаков через эту дверь): портятся только эти два;
    // Chromium сохраняет всё как есть. Здесь, а не в туле: имя приходит из
    // десятков мест, и не все идут через __outputName.
    filename=String(filename).replace(/;/g,',').replace(/"/g,"'");
    // ── A web page must never leave here as somebody's media file ──
    // The lane-refusal story above (see __serverFile) ends at THIS door: a
    // tool that fetched its own server response and never checked what came
    // back will call us with the site's HTML and an `.mp3` name, and every
    // channel below would dutifully save it — reporting success. The check is
    // deliberately synchronous (blob.type, set by Response.blob() from the
    // Content-Type) because an await here would spend the click's user
    // activation, which the iOS share sheet and the FSA picker still need.
    // Scoped by extension so a tool that genuinely delivers .html/.txt/.json
    // (report exports) is untouched.
    if(blob&&blob.type&&/^(text\/html|application\/(json|xhtml\+xml))\b/i.test(blob.type)
       &&/\.(mp3|wav|ogg|opus|m4a|aac|flac|mp4|webm|mov|avi|mkv|gif|png|jpe?g|webp|pdf|zip|docx?|xlsx?|pptx?|svg)$/i.test(filename)){
        try{if(window.__ae)window.__ae('tool_error',{slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
            error_type:'document_as_file',err_msg:blob.type+' → '+filename,size:blob.size});}catch(e){}
        try{
            var _L=(window.__LOCALE||'en').substring(0,2);
            var _M={ru:'Файл не получен — попробуйте ещё раз.',
                    en:'The file did not come through — please try again.',
                    de:'Die Datei kam nicht an — bitte erneut versuchen.',
                    es:'El archivo no llegó: inténtalo de nuevo.',
                    fr:'Le fichier n’est pas arrivé — réessayez.',
                    pt:'O arquivo não chegou — tente novamente.',
                    id:'File tidak diterima — coba lagi.',
                    ko:'파일을 받지 못했습니다. 다시 시도해 주세요.',
                    ar:'لم يصل الملف — حاول مرة أخرى.', uk: 'Файл не отримано — спробуйте ще раз.', tr: 'Dosya gelmedi — lütfen tekrar deneyin.'};
            window.__toast&&window.__toast(_M[_L]||_M.en,'error');
        }catch(e){}
        return Promise.reject(new Error('refused to deliver '+blob.type+' as '+filename));
    }
    // ── A program is not saved from this site (see __isProgramFile above) ──
    // Synchronous on purpose, like the document check above: an await here
    // would spend the click's activation. The bytes are checked when the tool
    // still holds them as an array (opts.bytes); otherwise the name decides.
    if(!(opts&&opts.program)&&window.__isProgramFile(filename,opts&&opts.bytes&&opts.bytes.subarray?opts.bytes.subarray(0,4096):null)){
        window.__isProgramFile.refuse('door',filename);
        return Promise.resolve(false);
    }
    // ── Имя не имеет права врать про контейнер ───────────────────────────
    // MediaRecorder на Safari не умеет webm и отдаёт audio/mp4 (по документации
    // WebKit; локально это НЕ проверено — в сборке WebKit у Playwright
    // MediaRecorder отсутствует вовсе, `__hasMR:false`. Подтвердит или опровергнет
    // событие `file_download_relabelled` ниже: если премиса верна, оно начнёт
    // приходить из Safari). А тул,
    // у которого расширение зашито строкой ('recording.webm'), вручает
    // человеку файл, который его устройство не откроет НИЧЕМ: iOS не знает
    // webm ни в «Файлах», ни в плеере. Это тот же класс, что и обращение
    // #7693 (отчёт .json ушёл VPN-клиенту) — файл, чьё имя не соответствует
    // тому, чем он является.
    //
    // Правило НАМЕРЕННО узкое: чиним только «.webm при не-webm байтах».
    // Обратную сторону трогать нельзя — .m4r у рингтона при audio/mp4 это
    // осознанное имя (замер 10.09.2026: m4r живёт в выдаче), и «починка» имени
    // сломала бы ровно то, ради чего тул существует. Масштаб самого класса
    // измерен там же: 11 выдач из шести тулов в Safari за 90 дней — мало, но
    // это ровно те люди, у которых файл мёртв, и одна строка в общей двери
    // закрывает и будущие тулы.
    if(blob&&blob.type&&/\.webm$/i.test(filename)&&!/webm/i.test(blob.type)){
        var _CONT={'audio/mp4':'m4a','audio/x-m4a':'m4a','audio/aac':'m4a','audio/mpeg':'mp3',
                   'audio/ogg':'ogg','audio/wav':'wav','audio/x-wav':'wav','audio/flac':'flac',
                   'video/mp4':'mp4','video/ogg':'ogg','video/quicktime':'mov','video/x-matroska':'mkv'};
        var _mime=String(blob.type).split(';')[0].toLowerCase().trim();
        var _to=_CONT[_mime];
        if(_to){
            filename=filename.replace(/\.webm$/i,'.'+_to);
            try{if(window.__ae)window.__ae('feature_use',{f:'file_download_relabelled',
                slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                from:'webm',to:_to,mime:_mime});}catch(e){}
        }
    }

    // ── The file leaves with the TYPE its name says, when its own type says nothing ──
    // A browser that saves a blob through its OWN download path never sees the `download`
    // attribute and names the file by the blob's type: DuckDuckGo for Android writes
    // `<uuid>.<extension of that type>`. FFmpegProcessor.process() types every VIDEO output
    // application/octet-stream (its map is the audio catalogue), so a repaired mp4 arrived
    // there as `31c507e6-….bin` — a file nothing on the phone offers to open (production,
    // 14.10.2026, the browser of comment #963). The relay learned the same on the server
    // (#1820, DownloadStashController::servedMimeFor); this is the door's own answer, for
    // every channel below: the share sheet and the relay get the right type from it too.
    // A Blob of a Blob is a reference — no bytes are copied. Markup (svg, html, xml) is
    // left as it came, as the relay leaves it.
    var _given=blob,_typedFrom='';
    if(blob&&typeof Blob!=='undefined'){
        var _bt=String(blob.type||'').split(';')[0].toLowerCase().trim();
        if(!_bt||/^(application\/(octet-stream|binary|x-download|force-download|download|unknown)|binary\/octet-stream)$/.test(_bt)){
            var _nt=window.__mimeOfName?window.__mimeOfName(filename):'';
            if(_nt){
                try{blob=new Blob([blob],{type:_nt});_typedFrom=_bt||'none';}catch(e){blob=_given;}
            }
        }
    }

    var src=(opts&&opts.src)||'direct';
    // Vias that actually put bytes on the device. THE primitive is the single
    // source of truth for "a file was delivered": it emits the telemetry, the
    // `file-downloaded` event (toast + history) and the where-hint — and it
    // does so ONLY here, only for these vias.
    //
    // Everything else (stash_fallback, classic_oversize, ios_share_cancel/fail)
    // is a FAILURE. Signalling success on those is what bug-report
    // egorkaravaev11 (2026-07-10, /ru/stereo-to-mono) was: "написано что файл
    // скачан, но ничего не скачалось" — 13 clicks, 13 dead fallbacks, 13 toasts.
    var DELIVERED={sw:1,stash:1,ios_share:1,ios_share_tap:1,classic_native:1,classic_anchor:1,classic:1,classic_no_fetch:1,tg_native:1,fsa:1,share_rescue:1};
    // Exactly-once `file-downloaded`: the classic_* vias click an <a download>,
    // which the HTMLAnchorElement.click patch already turns into the event. Only
    // the anchor-less channels (SW iframe, stash's attribute-less <a>, iOS share,
    // the FSA picker) must emit it themselves — dispatching for classic too would
    // double-fire and duplicate every entry in timbrica-history.
    var SELF_DISPATCH={sw:1,stash:1,ios_share:1,ios_share_tap:1,tg_native:1,fsa:1,share_rescue:1};
    // `ok` — ВЕРДИКТ, а не догадка потребителя: 1 только когда файл реально
    // дошёл. Все события этого модуля носят имя `file_download` (одно семейство
    // на канал, повтор, отмену и отказ), и до 2026-09-02 читатели выводили
    // «файл забрали» из ИМЕНИ. Из-за этого панель возвратов считала доставкой
    // `stash_retry` (6061 за 30 дней), `held_gesture` (791 — файл намеренно
    // ПРИДЕРЖАН), `blob_unreadable` (548 — отказ), `classic_oversize`,
    // `fsa_cancel`, `share_rescue_fail` — ~9600 событий, то есть 3% сверху, а у
    // отдельного человека это решало деньги: возврат #26 показывал «забрал
    // файлов 2», где один был ТОТ САМЫЙ отказ, на который человек и жаловался,
    // а второй — 20-секундный огрызок.
    // Истина уже жила здесь, в `DELIVERED`; неправильным был её ПОВТОРНЫЙ
    // вывод на стороне PHP. Поэтому вердикт едет в самом событии, и ни один
    // будущий канал не может разойтись с ним молча.
    // ── `ext`: what KIND of file we hand people, per device ──────────────
    // Added 2026-09-10, after support #7693: a music-analysis report went out
    // as .json, Android handed it to the app that claimed application/json —
    // on a Russian phone a VPN client — and the person got «Неподдерживаемая
    // конфигурация». The site could not answer the first question that bug
    // raises («how often do we do this, and to whom?») because delivery
    // telemetry carried the channel and the size but never the format: the
    // census had to be re-derived from ~424 source files, and even then it
    // missed tools whose name is built rather than written. One field here
    // makes «machine formats delivered to phones» a query instead of an
    // archaeology dig, for every future channel too.
    var _ext=(function(){
        var m=/\.([A-Za-z0-9]{1,8})$/.exec(String(filename||''));
        return m?m[1].toLowerCase():'';
    })();
    // The rescue copy this call put into IndexedDB (export-keep, see _route).
    // It is dropped 3 s after ANY confirmed delivery — not only the relay's:
    // until 2026-09-27 only the stash dropped it, so an iOS person who saved
    // through the share sheet met «Прошлый экспорт не был скачан» on their next
    // page for a file they already had. The 3 s are the relay's own reasoning:
    // a delivery that kills the page never runs the timer, and the copy stays.
    var _kept=false;
    // The page's last run took at least this long — its result is worth a copy
    // until the person has it. The same 20 s the run bracket waits before it asks
    // «точно уйти?»: shorter work is cheaper to redo than to store.
    var KEEP_RUN_S=20;
    var _costlyRun=function(){
        try{
            var M=window.__runMark;
            if(!M||typeof M.lastRun!=='function')return false;
            return (M.active()?M.since():M.lastRun())>=KEEP_RUN_S;
        }catch(e){return false;}
    };
    // A delivery confirms the copy away only on a page somebody is looking at.
    // An auto-download of a run that finished while the screen was off or the
    // browser minimized is a click into a tab nobody sees: on a phone that tab is
    // frozen and discarded next, and whether the browser took the file is unknown.
    // So: visible → 3 s later; hidden → 3 s after the page is seen again. Leaving
    // the page (pagehide) confirms it AT ONCE — the person pressed «Скачать» and went
    // on (a desktop browser finishes a background download by itself) — except for
    // a channel that itself navigates the tab (the relay; Telegram's openLink) and the
    // anchor on iOS, which can take the tab to the blob URL (__dlAnchorWatch): there a
    // pagehide may be the delivery dying. A tab killed while hidden runs nothing, and
    // the restore bar offers the copy on the next view. The confirmation is a sync
    // mark (__exportKeepDelivered) — a delete started in pagehide does not get to run.
    var KEEP_LEAVE_NAVIGATES={stash:1,tg_native:1};
    var _dropWhenSeen=function(via){
        var done=false,t=null;
        var leaveConfirms=!KEEP_LEAVE_NAVIGATES[via]&&
            !(/^classic/.test(String(via||''))&&window.__iosDevice&&window.__iosDevice());
        var off=function(){if(t){clearTimeout(t);t=null;}document.removeEventListener('visibilitychange',onVis);window.removeEventListener('pagehide',onHide);};
        var confirm=function(){
            if(done)return;done=true;off();
            try{if(window.__exportKeepDelivered)window.__exportKeepDelivered();else if(window.__exportKeepDrop)window.__exportKeepDrop();}catch(e){}
        };
        var arm=function(){if(!t&&!done)t=setTimeout(confirm,3000);};
        var onVis=function(){if(document.visibilityState!=='hidden')arm();};
        var onHide=function(e){if(e&&e.persisted)return;if(leaveConfirms)confirm();else{done=true;off();}};
        try{
            document.addEventListener('visibilitychange',onVis);
            window.addEventListener('pagehide',onHide);
            if(document.visibilityState!=='hidden')arm();
        }catch(e){arm();}
    };
    // true while the relay's take-watch owns what the person reads about this file.
    var _hintOwned=false;
    var _ev=function(via,extra){
        // kept/hid: was a rescue copy held for this delivery, and was the page out of
        // sight when it went — the two facts the restore bar's numbers are read by.
        var _held=_kept||!!(window.__exportKeepHas&&window.__exportKeepHas(_given));
        try{if(window.__ae)window.__ae('feature_use',Object.assign(
            {slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
             f:'file_download',via:via,src:src,ok:DELIVERED[via]?1:0,ext:_ext,
             sizeKB:blob?Math.round(blob.size/1024):0},_typedFrom?{ty:_typedFrom}:{},
             _held?{kept:1}:{},document.visibilityState==='hidden'?{hid:1}:{},extra||{}));}catch(e){}
        if(!DELIVERED[via])return;
        // …or the copy the TOOL put there when the result was born (a result that cost
        // minutes is kept from that moment, not from the first press on «Скачать» —
        // /repair-video, 13.10.2026): the same bytes, so the same delivery clears it.
        // Not while the relay's take-watch is still asking whether the browser
        // took the file: the copy goes when the server says it did (_takeWatch).
        if(_held&&!_hintOwned){
            _kept=false;
            _dropWhenSeen(via);
        }
        if(SELF_DISPATCH[via]){
            try{window.dispatchEvent(new CustomEvent('file-downloaded',{detail:{name:filename,via:via,size:blob?blob.size:0}}))}catch(e){}
        }
        // The iOS share sheet and the FSA save dialog both let the user pick
        // the destination, so a "look in Downloads" hint would be wrong there.
        // The same file pressed again is the one moment the hint is most likely
        // a lie — then the offer below speaks instead of it.
        // …and a delivery the relay is still checking (_takeWatch) speaks for itself.
        if(via.indexOf('ios_share')!==0&&via!=='fsa'&&via!=='share_rescue'&&!_hintOwned){
            var _sz=blob?blob.size:0,_ty=blob?blob.type:'';
            if(via==='classic_native')_repeatOffer().then(function(said){if(!said)window.__downloadWhereHint(_sz,_ty,filename);});
            else window.__downloadWhereHint(_sz,_ty,filename);
        }
    };
    // Does this call carry the user's tap? true / false where the engine says
    // (navigator.userActivation — Safari 16.4+, Chromium), null where it cannot.
    var _activationState=function(){
        try{var u=navigator.userActivation;if(u&&typeof u.isActive==='boolean')return u.isActive;}catch(e){}
        return null;
    };
    // The share sheet, one tap away: a bar that stays until pressed or closed,
    // whose button is a FRESH activation — share() works from it. Names the
    // item of the iOS menu to press, in that menu's own words, because «Save»
    // alone leaves the person hunting through a sheet of twenty icons.
    // `fallback` — what is left if the sheet refuses even from the tap (the
    // relay chain). It is PASSED IN: the chain lives inside _route, out of
    // this function's scope — calling it by name threw ReferenceError on
    // exactly this rescue path (caught by tests/node/js-scope-leak, 27.09).
    // The item of the iOS share menu that saves this file, in that menu's own words.
    var _shareItem=function(type){
        var L=(window.__LOCALE||'en').substring(0,2);
        var kind=/^video\//.test(type||'')?1:(/^image\//.test(type||'')?2:0);
        var ITEM={ru:['Сохранить в «Файлы»','Сохранить видео','Сохранить изображение'],
                  en:['Save to Files','Save Video','Save Image'],
                  de:['In „Dateien“ sichern','Video sichern','Bild sichern'],
                  es:['Guardar en Archivos','Guardar video','Guardar imagen'],
                  fr:['Enregistrer dans Fichiers','Enregistrer la vidéo','Enregistrer l’image'],
                  pt:['Salvar em Arquivos','Salvar Vídeo','Salvar Imagem'],
                  id:['Simpan ke File','Simpan Video','Simpan Gambar'],
                  ko:['파일에 저장','비디오 저장','이미지 저장'],
                  ar:['حفظ في الملفات','حفظ الفيديو','حفظ الصورة'],
                  uk:['Зберегти у «Файли»','Зберегти відео','Зберегти зображення'],
                  tr:['Dosyalar’a Kaydet','Videoyu Kaydet','Görüntüyü Kaydet']};
        return (ITEM[L]||ITEM.en)[kind];
    };
    // `tag` — the route's marks ({ph:1} for a video sent to Photos), carried by
    // every event of this bar so the route can be measured on its own.
    var _tapToSave=function(shareFile,fallback,tag){
        try{
            if(!window.__toast)return;
            var L=(window.__LOCALE||'en').substring(0,2);
            var it=_shareItem(shareFile.type);
            var MSG={ru:'Файл готов. Нажмите «Сохранить» — откроется меню «Поделиться», в нём выберите «'+it+'».',
                     en:'Your file is ready. Tap “Save” — the Share menu opens; choose “'+it+'”.',
                     de:'Die Datei ist fertig. Tippen Sie auf „Speichern“ — das Teilen-Menü öffnet sich; wählen Sie „'+it+'“.',
                     es:'Tu archivo está listo. Toca «Guardar»: se abre el menú Compartir; elige «'+it+'».',
                     fr:'Votre fichier est prêt. Touchez « Enregistrer » : le menu Partager s’ouvre, choisissez « '+it+' ».',
                     pt:'Seu arquivo está pronto. Toque em “Salvar” — o menu Compartilhar abre; escolha “'+it+'”.',
                     id:'File Anda siap. Ketuk “Simpan” — menu Bagikan terbuka; pilih “'+it+'”.',
                     ko:'파일이 준비되었습니다. “저장”을 누르면 공유 메뉴가 열립니다. “'+it+'”을 선택하세요.',
                     ar:'ملفك جاهز. اضغط «حفظ» — ستُفتح قائمة المشاركة؛ اختر «'+it+'».',
                     uk:'Файл готовий. Натисніть «Зберегти» — відкриється меню «Поділитися», у ньому виберіть «'+it+'».',
                     tr:'Dosyanız hazır. “Kaydet”e dokunun — Paylaş menüsü açılır; “'+it+'” seçeneğini seçin.'};
            var BTN={ru:'Сохранить',en:'Save',de:'Speichern',es:'Guardar',fr:'Enregistrer',pt:'Salvar',
                     id:'Simpan',ko:'저장',ar:'حفظ',uk:'Зберегти',tr:'Kaydet'};
            window.__toast(MSG[L]||MSG.en,'info',{ctx:'download_tap',action:{label:BTN[L]||BTN.en,onclick:function(){
                try{
                    navigator.share({files:[shareFile]}).then(function(){
                        _ev('ios_share_tap',tag);
                    }).catch(function(err){
                        var nm=(err&&err.name)||'';
                        if(nm==='AbortError'){_ev('ios_share_tap_cancel',tag);return;}
                        _ev('ios_share_tap_fail',Object.assign({err:String(nm||err).slice(0,40)},tag||{}));
                        // The sheet refused even from a tap: what the route has
                        // left (the relay in a WebView, the anchor in a browser).
                        if(fallback)fallback();
                    });
                }catch(e){_ev('ios_share_tap_fail',Object.assign({err:'throw'},tag||{}));if(fallback)fallback();}
            }}});
        }catch(e){}
    };
    // Honest failure: every local channel is exhausted and the classic blob
    // anchor is a KNOWN no-op in this browser class. Clicking one anyway would
    // trip the HTMLAnchorElement.click patch and show "saved!" for a file that
    // never arrived — so we don't, and we say so.
    var _failNotice=function(){
        try{
            if(!window.__toast)return;
            var L=(window.__LOCALE||'en').substring(0,2);
            var M={ru:'Не удалось сохранить файл. Попробуйте ещё раз или откройте страницу в обычном браузере.',
                   en:'Could not save the file. Try again, or open this page in a regular browser.',
                   de:'Datei konnte nicht gespeichert werden. Versuchen Sie es erneut oder öffnen Sie die Seite in einem normalen Browser.',
                   es:'No se pudo guardar el archivo. Inténtalo de nuevo o abre esta página en un navegador normal.',
                   fr:'Impossible d’enregistrer le fichier. Réessayez ou ouvrez cette page dans un navigateur classique.',
                   pt:'Não foi possível salvar o arquivo. Tente novamente ou abra esta página num navegador comum.',
                   id:'Gagal menyimpan file. Coba lagi, atau buka halaman ini di peramban biasa.',
                   ko:'파일을 저장하지 못했습니다. 다시 시도하거나 일반 브라우저에서 이 페이지를 여세요.',
                   ar:'تعذّر حفظ الملف. حاول مرة أخرى أو افتح هذه الصفحة في متصفح عادي.', uk: 'Не вдалося зберегти файл. Спробуйте ще раз або відкрийте сторінку у звичайному браузері.', tr: 'Dosya kaydedilemedi. Tekrar deneyin veya bu sayfayı normal bir tarayıcıda açın.'};
            // Where the OS can share files, add a one-tap "Save file" rescue.
            // Every path that reaches here (Android-WebView stash fully failed,
            // Telegram anchor failed, iOS share activation expired) has consumed
            // the original click's transient activation, so share() could not be
            // called inline — but the ACTION BUTTON's click is a FRESH activation,
            // so share({files}) works and delivers with ZERO upload.
            // Safe where the reverted SW channel was not: the SW served-ack was a
            // FALSE POSITIVE (OS download-manager re-fetched over the network,
            // bypassing the SW → 404, ack still fired → telemetry lied, stein-9966
            // → refund). share() resolves ONLY on real OS completion (cancel =
            // AbortError), so share_rescue can never report a phantom delivery.
            // Gated on canShare({files}); touches no happy path — purely additive.
            // Real-Android efficacy is deploy-measured, not claimed from a green.
            var _topts={ctx:'download_fail'};
            var shareFile=null;
            try{
                // ⚠️ canShare() DOES NOT CHECK SIZE. Chromium refuses
                // share({files}) above kMaxSharedFileBytes = 50 MB (and above 10
                // files) — but canShare() answers on TYPE alone and says yes, so
                // the rescue button was being offered on payloads it could never
                // carry. Measured on prod, RU shard, 30 days:
                //     <50 MB   37 delivered · 42 failed · 54 cancelled
                //    >=50 MB    0 delivered · 256 failed  (mean 329 MB)
                // Zero successes above the line, 256 dead ends below it — and
                // this button is the LAST thing a user sees before giving up
                // (Yuri, 406 MB, NotAllowedError twice, 2026-08-31). So the gate
                // is on OUR side of the call; iOS Safari carries ~300 MB but the
                // rescue only ever runs where the share sheet is the fallback,
                // and being 6× conservative there costs one honest message.
                if(blob&&blob.size<=SHARE_MAX_BYTES&&navigator.canShare){
                    shareFile=new File([blob],filename,{type:blob.type||'application/octet-stream'});
                    if(!navigator.canShare({files:[shareFile]}))shareFile=null;
                }
            }catch(e){shareFile=null;}
            if(shareFile){
                var SL={ru:'Сохранить файл',en:'Save file',de:'Datei speichern',es:'Guardar archivo',
                        fr:'Enregistrer',pt:'Salvar arquivo',id:'Simpan file',ko:'파일 저장',ar:'حفظ الملف',uk:'Зберегти файл',tr:'Dosyayı kaydet'};
                _topts.action={label:SL[L]||SL.en,onclick:function(){
                    try{
                        navigator.share({files:[shareFile]}).then(function(){
                            _ev('share_rescue');
                        }).catch(function(err){
                            var nm=(err&&err.name)||'';
                            if(nm==='AbortError'){_ev('share_rescue_cancel');return;}
                            _ev('share_rescue_fail',{err:String(nm||err).slice(0,40)});
                        });
                    }catch(e){_ev('share_rescue_fail',{err:'throw'});}
                }};
            }
            window.__toast(M[L]||M.en,'error',_topts);
        }catch(e){}
    };
    var _revokeAfterMs=function(b){
        return Math.max(120000,Math.round(((b&&b.size)||0)/(8*1048576)*1000));
    };
    var _classic=function(via){
        try{
            var url=URL.createObjectURL(blob);
            var a=document.createElement('a');
            a.href=url;a.download=filename;
            // Размер — для `file-downloaded` патча клика (telemetry-tier0): по
            // расширению и размеру дверь отличает повтор того же файла от нового.
            a.__dlSize=blob&&blob.size||0;
            // data-rawdl: tells the global click-interceptor below to leave
            // THIS anchor alone — otherwise the fallback would be re-caught
            // and loop back into __downloadFile.
            a.setAttribute('data-rawdl','1');
            document.body.appendChild(a);a.click();a.remove();
            try{if(window.__dlAnchorClicked)window.__dlAnchorClicked({blob:blob,name:filename,via:via||'classic'});}catch(e){}
            // Keep the blob URL alive long enough for a DELAYED re-read. Yandex
            // Browser Protect (AV re-scan) and pause/resume RE-REQUEST the
            // download source after the transfer has started; a blob: URL cannot
            // be re-fetched over the network, so the browser has to re-read it
            // in-page — and revoking at 4 s aborted exactly that («загрузка
            // прервана», bug-report mnaumov 2026-07-17). MEASURED in
            // tests/playwright/download-desktop-refetch.mjs: with the old 4 s
            // timer the blob is readable at +1 s and DEAD at +6 s. 120 s covers
            // an AV rescan plus a short pause/resume; it is still bounded because
            // the object URL pins the blob until revoked (so this is not "never").
            // 120 s is the FLOOR, not the window: it was sized on files of
            // megabytes, and a file of gigabytes is still being copied when it
            // runs out (a 22.9 GB /screen-recorder take took 352 s on a loaded
            // desktop — 65 MB/s; a person's «Сохранить как» of 9.5 GB took 390 s).
            // The window grows with the file at 8 MiB/s, a rate any disk beats.
            setTimeout(function(){try{URL.revokeObjectURL(url)}catch(e){}},_revokeAfterMs(blob));
            _ev(via||'classic');
            return true;
        }catch(e){return false;}
    };
    // ── FSA-confirmed save (repeat-download path, desktop Chromium) ──
    // showSaveFilePicker → createWritable → write(blob) → close(): close()
    // resolving IS the write confirmation the classic anchor can never give.
    // NOT built on __FsaSink — that primitive wraps a showDirectoryPicker
    // dirHandle for streamed multi-file output; this is a one-shot single-file
    // save dialog. Failure semantics:
    //   AbortError            → the user closed the dialog: an informed cancel,
    //                           NOT a failure. No classic fallback (they just
    //                           declined a save — surprising them with a silent
    //                           Downloads-folder copy would override that).
    //   SecurityError/act.    → no user activation (programmatic call) → the
    //                           dialog can't open; classic fallback, invisible.
    //   anything else         → the REAL local failure (AV lock, disk full,
    //                           policy) — finally observable. Honest error
    //                           toast naming it; no lying "saved" signal.
    // ── Readability pre-flight ──────────────────────────────────────────────
    // A Blob whose bytes cannot be READ cannot be delivered by ANY channel, so
    // this is checked once, up front, before a channel is chosen.
    //
    // Two measured failure modes produce exactly such a blob (2026-08-03):
    //  1. HARD LIMIT — a Blob of 2 GiB or more is unreadable in Chromium.
    //     Pinned exactly: 2 139 095 040 B (1.9922 GiB) reads fine;
    //     2 147 483 648 B (2^31) throws NotReadableError on head AND tail.
    //  2. STORE PRESSURE — below 2 GiB a blob can still be unreadable when too
    //     much other blob data is alive. video-frames: a 1.34 GB archive built
    //     while 1.34 GB of source frames were still held → the archive was
    //     unreadable in one read and in 8 MB slices alike.
    //
    // Because mode 2 is contextual, a size threshold would be a guess. Reading
    // 1 KB of the tail is not: it answers the only question that matters, costs
    // ~1 ms on a healthy blob, and turns a silent loss into an honest message.
    // Before this, `_classic` clicked an anchor for unreadable bytes, returned
    // true and fired the «Файл сохранён» hint — measured on screen-recorder-
    // shaped payloads (prod: up to 8.85 GB) and on the 1.34 GB archive.
    // Names browsers use when the BYTES are gone, as opposed to the probe itself
    // being unavailable. Same set as ToolErrors.isFileLostError.
    var _READ_ERR={NotReadableError:1,NotFoundError:1,SecurityError:1,InvalidStateError:1};
    var _readable=function(){
        if(!blob||!blob.size)return Promise.resolve(true);
        // ── The HEAD through a stream, not a 1 KB slice (2026-10-08) ──
        // Chromium keeps every Blob in ONE store per profile (2 GiB in memory when it
        // cannot page to disk; ~1 % of RAM on Android). Once it is full, every NEW
        // blob is born unreadable — even 1 byte. A slice is a new blob, so the old
        // probe refused files that were perfectly intact (built before the store
        // filled; reproduced: such a file downloads byte-identically while its slice
        // probe fails). Reading the head through stream() allocates nothing in the
        // store and matched the real download outcome in every case measured.
        // A stream rejects with TypeError, so ANY rejection means unreadable.
        try{
            if(typeof blob.stream==='function'){
                var rd=blob.stream().getReader();
                return rd.read().then(function(){
                    try{rd.cancel();}catch(_){}
                    _sliceVerdict();
                    return true;
                },function(){return false;});
            }
        }catch(e){/* no stream here — the slice below */}
        var tail;
        try{
            if(typeof blob.slice!=='function')return Promise.resolve(true);
            tail=blob.slice(Math.max(0,blob.size-1024),blob.size);
        }catch(e){
            // A SYNCHRONOUS throw from slice() is a real read failure, not a
            // missing probe — treating it as "fine" put «Файл сохранён» back on
            // screen for a file that could not be read (measured 2026-08-03).
            return Promise.resolve(!_READ_ERR[(e&&e.name)||'']);
        }
        if(!tail||typeof tail.arrayBuffer!=='function')return Promise.resolve(true);
        return tail.arrayBuffer().then(function(){return true;},function(){return false;});
    };
    // Facts about the pre-flight itself — NOT delivery attempts, so not `file_download`
    // (every `file_download` row is read as one attempt by the delivery dashboards).
    var _note=function(f,extra){
        try{if(window.__ae)window.__ae('feature_use',Object.assign({slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
            f:f,ext:_ext,sizeMB:blob?Math.round(blob.size/1048576):0},extra||{}));}catch(e){}
    };
    // How often the OLD probe would have refused a readable file, counted in prod
    // (does not wait, decides nothing): the size of the class the stream probe fixed.
    var _sliceVerdict=function(){
        try{
            blob.slice(Math.max(0,blob.size-1024),blob.size).arrayBuffer()
                .then(null,function(){_note('dl_probe_false_alarm');});
        }catch(e){_note('dl_probe_false_alarm',{sync:1});}
    };
    // ── The rescue: the caller's own bytes, written to OPFS (2026-10-08) ──
    // A blob born in a full store stays unreadable, and so does any new Blob or
    // data: URL built from the same bytes (the data: anchor even fails SILENTLY).
    // An OPFS file is a file on disk, not a store entry: written from the bytes the
    // tool still holds (opts.bytes), it was delivered byte-identically up to 490 MB
    // in the reproduction. Only the caller's own bytes — never a guess.
    var _rescue=function(){
        var b=opts&&opts.bytes;
        if(!b||(opts&&opts.__rescued))return Promise.resolve(null);
        var u8=b instanceof Uint8Array?b:(b instanceof ArrayBuffer?new Uint8Array(b):null);
        if(!u8||u8.byteLength!==blob.size||!navigator.storage||typeof navigator.storage.getDirectory!=='function')return Promise.resolve(null);
        var name='_dlr_'+Date.now()+'_'+Math.random().toString(36).slice(2,8)+((window.__opfsOwner&&window.__opfsOwner.tag('_'))||'');
        var fh=null;
        return navigator.storage.getDirectory().then(function(root){return root.getFileHandle(name,{create:true});})
            .then(function(h){fh=h;if(typeof h.createWritable!=='function')throw new Error('no createWritable');return h.createWritable();})
            .then(function(w){return w.write(u8).then(function(){return w.close();});})
            .then(function(){return fh.getFile();})
            .then(function(f){return f&&f.size===u8.byteLength?f:null;})
            .catch(function(e){_note('dl_rescue',{ok:0,err:String((e&&e.name)||e).slice(0,60)});return null;});
    };
    // What we actually know about an unreadable payload, so the message matches
    // the situation instead of always blaming size. Measured: a 64 KB File with
    // a revoked handle was told «Файл (0.0 ГБ) … слишком велик».
    var _unreadableKind=function(){
        if(blob.size>=2147483648)return 'oversize';          // hard limit: Blob >= 2^31 B
        if(typeof File!=='undefined'&&_given instanceof File)return 'file_lost';
        return 'pressure';                                    // our own result, too big to hand over here
    };
    // Honest notice when the payload itself cannot be handed to the browser.
    // `kind` decides WHICH truth is told — a revoked pick and an oversize
    // result are different problems with different user actions.
    var _tooLargeNotice=function(size,kind){
        try{
            if(!window.__toast)return;
            var GB=(size/1073741824).toFixed(1);
            var L=(window.__LOCALE||'en').substring(0,2);
            if(kind==='file_lost'){
                var FL={ru:'Файл больше недоступен для чтения — браузер освободил его (так бывает после сворачивания вкладки или нехватки памяти). Выберите файл заново и повторите.',
                        en:'The file can no longer be read — the browser released it (this happens after backgrounding the tab or under memory pressure). Pick the file again and retry.',
                        de:'Die Datei kann nicht mehr gelesen werden — der Browser hat sie freigegeben (das passiert nach dem Wechseln des Tabs oder bei Speichermangel). Wählen Sie die Datei erneut aus und versuchen Sie es nochmal.',
                        es:'El archivo ya no se puede leer: el navegador lo liberó (ocurre al pasar la pestaña a segundo plano o por falta de memoria). Vuelve a elegir el archivo e inténtalo de nuevo.',
                        fr:'Le fichier n’est plus lisible — le navigateur l’a libéré (cela arrive après un passage en arrière-plan ou par manque de mémoire). Sélectionnez à nouveau le fichier et réessayez.',
                        uk:'Файл більше не читається — браузер його звільнив (так буває після згортання вкладки або через брак памʼяті). Виберіть файл ще раз і повторіть.',
                        tr:'Dosya artık okunamıyor — tarayıcı onu serbest bıraktı (bu, sekme arka plana alındığında veya bellek yetersiz kaldığında olur). Dosyayı yeniden seçip tekrar deneyin.',
                        pt:'O arquivo não pode mais ser lido — o navegador o liberou (acontece ao deixar a aba em segundo plano ou por falta de memória). Selecione o arquivo novamente e tente de novo.',
                        id:'File tidak dapat dibaca lagi — peramban melepaskannya (terjadi setelah tab ke latar belakang atau saat memori menipis). Pilih file lagi lalu coba ulang.',
                        ko:'파일을 더 이상 읽을 수 없습니다 — 브라우저가 해제했습니다(탭을 백그라운드로 두거나 메모리가 부족할 때 발생). 파일을 다시 선택한 뒤 재시도하세요.',
                        ar:'لم يعد بالإمكان قراءة الملف — حرّره المتصفح (يحدث بعد نقل التبويب إلى الخلفية أو عند نقص الذاكرة). اختر الملف من جديد وأعد المحاولة.'};
                window.__toast(FL[L]||FL.en,'error');
                return;
            }
            // A SMALL result the browser refused to read is not "too large", and
            // saying so sends the person to shrink a file that is already tiny.
            // Measured 30 d on /audio-metadata alone: 9 × «Файл (0.0 ГБ) … слишком
            // велик» on ~5 MB MP3s. Below half a gigabyte the honest sentence is
            // what is actually known: the browser could not read the result —
            // retry, and a reload clears whatever state produced it.
            if(kind==='pressure'&&size<536870912){
                var MB=Math.max(1,Math.round(size/1048576));
                // The cause, measured 2026-10-08: the browser's file store (one per
                // profile) is full — other heavy tabs count too. Closing tabs frees it
                // at once; a reload only helps when THIS page holds the files.
                var SM={ru:'Браузер не смог прочитать готовый файл ('+MB+' МБ): его хранилище файлов переполнено — так бывает, когда открыто много тяжёлых вкладок. Закройте лишние вкладки и сохраните ещё раз.',
                        en:'The browser could not read the finished file ('+MB+' MB): its file storage is full — this happens with many heavy tabs open. Close the tabs you do not need and save again.',
                        uk:'Браузер не зміг прочитати готовий файл ('+MB+' МБ): його сховище файлів переповнене — так буває, коли відкрито багато важких вкладок. Закрийте зайві вкладки й збережіть ще раз.',
                        de:'Der Browser konnte die fertige Datei ('+MB+' MB) nicht lesen: Sein Dateispeicher ist voll — das passiert bei vielen schweren Tabs. Schließen Sie nicht benötigte Tabs und speichern Sie erneut.',
                        es:'El navegador no pudo leer el archivo terminado ('+MB+' MB): su almacenamiento de archivos está lleno; pasa cuando hay muchas pestañas pesadas abiertas. Cierra las pestañas que no necesites y guarda de nuevo.',
                        fr:'Le navigateur n’a pas pu lire le fichier terminé ('+MB+' Mo) : son espace de fichiers est plein — cela arrive avec de nombreux onglets lourds ouverts. Fermez les onglets inutiles et enregistrez de nouveau.',
                        pt:'O navegador não conseguiu ler o arquivo pronto ('+MB+' MB): o armazenamento de arquivos dele está cheio — isso acontece com muitas abas pesadas abertas. Feche as abas que não precisa e salve de novo.',
                        id:'Peramban tidak dapat membaca file yang sudah jadi ('+MB+' MB): penyimpanan file peramban penuh — terjadi saat banyak tab berat terbuka. Tutup tab yang tidak diperlukan lalu simpan lagi.',
                        ko:'브라우저가 완성된 파일('+MB+'MB)을 읽지 못했습니다. 브라우저의 파일 저장 공간이 가득 찼습니다(무거운 탭이 많이 열려 있을 때 발생). 필요 없는 탭을 닫고 다시 저장하세요.',
                        ar:'تعذّر على المتصفح قراءة الملف الجاهز ('+MB+' م.ب): مساحة الملفات في المتصفح ممتلئة — يحدث ذلك عند فتح علامات تبويب ثقيلة كثيرة. أغلق علامات التبويب غير اللازمة واحفظ مرة أخرى.',
                        tr:'Tarayıcı bitmiş dosyayı ('+MB+' MB) okuyamadı: dosya depolaması dolu — çok sayıda ağır sekme açıkken olur. Gereksiz sekmeleri kapatıp yeniden kaydedin.'};
                window.__toast(SM[L]||SM.en,'error');
                return;
            }
            var M={ru:'Файл ('+GB+' ГБ) не удалось передать браузеру — он слишком велик для сохранения целиком. Сделайте результат меньше (короче фрагмент, меньше качество или размер) или сохраняйте его частями.',
                   en:'The file ('+GB+' GB) could not be handed to the browser — it is too large to save in one piece. Make the result smaller (shorter piece, lower quality or size) or save it in parts.',
                   de:'Die Datei ('+GB+' GB) konnte nicht an den Browser übergeben werden — sie ist zu groß, um am Stück gespeichert zu werden. Machen Sie das Ergebnis kleiner (kürzerer Ausschnitt, geringere Qualität oder Größe) oder speichern Sie es in Teilen.',
                   es:'No se pudo entregar el archivo ('+GB+' GB) al navegador: es demasiado grande para guardarlo de una vez. Reduce el resultado (fragmento más corto, menor calidad o tamaño) o guárdalo por partes.',
                   fr:'Le fichier ('+GB+' Go) n’a pas pu être transmis au navigateur : il est trop volumineux pour être enregistré en une fois. Réduisez le résultat (extrait plus court, qualité ou taille moindre) ou enregistrez-le en plusieurs parties.',
                   uk:'Файл ('+GB+' ГБ) не вдалося передати браузеру — він завеликий, щоб зберегти його одним шматком. Зменшіть результат (коротший фрагмент, нижча якість чи розмір) або збережіть частинами.',
                   tr:'Dosya ('+GB+' GB) tarayıcıya aktarılamadı — tek parça hâlinde kaydedilemeyecek kadar büyük. Sonucu küçültün (daha kısa parça, daha düşük kalite veya boyut) veya parçalar hâlinde kaydedin.',
                   pt:'Não foi possível entregar o arquivo ('+GB+' GB) ao navegador — ele é grande demais para salvar de uma vez. Reduza o resultado (trecho menor, menos qualidade ou tamanho) ou salve em partes.',
                   id:'File ('+GB+' GB) tidak dapat diserahkan ke peramban — terlalu besar untuk disimpan sekaligus. Perkecil hasilnya (potongan lebih pendek, kualitas atau ukuran lebih rendah) atau simpan per bagian.',
                   ko:'파일('+GB+'GB)을 브라우저에 전달하지 못했습니다 — 한 번에 저장하기에 너무 큽니다. 결과를 줄이거나(구간 축소, 품질·크기 낮추기) 나누어 저장하세요.',
                   ar:'تعذّر تسليم الملف ('+GB+' ج.ب) إلى المتصفح — فهو أكبر من أن يُحفظ دفعة واحدة. صغّر النتيجة (مقطع أقصر أو جودة/حجم أقل) أو احفظها على أجزاء.'};
            window.__toast(M[L]||M.en,'error');
        }catch(e){}
    };
    // ⛔ NOT built: an "over the limit → save via showSaveFilePicker" rescue.
    // It was written, then measured, then deleted: the File System Access path
    // reads the same Blob, so it fails with the SAME NotReadableError (verified
    // at 2.44 GB, both as one write and in 8 MB slices). Once an unreadable
    // Blob exists the bytes are already gone — no channel can rescue them, and
    // a rescue branch would only add a dialog before the same failure. The fix
    // for oversize output belongs UPSTREAM: never materialise such a Blob
    // (stream to disk, or keep the result under the limit).
    /**
     * The DISK lane: showSaveFilePicker → createWritable → write(blob) → close().
     * `close()` resolving IS the write confirmation no anchor can give, it costs
     * no upload, and it has no size ceiling of its own.
     *
     * TRI-STATE, and the third state is the whole point:
     *   true  — delivered.
     *   false — the human saw a dialog and declined. STOP; do not "rescue" them
     *           with a silent copy they just refused.
     *   null  — THE LANE IS NOT AVAILABLE HERE. Try the next rung. Nothing was
     *           shown, nothing was decided, nothing is owed to the user.
     *
     * ⚠️ Distinguishing the last two is what makes this safe to route in a
     * WebView. File System Access shipped on Android and in Android WebView in
     * Chromium M132 (92.7 % of our WebView-class traffic runs M132+, measured
     * from a day of nginx logs on 2026-09-01) — but in a WebView the picker
     * only opens if the HOST APP implements WebChromeClient#onShowFileChooser
     * for MODE_SAVE, and most in-app browsers implement only the upload chooser.
     * When they don't, the promise rejects with AbortError — the SAME name a
     * human cancel produces. Read as a cancel it would strand the user with no
     * lane and no message; read by ELAPSED TIME it is unambiguous, because no
     * dialog was ever painted. A person cannot see, read and dismiss a system
     * dialog inside 400 ms; a missing implementation answers in single-digit ms.
     */
    var FSA_NO_DIALOG_MS=400;
    var _fsaConfirmed=function(){
        if(typeof window.showSaveFilePicker!=='function')return Promise.resolve(null);
        var _t0=Date.now();
        var pickerOpts={suggestedName:filename};
        // Offer a typed filter only when the blob carries a concrete mime and
        // the name has an extension — a mismatched pair makes the picker throw
        // TypeError, so fall back to an untyped dialog in that case.
        var ext=(/\.[a-z0-9]{1,8}$/i.exec(filename)||[])[0];
        if(blob.type&&/^(audio|video|image|text|application)\//.test(blob.type)&&ext){
            var acc={};acc[blob.type]=[ext.toLowerCase()];
            pickerOpts.types=[{accept:acc}];
        }
        var _pick=function(o){return window.showSaveFilePicker(o);};
        return _pick(pickerOpts)
            .catch(function(e){
                // TypeError = our types filter was rejected — retry untyped once.
                if(e&&e.name==='TypeError')return _pick({suggestedName:filename});
                throw e;
            })
            .then(function(handle){
                return handle.createWritable()
                    // ⚠️ ONE write(blob), never a slice loop. Measured
                    // 2026-09-01 (delivery-preserve-and-fsa): one shot is both
                    // faster AND more robust — 100/400/800/1200 MB at 0.15/0.56/
                    // 1.2/2.1 s with ~0 extra RSS, while the "streaming" sliced
                    // variant is ~25 % slower and DIED at 1200 MB with
                    // NotReadableError, because every live slice is another live
                    // blob reference against the same store-pressure wall. The
                    // stream consumes the blob without copying it; slicing it
                    // ourselves only adds pressure to look careful.
                    .then(function(w2){return w2.write(blob).then(function(){return w2.close();});})
                    .then(function(){_ev('fsa');return true;});
            })
            .catch(function(e){
                var n=(e&&e.name)||'';
                if(n==='AbortError'){
                    if(Date.now()-_t0<FSA_NO_DIALOG_MS){
                        // No dialog was painted — this WebView host has no
                        // MODE_SAVE. Not a decision; not the user's problem.
                        _ev('fsa_no_dialog',{ms:Date.now()-_t0});
                        return null;
                    }
                    // NOT releasing the gesture slot: the only routed caller
                    // falls through to the relay on a cancel, and that relay is
                    // still this gesture's one delivery. Releasing here would
                    // let a second click start a parallel upload of the same
                    // file — the exact double-delivery __dlGesture exists to
                    // prevent.
                    _ev('fsa_cancel',{ms:Date.now()-_t0});return false;
                }
                if(n==='SecurityError'||/activation|gesture/i.test(String(e&&e.message||''))){
                    _ev('fsa_noact');
                    return null;
                }
                // A real local write failure (AV lock, disk full, policy). Fall
                // through rather than stop: the next rung may still land the
                // bytes, and if nothing does, _failNotice speaks once at the
                // end. Recorded either way — this class was invisible until the
                // lane existed (comment #1071's «Ошибка сети» happened past our
                // code), and it stays visible now without costing the user a
                // scary toast for a lane we then recovered from.
                _ev('fsa_fail',{err:String(n||e).slice(0,40)});
                return null;
            });
    };
    // ── The same file asked for again: OFFER the confirmed save ────────────
    // Comment #1935 (16.10.2026, desktop Yandex 26.8, Премиум): «не получается
    // скачать, в папке загрузки не отображается». Her tab had been poisoned by
    // an earlier page (see _dlServerLink); every «Скачать» after that clicked the
    // anchor, logged classic_native ok:1, and showed «Файл сохранён в папку
    // «Загрузки» браузера (Ctrl+J)» — she pressed Ctrl+J and found nothing, 35
    // presses in 26 minutes. A page cannot see the browser drop a download (no
    // event, no error, no focus change — all measured), so the only signal is
    // the person's own: the SAME file asked for again. Measured on her build:
    // in a poisoned tab the File System Access save still writes the file —
    // it is not a download at all — and it also answers «where did it go?».
    // Offered, never forced: round 8 forced this dialog on every repeat and 45 %
    // of the dialogs were cancelled. Where showSaveFilePicker does not exist
    // (phones, Firefox, Safari) nothing changes. Resolves true when the offer
    // took the where-hint's place.
    var _repeatOffer=function(){
        if(typeof window.showSaveFilePicker!=='function'||!blob||!blob.size||!window.__toast)return Promise.resolve(false);
        return _dlFingerprint(blob).then(function(fp){
            if(!fp)return false;
            var r=_dlRepeatSeen(_ext,fp),now=Date.now();
            if(r.n<2)return false;
            // Still on screen (an action toast stays until closed) or just shut:
            // say nothing rather than stack a second copy.
            if(r.offeredAt&&now-r.offeredAt<60000)return true;
            r.offeredAt=now;
            return _offer(r);
        }).catch(function(){return false;});
    };
    var _offer=function(r){
        try{
            var L=(window.__LOCALE||'en').substring(0,2);
            var slug=(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||'');
            var M={ru:'Файла нет в «Загрузках»? Браузер иногда молча перестаёт сохранять файлы с открытой страницы. Нажмите «Сохранить как…» и выберите папку — файл запишется прямо туда.',
                   en:'Not in your Downloads? Browsers sometimes silently stop saving files from an open page. Click “Save as…” and pick a folder — the file is written straight there.',
                   de:'Nicht in „Downloads“? Browser speichern manchmal stillschweigend keine Dateien mehr von einer geöffneten Seite. Klicken Sie auf „Speichern unter…“ und wählen Sie einen Ordner — die Datei wird direkt dort gespeichert.',
                   es:'¿No aparece en Descargas? A veces el navegador deja de guardar archivos de una página abierta sin avisar. Haz clic en «Guardar como…» y elige una carpeta: el archivo se guarda directamente ahí.',
                   fr:'Pas dans vos Téléchargements ? Le navigateur cesse parfois sans prévenir d’enregistrer les fichiers d’une page ouverte. Cliquez sur « Enregistrer sous… » et choisissez un dossier : le fichier y est écrit directement.',
                   pt:'Não está em Downloads? Às vezes o navegador para de salvar arquivos de uma página aberta sem avisar. Clique em «Salvar como…» e escolha uma pasta — o arquivo é gravado diretamente nela.',
                   id:'Tidak ada di Unduhan? Peramban terkadang diam-diam berhenti menyimpan file dari halaman yang terbuka. Klik «Simpan sebagai…» lalu pilih folder — file langsung ditulis ke sana.',
                   ko:'다운로드 폴더에 없나요? 브라우저가 열린 페이지의 파일 저장을 알림 없이 멈출 때가 있습니다. «다른 이름으로 저장…»을 누르고 폴더를 고르면 파일이 그곳에 바로 저장됩니다.',
                   ar:'ليس في مجلد التنزيلات؟ قد يتوقف المتصفح أحيانًا دون تنبيه عن حفظ الملفات من صفحة مفتوحة. اضغط «حفظ باسم…» واختر مجلدًا — سيُكتب الملف فيه مباشرة.',
                   uk:'Файлу немає в «Завантаженнях»? Браузер іноді мовчки перестає зберігати файли з відкритої сторінки. Натисніть «Зберегти як…» і виберіть папку — файл запишеться просто туди.',
                   tr:'İndirilenler’de yok mu? Tarayıcı bazen açık bir sayfadan dosya kaydetmeyi sessizce durdurur. «Farklı kaydet…»e tıklayıp bir klasör seçin — dosya doğrudan oraya yazılır.'};
            var B={ru:'Сохранить как…',en:'Save as…',de:'Speichern unter…',es:'Guardar como…',fr:'Enregistrer sous…',
                   pt:'Salvar como…',id:'Simpan sebagai…',ko:'다른 이름으로 저장…',ar:'حفظ باسم…',uk:'Зберегти як…',tr:'Farklı kaydet…'};
            var OK={ru:'Файл записан в выбранную папку.',en:'The file is written to the folder you chose.',
                   de:'Die Datei ist im gewählten Ordner gespeichert.',es:'El archivo se guardó en la carpeta que elegiste.',
                   fr:'Le fichier est enregistré dans le dossier choisi.',pt:'O arquivo foi gravado na pasta escolhida.',
                   id:'File sudah ditulis ke folder pilihan Anda.',ko:'선택한 폴더에 파일이 저장되었습니다.',
                   ar:'كُتب الملف في المجلد الذي اخترته.',uk:'Файл записано у вибрану папку.',tr:'Dosya seçtiğiniz klasöre yazıldı.'};
            var NO={ru:'Файл не удалось записать. Так бывает, когда его сразу удаляет антивирус или защита браузера — загляните в их журнал.',
                   en:'The file could not be written. This happens when an antivirus or the browser’s protection removes it at once — check their log.',
                   de:'Die Datei konnte nicht gespeichert werden. Das passiert, wenn ein Virenscanner oder der Schutz des Browsers sie sofort entfernt — sehen Sie in deren Protokoll nach.',
                   es:'No se pudo guardar el archivo. Pasa cuando un antivirus o la protección del navegador lo elimina al instante: revisa su registro.',
                   fr:'Le fichier n’a pas pu être écrit. Cela arrive quand un antivirus ou la protection du navigateur le supprime aussitôt — consultez leur journal.',
                   pt:'Não foi possível gravar o arquivo. Isso acontece quando um antivírus ou a proteção do navegador o remove na hora — confira o registro deles.',
                   id:'File tidak dapat ditulis. Ini terjadi bila antivirus atau perlindungan peramban langsung menghapusnya — periksa log-nya.',
                   ko:'파일을 저장하지 못했습니다. 백신이나 브라우저 보호 기능이 즉시 삭제할 때 이런 일이 생깁니다 — 해당 기록을 확인하세요.',
                   ar:'تعذّر كتابة الملف. يحدث هذا عندما يحذفه برنامج مكافحة الفيروسات أو حماية المتصفح فورًا — راجع سجلهما.',
                   uk:'Файл не вдалося записати. Так буває, коли його одразу видаляє антивірус або захист браузера — перегляньте їхній журнал.',
                   tr:'Dosya yazılamadı. Bu, bir antivirüs ya da tarayıcının koruması dosyayı hemen sildiğinde olur — kayıtlarına bakın.'};
            var tag={slug:slug,ext:_ext,n:r.n,sizeKB:Math.round(blob.size/1024)};
            window.__toast(M[L]||M.en,'info',{silent:true,ctx:'dl_repeat',action:{label:B[L]||B.en,onclick:function(){
                try{if(window.__ae)window.__ae('feature_use',Object.assign({f:'dl_repeat_offer_click'},tag));}catch(e){}
                _fsaConfirmed().then(function(res){
                    if(res===true)window.__toast(OK[L]||OK.en,'success',{silent:true});
                    else if(res===null)window.__toast(NO[L]||NO.en,'error',{silent:true});
                },function(){});
            }}});
            try{if(window.__ae)window.__ae('feature_use',Object.assign({f:'dl_repeat_offer'},tag));}catch(e){}
            return true;
        }catch(e){return false;}
    };
    if(!window.fetch||!window.FormData||!blob){return Promise.resolve(_classic('classic_no_fetch'));}
    // Everything below only picks a CHANNEL. Whether there is anything
    // deliverable at all is decided once, by the readability pre-flight at the
    // bottom of this function — so the check gates every channel, not just the
    // classic anchor.
    var _route=function(){
    // ── The gesture's one delivery slot (see __dlGesture above) ──
    // Checked HERE, before a channel is chosen, because the browser's limit is
    // on the delivery and not on the channel: a second stash upload or a second
    // share sheet in one interaction is as wrong as a second anchor click, just
    // slower to fail.
    var _slot=filename+'|'+(blob?blob.size:0);
    if(!window.__dlGesture.claim(_slot)){
        if(window.__dlGesture.spentKey()===_slot){
            // The SAME file twice in one interaction: a caller firing twice, not
            // a file the user is missing. Nothing to hold, nothing to say.
            _ev('dup_same_gesture');
            return Promise.resolve(false);
        }
        window.__dlHold.add(blob,filename);
        _ev('held_gesture',{queued:window.__dlHold.count()});
        return Promise.resolve(false);
    }
    // ── Rescue net, BEFORE a channel is chosen ────────────────────────────
    // Persist the finished export first, so that NOTHING downstream — a failed
    // upload, a refusal for size, a refresh, or the delivery-navigation death
    // (lab-proven: the top-level GET to /api/download/{token} failing on a flap
    // replaces the tool page with the browser error page) — can destroy work
    // that is already done. Dropped 3 s after a confirmed delivery.
    //
    // ⚠️ This used to live INSIDE the relay, AFTER its size refusal. That is
    // exactly inverted: the exports too big for the relay are the ones whose
    // loss costs hours, and they were the only ones never kept. Yuri's 406 MB /
    // 3 h 18 min export died on that line. Preservation is not a property of a
    // lane; it belongs to the door.
    //
    // …and, in EVERY browser, the result of a run that cost work (16.10.2026).
    // Support mail 16.10 (/enhance-video, guest, Android 15, Yandex Browser, 2 GB):
    // a 6-minute video, the run finished, the daily unit was spent, the auto-
    // download fired — and the page was gone before it sent another event; the
    // next load was an empty tool. The 213 MB result lay in OPFS for 4.5 h (the
    // door pins a handed-over OPFS file for 30 min only) and the janitor removed it
    // as a dead page's temp file. Two tools kept their result themselves
    // (/repair-video, /podcast-editor); every other one relied on the page living
    // past the delivery. The run bracket knows how long the work took, so the door
    // decides once for all of them (_costlyRun).
    // Measured 16.10 (Chromium, OPFS-backed 200 MB result shaped as the pipeline
    // hands it over): the keep is a COPY — 0.34 s, usage 200 → 400 MB — and it reads
    // back byte-identical after the OPFS source is removed AND the renderer crashed.
    // opts.keep===false — a result that is a VIEW of the person's own file (byte ranges of the
    // source plus a few KB of header: /repair-video's camera index and reconstruction above
    // FFmpeg's heap, 30.10.2026). WebKit writes every component of such a Blob whole (above):
    // the copy of a 3.7 GB GoPro chapter is 3.7 GB written next to the download of the same
    // bytes, while redoing the work takes seconds. Chromium stores the slices as slices — the
    // copy would be free there, and useless all the same.
    if(!(opts&&opts.keep===false)&&(
       (window.__downloadNeedsStash&&window.__downloadNeedsStash())||
       (window.__iosSavesViaShareSheet&&window.__iosSavesViaShareSheet())||
       (window.__isTelegramWebview&&window.__isTelegramWebview())||
       (src!=='export_restore'&&_costlyRun()))){
        try{if(window.__exportKeepSave){window.__exportKeepSave(blob,filename);_kept=true;}}catch(e){}
    }
    // Stash pipeline, shared by the Android-WebView path (primary) and the
    // iOS-WebView path (fallback when the share sheet is unavailable).
    var _stash=function(){
        // Over the relay's cap — the server would 413 after the whole upload, so
        // refuse before spending the user's uplink. MUST equal
        // DownloadStashController::MAX_BYTES; DownloadStashLimitsAgreeTest holds
        // the two together (they disagreed for months, and the JS number was the
        // one people read).
        //
        // Reaching here at all now means the disk lane was unavailable or
        // declined, so this really is the end of the road: the classic anchor is
        // a known no-op in this class, and faking success is what we spent July
        // removing.
        if(blob.size>STASH_MAX_BYTES){window.__dlGesture.release();_ev('classic_oversize');_failNotice();return Promise.resolve(false);}
        // The same file already on its way: a second press joins that upload.
        // Starting another one split a slow uplink between two copies of the
        // same bytes, and both died (support chat 2026-09-25: two and three
        // parallel uploads of one 8.7 MB export in a row).
        var _flyKey=(opts&&typeof opts.srcUrl==='string'&&opts.srcUrl.indexOf('blob:')===0)?opts.srcUrl:blob;
        if(_stashInflight&&_stashInflight.has(_flyKey)){
            _ev('stash_join');
            try{
                if(window.__toast){
                    var JL=(window.__LOCALE||'en').substring(0,2);
                    var JM={ru:'Этот файл уже отправляется для сохранения — дождитесь конца, нажимать ещё раз не нужно.',
                            en:'This file is already being sent for saving — wait for it to finish, no need to press again.',
                            de:'Diese Datei wird bereits zum Speichern übertragen — bitte warten, erneutes Tippen ist nicht nötig.',
                            es:'Este archivo ya se está enviando para guardarlo; espera a que termine, no hace falta volver a pulsar.',
                            fr:'Ce fichier est déjà en cours d’envoi pour l’enregistrement — patientez, inutile d’appuyer à nouveau.',
                            pt:'Este arquivo já está sendo enviado para salvar — aguarde terminar, não precisa tocar de novo.',
                            id:'File ini sedang dikirim untuk disimpan — tunggu sampai selesai, tidak perlu menekan lagi.',
                            ko:'이 파일은 이미 저장을 위해 전송 중입니다 — 끝날 때까지 기다리세요. 다시 누를 필요가 없습니다.',
                            ar:'يجري إرسال هذا الملف للحفظ بالفعل — انتظر حتى ينتهي، لا داعي للضغط مرة أخرى.',
                            uk:'Цей файл уже надсилається для збереження — дочекайтеся завершення, натискати ще раз не потрібно.',
                            tr:'Bu dosya kaydetmek için zaten gönderiliyor — bitmesini bekleyin, tekrar basmanıza gerek yok.'};
                    window.__toast(JM[JL]||JM.en,'info');
                }
            }catch(e){}
            return _stashInflight.get(_flyKey);
        }
        // Big uploads on a mobile uplink take minutes and used to run in TOTAL
        // SILENCE — the user assumes a hang (#443's "минуты тишины", and Лаура
        // 2026-07-21 retried 5× while each 21 MB upload was still in flight or
        // freshly dead). One up-front toast explains the pause honestly.
        if(blob.size>3145728){
            try{
                if(window.__toast){
                    var UL=(window.__LOCALE||'en').substring(0,2);
                    var _mb=Math.round(blob.size/1048576);
                    // ⚠️ Two phrasings, because the cap moved. "A minute or
                    // two" was written when nothing over 200 MB could get here;
                    // at 400–1000 MB it is simply false, and an understated
                    // wait is how a working transfer gets cancelled by hand.
                    // The big-file line also names what this actually costs the
                    // person — the relay sends the file up and pulls it back
                    // down, so on mobile they pay about twice its size.
                    var _big=blob.size>104857600;
                    // A third phrasing since the cap is 4 GB: from half a
                    // gigabyte «several minutes» is false on a phone uplink
                    // (1.2 GB ≈ 26 min at the 0.8 MB/s measured in #1816), and
                    // the pill now carries the measured remaining time.
                    var _huge=blob.size>536870912;
                    var UM=_huge?{
                        ru:'Файл ('+_mb+' МБ) отправляется для сохранения — на мобильном интернете это может занять полчаса и дольше и потратит около '+(_mb*2)+' МБ трафика. Сколько осталось — на полосе загрузки. Не закрывайте страницу.',
                        en:'Sending the file ('+_mb+' MB) for saving — on mobile data this can take half an hour or more and uses about '+(_mb*2)+' MB of data. The time left is on the progress bar. Keep this page open.',
                        de:'Datei ('+_mb+' MB) wird zum Speichern übertragen — über Mobilfunk kann das eine halbe Stunde oder länger dauern und verbraucht rund '+(_mb*2)+' MB Daten. Die Restzeit steht in der Fortschrittsanzeige. Seite offen lassen.',
                        es:'Enviando el archivo ('+_mb+' MB) para guardarlo: con datos móviles puede tardar media hora o más y usará unos '+(_mb*2)+' MB de datos. El tiempo restante aparece en la barra de progreso. No cierres la página.',
                        fr:'Envoi du fichier ('+_mb+' Mo) pour l’enregistrement — sur réseau mobile cela peut prendre une demi-heure ou plus et consommer environ '+(_mb*2)+' Mo de données. Le temps restant s’affiche sur la barre de progression. Gardez la page ouverte.',
                        pt:'Enviando o arquivo ('+_mb+' MB) para salvar — em dados móveis pode levar meia hora ou mais e usa cerca de '+(_mb*2)+' MB de dados. O tempo restante aparece na barra de progresso. Mantenha a página aberta.',
                        id:'Mengirim file ('+_mb+' MB) untuk disimpan — dengan data seluler bisa memakan setengah jam atau lebih dan memakai sekitar '+(_mb*2)+' MB kuota. Sisa waktunya tampil di bilah progres. Jangan tutup halaman ini.',
                        ko:'파일('+_mb+'MB)을 저장을 위해 전송 중 — 모바일 데이터에서는 30분 이상 걸릴 수 있고 데이터를 약 '+(_mb*2)+'MB 사용합니다. 남은 시간은 진행 표시줄에 나옵니다. 페이지를 닫지 마세요.',
                        ar:'جارٍ إرسال الملف ('+_mb+' م.ب) للحفظ — عبر بيانات الجوال قد يستغرق نصف ساعة أو أكثر ويستهلك نحو '+(_mb*2)+' م.ب من البيانات. الوقت المتبقي يظهر على شريط التقدم. لا تغلق الصفحة.',
                        uk:'Файл ('+_mb+' МБ) надсилається для збереження — у мобільній мережі це може зайняти пів години й довше і витратить близько '+(_mb*2)+' МБ трафіку. Скільки лишилося — на смузі завантаження. Не закривайте сторінку.',
                        tr:'Dosya ('+_mb+' MB) kaydetmek için gönderiliyor — mobil veride bu yarım saat veya daha uzun sürebilir ve yaklaşık '+(_mb*2)+' MB veri kullanır. Kalan süre ilerleme çubuğunda görünür. Bu sayfayı açık tutun.'
                    }:_big?{
                        ru:'Файл ('+_mb+' МБ) отправляется для сохранения — это займёт несколько минут и потратит около '+(_mb*2)+' МБ мобильного трафика. Не закрывайте страницу.',
                        en:'Sending the file ('+_mb+' MB) for saving — this takes several minutes and uses about '+(_mb*2)+' MB of mobile data. Keep this page open.',
                        de:'Datei ('+_mb+' MB) wird zum Speichern übertragen — das dauert mehrere Minuten und verbraucht rund '+(_mb*2)+' MB mobile Daten. Seite offen lassen.',
                        es:'Enviando el archivo ('+_mb+' MB) para guardarlo: tardará varios minutos y usará unos '+(_mb*2)+' MB de datos móviles. No cierres la página.',
                        fr:'Envoi du fichier ('+_mb+' Mo) pour l’enregistrement — cela prendra plusieurs minutes et consommera environ '+(_mb*2)+' Mo de données mobiles. Gardez la page ouverte.',
                        pt:'Enviando o arquivo ('+_mb+' MB) para salvar — leva vários minutos e usa cerca de '+(_mb*2)+' MB de dados móveis. Mantenha a página aberta.',
                        id:'Mengirim file ('+_mb+' MB) untuk disimpan — perlu beberapa menit dan memakai sekitar '+(_mb*2)+' MB kuota. Jangan tutup halaman ini.',
                        ko:'파일('+_mb+'MB)을 저장을 위해 전송 중 — 몇 분이 걸리고 모바일 데이터를 약 '+(_mb*2)+'MB 사용합니다. 페이지를 닫지 마세요.',
                        ar:'جارٍ إرسال الملف ('+_mb+' م.ب) للحفظ — سيستغرق عدة دقائق ويستهلك نحو '+(_mb*2)+' م.ب من بيانات الجوال. لا تغلق الصفحة.',
                        uk:'Файл ('+_mb+' МБ) надсилається для збереження — це займе кілька хвилин і витратить близько '+(_mb*2)+' МБ мобільного трафіку. Не закривайте сторінку.',
                        tr:'Dosya ('+_mb+' MB) kaydetmek için gönderiliyor — bu birkaç dakika sürer ve yaklaşık '+(_mb*2)+' MB mobil veri kullanır. Bu sayfayı açık tutun.'
                    }:{
                        ru:'Файл ('+_mb+' МБ) отправляется для сохранения — на мобильном интернете это может занять минуту-другую…',
                        en:'Sending the file ('+_mb+' MB) for saving — on mobile data this can take a minute or two…',
                        de:'Datei ('+_mb+' MB) wird zum Speichern übertragen — über Mobilfunk kann das ein bis zwei Minuten dauern…',
                        es:'Enviando el archivo ('+_mb+' MB) para guardarlo; con datos móviles puede tardar uno o dos minutos…',
                        fr:'Envoi du fichier ('+_mb+' Mo) pour l’enregistrement — sur réseau mobile cela peut prendre une à deux minutes…',
                        pt:'Enviando o arquivo ('+_mb+' MB) para salvar — em dados móveis pode levar um ou dois minutos…',
                        id:'Mengirim file ('+_mb+' MB) untuk disimpan — dengan data seluler bisa memakan satu-dua menit…',
                        ko:'파일('+_mb+'MB)을 저장을 위해 전송 중 — 모바일 데이터에서는 1~2분 걸릴 수 있습니다…',
                        // ⚠️ ar and uk were BROKEN here: the Arabic string was
                        // cut after "(" and the Ukrainian slot carried Arabic
                        // text. Both locales showed a truncated sentence.
                        ar:'جارٍ إرسال الملف ('+_mb+' م.ب) للحفظ — عبر بيانات الجوال قد يستغرق دقيقة أو دقيقتين…',
                        uk:'Файл ('+_mb+' МБ) надсилається для збереження — у мобільній мережі це може зайняти хвилину-другу…',
                        tr:'Dosya ('+_mb+' MB) kaydetmek için gönderiliyor — mobil veride bu bir iki dakika sürebilir…'
                    };
                    window.__toast(UM[UL]||UM.en,'info');
                }
            }catch(e){}
        }
        var _upSizeMB=Math.round(blob.size/1048576);
        var _L=(window.__LOCALE||'en').substring(0,2);
        var _PT={ru:['Отправка файла','Повтор через','с…','Проверка ссылки…'],
                 en:['Uploading file','Retrying in','s…','Checking link…'],
                 de:['Datei wird gesendet','Neuer Versuch in','s…','Link wird geprüft…'],
                 es:['Enviando archivo','Reintento en','s…','Comprobando enlace…'],
                 fr:['Envoi du fichier','Nouvel essai dans','s…','Vérification du lien…'],
                 pt:['Enviando arquivo','Nova tentativa em','s…','Verificando link…'],
                 id:['Mengunggah file','Coba lagi dalam','dtk…','Memeriksa tautan…'],
                 ko:['파일 전송 중','재시도까지','초…','링크 확인 중…'],
                 ar:['جارٍ إرسال الملف','إعادة المحاولة بعد','ث…','جارٍ فحص الرابط…'],uk:['Надсилання файлу','Повтор через','с…','Перевірка посилання…'],
                 tr:['Dosya yükleniyor','Yeniden deneme:','sn…','Bağlantı kontrol ediliyor…']};
        var _pt=_PT[_L]||_PT.en;
        // «≈ N min» on the pill once the transfer has a measured pace. Since
        // the relay carries up to 4 GB, a send is 20–80 minutes on a phone
        // uplink (comment #1816: 966 MB took 21 min at ~0.8 MB/s), and a bare
        // percentage that moves one point a quarter-minute reads as a hang.
        // The estimator is the shared one (__acETA, rate from real advances,
        // a plateau only counts down); it is not on every page, so a big
        // transfer loads it — versioned, like the other scripts the core adds.
        var _ETA_MIN={ru:'мин',en:'min',de:'Min.',es:'min',fr:'min',pt:'min',id:'mnt',ko:'분',ar:'د',uk:'хв',tr:'dk'};
        var _etaKey=null,_etaOn=false,_etaBytes=0;
        if(blob.size>104857600){
            _etaKey='dl-relay-'+Date.now()+'-'+Math.round(Math.random()*1e6);
            if(!window.__acETA&&!window.__acETALoading){
                window.__acETALoading=1;
                try{
                    var _es=document.createElement('script');
                    _es.src=(typeof window.__jsSrc==='function')?window.__jsSrc('/js/auto-captions-eta.js'):'/js/auto-captions-eta.js';
                    _es.onerror=function(){window.__acETALoading=0;};
                    document.head.appendChild(_es);
                }catch(e){window.__acETALoading=0;}
            }
        }
        var _etaTail=function(bytes){
            if(!_etaKey||!window.__acETA)return '';
            try{
                if(bytes>_etaBytes)_etaBytes=bytes;
                if(!_etaOn){window.__acETA.beginStage(_etaKey);_etaOn=true;}
                window.__acETA.updateRate(_etaKey,_etaBytes/blob.size);
                var sec=window.__acETA.remainingSec(_etaKey);
                if(sec&&sec>=60)return ' · ≈ '+Math.round(sec/60)+' '+(_ETA_MIN[_L]||_ETA_MIN.en);
            }catch(e){}
            return '';
        };
        var _progOn=blob.size>3145728;
        // The pill is for waits a person notices, and on a slow uplink a SMALL
        // file is one of them too (2 MB at 20 KB/s is 100 s of nothing).
        var _upT0=Date.now();
        var _progShown=function(){return _progOn||(Date.now()-_upT0)>2500;};
        // What the person sees only ever goes UP. The number the browser
        // reports can fall back (it counts bytes handed to its own socket, and
        // it restarts from zero when the browser re-sends a request) — showing
        // that fall is how «загрузка сбрасывается в начало» looked from outside.
        var _shownPct=0;
        var _showProg=function(bytes){
            if(!_progShown())return;
            var p=Math.min(99,Math.floor(bytes/Math.max(1,blob.size)*100));
            if(p<_shownPct)p=_shownPct;
            _shownPct=p;
            window.__upProg.set(_pt[0]+' '+_upSizeMB+' MB · '+p+'%'+_etaTail(bytes));
        };
        // Wait `ms` OR until connectivity returns — whichever is sooner. The
        // 'online' race is what turns Лев's class (17 blind fails during a
        // 6-minute outage) into one successful attempt the moment the radio is
        // back. Counts down in the pill so the wait is honest, not a hang.
        var _waitOnline=function(ms){
            return new Promise(function(res){
                var t0=Date.now(),iv=null,done=false;
                var fin=function(){if(done)return;done=true;
                    try{window.removeEventListener('online',fin);}catch(e){}
                    if(iv)clearInterval(iv);res();};
                try{window.addEventListener('online',fin);}catch(e){}
                if(_progShown())iv=setInterval(function(){
                    var left=Math.ceil((ms-(Date.now()-t0))/1000);
                    if(left>0)window.__upProg.set(_pt[1]+' '+left+' '+_pt[2]);
                },500);
                setTimeout(fin,ms);
            });
        };
        // XHR instead of fetch for the upload leg: fetch cannot observe upload
        // progress, so a stalled link is indistinguishable from a slow one —
        // prod shows 499s with rt up to 330 s (users holding a dead POST for
        // minutes). upload.onprogress + a 20 s no-advance watchdog turns the
        // silent limbo into an abortable, retryable failure.
        var _postX=function(){
            return new Promise(function(res,rej){
                var meta=document.querySelector('meta[name="csrf-token"]');
                var url=(typeof window.__stashEndpointOverride==='string'&&window.__stashEndpointOverride)||'/api/download-stash';
                var xhr;
                try{xhr=new XMLHttpRequest();xhr.open('POST',url,true);}
                catch(e){rej({kind:'net',msg:'xhr-open'});return;}
                try{xhr.setRequestHeader('X-CSRF-TOKEN',meta?meta.content:'');}catch(e){}
                xhr.responseType='json';
                // Stall = no MEANINGFUL advance (≥20 KB) in 20 s — a plain
                // "any progress" check is defeated by a 100 B/s trickle, which
                // keeps ticking while the upload is hopeless (lab-measured).
                var lastAdv=Date.now(),lastLoaded=0,watchdog=null,settled=false;
                var fin=function(fn,v){if(settled)return;settled=true;
                    if(watchdog)clearInterval(watchdog);fn(v);};
                watchdog=setInterval(function(){
                    if(Date.now()-lastAdv>20000){
                        try{xhr.abort();}catch(e){}
                        fin(rej,{kind:'stall',msg:'upload stalled >20s'});
                    }
                },5000);
                xhr.upload.onprogress=function(ev){
                    // A FALL is the browser re-sending the whole request on a
                    // fresh connection (HTTP/2 PING failure, see the relay
                    // notes) — activity, not a stall.
                    if(ev.loaded<lastLoaded){lastLoaded=ev.loaded;lastAdv=Date.now();}
                    else if(ev.loaded-lastLoaded>=20480){lastLoaded=ev.loaded;lastAdv=Date.now();}
                    _showProg(ev.loaded);
                };
                xhr.onerror=function(){fin(rej,{kind:'net',msg:'network error'});};
                xhr.onabort=function(){fin(rej,{kind:'stall',msg:'aborted'});};
                xhr.ontimeout=function(){fin(rej,{kind:'net',msg:'timeout'});};
                xhr.onload=function(){
                    var st=xhr.status,j=xhr.response;
                    if(st>=200&&st<300&&j&&j.url)fin(res,j);
                    else if(st>=200&&st<300)fin(rej,{kind:'http',msg:'stash no-url'});
                    else fin(rej,{kind:'http',msg:'stash '+st,status:st});
                };
                var fd=new FormData();
                fd.append('file',blob,filename);
                fd.append('filename',filename);
                try{xhr.send(fd);}catch(e){fin(rej,{kind:'net',msg:'send threw'});}
            });
        };
        // Pre-flight the delivery URL before the top-level navigation. The lab
        // reproduced the roulette deterministically: POST lands, the network
        // flaps, the GET navigation dies → chrome-error page, tool + result
        // gone. A HEAD probe (token is NOT consumed; 15-min TTL owns cleanup)
        // keeps the page alive on failure and retries once the link is back.
        var _probe=function(url){
            return new Promise(function(res,rej){
                var xhr;
                try{xhr=new XMLHttpRequest();xhr.open('HEAD',url,true);}
                catch(e){rej({kind:'net',msg:'probe-open'});return;}
                xhr.timeout=10000;
                xhr.onload=function(){
                    if(xhr.status>=200&&xhr.status<300)res(true);
                    else rej({kind:'http',msg:'probe '+xhr.status,status:xhr.status});
                };
                xhr.onerror=function(){rej({kind:'net',msg:'probe net'});};
                xhr.ontimeout=function(){rej({kind:'net',msg:'probe timeout'});};
                try{xhr.send();}catch(e){rej({kind:'net',msg:'probe send'});}
            });
        };
        var _go=function(url){
            // Navigate to the attachment URL — through the one door for a navigation
            // that IS a download: the page's «точно уйти?» guards must not cancel it
            // (__downloadNavigation above; before 30.10.2026 a guard armed by a run
            // of 20 s+ cancelled the navigation, and the file was never requested).
            window.__downloadNavigation(url);
        };
        // ── Did the browser's download manager come for the file? ──────────
        // Only where the navigation is not the download (window.__relayTakeCheck
        // — Huawei Browser and in-app Android WebViews). Before 18.10.2026 the
        // page said «Файл сохранён в папку Download» the moment it navigated,
        // whether or not the browser ever requested the file (support chat 695:
        // five results, five navigations, not one request from the download
        // manager; «Куда скачиваются файлы?»). Now the server answers
        // (/api/download/{token}/taken) and the bar follows the answer. The page
        // itself sees NOTHING of the browser's «Загрузить» window — measured on
        // an AlertDialog over Android WebView 113: no blur, document.hasFocus()
        // stays true — so the server is the only witness, and the ladder below
        // is built on it (docs/why/primitives/relay-take-check.md):
        //   taken — a request beyond the page's own navigations, or any Range
        //           piece → the browser took it (and where it puts files);
        //   15 s of nothing → «Скачать ещё раз» — a navigation carrying the
        //           person's tap (a second try lands 69 % of the time, nginx);
        //   15 s of nothing again → «Скопировать ссылку» — the relay URL,
        //           opened in another browser (or a Huawei Browser tab, out of
        //           its installed-app window, where it failed 10 of 12) is a
        //           plain download the server sees too;
        //   Huawei only → «Сохранить напрямую» — the blob anchor (Huawei 17 has
        //           a blob path; an in-app WebView has none: its host receives a
        //           blob: URL no download manager can fetch).
        // 15 s: by then 91.6 % (Huawei) / 90.7 % (WebView) of all takes have
        // happened (nginx, 14 days); a later take still turns the bar to «taken».
        var TAKE_MISS_MS=15000,TAKE_GIVE_UP_MS=180000,TAKE_LINK_MS=900000;
        var _takeWatch=function(url){
            var kind=(window.__relayTakeCheck&&window.__relayTakeCheck())||'huawei';
            var hw=kind==='huawei';
            var L=(window.__LOCALE||'en').substring(0,2);
            var place=window.__downloadPlace?window.__downloadPlace(blob.type,filename):null;
            var w=place?place.path:'Download';
            var H={
                ru:{ask:'Huawei Browser сохраняет файл сам: в его окне нажмите «Загрузить». Файл будет в «Файлы» → '+w+'.',
                    taken:'Браузер забрал файл — он в «Файлы» → '+w+'.',
                    miss:'Браузер не начал загрузку. Нажмите «Скачать ещё раз», а в окне браузера — «Загрузить». Если он пишет, что загрузка начнётся при подключении к Wi-Fi, включите в его настройках «Загрузка по мобильной сети».',
                    alt:'Если браузер сохранит файл этим способом, он будет в «Файлы» → '+w+'.'},
                en:{ask:'Huawei Browser saves the file itself: tap “Download” in its window. The file goes to Files → '+w+'.',
                    taken:'The browser took the file — it’s in Files → '+w+'.',
                    miss:'The browser didn’t start the download. Tap “Download again”, then “Download” in the browser’s window. If it says the download will start on Wi-Fi, turn on “Download with mobile data” in its settings.',
                    alt:'If the browser saves the file this way, it will be in Files → '+w+'.'},
                de:{ask:'Huawei Browser speichert die Datei selbst: Tippen Sie in seinem Fenster auf „Herunterladen“. Die Datei landet in Dateien → '+w+'.',
                    taken:'Der Browser hat die Datei übernommen — sie liegt in Dateien → '+w+'.',
                    miss:'Der Browser hat den Download nicht gestartet. Tippen Sie auf „Erneut herunterladen“ und im Browserfenster auf „Herunterladen“. Meldet er, dass der Download bei WLAN beginnt, schalten Sie in seinen Einstellungen „Über mobile Daten herunterladen“ ein.',
                    alt:'Speichert der Browser die Datei auf diesem Weg, liegt sie in Dateien → '+w+'.'},
                es:{ask:'Huawei Browser guarda el archivo por su cuenta: en su ventana toca «Descargar». El archivo quedará en Archivos → '+w+'.',
                    taken:'El navegador tomó el archivo: está en Archivos → '+w+'.',
                    miss:'El navegador no inició la descarga. Toca «Descargar de nuevo» y, en la ventana del navegador, «Descargar». Si dice que la descarga empezará con Wi-Fi, activa «Descargar con datos móviles» en su configuración.',
                    alt:'Si el navegador guarda el archivo de esta forma, estará en Archivos → '+w+'.'},
                fr:{ask:'Huawei Browser enregistre le fichier lui-même : dans sa fenêtre, touchez « Télécharger ». Le fichier ira dans Fichiers → '+w+'.',
                    taken:'Le navigateur a pris le fichier : il est dans Fichiers → '+w+'.',
                    miss:'Le navigateur n’a pas lancé le téléchargement. Touchez « Télécharger à nouveau », puis « Télécharger » dans la fenêtre du navigateur. S’il indique que le téléchargement démarrera en Wi-Fi, activez « Télécharger via données mobiles » dans ses paramètres.',
                    alt:'Si le navigateur enregistre le fichier de cette façon, il sera dans Fichiers → '+w+'.'},
                pt:{ask:'O Huawei Browser salva o arquivo sozinho: na janela dele, toque em “Baixar”. O arquivo vai para Arquivos → '+w+'.',
                    taken:'O navegador pegou o arquivo — ele está em Arquivos → '+w+'.',
                    miss:'O navegador não iniciou o download. Toque em “Baixar de novo” e, na janela do navegador, em “Baixar”. Se ele disser que o download começará no Wi-Fi, ative “Baixar usando dados móveis” nas configurações dele.',
                    alt:'Se o navegador salvar o arquivo desse jeito, ele estará em Arquivos → '+w+'.'},
                id:{ask:'Huawei Browser menyimpan file sendiri: di jendelanya ketuk “Unduh”. File akan ada di File → '+w+'.',
                    taken:'Browser sudah mengambil file — ada di File → '+w+'.',
                    miss:'Browser tidak memulai unduhan. Ketuk “Unduh lagi”, lalu “Unduh” di jendela browser. Jika tertulis unduhan akan dimulai saat tersambung Wi-Fi, aktifkan “Unduh dengan data seluler” di pengaturannya.',
                    alt:'Jika browser menyimpan file dengan cara ini, file akan ada di File → '+w+'.'},
                ko:{ask:'Huawei Browser가 파일을 직접 저장합니다. 브라우저 창에서 “다운로드”를 누르세요. 파일은 파일 → '+w+'에 저장됩니다.',
                    taken:'브라우저가 파일을 받았습니다. 파일 → '+w+'에 있습니다.',
                    miss:'브라우저가 다운로드를 시작하지 않았습니다. “다시 다운로드”를 누른 뒤 브라우저 창에서 “다운로드”를 누르세요. Wi-Fi에 연결되면 다운로드가 시작된다고 나오면 설정에서 “모바일 데이터로 다운로드”를 켜세요.',
                    alt:'브라우저가 이 방법으로 파일을 저장하면 파일 → '+w+'에 있습니다.'},
                ar:{ask:'يحفظ Huawei Browser الملف بنفسه: اضغط «تنزيل» في نافذته. سيكون الملف في الملفات ← '+w+'.',
                    taken:'استلم المتصفح الملف — إنه في الملفات ← '+w+'.',
                    miss:'لم يبدأ المتصفح التنزيل. اضغط «نزّل مرة أخرى» ثم «تنزيل» في نافذة المتصفح. إذا ظهر أن التنزيل سيبدأ عند الاتصال بشبكة Wi-Fi، فشغّل «التنزيل باستخدام بيانات الجوال» في إعداداته.',
                    alt:'إذا حفظ المتصفح الملف بهذه الطريقة، فسيكون في الملفات ← '+w+'.'},
                uk:{ask:'Huawei Browser зберігає файл сам: у його вікні натисніть «Завантаження». Файл буде у «Файли» → '+w+'.',
                    taken:'Браузер забрав файл — він у «Файли» → '+w+'.',
                    miss:'Браузер не почав завантаження. Натисніть «Завантажити ще раз», а у вікні браузера — «Завантаження». Якщо він пише, що завантаження почнеться після підключення до Wi-Fi, увімкніть у його налаштуваннях «Завантаження через мобільну мережу».',
                    alt:'Якщо браузер збереже файл цим способом, він буде у «Файли» → '+w+'.'},
                tr:{ask:'Huawei Browser dosyayı kendisi kaydeder: penceresinde “İndir”e dokunun. Dosya Dosyalar → '+w+' konumuna gider.',
                    taken:'Tarayıcı dosyayı aldı — Dosyalar → '+w+' konumunda.',
                    miss:'Tarayıcı indirmeyi başlatmadı. “Tekrar indir”e, ardından tarayıcı penceresinde “İndir”e dokunun. İndirmenin Wi-Fi’a bağlanınca başlayacağını söylüyorsa ayarlarından “Mobil veri ile indir”i açın.',
                    alt:'Tarayıcı dosyayı bu yolla kaydederse Dosyalar → '+w+' konumunda olur.'}};
            var V={
                ru:{ask:'Файл передан браузеру этого приложения — загрузка появится в уведомлениях.',
                    taken:'Загрузка началась — файл будет в папке Download (смотрите уведомления).',
                    miss:'Браузер этого приложения не начал загрузку. Нажмите «Скачать ещё раз».'},
                en:{ask:'The file has been passed to this app’s browser — the download will show up in your notifications.',
                    taken:'The download has started — the file will be in the Download folder (see your notifications).',
                    miss:'This app’s browser didn’t start the download. Tap “Download again”.'},
                de:{ask:'Die Datei wurde an den Browser dieser App übergeben — der Download erscheint in den Benachrichtigungen.',
                    taken:'Der Download hat begonnen — die Datei landet im Ordner „Download“ (siehe Benachrichtigungen).',
                    miss:'Der Browser dieser App hat den Download nicht gestartet. Tippen Sie auf „Erneut herunterladen“.'},
                es:{ask:'El archivo se pasó al navegador de esta app; la descarga aparecerá en tus notificaciones.',
                    taken:'La descarga comenzó: el archivo estará en la carpeta Download (mira tus notificaciones).',
                    miss:'El navegador de esta app no inició la descarga. Toca «Descargar de nuevo».'},
                fr:{ask:'Le fichier a été transmis au navigateur de cette appli — le téléchargement apparaîtra dans vos notifications.',
                    taken:'Le téléchargement a commencé — le fichier sera dans le dossier Download (voir vos notifications).',
                    miss:'Le navigateur de cette appli n’a pas lancé le téléchargement. Touchez « Télécharger à nouveau ».'},
                pt:{ask:'O arquivo foi passado ao navegador deste app — o download vai aparecer nas notificações.',
                    taken:'O download começou — o arquivo ficará na pasta Download (veja as notificações).',
                    miss:'O navegador deste app não iniciou o download. Toque em “Baixar de novo”.'},
                id:{ask:'File sudah diteruskan ke browser aplikasi ini — unduhan akan muncul di notifikasi.',
                    taken:'Unduhan dimulai — file akan ada di folder Download (lihat notifikasi).',
                    miss:'Browser aplikasi ini tidak memulai unduhan. Ketuk “Unduh lagi”.'},
                ko:{ask:'파일을 이 앱의 브라우저로 넘겼습니다. 다운로드는 알림에 표시됩니다.',
                    taken:'다운로드가 시작되었습니다. 파일은 Download 폴더에 저장됩니다(알림 확인).',
                    miss:'이 앱의 브라우저가 다운로드를 시작하지 않았습니다. “다시 다운로드”를 누르세요.'},
                ar:{ask:'تم تسليم الملف إلى متصفح هذا التطبيق — سيظهر التنزيل في الإشعارات.',
                    taken:'بدأ التنزيل — سيكون الملف في مجلد Download (راجع الإشعارات).',
                    miss:'لم يبدأ متصفح هذا التطبيق التنزيل. اضغط «نزّل مرة أخرى».'},
                uk:{ask:'Файл передано браузеру цього застосунку — завантаження з’явиться в сповіщеннях.',
                    taken:'Завантаження почалося — файл буде в папці Download (дивіться сповіщення).',
                    miss:'Браузер цього застосунку не почав завантаження. Натисніть «Завантажити ще раз».'},
                tr:{ask:'Dosya bu uygulamanın tarayıcısına aktarıldı — indirme bildirimlerde görünecek.',
                    taken:'İndirme başladı — dosya Download klasöründe olacak (bildirimlere bakın).',
                    miss:'Bu uygulamanın tarayıcısı indirmeyi başlatmadı. “Tekrar indir”e dokunun.'}};
            var C={
                ru:{miss2:'Браузер так и не забрал файл. Нажмите «Скопировать ссылку» и откройте её в другом браузере — Chrome или Яндекс Браузере'+(hw?' либо в обычной вкладке Huawei Browser':'')+': файл скачается оттуда. Ссылка действует час.',
                    copied:'Ссылка скопирована. Вставьте её в адресную строку браузера — файл скачается.'+(hw?' Не выходит — нажмите «Сохранить напрямую».':''),
                    copyFail:'Скопировать не удалось. Ссылка на файл (действует час): ',
                    takenLink:'Ссылку открыли — файл скачивается.',
                    again:'Скачать ещё раз',copy:'Скопировать ссылку',other:'Сохранить напрямую'},
                en:{miss2:'The browser still didn’t take the file. Tap “Copy link” and open it in another browser — Chrome or any other'+(hw?', or a regular Huawei Browser tab':'')+': the file downloads from there. The link works for an hour.',
                    copied:'Link copied. Paste it into a browser’s address bar — the file will download.'+(hw?' Doesn’t work? Tap “Save directly”.':''),
                    copyFail:'Couldn’t copy. Link to the file (works for an hour): ',
                    takenLink:'The link was opened — the file is downloading.',
                    again:'Download again',copy:'Copy link',other:'Save directly'},
                de:{miss2:'Der Browser hat die Datei immer noch nicht übernommen. Tippen Sie auf „Link kopieren“ und öffnen Sie ihn in einem anderen Browser — Chrome oder einem anderen'+(hw?', auch in einem normalen Tab des Huawei Browser':'')+': Dort wird die Datei heruntergeladen. Der Link gilt eine Stunde.',
                    copied:'Link kopiert. Fügen Sie ihn in die Adresszeile eines Browsers ein — die Datei wird heruntergeladen.'+(hw?' Klappt nicht? Tippen Sie auf „Direkt speichern“.':''),
                    copyFail:'Kopieren nicht möglich. Link zur Datei (eine Stunde gültig): ',
                    takenLink:'Der Link wurde geöffnet — die Datei wird heruntergeladen.',
                    again:'Erneut herunterladen',copy:'Link kopieren',other:'Direkt speichern'},
                es:{miss2:'El navegador sigue sin tomar el archivo. Toca «Copiar enlace» y ábrelo en otro navegador (Chrome u otro'+(hw?', o una pestaña normal de Huawei Browser':'')+'): el archivo se descargará desde ahí. El enlace funciona durante una hora.',
                    copied:'Enlace copiado. Pégalo en la barra de direcciones de un navegador y el archivo se descargará.'+(hw?' ¿No funciona? Toca «Guardar directamente».':''),
                    copyFail:'No se pudo copiar. Enlace al archivo (funciona una hora): ',
                    takenLink:'Se abrió el enlace: el archivo se está descargando.',
                    again:'Descargar de nuevo',copy:'Copiar enlace',other:'Guardar directamente'},
                fr:{miss2:'Le navigateur n’a toujours pas pris le fichier. Touchez « Copier le lien » et ouvrez-le dans un autre navigateur — Chrome ou un autre'+(hw?', ou un onglet normal de Huawei Browser':'')+' : le fichier s’y téléchargera. Le lien reste valable une heure.',
                    copied:'Lien copié. Collez-le dans la barre d’adresse d’un navigateur — le fichier se téléchargera.'+(hw?' Ça ne marche pas ? Touchez « Enregistrer directement ».':''),
                    copyFail:'Copie impossible. Lien vers le fichier (valable une heure) : ',
                    takenLink:'Le lien a été ouvert — le fichier se télécharge.',
                    again:'Télécharger à nouveau',copy:'Copier le lien',other:'Enregistrer directement'},
                pt:{miss2:'O navegador ainda não pegou o arquivo. Toque em “Copiar link” e abra-o em outro navegador — Chrome ou outro'+(hw?', ou uma aba normal do Huawei Browser':'')+': o arquivo será baixado por lá. O link vale por uma hora.',
                    copied:'Link copiado. Cole na barra de endereço de um navegador — o arquivo será baixado.'+(hw?' Não deu certo? Toque em “Salvar direto”.':''),
                    copyFail:'Não foi possível copiar. Link do arquivo (vale por uma hora): ',
                    takenLink:'O link foi aberto — o arquivo está sendo baixado.',
                    again:'Baixar de novo',copy:'Copiar link',other:'Salvar direto'},
                id:{miss2:'Browser tetap tidak mengambil file. Ketuk “Salin tautan” lalu buka di browser lain — Chrome atau lainnya'+(hw?', atau tab biasa Huawei Browser':'')+': file akan terunduh dari sana. Tautan berlaku satu jam.',
                    copied:'Tautan disalin. Tempel di bilah alamat browser — file akan terunduh.'+(hw?' Tidak berhasil? Ketuk “Simpan langsung”.':''),
                    copyFail:'Gagal menyalin. Tautan file (berlaku satu jam): ',
                    takenLink:'Tautan sudah dibuka — file sedang diunduh.',
                    again:'Unduh lagi',copy:'Salin tautan',other:'Simpan langsung'},
                ko:{miss2:'브라우저가 여전히 파일을 받지 않았습니다. “링크 복사”를 누르고 다른 브라우저(Chrome 등'+(hw?' 또는 Huawei Browser의 일반 탭':'')+')에서 여세요. 그곳에서 파일이 다운로드됩니다. 링크는 1시간 동안 유효합니다.',
                    copied:'링크를 복사했습니다. 브라우저 주소창에 붙여 넣으면 파일이 다운로드됩니다.'+(hw?' 안 되나요? “바로 저장”을 누르세요.':''),
                    copyFail:'복사하지 못했습니다. 파일 링크(1시간 유효): ',
                    takenLink:'링크가 열렸습니다. 파일을 다운로드하는 중입니다.',
                    again:'다시 다운로드',copy:'링크 복사',other:'바로 저장'},
                ar:{miss2:'لم يستلم المتصفح الملف بعد. اضغط «انسخ الرابط» وافتحه في متصفح آخر — Chrome أو غيره'+(hw?' أو علامة تبويب عادية في Huawei Browser':'')+': سيُنزَّل الملف من هناك. الرابط صالح لمدة ساعة.',
                    copied:'تم نسخ الرابط. الصقه في شريط العنوان في متصفح — وسيُنزَّل الملف.'+(hw?' لم ينجح؟ اضغط «احفظ مباشرة».':''),
                    copyFail:'تعذّر النسخ. رابط الملف (صالح لمدة ساعة): ',
                    takenLink:'تم فتح الرابط — جارٍ تنزيل الملف.',
                    again:'نزّل مرة أخرى',copy:'انسخ الرابط',other:'احفظ مباشرة'},
                uk:{miss2:'Браузер так і не забрав файл. Натисніть «Скопіювати посилання» й відкрийте його в іншому браузері — Chrome чи іншому'+(hw?' або у звичайній вкладці Huawei Browser':'')+': файл завантажиться звідти. Посилання діє годину.',
                    copied:'Посилання скопійовано. Вставте його в адресний рядок браузера — файл завантажиться.'+(hw?' Не виходить — натисніть «Зберегти напряму».':''),
                    copyFail:'Скопіювати не вдалося. Посилання на файл (діє годину): ',
                    takenLink:'Посилання відкрили — файл завантажується.',
                    again:'Завантажити ще раз',copy:'Скопіювати посилання',other:'Зберегти напряму'},
                tr:{miss2:'Tarayıcı dosyayı hâlâ almadı. “Bağlantıyı kopyala”ya dokunun ve başka bir tarayıcıda açın — Chrome veya başka biri'+(hw?' ya da Huawei Browser’ın normal bir sekmesi':'')+': dosya oradan inecek. Bağlantı bir saat geçerli.',
                    copied:'Bağlantı kopyalandı. Bir tarayıcının adres çubuğuna yapıştırın — dosya inecek.'+(hw?' Olmadı mı? “Doğrudan kaydet”e dokunun.':''),
                    copyFail:'Kopyalanamadı. Dosyanın bağlantısı (bir saat geçerli): ',
                    takenLink:'Bağlantı açıldı — dosya iniyor.',
                    again:'Tekrar indir',copy:'Bağlantıyı kopyala',other:'Doğrudan kaydet'}};
            var t=Object.assign({},C[L]||C.en,(hw?(H[L]||H.en):(V[L]||V.en)));
            // stage: 0 first try, 1 after «Скачать ещё раз», 2 link offered, 3 link copied
            var bar=null,done=false,navs=1,t0=Date.now(),lastNav=t0,missed=0,stage=0,copiedAt=0,timer=null;
            var say=function(msg,type,action){
                try{if(bar&&bar.dismiss)bar.dismiss();}catch(e){}
                try{bar=window.__toast?window.__toast(msg,type,{keep:true,ctx:'relay_take',action:action}):null;}catch(e){bar=null;}
            };
            var tk=function(st,x){
                try{if(window.__ae)window.__ae('feature_use',Object.assign({slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                    f:'relay_take',st:st,kind:kind,stage:stage,ms:Date.now()-t0,navs:navs},x||{}));}catch(e){}
            };
            var again=function(){
                navs++;lastNav=Date.now();stage=1;
                _go(url);
                tk('again');
                say(t.ask,'info');
                schedule();
            };
            var other=function(){
                tk('other');
                _classic('relay_other');
                say(t.alt,'info');
            };
            var copy=function(){
                stage=3;copiedAt=Date.now();
                // Inside the tap: the clipboard needs the person's activation.
                var p=window.__copyText?window.__copyText(url):Promise.resolve(false);
                p.then(function(ok){
                    tk('copied',{ok:ok?1:0});
                    say(ok?t.copied:(t.copyFail+url),ok?'info':'warning',hw?{label:t.other,onclick:other}:undefined);
                    schedule();
                });
            };
            var check=function(){
                timer=null;
                if(done)return;
                var takenUrl=String(url).replace(/[?#].*$/,'').replace(/\/+$/,'')+'/taken';
                fetch(takenUrl,{credentials:'same-origin',cache:'no-store'}).then(function(r){
                    if(r.status===404)return {gone:true};
                    return r.ok?r.json():null;
                }).then(function(j){
                    if(done)return;
                    if(j&&j.gone){done=true;tk('gone');return;}
                    if(j&&((j.ranged|0)>0||(j.gets|0)>navs)){
                        done=true;
                        tk('taken',{gets:j.gets|0,ranged:j.ranged|0});
                        say(stage>=3?t.takenLink:t.taken,'success');
                        _dropWhenSeen('stash');
                        return;
                    }
                    var now=Date.now();
                    if(stage<2&&now-lastNav>=TAKE_MISS_MS&&missed<navs){
                        missed=navs;
                        tk('missed',{gets:j?j.gets|0:-1});
                        if(navs<2)say(t.miss,'warning',{label:t.again,onclick:again});
                        else{stage=2;say(t.miss2,'warning',{label:t.copy,onclick:copy});}
                    }
                    // A copied link is opened in another app: listen as long as a
                    // person plausibly takes to paste it (the link lives an hour).
                    if(stage>=3?(now-copiedAt>=TAKE_LINK_MS):(now-t0>=TAKE_GIVE_UP_MS)){done=true;tk('never',{gets:j?j.gets|0:-1});return;}
                    schedule();
                },function(){if(!done)schedule();});
            };
            var schedule=function(){
                if(done)return;
                if(timer)clearTimeout(timer);
                // Every 3 s while the browser's window is likely open, then 10 s.
                timer=setTimeout(check,(stage<2&&(Date.now()-lastNav)<TAKE_MISS_MS+5000)?3000:10000);
            };
            say(t.ask,'info');
            schedule();
        };
        var _nav=function(url,extra){
            // ug — a «точно уйти?» guard was armed when the file left: before the
            // download door, exactly these navigations were cancelled (the T+7 measure).
            try{if(window.__unsavedWork&&window.__unsavedWork.isArmed())extra=Object.assign({},extra||{},{ug:1});}catch(e){}
            _go(url);
            // `_ev` fires the `file-downloaded` event for us (the anchor has
            // no `download` attribute — the server's Content-Disposition does
            // the work — so the HTMLAnchorElement.click patch stays silent).
            window.__upProg.done();
            var watch=!!(window.__relayTakeCheck&&window.__relayTakeCheck());
            if(watch){
                _hintOwned=true;
                _ev('stash',Object.assign({tw:1},extra||{}));
                _takeWatch(url);
                return true;
            }
            _ev('stash',extra);
            // If the navigation itself dies (residual ms-window after the
            // probe), this timer never runs and the export-keep entry stays
            // for the restore bar. On a normal download the page lives on and
            // the net is cleared.
            try{setTimeout(function(){if(window.__exportKeepDrop)window.__exportKeepDrop();},3000);}catch(e){}
            return true;
        };
        var RETRY_MS=[1500,6000,20000];
        // 419 self-heal: PageCached tool pages carry a FOREIGN session's csrf
        // meta for a fresh anonymous visitor until the layout's async
        // /api/csrf-token refresh lands — a stash POST in that window dies 419
        // (the same class the video-dubbing translate route was exempted for).
        // One token refresh + one replay fixes it without weakening CSRF.
        var csrfHealed=false;
        var _healCsrf=_healCsrfToken;
        // ── The upload itself: slices sized by the link (see the notes above
        // SLICE_TARGET_MS). What each slice does, and why:
        //   • its size is what the previous slice showed the link carries in
        //     SLICE_TARGET_MS, measured END TO END — from sending to the
        //     server's answer. The browser's upload progress is not a clock
        //     for the link: it counts bytes handed to the device's own socket,
        //     and on a phone the last megabyte of a request can sit there for
        //     half a minute after "100 %";
        //   • the server answers with how many bytes it really holds
        //     (`received`), and the next slice starts THERE — so a slice the
        //     client saw fail but the server kept is never sent twice, and a
        //     slice the server never saw is never skipped;
        //   • a stalled or dropped slice is re-sent SMALLER: the link has just
        //     said it carries less than was asked of it;
        //   • a failed upload remembers where it stopped (per Blob), so the
        //     person's next press continues from the server's byte count
        //     instead of from zero — the server keeps the part for 30 min.
        // Still immune, like the old fixed chunks, to post_max_size ceilings
        // and to the prod ~60 s zero-byte body timeout: every slice is its own
        // short request. The 240/min route budget is not at risk either: a
        // slice is ~4 s of link time, and on fast links slices grow to 8 MB.
        // ⚠️ `mime` едет полем: у среза Blob своего типа нет, кусок уходит как
        // application/octet-stream, и с таким типом ретранслятор отдавал файл —
        // Android переименовывал «песня.mp3» в «песня.bin» (#1820, 03.10.2026).
        // Тот же урок у заливки в лейны (fields:{mime} в __uploadBody).
        var _postSlices=function(){
            return _sliceSend({url:'/api/download-stash-chunk',blob:blob,filename:filename,key:_flyKey,
                fields:{mime:blob.type||''},
                resume:_stashResume,onProgress:_showProg,onEvent:_ev,waitOnline:_waitOnline,
                healCsrf:function(){csrfHealed=true;return _healCsrf();},mono:_postX
            }).then(function(r){
                var j=r.json||{};
                if(r.mode==='mono-fallback'){j.mode='mono-fallback';return j;}
                return {token:j.token,url:j.url,mode:r.mode,chunks:r.chunks,resends:r.resends,restarts:r.restarts,
                        secs:r.secs,kbps:r.kbps,maxKB:r.maxKB,resumedKB:r.resumedKB};
            });
        };
        var _fail=function(err,retried){
            window.__upProg.done();
            _ev('stash_fallback',{err:String((err&&err.msg)||'net').slice(0,40),retried:retried||0,
                stalled:err&&err.kind==='stall'?1:0});
            _failNotice();
            return false;
        };
        // Attempt driver: initial try + up to 3 retries for NETWORK/STALL-class
        // failures, each wait short-circuited by the 'online' event. HTTP-status
        // failures are never retried (same request, same answer) — except a
        // probe 404 (token pruned by TTL), which re-uploads once. Every size
        // takes the sliced leg, whose per-slice ladder replaces the outer
        // whole-file retries (a chunkgive is terminal here — it already burned
        // the same ladder on one slice).
        var attempt=function(n){
            return _postSlices().then(function(j){
                var probeRound=function(p){
                    if(_progShown())window.__upProg.set(_pt[3]);
                    return _probe(j.url).then(function(){
                        return _nav(j.url,{mode:j.mode||'mono',chunks:j.chunks,resends:j.resends,
                            restarts:j.restarts,secs:j.secs,kbps:j.kbps,maxKB:j.maxKB,resumedKB:j.resumedKB});
                    }).catch(function(perr){
                            if(perr&&perr.kind==='http'&&perr.status===404&&n<RETRY_MS.length){
                                // token expired while we waited — one fresh upload
                                _ev('stash_retry',{err:'probe 404',n:n+1});
                                return attempt(n+1);
                            }
                            // 502/504 — the front door hiccuping (a deploy), not a
                            // verdict on the token: probe again like a dropped link.
                            if(perr&&perr.kind==='http'&&perr.status!==502&&perr.status!==504)return _fail(perr,n);
                            if(p>=RETRY_MS.length)return _fail(perr,n);
                            _ev('stash_retry',{err:String(perr&&perr.msg||'probe').slice(0,40),probe:1,n:p+1});
                            return _waitOnline(RETRY_MS[p]).then(function(){return probeRound(p+1);});
                        });
                };
                return probeRound(0);
            }).catch(function(err){
                if(err&&err.kind==='http'&&err.status===419&&!csrfHealed){
                    csrfHealed=true;
                    _ev('stash_retry',{err:'csrf heal',n:n});
                    return _healCsrf().then(function(){return attempt(n);})
                        .catch(function(){return _fail(err,n);});
                }
                // 429 = shared per-IP throttle bucket (CGNAT puts many users
                // behind one IP) — a window that clears, not a verdict. Walk
                // the same ladder instead of failing instantly.
                if(err&&err.kind==='http'&&err.status!==429&&err.status!==502&&err.status!==504)return _fail(err,n);
                // the slice ladder's own retries are the ones that were spent
                if(err&&err.kind==='chunkgive')return _fail(err,(err.resends||0)+n);
                if(n>=RETRY_MS.length)return _fail(err,n);
                _ev('stash_retry',{err:String(err&&err.msg||'net').slice(0,40),n:n+1,
                    stalled:err&&err.kind==='stall'?1:0});
                return _waitOnline(RETRY_MS[n]).then(function(){return attempt(n+1);});
            });
        };
        var _run=attempt(0);
        if(_stashInflight){
            _stashInflight.set(_flyKey,_run);
            var _landed=function(){_stashInflight['delete'](_flyKey);};
            _run.then(_landed,_landed);
        }
        return _run;
    };
    // ── iOS in-app WebView: share sheet first ──
    // The blob anchor is a silent no-op here and Content-Disposition needs
    // WKDownloadDelegate in the host app (rare). The native share sheet
    // (Web Share Level 2 with files; WKWebView ships it since iOS 15) offers
    // "Save Video" / "Save to Files" — the one reliable save path. Must run
    // synchronously inside the user's click (transient activation), which
    // holds: tools call __downloadFile directly from their click handlers,
    // and the interceptor path only adds a same-tick blob fetch.
    // canShare({files}) is the runtime gate — where absent (iOS < 15, host
    // restrictions) we degrade to the stash chain.
    // ── Local channel: the Service Worker synthesises the very response the
    // WebView download manager wants (200 + Content-Disposition) straight from
    // the in-memory Blob. ZERO upload, no size cap, works offline, and it
    // reports back when it actually served the bytes — the only *confirmed*
    // delivery signal we have. Proven on a real 82 MB MP3: byte-exact in
    // ~200 ms with 0 network requests (the same file died in a 330 s stash
    // upload for the reporter). Falls through cleanly when no SW controls
    // the page (first visit, hard reload, SW disabled).
    // ⚠️ ROUND 5 (2026-07-18): NOT ROUTED TO ANY MORE — no caller. Kept (together
    // with window.__swDownload and the sw.js /__tmb-dl route) because the channel
    // is correct in isolation, but EVERY real client class re-requests the
    // delivery url after the transfer starts, and this url cannot be served then:
    // the re-request goes over the NETWORK, bypassing the SW, and /__tmb-dl has no
    // server route → 404. Measured for mobile in round-4 (~100 failed downloads/day)
    // and for desktop in round-5 (download-desktop-refetch.mjs). Do NOT re-wire any
    // class to it without first MEASURING that class's re-request behaviour.
    var _local=function(){
        if(typeof window.__swDownload!=='function')return Promise.resolve(false);
        return window.__swDownload(blob,filename).then(function(ok){
            if(!ok)return false;
            _ev('sw');
            return true;
        }).catch(function(){return false;});
    };
    // The WebView class: try local first, only then pay for an upload.
    //
    // iOS is DELIBERATELY excluded from the SW channel. On Chromium (Android
    // WebView, Yandex/GSA/FB in-app, desktop) a SW response carrying
    // Content-Disposition is routed to the download manager — proven on a real
    // 82 MB MP3, byte-exact. On iOS WKWebView that routing is unreliable, and
    // our "served" ack would then report success for a file that never landed —
    // re-introducing the exact lie this change removes. iOS already has a
    // working channel (the share sheet), so it keeps share → stash. Revisit
    // only with real-device evidence, never on a Playwright-webkit green.
    var _webviewChain=function(){
        // In-app WebViews (Android) and iOS WKWebView hosts hand the download to
        // the OS download manager (Android DownloadManager / WKDownload), which
        // RE-FETCHES the URL from the NETWORK — bypassing the Service Worker. The
        // SW-only /__tmb-dl/<id> URL then 404s (it has no server route), so the SW
        // channel NEVER delivers here even though its served-ack fires (which is
        // why telemetry reported these as "delivered"). CONFIRMED live 2026-07-18:
        // a plain network GET /__tmb-dl/<id> → 404, and the nginx access log shows
        // ~100 such 404/day, all in-app shells (YaSearchApp, Android ; wv, Huawei,
        // Xiaomi, DDG) — bug-report stein-9966, /ru/join, «ошибка скачивания» →
        // refund. The stash serves a REAL /api/download/{token} the download
        // manager CAN re-fetch (proven: page.request.get bypassing the SW → 200 +
        // bytes) — the path that "worked for weeks" before the 2026-07-10 SW
        // switch. So the WebView class MUST use the stash; the SW channel is
        // desktop-only (where the browser streams the SW response inline).
        //
        // ── …but the relay is not the only lane any more ──────────────────
        // A LADDER, the same shape the batch door has had since 2026-08-31
        // (__batchDeliver: folder → archive → honest refusal). The single-file
        // door had no disk rung at all, so its only answer above the relay cap
        // was "no".
        //
        //   disk (showSaveFilePicker)  — zero upload, no cap, confirmed write
        //   relay (stash)              — works, costs the user 2× the file
        //   honest refusal             — with the work KEPT (see _preserve)
        //
        // Why the disk rung is tried first only above DISK_LANE_MIN_BYTES and
        // not always: below it the relay is invisible and fast, and replacing
        // an invisible working channel with a save dialog is precisely the
        // round-8 desktop regression (45 % of those dialogs were cancelled).
        // Above the relay cap the disk rung is tried whatever the size — there
        // is no next rung to protect.
        // ⚠️ A CANCEL HERE FALLS THROUGH, and that is deliberate — it is the
        // opposite of what the desktop lane does, for a reason worth writing
        // down. On desktop the save dialog IS the mechanism the user reached
        // for, so dismissing it is a decision and we honour it (round 8: 45 %
        // of hijacked dialogs were cancelled). Here the dialog is an
        // OPTIMISATION WE INSERTED in front of a channel that already worked:
        // the user pressed "download", and the dialog only asked WHERE. Reading
        // that dismissal as "no file, then" would turn a 96 %-succeeding lane
        // (measured: 356 delivered vs 15 dead at 100–200 MB) into a coin flip.
        // So the worst case of this whole rung is: one dialog, then exactly the
        // old behaviour. It cannot regress anyone.
        // ⚠️ The field answer to "do in-app browsers open this dialog at all"
        // (the question this rung shipped with, 2026-09-01) is in: they open
        // it — and people dismiss it. RU, 30 days: 28 files saved through it
        // against 1 194 dismissals in 808 sessions, most within 1–10 s, a third
        // of them repeats in the same session (up to nine times). A dismissal
        // still ends in the relay, so each one only cost a confusing screen;
        // but asking again what the person just answered is the round-8
        // lesson all over. So a dismissal is REMEMBERED on the device for
        // FSA_DECLINE_DAYS: from then on this rung is skipped straight to the
        // relay. Of the 28 successes only 4 followed an earlier dismissal, so
        // the cost of listening is small and bounded. Above the relay cap the
        // dialog is still offered — there it is the only way.
        var _tryDisk=(blob.size>=DISK_LANE_MIN_BYTES)&&typeof window.showSaveFilePicker==='function'&&
            (blob.size>STASH_MAX_BYTES||!_fsaDeclinedRecently());
        if(!_tryDisk)return _stash();
        return _fsaConfirmed().then(function(r){
            if(r===true)return true;
            if(r===false){try{localStorage.setItem(FSA_DECLINE_KEY,String(Date.now()));}catch(e){}}
            return _stash();   // declined, or the lane was never there
        },function(){return _stash();});
    };
    // ── Telegram Mini App: the client's own download dialog (Bot API 8.0) ──
    // The webview cannot save a local blob (`<a download>` no-op, and iOS
    // hosts rarely implement WKDownloadDelegate), but the Telegram CLIENT
    // can download any HTTPS URL natively. So: stash the blob (same relay
    // the Android-WebView path uses), then hand the attachment URL to
    // Telegram.WebApp.downloadFile — the user gets Telegram's own
    // "Download file?" dialog and the bytes land in their Downloads,
    // signed, sealed, confirmed via the callback. Clients < 8.0 skip this
    // branch and take the generic webview routes below (Android TG carries
    // "; wv)" → stash navigation via DownloadListener; iOS TG is a bare
    // WKWebView host → share sheet).
    if(window.__isTelegramWebview()){
        var _twa=null,_tgOk=false;
        try{
            _twa=window.Telegram&&window.Telegram.WebApp;
            _tgOk=!!(_twa&&typeof _twa.downloadFile==='function'&&_twa.isVersionAtLeast('8.0'));
        }catch(e){}
        if(_tgOk){
            // Same relay, same ceiling — this branch POSTs to the very same
            // /api/download-stash. It carried its own copy of the number and I
            // missed it when the cap moved: Telegram users would have stayed at
            // 200 MB while every other WebView got a gigabyte, and the only
            // symptom would have been «не удалось сохранить» on a file the
            // server would have accepted. A second literal of a shared limit is
            // not duplication, it is a divergence waiting for its turn.
            if(blob.size>STASH_MAX_BYTES){window.__dlGesture.release();_ev('classic_oversize');_failNotice();return Promise.resolve(false);}
            var _tgMeta=document.querySelector('meta[name="csrf-token"]');
            var _tgFd=new FormData();
            _tgFd.append('file',blob,filename);
            _tgFd.append('filename',filename);
            return fetch('/api/download-stash',{method:'POST',headers:{'X-CSRF-TOKEN':_tgMeta?_tgMeta.content:''},body:_tgFd})
                .then(function(r){if(!r.ok)throw new Error('stash '+r.status);return r.json();})
                .then(function(j){
                    if(!j||!j.url)throw new Error('stash no-url');
                    var abs=new URL(j.url,location.href).href;
                    return new Promise(function(resolve){
                        var settled=false;
                        var done=function(ok,via,extra){if(settled)return;settled=true;_ev(via,extra);resolve(ok);};
                        try{
                            _twa.downloadFile({url:abs,file_name:filename},function(accepted){
                                // accepted=false is the user pressing "Cancel" on
                                // Telegram's dialog — an informed cancel, not a
                                // delivery failure (same stance as ios_share_cancel).
                                if(accepted)done(true,'tg_native');
                                else done(false,'tg_native_cancel');
                            });
                        }catch(e){
                            // Client refused the method → the stash URL is already
                            // minted; Content-Disposition navigation still works on
                            // Android TG (DownloadListener). Same anchor dance as _stash.
                            try{
                                var a=document.createElement('a');
                                a.href=j.url;a.rel='noopener';
                                document.body.appendChild(a);a.click();a.remove();
                                done(true,'stash');
                            }catch(e2){done(false,'tg_native_fail',{err:String(e2&&e2.message||'anchor').slice(0,40)});_failNotice();}
                        }
                        // Old clients that silently swallow the event: report the
                        // truth after 30s so the promise never hangs a tool's UI.
                        setTimeout(function(){done(false,'tg_native_timeout');},30000);
                    });
                })
                .catch(function(err){
                    _ev('stash_fallback',{err:String((err&&err.message)||'net').slice(0,40)});
                    _failNotice();
                    return false;
                });
        }
        // No native downloadFile — fall through to the generic webview
        // handling (iOS share sheet branch / _webviewChain via needsStash).
    }
    // A video on iOS goes through the same sheet — to «Фото» (see __iosPhotosKind).
    // Outside the classes above this browser's anchor works, so when the sheet is
    // unavailable or refuses, the anchor is what is left — never the relay.
    var _viaSheet=window.__iosSavesViaShareSheet();
    var _photos=_viaSheet?'':window.__iosPhotosKind(filename,blob.type,blob.size);
    if(!_viaSheet&&!_photos&&window.__iosDevice()&&window.__iosPhotosKind(filename,blob.type,0)){
        _note('ios_photos_over_cap');
    }
    if(_viaSheet||_photos){
        var _inApp=window.__iosInAppBrowser()?1:0;
        var _tag=_photos?{ph:_photos==='video'?'v':'i'}:{};
        var _sheetFallback=_photos?function(){return Promise.resolve(_classic('classic_native'));}:_webviewChain;
        try{
            if(navigator.share&&navigator.canShare){
                var shareFile=new File([blob],filename,{type:blob.type||'application/octet-stream'});
                if(navigator.canShare({files:[shareFile]})){
                    // ── No tap, no sheet ──────────────────────────────────────
                    // share() needs the user's transient activation, and a tool
                    // that saves BY ITSELF when a long export finishes has none:
                    // the call can only reject NotAllowedError. Measured (60 d):
                    // every ios_share_fail was NotAllowedError, 21 of 23 from
                    // such programmatic calls, and 19 of them then went on to
                    // the relay — upload the whole file and navigate to it,
                    // which an in-app WebView honours only if its host app
                    // implements downloads (rare), and which costs the person
                    // the file twice over mobile data. The share sheet itself
                    // is the lane that works; it only needs a tap. So ask for
                    // the tap — once, in a bar that stays until pressed.
                    if(_activationState()===false){
                        window.__dlGesture.release();
                        _ev('ios_share_needs_tap',Object.assign({inapp:_inApp},_tag));
                        _tapToSave(shareFile,_sheetFallback,_tag);
                        return Promise.resolve(false);
                    }
                    // The sheet opens over the lower part of the screen; the
                    // line above it names the item that puts the file in «Фото»
                    // («Сохранить» alone leaves the person hunting through a
                    // sheet of twenty icons, and an unexplained sheet where a
                    // download used to be reads as a mistake).
                    if(_photos&&window.__toast){
                        try{
                            var _hL=(window.__LOCALE||'en').substring(0,2),_hIt=_shareItem(shareFile.type);
                            var _hM={ru:'Выберите «'+_hIt+'» — так оно попадёт в «Фото». Для папки — «Сохранить в „Файлы“».',
                                     en:'Choose “'+_hIt+'” — that puts it in Photos. For a folder, “Save to Files”.',
                                     de:'Wählen Sie „'+_hIt+'“ — so landet es in „Fotos“. Für einen Ordner: „In ‚Dateien‘ sichern“.',
                                     es:'Elige «'+_hIt+'»: así se guarda en Fotos. Para una carpeta, «Guardar en Archivos».',
                                     fr:'Choisissez « '+_hIt+' » : elle ira dans Photos. Pour un dossier, « Enregistrer dans Fichiers ».',
                                     pt:'Escolha “'+_hIt+'” — assim vai para Fotos. Para uma pasta, “Salvar em Arquivos”.',
                                     id:'Pilih “'+_hIt+'” agar masuk ke Foto. Untuk folder, “Simpan ke File”.',
                                     ko:'“'+_hIt+'”을 선택하면 사진 앱에 저장됩니다. 폴더에 저장하려면 “파일에 저장”.',
                                     ar:'اختر «'+_hIt+'» ليُحفظ في «الصور». للحفظ في مجلد: «حفظ في الملفات».',
                                     uk:'Виберіть «'+_hIt+'» — так воно потрапить у «Фото». Для папки — «Зберегти у „Файли“».',
                                     tr:'“'+_hIt+'” seçin — böylece Fotoğraflar’a kaydedilir. Klasör için “Dosyalar’a Kaydet”.'};
                            window.__toast(_hM[_hL]||_hM.en,'info',{ctx:'download_photos'});
                        }catch(e){}
                    }
                    return navigator.share({files:[shareFile]}).then(function(){
                        _ev('ios_share',Object.assign({inapp:_inApp},_tag));
                        return true;
                    }).catch(function(err){
                        var n=(err&&err.name)||'';
                        // AbortError = the user saw the sheet and chose to close
                        // it — an informed cancel, NOT a delivery failure. Do not
                        // cascade into the stash (a surprise navigation right
                        // after an explicit cancel).
                        if(n==='AbortError'){window.__dlGesture.release();_ev('ios_share_cancel',_tag);return false;}
                        _ev('ios_share_fail',Object.assign({err:String(n||err).slice(0,60),inapp:_inApp},_tag));
                        // Activation gone (engines without navigator.userActivation
                        // land here instead of above): same answer — the tap.
                        if(n==='NotAllowedError'){window.__dlGesture.release();_tapToSave(shareFile,_sheetFallback,_tag);return false;}
                        // Host-blocked or anything else: the relay (WebView) or the
                        // anchor (a browser whose anchor works) still has a chance.
                        return _sheetFallback();
                    });
                }
            }
        }catch(e){/* File ctor / canShare threw — degrade below */}
        return _sheetFallback();
    }
    // Real browsers (not the WebView class). Two sub-cases:
    //
    // Chromium (desktop / Android-standalone) — prefer the SW Content-Disposition
    // channel (via:sw) over the classic blob anchor. A blob: URL we revoke is
    // fragile when the browser RE-REQUESTS the download after our revoke: Yandex
    // Browser Protect's AV-scan and pause/resume both do (bug-report mnaumov
    // 2026-07-17, /ru/join — an 11 MB join "загрузка прервана", 8 retries, every
    // file_download telemetried as delivered because the classic anchor fired but
    // the browser aborted the transfer; our JS is blind to that). The SW serves a
    // REAL /__tmb-dl/<id> HTTP response with Content-Disposition that survives
    // re-request / Range / pause-resume (the #1010 hardening) — the download
    // manager treats it as an ordinary file, not a synthetic blob it can drop.
    // ZERO upload (structured-clone handoff), so this is NOT the #443 stash path.
    // Falls back to the classic anchor whenever no SW controls the page (first
    // visit, hard reload, SW disabled) — identical graceful degradation to the
    // WebView chain, so a user without a controlling SW is exactly as before.
    //
    // Gated to Chromium (`Chrome/` token): SW→download-manager routing is proven
    // only on Blink (the WebView path is the same engine). Firefox and desktop
    // Safari keep the classic anchor — their SW Content-Disposition download
    // routing is unverified, and a served-ack for a file that never landed would
    // re-create the very lie iOS is excluded for.
    if(!window.__downloadNeedsStash()){
        // The SW Content-Disposition channel is DESKTOP-CHROMIUM ONLY. On desktop
        // the browser streams the SW response straight to disk (proven, round-3
        // mnaumov). On MOBILE the OS download manager (Android DownloadManager,
        // and the standalone browsers' own download services) RE-FETCHES
        // /__tmb-dl/<id> from the NETWORK, bypassing the SW → 404 (confirmed live
        // 2026-07-18, ~100/day: standalone Android Chrome, Opera, Huawei, Xiaomi
        // all re-fetch and 404). A real mobile browser's classic blob anchor stays
        // in-page (a blob: URL cannot be handed to the OS download manager, so it
        // is never network-re-fetched), so it downloads reliably — the behaviour
        // for this class before the 2026-07-17 SW-on-mobile extension. Only the
        // WebView shells (needsStash, below) genuinely no-op the blob anchor and
        // need the stash.
        // ROUND 5 (2026-07-18): the SW channel is no longer used for REAL browsers
        // at all — desktop included. Measured (download-desktop-refetch.mjs) on a
        // desktop YaBrowser UA: a delayed re-request of the SW url → 404 (it is
        // re-fetched over the NETWORK, bypassing the SW, and has no server route),
        // which is what the prod nginx log showed after round-4 — a desktop tail of
        // ~12 failed downloads / 2 h, 27/38 lines desktop YaBrowser, with retry
        // storms (one id re-requested 11×). The classic blob anchor survives that
        // same delayed re-read now that the revoke window is 120 s (a blob: url can
        // never be network-re-fetched, so the browser re-reads it in-page) — and it
        // costs no upload. So round-3's "desktop → SW" is reverted: its actual
        // motivation (mnaumov's «загрузка прервана») was the 4 s revoke race, which
        // is fixed at the source in _classic above.
        //
        // ROUND 8 (2026-07-24): auto-FSA-on-repeat REMOVED — it was a net loss.
        // Round 7 sent a REPEAT download of the same file through the FSA save
        // dialog to get a write confirmation. Three days of prod said no:
        //   • 45 % of the dialogs were CANCELLED (351 fsa_cancel vs 435 fsa) —
        //     a user clicking «Скачать ещё раз» expects a re-download, not a
        //     surprise «Save As» dialog; hijacking that button was wrong UX.
        //   • 66 fsa_fail, of which 35 NotFoundError, ACROSS yandex/chrome/edge
        //     — and with NO fallback the user was trapped in an error loop
        //     (bug-reports elena-aek + 962561, 2026-07-23: ~10 retries each,
        //     every one NotFoundError, rage-click on the toast).
        //   • Crucially it did NOT rescue the target cohort: those users are
        //     blocked by security software (Yandex Protect etc.) that removes
        //     the written file — classic shows «сохранён» but the file is
        //     quarantined, AND FSA's createWritable throws NotFoundError. The
        //     SAME AV blocks BOTH channels, so FSA confirmed nothing there.
        // The honest answer to "the file won't save" is not another web
        // download mechanism (they all lose to the AV) — it is telling the user
        // their security software is the cause (surfaced per-tool, e.g. /join's
        // repeated-retry note). Delivery here is the plain classic anchor,
        // which is what a real desktop browser handles natively; `_fsaConfirmed`
        // is kept below but UNROUTED (do not re-wire without measuring the
        // cancel + NotFoundError rates first).
        return Promise.resolve(_classic('classic_native'));
    }
    return _webviewChain();
    };
    return _readable().then(function(isReadable){
        if(isReadable)return _route();
        // Unreadable bytes cannot be delivered by ANY channel — clicking an
        // anchor here is what produced «Файл сохранён» for files that never
        // existed on disk. Unless the caller still holds the bytes (rescue
        // above), fail honestly and record it.
        return _rescue().then(function(f){
            if(f){
                _note('dl_rescue',{ok:1});
                return window.__downloadFile(f,filename,Object.assign({},opts||{},{bytes:null,__rescued:1,__aiMarked:1}));
            }
            var kind=_unreadableKind();
            _ev('blob_unreadable',{sizeMB:Math.round(blob.size/1048576),kind:kind,bytes:(opts&&opts.bytes)?1:0});
            _tooLargeNotice(blob.size,kind);
            return false;
        });
    });
};

// ── «The same file again» (module scope: survives across __downloadFile calls) ──
// Keyed by CONTENT, not by name and not by size alone: a tool that stamps the
// time into the name (/text-to-speech) gives the same bytes a new name on every
// press, and two different files of one size are common (a 10-second WAV from
// /tone-generator is the same length at 440 and at 880 Hz). Size + a hash of a
// 64 KB window from the middle and one from the end — the head is skipped, it
// is the format's header and the same in every file of one length. Returns the
// record of this file's classic deliveries in the last 10 minutes, this one
// counted, or null when the bytes could not be read (then nothing is offered).
var DL_REPEAT_WINDOW_MS=600000;
window.__dlRecentClassic=window.__dlRecentClassic||{};
function _dlFingerprint(blob){
    try{
        var n=blob.size,W=65536,parts=n<=2*W?[[0,n]]:[[Math.floor(n/2)-W/2,Math.floor(n/2)+W/2],[n-W,n]];
        return Promise.all(parts.map(function(p){return blob.slice(p[0],p[1]).arrayBuffer();})).then(function(bufs){
            var h=0x811c9dc5;
            for(var i=0;i<bufs.length;i++){var u=new Uint8Array(bufs[i]);for(var j=0;j<u.length;j++){h^=u[j];h=Math.imul(h,0x01000193);}}
            return n+':'+(h>>>0).toString(36);
        }).catch(function(){return null;});
    }catch(e){return Promise.resolve(null);}
}
function _dlRepeatSeen(ext,fp){
    var m=window.__dlRecentClassic,now=Date.now(),k=ext+'|'+fp;
    for(var x in m){ if(now-m[x].t>DL_REPEAT_WINDOW_MS)delete m[x]; }
    var r=m[k]||(m[k]={n:0,t:0,offeredAt:0});
    r.n++;r.t=now;
    return r;
}

// ── Global download interceptor ──
// ~181 tools save files via `<a download href="blob:|data:">` + click. That is
// a silent no-op in Android WebView (see __downloadFile above). Rather than
// migrate 181 call sites — and miss every future one — we intercept at the DOM
// boundary, the same "patch once, tools need no changes" pattern as
// ios-audio-unmute.js's AudioNode.connect patch.
//
// A capture-phase document click listener catches BOTH programmatic
// `a.click()` (HTMLElement.click() dispatches a real, capturable click event)
// AND genuine user clicks. When the click resolves to an <a[download]> whose
// href is a blob:/data: URL, we cancel the default (dead-in-WebView) action,
// pull the bytes, and route through __downloadFile.
//
// NOT intercepted: http(s) <a download> (server file — Content-Disposition
// already works in WebView); anchors marked data-rawdl (that IS __downloadFile's
// own fallback — re-catching it would loop). A server file is still REPORTED as
// a delivery (`_dlServerLink`, bubble phase) — see there.
//
// ⚠️ «Catches programmatic a.click()» held only for an anchor IN the document:
// an event on a detached element has no path to `document`, so
// `document.createElement('a'); a.href=blob; a.download=…; a.click()` — the
// form /video-watermark, /gif-trim, /colorblind-camera use — skipped this whole
// block: no WebView rescue, no iPhone «Фото» sheet, no one-file-per-gesture
// budget, no delivery event. Refund request №47 (16.10.2026) is how it
// surfaced: the panel printed «ничего не выгружал» for a buyer who had taken
// three files. `_dlAttachDetached` below closes it for every tool at once.
(function(){
    if(!window.fetch)return; // no fetch → can't pull blob bytes; leave classic path
    document.addEventListener('click',function(ev){
        var a=ev.target&&ev.target.closest?ev.target.closest('a[download]'):null;
        if(!a)return;
        if(a.hasAttribute('data-rawdl'))return; // __downloadFile's own fallback anchor
        var href=a.getAttribute('href')||'';
        if(href.slice(0,5)!=='blob:'&&href.slice(0,5)!=='data:')return; // server files: leave alone
        var name=a.getAttribute('download')||'download';
        // Real browsers download a blob anchor natively — leave it ALONE.
        // Intercepting would force a needless stash round-trip (the #443
        // "minutes-then-silence" failure). Only the WebView class — where
        // the anchor is a silent no-op — needs the takeover. iOS in-app
        // WebViews are part of that class: their anchors are equally dead,
        // and __downloadFile routes them via the share sheet.
        // A video on iOS is taken over too: it goes to «Фото» through the share
        // sheet (__iosPhotosKind), which a native anchor download never reaches.
        if(!window.__downloadNeedsStash()&&!window.__iosSavesViaShareSheet()&&!window.__iosPhotosKind(name,'',0)){
            // …with ONE exception, and it is the reason this branch exists at
            // all now: a tool that clicks its own anchors in a loop never
            // reaches __downloadFile on desktop, so the gesture budget above
            // would not see it. The browser would drop every anchor past the
            // first AND poison the tab for the whole site. The first anchor
            // keeps the slot, the rest are taken over and held.
            //
            // ⚠️ The first anchor's claim is NOT made here. In the capture
            // phase nobody yet knows whether this anchor downloads anything:
            // a listener further along the dispatch can preventDefault() and
            // deliver the file through __downloadFile itself — which is exactly
            // what /screen-recorder does. Claiming here charged the gesture for
            // a native download that then never happened: the page's own, REAL
            // delivery found the slot spent and was demoted to a hold bar, so a
            // single click on «Скачать» handed the user nothing (measured
            // 2026-09-03: zero files, one hold bar, then a ZIP) — 149 sessions
            // in 30 days on this tool alone. Worse, the phantom was REPORTED as
            // a delivery, and `delivered_files` is what RefundService reads to
            // decide whether the service was rendered.
            // The claim moves to the end of the dispatch (the bubble listener
            // below), where `defaultPrevented` is final. Here we only handle
            // what is already decided: the slot is gone, so this anchor cannot
            // download natively and must be held instead of dropped.
            if(!(window.__dlGesture&&window.__dlGesture.taken())) return;
            // The SAME anchor fired twice in one interaction is a double-fire in
            // the caller, not a second file: nothing was lost, so hold nothing.
            if(window.__dlGesture&&window.__dlGesture.spentKey()===name+'|anchor'){ev.preventDefault();return;}
            ev.preventDefault();
            fetch(href).then(function(r){return r.blob();}).then(function(b){
                window.__dlHold.add(b,name);
                try{if(window.__ae)window.__ae('feature_use',{f:'file_download',via:'held_gesture',src:'interceptor',
                    slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                    sizeKB:b?Math.round(b.size/1024):0,queued:window.__dlHold.count()});}catch(e){}
            }).catch(function(){/* bytes already gone — nothing to hold */});
            return;
        }
        // This anchor's download is dead in WebView — take it over. Only
        // preventDefault (kill the dead/duplicate browser download); do NOT
        // stop propagation, so a tool's own click telemetry still runs.
        ev.preventDefault();
        // fetch() is invoked synchronously inside the click dispatch, before the
        // tool's post-click code (e.g. revokeObjectURL) runs — so href is valid.
        fetch(href).then(function(r){return r.blob();}).then(function(blob){
            return window.__downloadFile(blob,name,{src:'interceptor',srcUrl:href});
        }).catch(function(){
            // Blob unfetchable (already revoked, etc.) — genuinely can't relay
            // it. Re-fire the ORIGINAL anchor with its download attr KEPT
            // (mark data-rawdl so this interceptor doesn't re-catch it): a real
            // browser still downloads classically; WebView no-ops, but the blob
            // is gone so there is nothing better. Crucially we do NOT strip
            // `download` — that would navigate the page to the raw blob.
            try{a.setAttribute('data-rawdl','1');a.click();}catch(e){}
        });
    },true);

    // ── The other half: the anchor that ACTUALLY downloads ──────────────
    // Bubble phase on document: every listener on the anchor and its ancestors
    // has had its say, so `defaultPrevented` is final and the gesture can be
    // charged to the delivery that really happens.
    //   • default prevented  → somebody else is delivering this file; the
    //     browser downloads nothing, the gesture keeps its slot, and there is
    //     no delivery to report.
    //   • slot taken mid-dispatch (the page called __downloadFile in its own
    //     handler and did NOT cancel the anchor) → two files in one gesture:
    //     cancel this one and hold it, the same rescue the capture branch does
    //     for the second anchor of a loop.
    //   • otherwise → the browser is downloading this natively: take the slot
    //     (so a loop's later anchors are held, not dropped) and report it — a
    //     hand-written <a download> leaves no other trace, and `delivered_files`
    //     is read by RefundService.
    // via:'classic_anchor', not 'classic_native': a DIFFERENT path (the
    // primitive never touched it), and the only mark that shows a tool has not
    // migrated yet.
    document.addEventListener('click',function(ev){
        if(ev.defaultPrevented)return;
        var a=ev.target&&ev.target.closest?ev.target.closest('a[download]'):null;
        if(!a||a.hasAttribute('data-rawdl'))return;
        var href=a.getAttribute('href')||'';
        if(href.slice(0,5)!=='blob:'&&href.slice(0,5)!=='data:'){ _dlServerLink(a,href,ev); return; }
        if(window.__downloadNeedsStash()||window.__iosSavesViaShareSheet()||window.__iosPhotosKind(a.getAttribute('download')||'','',0))return; // capture branch owns those
        if(!window.__dlGesture)return;
        var name=a.getAttribute('download')||'download';
        if(window.__dlGesture.taken()){
            if(window.__dlGesture.spentKey()===name+'|anchor'){ev.preventDefault();return;}
            ev.preventDefault();
            fetch(href).then(function(r){return r.blob();}).then(function(b){
                window.__dlHold.add(b,name);
                try{if(window.__ae)window.__ae('feature_use',{f:'file_download',via:'held_gesture',src:'interceptor',
                    slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                    sizeKB:b?Math.round(b.size/1024):0,queued:window.__dlHold.count()});}catch(e){}
            }).catch(function(){/* bytes already gone — nothing to hold */});
            return;
        }
        window.__dlGesture.claim(name+'|anchor');
        // `ext`, а не имя (16.10.2026): имя файла — текст человека (запись,
        // ученик, класс), а вопрос доставки — «какой формат и кому», как у
        // `_ev` примитива (DeliveryFormatCensus). До этой даты здесь ехал
        // хвост имени в 40 знаков и не ехал формат.
        try{if(window.__ae)window.__ae('feature_use',{f:'file_download',via:'classic_anchor',src:'interceptor',
            slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
            ext:((/\.([A-Za-z0-9]{1,8})$/.exec(String(name))||[])[1]||'').toLowerCase()});}catch(e){}
        try{if(window.__dlAnchorClicked)window.__dlAnchorClicked({href:href,name:name,via:'classic_anchor'});}catch(e){}
    },false);

    /* A file from OUR server, handed to the browser's download manager by a
       plain <a download href="/…">: /song-generator and /music-generator
       (master-download.js — deliberately a link, the browser resumes a broken
       transfer), «Мои генерации», /printer-test's PDF, the sample files. The
       browser downloads it; nothing here reported it. RU, 7 days: 66 of 74
       /song-generator sessions that downloaded a song carried no delivery at
       all, and the refund panel and RenderedPaidService read exactly this
       event (`BuyerActivity::isDelivery`) — request №47 read «ничего не
       выгружал» for three files the server had sent in full.
       Not routed (the server's Content-Disposition already works in WebView),
       but BUDGETED like every other delivery. `ok:1` on the same footing as
       `classic_anchor` — the browser accepted the link; same-origin only, a
       link to another site is not our delivery.

       The budget (17.10.2026, comment #1935, desktop Yandex 26.8). A link is
       the browser's download exactly like a blob anchor, so the browser's one-
       file-per-interaction limit is the same — and this branch used to only
       count. master-download.js clicks its link AFTER a «prepare» request; on
       /music-generator she pressed FLAC, WAV and MP3, the three prepares came
       back in the same second, and three links fired with no interaction
       between them. Measured on her browser build (real profile, real input,
       truth from the download folder and History): two links together → 1 of 2,
       and the tab is poisoned — every later single download by click is dropped
       silently, through a same-origin navigation, 90 s later, until F5. Her next
       page in that tab was /text-to-speech: 35 «Скачать» in 26 minutes, every
       one logged `classic_native ok:1`, zero files. So the slot is claimed
       here, and a link that would be this interaction's SECOND download is
       held — by address, not bytes: the point of a link is that the browser
       carries the transfer (resume, background), so nothing is pulled into
       the page — and handed back on the next press, like a held blob. */
    function _dlServerLink(a,href,ev){
        try{
            var u=new URL(href,location.href);
            if(u.origin!==location.origin)return;
            var name=a.getAttribute('download')||decodeURIComponent((u.pathname.split('/').pop()||''));
            var m=/\.([A-Za-z0-9]{1,8})$/.exec(name)||/\.([A-Za-z0-9]{1,8})$/.exec(u.pathname);
            var ext=m?m[1].toLowerCase():'';
            var slug=(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||'');
            var g=window.__dlGesture;
            if(g&&!g.claim(u.href+'|link')){
                // The same link twice in one interaction is a double-fire, not a
                // second file: nothing is missing, so nothing is held.
                ev.preventDefault();
                if(g.spentKey()===u.href+'|link')return;
                a.__dlHeld=1;
                // The attribute as written: "" means «the server names it».
                window.__dlHold.add(null,a.getAttribute('download')||'',{url:u.href});
                if(window.__ae)window.__ae('feature_use',{f:'file_download',via:'held_gesture',src:'server_link',ok:0,
                    slug:slug,ext:ext,queued:window.__dlHold.count()});
                return;
            }
            if(window.__ae)window.__ae('feature_use',{f:'file_download',via:'server_link',src:'interceptor',ok:1,
                slug:slug,ext:ext});
        }catch(e){}
    }
})();

/* ── window.__downloadLink(url, name) — a file on OUR server, to the browser ──
   The one door for «the server has the file, let the browser fetch it»: an
   attached `<a download>` clicked here passes the interceptor above, so it
   spends this interaction's one download (or waits on the «Сохранить» bar if
   another download already took it) and is reported as a delivery. A bare
   navigation to an attachment route does the same download with
   neither: it cannot be held, so it is exactly the second file that poisons
   the tab, and the refund panel never sees it. `name` "" (or omitted) leaves
   the file name to the server's Content-Disposition.
   Returns false when the link was held rather than handed over. */
window.__downloadLink=function(url,name){
    var a=document.createElement('a');
    a.href=url;a.setAttribute('download',name||'');a.style.display='none';
    (document.body||document.documentElement).appendChild(a);
    a.click();a.remove();
    return !a.__dlHeld;
};

/* A download anchor clicked while DETACHED goes through the document for the
   length of the click, so the interceptor above sees it like any other: the
   WebView takeover, the iPhone «Фото» sheet, the one-file-per-gesture budget
   and the delivery event all start working for it. RU, 20 days: 43 sessions of
   /video-watermark and 9 of /gif-trim in the Yandex app — the WebView class
   where such an anchor downloads nothing — clicked one. Hidden, removed right
   after the synchronous click; an anchor already in the page, one without
   `download`, and __downloadFile's own `data-rawdl` fallback are left alone. */
(function(){
    var P=window.HTMLAnchorElement&&HTMLAnchorElement.prototype;
    if(!P||P.__dlAttachDetached)return;
    var orig=P.click;
    P.click=function(){
        if(this.isConnected||!this.hasAttribute('download')||this.hasAttribute('data-rawdl')||!document.body){
            return orig.apply(this,arguments);
        }
        var st=this.getAttribute('style');
        this.style.display='none';
        document.body.appendChild(this);
        try{ return orig.apply(this,arguments); }
        finally{
            try{
                if(this.parentNode)this.parentNode.removeChild(this);
                if(st==null)this.removeAttribute('style'); else this.setAttribute('style',st);
            }catch(e){}
        }
    };
    P.__dlAttachDetached=1;
})();

/* __dlAnchorWatch:begin */
// ── A blob anchor that took the page away ──────────────────────────────────
// The blob anchor is the one lane whose failure we could not see: we click, the
// browser does whatever it does, and «file_download via:classic_native ok:1» is
// written either way. Report #8048 (2026-09-26, iPhone iOS 17.5.1, YaBrowser
// 25.4) showed what «whatever» can be: the browser NAVIGATED the tab to the blob
// URL — «адрес недействителен или изменён» — and the page with a finished 36 MB
// video was gone. Nine exports, nine times, and every one logged as delivered.
// The signature is unambiguous: pagehide within seconds of our own click — a
// working download does not unload the page at all.
//   • a beacon (tier-0, carries the raw UA) — so the class is COUNTED per
//     browser from now on, not inferred from repeat clicks;
//   • on iOS the device remembers it (__iosSavesViaShareSheet then routes the
//     next save through the share sheet) — the browsers nobody has measured
//     cannot hide behind a version list;
//   • when the tab comes back ALIVE from the back-forward cache (it did in her
//     session: the result panel was still there 30 s later), the file is still
//     in memory — the share sheet is offered at once, one tap.
(function(){
    // pagehide of the old document fires when the NEW one commits. For a blob
    // navigation that is immediate (her beacon came the same second as the
    // click), for a network page it took 2.7 s on the bench — the window has
    // room for both. A false positive on iOS costs nothing: the share sheet
    // saves files on every iOS browser. `ms` travels with the beacon.
    var WINDOW_MS=8000, last=null;
    window.__dlAnchorClicked=function(rec){
        if(!rec)return;
        rec.t=Date.now();rec.left=0;
        last=rec;
        // The watch is for the few seconds a navigation would take; after that a
        // held reference to someone's 40 MB result is only a leak.
        setTimeout(function(){if(last===rec&&!rec.left)last=null;},60000);
    };
    var extOf=function(n){var m=/\.([A-Za-z0-9]{1,8})$/.exec(String(n||''));return m?m[1].toLowerCase():'';};
    window.addEventListener('pagehide',function(ev){
        try{
            if(!last||last.left)return;
            var ms=Date.now()-last.t;
            if(ms>WINDOW_MS)return;
            last.left=Date.now();
            var ios=!!(window.__iosDevice&&window.__iosDevice());
            if(ios){try{localStorage.setItem(window.__DL_ANCHOR_NAV_KEY,JSON.stringify({at:Date.now(),ms:ms}));}catch(e){}}
            var data={slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||''),
                      ms:ms,persisted:ev&&ev.persisted?1:0,ios:ios?1:0,via:last.via||'',ext:extOf(last.name),
                      sizeKB:last.blob?Math.round(last.blob.size/1024):0};
            // The critical beacon belongs to the class that exists: iOS. A desktop or
            // Android download never unloads the page, so a pagehide inside the window
            // there is the person moving on — 13 of the first 16 beacons after the
            // deploy (27.09) were Windows, macOS and Android, ms 148…7983, flooding
            // client-errors.log. They stay as feature_use with ios:0, which is where
            // another class would show up (as ms near zero).
            try{if(ios&&window.__aeCritical)window.__aeCritical('download_left_page',data);}catch(e){}
            try{if(window.__ae)window.__ae('feature_use',Object.assign({f:'download_left_page'},data));}catch(e){}
        }catch(e){}
    });
    window.addEventListener('pageshow',function(ev){
        try{
            if(!ev||!ev.persisted||!last||!last.left)return;
            var back=last;last=null;
            var ios=!!(window.__iosDevice&&window.__iosDevice());
            try{if(window.__ae)window.__ae('feature_use',{f:'download_left_page_back',ios:ios?1:0,
                slug:(window.__TOOL_SLUG||(document.querySelector('meta[name="tool-slug"]')||{}).content||'')});}catch(e){}
            if(!ios)return;
            var get=back.blob?Promise.resolve(back.blob)
                :(back.href?fetch(back.href).then(function(r){return r.blob();}):Promise.reject(new Error('no bytes')));
            get.then(function(b){
                // Through the door: the device is now in the share-sheet class,
                // and the door has no tap here, so it answers with the one-tap bar.
                // The failed click already spent this interaction's delivery slot;
                // without the release the door would read the rescue as a double
                // fire of the same file and say nothing at all.
                try{if(window.__dlGesture)window.__dlGesture.release();}catch(e){}
                if(window.__downloadFile)window.__downloadFile(b,back.name||'download',{src:'anchor_back'});
            }).catch(function(){/* the bytes went with the page — nothing to offer */});
        }catch(e){}
    });
})();
/* __dlAnchorWatch:end */

// ── Export-keep restore bar: if the last kept export never confirmed
// delivery (failed upload / refresh / delivery-navigation death / the page
// died before «Скачать» was ever pressed), offer it back on the next page view
// instead of forcing a re-export (which also re-bills the tool's daily run).
//
// Every browser since 13.10.2026. It used to answer only to the classes the
// DOOR keeps for (in-app WebViews, the iOS share sheet, Telegram), on the
// reasoning that nobody else ever has a kept copy. A tool may now keep its
// result the moment it exists (/repair-video: 36 minutes of a phone's work,
// gone with a reload 40 s after they ended — and the browser was DuckDuckGo
// for Android, a WebView whose pages present Chrome's own User-Agent, so the
// gate did not even know it was fragile). Whoever stored the copy, the person
// it belongs to is the one looking at this page.
(function(){
    var boot=function(){
        try{
            if(!window.__exportKeepLoad)return;
            window.__exportKeepLoad().then(function(entry){
                if(!entry||!entry.blob)return null;
                // Offer only what can still be read. In an off-the-record profile IndexedDB
                // keeps a file-backed Blob (an OPFS result) as a REFERENCE, not a copy —
                // measured 16.10.2026: once the janitor removed the source, «Скачать» on
                // the bar answered «файл больше недоступен». The head through a stream, as
                // the door's own probe: a slice would be a new Blob (a full store refuses it).
                var rd=null;
                try{if(typeof entry.blob.stream==='function')rd=entry.blob.stream().getReader();}catch(e){rd=null;}
                if(!rd)return entry;
                return rd.read().then(function(){try{rd.cancel();}catch(_){}return entry;},function(){
                    try{if(window.__ae)window.__ae('feature_use',{f:'export_restore_unreadable',tool:entry.tool||'',
                        sizeKB:Math.round(entry.blob.size/1024),age_min:Math.round((Date.now()-entry.at)/60000)});}catch(_){}
                    if(window.__exportKeepDrop)window.__exportKeepDrop();
                    return null;
                });
            }).then(function(entry){
                if(!entry||!entry.blob)return;
                var L=(window.__LOCALE||'en').substring(0,2);
                var M={ru:['Прошлый экспорт не был скачан:','Скачать'],
                       en:['Your last export was not downloaded:','Download'],
                       de:['Ihr letzter Export wurde nicht heruntergeladen:','Herunterladen'],
                       es:['Tu última exportación no se descargó:','Descargar'],
                       fr:['Votre dernier export n’a pas été téléchargé :','Télécharger'],
                       uk:['Минулий експорт не було завантажено:','Завантажити'],
                       tr:['Son dışa aktarımınız indirilmedi:','İndir'],
                       pt:['Sua última exportação não foi baixada:','Baixar'],
                       id:['Ekspor terakhir Anda belum diunduh:','Unduh'],
                       ko:['마지막 내보내기가 다운로드되지 않았습니다:','다운로드'],
                       ar:['لم يتم تنزيل آخر تصدير:','تنزيل']};
                var t=M[L]||M.en;
                var bar=document.createElement('div');
                bar.id='tmb-export-restore';
                bar.setAttribute('role','region');
                bar.style.cssText='position:fixed;left:50%;transform:translateX(-50%);z-index:9991;'+
                    'bottom:'+(12+(function(){try{var b=document.querySelector('.cc-banner');
                        if(!b||!document.body.contains(b))return 0;
                        var st=window.getComputedStyle(b);
                        if(st.display==='none'||st.visibility==='hidden')return 0;
                        return Math.round(b.getBoundingClientRect().height)||0;}catch(e){return 0;}})())+'px;'+
                    'display:flex;align-items:center;gap:10px;max-width:94vw;box-sizing:border-box;'+
                    'background:rgba(20,24,32,.94);color:#fff;border:1px solid rgba(255,255,255,.18);'+
                    'border-radius:14px;padding:10px 14px;font:13px/1.35 system-ui,sans-serif;'+
                    'box-shadow:0 6px 22px rgba(0,0,0,.4)';
                var nm=String(entry.name||'download');
                if(nm.length>34)nm=nm.slice(0,31)+'…';
                var txt=document.createElement('span');
                // Two lines, not one with an ellipsis: on a phone the single line ended at
                // "…was not downloa…" and the name of the file never showed.
                txt.style.cssText='min-width:0;flex:1 1 auto;overflow:hidden;display:-webkit-box;'+
                    '-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow-wrap:anywhere';
                // Under a megabyte in kilobytes: a 52 KB clip read «(0 МБ)», as if the copy were empty.
                var _sz=entry.blob.size,_mbU=_sz>=1048576;
                txt.textContent=t[0]+' '+nm+' ('+(_mbU?Math.round(_sz/1048576):Math.max(1,Math.round(_sz/1024)))+' '+
                    (_mbU?({ru:'МБ',uk:'МБ',ar:'م.ب'}[L]||'MB'):({ru:'КБ',uk:'КБ',ar:'ك.ب'}[L]||'KB'))+')';
                var btn=document.createElement('button');
                btn.type='button';
                btn.style.cssText='flex:0 0 auto;background:#3b82f6;color:#fff;border:0;border-radius:10px;'+
                    'padding:7px 14px;font:600 13px system-ui,sans-serif;cursor:pointer';
                btn.textContent=t[1];
                var x=document.createElement('button');
                x.type='button';
                x.setAttribute('aria-label',((window.__commonStrings||{}).ui_close || "Close"));
                x.style.cssText='flex:0 0 auto;background:transparent;color:rgba(255,255,255,.7);border:0;'+
                    'font:16px/1 system-ui,sans-serif;cursor:pointer;padding:4px 6px';
                x.textContent='✕';
                btn.addEventListener('click',function(){
                    try{bar.remove();}catch(e){}
                    try{
                        if(window.__ae)window.__ae('feature_use',{f:'export_restore_download',
                            sizeKB:Math.round(entry.blob.size/1024),tool:entry.tool||''});
                    }catch(e){}
                    // The door clears a copy it stored itself; one a tool stored on a
                    // page that no longer exists is cleared here, by the same rule
                    // (3 s after a confirmed delivery — a delivery that kills the
                    // page never runs the timer, and the copy stays).
                    var p=window.__downloadFile(entry.blob,entry.name,{src:'export_restore'});
                    if(p&&p.then)p.then(function(ok){
                        if(ok)setTimeout(function(){if(window.__exportKeepDrop)window.__exportKeepDrop();},3000);
                    },function(){});
                });
                x.addEventListener('click',function(){
                    try{bar.remove();}catch(e){}
                    if(window.__exportKeepDrop)window.__exportKeepDrop();
                });
                bar.appendChild(txt);bar.appendChild(btn);bar.appendChild(x);
                document.body.appendChild(bar);
                try{
                    if(window.__ae)window.__ae('feature_use',{f:'export_restore_offered',
                        sizeKB:Math.round(entry.blob.size/1024),tool:entry.tool||'',age_min:Math.round((Date.now()-entry.at)/60000)});
                }catch(e){}
            });
        }catch(e){}
    };
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot);
    else boot();
})();

// ── Upload progress UI ──
// MOVED to layouts/app.blade.php (inline, before this script tag) — Sentry 116995413
// (RU/Yandex Browser 26.4/Android 9, 2026-05-02): when timbrica-core.js's primary
// `<script src>` failed and `onerror=this.src=fallback` swapped to the local URL,
// the fallback load is async — the parser does NOT block on it. The next inline
// `<script>` then runs with no globals defined, and 7 tools that call
// `initUploadProgress(...)` at parse-time die with ReferenceError. Inlining the
// definition in the layout removes the load-race entirely. Don't re-add here.

// ── iOS Safari AudioContext sleep fix + battery-friendly suspend-on-background
// (extended 2026-06-03 site-wide audit). The Proxy registers every tool's
// AudioContext into a shared pool so we can (a) resume them when the tab
// returns from background AND (b) suspend them when the tab goes to background
// while no media element is actively playing — Web Audio is decoupled from
// `<audio>` playback so without this hook the context keeps eating battery
// on mobile even after the user switches tabs. We don't suspend when ANY
// HTMLMediaElement is non-paused so foreground media + WebAudio chains keep
// working (equalizer's live preview etc.).
!function(){
    var AC=window.AudioContext||window.webkitAudioContext;
    if(!AC||typeof Proxy==='undefined')return;
    var pool=[];
    window.__acPool=pool;
    function pruneAndApply(fn){
        for(var i=pool.length-1;i>=0;i--){
            try { if(pool[i].state==='closed'||pool[i].__tmbAudioError){pool.splice(i,1);continue} fn(pool[i]); }
            catch(e){ pool.splice(i,1); }
        }
    }
    // `interrupted` (WebKit) спит так же, как `suspended` — см. resumeAll выше.
    function resumeAll(){ pruneAndApply(function(c){ if(c.state==='suspended'||c.state==='interrupted') c.resume().catch(function(){}); }); }
    function anyMediaPlaying(){
        var els = document.querySelectorAll('audio,video');
        for (var i=0; i<els.length; i++) {
            if (!els[i].paused && !els[i].ended) return true;
        }
        return false;
    }
    function suspendAllIfIdle(){
        if (anyMediaPlaying()) return;
        pruneAndApply(function(c){ if(c.state==='running' && !c.__tmbProducer && !c.__tmbScheduled) c.suspend().catch(function(){}); });
    }
    /* ⚠️⚠️ Третий повод НЕ усыплять: у контекста ЗАБРОНИРОВАНА работа в
       будущем. Хук судил о нужности контекста по двум косвенным признакам —
       играет ли медиа-элемент и кормит ли контекст MediaStream, — и ни один из
       них не знает про звук, поставленный в очередь звуковой карты на 10:45.
       А усыплённый контекст останавливает СВОИ ЧАСЫ, поэтому забронированный
       голос не звучит вовсе.

       ⚠️ Здесь нельзя писать имя браузерного API замка экрана целиком:
       WakeLockPrimitiveTest ищет его по всем js и комментарии не снимает —
       строка в пояснении читается как рукописная копия примитива.

       Замер 22.09.2026 на живой странице /school-bell (Chrome 153, замок
       экрана взят через API, то есть БЕЗ видео-запаски, то есть в
       документе нет ни одного медиа-элемента): вкладка скрывается — контексты
       ["running","running"] → ["suspended","suspended"], звонок отмечается
       `failed`, над расписанием появляется «Сегодня пропущено звонков: 1».
       С удержанием — ["running","running"] и `rang`. Разделитель ровно этот
       хук: там, где замок экрана уходил на видео-запаску (браузер без API),
       игравший <video> делал anyMediaPlaying() истинным, и звонок выживал —
       то есть звонок работал у тех, у кого ХУЖЕ поддержка API.

       Объявлять это тул обязан ЯВНО — `__audioBackground.holdScheduled(ctx)`,
       — а не случайным побочным эффектом: `__tmbProducer` выставляется самим
       вызовом createMediaStreamDestination, и именно поэтому класс «звук
       забронирован» два месяца выглядел как «у нас всё хорошо». */
    // A context that feeds a MediaStream (createMediaStreamDestination) is a
    // PRODUCER — a recorder or an RTC sender consumes its output while this tab
    // is hidden, which is the NORMAL state of a screen recording (the user is
    // in the tab being captured). Suspending it is not a battery saving, it is
    // a hole in someone's file: /screen-recorder lost 0.4–2.7 s of audio on
    // every tab switch (prod telemetry 2026-09-02, Анастасия #1443/#1553), and
    // the 08-20 «Chrome suspends the mixer when hidden» finding was this very
    // hook looking at itself. Mark such contexts and never suspend them.
    function markProducer(c){
        try{
            var mk=c.createMediaStreamDestination;
            if(typeof mk==='function')c.createMediaStreamDestination=function(){c.__tmbProducer=true;return mk.apply(c,arguments)};
        }catch(e){}
    }
    var P=new Proxy(AC,{construct:function(t,a){var c=a.length?new t(a[0]):new t();pool.push(c);markProducer(c);return c}});
    window.AudioContext=P;
    if(window.webkitAudioContext)window.webkitAudioContext=P;
    document.addEventListener('visibilitychange',function(){
        if(document.visibilityState==='visible') resumeAll();
        else suspendAllIfIdle();
    });
    document.addEventListener('click',resumeAll,{passive:true});
    document.addEventListener('touchstart',resumeAll,{passive:true});
}();

// ── Range input fill styling ──
(function(){
    /* A zero bound is a number, not a missing attribute. A falsy-or default
       (`… || 100`) read max="0" as 100: every threshold slider on the site (−60…0 dB, 23 of
       them in ~15 tools) drew its fill at 22 % under a thumb at 60 %. And a
       slider with no range yet (a scrubber before its media: 0…0) paints 0 %,
       not NaN% — that would void the whole gradient and hide the track. */
    function num(v,d){v=parseFloat(v);return isFinite(v)?v:d}
    function fill(el){var min=num(el.min,0),max=num(el.max,100),val=num(el.value,min);var pct=max>min?Math.max(0,Math.min(100,((val-min)/(max-min))*100)):0;el.style.setProperty('--range-fill',pct+'%')}
    /* Exposed because the delegated listener below only sees USER input. A tool
       that writes el.value itself (restoring a saved setting, snapping a value
       to a derived one) moves the thumb and leaves the fill where it was — the
       blue then ends somewhere the thumb is not. Measured on /text-to-handwriting
       2026-09-05: thumb at 55%, fill at 80%. Call this after any programmatic
       write. */
    window.__rangeFill=fill;
    document.addEventListener('input',function(e){if(e.target.type==='range')fill(e.target)});
    document.addEventListener('DOMContentLoaded',function(){var all=document.querySelectorAll('input[type=range]');for(var i=0;i<all.length;i++)fill(all[i])});
    var mo=new MutationObserver(function(muts){muts.forEach(function(m){m.addedNodes.forEach(function(n){if(n.nodeType===1){if(n.type==='range')fill(n);var ch=n.querySelectorAll?n.querySelectorAll('input[type=range]'):[];for(var i=0;i<ch.length;i++)fill(ch[i])}})})});
    mo.observe(document.body||document.documentElement,{childList:true,subtree:true});
})();

// ── Whats-new bell: unread dot via localStorage vs the layout's data-latest
//    timestamp (server value is cache-safe — same HTML for everyone). Visiting
//    /whats-new marks everything seen. ──
(function(){
    var bell=document.getElementById('newsBell');if(!bell)return;
    var dot=bell.querySelector('.news-bell-dot');
    var latest=parseInt(bell.getAttribute('data-latest')||'0',10)||0;
    var KEY='tmb_news_seen_ts';
    var seen=0;try{seen=parseInt(localStorage.getItem(KEY)||'0',10)||0}catch(e){}
    if(/\/whats-new(\/|$|\?)/.test(location.pathname+location.search)){
        // CF/PageCache can serve a stale data-latest for up to ~1h — stamp
        // "now" too so the dot never re-lights for posts already on screen.
        var now=Math.floor(Date.now()/1000);
        try{localStorage.setItem(KEY,String(Math.max(latest,now)))}catch(e){}
        return;
    }
    if(dot&&latest>0&&latest>seen)dot.hidden=false;
})();

// ── Шапка на телефоне: компактная после прокрутки; подпись логотипа — в свой ряд ──
// Сжатие рисует critical.css (.header.hdr-compact) только transform/opacity: это
// не сдвиг раскладки для CLS, и высота документа от него не меняется — у порога
// переключатель не раскачивает сам себя. Класс — на самой шапке, а не на <html>:
// пересчёт стиля остаётся в её поддереве. Порог с запасом (32 вниз, 4 вверх) —
// шапка не дрожит, когда палец останавливается у границы. На кадре — только
// scrollY, без чтения раскладки.
(function(){
    var hd=document.querySelector('.layout .header');if(!hd)return;
    var on=false,raf=0;
    function tick(){
        raf=0;
        // Тело, замороженное position:fixed; top:-y (так делала шторка меню до 18.10),
        // роняет scrollY в 0 — это не прокрутка к началу: шапка остаётся какой была.
        var b=document.body;if(b&&b.style.position==='fixed')return;
        var y=window.scrollY||window.pageYOffset||0,next=on?y>4:y>32;
        if(next!==on){on=next;hd.classList.toggle('hdr-compact',on)}
    }
    window.addEventListener('scroll',function(){if(!raf)raf=requestAnimationFrame(tick)},{passive:true});
    tick();

    // Подпись («Создаём инструменты») длиннее места на узком экране; ряд шапки один
    // (critical.css), поэтому кегль подписи уменьшается ровно настолько, чтобы она
    // встала целиком, — не мельче 12 px (остаток подрезает overflow-x:clip). Сердечко
    // (::after) — фиксированной ширины, его из пропорции вычитаем. Ширина подписи
    // меняется уже после этого скрипта (шрифт Caveat, сердечко из отложенного app.css,
    // поворот экрана) — поэтому подгонку повторяет ResizeObserver; своя же запись
    // кегля его не зацикливает: после неё размер тот же, что в прошлый раз.
    var txt=hd.querySelector('.logo-text'),sub=hd.querySelector('.logo-sub');
    if(!txt||!sub)return;
    var fr=0;
    function fit(){
        fr=0;
        sub.style.fontSize='';
        var room=txt.clientWidth,need=sub.getBoundingClientRect().width;
        if(!room||need<=room+0.5)return;
        var a=getComputedStyle(sub,'::after'),heart=(parseFloat(a.width)||0)+(parseFloat(a.marginInlineStart)||0);
        var f=parseFloat(getComputedStyle(sub).fontSize)||20;
        sub.style.fontSize=Math.max(12,Math.floor(f*(room-heart)/(need-heart)*4)/4)+'px';
    }
    function later(){if(!fr)fr=requestAnimationFrame(fit)}
    fit();
    if(window.ResizeObserver){var ro=new ResizeObserver(later);ro.observe(txt);ro.observe(sub)}
    else{
        window.addEventListener('resize',later);
        if(document.fonts&&document.fonts.ready)document.fonts.ready.then(later);
    }
})();

// ── Referral capture (?ref=…) ──
// Куку ставит БРАУЗЕР, а не сервер, и это не прихоть. На RU-домене nginx режет
// Set-Cookie на всех HTML-страницах (`proxy_hide_header Set-Cookie` в
// `location /`) и кэширует ответ вместе с query-строкой: серверная кука со
// страницы инструмента не доехала бы никогда, а второй человек по той же
// ссылке не дошёл бы до PHP вообще. Поэтому раньше реферал работал ровно с
// одного адреса — голого корня, у которого свой `location = /` без стриппинга.
// Отсюда же plaintext-кука: JS не умеет шифровать по-ларавелевски, так что
// `timbrica_ref` вынесен из EncryptCookies (bootstrap/app.php).
(function(){
    var m=/[?&]ref=([A-Za-z0-9_-]{1,32})(?:&|$)/.exec(window.location.search||'');
    if(!m)return;
    var code=m[1];
    if(code===window.__REF_CODE)return; // свою же ссылку не считаем и не пишем
    try{
        document.cookie='timbrica_ref='+code+';path=/;max-age=2592000;samesite=lax'+
            (location.protocol==='https:'?';secure':'');
    }catch(e){}

    // Маячок «перешли по ссылке»: считаем ОТКРЫТИЕ, а не нажатие кнопки
    // «поделиться» — нажатие ничего не доказывает (человек мог закрыть шторку),
    // открытие доказывает. Сервер дедуплицирует по ip_hash; localStorage лишь
    // бережёт трафик на повторных заходах.
    function fire(){
        var seen='tmb_ref_seen:'+code;
        try{if(localStorage.getItem(seen))return;localStorage.setItem(seen,'1')}catch(e){}
        var payload=JSON.stringify({code:code});
        try{
            var blob=new Blob([payload],{type:'application/json'});
            if(navigator.sendBeacon&&navigator.sendBeacon('/api/ref/visit',blob))return;
        }catch(e){}
        try{fetch('/api/ref/visit',{method:'POST',headers:{'Content-Type':'application/json'},body:payload,keepalive:true}).catch(function(){})}catch(e){}
    }
    // Chrome-пререндер выполняет скрипты страницы, которую человек ещё не
    // открыл — тот же капкан, что уже накручивал «использовал тул».
    if(document.prerendering)document.addEventListener('prerenderingchange',fire,{once:true});
    else fire();
})();

// ── window.__revealInBox(el) — показать строку внутри ЕЁ контейнера ──
//
// ⚠️⚠️ `scrollIntoView({block:'nearest'})` НЕ ЗНАЧИТ «не трогать страницу».
// `nearest` значит «не прокручивать, ЕСЛИ элемент уже видно»; а если
// контейнер со строкой ушёл за нижний край окна, браузер прокрутит ОКНО,
// чтобы его показать. Глобальное правило запрещает `scrollIntoView()` без
// `nearest`, и из этого легко вывести, что `nearest` — безопасная форма.
// Она не безопасная: на 2026-09-01 так написаны 246 вызовов в 164 файлах,
// то есть весь проект.
//
// Что это стоило. Жалоба на /checkers (Егоров Николай егорович, 29.08, одна
// звезда): «Потому что бегает шашечная доска. Из этого пустая трата времени
// на поправку доски. Всё время вверх и вниз.» Замер на телефоне 360×740,
// доска поставлена по центру экрана, партия против бота
// (tests/playwright/checkers-board-does-not-run-away.mjs):
//
//   как есть                        8 прыжков за 20 ходов, 364 px
//   человек возвращает доску        −52 · +52 · −52 · +52 · −52 · +52 …
//   scrollIntoView обезврежен       0 прыжков, 0 px  (47 вызовов перехвачено)
//
// Последняя строка — решающий опыт: причина названа, а не угадана.
//
// ⚠️ Стандартный прибор этого НЕ ВИДИТ. Прокрутка — не layout shift, и CLS у
// /checkers 0.028 при среднем по сайту 0.0282, то есть ровно норма. Метрика
// говорила «всё хорошо» ровно там, откуда человек ушёл с одной звездой.
//
// Передняя дверь для нового кода — эта функция: она двигает ТОЛЬКО прокрутку
// прокручиваемых предков и физически не может задеть окно. Возвращает true,
// если такой предок нашёлся.
window.__revealInBox=function(el,opts){
    if(!el||!el.getBoundingClientRect)return false;
    var pad=(opts&&typeof opts.pad==='number')?opts.pad:4;
    var boxes=window.__revealInBox._boxes(el);
    if(!boxes.length)return false;
    for(var i=0;i<boxes.length;i++){
        var b=boxes[i].box,cr,er;
        if(boxes[i].y){
            cr=b.getBoundingClientRect();er=el.getBoundingClientRect();
            if(er.top<cr.top+pad)b.scrollTop-=(cr.top+pad-er.top);
            else if(er.bottom>cr.bottom-pad)b.scrollTop+=(er.bottom-(cr.bottom-pad));
        }
        if(boxes[i].x){
            cr=b.getBoundingClientRect();er=el.getBoundingClientRect();
            if(er.left<cr.left+pad)b.scrollLeft-=(cr.left+pad-er.left);
            else if(er.right>cr.right-pad)b.scrollLeft+=(er.right-(cr.right-pad));
        }
    }
    return true;
};
// Прокручиваемые предки ДО документа — сам документ сюда не попадает никогда,
// и в этом вся суть: окно не наше дело.
window.__revealInBox._boxes=function(el){
    var out=[],n=el&&el.parentElement,hops=0;
    while(n&&n!==document.body&&n!==document.documentElement&&hops++<40){
        var cs=null;try{cs=getComputedStyle(n);}catch(e){break;}
        if(cs){
            var oy=cs.overflowY,ox=cs.overflowX;
            var sy=(oy==='auto'||oy==='scroll')&&n.scrollHeight>n.clientHeight+1;
            var sx=(ox==='auto'||ox==='scroll')&&n.scrollWidth>n.clientWidth+1;
            if(sy||sx)out.push({box:n,y:sy,x:sx});
        }
        n=n.parentElement;
    }
    return out;
};
// Сетка для 180 существующих молчаливых вызовов — тот же приём, что у
// `__downloadFile` с его перехватчиком `<a download>`: передняя дверь для
// нового кода, сетка для написанного раньше. Включается, только когда сошлись
// ТРИ условия:
//
//   1. вызов просит `block:'nearest'` — то есть сам заявляет «лишнего не двигай»;
//   2. у элемента есть прокручиваемый предок — то есть он живёт в своём
//      контейнере, и вся задача контейнерная;
//   3. движение НЕ анимированное (`behavior:'smooth'` не просили).
//
// Третье условие — не перестраховка, а разделитель по смыслу. `smooth` ставят
// тогда, когда движение должно быть ЗАМЕЧЕНО: указатель «файл вот здесь»
// (`__resultCue`) и подсказка зоны загрузки прокручивают страницу ПО ТАПУ, и
// глобальное правило это прямо разрешает. Таких вызовов 66; их сетка не
// трогает. Остальные 180 — молчаливые, на каждой перерисовке, и это ровно тот
// класс, который уводил доску из-под руки.
(function(){
    var native=Element.prototype&&Element.prototype.scrollIntoView;
    if(!native||Element.prototype.__tmbSivNet)return;
    Element.prototype.scrollIntoView=function(arg){
        try{
            if(arg&&typeof arg==='object'&&arg.block==='nearest'&&arg.behavior!=='smooth'
               &&window.__revealInBox._boxes(this).length){
                window.__revealInBox(this);
                window.__sivNetHits=(window.__sivNetHits||0)+1;
                return;
            }
        }catch(e){}
        return native.apply(this,arguments);
    };
    Element.prototype.__tmbSivNet=true;
})();

// ── window.__withRef(url) ──
// Добавляет к ссылке реф-код залогиненного посетителя. Одна реализация на весь
// сайт: ею пользуются и модалка «поделиться», и комнатные тулы (звонки, рации,
// экран, радионяня), где ссылка-приглашение — самый естественный способ
// привести человека. Для гостя и для страницы, где ref уже стоит, — no-op:
// чужая метка не перетирается, своя не навязывается.
window.__withRef=function(url){
    var code=window.__REF_CODE;
    if(!code||!url)return url;
    try{
        var u=new URL(url,window.location.origin);
        if(u.searchParams.has('ref'))return url;
        u.searchParams.set('ref',code);
        return u.toString();
    }catch(e){return url;}
};

// ── Share modal (deferred) ──
(function(){var _rIC=window.requestIdleCallback||function(cb){setTimeout(cb,200)};_rIC(function(){
    var btn=document.getElementById('shareBtn');
    var overlay=document.getElementById('shareOverlay');
    var modal=overlay?overlay.querySelector('.share-modal'):null;
    var closeBtn=document.getElementById('shareClose');
    var urlInput=document.getElementById('shareUrl');
    var copyBtn=document.getElementById('shareCopy');

    /* The shared URL carries the sharer's referral code when they are signed in.
       Without it the button handed out a bare address and nobody could ever be
       credited — the program had 0 referred users while being fully live.
       Never overwrite an existing ?ref (someone else's link stays theirs), and
       never touch anchors/other params. */
    function withRef(url){ return window.__withRef ? window.__withRef(url) : url; }

    function getShareData(){
        var t=document.title.replace(/ — Timbrica$| \| Timbrica$/,'').trim();
        return {title:t||'Timbrica',text:t,url:withRef(window.location.href)};
    }

    function openModal(){
        var d=getShareData();
        var u=d.url;var t=d.title;var txt=d.text+' '+u;
        if(urlInput)urlInput.value=u;
        var e=encodeURIComponent;
        var el;
        el=document.getElementById('shareTelegram');if(el)el.href='https://t.me/share/url?url='+e(u)+'&text='+e(t);
        el=document.getElementById('shareWhatsapp');if(el)el.href='https://wa.me/?text='+e(txt);
        el=document.getElementById('shareTwitter');if(el)el.href='https://twitter.com/intent/tweet?url='+e(u)+'&text='+e(t);
        el=document.getElementById('shareFacebook');if(el)el.href='https://www.facebook.com/sharer/sharer.php?u='+e(u);
        el=document.getElementById('shareLinkedin');if(el)el.href='https://www.linkedin.com/sharing/share-offsite/?url='+e(u);
        el=document.getElementById('shareReddit');if(el)el.href='https://www.reddit.com/submit?url='+e(u)+'&title='+e(t);
        el=document.getElementById('shareKakao');if(el)el.href='https://sharer.kakao.com/talk/friends/picker/link?url='+e(u);
        el=document.getElementById('shareLine');if(el)el.href='https://social-plugins.line.me/lineit/share?url='+e(u);
        el=document.getElementById('shareEmail');if(el)el.href='mailto:?subject='+e(t)+'&body='+e(txt);
        var loc=window.__LOCALE||'en';
        el=document.getElementById('shareKakao');if(el)el.style.display=(loc==='ko')?'':'none';
        el=document.getElementById('shareLine');if(el)el.style.display=(loc==='id'||loc==='ko')?'':'none';
        el=document.getElementById('shareMore');
        if(el&&navigator.share)el.style.display='';
        overlay.classList.add('active');
        overlay.setAttribute('aria-hidden','false');
        if(modal)shareRelease=window.__focusTrap.trap(modal,closeModal);
    }

    var shareRelease=null;
    function closeModal(){
        if(shareRelease){shareRelease();shareRelease=null;}
        overlay.classList.remove('active');
        overlay.setAttribute('aria-hidden','true');
        if(copyBtn){copyBtn.textContent=copyBtn.dataset.label;copyBtn.classList.remove('copied');}
    }

    if(btn)btn.addEventListener('click',function(){
        if(navigator.share&&/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)){
            navigator.share(getShareData()).catch(function(){});
            return;
        }
        openModal();
    });

    if(closeBtn)closeBtn.addEventListener('click',closeModal);
    if(overlay)overlay.addEventListener('click',function(e){
        if(e.target===overlay)closeModal();
    });

    if(copyBtn)copyBtn.addEventListener('click',function(){
        var u=urlInput?urlInput.value:window.location.href;
        var _lbl=copyBtn.dataset.label,_cpd=copyBtn.dataset.copied;
        if(navigator.clipboard){
            navigator.clipboard.writeText(u).then(function(){
                copyBtn.textContent=_cpd;copyBtn.classList.add('copied');
                setTimeout(function(){copyBtn.textContent=_lbl;copyBtn.classList.remove('copied');},2000);
            });
        }else{
            urlInput.select();document.execCommand('copy');
            copyBtn.textContent=_cpd;copyBtn.classList.add('copied');
            setTimeout(function(){copyBtn.textContent=_lbl;copyBtn.classList.remove('copied');},2000);
        }
    });

    var moreBtn=document.getElementById('shareMore');
    if(moreBtn)moreBtn.addEventListener('click',function(e){
        e.preventDefault();
        if(navigator.share){closeModal();navigator.share(getShareData()).catch(function(){});}
    });

    var toolShareBtn=document.getElementById('toolShareBtn');
    if(toolShareBtn)toolShareBtn.addEventListener('click',function(){
        if(navigator.share&&/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)){
            navigator.share(getShareData()).catch(function(){});
            return;
        }
        openModal();
    });

});})();

// ── Keyboard shortcuts (deferred) ──
(function(){var _rIC=window.requestIdleCallback||function(cb){setTimeout(cb,200)};_rIC(function(){
    /* На WINDOW, а не на document: пузырь доходит до window ПОСЛЕ всех
       обработчиков документа, и `defaultPrevented` честно говорит, что тул уже
       взял клавишу сам. Прежде (document + requestIdleCallback) этот общий
       «пробел = Play» шёл СЛЕДОМ за обработчиком тула и нажимал ту же кнопку
       второй раз: на /trim пробел включал звук и тут же его выключал (замер
       15.10.2026: с этим обработчиком — пауза, без него — играет). «Чей фокус» —
       общий примитив __keyOwnedByFocus, а не тег: после ползунка или клика по
       галочке пробел здесь тоже молчал. Разбор — key-owned-by-focus.md. */
    window.addEventListener('keydown',function(e){
        if(e.defaultPrevented)return;
        // A keyboard-driven tool (e.g. a game) can claim every key by setting
        // body[data-kbd-capture]; bail so Space/Escape don't hijack its controls.
        if(document.body&&document.body.hasAttribute('data-kbd-capture'))return;
        var owned;
        if(typeof window.__keyOwnedByFocus==='function')owned=window.__keyOwnedByFocus(e);
        else{
            var tag=document.activeElement?document.activeElement.tagName:'';
            owned=tag==='INPUT'||tag==='TEXTAREA'||tag==='SELECT'||(document.activeElement&&document.activeElement.isContentEditable);
        }
        if(e.code==='Space'&&!owned){
            // Зажатый пробел шлёт повторы: страницу не листаем, кнопку не перещёлкиваем.
            var ids=['play-btn','rvPlayBtn','spPlayBtn','ebPlayBtn','agPlayBtn','ckPlayBtn','amPlayBtn','laPlayBtn','mgPlayBtn','playBtn'];
            for(var i=0;i<ids.length;i++){
                var b=document.getElementById(ids[i]);
                if(b&&b.offsetParent!==null){e.preventDefault();if(!e.repeat)b.click();return;}
            }
            var btns=document.querySelectorAll('.play-btn,.sp-player-btn,[data-action="play"]');
            for(var j=0;j<btns.length;j++){if(btns[j].offsetParent!==null){e.preventDefault();if(!e.repeat)btns[j].click();return;}}
        }
        if(e.key==='Escape'){
            var overlay=document.querySelector('.share-overlay.active');
            if(overlay){var cl=document.getElementById('shareClose');if(cl)cl.click();return;}
            var progOver=document.getElementById('progressOverlay');
            if(progOver&&progOver.style.display!=='none'){var cancelBtn=progOver.querySelector('[id*="cancel"],[id*="Cancel"]');if(cancelBtn)cancelBtn.click();return;}
        }
    });
});})();

// ── Download toast ──
window.addEventListener('file-downloaded',function(){var t=document.getElementById('dl-toast');if(!t)return;t.classList.add('visible');clearTimeout(t._h);t._h=setTimeout(function(){t.classList.remove('visible')},5000)});
window.showDownloadToast=function(){window.dispatchEvent(new CustomEvent('file-downloaded'))};

// ── Offline bar (confirmed-offline only) ──
// `navigator.onLine === false` is NOT trustworthy on mobile: Yandex/Android,
// in-app WebViews and data-saver proxies report false-negatives even with full
// connectivity. The old code toggled the "Нет подключения" bar straight off
// navigator.onLine and only cleared it on an 'online' event — which never fires
// if the browser already considers itself online and merely mis-reported at
// load. Result: a fully-online user (page loaded, ads loaded, tool worked) saw
// a permanent "no connection" alarm (bug report 2026-06-13, channel-converter,
// sid 6c208fcb — user reported it then successfully converted two files).
//
// onLine===true IS reliable → treat as online immediately. onLine===false is
// only a *hint*: confirm with a real same-origin probe before alarming. The
// probe is biased toward "online" (any resolved fetch — even SW-cached — counts
// as connected) so we never raise a false alarm; a genuinely offline device
// (probe rejects/times out) still gets the bar, and while the bar is up we
// re-probe so a transient blip self-heals without needing a user action.
(function(){
    function init(){
        var bar=document.getElementById('offlineBar');
        if(!bar)return;
        var ratingForm=document.querySelector('.rating-form');
        var probing=false, shownOffline=false, healInterval=null;
        function setBar(offline){
            shownOffline=offline;
            bar.classList.toggle('is-visible',offline);
            if(ratingForm)ratingForm.style.display=offline?'none':'';
            if(offline){
                if(!healInterval)healInterval=setInterval(probe,12000); // self-heal transient false-positives
            }else if(healInterval){clearInterval(healInterval);healInterval=null;}
        }
        function probe(){
            if(navigator.onLine){setBar(false);return;} // onLine true = reliably online
            if(probing)return; probing=true;
            var done=false;
            var ctrl=('AbortController'in window)?new AbortController():null;
            var to=setTimeout(function(){if(done)return;done=true;probing=false;if(ctrl)try{ctrl.abort();}catch(_){}setBar(true);},4000);
            // Static, DB-free, tiny (169 B). cache:'no-store' asks for a fresh hit;
            // if the SW serves it from cache we still resolve = treat as online.
            fetch('/favicon.ico?_o='+Date.now(),{cache:'no-store',signal:ctrl?ctrl.signal:undefined})
                .then(function(){if(done)return;done=true;clearTimeout(to);probing=false;setBar(false);})
                .catch(function(){if(done)return;done=true;clearTimeout(to);probing=false;setBar(!navigator.onLine);});
        }
        window.addEventListener('online',function(){setBar(false);});
        window.addEventListener('offline',probe);
        document.addEventListener('visibilitychange',function(){if(!document.hidden&&shownOffline)probe();});
        window.addEventListener('focus',function(){if(shownOffline)probe();});
        probe();
    }
    if(document.readyState!=='loading')init();
    else document.addEventListener('DOMContentLoaded',init);
})();

// ── Tool actions 3-dot menu (single HTML, CSS switches layout) ──
(function(){
    var menu=document.getElementById('toolActionsMenu');
    var dots=document.getElementById('toolActionsDots');
    if(!menu||!dots)return;
    dots.addEventListener('click',function(e){
        e.stopPropagation();
        menu.classList.toggle('open');
    });
    document.addEventListener('click',function(e){
        if(!menu.contains(e.target))menu.classList.remove('open');
        // Close on backdrop click (::before covers viewport, click target = menu itself)
        if(e.target===menu&&menu.classList.contains('open'))menu.classList.remove('open');
    });
    // Close dropdown after any button click inside
    menu.querySelectorAll('.ta-btn').forEach(function(btn){
        btn.addEventListener('click',function(){menu.classList.remove('open');});
    });
    // Close dropdown on resize past breakpoint.
    // MediaQueryList is NOT an EventTarget on WebKit < iOS/Safari 14 — only the
    // deprecated addListener exists. An unguarded addEventListener here threw at
    // top level and ABORTED the rest of this file, so every global defined below
    // (__safeFullscreen, __safeResume, __capabilityGate, FormatSmart stub, …)
    // never existed on legacy iPads → "window.__safeFullscreen is not a function"
    // on tool pages (TIMBRICA-FRONTEND-AM). Same guard as the dark-theme MQL above.
    var mq=window.matchMedia('(max-width:1024px)');
    var mqClose=function(){menu.classList.remove('open');};
    if(mq.addEventListener) mq.addEventListener('change',mqClose);
    else if(mq.addListener) mq.addListener(mqClose);
})();

// ── FormatSmart pass-through stub — ПЕРЕЕХАЛА В layout ──
// Заглушка страхует случай «format-smart.js не доехал», поэтому жить во
// внешнем файле она не может: этот файл сам не доезжает 283 раза за 30 дней
// (своя телеметрия resource_error, RU), а её место — строка 4921 из 6816 —
// уносил любой бросок выше (TIMBRICA-FRONTEND-AM). Теперь она инлайном в
// <head> layout'а. Сюда её не возвращать: две копии одной страховки
// расходятся молча. Сторож — tests/playwright/format-smart-stub.mjs.

// ── AdBlock detection + FFmpeg init error helper ──
// When FFmpeg fails to load, detect likely cause and show specific guidance.
(function(){
    window.__detectAdBlock=function(cb){
        /* One owner for "is content blocking active": js/reach-probe.js, which
           verifies the PAYLOAD of the ad SDK (a blocker answers famous ad URLs
           with a surrogate stub, and a stub fires onload — measured across
           uBO / uBO Lite / AdGuard / ABP on 2026-09-01).

           What stood here injected a 1px div carrying the ad-network class
           names (spelled out in AdBaitClassnamesLintTest, not here — the lint
           now scans this file, and a literal example in a comment reads to it
           exactly like the real thing).
           That is the spoofed-slot pattern AdSense flagged in the 2026-05-12
           invalid-traffic limitation, and the pattern AdBaitClassnamesLintTest
           forbids — the lint only ever scanned blades, so this copy in JS was
           invisible to it. The lint now scans public/js too.

           Inconclusive (offline, probe unreachable) answers FALSE on purpose:
           this callback decides whether to tell someone "your ad blocker is
           breaking the page", and that accusation must be earned. The generic
           "check your connection" message is also the correct one offline. */
        var answered=false;
        function done(v){ if(answered)return; answered=true; try{cb(!!v)}catch(_){} }
        function ask(){
            try{
                window.__reachProbe.run({timeoutMs:3000}).then(function(r){
                    done(r && r.verdict==='blocking');
                },function(){ done(false); });
            }catch(_){ done(false); }
        }
        if(window.__reachProbe) return ask();
        var s=document.createElement('script');
        /* Versioned: nginx serves /js/*.js immutable for 180 days, and a script
           inserted BY JS carries no @light stamp of its own. */
        s.src=(typeof window.__jsSrc==='function')?window.__jsSrc('/js/reach-probe.js'):'/js/reach-probe.js';
        s.onload=function(){ window.__reachProbe?ask():done(false); };
        s.onerror=function(){ done(false); };
        setTimeout(function(){ done(false); },6000);
        document.head.appendChild(s);
    };
    /* Nuclear option — unregister all Service Workers + drop every Cache Storage
       entry, then reload. Used when a plain location.reload() keeps showing the
       same error because the SW is still serving a truncated/corrupted script
       from its cache. Exposed so any tool page can call it directly. */
    window.__hardReset=function(){
        var run=function(){
            var jobs=[];
            try{
                if(navigator.serviceWorker&&navigator.serviceWorker.getRegistrations){
                    jobs.push(navigator.serviceWorker.getRegistrations().then(function(rs){
                        return Promise.all(rs.map(function(r){return r.unregister()}));
                    }).catch(function(){}));
                }
            }catch(_){}
            try{
                if(window.caches&&caches.keys){
                    jobs.push(caches.keys().then(function(ks){
                        return Promise.all(ks.map(function(k){return caches.delete(k)}));
                    }).catch(function(){}));
                }
            }catch(_){}
            Promise.all(jobs).then(function(){
                /* Bypass HTTP cache with a cache-busting query string */
                try{
                    var u=new URL(location.href);
                    u.searchParams.set('_r',Date.now());
                    location.replace(u.toString());
                }catch(_){ location.reload(); }
            });
        };
        /* Don't leave the user staring at a dead modal — show a brief spinner */
        var busy=document.createElement('div');
        busy.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.7);color:#fff;font-size:14px';
        busy.textContent='…';
        document.body.appendChild(busy);
        run();
    };
    /* Enhanced INIT_ERROR message — shown as modal dialog, not just toast */
    window.__showInitError=function(){
        var L=window.__LOCALE||'en';
        /* Count attempts per session — after the first failed reload, promote the
           hard-reset button to primary since a plain reload clearly didn't help. */
        var attempts=0;
        try{ attempts=parseInt(sessionStorage.getItem('__initErr')||'0',10)||0; }catch(_){}
        try{ sessionStorage.setItem('__initErr',String(attempts+1)); }catch(_){}
        window.__detectAdBlock(function(adblocked){
            var title={en:'Processing engine failed to load',ru:'Не удалось загрузить обработчик',ko:'처리 엔진 로드 실패',ar:'فشل تحميل محرك المعالجة',id:'Gagal memuat mesin pemrosesan',pt:'Falha ao carregar o processador',de:'Verarbeitungs-Engine konnte nicht geladen werden',es:'No se pudo cargar el procesador',fr:'Impossible de charger le moteur de traitement',uk:'Не вдалося завантажити обробник',tr:'İşleme motoru yüklenemedi'}[L]||'Processing engine failed to load';
            var msg;
            if(adblocked){
                msg={en:'Your ad blocker is preventing the processing engine from loading. Disable AdBlock/uBlock for this site and reload the page.',ru:'Блокировщик рекламы мешает загрузке. Отключите AdBlock/uBlock для этого сайта и обновите страницу.',ko:'광고 차단기가 로딩을 방해합니다. 이 사이트에서 AdBlock/uBlock을 비활성화하고 페이지를 새로고침하세요.',ar:'مانع الإعلانات يمنع التحميل. قم بتعطيل AdBlock/uBlock لهذا الموقع وأعد تحميل الصفحة.',id:'Ad blocker menghalangi pemuatan. Nonaktifkan AdBlock/uBlock untuk situs ini dan muat ulang halaman.',pt:'O bloqueador de anúncios está impedindo o carregamento. Desative o AdBlock/uBlock para este site e recarregue a página.',de:'Ein Werbeblocker verhindert das Laden. Deaktivieren Sie AdBlock/uBlock für diese Seite und laden Sie neu.',es:'Un bloqueador de anuncios impide la carga. Desactiva AdBlock/uBlock para este sitio y recarga la página.',fr:'Un bloqueur de publicités empêche le chargement. Désactivez AdBlock/uBlock pour ce site et rechargez la page.',uk:'Блокувальник реклами не дає завантажити обробник. Вимкніть AdBlock/uBlock для цього сайту та перезавантажте сторінку.',tr:'Reklam engelleyiciniz işleme motorunun yüklenmesini engelliyor. Bu site için AdBlock/uBlock’u devre dışı bırakın ve sayfayı yeniden yükleyin.'}[L]||'Your ad blocker is preventing the processing engine from loading. Disable AdBlock/uBlock for this site and reload the page.';
            }else{
                msg={en:'Check your internet connection and try reloading the page. If the problem persists, try a different browser (Chrome, Edge).',ru:'Проверьте интернет-соединение и попробуйте обновить страницу. Если проблема повторяется, попробуйте другой браузер (Chrome, Edge).',ko:'인터넷 연결을 확인하고 페이지를 새로고침하세요. 문제가 계속되면 다른 브라우저(Chrome, Edge)를 사용해 보세요.',ar:'تحقق من اتصالك بالإنترنت وأعد تحميل الصفحة. إذا استمرت المشكلة، جرب متصفحًا آخر (Chrome, Edge).',id:'Periksa koneksi internet dan coba muat ulang halaman. Jika masalah berlanjut, coba browser lain (Chrome, Edge).',pt:'Verifique sua conexão com a internet e recarregue a página. Se o problema persistir, tente outro navegador (Chrome, Edge).',de:'Prüfen Sie Ihre Internetverbindung und laden Sie die Seite neu. Falls das Problem bestehen bleibt, versuchen Sie einen anderen Browser (Chrome, Edge).',es:'Comprueba tu conexión a internet y recarga la página. Si el problema persiste, prueba otro navegador (Chrome, Edge).',fr:'Vérifiez votre connexion Internet et rechargez la page. Si le problème persiste, essayez un autre navigateur (Chrome, Edge).',uk:'Перевірте зʼєднання з інтернетом і перезавантажте сторінку. Якщо не допомогло — спробуйте інший браузер (Chrome, Edge).',tr:'İnternet bağlantınızı kontrol edin ve sayfayı yeniden yüklemeyi deneyin. Sorun devam ederse farklı bir tarayıcı deneyin (Chrome, Edge).'}[L]||'Check your internet connection and try reloading the page. If the problem persists, try a different browser (Chrome, Edge).';
            }
            var L_reload={en:'Reload page',ru:'Обновить страницу',ko:'페이지 새로고침',ar:'إعادة تحميل الصفحة',id:'Muat ulang halaman',pt:'Recarregar página',de:'Seite neu laden',es:'Recargar página',fr:'Recharger la page',uk:'Перезавантажити сторінку',tr:'Sayfayı yenile'}[L]||'Reload page';
            var L_reset={en:'Clear cache & reload',ru:'Очистить кеш и перезагрузить',ko:'캐시 지우고 새로고침',ar:'مسح التخزين المؤقت وإعادة التحميل',id:'Hapus cache dan muat ulang',pt:'Limpar cache e recarregar',de:'Cache leeren und neu laden',es:'Borrar caché y recargar',fr:'Vider le cache et recharger',uk:'Очистити кеш і перезавантажити',tr:'Önbelleği temizle ve yeniden yükle'}[L]||'Clear cache & reload';
            var L_hint={en:'If a plain reload does not help — clear the cache.',ru:'Если обычная перезагрузка не помогает — очистите кеш.',ko:'일반 새로고침이 도움이 되지 않으면 캐시를 지우세요.',ar:'إذا لم تساعد إعادة التحميل العادية — امسح التخزين المؤقت.',id:'Jika reload biasa tidak membantu — hapus cache.',pt:'Se o reload normal não ajudar — limpe o cache.',de:'Wenn normales Neuladen nicht hilft — leeren Sie den Cache.',es:'Si la recarga normal no ayuda — borra la caché.',fr:'Si le rechargement normal ne suffit pas — videz le cache.',uk:'Якщо звичайне перезавантаження не допомагає — очистіть кеш.',tr:'Basit bir yenileme yardımcı olmuyorsa — önbelleği temizleyin.'}[L]||'If a plain reload does not help — clear the cache.';
            var ov=document.createElement('div');
            ov.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.5);backdrop-filter:blur(6px)';
            var d=document.createElement('div');
            d.style.cssText='background:var(--bg-primary,#1a1a2e);border:1px solid var(--border,#333);border-radius:16px;padding:24px;max-width:400px;width:90%;color:var(--text-primary,#fff);text-align:center';
            /* Promote hard-reset to primary after the second attempt */
            var primaryIsReset=attempts>=1;
            var primaryStyle='padding:12px 24px;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;width:100%;color:#fff;background:var(--accent-gradient,#38bdf8)';
            var secondaryStyle='margin-top:10px;padding:10px 24px;border:1px solid var(--border,#333);border-radius:10px;font-size:13px;font-weight:500;cursor:pointer;width:100%;color:var(--text-secondary,#aaa);background:transparent';
            d.innerHTML='<div style="font-size:36px;margin-bottom:10px">'+(adblocked?'🛡️':'⚠️')+'</div>'+
                '<div style="font-size:16px;font-weight:600;margin-bottom:8px">'+title+'</div>'+
                '<div style="font-size:13px;color:var(--text-muted);margin-bottom:16px">'+msg+'</div>'+
                (primaryIsReset
                    ? '<button id="__ie_reset" style="'+primaryStyle+'">'+L_reset+'</button>'+
                      '<button id="__ie_reload" style="'+secondaryStyle+'">'+L_reload+'</button>'
                    : '<button id="__ie_reload" style="'+primaryStyle+'">'+L_reload+'</button>'+
                      '<button id="__ie_reset" style="'+secondaryStyle+'">'+L_reset+'</button>'+
                      '<div style="margin-top:10px;font-size:12px;color:var(--text-muted)">'+L_hint+'</div>'
                );
            ov.appendChild(d);document.body.appendChild(ov);
            d.querySelector('#__ie_reload').addEventListener('click',function(){ location.reload(); });
            d.querySelector('#__ie_reset').addEventListener('click',function(){ window.__hardReset(); });
            ov.addEventListener('click',function(e){if(e.target===ov)document.body.removeChild(ov);});
        });
    };
    /* The site-wide «Ad blocker detected — some tools may not work» bar that
       lived here was removed 2026-09-02 (bug report /en/contact: «Remove ad
       blocker warning»). Three measured reasons, none of them taste:
         · it ran the probe on EVERY page view for everyone — 79 229 probes in
           63 129 sessions over 36 h on RU — although its own comment promised
           «once per session»;
         · its premise did not hold: sessions with a blocker finished with a
           file at 52.9% against 57.8% without (intl, 119 vs 512 sessions) —
           the site already engineers around filter lists (neutral asset paths,
           script fallbacks), so «tools may not work» was a revenue ask dressed
           as a warning;
         · 45% of RU «blocking» verdicts were Google's ad host timing out inside
           Russia, and 7 500 of 13 085 RU sessions got BOTH verdicts in one
           visit — a cache hit of the real SDK read as an interception.
       The accusation stays where a failure is actually observed: __showInitError
       above (the processing engine did not load) and /privacy-audit (the person
       asked). Do not reintroduce a blanket bar without a measured breakage. */
})();

// ── Pre-register stubs for queue-style shared modules ──
// Run at the END of timbrica-core.js. timbrica-core.js is the FIRST deferred
// shared script in document order (layouts/app.blade.php loads it before any
// other defer script), so by the time channel-module / tool-uploader / etc
// run their own load (also defer), the stubs are guaranteed in place. This
// closes the defer-onerror race documented at the helper definition above.
__sharedModule('ChannelModule', {
    init: undefined, detect: undefined, updateForFormat: undefined,
    getArgs: [], getValue: '', getLayout: null, getDetected: null,
    supportsSurround: true, getUpmixTarget: null,
});
// UpmixModule (stereo → 5.1/7.1 matrix upmix). Every query answers "no upmix",
// so a page whose module script hasn't landed yet degrades to honest stereo
// instead of advertising surround it cannot synthesise.
__sharedModule('UpmixModule', {
    isUpmix: false, graph: '', ffmpegArgs: [], ffmpegArgsWithVideo: [],
});
__sharedModule('ToolUploader', {
    wire: undefined, scan: undefined,
    inject: false, // returns boolean — false = injection failed (callers handle)
    // Same contract as inject: false = "there was nothing to clear / the zone
    // isn't wired yet". A tool calling clear() before hydration is asking to be
    // in the empty state, which is where it already is — so the stub's no-op is
    // the honest answer, not a swallowed error.
    clear: false,
    // accepts(file, acceptAttr) — the accept matcher, shared with the
    // page-level folder-drop handler so a walked folder is filtered by exactly
    // the rule a zone would use. Pre-init default is `true`: a folder dropped
    // before the uploader lands should hand the files to the tool and let its
    // own accept gate refuse them, rather than silently discarding a folder the
    // tool could have taken.
    accepts: true,
    // neededKind(acceptAttr) — which noun a rejection should use. Pre-init
    // default is 'file', the catch-all the real classifier itself falls back
    // to: before the module lands we genuinely do not know whether the zone
    // wants audio, a subtitle track or a 3D model, and "a compatible file" is
    // the one answer that is never wrong.
    neededKind: 'file',
    // kindOf(file) — what the zone decided this file IS, from its bytes where
    // the OS stayed silent. Pre-init default is '' («no zone has answered»),
    // which is exactly what the real function returns for a file that never
    // came through a zone: a caller must treat '' as «decide for yourself»,
    // and a stub that guessed a kind here would be a worse lie than silence.
    kindOf: '',
    // identify(bytes) / repair(file) — the byte-level identification and the
    // name/type repair the zone performs before it hands a file over. null is
    // the real functions' own answer for «nothing to say about this file», so a
    // caller that runs early behaves as if the file were already well described
    // — the state it was in before any of this existed.
    identify: null,
    repair: null,
});
// AudioDecoderRobust (renamed 2026-05-22 from AudioDecoder to avoid
// colliding with the native WebCodecs `AudioDecoder` class — see
// audio-decoder.js:431 comment).
__sharedModule('AudioDecoderRobust', {
    robust: '__defer_promise__',
    // robustRange (22.09.2026): a time WINDOW of a file — the clip-audio door
    // of /slideshow-maker and /video-merge. Decodes, so it queues like robust.
    robustRange: '__defer_promise__',
    // Bounded-head decode API (v1.3.0, 2026-07-11 long-recording OOM class).
    // decodeHead queues like robust. The two estimators are PURE and may be
    // called at upload time before the real script lands — the stub mirrors
    // the real math (duration-first, conservative size ratios) so the
    // bounded-vs-whole decision is identical either way.
    decodeHead: '__defer_promise__',
    // decodeWithin does real decoding work, so it queues like robust/decodeHead
    // rather than answering from the stub. Its callers are result-time panels;
    // the upload-time decision uses the two pure estimators below.
    decodeWithin: '__defer_promise__',
    // probeDuration / decodeGated (2026-09-12, support #6831): the pre-decode
    // door. Both read bytes or decode, so they queue until the real module
    // lands — a tool that hands a file to the stub gets the real verdict, late,
    // never an early «fits».
    probeDuration: '__defer_promise__',
    decodeGated: '__defer_promise__',
    decodeBudgetMB: function() {
        // ⚠️ This used to read `navigator.deviceMemory || 8`, which is the
        // exact line the real implementation removed: WebKit does not ship
        // deviceMemory at all, so an iPhone answered 384 MB here — the budget
        // a 32 GB workstation gets — while the real module, once loaded, said
        // 192. A stub that disagrees with its implementation is worse than no
        // stub: it is the high-memory answer, handed out for the window in
        // which the page is least able to survive being wrong.
        var gb = (window.__memBudget && window.__memBudget.gb)
            ? window.__memBudget.gb()
            : (navigator.deviceMemory || 8);
        return gb <= 4 ? 192 : 384;
    },
    estimateDecodedMB: function(fileOrBlob, durSec) {
        if (durSec > 0) return durSec * 48000 * 2 * 4 / 1048576;
        var sz = (fileOrBlob && fileOrBlob.size) || 0;
        var name = (fileOrBlob && fileOrBlob.name) || '';
        var mime = (fileOrBlob && fileOrBlob.type) || '';
        if (/\.(wav|aiff?|flac)(\W|$)/i.test(name) || /wav|aiff|flac/i.test(mime)) return sz * 2.5 / 1048576;
        if (/\.(ogg|opus|webm|amr|3gp[p2]?|spx)(\W|$)/i.test(name) || /ogg|opus|webm|amr|speex/i.test(mime)) return sz * 40 / 1048576;
        return sz * 11 / 1048576;
    },
    // Tier-0 memory gate (added to audio-decoder.js 2026-07-29 with the
    // music-mastering budget work; the stub was not updated, which red-lit
    // SharedModuleStubLeakTest and blocked every session's push).
    // headerAudioInfo reads bytes → async, queues like the others. Its
    // callers already treat a null result as "header unknown → fail open",
    // so deferring costs nothing.
    headerAudioInfo: '__defer_promise__',
    // memBudgetMB is PURE and may be consulted at upload time — mirror the
    // real math (floor 1024 MB, 1536 MB per GB of deviceMemory) so the
    // gate's verdict is identical whether or not the real script has landed.
    memBudgetMB: function() {
        var o = (typeof window !== 'undefined' && (window.__memBudgetMB | 0));
        if (o > 0) return o;
        // Same rule as the real one (audio-decoder.js memBudgetMB): the owner
        // of the number is window.__memBudget — a constrained iOS tab answers
        // 700 MB, everything else floor 1024 / 1536 per GB. The old mirror
        // read `navigator.deviceMemory || 8`, i.e. 12 GB on an iPhone.
        var B = window.__memBudget;
        if (B && typeof B.constrained === 'function' && B.constrained()) return 700;
        var gb = (B && typeof B.gb === 'function') ? B.gb() : (navigator.deviceMemory || 8);
        return Math.max(1024, gb * 1536);
    },
});
// AudioRecover predicates are sync and may be called in error-handling
// catch blocks before the real script loads. Safe defaults pick the
// "do nothing special" path: predicates return false (= "I don't know
// this is recoverable, fall through to the normal error UX"), which is
// the behaviour we want when the recovery module simply isn't there.
__sharedModule('AudioRecover', {
    tryRepair:           '__defer_promise__',
    isContainerError:    false,
    isAudioInput:        false,
    detectMoovMissing:   false,
    // Added 2026-06-02 to keep SharedModuleStubLeakTest green —
    // showUnrecoverableHint is a UX-only no-op when AudioRecover real
    // impl hasn't loaded yet (called from generic error handlers).
    showUnrecoverableHint: function(){},
});
// ApolloProcessor — AI music restoration (ai-music-cleaner tool). Heavy
// engine; stub queues warm/process until apollo-processor.js lands.
__sharedModule('ApolloProcessor', {
    warm:    '__defer_promise__',
    process: '__defer_promise__',
});
// QualityModule — format-aware quality/bit-depth selector. Used by audio-
// enhancer (pilot, 2026-05-06) and rolling out to ~50 tools that currently
// hardcode kbps options. Stub queues init() but synchronous predicates
// return their safe-default ([] for getArgs = "let codec defaults handle it").
__sharedModule('QualityModule', {
    init:        undefined,
    repopulate:  undefined,
    detectSource: '__defer_promise__',  // probes WAV/FLAC headers — async
    getArgs:     [],     // empty array = "no override, use codec defaults"
    getCurrentValue: null,
    getDetectedSource: { bitDepth: null, bitrate: null, isFloat: false },
    isLossless:  false,
    populateOptions: undefined,  // stateless multi-instance helper (action)
    argsFor:     [],             // stateless args producer (returns [])
});
// ResolutionModule — shared output-resolution selector for video tools.
// Same defer-onerror race protection as QualityModule. Sync defaults are the
// safe fall-throughs: no scale filter (=> tool's existing behaviour), output
// dims = source (=> no canvas resize), classification = '' (badge stays hidden).
__sharedModule('ResolutionModule', {
    init:                 undefined,
    detectSource:        '__defer_promise__', // creates <video>, waits for loadedmetadata
    setDetectedSource:    undefined,
    getDetectedSource:   { width: 0, height: 0, classification: '' },
    getCurrentValue:     'original',
    getOutputDimensions: { width: 0, height: 0, didDownscale: false, capped: false },
    getScaleFilter:       null,    // null = no -vf needed
    getScaleFilterChain:  null,    // pass-through when no extra filter chain
    getPipelineResolution: null,   // null = source size unknown, caller decides
    classify:             '',
    formatDimensions:     '',
    computeFor:          { width: 0, height: 0, didDownscale: false, capped: false },
    buildScaleFilter:     null,
});
// FFmpegProcessor — biggest shared module by API surface (21 public methods),
// loaded by ~150 tools. Same defer-onerror race surfaced via Yandex Browser
// (Sentry 116972319 / 116972119, 2026-05-02): tool catch-block called
// FFmpegProcessor.classifyError(err), tool init called probeAudioBitrate(file),
// both before the script finished loading.
//
// Sync defaults pick the safe fall-through path:
//   - isReady=false      → tools won't skip init they'd otherwise bypass
//   - isBrowserFormat=false → tools fall through to FFmpeg path instead of
//     a native-decode shortcut they'd otherwise pick (slower but correct)
//   - classifyError='unknown' → catch blocks show a generic message
//   - getCodecArgs=[]    → FFmpeg uses defaults (no -c:a/-c:v hints)
// Constant bags that tools index directly (e.g. `FFmpegProcessor.MIME_TYPES[ext]`).
// __sharedModule's "sync getter" mode returns the default WRAPPED in a function
// — wrong shape for direct subscripting. Inject as raw properties after the
// stub registers so `Stub.CODECS["mp3"]` returns undefined (safe) instead of
// throwing `Cannot read properties of undefined`.
__sharedModule('FFmpegProcessor', {
    // Promise-returning methods → deferred (resolves when real script drains queue)
    init:              '__defer_promise__',
    initWithProgress:  '__defer_promise__',
    reinit:            '__defer_promise__',
    process:           '__defer_promise__',
    processMulti:      '__defer_promise__',
    cacheFile:         '__defer_promise__',
    probe:             '__defer_promise__',
    probeAudioBitrate: '__defer_promise__',
    download:          '__defer_promise__',
    // Sync getters → safe defaults
    getCodecArgs:    [],
    getOutputExt:    'bin',
    classifyError:   'unknown',
    getErrorMessage: 'FFmpeg engine not loaded',
    isReady:         false,
    isBrowserFormat: false,
    // Sentry 117484834 (denoise → updateQualitySection on selectFormat) —
    // user changed output format on /ru/denoise BEFORE the FFmpeg script
    // had finished loading. Real impl is `e=>-1!==l.indexOf(e)` (true when
    // format is FLAC/WAV/ALAC/etc). Stub returns `false` — safe default
    // because the call site uses isLossless to GATE bitrate UI ("hide
    // bitrate selector for FLAC"); returning false keeps the bitrate
    // selector visible for one tick until the real impl drains, which
    // self-corrects when the queued call replays.
    isLossless:      false,
    // `getLastAudioBitrate` returns the last probed bitrate as int (kbps).
    // Tools call it post-probe to seed bitrate UI; pre-load returns 0 →
    // tools fall back to their default bitrate selector.
    getLastAudioBitrate: 0,
    // Action methods → queue, no return
    onProgress:  undefined,
    abort:       undefined,
    cancel:      undefined,
    reportError: undefined,
    // Rotation-fix methods (added as post-literal property assignments in
    // ffmpeg-processor.js — `FFmpegProcessor.X = function(){}` rather than
    // inside the `window.FFmpegProcessor = {...}` literal). Sentry 118272984
    // (/ru/video-to-gif Opera Mac, 2026-05-07) — call site fired before
    // ffmpeg-processor.js drained, hit the stub without these keys, threw
    // `processWithRotationFix is not a function`. Lint test was blind to
    // post-literal assignments — extended in same fix.
    probeRotation:           '__defer_promise__',
    processWithRotationFix:  '__defer_promise__',
    applyRotationCorrection: '__defer_promise__',
    parseRotation:           0,           // no rotation
    getRotationFilterPrefix: '',          // no transpose filter
    getRotationPreInputArgs: [],          // no `-noautorotate` etc.
    getRotationStripPostArgs: [],         // no extra strip args
});
// Constant bags — populated as raw properties on the stub so `.CODECS[ext]`
// subscripting works (returns undefined safely) instead of crashing on a
// function value. Real impl overwrites these with the actual maps.
if (window.FFmpegProcessor && window.FFmpegProcessor.__isStub) {
    window.FFmpegProcessor.CODECS = {};
    window.FFmpegProcessor.MIME_TYPES = {};
    window.FFmpegProcessor.BROWSER_FORMATS = [];
}
// `version` strings on shared modules — read directly via `Module.version`
// (e.g. for telemetry feature_use payloads). Stub returns a sentinel
// '0.0.0-stub' so callers can detect "real impl not loaded yet" if they
// care, or just use it as an opaque tag.
['ToolUploader','AudioDecoderRobust','AudioRecover'].forEach(function(_n){
    if (window[_n] && window[_n].__isStub && window[_n].version === undefined) {
        window[_n].version = '0.0.0-stub';
    }
});
// EngineWarmer — declarative warm-on-upload for heavy WASM/AI engines
// (DFN3 model 16 MB, FFmpeg core 30 MB, ONNX vocal-remover 30 MB, etc.).
// Stub queues register/warm calls so tools can register engines + the
// uploader can fire warm() before public/js/engine-warmer.js finishes its
// defer load. ensureWarm is promise-deferred so process-button click paths
// that arrive before the real script wait cleanly instead of crashing.
__sharedModule('EngineWarmer', {
    register:    undefined,            // action — queue (idempotent merge)
    warm:        undefined,            // action — queue (fire-and-forget)
    ensureWarm:  '__defer_promise__',  // promise — deferred until real loads
    isWarm:      false,                // sync getter — safe default
    isWarming:   false,
    isRegistered:false,                // pre-init: nothing is registered yet
});

// TestFileGenerator — shared utilities for the `samples` tool group
// (dummy-file-generator, sample-video, sample-audio, etc.). Synchronous
// helpers (formatBytes/parseSize/detectCaps) have safe defaults so callers
// at parse-time of inline tool scripts don't ReferenceError if the deferred
// /js/test-file-generator.js hasn't resolved yet. The Promise-returning
// generator deferred-promises until drain.
__sharedModule('TestFileGenerator', {
    generateDummyFile:  '__defer_promise__',
    safeBlobDownload:   '__defer_promise__',
    formatBytes:        '—',
    parseSize:          0,
    detectCaps:         { hasFileSystemAccess: false, maxSafeBlobBytes: 0, maxStreamBytes: 0 },
    recordDownload:     undefined,
    recordAttempt:      undefined,
    recordError:        undefined,
    recordFeature:      undefined,
});

// Free-function shared modules (window.__foo() instead of window.Foo.bar()).
// Same defer-onerror race risk; preemptive coverage based on the 60% race
// rate observed across queue-able modules. Only Promise-returning entries
// — sync side-effect setters like `__pdfSetFont` are NOT stubbed (queueing
// them would race the caller's next sync line).
__sharedFunction('__videoThumbFallback');  // 20 call sites — video tools
__sharedFunction('__Mp4BoxProbe');         // 11 call sites
__sharedFunction('__pdfReady');            // 10 call sites — PDF-export tools
__sharedFunction('__pdfLoadUnicodeFont');  //  7 call sites

// ── AudioBuffer safe-allocator ──────────────────────────────────────────
//
// `new AudioBuffer({ length, numberOfChannels, sampleRate })` and
// `AudioContext.createBuffer(...)` BOTH throw `NotSupportedError` when the
// requested length exceeds the engine's per-buffer cap. The cap is
// hardware-/OS-/browser-dependent; we've seen failures at:
//   - Chrome Mobile Android: ~5-13M samples/channel (varies by build)
//   - Mobile Safari iOS: ~10M samples/channel (after iOS 15)
//   - Desktop: typically 200M+, no real-world hits
//
// Pre-fix telemetry surfaced via Sentry 117360552 / 117362872 (vocal-remover
// applyStrengthMix on Chrome Mobile 138/147 Android, 9.7M and 12.95M frames
// @ 48 kHz — 4 m and 5 m stereo files): the raw `new AudioBuffer` threw
// uncaught, the `applyStrengthMix` chain died mid-render, and the user
// saw nothing change after dragging the strength slider. No telemetry
// either — Sentry was the only signal.
//
// `__safeAudioBuffer(opts)` wraps the allocation, attempts both the modern
// constructor and the legacy ctx.createBuffer fallback, tags failures with
// telemetry (action='audio_buffer_alloc_failed' carries channels/length/
// sampleRate/durationSec/needMB so the cohort is measurable), and throws a
// typed Error with `.code === 'AUDIO_BUFFER_ALLOC_FAILED'` so callers can
// branch on the type instead of message-grep. Recommended caller pattern:
//
//   try {
//       var buf = window.__safeAudioBuffer({ length, numberOfChannels: 2, sampleRate });
//   } catch (e) {
//       if (e.code === 'AUDIO_BUFFER_ALLOC_FAILED') {
//           window.__toast(__('tools/foo.error_too_long_for_device'), 'error');
//           return;
//       }
//       throw e;
//   }
window.__safeAudioBuffer = function(opts, ctx){
    if (!opts || typeof opts.length !== 'number' || opts.length <= 0) {
        var bad = new Error('safeAudioBuffer: invalid length ' + (opts && opts.length));
        bad.code = 'AUDIO_BUFFER_INVALID_OPTS';
        throw bad;
    }
    var ch = opts.numberOfChannels || 1;
    var sr = opts.sampleRate || 44100;
    var len = opts.length;
    var attempt;
    var lastErr = null;

    // Try modern AudioBuffer constructor (all evergreen + Safari ≥14.1).
    try {
        if (typeof AudioBuffer === 'function') {
            return new AudioBuffer({ length: len, numberOfChannels: ch, sampleRate: sr });
        }
    } catch (e1) { lastErr = e1; attempt = 'AudioBuffer'; }

    // Fall back to AudioContext.createBuffer — works on older Safari and
    // any environment where `new AudioBuffer` isn't a constructor. Caller
    // can pass an existing ctx; otherwise use a temporary one.
    try {
        var c = ctx;
        if (!c) {
            var Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) c = new Ctx();
        }
        if (c && typeof c.createBuffer === 'function') {
            return c.createBuffer(ch, len, sr);
        }
    } catch (e2) { lastErr = e2; attempt = (attempt ? attempt + '+' : '') + 'createBuffer'; }

    // Both paths failed — emit telemetry with enough context to size the
    // problem cohort, then throw the typed error.
    try {
        if (typeof window.__ae === 'function') {
            var needMB = Math.round((len * ch * 4) / 1048576);
            window.__ae('feature_use', {
                action: 'audio_buffer_alloc_failed',
                ch: ch,
                len: len,
                sr: sr,
                durationSec: Math.round(len / sr),
                needMB: needMB,
                attempt: attempt || 'none',
                err: String((lastErr && lastErr.name) || 'unknown') + ':' + String((lastErr && lastErr.message) || '').substring(0, 80)
            });
        }
    } catch(_){}

    var err = new Error('AudioBuffer allocation failed (' + ch + '×' + len + '@' + sr + 'Hz)');
    err.code = 'AUDIO_BUFFER_ALLOC_FAILED';
    err.cause = lastErr;
    err.allocOpts = { length: len, numberOfChannels: ch, sampleRate: sr };
    // Mark as expected — the browser refused the allocation. Every caller is
    // contracted to catch this (and either retry with a smaller buffer,
    // downsample the input, or surface a user-facing toast). It is NOT a
    // bug in our code, so Sentry's beforeSend drops events carrying
    // err.mechanism.data.expected === true. The invalid-opts branch above
    // is intentionally left UN-marked because that one IS a programming
    // error worth paging on. Closes Sentry TIMBRICA-FRONTEND-4J (vocal-
    // remover 31-min stereo ≈ 720MB, surfaced toast already).
    err.expected = true;
    throw err;
};

// ── __safeWebGL — feature-detect WebGL context availability ─────────────
//
// Sentry TIMBRICA-FRONTEND-4B: /ru/3d-photo-to-model on Edge 148 / Windows
// (low-end GPU or driver issue) — `new THREE.WebGLRenderer()` threw
// `Error: Error creating WebGL context.` with no user-facing UI. Tool
// went silent-dead.
//
// Pattern matches __safeFullscreen / __safeAudioBuffer: a tiny capability
// probe that callers consult BEFORE building the heavy renderer. Result is
// cached on the function so repeat probes from other tools (vr-preview,
// future 3D tools) cost zero.
//
// Returns `true` when WebGL is creatable, `false` when not. Telemetry
// `feature_use { action: 'webgl_unavailable', gpu }` fires once per page.
//
// Companion: `window.__safeWebGL.assert()` throws a typed
// `Error{code:'WEBGL_UNAVAILABLE', expected:true}` so callers wanting a
// fail-fast control-flow can use it instead of branching on bool. Both
// shapes interop with the global preventDefault() listener via
// `.expected = true`.
window.__safeWebGL = function () {
    if (typeof window.__safeWebGL._cached === 'boolean') return window.__safeWebGL._cached;
    var canvas = document.createElement('canvas');
    canvas.width = 1; canvas.height = 1;
    var gl = null;
    try {
        gl = canvas.getContext('webgl2')
            || canvas.getContext('webgl')
            || canvas.getContext('experimental-webgl');
    } catch (_) { /* getContext throws in some browsers when WebGL is disabled */ }
    var available = !!gl;
    // Release the probe's context at once: iOS keeps a small budget of live
    // WebGL contexts per page, and a 1×1 probe that is never released holds a
    // slot until GC — a slot the real viewer may then fail to get (Sentry JC).
    try { var lc = gl && gl.getExtension('WEBGL_lose_context'); if (lc) lc.loseContext(); } catch (_) {}
    if (!available) {
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    action: 'webgl_unavailable',
                    ua: (navigator.userAgent || '').slice(0, 200),
                });
            }
        } catch (_) {}
    }
    try { canvas.width = 0; canvas.height = 0; } catch (_) {}
    window.__safeWebGL._cached = available;
    return available;
};
window.__safeWebGL.assert = function () {
    if (window.__safeWebGL()) return true;
    var err = new Error('WebGL is not available on this browser/device');
    err.code = 'WEBGL_UNAVAILABLE';
    err.expected = true;
    throw err;
};

// ── __safeMediaDevices — guard navigator.mediaDevices access ────────────
//
// Sentry TIMBRICA-FRONTEND-4G: /en/dictaphone on Firefox 140 with
// `privacy.resistFingerprinting=true` — `navigator.mediaDevices` is
// undefined, and the tool's top-level
// `navigator.mediaDevices.addEventListener('devicechange', ...)` crashes
// at script-load. Tool went silent-dead with no user UI.
//
// Boolean probe matches __safeWebGL shape. Three call shapes:
//
//   window.__safeMediaDevices()                   → bool (mediaDevices present?)
//   window.__safeMediaDevices.has('getUserMedia') → bool (method callable?)
//   window.__safeMediaDevices.assert([method])    → mediaDevices (or throw typed)
//
// Throws Error{code:'MEDIA_DEVICES_UNAVAILABLE'|'MEDIA_DEVICES_METHOD_UNAVAILABLE',
// expected:true} — same global-handler-absorbed shape as __safeWebGL /
// __safeAudioBuffer.
window.__safeMediaDevices = function () {
    return !!(navigator && navigator.mediaDevices);
};
window.__safeMediaDevices.has = function (method) {
    return !!(navigator && navigator.mediaDevices
        && typeof navigator.mediaDevices[method] === 'function');
};
window.__safeMediaDevices.assert = function (method) {
    if (!(navigator && navigator.mediaDevices)) {
        var err = new Error('navigator.mediaDevices is not available — check browser privacy settings');
        err.code = 'MEDIA_DEVICES_UNAVAILABLE';
        err.expected = true;
        throw err;
    }
    if (method && typeof navigator.mediaDevices[method] !== 'function') {
        var err2 = new Error('navigator.mediaDevices.' + method + ' is not available');
        err2.code = 'MEDIA_DEVICES_METHOD_UNAVAILABLE';
        err2.expected = true;
        throw err2;
    }
    return navigator.mediaDevices;
};

// ── __onDeviceChange — the ONE safe way to watch for audio device changes ────
//
// `navigator.mediaDevices.addEventListener('devicechange', fn)` is a landmine
// twice over:
//   1. mediaDevices is undefined (Firefox privacy.resistFingerprinting, some
//      WebViews)  →  "Cannot read properties of undefined"
//   2. mediaDevices EXISTS but is not an EventTarget (older Safari / WebKit
//      builds)     →  "addEventListener is not a function"
// Both throw at the call site. In a tool blade that call usually sits at the
// TOP of the init IIFE, so the throw kills every handler defined below it and
// the page goes silently dead — buttons render, nothing works (measured on
// /speaker-test 2026-07-30: clicking "Left" produced no status, no active
// speaker, no sound, and the only trace was one window.onerror row).
//
// Tool code must call this instead of touching mediaDevices directly; the
// contract is enforced by tests/Unit/AudioOutputPickerContractTest.php.
// Returns a detach function (no-op when unsupported), never throws.
window.__onDeviceChange = function (handler) {
    if (typeof handler !== 'function') return function () {};
    if (!window.__safeMediaDevices.has('addEventListener')) {
        // Last-ditch legacy path: some builds expose the on-property only.
        try {
            if (navigator && navigator.mediaDevices && 'ondevicechange' in navigator.mediaDevices) {
                navigator.mediaDevices.ondevicechange = handler;
                return function () { try { navigator.mediaDevices.ondevicechange = null; } catch (e) {} };
            }
        } catch (e) {}
        return function () {};
    }
    try {
        navigator.mediaDevices.addEventListener('devicechange', handler);
        return function () {
            try { navigator.mediaDevices.removeEventListener('devicechange', handler); } catch (e) {}
        };
    } catch (e) { return function () {}; }
};

// ── __wrapRawRejection — promote raw WASM-module rejections to typed Error ──
//
// Many WASM modules (gifsicle-wasm, ffmpeg.wasm, libwebp, etc) reject their
// `.run()` / `.exec()` promises with a plain object literal —
//   { code: -1, message: '...', data: <Uint8Array> }
// — instead of throwing an Error instance. Sentry serialises raw promise
// rejections as `UnhandledRejection: Object captured as promise rejection
// with keys: code, data, message` with **zero stack frames** because there
// is no Error object to extract a stack from. Sentry TIMBRICA-FRONTEND-4A
// (/ru/gif-compressor, gifsicle-wasm `<unknown>` event) is the canonical
// example: the failure is real, the user toast fires, but the dashboard
// gets an undeb buggable headline.
//
// This helper wraps the raw rejection in a proper Error so the .catch()
// chain (and any unhandledrejection observer) sees a stack-bearing object.
// Usage:
//
//   gifsicle.run({...}).then(ok, rawErr => {
//       throw window.__wrapRawRejection(rawErr, 'gifsicle');
//   })
//
// The wrapper passes Error instances through unchanged so it is safe to
// apply unconditionally. Original raw rejection is preserved on `.cause`
// and `.code` is hoisted onto the wrapper when present.
window.__wrapRawRejection = function (rawErr, source) {
    if (rawErr instanceof Error) return rawErr;
    var msg;
    try {
        if (rawErr && typeof rawErr === 'object') {
            msg = rawErr.message || rawErr.error
                || (function () { try { return JSON.stringify(rawErr).slice(0, 200); } catch (_) { return '[unserializable]'; } })();
        } else {
            msg = String(rawErr);
        }
    } catch (_) { msg = '[unserializable]'; }
    var prefix = source ? (source + ': ') : '';
    var err = new Error(prefix + msg);
    err.cause = rawErr;
    if (rawErr && rawErr.code !== undefined) err.code = rawErr.code;
    if (rawErr && typeof rawErr.name === 'string') err.rawName = rawErr.name;
    return err;
};

// ── Blob.arrayBuffer polyfill — Pale Moon / ancient Firefox forks ───────
//
// Sentry 117712060 (Pale Moon 33.6 / Goanna engine fork of Firefox 52):
// `file.arrayBuffer is not a function` — the polyfill arrived in Firefox
// 76, but Pale Moon never adopted the spec. Caller path:
// file-buffer-cache.js → primeBytes → tool-uploader emits 'file' event →
// FFmpeg cacheFile loop. Without the polyfill, every cacheFile attempt
// crashes the same way and we lose the entire pre-FFmpeg upload path.
//
// FileReader.readAsArrayBuffer is available everywhere we ship to. Wrap
// it as a Promise<ArrayBuffer> matching the spec shape. Polyfill applies
// to both Blob and File (File extends Blob — same prototype chain).
// ⚠️ Проверять `typeof Blob !== 'undefined'` НЕДОСТАТОЧНО: у null тип 'object',
// поэтому такая проверка его пропускает, и следующее же обращение к
// Blob.prototype бросает «Cannot read properties of null (reading 'prototype')».
// Это не гипотеза: ошибка пришла из Sentry (09.08.2026, Chrome 116 на Linux) и
// воспроизведена — приватные расширения обнуляют window.Blob, чтобы перекрыть
// файловые API. Замер показал, что остальное ядро при этом доживает до конца,
// то есть цена — одна лишняя ошибка в логе, а не мёртвая страница; но ошибка
// в общем слое шумит на каждой странице такого пользователя.
// `typeof Blob === 'function'` — правильная проверка конструктора: она
// отсеивает и null, и undefined, и подменённый не-вызываемый объект.
if (typeof Blob === 'function' && Blob.prototype && typeof Blob.prototype.arrayBuffer !== 'function') {
    Blob.prototype.arrayBuffer = function(){
        var blob = this;
        return new Promise(function(resolve, reject){
            try {
                var fr = new FileReader();
                fr.onload = function(){ resolve(fr.result); };
                fr.onerror = function(){ reject(fr.error || new Error('FileReader failed')); };
                fr.readAsArrayBuffer(blob);
            } catch (e) { reject(e); }
        });
    };
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('feature_use', { action: 'blob_arraybuffer_polyfilled' });
        }
    } catch(_){}
}

// ── Resilient video-player constructor ─────────────────────────────────
//
// 15 video tools (extract-audio, voice-changer, video-volume / -subtitles
// / -speed / -flip / -crop / -compressor / -frames / -overlay / -to-vr /
// -merge / -colorize, reels-maker, add-music-to-video, subtitle-editor)
// instantiate `new TimbricaPlayer(wrapEl, opts)`. When `video-player.js`
// loses its defer load (CSP, ad-blocker, network), the bare `new` throws
// a ReferenceError and the tool's file-pick handler dies — Sentry
// 117690970 (extract-audio, Yandex 26.37 Android).
//
// Centralised guard: every tool calls `window.__createTimbricaPlayer(...)`
// instead. If the real class is loaded, behaviour is identical
// (`new TimbricaPlayer(wrap, opts)`). If not, returns a minimal stub that
// surfaces a native `<video controls>` so the user can still hear/see the
// file. The stub honours `onReady`, `.video`, `.dispose()`, `.pause()`,
// `.play()` — the surface every consumer call site uses.
window.__createTimbricaPlayer = function(wrapEl, opts){
    opts = opts || {};
    if (typeof TimbricaPlayer === 'function') {
        return new TimbricaPlayer(wrapEl, opts);
    }
    try {
        if (window.__ae) window.__ae('feature_use', {
            action: 'video_player_load_failed',
            slug: (window.__SLUG || '') // populated by tool inline scripts where available
        });
    } catch(_){}
    var v = document.createElement('video');
    v.controls = true;
    v.preload = 'metadata';
    v.style.width = '100%';
    v.style.maxWidth = '100%';
    if (opts.maxHeight) v.style.maxHeight = opts.maxHeight + 'px';
    if (opts.src) v.src = opts.src;
    // The canonical TimbricaPlayer (public/js/video-player.js) exposes
    // `.destroy()` — and that's the name every consumer call site uses
    // (audited 2026-06-01: 9 sites across auto-captions / extract-audio /
    // reverse-video / video-speed / video-to-vr all call `.destroy()`,
    // ZERO call `.dispose()`). Earlier stub only exposed `.dispose()`,
    // so when video-player.js failed to load the fallback path was missing
    // the documented method and consumers crashed with "TypeError:
    // el.vePlayer.destroy is not a function" (Sentry TIMBRICA-FRONTEND-7P,
    // Edge 148 on Windows, 2026-05-29). Expose BOTH names with `dispose`
    // kept as an alias in case any third-party / legacy code uses it.
    var _stubObjUrl = null;
    function stubCleanup(){
        try { v.pause(); v.removeAttribute('src'); v.load(); } catch(_){}
        if (_stubObjUrl) { try { URL.revokeObjectURL(_stubObjUrl); } catch(_){} _stubObjUrl = null; }
    }
    var stub = {
        video: v,
        // `.load(urlOrFile)` is the method EVERY video tool calls right after
        // `__createTimbricaPlayer(...)` (video-speed:774, reverse-video,
        // auto-captions, …). The real TimbricaPlayer has it; the stub did NOT,
        // so when video-player.js failed to load (ad-blocker / Yandex / CSP)
        // the very next line `player.load(file)` threw "player.load is not a
        // function" and killed the whole file-pick flow — top video-speed error
        // in prod telemetry (Yandex desktop). Mirror the real player: accept a
        // URL string or a File/Blob, set src, and let the onReady listener
        // (loadedmetadata, registered below) fire so callers get duration/size.
        load: function(srcOrFile){
            try {
                if (_stubObjUrl) { try { URL.revokeObjectURL(_stubObjUrl); } catch(_){} _stubObjUrl = null; }
                if (typeof srcOrFile === 'string') {
                    v.src = srcOrFile;
                } else if (srcOrFile instanceof Blob) {
                    _stubObjUrl = URL.createObjectURL(srcOrFile);
                    v.src = _stubObjUrl;
                }
                v.load();
            } catch(_){}
        },
        play:  function(){ try { return v.play(); } catch(e){} },
        pause: function(){ try { v.pause(); } catch(_){} },
        destroy: stubCleanup,
        dispose: stubCleanup,
        // Trim/speed/volume toggles — minimal no-op surface so call sites
        // don't have to feature-detect every method.
        setTrim:    function(){},
        setSpeed:   function(){},
        setVolume:  function(){},
        seek:       function(t){ try { v.currentTime = t; } catch(_){} },
        getCurrentTime: function(){ return v.currentTime || 0; },
        getDuration:    function(){ return v.duration || 0; },

        /* Остальная поверхность настоящего плеера.
         *
         * ⚠️ Этот класс кусался ТРИЖДЫ, и каждый раз чинился дописыванием
         * ОДНОГО метода по факту падения: 7P — не было `.destroy()`;
         * /video-speed — не было `.load()`; G6 (/auto-captions, iOS 18.7) —
         * снова `.load` у объекта, где `.video` есть. Дописывать по одному —
         * значит ждать следующего падения, поэтому ниже закрыта ВСЯ поверхность,
         * которую зовут тулы, а сторож
         * tests/playwright/player-stub-surface.mjs сверяет её с настоящим
         * плеером и падает при любом расхождении. Он же и нашёл эти пять:
         * getTrimRange, skip, togglePlay, setTrimRange, getLoop.
         *
         * Смысл повторяет настоящий плеер, а не выдумывает свой: у заглушки нет
         * обрезки и A-B петли, поэтому диапазон обрезки — это ВЕСЬ ролик, а
         * петли нет (null). Так вызывающий получает правдивый ответ, а не
         * молчаливый undefined. */
        togglePlay: function(){
            try { if (v.paused) { return v.play(); } v.pause(); } catch(_){}
        },
        skip: function(delta){
            try {
                var d = v.duration || Infinity;
                v.currentTime = Math.max(0, Math.min(d, (v.currentTime || 0) + (+delta || 0)));
            } catch(_){}
        },
        // Обрезки у нативного <video> нет — честный ответ «весь ролик».
        getTrimRange: function(){ return { start: 0, end: v.duration || 0 }; },
        setTrimRange: function(){},
        // A-B петли тоже нет: null — то же, что возвращает настоящий плеер,
        // когда петля не задана.
        getLoop:   function(){ return null; },
        setLoop:   function(){ return null; },
        clearLoop: function(){},
        __isStubPlayer: true
    };
    try { wrapEl.innerHTML = ''; wrapEl.appendChild(v); } catch(_){}
    // Mark the closest tool-page wrapper so per-tool CSS can hide
    // controls that don't work on the native fallback (Trim handles,
    // Speed slider, custom Volume, etc.). Tools opt in by adding rules
    // like `.tool-page[data-player-fallback="1"] .vc-trim-handle { display:none }`.
    try {
        var toolPage = wrapEl.closest && (wrapEl.closest('.tool-page') || wrapEl.closest('[data-tool-slug]'));
        if (toolPage) toolPage.setAttribute('data-player-fallback', '1');
    } catch(_){}
    // User-visible warning so the fallback isn't silent — tells the
    // user some advanced features may not work and they can refresh
    // / try a different browser if they need them. Localised string
    // is exposed via `window.__commonStrings` (rendered in the layout
    // from tools.common.* translations).
    try {
        var msg = (window.__commonStrings && window.__commonStrings.video_player_fallback) || null;
        if (window.__toast && msg) window.__toast(msg, 'warning');
    } catch(_){}
    if (typeof opts.onReady === 'function') {
        v.addEventListener('loadedmetadata', function(){
            try { opts.onReady(stub); } catch(_){}
        }, { once: true });
    }
    return stub;
};

// ── Cover-art client-side normaliser ─────────────────────────────────────
// `__embedCover(blob, opts?)` returns a JPEG Blob of the input cover image.
// Strategy: if the blob is already JPEG (FFD8FF magic-byte sniff), pass
// through unchanged (no quality loss). Otherwise decode via Image+canvas
// and re-encode as JPEG q=0.92.
//
// Why client-side: the audio container's picture frame (mp3 ID3v2 APIC,
// m4a covr atom, flac/ogg METADATA_BLOCK_PICTURE) accepts ONLY MJPEG-coded
// data — `-c copy` of PNG/WebP/GIF into the frame fails at the muxer with
// "Could not write header for output file #0 (incorrect codec parameters):
// Invalid argument | Error initializing output stream 0:1". Instead of
// patching every consuming tool with `-c:v mjpeg` (which re-encodes JPEG
// covers too, lossy), we eliminate the problem upstream — by the time
// FFmpeg sees the cover, it's already JPEG.
//
// Sentry/telemetry root-cause: 19 events / 24h on /pino/streams 2026-05-08,
// concentrated to audio-metadata. Closes the class for ALL tools that embed
// cover art (audio-metadata today, voice-changer/audio-enhancer/etc.
// later), via single shared primitive.
//
// Returns: Promise<Blob> — JPEG-coded image, ready to feed to FFmpeg.
window.__embedCover = function(blob, opts) {
    opts = opts || {};
    var quality = typeof opts.quality === 'number' ? opts.quality : 0.92;
    // Default maxDim: 1500 px on the long edge. iTunes, Spotify and most
    // streaming services normalise covers to roughly this size; players
    // (Windows Media Player, Yandex Music, foobar2000, Groove, Telegram)
    // reliably display covers up to ~2000 px / ~1 MB but get flaky beyond
    // that — silently skip extraction, fall back to "no thumbnail",
    // truncate during ID3 parse, etc. Pre-fix audio-metadata users hit
    // tagged_* outputs of 20+ MB (one 147 MB) where the embedded image
    // alone was ≥ the audio. Bug-report 2026-05-28 from sid 033ef5c4
    // ("не сохранил обложку") on a saved file with a 4 MB embedded
    // cover — the file HAD the picture but the user's player ignored it.
    // 1500 px keeps quality high while making the embed universally
    // compatible. Opt out by passing { maxDim: 0 } explicitly.
    var maxDim  = typeof opts.maxDim  === 'number' ? opts.maxDim  : 1500;
    return new Promise(function(resolve, reject) {
        if (!(blob instanceof Blob)) {
            reject(new Error('__embedCover: input is not a Blob'));
            return;
        }
        // Magic-byte sniff: JPEG starts with FF D8 FF (any third byte).
        blob.slice(0, 3).arrayBuffer().then(function(headBuf) {
            var head = new Uint8Array(headBuf);
            var isJpeg = head[0] === 0xFF && head[1] === 0xD8 && head[2] === 0xFF;
            // Decode → check dims → maybe passthrough → otherwise resize.
            // Even when the blob is JPEG we now load it so we can compare
            // against maxDim. Passthrough on JPEGs that already fit; the
            // image-load overhead (~50-200 ms on a 4 MB JPEG) buys universal
            // player compatibility for the long tail of huge phone-camera
            // covers.
            var url = URL.createObjectURL(blob);
            var img = new Image();
            img.onload = function() {
                try {
                    var w = img.naturalWidth, h = img.naturalHeight;
                    var fits = !maxDim || Math.max(w, h) <= maxDim;
                    if (isJpeg && fits) {
                        URL.revokeObjectURL(url);
                        resolve(blob);
                        return;
                    }
                    if (maxDim && Math.max(w, h) > maxDim) {
                        var scale = maxDim / Math.max(w, h);
                        w = Math.round(w * scale); h = Math.round(h * scale);
                    }
                    var c = document.createElement('canvas');
                    c.width = w; c.height = h;
                    c.getContext('2d').drawImage(img, 0, 0, w, h);
                    // the shared ladder: a toBlob that never answers used to hold
                    // the whole export (audio + cover) for good
                    window.__canvasBlob(c, 'image/jpeg', quality).then(function(out) {
                        URL.revokeObjectURL(url);
                        resolve(out);
                    }, function(err) {
                        URL.revokeObjectURL(url);
                        reject(err);
                    });
                } catch (err) {
                    URL.revokeObjectURL(url);
                    reject(err);
                }
            };
            img.onerror = function() {
                URL.revokeObjectURL(url);
                reject(new Error('__embedCover: image decode failed (corrupt or unsupported format)'));
            };
            img.src = url;
        }).catch(reject);
    });
};

// ── APIC type=3 marker primitive ─────────────────────────────────────────
// `__coverPreserveArgs(ext)` returns the canonical FFmpeg args that force
// APIC picture_type=3 (Cover, front) on output, regardless of what the
// input had. ALWAYS append after `-c copy` / `-c:v copy` whenever you
// re-mux audio with a cover stream.
//
// Why this exists: `-c copy` preserves the input APIC picture_type byte
// verbatim. If the upstream encoder wrote type=0 (Other) — common for
// sloppy ID3 writers and many "AI music" generators — every downstream
// re-mux propagates type=0 forever. Windows Media Player, iTunes, Groove,
// and foobar2000 all silently ignore type=0 and only display type=3 as
// the track cover. From the user: "обложка слетела".
//
// FFmpeg's MP3/M4A/FLAC muxers map per-stream `title=Album cover` AND
// `comment=Cover (front)` → APIC picture_type=3. Setting `-disposition:v
// attached_pic` ensures the muxer treats the video stream as APIC at all
// (rather than a raw video track that players don't know to render as
// cover). Empirically verified: input type=0 + these markers → output
// type=3 (2026-05-08, ffmpeg 6.x CLI + WASM).
//
// Cover-incompatible containers (WAV, AIFF, raw PCM) get an empty array
// — they can't carry a picture frame at all. Caller spreads/concats:
//
//   args.push(...window.__coverPreserveArgs(ext));
//
// Returns: string[] — pushable args. Empty for no-cover formats.
window.__coverPreserveArgs = function(ext){
    if (!ext) return [];
    var lo = String(ext).toLowerCase();
    if (lo === 'wav' || lo === 'aiff' || lo === 'aif' || lo === 'pcm' || lo === 'raw') return [];
    return ['-disposition:v', 'attached_pic',
            '-metadata:s:v', 'title=Album cover',
            '-metadata:s:v', 'comment=Cover (front)'];
};

// ── Capability-gate primitive ────────────────────────────────────────────
// `__capabilityGate(name, probeFn, opts?)` returns Promise<{ok, reason}>.
// Caches probe result per-page-load (probe runs at most once per name).
//
// Heavy features (AI Upscale, WebGPU compute, SAB cross-origin-isolation,
// MediaRecorder advanced codecs) sometimes fail on the user's device for
// reasons we can detect cheaply BEFORE committing to a long-running
// operation. Pre-flight gate hides the feature toggle entirely if the
// probe fails — user never clicks an option that won't work, never waits
// for a doomed run, never sees a misleading "try other format" toast.
//
// Reactive (today) → predictive (this primitive). Use:
//
//   var gate = await window.__capabilityGate('ai-upscale', async function() {
//       // tiny test inference — if it succeeds, full inference will too
//       var session = await ort.InferenceSession.create(testModelUrl, {executionProviders: ['webgpu']});
//       var out = await session.run({input: testTensor});
//       return out.output.dims.length > 0;
//   });
//   if (!gate.ok) { aiToggle.style.display = 'none'; return; }
//
// Shared cache means subsequent calls return the cached result instantly,
// so each tool can call this without retest cost.
window.__capabilityGate = (function() {
    var cache = {};
    return function(name, probeFn, opts) {
        opts = opts || {};
        if (cache[name]) return cache[name];
        var timeout = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 5000;
        cache[name] = new Promise(function(resolve) {
            var timer = setTimeout(function() {
                resolve({ ok: false, reason: 'probe-timeout' });
            }, timeout);
            try {
                Promise.resolve(probeFn()).then(function(passed) {
                    clearTimeout(timer);
                    resolve({ ok: !!passed, reason: passed ? null : 'probe-failed' });
                }, function(err) {
                    clearTimeout(timer);
                    resolve({ ok: false, reason: 'probe-throw:' + (err && err.message || String(err)).substring(0, 80) });
                });
            } catch (err) {
                clearTimeout(timer);
                resolve({ ok: false, reason: 'probe-sync-throw:' + (err && err.message || String(err)).substring(0, 80) });
            }
        });
        return cache[name];
    };
})();

/* Квантователь float → 16 бит для тулов со своим писателем WAV/Int16:
 * window.__pcm16Quantizer(dither) → q(src, n, dst, at, stride). Тот же блок, что у
 * общих писателей WAV (нетронутое бит-в-бит, тишина — ноль, dither — готовый файл,
 * без него — округление к чётному). Разбор — docs/why/pcm16-quantize.md. */
window.__pcm16Quantizer = (function () {
    /* __pcm16:begin
     * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
     * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
     * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
     * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
     * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
     * docs/why/pcm16-quantize.md.
     *
     * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
     * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
     *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
     *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
     *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
     *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
     *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
     *    точно — без шума и без округления мимо.
     *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
     *    уходит в шум, а не в тишину и не в гармоники).
     *  · dither=false — округление «половина к чётному», без шума: для того, что
     *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
     *  · Зерно постоянное: один и тот же вход — одни и те же байты.
     */
    function pcm16Quantizer(dither) {
        var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
        // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
        // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
        // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
        // (32640…32767) ложно садился на сетку 32767 и терял единицу.
        function exact(src, b, e, dst, at, stride, pos) {
            for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
                var s = src[i];
                if (s !== s) s = 0;
                var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
                if (d > TOL || d < -TOL) return false;
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
            return true;
        }
        return function (src, n, dst, at, stride) {
            // Состояние генератора — в локальной на время вызова: через замыкание
            // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
            var b, e, i, w, s, v, k, r = x, tri = dither;
            for (b = 0; b < n; b += BLOCK) {
                e = b + BLOCK < n ? b + BLOCK : n;
                if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
                for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                    s = src[i];
                    if (s !== s) s = 0;
                    v = s < 0 ? s * 32768 : s * 32767;
                    if (tri) {
                        r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                        k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                    } else {
                        k = Math.floor(v + 0.5);
                        if (k - v === 0.5 && (k & 1)) k--;
                    }
                    dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
                }
            }
            x = r;
        };
    }
    /* __pcm16:end */
    return pcm16Quantizer;
})();
// ── Safe AudioContext resume ──────────────────────────────────────────
// `__safeResume(ctx) → Promise<bool>` — call this everywhere instead of
// `ctx.resume()`. iOS Safari rejects `resume()` with `InvalidStateError:
// Failed to start the audio device` whenever the call isn't directly
// in a user-gesture chain (mobile-Safari strict autoplay policy) or when
// the audio device is busy (other app holding mic, in-call, etc.). The
// rejection is EXPECTED platform behaviour — not a bug. Without a
// `.catch()`, every such rejection becomes an `unhandledrejection` →
// Sentry event (116880408 — Mobile Safari /ru/white-noise + Yandex
// /ru/text-to-speech 2026-05-07).
//
// Audit 2026-05-08: 118 tools call raw `audioCtx.resume()` without
// catching. This helper is the shared primitive — migrate each tool to
// `__safeResume(audioCtx)` so the rejection is swallowed silently and
// playback retries on the next user gesture. Returns boolean (true =
// resumed, false = rejected — caller can decide whether to retry).
//
// Telemetry: emits `feature_use:audio_resume_blocked` with reason on
// rejection so we measure how often iOS Safari's policy fires per tool.
window.__safeResume = function (ctx) {
    // `interrupted` is iOS's fourth state (phone call, Siri, route change, and
    // — the common one — the page being backgrounded behind a permission
    // sheet). It is exactly as silent as `suspended` and is cleared the same
    // way, by resume(). Testing only for `suspended` made this helper refuse
    // to revive precisely the contexts that most need it, on the one platform
    // the helper exists for. Found while tracing bug report 2026-07-26
    // (/megaphone on iPhone: tool alive, audio dead, nothing thrown).
    if (!ctx || (ctx.state !== 'suspended' && ctx.state !== 'interrupted') || typeof ctx.resume !== 'function') {
        return Promise.resolve(false);
    }
    /* Офлайн-контекст сюда не ходит: до `startRendering()` он `suspended` по
     * замыслу, а `resume()` на нём — ошибка по спецификации, а не отказ
     * устройства. Пропустив его, мы бы записали InvalidStateError в
     * `audio_resume_blocked` и отдали вызывающему false, который выше читается
     * как «звука нет» (так и родился тост про DAW у /slideshow-maker). */
    try { if (typeof ctx.startRendering === 'function') return Promise.resolve(false); } catch (_) {}
    try {
        var p = ctx.resume();
        // Some old WebKit returned undefined (synchronous resume). Wrap.
        var settled = false;
        var done = Promise.resolve(p).then(function () { settled = true; return true; }, function (err) {
            settled = true;
            try {
                if (window.__ae) window.__ae('feature_use', {
                    action: 'audio_resume_blocked',
                    reason: (err && err.name) || 'unknown',
                    msg: ((err && err.message) || String(err)).slice(0, 120),
                });
            } catch (_) {}
            return false;
        });
        /* ⏱ ПОТОЛОК. `resume()` умеет не отвечать НИКОГДА: у контекста, у
         * которого отняли устройство вывода (другая программа взяла звуковую
         * карту монопольно — DAW, ASIO-драйвер, звонок; отключили наушники),
         * промис не резолвится и не реджектится, и остаётся таким ПОСЛЕ того,
         * как устройство освободили (замерено 2026-09-02 на настоящем железе,
         * Chrome 152 / Chromium 128 / Yandex 26: два ожидания по 4 с подряд).
         * Тул, который «await __safeResume(ctx)» перед воспроизведением, висел
         * в этом месте молча — кнопка занята, звука нет, сообщения нет.
         *
         * Поэтому: не дождались — отвечаем false (вызывающий уже умеет
         * ветвиться) и ПОМЕЧАЕМ контекст мёртвым, чтобы следующий
         * `__liveAudioCtx()` отдал свежий, а автопробуждения по жесту и по
         * возврату во вкладку перестали звать на нём тот же вечный resume().
         * 4 с — с запасом над самым медленным ЧЕСТНЫМ пробуждением
         * (открытие устройства ОС: 544–1271 мс, замер 2026-07-30).
         * Настоящий отказ политики автозапуска сюда не попадает: он
         * РЕДЖЕКТИТСЯ мгновенно и уходит веткой выше. */
        return Promise.race([done, new Promise(function (resolve) {
            setTimeout(function () {
                if (settled) return;
                try {
                    if (window.__markAudioCtxError) window.__markAudioCtxError(ctx, 'resume_hung');
                    if (window.__ae) window.__ae('feature_use', { action: 'audio_resume_blocked', reason: 'timeout' });
                } catch (_) {}
                resolve(false);
            }, 4000);
        })]);
    } catch (err) {
        // Synchronous throw (very old WebKit) — treat same as rejection.
        return Promise.resolve(false);
    }
};

/* __safeFullscreen:begin */
// `__safeFullscreen(el?, opts?) → Promise<bool>` — call this everywhere
// instead of `el.requestFullscreen()`. The Fullscreen API returns a
// Promise that REJECTS with `TypeError: Fullscreen is not supported`
// when the device denies it (PiP active, iframe restrictions, mobile-
// Safari/Chrome quirks, no user-gesture). The rejection is EXPECTED
// platform behaviour — not a bug. Without a `.catch()`, the rejection
// becomes an `unhandledrejection` → Sentry event (3R, 3S — 2026-05-09
// /ru/cat-games + /ru/phone-display-test).
//
// Audit 2026-05-09: 49 blade files call requestFullscreen/webkit-
// RequestFullscreen, almost all wrapped in try/catch that catches sync
// throws but lets the async Promise rejection escape.
//
// Returns boolean (true = entered, false = denied/unsupported).
// Pass opts.onDenied / opts.onUnsupported callbacks for tool-specific
// fallback (fakeFs / overlay / no-op). Telemetry emits
// `feature_use:fullscreen_denied{reason}` so we measure denial rate.
window.__safeFullscreen = function (el, opts) {
    el = el || document.documentElement;
    opts = opts || {};
    var fn = el.requestFullscreen || el.webkitRequestFullscreen;
    if (typeof fn !== 'function') {
        // The API is absent (iPhone Safari): until 14.10.2026 this branch left
        // no trace, so nobody could say how many presses of a fullscreen
        // button did nothing. `handled` — the caller has its own way out
        // (`__wholeScreen` turns the element into an overlay).
        try {
            if (window.__ae) window.__ae('feature_use', { action: 'fullscreen_unsupported', handled: opts.handled ? 1 : 0 });
        } catch (_) {}
        if (typeof opts.onUnsupported === 'function') {
            try { opts.onUnsupported(); } catch (_) {}
        }
        return Promise.resolve(false);
    }
    try {
        var p = fn.call(el);
        // Some browsers (older webkit) return undefined synchronously.
        return Promise.resolve(p).then(function () { return true; }, function (err) {
            try {
                if (window.__ae) window.__ae('feature_use', {
                    action: 'fullscreen_denied',
                    reason: (err && err.name) || 'unknown',
                    msg: ((err && err.message) || String(err)).slice(0, 120),
                });
            } catch (_) {}
            if (typeof opts.onDenied === 'function') {
                try { opts.onDenied(err); } catch (_) {}
            }
            return false;
        });
    } catch (err) {
        // Synchronous throw — treat same as rejection.
        if (typeof opts.onDenied === 'function') {
            try { opts.onDenied(err); } catch (_) {}
        }
        return Promise.resolve(false);
    }
};

// `__safeExitFullscreen() → Promise<bool>` — the way out, same contract as
// `__safeFullscreen`. Call it instead of
// `(document.exitFullscreen || document.webkitExitFullscreen).call(document)`.
// The prefixed methods (iPad Safari before 16.4, old Chromium) return
// undefined, NOT a Promise, so a `.catch()` chained on that call — the very
// fix the unhandled-rejection class above asks for — throws «undefined is not
// an object (evaluating '….catch')» on exactly the browsers that need the
// prefix: /drawing's fullscreen button threw on the way in and out, and the
// canvas resize scheduled after the call never ran (TIMBRICA-FRONTEND-NQ, iPad
// iOS 12, 03.10.2026). Not in fullscreen → false without a call (the standard
// method rejects then). Guard — FullscreenCallIsNotAssumedAPromiseTest.
window.__safeExitFullscreen = function () {
    var fn = document.exitFullscreen || document.webkitExitFullscreen;
    if (typeof fn !== 'function' || !(document.fullscreenElement || document.webkitFullscreenElement)) {
        return Promise.resolve(false);
    }
    try {
        return Promise.resolve(fn.call(document)).then(function () { return true; }, function () { return false; });
    } catch (_) {
        return Promise.resolve(false);
    }
};
/* __safeFullscreen:end */

/* __immersive:begin */
// `__immersive(key) → release()` — a tool says «my surface owns the screen now»
// (a reading screen, a faux fullscreen where the Fullscreen API is absent).
// While anyone holds it, <html> carries `tmb-immersive`, and floating site
// chrome that is neither an ad nor the consent banner — today the support chat
// launcher — steps aside (the rule itself lives with the launcher, in
// partials/support-widget and partials/support-operator-widget). Real
// fullscreen needs no call: the launcher hides under any `:fullscreen`
// ancestor on its own.
//
// Counted per key, so two surfaces on one page do not release each other, and
// a second release() of the same hold is a no-op. Asked for by a reader of
// /ebook-reader (support chat 574, 12.10.2026): the launcher sat on the
// progress bar of the reading screen. Guard —
// FloatingChatLauncherYieldsTheScreenTest.
window.__immersive = (function () {
    var held = {};
    function sync() {
        var on = false;
        for (var k in held) { if (held[k] > 0) { on = true; break; } }
        try { document.documentElement.classList.toggle('tmb-immersive', on); } catch (_) {}
    }
    return function (key) {
        key = String(key || 'page');
        held[key] = (held[key] || 0) + 1;
        sync();
        var done = false;
        return function () {
            if (done) return;
            done = true;
            held[key] = Math.max(0, (held[key] || 0) - 1);
            sync();
        };
    };
})();
/* __immersive:end */

/* __wholeScreen:begin */
// `__wholeScreen(el, opts) → { enter, exit, toggle, active, destroy }` — one
// «whole screen» button with one outcome on every device.
//
// Where the browser gives an ELEMENT real fullscreen, the element gets it
// (through `__safeFullscreen`). Where it does not — iPhone Safari has no
// Fullscreen API for elements at all, and a kitchen display such as Apple
// HomeView may have none — the same element becomes an overlay over the whole
// viewport. Census 14.10.2026: 66 tools carry a fullscreen button, 57 of them
// did nothing where the API is absent; a button that does nothing is the bug
// this door closes, one tool at a time.
//
// The element carries `tmb-whole` in both modes and `tmb-whole-overlay` in the
// overlay (rules — public/css/critical.css, inline with every page: app.css
// arrives later than a fast finger). The overlay ends above the consent
// banner (--cc-bottom-inset), keeps clear of rounded corners and notches
// (safe-area insets) and asks floating site chrome to step aside
// (`__immersive`). Esc closes it; leaving real fullscreen by the browser's own
// means is noticed.
//
//   opts.key          name of the hold for `__immersive` (default 'whole')
//   opts.onChange     (on, mode) — mode is 'fullscreen' | 'overlay' | null
//   opts.overlayOnly  never ask for real fullscreen
//   opts.escape       false — Esc stays the tool's own key (an editor deselects with it); the
//                     tool closes the overlay itself (/presentation-maker, 28.10.2026)
//   opts.classes      {full, overlay} — extra class names mirrored on the element
//
// The element may sit inside a glass card (backdrop-filter, isolation,
// transform…): while the overlay is open the door lifts those properties off
// its ancestors and gives them back on exit (liftAncestors below). Guard —
// tests/js/whole-screen.test.mjs. Why — docs/why/primitives/whole-screen.md.
window.__wholeScreen = function (el, opts) {
    opts = opts || {};
    var doc = document;
    var key = opts.key || 'whole';
    var extra = opts.classes || {};
    var mode = null, release = null;

    function fsElement() { return doc.fullscreenElement || doc.webkitFullscreenElement || null; }
    function changed() {
        if (typeof opts.onChange !== 'function') return;
        try { opts.onChange(mode !== null, mode); } catch (_) {}
    }
    function paint() {
        var on = mode !== null, overlay = mode === 'overlay';
        el.classList.toggle('tmb-whole', on);
        el.classList.toggle('tmb-whole-overlay', overlay);
        if (extra.full) el.classList.toggle(extra.full, on);
        if (extra.overlay) el.classList.toggle(extra.overlay, overlay);
        doc.documentElement.classList.toggle('tmb-whole-on', overlay);
    }
    // An overlay is `position:fixed`, and an ancestor can hold it in two ways
    // (measured 16.10.2026 in Chromium, WebKit and Gecko): as its CONTAINING
    // BLOCK — transform and its parts, perspective, filter, backdrop-filter,
    // contain:layout/paint, content-visibility:auto, will-change of those — and
    // the «whole screen» is then the size of the card; or as a STACKING CONTEXT
    // — isolation, a z-index, opacity, blend, clip, mask — and the overlay's
    // z-index is then counted inside the card, so the site header paints over it.
    // The shared console's glass panel (.tc-panel: backdrop-filter + isolation)
    // does both: on /timeline-maker the show came out 356×420 in a 390×760
    // window with the header and the rail on top (QA 15.10.2026).
    // `container-type` is NOT a trap (style + inline-size containment, no
    // layout containment) and stays: the overlay's content keeps its queries.
    // While the overlay is open the door takes exactly those properties off
    // exactly those ancestors (inline !important) and hands them back on exit —
    // the page under the overlay is covered anyway. Rejected (same instrument,
    // docs/why/primitives/whole-screen.md): a `:has()` rule (no :has() before
    // iOS 15.4 / Chromium 105 / Firefox 121), moving the element to <body>
    // (it loses the inherited --lg-* tokens and the tool's ancestor selectors),
    // the top layer via popover (it covers the menu, toasts and consent banner).
    var LIFT = [
        // [property, its neutral value]; holds() says whether a computed value is a trap
        ['transform', 'none'], ['translate', 'none'], ['rotate', 'none'], ['scale', 'none'],
        ['perspective', 'none'], ['filter', 'none'],
        ['backdrop-filter', 'none'], ['-webkit-backdrop-filter', 'none'],
        ['contain', 'none'], ['content-visibility', 'visible'], ['will-change', 'auto'],
        ['isolation', 'auto'], ['z-index', 'auto'], ['opacity', '1'], ['mix-blend-mode', 'normal'],
        ['clip-path', 'none'], ['mask', 'none'], ['-webkit-mask', 'none']
    ];
    function holds(p, v) {
        if (!v) return false;
        switch (p) {
            case 'contain': return /layout|paint|strict|content/.test(v);
            case 'content-visibility': return v !== 'visible';
            case 'will-change': return v !== 'auto';
            case 'isolation': return v === 'isolate';
            case 'z-index': return v !== 'auto';
            case 'opacity': return +v < 1;
            case 'mix-blend-mode': return v !== 'normal';
            default: return v !== 'none';
        }
    }
    function value(cs, p) {
        // the computed mask shorthand is not serialised everywhere — its image says it
        if (p === 'mask') p = 'mask-image';
        if (p === '-webkit-mask') p = '-webkit-mask-image';
        return String(cs.getPropertyValue(p) || '').trim();
    }
    var lifted = null;
    function liftAncestors() {
        lifted = [];
        for (var a = el.parentElement; a && a !== doc.body && a !== doc.documentElement; a = a.parentElement) {
            var cs = window.getComputedStyle(a), take = [];
            for (var i = 0; i < LIFT.length; i++) if (holds(LIFT[i][0], value(cs, LIFT[i][0]))) take.push(LIFT[i]);
            if (!take.length) continue;
            // Aliases go together (-webkit-backdrop-filter is backdrop-filter in Chromium, -webkit-mask feeds
            // mask): one of a pair left on would keep the trap.
            var names = take.map(function (t) { return t[0]; });
            if (names.indexOf('backdrop-filter') >= 0 || names.indexOf('-webkit-backdrop-filter') >= 0) take.push(['backdrop-filter', 'none'], ['-webkit-backdrop-filter', 'none']);
            if (names.indexOf('mask') >= 0 || names.indexOf('-webkit-mask') >= 0) take.push(['mask', 'none'], ['-webkit-mask', 'none']);
            // The whole inline state first, then the overrides: a value read after its alias was set
            // would be our own override, handed back on exit as if it were the page's.
            var saved = [];
            for (var s = 0; s < take.length; s++) saved.push([take[s][0], a.style.getPropertyValue(take[s][0]), a.style.getPropertyPriority(take[s][0])]);
            for (var t = 0; t < take.length; t++) a.style.setProperty(take[t][0], take[t][1], 'important');
            lifted.push([a, saved]);
        }
    }
    function dropAncestors() {
        if (!lifted) return;
        for (var k = 0; k < lifted.length; k++) {
            var a = lifted[k][0], saved = lifted[k][1];
            for (var i = 0; i < saved.length; i++) a.style.removeProperty(saved[i][0]);
            for (var m = 0; m < saved.length; m++) if (saved[m][1]) a.style.setProperty(saved[m][0], saved[m][1], saved[m][2]);
            if (a.getAttribute('style') === '') a.removeAttribute('style');
        }
        lifted = null;
    }
    function leaveOverlay() {
        if (release) { release(); release = null; }
        dropAncestors();
    }
    function onFullscreenChange() {
        if (mode === 'fullscreen' && fsElement() !== el) { mode = null; paint(); changed(); }
    }
    function onKey(e) {
        if (mode === 'overlay' && e.key === 'Escape' && opts.escape !== false) api.exit();
    }
    doc.addEventListener('fullscreenchange', onFullscreenChange);
    doc.addEventListener('webkitfullscreenchange', onFullscreenChange);
    doc.addEventListener('keydown', onKey);

    var api = {
        enter: function () {
            if (mode) return Promise.resolve(mode);
            // The browser takes several frames to grant fullscreen, and the
            // element has to be rendered while it is asked. Until the answer
            // comes the element waits out of the page flow and unseen — else
            // a hidden stage shown «for the request» shoves the page down and
            // back.
            el.classList.add('tmb-whole-pending');
            var ask = (opts.overlayOnly || typeof window.__safeFullscreen !== 'function')
                ? Promise.resolve(false)
                : window.__safeFullscreen(el, { handled: true });
            return Promise.resolve(ask).then(function (ok) {
                el.classList.remove('tmb-whole-pending');
                if (mode) return mode;
                mode = ok ? 'fullscreen' : 'overlay';
                if (mode === 'overlay' && typeof window.__immersive === 'function') release = window.__immersive(key);
                if (mode === 'overlay') liftAncestors();
                paint(); changed();
                try { if (window.__ae) window.__ae('feature_use', { action: 'whole_screen', mode: mode }); } catch (_) {}
                return mode;
            });
        },
        exit: function () {
            if (!mode) return Promise.resolve(false);
            var was = mode;
            mode = null;
            leaveOverlay(); paint(); changed();
            if (was === 'fullscreen' && typeof window.__safeExitFullscreen === 'function') {
                return window.__safeExitFullscreen().then(function () { return true; });
            }
            return Promise.resolve(true);
        },
        toggle: function () { return mode ? api.exit() : api.enter(); },
        active: function () { return mode; },
        destroy: function () {
            doc.removeEventListener('fullscreenchange', onFullscreenChange);
            doc.removeEventListener('webkitfullscreenchange', onFullscreenChange);
            doc.removeEventListener('keydown', onKey);
            if (mode) { mode = null; leaveOverlay(); paint(); }
        }
    };
    return api;
};
/* __wholeScreen:end */

// ── The length of a media FILE the browser has not measured yet ──────────────
// (29.09.2026, support chat 655.) Every Chromium browser — Chrome, Yandex,
// Edge, Opera, Android — reports `duration === Infinity` at `loadedmetadata`
// for a file whose container does not STORE its length: WebM from
// MediaRecorder (screen and camera recordings, «online recorders», this
// site's own voice-over recorder), FLAC written to a pipe. Firefox walks the
// file and answers itself. Infinity is «not recorded», not «endless», and
// tools that took it as a length did endless things with it: /slideshow-maker
// encoded a show forever at «2 %»; a site-wide read found 35 more places
// (timeline rulers stepping toward Infinity, `durationSec: Infinity` into the
// encoder, `-t Infinity` into FFmpeg, bitrate = size / Infinity = 0).
//
// Asking the element for a time past its end makes Chromium walk to the last
// packet and publish the real length in `durationchange` — after that
// `el.duration` itself is finite for every reader. Measured (Chrome 154):
// a 40-s WebM 0.3–2 s, a 1-hour WebM (50 MB) 0.55 s, a 10-min piped FLAC 0.2 s.
//
//   __mediaLength(el) → Promise<seconds>   finite, or 0 when it cannot be known.
//   Walks at most once per element (the promise is shared), puts the playhead
//   back where it was.
//
// And the net under the tools that never ask: a capture listener holds a
// file-backed element's `loadedmetadata` while its length is Infinity, walks
// it, and replays `loadedmetadata` (then `loadeddata` / `canplay` as far as the
// element got) — so the tool's own handler reads a finite `duration`, and the
// events the walk itself causes (seeking, seeked, timeupdate…) never reach
// it. Only `blob:` sources: a camera / screen / WebRTC stream (`srcObject`)
// and an http(s) live radio really are endless and are left alone.
// Elements that never join the document (probes: `createElement('video')`,
// `new Audio(url)`) get the same listener on themselves when they are made.
(function () {
    if (typeof window === 'undefined' || typeof document === 'undefined' || window.__mediaLength) return;
    var WALK_MS = 15000;
    function finiteDur(el) {
        var d = el ? el.duration : NaN;
        return (typeof d === 'number' && isFinite(d) && d > 0) ? d : 0;
    }
    function isFileMedia(el) {
        if (!el || el.srcObject) return false;
        return /^blob:/i.test(String(el.currentSrc || el.src || ''));
    }
    /* The walk's own listeners. While an element is HELD (below), every media
       event is stopped at the element before the page's listeners see it —
       and a listener added after that stop would be stopped too; so the hold
       hands each held event to these taps itself. Handlers are idempotent:
       one registered before the hold may hear an event twice. */
    function on(el, type, fn) {
        el.addEventListener(type, fn, true);
        var t = el.__mlTaps || (el.__mlTaps = {});
        (t[type] || (t[type] = [])).push(fn);
    }
    function off(el, type, fn) {
        el.removeEventListener(type, fn, true);
        var list = el.__mlTaps && el.__mlTaps[type];
        if (list) { var k = list.indexOf(fn); if (k >= 0) list.splice(k, 1); }
    }
    function walk(el) {
        if (el.__mlWalk) return el.__mlWalk;
        var p = new Promise(function (resolve) {
            var have = finiteDur(el);
            if (have) { resolve(have); return; }
            if (el.duration !== Infinity || !isFileMedia(el)) { resolve(0); return; }
            var back = el.currentTime || 0, auto = !!el.autoplay, done = false, t1 = null;
            if (auto) el.autoplay = false;          // playback would start at the far end
            function onDur() { if (finiteDur(el)) finish(); }
            function finish() {
                if (done) return;
                done = true;
                clearTimeout(t1);
                off(el, 'durationchange', onDur);
                off(el, 'seeked', finish);
                var sec = finiteDur(el), settled = false, t2 = null;
                function restored() {
                    if (settled) return;
                    settled = true;
                    clearTimeout(t2);
                    off(el, 'seeked', restored);
                    if (auto) el.autoplay = true;
                    resolve(sec);
                }
                if (Math.abs((el.currentTime || 0) - back) < 1e-3) { restored(); return; }
                on(el, 'seeked', restored);
                t2 = setTimeout(restored, 3000);
                try { el.currentTime = back; } catch (_) { restored(); }
            }
            on(el, 'durationchange', onDur);
            on(el, 'seeked', finish);
            t1 = setTimeout(finish, WALK_MS);
            try { el.currentTime = 1e101; } catch (_) { finish(); }
        });
        el.__mlWalk = p;
        p.then(function () { el.__mlWalk = null; });
        return p;
    }
    window.__mediaLength = function (el) {
        var d = finiteDur(el);
        if (d) return Promise.resolve(d);
        if (!el) return Promise.resolve(0);
        if (el.readyState >= 1) return walk(el);
        return new Promise(function (resolve) {
            el.addEventListener('loadedmetadata', function () { walk(el).then(resolve); }, { once: true });
        });
    };

    var HOLD_MS = 6000;
    var HELD = ['durationchange', 'loadeddata', 'canplay', 'canplaythrough', 'seeking', 'seeked',
                'timeupdate', 'progress', 'waiting', 'playing', 'play', 'pause', 'ended', 'suspend', 'stalled'];
    function onMeta(e) {
        var el = e.target;
        if (!el || el.__mlReplay || el.duration !== Infinity || !isFileMedia(el)) return;
        e.stopImmediatePropagation();
        if (el.__mlHolding) return;              // the other listener of the pair got it first
        el.__mlHolding = true;
        // Capture listeners AT the target run before the page's own ones there.
        function swallow(ev) {
            ev.stopImmediatePropagation();
            var list = el.__mlTaps && el.__mlTaps[ev.type];
            if (list) list.slice().forEach(function (fn) { try { fn(ev); } catch (_) {} });
        }
        for (var i = 0; i < HELD.length; i++) el.addEventListener(HELD[i], swallow, true);
        var t0 = Date.now();
        /* The page waits for this event, so the hold is bounded: Chromium walks
           a file in 0.3–2 s (a 1-hour WebM in 0.55 s); a build that never
           publishes the length must not keep a tool waiting for the whole walk.
           Past the bound the event goes out as it is and the walk finishes
           behind it — its `durationchange` then reaches the page as usual. */
        Promise.race([walk(el), new Promise(function (r) { setTimeout(function () { r(finiteDur(el)); }, HOLD_MS); })]).then(function (sec) {
            for (var j = 0; j < HELD.length; j++) el.removeEventListener(HELD[j], swallow, true);
            el.__mlHolding = false;
            el.__mlReplay = true;
            try {
                el.dispatchEvent(new Event('durationchange'));
                el.dispatchEvent(new Event('loadedmetadata'));
                if (el.readyState >= 2) el.dispatchEvent(new Event('loadeddata'));
                if (el.readyState >= 3) el.dispatchEvent(new Event('canplay'));
                if (el.readyState >= 4) el.dispatchEvent(new Event('canplaythrough'));
            } catch (_) {
            } finally { el.__mlReplay = false; }
            try {
                if (window.__ae) window.__ae('feature_use', {
                    f: 'media_length_walked', tag: String(el.tagName || '').toLowerCase(),
                    sec: Math.round(sec * 10) / 10, ms: Date.now() - t0, attached: el.isConnected ? 1 : 0,
                });
            } catch (_) {}
        });
    }
    // Elements in the document: one capture listener catches them all, however
    // they were made (template HTML, innerHTML, createElement + append).
    try { document.addEventListener('loadedmetadata', onMeta, true); } catch (_) {}
    // Elements that never join the document (a probe `createElement('video')`,
    // `new Audio(url)`) are outside the document's capture path: they get the
    // same listener, on themselves, the moment they are made — before the page
    // can add its own.
    function arm(el) {
        if (!el || el.__mlArmed) return el;
        el.__mlArmed = true;
        try { el.addEventListener('loadedmetadata', onMeta, true); } catch (_) {}
        return el;
    }
    try {
        var ce = Document.prototype.createElement;
        Document.prototype.createElement = function (tag) {
            var el = ce.apply(this, arguments);
            if (typeof tag === 'string' && /^(video|audio)$/i.test(tag)) arm(el);
            return el;
        };
    } catch (_) {}
    try {
        var NativeAudio = window.Audio;
        if (typeof NativeAudio === 'function') {
            var ArmedAudio = function Audio(src) {
                return arm(arguments.length ? new NativeAudio(src) : new NativeAudio());
            };
            ArmedAudio.prototype = NativeAudio.prototype;
            window.Audio = ArmedAudio;
        }
    } catch (_) {}
})();

// ── a decorative loop rests while it is off screen ──────────────────────
//
// The glow behind a tool's intro is an infinite animation of opacity. On a
// page that just sits there it costs the main thread nothing (the compositor
// runs it, in steps — see tool-intro.blade.php). But a page that runs a frame
// loop — a player, a visualiser, a game — makes a main-thread frame every
// 16 ms, and on each of them the engine ticks every running animation and
// recalculates the style of its element, even one scrolled far away.
// Measured 2026-10-07, /trim playing with the intro off screen: 75 style
// passes a second against 7 with the glow paused; 6–8 ms of main thread a
// second, about 1 % of a core — on every tool page that plays anything.
// So the intro says when it is off screen, and its CSS pauses the glow there
// (`.tool-intro--away::before`). Paused, it resumes where it stopped.
(function () {
    function rest() {
        try {
            var intro = document.querySelector('.tool-intro');
            if (!intro || typeof IntersectionObserver !== 'function') return;
            // the glow reaches 40 px beyond the block, and its blur a little further
            new IntersectionObserver(function (list) {
                intro.classList.toggle('tool-intro--away', !list[list.length - 1].isIntersecting);
            }, { rootMargin: '96px 0px' }).observe(intro);
        } catch (_) {}
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', rest);
    else rest();
})();

// ── window.__perPixel(el, prop, value[, unit]) · window.__setText(el, text) ──
//
// A page that plays something redraws its playhead and its clock from an
// animation-frame loop — `playhead.style.left = pct + '%'`,
// `clock.textContent = …` — sixty times a second. What the two SHOW changes
// far less often: a playhead on a three-minute track reaches a new pixel
// about five times a second, a clock that reads «1:07» changes once a
// second. Every write in between asks the browser for a style pass, a
// layout, a paint and a frame that is, pixel for pixel, the frame before it.
//
// Measured 2026-10-07 as CPU time of the browser's own processes, real GPU,
// production and the fixed page turn about on one machine:
//     /trim playing                    62 % of a core (renderer 37, GPU process 22), 75 layouts a second
//     the same after                   17 %          (renderer 12, GPU process 4),   8 layouts a second
// and by the census of every audio and video tool
// (tests/playwright/per-frame-writes-census.mjs): of 95 that played, 50 rewrote
// an element twenty and more times a second.
// On a two-core machine that loop alone is most of a core, taken from
// whatever is processing the sound.
// ⚠️ `el.textContent = theSameString` is NOT free either: the text node is
// replaced every time. /trim, 90 frames: two strings shown, 90 new text
// nodes, 90 layouts.
//
// __perPixel(el, 'left', 37.42)   — value in percent of the containing box
// __perPixel(el, 'left', 412, 'px')
//   writes `el.style[prop]` when the element would be PAINTED somewhere new,
//   and not otherwise: the element is, at every frame, on the pixel the
//   per-frame write would have put it on. A playhead that really moves a
//   pixel a frame — a short clip, a zoomed-in wave — is written every frame.
//
//   Everything this rests on was measured against the engines themselves —
//   Chromium, WebKit, Firefox (tests/playwright/per-pixel-engines-probe.mjs):
//
//   WHERE A BOX IS PAINTED (screenshots, positions a 16th of a pixel apart):
//   on the DEVICE pixel nearest to its absolute position, each edge on its
//   own — in all three engines, at 1, 1.25, 1.5, 2 and 3 device pixels per CSS
//   pixel. A box that itself sits on a fraction of a pixel moves the boundary
//   with it; a line 1.5 px wide is painted one or two pixels wide.
//   ⚠️ Playwright's `deviceScaleFactor` (and DevTools' device mode) is not a
//   screen in Chromium: layout stays in CSS pixels and so does the snapping.
//   A real scale is `--force-device-scale-factor`.
//
//   WHERE THE EDGE IS, for a value (9 734 percentages per engine, the
//   element's own rectangle read back): single-precision arithmetic, the
//   result CUT — not rounded — to a grain: a 64th of a layout pixel in
//   Chromium and Safari, a 60th in Firefox. Chromium's layout pixel is the
//   device pixel (a 2× screen lays out in 128ths of a CSS pixel), the others'
//   is the CSS pixel. The formula below gives the engine's length for every
//   value in Safari, and for every value in Chromium and Firefox but exact
//   ties, where the engine comes out a grain off (Chromium reads no more than
//   seven decimals of a percentage written to `left` / `width`). So the
//   formula is not trusted at the boundary itself: A VALUE WITHIN A GRAIN OF
//   A PIXEL BOUNDARY IS ALWAYS WRITTEN — and so is the one after it — and the
//   engine decides. Everywhere else the answer is certain.
//
//   WHERE THE EDGE STARTS comes from the element, not from a guess about its
//   box. After its first writes the element is asked where it stands (its own
//   rectangle, read right after a write, six times at most after each change
//   of size): that gives where the edge starts from — the box's own position
//   to a fraction of a pixel, a border, a centring transform — how far its other
//   edge is, which way it moves (a logical inset in a right-to-left page
//   moves backwards; until that is known, half a pixel of travel, every value
//   is written), and whether the size the percentage was counted from was the
//   right one. If the element's answers do not fit — a scaled ancestor, a
//   containing block nothing in the DOM names — the measured slope is used and
//   the band of doubt grows by an eighth of a device pixel: more writes, never
//   further than a device pixel from the asked position. An element that does
//   not answer at all (hidden, a property without effect) is simply written
//   every time.
//
//   What it also guarantees:
//     · AT REST THE VALUE IS EXACT. A skipped value is remembered, and when
//       it stops changing (pause, the end, a hidden tab — whether the loop
//       has stopped calling or goes on asking for the same position) it is
//       written as given within ~0.2 s: code that reads the style back gets
//       the true position.
//     · A WRITE FROM ANYWHERE ELSE IS SEEN. The inline style is compared with
//       what this function wrote last; if something else moved the element
//       (a reset to '0%'), the next call writes.
//     · THE BOX IS THE RIGHT ONE: the padding box of the offsetParent for an
//       absolutely positioned element, the CONTENT box of the parent for one
//       in the flow, the viewport for a fixed one — to a fraction of a pixel.
//       And IT IS MEASURED AGAIN when the box changes size (ResizeObserver)
//       or the window does (a zoom is a resize) — zooming a waveform changes
//       the step. A size is never read on a frame for its own sake: that read
//       forces the very layout this is here to spare.
//     · NOTHING TO MEASURE WITH → EVERY WRITE GOES THROUGH. No WeakMap, no
//       ResizeObserver, a box with no size (hidden): it behaves as the plain
//       assignment it replaces.
//   Returns true when it wrote.
//   ⚠️ A CSS `transition` on the property turns every write into several
//   frames of layout — a playhead driven from a frame loop should not have one.
//
// __setText(el, text) — writes when the text differs. An element holding one
// text node gets its data changed in place; anything else is `textContent`.
//
// For everyone, not for weak machines only: the pixels are the same, so there
// is nothing to trade. Until this file loads, both names are the plain writes
// (a shim in the layout). Story — docs/why/primitives/per-pixel-writes.md.
(function () {
    var WM = typeof WeakMap === 'function';
    var states = WM ? new WeakMap() : null;      // element → { prop → state }
    var boxes = WM ? new WeakMap() : null;       // box → its measured sizes and edges
    var epoch = 0;                               // goes up when the window changes size: everything is measured again
    var ro = null;
    try {
        if (WM && typeof ResizeObserver === 'function') {
            ro = new ResizeObserver(function (list) {
                for (var i = 0; i < list.length; i++) {
                    var t = list[i].target, b = boxes.get(t), cr = list[i].contentRect;
                    if (!b) continue;
                    // a box taken out of the page (it reports a size of nothing) is let go: an observer
                    // must not be what keeps a removed player in memory. Used again, it is watched again.
                    if (t.isConnected === false) { b.dead = true; boxes.delete(t); try { ro.unobserve(t); } catch (_) {} continue; }
                    // the observer also reports once when it starts watching: the size just measured is not news
                    if (cr && b.m && Math.abs(cr.width - b.cw) < 0.01 && Math.abs(cr.height - b.ch) < 0.01) continue;
                    b.m = false;
                }
            });
        }
    } catch (_) { ro = null; }
    try { window.addEventListener('resize', function () { epoch++; }); } catch (_) {}

    var F = Math.fround || function (x) { return x; };
    // The grain of layout: 64ths of a layout pixel in Chromium and Safari, 60ths in Firefox.
    // Chromium lays out in DEVICE pixels, the other two in CSS pixels. Chromium is told by
    // two signs of its own (not by `window.chrome`: a browser on iOS may carry that name
    // over WebKit). A wrong guess is survived — the element's readings do not fit the grain,
    // and the doubt grows — it only costs writes.
    var G = 64, DEVICE_LAYOUT = false;
    try { if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('-moz-appearance', 'none')) G = 60; } catch (_) {}
    try { DEVICE_LAYOUT = G === 64 && ((typeof Intl !== 'undefined' && typeof Intl.v8BreakIterator === 'function') || (typeof navigator !== 'undefined' && !!navigator.userAgentData)); } catch (_) {}
    // How near a pixel boundary the formula is not trusted, in grains: one (two in Firefox,
    // where a tie can fall either way).
    var DOUBT = G === 60 ? 2 : 1;
    var VERTICAL = { top: 1, bottom: 1, height: 1 };
    var SIZE = { width: 1, height: 1 };
    var BACK = { right: 1, bottom: 1, insetInlineEnd: 1 };
    var FAR = [0.5, 4, 32, 256, 1024];           // how far on (px) the element is asked again
    var pending = [], timer = 0;

    function frac(x) { return x - Math.floor(x); }
    // a computed length as the engine holds it: on its grain (g grains to a CSS pixel)
    function held(text, g) {
        var x = (parseFloat(text) || 0) * g, n = Math.round(x);
        return (Math.abs(x - n) < 0.02 ? n : Math.floor(x)) / g;
    }
    function watch(box) {
        var b = boxes.get(box);
        if (!b) {
            b = { pw: 0, ph: 0, cw: 0, ch: 0, px: 0, py: 0, cx: 0, cy: 0, m: false, e: -1, n: 0, dead: false };
            boxes.set(box, b);
            try { ro.observe(box); } catch (_) {}
        }
        return b;
    }
    // Which box a percentage is OF. An absolutely positioned element: the padding box of
    // its offsetParent. A fixed one: the viewport. Anything in the flow: the CONTENT box
    // of its parent — a fill inside a padded track is a few percent narrower than the
    // track, and counting from the track put it on the wrong pixel in 14 checks of 46.
    function resolveBox(k, el) {
        var pos = '', p = el.parentElement, o = null, root = document.documentElement;
        try { pos = getComputedStyle(el).position; } catch (_) {}
        try { o = el.offsetParent; } catch (_) {}
        k.parent = p;
        k.again = 0;
        k.fixed = pos === 'fixed';
        if (k.fixed) { k.boxEl = root; k.content = false; }
        else if (pos === 'absolute') {
            k.boxEl = (o && o !== document.body) ? o : root;
            k.content = false;
            // an element that is not displayed has no offsetParent to name: ask again later
            if (!o) k.again = Date.now() + 500;
        }
        else { k.boxEl = p || root; k.content = true; }
        k.box = k.boxEl ? watch(k.boxEl) : null;
        k.seen = -1;
    }
    // The box: its size in CSS px (0 = cannot tell now) and, in k.off, a first estimate of
    // where its edge sits on the page — the element's own answer (ask(), below)
    // replaces the estimate. Measured once and again only after the observer says the box
    // changed or the window did: reading a size forces the layout this is here to spare.
    function measure(k, el, prop, vertical, g) {
        if (!k.box || k.box.dead || k.parent !== el.parentElement || (k.again && Date.now() > k.again)) resolveBox(k, el);
        var b = k.box;
        if (!b) return 0;
        if (!b.m || b.e !== epoch) {
            var box = k.boxEl, r = null, cs = null;
            var cw = box.clientWidth || 0, ch = box.clientHeight || 0;
            if (box === document.documentElement) {
                // the viewport: what a fixed element, or one with no positioned ancestor, is laid out in
                b.pw = b.cw = cw; b.ph = b.ch = ch; b.px = b.py = b.cx = b.cy = 0;
            } else {
                try { r = box.getBoundingClientRect(); } catch (_) {}
                try { cs = getComputedStyle(box); } catch (_) {}
                var ow = box.offsetWidth || 0, oh = box.offsetHeight || 0;
                var bl = cs ? held(cs.borderLeftWidth, g) : 0, br = cs ? held(cs.borderRightWidth, g) : 0;
                var bt = cs ? held(cs.borderTopWidth, g) : 0, bb = cs ? held(cs.borderBottomWidth, g) : 0;
                var pl = cs ? held(cs.paddingLeft, g) : 0, pr = cs ? held(cs.paddingRight, g) : 0;
                var pt = cs ? held(cs.paddingTop, g) : 0, pb = cs ? held(cs.paddingBottom, g) : 0;
                // a scrollbar takes its width out of the padding box
                var sx = ow - cw - Math.round(bl + br), sy = oh - ch - Math.round(bt + bb);
                if (!(sx >= 3)) sx = 0;
                if (!(sy >= 3)) sy = 0;
                // to a fraction of a pixel where the box is not scaled by a transform; whole pixels otherwise
                var flat = !!r && ow > 0 && Math.abs(r.width - ow) < 1 && Math.abs(r.height - oh) < 1;
                b.pw = flat ? Math.max(0, r.width - bl - br - sx) : cw;
                b.ph = flat ? Math.max(0, r.height - bt - bb - sy) : ch;
                b.px = r ? r.left + bl + (window.pageXOffset || 0) : 0;
                b.py = r ? r.top + bt + (window.pageYOffset || 0) : 0;
                b.cw = Math.max(0, b.pw - pl - pr); b.ch = Math.max(0, b.ph - pt - pb);
                b.cx = b.px + pl; b.cy = b.py + pt;
            }
            b.m = true;
            b.e = epoch;
            b.n++;
        }
        if (b.n !== k.seen) {
            // the box was measured anew: what the element told us before is void
            k.seen = b.n;
            k.off = vertical ? (k.content ? b.cy : b.py) : (k.content ? b.cx : b.px);
            // (which way it moves is not: a box that changes size does not turn a playhead round)
            if (!k.dir) k.sign = BACK[prop] === 1 ? -1 : 1;
            k.scale = 1; k.wide = 0; k.loose = k.odd; k.asked = 0; k.tries = 0; k.tried = 0; k.p1 = null; k.step = 0;
            k.q = NaN;
        }
        return vertical ? (k.content ? b.ch : b.ph) : (k.content ? b.cw : b.pw);
    }
    // The length the engine lays out for a value, CSS px: single-precision arithmetic on
    // layout pixels, cut to the grain (g grains to a CSS pixel, G to a layout pixel).
    function laid(value, unit, size, g) {
        var x;
        if (unit === 'px') x = F(value * (g / G)) * G;
        else {
            var n = size * g, whole = Math.round(n);                      // the box, in grains: a whole number of them
            x = F(F(F(Math.abs(n - whole) < 0.01 ? whole : n) * F(value)) / 100);
        }
        return (x < 0 ? Math.ceil(x) : Math.floor(x)) / g;
    }
    // Where the moving edge is for that length, in page coordinates (CSS px). The whole position, not its
    // fraction: at 1.5 device pixels to a CSS pixel a whole CSS pixel is not a whole number of device pixels.
    function where(k, len) { return k.off + k.sign * k.scale * len; }
    // The device pixel an edge is painted on — or NaN (equal to nothing) when it is too near a
    // boundary for the formula to say which side it is on. The engine's own length is the
    // formula's or a grain off it: the doubt is from a grain below the boundary up to (not
    // including) a grain above. Where the edge starts was READ, and a rectangle is read in
    // single precision (Chromium, Firefox): the limits give way by that much.
    function pixel(k, pos, d, g) {
        var band = DOUBT / g + (k.loose ? 0.125 / d : 0);
        var noise = 1e-7 + Math.abs(pos) * 1.2e-7;
        var y = (frac(pos * d) - 0.5) / d;
        return (y >= -band - noise && y < band - noise) ? NaN : Math.round(pos * d);
    }
    // How far the element's other edge is from the one followed, when that is not a whole
    // number of device pixels (0 otherwise, and for a size: there the other edge stays).
    function wideOf(prop, a, z, d) {
        if (SIZE[prop] === 1) return 0;
        var w = z - a, f = frac(w * d);
        return (f < 0.004 || f > 0.996) ? 0 : w;
    }
    // Ask the element where it stands, right after a write (the value in the style is the
    // one just asked for). The first reading gives where its edge starts from and how wide
    // it is. Later ones — half a pixel on, then 4, 32, 256 and 1024 px — say which edge
    // moves, which way, and how far per pixel asked: a slope that is not one (beyond what
    // two readings good to a grain can tell at that distance) means the percentage is not
    // of the box measured, and the measured slope is used instead. Until the second
    // reading the direction is not known, and every value is written.
    function ask(k, el, prop, len, vertical, d, g) {
        if (k.p1 && Math.abs(len - k.p1.len) < FAR[k.step]) return;       // not far enough yet for the next reading
        var now = Date.now(), r;
        // an element that did not answer (hidden, held by a transition) is asked again a few
        // times a second, then once in two: each asking is a layout
        if (k.tries && now - k.tried < (k.tries < 8 ? 250 : 2000)) return;
        try { r = el.getBoundingClientRect(); } catch (_) { k.tries = 99; k.tried = now; return; }
        if (!(r.width > 0 || r.height > 0)) { k.tries++; k.tried = now; return; }   // not on the page now
        var page = k.fixed ? 0 : (vertical ? (window.pageYOffset || 0) : (window.pageXOffset || 0));
        var a = (vertical ? r.top : r.left) + page, z = (vertical ? r.bottom : r.right) + page;
        // a reading is on the engine's grain unless something between the element and the page
        // scales or shifts it — then the formula is only a guide
        var slack = 0.03 + Math.abs(z) * g * 1.2e-7;
        if (Math.abs(a * g - Math.round(a * g)) > slack || Math.abs(z * g - Math.round(z * g)) > slack) k.loose = true;
        if (!k.p1) {
            // the edge followed: the one known to move, or — before that is known — the far
            // edge of a size and the near edge of anything else
            var near = k.dir ? k.edgeA : SIZE[prop] !== 1;
            k.p1 = { len: len, a: a, z: z };
            k.off = (near ? a : z) - k.sign * len;
            k.wide = near ? wideOf(prop, a, z, d) : -wideOf(prop, a, z, d);
            k.asked = 1; k.step = 0; k.tries = 0;
            return;
        }
        var dl = len - k.p1.len, da = a - k.p1.a, dz = z - k.p1.z;
        var first = Math.abs(da) >= Math.abs(dz), moved = first ? da : dz;
        if (Math.abs(moved) < 0.25) { k.tries++; k.tried = now; return; } // it did not move: a transition, a property without effect
        var slope = moved / dl, s = Math.abs(slope);
        k.sign = slope < 0 ? -1 : 1; k.edgeA = first; k.dir = true; k.tries = 0;
        if (Math.abs(s - 1) <= 4 / g / Math.abs(dl)) k.scale = 1;
        else { k.scale = s; k.loose = true; k.odd = true; }
        k.off = (first ? a : z) - k.sign * k.scale * len;
        k.wide = first ? wideOf(prop, a, z, d) : -wideOf(prop, a, z, d);
        if (++k.step >= FAR.length) k.asked = 2;
    }
    function put(k, el, prop, value, unit) {
        el.style[prop] = value + unit;
        k.str = el.style[prop];
        k.val = value;
        k.pend = null;
    }
    function flush() {
        timer = 0;
        var now = Date.now(), keep = [];
        for (var i = 0; i < pending.length; i++) {
            var k = pending[i];
            if (k.pend === null) { k.queued = false; continue; }          // written since
            if (now - k.at < 100) { keep.push(k); continue; }             // still moving
            // the value has come to rest: what was asked for last is what stays on the page —
            // unless something else has moved the element in the meantime
            try { if (k.el.style[k.prop] === k.str) { put(k, k.el, k.prop, k.pend, k.unit); k.q = NaN; } } catch (_) {}
            k.pend = null;
            k.queued = false;
        }
        pending = keep;
        if (pending.length) timer = setTimeout(flush, 120);
    }

    // ⚠️ RTL. A playhead is usually styled `inset-inline-start:0` and moved by writing `left`.
    // In a right-to-left page `inset-inline-start` IS `right`, and with a width set
    // `left + right + width` is over-constrained: the browser drops `left` there, and every
    // write below moves nothing (the element answers «did not move», and the writes go on).
    // Measured 2026-10-16 on tembrica.com/ar: nine playheads in seven tools (mixer, join ×2,
    // chapter splitter, looper, ACX mastering, karaoke, video trim ×2) stood at the right edge
    // for the whole playback while the clock ran. Time on those canvases runs left to right in
    // every language, so a box this primitive moves by `left` is freed from a pinned `right` —
    // once, the first time it is written while laid out. In a left-to-right page `right` was
    // already the one dropped: nothing changes there. A box moved by its logical inset
    // (`insetInlineStart` — mirrored on purpose) is not touched.
    // Test — tests/js/per-pixel-rtl-unpin.test.mjs; stand — tests/playwright/rtl-playheads-follow-time.mjs.
    var unpinned = typeof WeakSet === 'function' ? new WeakSet() : null;
    function unpinRtl(el) {
        if (!unpinned || unpinned.has(el)) return;
        try {
            var cs = window.getComputedStyle(el);
            if (!cs || !cs.direction) return;                              // not laid out yet: ask again next time
            unpinned.add(el);
            if (cs.direction === 'rtl' && cs.position === 'absolute' && cs.right && cs.right !== 'auto') el.style.right = 'auto';
        } catch (e) {}
    }

    window.__perPixel = function (el, prop, value, unit) {
        if (!el || !el.style) return false;
        unit = unit || '%';
        value = +value;
        if (!isFinite(value)) return false;
        if (prop === 'left') unpinRtl(el);
        if (!states || !ro) { el.style[prop] = value + unit; return true; }

        var byProp = states.get(el);
        if (!byProp) { byProp = {}; states.set(el, byProp); }
        var k = byProp[prop];
        if (!k) k = byProp[prop] = { el: el, prop: prop, unit: unit, val: NaN, str: null, pend: null, at: 0, queued: false, box: null, boxEl: null, seen: -1, q: NaN, q2: NaN, dir: false, edgeA: true, odd: false };
        var mine = el.style[prop] === k.str;                               // nobody else has written it since
        if (mine && value === k.val && unit === k.unit) { k.pend = null; return false; }   // exactly what is there already
        k.unit = unit;

        var d = window.devicePixelRatio || 1;
        var g = G * (DEVICE_LAYOUT ? d : 1);                               // grains to a CSS pixel
        var vertical = VERTICAL[prop] === 1;
        var size = measure(k, el, prop, vertical, g);
        if (unit !== 'px' && !size) { put(k, el, prop, value, unit); k.q = NaN; return true; }
        var len = laid(value, unit, size, g), pos = where(k, len);

        if (mine && k.dir && pixel(k, pos, d, g) === k.q && (k.wide === 0 || pixel(k, pos + k.wide, d, g) === k.q2)) {
            // the same device pixel, for certain: nothing new to show. (The clock of «at rest»
            // starts when the VALUE last changed: a loop that never stops and goes on asking for
            // the same position — a paused player — must get it written exactly too.)
            if (k.pend !== value) { k.pend = value; k.at = Date.now(); }
            if (!k.queued) { k.queued = true; pending.push(k); }
            if (!timer) timer = setTimeout(flush, 120);
            return false;
        }
        put(k, el, prop, value, unit);
        if (k.asked < 2) { ask(k, el, prop, len, vertical, d, g); pos = where(k, len); }
        // NaN near a boundary: there the engine decided, and the next value is written as well
        k.q = pixel(k, pos, d, g);
        if (k.wide !== 0) k.q2 = pixel(k, pos + k.wide, d, g);
        return true;
    };

    window.__setText = function (el, text) {
        if (!el) return false;
        text = (text === null || text === undefined) ? '' : String(text);
        var n = el.firstChild;
        if (n && n === el.lastChild && n.nodeType === 3) {
            if (n.nodeValue === text) return false;
            n.nodeValue = text;
            return true;
        }
        if (!n && text === '') return false;
        el.textContent = text;
        return true;
    };
})();

// ── window.__seekPresented(videoEl, t, timeoutMs) → Promise<via> ─────────
//
// Seek a <video> and resolve only once the target frame has actually been
// PRESENTED for composition — i.e. when drawImage() will capture real
// pixels. `seeked` alone does not guarantee that: engines fire it when the
// seek algorithm completes, which can precede the decoded frame reaching
// the compositor (hardware decode paths, busy main thread). drawImage()
// right inside a `seeked` handler then captures a transparent/stale
// canvas. Field telemetry on /video-upscaler measured 4.1% of frame
// analyses (19/461 over 30 d, desktop AND mobile, chrome/firefox/yandex)
// hitting exactly this race; a 2026-07-10 sweep found the same
// single-shot capture-after-seeked pattern in 14 more tools (exports,
// thumbnails, previews, tracking analysis).
//
// Wait ladder after `seeked`:
//   1. requestVideoFrameCallback — the spec "frame was presented" signal
//      (Chrome 87+, Safari 15.4+, Firefox 132+). Fires for the seeked-to
//      frame of a paused video.
//   2. 300 ms timer racing rVFC — a paused video that presented the frame
//      BEFORE the callback was registered never fires it (no new frames
//      are coming); the timer proceeds once composition has had time.
//   3. double-rAF where rVFC is unavailable.
//
// Seeking to the CURRENT position is spec'd to abort without a `seeked`
// event (Firefox honors this) — detected up front, skips straight to the
// presentation wait. Pass t = null/undefined to skip seeking and just
// wait for the CURRENT frame to be presented (for callers that manage
// currentTime themselves).
//
// Resolves with how the wait ended ('rvfc' | 'rvfc_timer' | 'raf' |
// '*_already_there' | '*_no_seek' | '*_after_data' | 'timeout' |
// 'seek_error') for telemetry. '*_after_data': the element was still
// seeking or had no decoded frame, and the wait began only once it had one. NEVER rejects. addEventListener only — safe next to
// caller-owned `onseeked` properties.
//
// Canonical copy; window.__videoProbe.seekPresented delegates here.
window.__seekPresented = function (v, t, timeoutMs) {
    return new Promise(function (resolve) {
        var done = false;
        var outer = setTimeout(function () { finish('timeout'); }, timeoutMs || 4000);
        var untilFrame = null;
        function finish(via) {
            if (done) return;
            done = true;
            clearTimeout(outer);
            v.removeEventListener('seeked', onSeekedLadder);
            v.removeEventListener('seeked', onSeekedSafety);
            dropUntilFrame();
            resolve(via);
        }
        // Post-seeked wait — for cases where the frame may ALREADY have
        // been presented before we could register rVFC (no-seek / same
        // position). The 300 ms timer covers exactly that ambiguity.
        function waitPresented(suffix) {
            if (typeof v.requestVideoFrameCallback === 'function') {
                v.requestVideoFrameCallback(function () { finish('rvfc' + suffix); });
                setTimeout(function () { finish('rvfc_timer' + suffix); }, 300);
            } else {
                requestAnimationFrame(function () {
                    requestAnimationFrame(function () { finish('raf' + suffix); });
                });
            }
        }
        function onSeekedLadder() {
            v.removeEventListener('seeked', onSeekedLadder);
            waitPresented('');
        }
        // Safety net for the pre-registered-rVFC path: if rVFC somehow
        // never fires for this seek, resolve 300 ms after `seeked` rather
        // than eating the full outer timeout. Keeps per-frame export
        // loops bounded (worst case +300 ms, normal case = presentation
        // latency of a few ms).
        function onSeekedSafety() {
            v.removeEventListener('seeked', onSeekedSafety);
            setTimeout(function () { finish('rvfc_timer_post_seek'); }, 300);
        }
        // "Already there" is a statement about the CLOCK, not about the picture.
        // A seek to this spot may still be in flight (the caller, or another
        // call on the same element, started it), and an element loaded with
        // preload=metadata may hold no decoded frame at all. Both branches
        // below used to go straight to waitPresented(), whose 300 ms timer
        // then resolved at readyState 1 — drawImage() painted nothing. On
        // /video-overlay (2026-09-26) the scene asked for the very second the
        // key-colour auto-detect was already seeking to, and the preview came
        // up WITHOUT the overlay in 3 runs of 5, never redrawn. So: first a
        // frame (`seeked` / `loadeddata` / `canplay` with readyState >= 2),
        // then the usual presentation wait; the outer timeout still bounds it.
        // Guard: tests/playwright/seek-presented-waits-for-the-frame.mjs.
        function dropUntilFrame() {
            if (!untilFrame) return;
            v.removeEventListener('seeked', untilFrame);
            v.removeEventListener('loadeddata', untilFrame);
            v.removeEventListener('canplay', untilFrame);
            untilFrame = null;
        }
        function whenFrame(suffix) {
            if (!v.seeking && v.readyState >= 2) { waitPresented(suffix); return; }
            untilFrame = function () {
                if (done || v.seeking || v.readyState < 2) return;
                dropUntilFrame();
                waitPresented(suffix + '_after_data');
            };
            v.addEventListener('seeked', untilFrame);
            v.addEventListener('loadeddata', untilFrame);
            v.addEventListener('canplay', untilFrame);
        }
        if (t == null) {
            whenFrame('_no_seek');
            return;
        }
        if (Math.abs((v.currentTime || 0) - t) < 0.01) {
            whenFrame('_already_there');
            return;
        }
        if (v.paused && typeof v.requestVideoFrameCallback === 'function') {
            // PAUSED video, real seek: register rVFC BEFORE seeking. A
            // paused video presents no spontaneous frames, so the next
            // presentation IS our seeked-to frame — the "presented before
            // registration" race is structurally impossible and the 300 ms
            // ambiguity timer is not needed. This keeps per-frame export
            // loops (seek → capture × thousands of frames) at
            // presentation-latency cost instead of a timer-bound one.
            v.requestVideoFrameCallback(function () { finish('rvfc_preseek'); });
            v.addEventListener('seeked', onSeekedSafety);
        } else {
            // Playing video (frames present continuously — a pre-seek
            // registration would fire with a PRE-seek frame) or no rVFC:
            // wait for `seeked`, then run the post-seeked ladder.
            v.addEventListener('seeked', onSeekedLadder);
        }
        try { v.currentTime = t; } catch (_) { finish('seek_error'); }
    });
};

// ── Free-tier export watermark (shared primitive) ───────────────────────────
// The Premium nudge: free exports carry a small semi-transparent wordmark in
// the bottom-right corner; paid tiers export clean. WHO is per-tier config
// (tiers.php 'watermark' → window.__tierLimits.watermark), WHERE is the
// rollout scope (tiers.php 'watermark_scope' → the tool blade inlines the
// PageCache-safe boolean and the per-domain text via watermark_scope_active()
// / watermark_text()). A tool's export path calls:
//   if (window.__wmShould(WM_SCOPE)) window.__wmDraw(ctx, w, h, WM_TEXT);
// Deliberately "annoying but not too much" (Farid 2026-07-10): corner
// placement, 60% alpha, ~2.6% of frame height — visible on shared clips,
// never over the content.
window.__wmShould = function (scopeActive) {
    if (!scopeActive) return false;
    var t = window.__tierLimits;
    if (t && typeof t.watermark === 'boolean') return t.watermark;
    if (t && t.is_pro) return false; // older cached bootstrap payloads lack .watermark
    return true;                     // guests / unknown tier → free default
};
window.__wmDraw = function (c, w, h, text) {
    var fs = Math.max(11, Math.round(h * 0.026));
    c.save();
    c.font = '600 ' + fs + 'px Inter, system-ui, -apple-system, sans-serif';
    c.textAlign = 'right';
    c.textBaseline = 'alphabetic';
    c.shadowColor = 'rgba(0,0,0,0.55)';
    c.shadowBlur = Math.max(2, Math.round(fs * 0.3));
    c.shadowOffsetX = 0;
    c.shadowOffsetY = 1;
    c.globalAlpha = 0.6;
    c.fillStyle = '#ffffff';
    c.fillText(text || 'timbrica.com', w - Math.round(w * 0.022), h - Math.round(h * 0.04));
    c.restore();
};
/* The same mark as a transparent PNG, for lanes that do NOT paint frames in JS
 * — an FFmpeg-WASM encode gets it as `-i wm.png` + one `overlay` filter.
 *
 * ⚠️ Why a PNG and not drawtext: drawtext needs a font file inside the WASM FS,
 * and shipping one (plus picking a face that covers Cyrillic/Arabic) is a whole
 * dependency. /video-watermark solved this in 2026-06 by compositing on a canvas
 * first — full browser font support, one filter, uniform for text and images.
 * This is that trick, extracted so the free-tier mark can reuse it.
 *
 * Returns a Promise<Uint8Array>, or null when the canvas/PNG path is unavailable
 * (the caller then encodes UNMARKED — fail-safe, same direction as everywhere
 * else in this feature: a missing mark costs a nudge, a wrong one brands a
 * paying customer). */
window.__wmOverlayPng = function (w, h, text) {
    try {
        var cv = document.createElement('canvas');
        cv.width = Math.max(2, w | 0);
        cv.height = Math.max(2, h | 0);
        var cx = cv.getContext('2d');
        if (!cx) return null;
        window.__wmDraw(cx, cv.width, cv.height, text);
        /* The shared ladder: a toBlob that never answered held the whole
           watermarked encode for good; no rung answering is `null`, the
           unmarked fail-safe above. */
        return window.__canvasBlob(cv, 'image/png').then(function (b) {
            return b.arrayBuffer();
        }).then(function (ab) { return new Uint8Array(ab); }, function () { return null; });
    } catch (_) { return null; }
};

// ── Back to top ──


/* ── Linkify user text — moved to public/js/linkify-user-text.js.
   window.__linkifyUserText / __setLinkifiedText live there so the admin layout
   (which does not load this 274 KB bundle) can linkify its support chat too.
   Loaded site-wide from layouts/app.blade.php, right before this file. */


/* ── Animated avatars: portraits at rest ──────────────────────────────────────
   Farid 2026-08-05: «может работал как фотографии в Гарри Поттере».

   An animated avatar sits still and comes alive when someone actually looks at
   it — hover, keyboard focus, or a tap on touch. Before this, every animated
   avatar looped forever at every render site at once; a forum thread with thirty
   commenters decoded thirty VP9 videos in parallel.

   Everything is delegated from document, so avatars injected later (comment
   replies, "показать ещё", the profile dialog) need no wiring. Markup contract
   is in resources/views/components/user-avatar.blade.php:

     <video data-hp-avatar="video" [data-hp-always]>   paused, poster showing
     <img   data-hp-avatar="gif" data-hp-src="…gif">   poster file, swapped on wake

   prefers-reduced-motion:reduce disables waking entirely — including the
   profile hero, whose data-hp-always is dropped. Someone who asked the OS for
   less motion did not ask for an exception on their own page. */
(function () {
    var CALM = false;
    try { CALM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    // How long a tap keeps the portrait awake. Touch has no "leave", and a
    // portrait that woke on a scroll-past and never slept again is the very
    // thing this replaces.
    var TAP_MS = 4000;
    var tapTimer = null;

    function wake(el) {
        if (CALM || !el) return;
        // Motion is opt-in per surface. Only the avatar that IS the subject of
        // its page carries data-hp-motion; a chip in the sidebar or a face in a
        // comment thread never moves, not even under the cursor. Without this
        // the delegated listener woke every avatar on every page — which is how
        // "анимация только на странице профиля" fails silently, since each
        // individual avatar looks correct on its own.
        if (!el.hasAttribute('data-hp-motion')) return;
        if (el.tagName === 'VIDEO') {
            var p = el.play();
            if (p && p.catch) p.catch(function () {});   // autoplay policy — stay still, never throw
        } else if (el.dataset.hpSrc && el.src.indexOf(el.dataset.hpSrc) === -1) {
            el.dataset.hpRest = el.src;                  // remember the poster
            el.src = el.dataset.hpSrc;
        }
    }

    function rest(el) {
        if (!el || el.hasAttribute('data-hp-always')) return;
        if (el.tagName === 'VIDEO') {
            try { el.pause(); el.currentTime = 0; } catch (e) {}
        } else if (el.dataset.hpRest && el.src !== el.dataset.hpRest) {
            el.src = el.dataset.hpRest;
        }
    }

    function target(node) {
        return node && node.closest ? node.closest('[data-hp-avatar]') : null;
    }

    document.addEventListener('mouseover', function (e) {
        var el = target(e.target);
        if (el) wake(el);
    }, { passive: true });

    document.addEventListener('mouseout', function (e) {
        var el = target(e.target);
        // Ignore moves between the element and its own children.
        if (el && !(e.relatedTarget && el.contains(e.relatedTarget))) rest(el);
    }, { passive: true });

    // Keyboard: focusin/out bubble, so a tabbing user gets the same behaviour.
    document.addEventListener('focusin', function (e) { wake(target(e.target)); });
    document.addEventListener('focusout', function (e) { rest(target(e.target)); });

    document.addEventListener('touchstart', function (e) {
        var el = target(e.target);
        if (!el) return;
        wake(el);
        clearTimeout(tapTimer);
        tapTimer = setTimeout(function () { rest(el); }, TAP_MS);
    }, { passive: true });

    /* A paused <video> paints nothing until a frame is decoded, so an avatar
       uploaded before posters existed would be an empty circle. Nudging
       currentTime forces frame 0 onto the screen. Runs once per element. */
    function paintFirstFrame(v) {
        if (v.dataset.hpPainted || v.hasAttribute('data-hp-always')) return;
        v.dataset.hpPainted = '1';
        try { if (v.paused) v.currentTime = 0; } catch (e) {}
    }
    document.addEventListener('loadeddata', function (e) {
        var el = e.target;
        if (el && el.tagName === 'VIDEO' && el.hasAttribute('data-hp-avatar')) paintFirstFrame(el);
    }, true);   // capture: media events do not bubble

    /* ── Can this browser decode a TRANSPARENT VP9? ───────────────────────────
       Avatars exported with a cut-out background are WebM with an alpha channel.
       Chromium and Firefox play them; whether Safari does could not be measured
       from here (Playwright's WebKit build has no WebM support at all — it times
       out on an ordinary opaque clip too, so it can answer nothing about alpha),
       and a browser that ignores alpha paints the transparent part BLACK — a
       black disc where the page should show through.

       So instead of guessing about anyone's browser, ask the browser: decode a
       2×2 fully transparent clip and read the pixel back. Alpha 0 → supported.
       Anything else → fall back to the still frame, which is a WebP whose
       transparency every engine handles. Runs once, only on pages that actually
       carry a transparent avatar, and the answer is cached for the tab. */
    var ALPHA_PROBE = 'data:video/webm;base64,GkXfo59ChoEBQveBAULygQRC84EIQoKEd2VibUKHgQJChYECGFOAZwEAAAAAAAIIEU2bdLpNu4tTq4QVSalmU6yBoU27i1OrhBZUrmtTrIHWTbuMU6uEElTDZ1OsggEgTbuMU6uEHFO7a1OsggHy7AEAAAAAAABZAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmsCrXsYMPQkBNgIxMYXZmNjEuMS4xMDBXQYxMYXZmNjEuMS4xMDBEiYhAj0AAAAAAABZUrmvFrgEAAAAAAAA814EBc8WIXtc6EJR/bA6cgQAitZyDdW5kiIEAhoVWX1ZQOYOBASPjg4Q7msoA4I2wgQK6gQKagQJTwIEBElTDZ/5zc59jwIBnyJlFo4dFTkNPREVSRIeMTGF2ZjYxLjEuMTAwc3PZY8CLY8WIXtc6EJR/bA5nyKRFo4dFTkNPREVSRIeXTGF2YzYxLjMuMTAwIGxpYnZweC12cDlnyKFFo4hEVVJBVElPTkSHkzAwOjAwOjAxLjAwMDAwMDAwMAAfQ7Z1yueBAKDFoaCBAAAAgkmDQgAAEAAWADgkHBnAAAAgIAARqsfr39mAAHWhoKae7oEBpZmCSYNCAAAQABYAOCQcGcAAACAgABG/zvAAHFO7a5G7j7OBALeK94EB8YIBo/CBAw==';

    function alphaCapable() {
        var cached = null;
        try { cached = sessionStorage.getItem('tmb_vp9_alpha'); } catch (e) {}
        if (cached === '1') return Promise.resolve(true);
        if (cached === '0') return Promise.resolve(false);

        return new Promise(function (resolve) {
            var done = false;
            var finish = function (ok) {
                if (done) return;
                done = true;
                try { sessionStorage.setItem('tmb_vp9_alpha', ok ? '1' : '0'); } catch (e) {}
                try { v.remove(); } catch (e) {}
                resolve(ok);
            };
            var v = document.createElement('video');
            v.muted = true; v.playsInline = true; v.src = ALPHA_PROBE; try { v.load(); } catch (_) { }
            v.style.cssText = 'position:fixed;width:2px;height:2px;opacity:0;pointer-events:none;top:0;left:0';
            document.body.appendChild(v);
            // A decode failure is not a verdict about alpha — the engine may not
            // do WebM at all — but the fallback is the same either way.
            v.addEventListener('error', function () { finish(false); });
            setTimeout(function () { finish(false); }, 4000);
            v.addEventListener('loadeddata', function () {
                var read = function () {
                    try {
                        var c = document.createElement('canvas');
                        c.width = 2; c.height = 2;
                        var g = c.getContext('2d', { willReadFrequently: true });
                        g.clearRect(0, 0, 2, 2);
                        g.drawImage(v, 0, 0, 2, 2);
                        // The clip is fully transparent: an engine that honours
                        // alpha leaves the canvas untouched.
                        finish(g.getImageData(0, 0, 1, 1).data[3] === 0);
                    } catch (e) { finish(false); }
                };
                // Drawing before a frame is painted yields zeros and would make
                // an alpha-blind engine look capable — wait for a real frame.
                if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(read);
                else setTimeout(read, 250);
                var p = v.play(); if (p && p.catch) p.catch(function () {});
            });
        });
    }

    /* An engine that cannot decode alpha VP9 used to lose the animation
       entirely — the avatar became a photograph. Since 2026-08-06 the server
       writes an ANIMATED WebP companion next to any transparent avatar
       (data-hp-alpha-src), and WebP transparency is handled by every engine
       that gets here. MEASURED, not assumed: WebKit, Chromium and Firefox all
       animate it and keep the cut-out (screenshot diff with an animated-GIF
       control, because a canvas probe reported "frozen" for all three and was
       measuring itself, not them).

       The companion is handed to the SAME markup contract the GIF branch uses
       — src = still poster, data-hp-src = the animated file — so these avatars
       keep resting and waking like every other portrait instead of becoming a
       special case. Without a companion we still fall back to the still frame:
       a black disc is the one outcome that is never acceptable. */
    function swapToStill(root) {
        var list = (root || document).querySelectorAll('video[data-hp-alpha]');
        for (var i = 0; i < list.length; i++) {
            var v = list[i];
            var poster = v.getAttribute('poster');
            if (!poster) continue;              // nothing safe to fall back to
            var anim = v.getAttribute('data-hp-alpha-src');
            var img = document.createElement('img');
            img.className = v.className;
            img.src = poster;
            img.alt = v.getAttribute('aria-label') || '';
            img.loading = 'lazy';
            img.style.cssText = v.style.cssText;
            if (anim) {
                img.setAttribute('data-hp-avatar', 'gif');
                img.setAttribute('data-hp-src', anim);
                // Carry the motion permission across. Without it the swap
                // silently demotes a profile-page avatar to a photograph for
                // exactly the visitors this companion was built for — and it
                // looks identical to "the companion did not load".
                if (v.hasAttribute('data-hp-motion')) img.setAttribute('data-hp-motion', '1');
                // The hero plays on its own everywhere else; keep that here too.
                if (v.hasAttribute('data-hp-always')) {
                    img.setAttribute('data-hp-always', '1');
                    img.src = anim;
                }
            }
            try { v.replaceWith(img); } catch (e) {}
        }
    }

    function guardTransparentAvatars() {
        if (!document.querySelector('video[data-hp-alpha]')) return;
        alphaCapable().then(function (ok) {
            if (ok) return;
            swapToStill(document);
            // Comment lists re-render themselves after hydration (tool-rating.js
            // rebuilds the whole thread), so a one-shot sweep would be undone a
            // second later — the same trap the portrait markup already fell into.
            // The observer is installed ONLY for engines that failed the probe.
            if (window.MutationObserver) {
                new MutationObserver(function (muts) {
                    for (var i = 0; i < muts.length; i++) {
                        var added = muts[i].addedNodes;
                        for (var j = 0; j < added.length; j++) {
                            var n = added[j];
                            if (n.nodeType !== 1) continue;
                            if (n.matches && n.matches('video[data-hp-alpha]')) swapToStill(n.parentNode);
                            else if (n.querySelector && n.querySelector('video[data-hp-alpha]')) swapToStill(n);
                        }
                    }
                }).observe(document.body, { childList: true, subtree: true });
            }
        });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', guardTransparentAvatars);
    else guardTransparentAvatars();

    /* Late-rendered lists (a comment thread rebuilt after hydration, "показать
       ещё") can bring in the FIRST transparent avatar on the page — and the
       check above would already have bailed out having found none. Renderers
       call this after they paint; it is idempotent and the probe answer is
       cached, so calling it often costs nothing. */
    window.__hpAvatarsRefresh = guardTransparentAvatars;

    /* Reduced motion has to beat data-hp-always too — the attribute is written
       by the profile hero at render time, before we know the visitor's setting. */
    if (CALM) {
        var stopAll = function () {
            var list = document.querySelectorAll('video[data-hp-avatar]');
            for (var i = 0; i < list.length; i++) {
                try { list[i].removeAttribute('autoplay'); list[i].pause(); } catch (e) {}
            }
        };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', stopAll);
        else stopAll();
    }
})();

/* ── Holographic avatar ───────────────────────────────────────────────────────
   Farid 2026-08-06, after the Threads post about Airbnb's verification card: a
   foil sheen over the portrait that moves with the phone. Lit by pointer
   position on desktop and by device tilt on a phone.

   Markup contract: put `data-holo` on the CONTAINER that already clips the
   avatar (the round frame), not on the media element. The shimmer layer is
   injected HERE and never rendered server-side, for two reasons:
     · a visitor with prefers-reduced-motion must not receive the markup at all,
       and the page is cached by PageCache — the server does not know who asks;
     · the blend mode depends on what the avatar turned out to be (photo vs the
       flat emoji plate), which the DOM knows and Blade does not.

   Scope is deliberate: only where the avatar IS the subject — the profile hero,
   the photo lightbox, the private-profile card and the avatar cabinet preview.
   NOT in lists. Thirty shimmering circles in a forum thread is the same mistake
   the portrait-at-rest change was made to undo, only with the phone moving
   instead of the video playing. tests/Unit/AvatarHologramScopeTest.php holds
   that line. */
(function () {
    var CALM = false;
    try { CALM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    if (CALM) return;

    var hosts = [];
    var tiltX = 0, tiltY = 0;   // -1..1, shared by every host under the tilt driver
    var frame = null;

    function clamp(v) { return v < -1 ? -1 : (v > 1 ? 1 : v); }

    /* The layer is re-created whenever it goes missing rather than once at
       startup: avatar-editor.js repaints #pfAvatarPreview and [data-avatar-live]
       with replaceChildren() when you pick a new picture, which throws away
       anything we appended. Same class of trap as tool-rating.js rebuilding the
       comment list over the portrait markup — a JS renderer silently undoes the
       component. Cheap enough to check on every write. */
    /* An ANIMATED avatar gets no sheen (Фарид 2026-08-07: «голограмма пусть
       работает только на не анимированных аватарках»). Two moving things in one
       circle compete: the clip is the thing to look at, and a foil that drifts
       with the cursor on top of it reads as interference rather than depth.
       A photo, a catalogue picture or an emoji plate holds still, and that is
       where the sheen has something to do.

       Detected by the markers the avatar component already emits, so this needs
       no second copy of "is this file animated" logic:
         video[data-hp-avatar]  — the player (a surface that may move)
         img[data-hp-avatar]    — the GIF that swaps to the animated file on hover
         img[data-hp-still]     — the still frame OF an animated avatar
       A plain photo carries none of them. */
    function isAnimatedAvatar(host) {
        return !!(host && host.querySelector &&
            host.querySelector('video[data-hp-avatar], img[data-hp-avatar], img[data-hp-still]'));
    }

    function layer(host) {
        if (isAnimatedAvatar(host)) {
            // A sheen may already be there from before the avatar changed
            // in-place (avatar-editor repaints the preview), so drop it.
            if (host.__holo && host.__holo.parentNode === host) {
                try { host.__holo.remove(); } catch (e) {}
            }
            if (host.__holoGlare && host.__holoGlare.parentNode === host) {
                try { host.__holoGlare.remove(); } catch (e) {}
            }
            host.__holo = null;
            host.__holoGlare = null;
            // The tilt rides on the same vars; clear them or the avatar keeps
            // whatever angle it was left at when the picture changed.
            host.style.removeProperty('--holo-x');
            host.style.removeProperty('--holo-y');
            return null;
        }
        var l = host.__holo;
        if (l && l.parentNode === host) return l;
        l = document.createElement('span');
        l.setAttribute('aria-hidden', 'true');
        // No <img>/<video> inside means the emoji (or initials) branch: a glyph
        // on a flat plate, which needs a different blend — see app.css.
        var media = host.querySelector('img, video');
        l.className = media ? 'holo' : 'holo holo--emoji';
        if (!media) {
            var glyph = host.querySelector('span');
            if (glyph) glyph.classList.add('holo-glyph');
        }
        host.appendChild(l);
        host.__holo = l;

        /* SECOND glare, counter-phase (2026-08-07). One sweep plus a tilt reads
           as a lit surface; two sweeps travelling opposite ways read as a FILM
           on top of that surface, which is what a holo card actually looks like.
           It is a separate element rather than a ::after inside .holo because
           .holo carries a band mask — a child would inherit it and both glares
           would live in the same stripe, cancelling the effect they exist for.
           Skipped on the emoji plate: there the foil sits UNDER the glyph and a
           second highlight over it only muddies the face (measured 08-06). */
        if (media) {
            var g = host.__holoGlare;
            if (!g || g.parentNode !== host) {
                g = document.createElement('span');
                g.setAttribute('aria-hidden', 'true');
                g.className = 'holo-glare';
                host.appendChild(g);
                host.__holoGlare = g;
            }
        }
        return l;
    }

    function write(host, x, y) {
        layer(host);
        host.style.setProperty('--holo-x', x.toFixed(3));
        host.style.setProperty('--holo-y', y.toFixed(3));
    }

    function paintTilt() {
        frame = null;
        for (var i = 0; i < hosts.length; i++) {
            if (hosts[i].isConnected) write(hosts[i], tiltX, tiltY);
        }
    }

    function collect() {
        // Animated avatars are dropped from the driver list entirely, not just
        // left without a layer: otherwise every tilt/pointer frame would still
        // walk them and write CSS variables onto a host that has nothing to
        // light.
        hosts = [].slice.call(document.querySelectorAll('[data-holo]'))
            .filter(function (h) { return !isAnimatedAvatar(h); });
        for (var i = 0; i < hosts.length; i++) layer(hosts[i]);   // static sheen from the start
    }

    /* ── Pointer driver ───────────────────────────────────────────────────────
       Desktop has no tilt sensor, but the cursor gives the same "where is the
       light coming from" signal for free — and asks nobody for permission. */
    function pointer(e) {
        var host = e.currentTarget;
        var r = host.getBoundingClientRect();
        if (!r.width || !r.height) return;
        write(host, clamp((e.clientX - r.left) / r.width * 2 - 1),
                    clamp((e.clientY - r.top) / r.height * 2 - 1));
    }

    function bindPointer() {
        for (var i = 0; i < hosts.length; i++) {
            var h = hosts[i];
            if (h.__holoBound) continue;
            h.__holoBound = 1;
            h.addEventListener('pointermove', pointer, { passive: true });
            h.addEventListener('pointerleave', function (e) { write(e.currentTarget, 0, 0); }, { passive: true });
        }
    }

    /* ── Tilt driver ──────────────────────────────────────────────────────────
       Angles are read as DELTAS from the first sample, not from an assumed
       "phone held at 45°" baseline: someone reading in bed holds it at an angle
       that would peg a fixed baseline to the edge and leave no travel at all. */
    var tilting = false;
    function startTilt() {
        if (tilting) return;
        tilting = true;
        var origin = null;
        window.addEventListener('deviceorientation', function (e) {
            if (e.beta == null || e.gamma == null) return;   // a sensorless desktop fires nulls
            if (!origin) origin = { b: e.beta, g: e.gamma };
            tiltX = clamp((e.gamma - origin.g) / 22);
            tiltY = clamp((e.beta - origin.b) / 22);
            if (!frame) frame = requestAnimationFrame(paintTilt);
        });
    }

    /* iOS keeps the sensor behind a native dialog that only a user gesture may
       raise, and Safari's "Motion & Orientation Access" setting answers denied
       without showing anything at all. So the sheen is never conditional on a
       sensor — it is there, still, either way — and the ask rides the tap the
       visitor already makes on the avatar, once per tab, with no UI of its own
       and nothing to translate. */
    function armTiltAsk() {
        var DOE = window.DeviceOrientationEvent;
        if (!DOE) return;
        if (typeof DOE.requestPermission !== 'function') { startTilt(); return; }
        var asked = false;
        document.addEventListener('touchend', function (e) {
            if (asked || !e.target || !e.target.closest || !e.target.closest('[data-holo]')) return;
            asked = true;
            try {
                DOE.requestPermission().then(function (state) {
                    if (state === 'granted') startTilt();
                }).catch(function () {});
            } catch (err) {}
        }, { passive: true });
    }

    function init() {
        collect();
        if (!hosts.length) return;
        bindPointer();
        armTiltAsk();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();

    /* Renderers that replace an avatar container call this after they paint —
       idempotent, and the only way a repainted preview gets its sheen back
       before the next pointer move. */
    window.__holoRefresh = init;
})();
/* ═══════════════════════════════════════════════════════════════════════════
   window.__printPrep(fn) — redraw a canvas for paper.

   @media print can recolour every DOM node (app.css remaps the theme tokens
   to ink), but a <canvas> is a bitmap: whatever was painted with dark-theme
   colours stays painted. Measured 2026-08-14 on /anxiety-tracker: the mood
   chart printed with grid lines at rgba(255,255,255,.08) and a trend line at
   rgba(56,189,248,.6) — 6% of the canvas carried any pixel at all, and none
   of it survived on white paper. A clinician's report came out with the chart
   area blank.

   Tools that draw charts pass their draw function here. The hook puts
   `is-printing` on <html> BEFORE calling it — that class carries the same
   paper palette as @media print, because `beforeprint` fires before print
   media is applied, so a chart sampling getPropertyValue('--border') at that
   moment would otherwise still read the screen colour.

   Usage (one line, at the end of the tool's chart setup):
       if (window.__printPrep) window.__printPrep(renderChart);

   The callback receives `true` when preparing for paper and `false` when
   restoring, for the rare tool that needs to know.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';
    var fns = [];
    var printing = false;

    function apply(on) {
        if (on === printing) return;          // browsers fire both beforeprint and the mq change
        printing = on;
        try { document.documentElement.classList.toggle('is-printing', on); } catch (e) {}
        for (var i = 0; i < fns.length; i++) {
            try { fns[i](on); } catch (e) { /* one bad chart must not block the print */ }
        }
    }

    /* Tools that inline their <script> in @section('content') run during body
       parse, i.e. before this file. The layout installs a queueing stub in
       <head> so their registration is never dropped on the floor; take it over
       here and replay what it collected. */
    var queued = (window.__printPrep && window.__printPrep.q) || [];

    window.__printPrep = function (fn) {
        if (typeof fn !== 'function') return;
        fns.push(fn);
        // A tool can register after a print has already started (lazy chart tab).
        if (printing) { try { fn(true); } catch (e) {} }
    };
    for (var q = 0; q < queued.length; q++) window.__printPrep(queued[q]);

    window.addEventListener('beforeprint', function () { apply(true); reportPrint('browser'); });
    window.addEventListener('afterprint', function () { apply(false); });

    /* ── Which tools do people actually print? ──────────────────────────────
       Until now this was unanswerable. `feature_use{f:print}` only ever fired
       from a tool's own Print button, so the data showed printing on the ~35
       tools that HAD a button and silence everywhere else — pure survivorship.
       The clinician who reported "I can't print the report" was using tools
       with no button at all, so nothing recorded the attempt.

       beforeprint fires for Ctrl+P and the mobile browser menu too, so this
       measures real demand across every page. `src` separates a deliberate
       button press from someone reaching for the browser's own print, which is
       exactly the signal for deciding where a button is missing. Once per page
       view: a user who prints twice is one interested user, not two. */
    var reported = false;
    function reportPrint(src) {
        if (reported) return;
        reported = true;
        try { if (window.__aeFeature) window.__aeFeature('print', { src: src }); } catch (e) {}
    }

    /* Delegated handler for <x-tool-print>. Keeping it here rather than in the
       component means a tool adds the button with one line and no script. */
    document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest && e.target.closest('[data-tool-print]');
        if (!btn) return;
        reportPrint('button');
        try { window.print(); } catch (err) {}
    });

    /* Safari drives print only through the media query, and headless renderers
       (our own Playwright print checks included) never fire beforeprint at all
       — without this branch the charts would be verified in screen colours and
       the test would pass on a chart that prints blank. */
    try {
        var mq = window.matchMedia('print');
        var onChange = function (e) { apply(!!e.matches); };
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
    } catch (e) {}
})();

/* ── data-vc-confirm: «точно?» перед отправкой формы — слушателем, не атрибутом ──
   Инлайновые onsubmit= держат `script-src-attr 'unsafe-inline'` в CSP
   (InlineHandlerBudgetTest ведёт их потолок вниз). Одна форма = один атрибут
   с текстом; отмена в диалоге останавливает отправку. */
(function () {
    document.addEventListener('submit', function (e) {
        var f = e.target;
        if (!f || !f.getAttribute) return;
        var q = f.getAttribute('data-vc-confirm');
        if (q && !window.confirm(q)) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);
})();

/* ── One gesture, one answer (data-answer / data-answer-scope) ────────────────
   Every one-question-at-a-time test on the site draws the next question where
   the last one was, with its buttons in the same places. Two ordinary input
   habits then answer questions the person never saw. Measured 2026-09-23 in a
   real browser on ~20 questionnaires (tests/playwright/answer-guard-sweep.mjs):

   * the second click of a double-click — Windows counts clicks up to 500 ms
     apart as one — lands on the NEXT question (personality 1 -> 3; greed,
     trust, risk, eq, burnout, english-level, stroke, iq: two items), or, where
     the tool waits before advancing, on the SAME item and schedules a second
     advance (autism and dementia skip an item that then scores 0; dyslexia
     letters and cognitive attention count one answer twice: "120 %");
   * a held key auto-repeats ~30 times a second and every repeat answers:
     burnout 17 of 19 items, english-level 21 of 28 words (made-up words marked
     "known"), ikigai 16, workaholism 13, iq 7.

   At /depression-test a double-click on the self-harm question replaced the
   crisis screen with the next question within milliseconds. The 160 ms locks
   two tools had built for this stopped Playwright's instant double-click and
   nothing slower.

   Declarative. A tool marks the controls of the question on screen with
   data-answer, and may mark the whole container its flow runs in with
   data-answer-scope. Then:
   - a click whose `detail` > 1 (the 2nd+ click of a multi-click at one spot)
     inside either is cancelled before any listener sees it — capture phase on
     window, so inline onclick attributes are stopped too. A first click, a
     keyboard-activated click and a programmatic .click() carry detail 0/1 and
     pass. Editable fields are left alone (a double-click there selects a word).
   - an auto-repeated keydown (`repeat`) is cancelled: an Enter repeat when it
     comes from inside a scope or while an answer control is on screen; any
     other key's repeat only while an answer control is on screen. In a text
     field only Enter's repeat is cancelled (holding Backspace still deletes);
     in a textarea or contenteditable nothing is. Tab, modifiers, Space and the
     page-scroll keys always pass.
   - [data-answer-input] marks an entry area inside a scope — a keypad, a grid
     of cells, a sheet of chips — where many quick presses are the answer. There
     a multi-click on ANOTHER control passes, and only a repeat on the same
     control is cancelled. `detail` counts presses on one spot only on a desktop
     (20 px, 400–500 ms); Chrome on Android counts every tap within 100 dp and
     300 ms of the last one, whatever it hit (ui/events/gesture_detection/
     gesture_detector.cc IsRepeatedTap, slop from ViewConfiguration), so a quick
     player's taps on neighbouring cells arrived as detail 2 and 3 and were
     thrown away. /cognitive-test's sequence grid lost them from 23.09 to
     30.10.2026. A keypad whose SAME key legitimately repeats ("100") keeps out
     of a scope instead (/multiplication-table).
   Counts ride on window.__answerGuard; a page that swallowed anything reports
   the new counts once each time it is hidden (feature_use{f:'answer_guard'}). */
(function () {
    var G = window.__answerGuard = window.__answerGuard || { dbl: 0, rep: 0 };
    var ANY = '[data-answer],[data-answer-scope]';
    var INPUT = '[data-answer-input]';
    var PASS = /^(Tab|Shift|Control|Alt|AltGraph|Meta|CapsLock|Escape| |Spacebar|PageUp|PageDown|Home|End)$/;

    function editable(el) {
        if (!el || el.nodeType !== 1) return '';
        if (el.isContentEditable || el.tagName === 'TEXTAREA') return 'rich';
        if (el.tagName === 'SELECT') return 'field';
        if (el.tagName === 'INPUT') {
            var t = String(el.type || 'text').toLowerCase();
            return /^(button|submit|reset|checkbox|radio|image|range|color|file)$/.test(t) ? '' : 'field';
        }
        return '';
    }
    function answerOnScreen() {
        var list = document.querySelectorAll('[data-answer]');
        for (var i = 0; i < list.length; i++) if (list[i].getClientRects().length) return true;
        return false;
    }

    var lastControl = null;
    function controlOf(t) {
        return (t && t.closest && t.closest('button,a,input,select,textarea,label,[role="button"],[data-action]')) || t;
    }

    window.addEventListener('click', function (ev) {
        var t = ev.target;
        var prev = lastControl;
        lastControl = controlOf(t);
        if (!(ev.detail > 1)) return;
        if (!t || !t.closest || !t.closest(ANY) || editable(t)) return;
        if (t.closest(INPUT) && lastControl !== prev) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
        G.dbl++;
    }, true);

    window.addEventListener('keydown', function (ev) {
        if (!ev.repeat || PASS.test(ev.key || '')) return;
        var t = ev.target, ed = editable(t);
        if (ed === 'rich' || (ed === 'field' && ev.key !== 'Enter')) return;
        var inScope = !!(t && t.closest && t.closest(ANY));
        if (!(ev.key === 'Enter' && inScope) && !answerOnScreen()) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
        G.rep++;
    }, true);

    var sentDbl = 0, sentRep = 0;
    function report() {
        var d = G.dbl - sentDbl, r = G.rep - sentRep;
        if (d <= 0 && r <= 0) return;
        sentDbl = G.dbl; sentRep = G.rep;
        try { if (window.__ae) window.__ae('feature_use', { f: 'answer_guard', dbl: d, rep: r }); } catch (e) {}
    }
    window.addEventListener('pagehide', report);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') report();
    });
})();

/* __holdRepeat:begin */
/* ── window.__holdRepeat — press and hold to repeat, ONE owner per press ──────
   window.__holdRepeat(btn, step, opts) → { stop, destroy }
     step(info)    one step; returning false ends the repeat (a limit was hit).
                   info = { n, heldMs }: n = 0 is the press itself, 1… the repeats.
     opts.delay    ms from the press to the first repeat (400: a slow tap is ~250 ms);
     opts.period   ms between repeats — a number or fn(n, heldMs) (80);
     opts.when()   checked on press; false = the press is not ours (busy, wrong phase);
     opts.onEnd(i) once, when the press ends: release, cancel, leaving the button,
                   or the button turning disabled. step() returning false only
                   stops the repeat — the press (and its click) stays owned.
   The press acts on pointerdown (primary button, finger, pen). The browser still
   fires click for that same press — right after the release for a mouse, in a
   later task for a finger — and a capture listener on the button swallows it
   before any of the tool's click handlers. So the tool's own click handler stays
   the ONE path for the keyboard (Enter/Space → click with detail 0) and assistive
   tech: one step each. A second finger on a held button is ignored.

   Why one primitive (census 29.09.2026, sweep «один жест — одно действие»): nine
   hand-made copies, seven of them double-acting — no start delay, so a 120–180 ms
   tap stepped twice (drawing undo/redo, maze and sokoban D-pads, trim zoom); a
   guard reset by setTimeout(0) racing the touch click (hourglass: a tap stepped
   twice); a guard counted from pointerdown, so every hold longer than 800 ms added
   a step on release (/speed); an interval left running by a second finger or by a
   button that disabled itself mid-hold (tank-duel, trim). Guard — tests/js/hold-repeat.test.mjs. */
(function () {
    var perf = (window.performance && typeof performance.now === 'function') ? function () { return performance.now(); } : function () { return Date.now(); };
    window.__holdRepeat = function (btn, step, opts) {
        opts = opts || {};
        var delay = opts.delay != null ? +opts.delay : 400;
        var period = opts.period != null ? opts.period : 80;
        var press = null, timer = 0, endedAt = -1e9;
        function info() { return { n: press ? press.n : 0, heldMs: press ? perf() - press.t : 0 }; }
        function stopRepeat() { if (timer) { clearTimeout(timer); timer = 0; } }
        function end() {
            stopRepeat();
            if (!press) return;
            var i = info();
            press = null;
            endedAt = perf();
            if (opts.onEnd) { try { opts.onEnd(i); } catch (e) { setTimeout(function () { throw e; }, 0); } }
        }
        function run() {
            var r;
            try { r = step(info()); } catch (e) { end(); throw e; }
            return r !== false;
        }
        function tick() {
            timer = 0;
            if (!press) return;
            if (btn.disabled) { end(); return; }
            press.n++;
            if (!run()) return;
            var p = typeof period === 'function' ? period(press.n, perf() - press.t) : period;
            timer = setTimeout(tick, Math.max(16, +p || 80));
        }
        function down(e) {
            if (press || btn.disabled) return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (opts.when && opts.when() === false) return;
            press = { id: e.pointerId, t: perf(), n: 0 };
            if (run() && press) timer = setTimeout(tick, delay);
        }
        function up(e) { if (press && e.pointerId === press.id) end(); }
        function click(e) {
            if (e.detail !== 0 && perf() - endedAt < 1000) {
                endedAt = -1e9;
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        }
        function menu(e) { if (press) e.preventDefault(); }
        btn.addEventListener('pointerdown', down);
        btn.addEventListener('pointerup', up);
        btn.addEventListener('pointercancel', up);
        btn.addEventListener('pointerleave', up);
        btn.addEventListener('click', click, true);
        btn.addEventListener('contextmenu', menu);
        try { if (!btn.style.touchAction) btn.style.touchAction = 'manipulation'; } catch (_) {}
        return {
            stop: end,
            destroy: function () {
                end();
                btn.removeEventListener('pointerdown', down);
                btn.removeEventListener('pointerup', up);
                btn.removeEventListener('pointercancel', up);
                btn.removeEventListener('pointerleave', up);
                btn.removeEventListener('click', click, true);
                btn.removeEventListener('contextmenu', menu);
            }
        };
    };
})();
/* __holdRepeat:end */

/* __keyOwnedByFocus:begin */
/* ── window.__keyOwnedByFocus(ev) — чья это клавиша: элемента в фокусе или
   горячей клавиши тула? ───────────────────────────────────────────────────────
   true — у клавиши есть РАБОТА в элементе, где стоит фокус (набор текста, шаг
   ползунка, выбор пункта, нажатие кнопки с клавиатуры), и горячая клавиша тула
   её не трогает; false — элементу она ни к чему, и тул волен взять её себе.

     document.addEventListener('keydown', (ev) => {
         if (window.__keyOwnedByFocus(ev)) return;
         if (ev.code === 'Space') { ev.preventDefault(); if (!ev.repeat) togglePlay(); }
     });

   Правило — по работе клавиши, а не по тегу:
     · поле набора текста (input text/number/date/…, textarea, contenteditable,
       role=textbox/searchbox/combobox) и ввод через IME — ему ВСЁ;
     · ползунок (range, role=slider/spinbutton) — стрелки, Home/End,
       PageUp/PageDown; пробел, Delete и буквы ему не нужны;
     · список <select> — стрелки, Home/End, PageUp/PageDown и буквы (поиск по
       первой букве); пробел и Enter — только при клавиатурном фокусе;
     · кнопка, ссылка, галочка, переключатель, <summary>, role=button/tab/… —
       пробел и Enter только при клавиатурном фокусе; стрелки у переключателей
       (radio, role=radio/tab/option/menuitem) — их всегда.
   Enter — по тому же правилу, что пробел: кнопка, нажатая мышью, держит фокус,
   и «кликнул ответ → Enter = дальше» иначе нажимал бы тот же ответ ещё раз.
   Клавиатурный фокус — тот, что пришёл после клавиши, а не после нажатия
   указателем: так различаются человек, дошедший до кнопки Tab-ом (пробел для
   него — нажатие), и тот, кто кликнул мышью и жмёт пробел, чтобы послушать.
   Происхождение фокуса примитив записывает сам (`focusin` после `keydown` или
   после `pointerdown`), а `:focus-visible` — лишь запасной ответ: Chromium
   метит им <select> и после клика мышью (замер 15.10: выбрали формат мышью —
   `:focus-visible` истинно), и пробел оставался бы за списком. Где нет ни
   записи, ни `:focus-visible` (Chrome < 86, Safari < 15.4), фокус считается
   клавиатурным: у кнопки пробел остаётся за кнопкой, ничего не отнимаем.

   Почему примитив (жалоба 14.10.2026, /audio-overlay, Edge 155): тулы с
   транспортом решали это копией `if (tag === 'INPUT' || tag === 'SELECT') return;`
   (перепись того дня — около 60 копий). Тег не знает работы клавиши: после
   ползунка громкости, выбора формата или галочки пробел молчал, а галочку ещё
   и переключал. Сторожа — tests/js/key-owned-by-focus.test.mjs,
   TransportHotkeysAskTheFocusTest; разбор — docs/why/primitives/key-owned-by-focus.md. */
(function () {
    var TEXT_ROLES = { textbox: 1, searchbox: 1, combobox: 1 };
    var SLIDER_ROLES = { slider: 1, spinbutton: 1, scrollbar: 1 };
    var PRESS_TYPES = { checkbox: 1, radio: 1, button: 1, submit: 1, reset: 1, file: 1, color: 1, image: 1 };
    var PRESS_ROLES = { button: 1, link: 1, checkbox: 1, 'switch': 1, radio: 1, tab: 1, option: 1, menuitem: 1, menuitemcheckbox: 1, menuitemradio: 1, treeitem: 1 };
    var PICK_ROLES = { radio: 1, tab: 1, option: 1, menuitem: 1, menuitemcheckbox: 1, menuitemradio: 1, treeitem: 1 };
    var NAV = { ArrowLeft: 1, ArrowRight: 1, ArrowUp: 1, ArrowDown: 1, Home: 1, End: 1, PageUp: 1, PageDown: 1 };
    var ARROWS = { ArrowLeft: 1, ArrowRight: 1, ArrowUp: 1, ArrowDown: 1 };
    /* Откуда пришёл фокус: 'k' — после клавиши, 'p' — после указателя. Слушатели
       в захвате — раньше любого обработчика тула; ничего не меняют в событиях. */
    var focusBy = typeof WeakMap === 'function' ? new WeakMap() : null;
    var lastInput = '';
    try {
        if (focusBy && typeof document !== 'undefined' && document.addEventListener) {
            document.addEventListener('keydown', function (e) {
                if (!(e.metaKey || e.ctrlKey || e.altKey)) lastInput = 'k';
            }, true);
            ['pointerdown', 'mousedown', 'touchstart'].forEach(function (t) {
                document.addEventListener(t, function () { lastInput = 'p'; }, { capture: true, passive: true });
            });
            document.addEventListener('focusin', function (e) {
                var t = e.target;
                if (lastInput && t && t.nodeType === 1) focusBy.set(t, lastInput);
            }, true);
        }
    } catch (_) {}
    function keyboardFocus(el) {
        var by = focusBy && focusBy.get(el);
        if (by) return by === 'k';
        try { return el.matches(':focus-visible'); } catch (_) { return true; }
    }
    function focused(ev) {
        var t = ev.target;
        try {
            // Внутри теневого дерева target — его хозяин; настоящий фокус — первый в пути.
            if (typeof ev.composedPath === 'function') { var p = ev.composedPath(); if (p && p[0] && p[0].nodeType === 1) t = p[0]; }
        } catch (_) {}
        return t && t.nodeType === 1 ? t : null;
    }
    window.__keyOwnedByFocus = function (ev) {
        if (!ev) return false;
        if (ev.isComposing || ev.keyCode === 229) return true;            // идёт набор через IME
        var el = focused(ev);
        if (!el) return false;
        if (el.isContentEditable) return true;
        var tag = String(el.tagName || '').toUpperCase();
        var role = '';
        try { role = String((el.getAttribute && el.getAttribute('role')) || '').toLowerCase(); } catch (_) {}
        var key = ev.key || '';
        var space = key === ' ' || key === 'Spacebar' || ev.code === 'Space';
        var mod = !!(ev.ctrlKey || ev.metaKey || ev.altKey);
        if (tag === 'TEXTAREA' || TEXT_ROLES[role]) return true;
        if (tag === 'INPUT') {
            var type = String(el.type || 'text').toLowerCase();
            if (type === 'range') return !mod && !!NAV[key];
            if (!PRESS_TYPES[type]) return true;                          // поле набора (и всё незнакомое)
            if (type === 'radio' && !mod && ARROWS[key]) return true;
            return space || key === 'Enter' ? keyboardFocus(el) : false;
        }
        if (tag === 'SELECT') {
            if (mod) return false;
            if (space || key === 'Enter') return keyboardFocus(el);
            return !!NAV[key] || key.length === 1;
        }
        if (SLIDER_ROLES[role]) return !mod && !!NAV[key];
        var pressable = tag === 'BUTTON' || tag === 'SUMMARY' || PRESS_ROLES[role] ||
            (tag === 'A' && !!(el.hasAttribute && el.hasAttribute('href')));
        if (pressable) {
            if (PICK_ROLES[role] && !mod && ARROWS[key]) return true;
            return space || key === 'Enter' ? keyboardFocus(el) : false;
        }
        return false;
    };
    /* Тот же ответ «фокус пришёл с клавиатуры?» — для страницы, которой нужна
       не работа клавиши, а намерение человека: /keyboard-test отдаёт Tab
       странице, пока человек ходит по ней клавиатурой, и забирает в тест,
       когда он работал мышью (список, выбранный мышью, — уже не его). */
    window.__keyOwnedByFocus.byKeyboard = function (el) {
        return !!(el && el.nodeType === 1) && keyboardFocus(el);
    };
})();
/* __keyOwnedByFocus:end */

/* __keyLetter:begin */
/* ── window.__keyLetter(ev) — какую букву (a–z) или цифру (0–9) горячей
   клавиши нажал человек, на какой бы раскладке он ни был ─────────────────────
   'a'…'z', '0'…'9' или '' (клавиша не буква и не цифра). Регистр и Shift не
   учитываются, модификаторы (Ctrl, Alt, ⌘) проверяет сам тул.

     document.addEventListener('keydown', (ev) => {
         if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
         if (window.__keyOwnedByFocus(ev)) return;
         if (window.__keyLetter(ev) === 'p') { ev.preventDefault(); togglePause(); }
     });

   Правило:
     · `key` — латинская буква: она и есть ответ. Человек на AZERTY, QWERTZ или
       Двораке получает клавишу с той буквой, что на ней НАПИСАНА («a» на AZERTY
       стоит там, где у QWERTY «q»);
     · `key` — буква другой письменности (кириллица, арабская, греческая…),
       буква с диакритикой, мёртвая клавиша или ввод без символа — ответ по
       месту клавиши (`code` KeyR → 'r'): на таких раскладках латиница напечатана
       на той же клавише, что у QWERTY;
     · `key` — ASCII-знак (запятая на месте «m» у AZERTY, «;» на месте «z» у
       Дворака) — это не буква;
     · ряд цифр — цифра по месту (`Digit1` → '1'): на AZERTY и чешской раскладке
       без Shift там «&», «ě»; цифровой блок — только при NumLock (без него это
       стрелки и End).

   Почему примитив (жалоба 22.10.2026, /ru/screen-recorder, Яндекс 26: «не
   работают горячие клавиши»). Тулы сравнивали `e.key` с латинской буквой, а
   у человека, который только что набрал запрос по-русски, раскладка русская:
   клавиша R отдаёт «к», P — «з», M — «ь», Ctrl+Z — «я». Стенд: R на русской,
   украинской и арабской раскладке запись не начинал. Разбор —
   docs/why/primitives/key-letter.md, сторож — tests/js/key-letter.test.mjs. */
(function () {
    var CODE_LETTER = /^Key([A-Z])$/;
    var CODE_DIGIT = /^Digit([0-9])$/;
    var CODE_PAD = /^Numpad([0-9])$/;
    window.__keyLetter = function (ev) {
        if (!ev) return '';
        var key = typeof ev.key === 'string' ? ev.key : '';
        var code = typeof ev.code === 'string' ? ev.code : '';
        var m = CODE_DIGIT.exec(code);
        if (m) return m[1];
        m = CODE_PAD.exec(code);
        if (m) return /^[0-9]$/.test(key) ? m[1] : '';
        if (key.length === 1 && key.charCodeAt(0) < 128) {
            return /^[A-Za-z]$/.test(key) ? key.toLowerCase() : '';
        }
        m = CODE_LETTER.exec(code);
        if (m) return m[1].toLowerCase();
        // Без `code` (старые движки) — виртуальный код клавиши: у букв 65…90 на любой раскладке.
        var kc = ev.keyCode | 0;
        if (!code && kc >= 65 && kc <= 90) return String.fromCharCode(kc + 32);
        return '';
    };
})();
/* __keyLetter:end */

/* __keySign:begin */
/* ── window.__keySign(ev) — какой ЗНАК горячей клавиши нажал человек ([ ] , . /
   ; ' ` \ - = и их Shift-знаки { } < > ? : " ~ | _ +), на какой бы раскладке он
   ни был ──────────────────────────────────────────────────────────────────────
   Знак или '' (клавиша не знак). Модификаторы проверяет сам тул.

     if (window.__keySign(ev) === '[') { ev.preventDefault(); brushSmaller(); }

   Правило:
     · `key` — ASCII-знак: он и есть ответ. Как напечатано: AZERTY «,» на месте
       QWERTY-M, португальские «[ ]» на своих местах, у иврита скобки зеркальные;
     · клавиша знака (места QWERTY: Comma, Period, BracketLeft…) напечатала НЕ
       ASCII — букву другой письменности или мёртвую клавишу: русские «х ъ б ю ж
       э ё», немецкая «ü», французская «^» — знак этого места, с Shift — его
       верхний знак («Б» → «<»). На русской раскладке «[» и «]» иначе нет вовсе;
     · буква и цифра — не знак (у них __keyLetter): AZERTY «m» на месте «;» —
       это M, не точка с запятой.

   Почему (04.11.2026): после __keyLetter молчали знаки — «[ ]» (кисть, вход и
   выход отрезка), «, .» (кадр назад и вперёд) на русской, украинской, арабской,
   турецкой раскладке давали «х ъ б ю», «ğ ü ö ç». Знак ASCII ПО МЕСТУ всегда —
   отвергнуто: AZERTY «,» стоит на месте M, одна клавиша дала бы два действия.
   Разбор — docs/why/primitives/key-letter.md, сторож — tests/js/key-sign.test.mjs. */
(function () {
    var PLACE = {
        Backquote: '`~', Minus: '-_', Equal: '=+', BracketLeft: '[{', BracketRight: ']}',
        Backslash: '\\|', Semicolon: ';:', Quote: '\'"', Comma: ',<', Period: '.>', Slash: '/?'
    };
    window.__keySign = function (ev) {
        if (!ev) return '';
        var key = typeof ev.key === 'string' ? ev.key : '';
        if (key.length === 1) {
            var c = key.charCodeAt(0);
            if (c > 32 && c < 127) return /^[A-Za-z0-9]$/.test(key) ? '' : key;
        }
        var place = PLACE[typeof ev.code === 'string' ? ev.code : ''];
        return place ? place.charAt(ev.shiftKey ? 1 : 0) : '';
    };
    /* window.__keyChord(ev) — для ЗНАКА: человек нажал сочетание (Ctrl, ⌘), а не просто
       набрал знак. Windows сообщает AltGr как Ctrl+Alt, а немецкая, французская,
       испанская раскладки печатают «] \ { }» только через AltGr; на Маке знаки набирают с
       Option. Поэтому Ctrl вместе с Alt и один Alt — набор, а не сочетание. Буквам и
       цифрам это не годится: AltGr+a на польской — «ą», AltGr+0 на немецкой — «}». */
    window.__keyChord = function (ev) {
        return !!ev && (!!ev.metaKey || (!!ev.ctrlKey && !ev.altKey));
    };
})();
/* __keySign:end */

/* __timeCommit:begin */
/* ── window.__timeCommit — поле часов <input type=time> сохраняется, когда
   человек ЗАКОНЧИЛ ввод ─────────────────────────────────────────────────────
   window.__timeCommit(input, commit) — commit() зовётся один раз на правку:
   при уходе из поля, по Enter или когда окно потеряло фокус; `change`, пока
   курсор в поле, только помечает правку. Значение берётся из input.value.

   Зачем. Chromium шлёт `change` у type=time после КАЖДОГО сегмента, если
   значение уже было: набрал «1» в часах «08:30» — пришло «01:30». Обработчик,
   который на `change` сохраняет и пересобирает список, теряет поле посреди
   набора, остальные цифры уходят мимо. Замер 08.10.2026, Chrome 155, ввод с
   клавиатуры: /school-bell «13:00» → в расписании 01:30 (звонки встали на
   ночь — обращение #1856 «Не звонит звонок»), /daily-routine «14:17» → 01:40,
   строка уехала наверх. Пустое поле этим не страдает: оно шлёт `change`
   только когда заполнены и часы, и минуты.
   ⚠️ «Курсор ещё в поле?» спрашиваем тактом позже, не внутри `change`: на
   переходе с часов на минуты Chromium шлёт его, когда фокус в пути и
   activeElement пуст (замер: «2215» → 02:35 с фокусом, 22:35 без, 22:01,
   22:15 — снова с фокусом). Тот же такт нужен после blur: новый фокус
   назначается позже, и перерисовка иначе не знает, куда его вернуть.
   ⚠️ Свёрнутое окно — тоже «закончил»: activeElement при этом остаётся полем,
   и без document.hasFocus() набранное время ждало бы возвращения.
   Разбор — docs/why/primitives/time-commit.md. */
window.__timeCommit = function (input, commit) {
    if (!input || typeof commit !== 'function') return;
    var dirty = false;
    function done() { dirty = false; commit.call(input); }
    function settle() {
        setTimeout(function () {
            if (!dirty) return;
            if (document.activeElement === input && document.hasFocus()) return;
            done();
        }, 0);
    }
    input.addEventListener('change', function () { dirty = true; settle(); });
    input.addEventListener('blur', settle);
    input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && dirty) { e.preventDefault(); done(); }
    });
};
/* __timeCommit:end */

/* ── window.__progress — a tool reports a finished result for «Мой прогресс» ──
   One call at the moment a result is final:
       window.__progress && window.__progress.record('iq', { value: 118 });
   Every result goes through a small queue in localStorage first, so nothing
   is lost to a dropped connection or a closed tab, and a guest's results wait
   for sign-in and then land in their profile (the same promise the calendar
   makes about a guest's entries). The server answers 401 to a guest; the
   queue simply stays. Tracks and their valid ranges live in
   config/progress.php — a value outside them is dropped there, not here.

   Health results are different (Farid's call 2026-09-23: they are not kept
   on our server). A tool measuring hearing, vision, colour vision or pulse
   calls
       window.__progress.keep('vision', { value: 87 });
   which writes to this browser's own journal (tmb_progress_local) and never
   to the queue — «Мой прогресс» reads it from there. The API refuses those
   tracks as well; if a page cached from before still sends one through
   record(), the answer names the track and the result moves into the local
   journal instead of being lost. */
(function () {
    'use strict';
    var KEY = 'tmb_progress_q';
    var LOCAL = 'tmb_progress_local';
    var LOCAL_MAX = 100;
    var MAX = 100;
    var TTL = 90 * 86400000;
    var sending = false;

    function read() {
        try { var q = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(q) ? q : []; }
        catch (e) { return []; }
    }
    function write(q) {
        try {
            if (q.length) localStorage.setItem(KEY, JSON.stringify(q.slice(-MAX)));
            else localStorage.removeItem(KEY);
        } catch (e) { /* storage blocked — the live POST below still runs */ }
    }
    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.getAttribute('content') : '';
    }

    function flush() {
        if (sending || !window.__AUTH_USER) return;
        var now = Date.now();
        var q = read().filter(function (x) { return x && x.track && (now - (x.at || 0)) < TTL; });
        if (!q.length) { write([]); return; }
        var batch = q.slice(0, 300);
        var ids = {};
        batch.forEach(function (x) { ids[x.id] = 1; });
        sending = true;
        fetch('/api/progress', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': csrf(),
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: batch })
        }).then(function (r) {
            sending = false;
            // 2xx: stored (or deduplicated). 422: the batch itself is malformed —
            // resending it forever would block every later result behind it.
            if (r.ok || r.status === 422) {
                write(read().filter(function (x) { return !ids[x.id]; }));
                if (!r.ok) return;
                var more = q.length > batch.length;
                return r.json().then(function (j) {
                    var dev = j && Array.isArray(j.device) ? j.device : [];
                    if (dev.length) batch.forEach(function (x) { if (dev.indexOf(x.track) !== -1) keepItem(x); });
                    if (more) setTimeout(flush, 500);
                }, function () { if (more) setTimeout(flush, 500); });
            }
            // 401 (guest) / 419 (stale token) / 5xx: keep, retry on the next
            // page view or after sign-in.
        }).catch(function () { sending = false; });
    }

    function makeItem(track, p) {
        p = p || {};
        if (!track) return null;
        var v = p.value == null ? null : Number(p.value);
        if (v !== null && !isFinite(v)) return null;
        return {
            id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
            track: String(track),
            value: v,
            label: p.label ? String(p.label).slice(0, 60) : null,
            data: p.data && typeof p.data === 'object' ? p.data : null,
            at: typeof p.at === 'number' && isFinite(p.at) ? p.at : Date.now()
        };
    }

    /* This browser's journal: { track: [{at, value, label, data}] }, newest
       last, capped per track. The same result reported twice within a second
       is one result. */
    function keepItem(item) {
        if (!item || !item.track) return;
        try {
            var all = JSON.parse(localStorage.getItem(LOCAL) || '{}');
            if (!all || typeof all !== 'object' || Array.isArray(all)) all = {};
            var list = Array.isArray(all[item.track]) ? all[item.track] : [];
            var dup = list.some(function (x) { return x && Math.abs((x.at || 0) - item.at) < 1000 && x.value === item.value; });
            if (!dup) list.push({ at: item.at, value: item.value, label: item.label, data: item.data });
            all[item.track] = list.slice(-LOCAL_MAX);
            localStorage.setItem(LOCAL, JSON.stringify(all));
        } catch (e) { /* storage blocked — nothing to keep it in */ }
    }

    function keep(track, p) {
        var item = makeItem(track, p);
        if (!item) return;
        keepItem(item);
        try { if (window.__ae) window.__ae('feature_use', { f: 'progress_keep', track: item.track }); } catch (e) {}
    }

    function record(track, p) {
        var item = makeItem(track, p);
        if (!item) return;
        var q = read();
        q.push(item);
        write(q);
        try { if (window.__ae) window.__ae('feature_use', { f: 'progress_record', track: item.track, authed: window.__AUTH_USER ? 1 : 0 }); } catch (e) {}
        flush();
    }

    window.__progress = { record: record, keep: keep, flush: flush };
    // ⚠️ На window: layout шлёт auth-ready через window.dispatchEvent, и до
    // события на document оно не доходит — очередь гостя, вошедшего на этой
    // странице, ждала следующего перехода.
    window.addEventListener('auth-ready', function () { setTimeout(flush, 300); });
    if (document.readyState === 'complete') setTimeout(flush, 1500);
    else window.addEventListener('load', function () { setTimeout(flush, 1500); });
})();
